// Pure utility functions — no React, no Supabase, safe to import in tests.
// These are the source of truth; AppContext.jsx and ProcurementApp.jsx import from here.

// ── STATUS SYSTEM ─────────────────────────────────────────────────────────────
// Single source of truth. ProcurementApp.jsx and AppContext.jsx must import
// from here — never define STATUS constants locally.

// Canonical status list — the 2026-07-28 tracker-rebuild names (Cimon's
// blessed physical chain). The DB may still hold LEGACY names until the
// status-flip migration applies; rowToItem normalizes every row through
// normalizeItemStatus at the mapper boundary (the Fix D 1.3 pattern), so
// in-memory statuses are ALWAYS canonical. Legacy → canonical:
//   Pricing Check / Quote Requested → Specifying
//   Order Confirmed                 → Ordered
//   Received                        → At Receiver
//   Invoiced                        → At Receiver | Ordered (per received_date)
export var STATUSES = [
  // Zone 1 — Specifying (yellow #F5C800)
  'Specifying',
  // Zone 2 — Quoting & Proposal (fuchsia #D4007A)
  'Quote Received',
  'Presented to Client',
  'On Proposal',
  // Lock point ── Client Approved ──────────────────────────────────────────
  // All material fields locked from here onward. Change Order required.
  'Client Approved',
  // Zone 3 — Committed / Financial (green #1A5C38). From Ordered on, this
  // is the PHYSICAL chain — money truth lives on the Thread's derived
  // money rail, never on these strings (Rule 10).
  'Deposit Paid',
  'PO Issued',
  'Ordered',        // vendor paid/confirmed — the order is real
  'Shipped',        // carrier has it (shipped_date, tracking_number, carrier)
  'At Receiver',    // at the intermediate stop — receiver warehouse,
                    // workroom, or fabricator, per the item's ship-to
                    // (received_date; direct-to-site items skip this)
  'Delivered',      // at the FINAL place, usually site (delivered_date)
  'Installed',      // install complete (install_date)
  'Complete',
  // Terminal — item dropped after Client Approved. Financial history preserved
  // via client_credit_ledger / vendor_credit_ledger entries; the item itself
  // is excluded from POs, schedules, tracker, and billing math.
  'Cancelled',
];

// Legacy → canonical status aliases. Exact full-string matches only
// ('Quote Received' is canonical and must never be touched by the
// 'Received' alias). Keys stay here until well after the status-flip
// migration lands — old names can arrive from un-migrated DB rows, saved
// browser drafts, imports, and frozen payloads.
export var LEGACY_STATUS_ALIASES = {
  'Pricing Check':   'Specifying',
  'Quote Requested': 'Specifying',
  'Quote Verified':  'Presented to Client',
  'Order Confirmed': 'Ordered',
  'Received':        'At Receiver',
};

// Alias-map lookup only — safe for any consumer that has just a status
// string. 'Invoiced' needs row context (see normalizeItemStatus) and is
// deliberately NOT mapped here; it zone-colors committed until normalized.
export function canonicalItemStatus(status) {
  var s = String(status == null ? '' : status).trim();
  return LEGACY_STATUS_ALIASES[s] || s;
}

// Full normalization at the mapper boundary (rowToItem). 'Invoiced' is the
// one per-row mapping: the item was received before invoicing → it's
// physically At Receiver; never received → still Ordered. The status-flip
// migration applies the same rule durably after Cimon reviews the rows.
export function normalizeItemStatus(status, receivedDate) {
  var s = String(status == null ? '' : status).trim();
  if (s === 'Invoiced') return receivedDate ? 'At Receiver' : 'Ordered';
  return LEGACY_STATUS_ALIASES[s] || s;
}

// The status at which material fields lock. Change Order required from here on.
export var LOCK_STATUS = 'Client Approved';
export var LOCK_INDEX  = STATUSES.indexOf(LOCK_STATUS); // 7

// Dot color zone by status. Canonical names first; the legacy block keeps
// every pre-flip name resolving to the right zone for any path that sees
// an un-normalized string (frozen payloads, imports, saved drafts).
export var STATUS_ZONE = {
  'Specifying':          'spec',
  'Quote Received':      'quote',
  'Presented to Client': 'quote',
  'On Proposal':         'quote',
  'Client Approved':     'committed',
  'Deposit Paid':        'committed',
  'PO Issued':           'committed',
  'Ordered':             'committed',
  'Shipped':             'committed',
  'At Receiver':         'committed',
  'Delivered':           'committed',
  'Installed':           'committed',
  'Complete':            'committed',
  'Cancelled':           'cancelled',
  // Legacy — pre-flip names (see LEGACY_STATUS_ALIASES) + ancient
  // Design-Manager-era statuses; zone-resolve until migrated
  'Pricing Check':       'spec',
  'Quote Requested':     'quote',
  'Quote Verified':      'quote',
  'Order Confirmed':     'committed',
  'Received':            'committed',
  'Invoiced':            'committed',
  'Order Placed':        'committed',
  'Payment Received':    'committed',
  'PO Requested':        'committed',
  'PO Received':         'committed',
  'PO Verified':         'committed',
};

export var ZONE_SPEC      = Object.keys(STATUS_ZONE).filter(function(s) { return STATUS_ZONE[s] === 'spec'; });
export var ZONE_QUOTE     = Object.keys(STATUS_ZONE).filter(function(s) { return STATUS_ZONE[s] === 'quote'; });
export var ZONE_COMMITTED = Object.keys(STATUS_ZONE).filter(function(s) { return STATUS_ZONE[s] === 'committed'; });

export var ZONE_COLORS = {
  spec:      '#F5C800',
  quote:     '#D4007A',
  committed: '#1A5C38',
  cancelled: '#888880',
};

// Single source of truth for the 3 displayable lifecycle zones used to
// group items in the Items list and Tracker. Order matters: items display
// top-to-bottom in this order. Cancelled is terminal and excluded from
// list groupings (cancelled items are filtered out of isBillable). The
// dot color, label, and member statuses all live together so a single
// edit ripples to every consumer.
export var ZONES = [
  { key: 'spec',      label: 'Specifying',         color: ZONE_COLORS.spec,      statuses: ZONE_SPEC },
  { key: 'quote',     label: 'Quoting & Proposal', color: ZONE_COLORS.quote,     statuses: ZONE_QUOTE },
  { key: 'committed', label: 'Committed',          color: ZONE_COLORS.committed, statuses: ZONE_COMMITTED },
];

export function statusColor(status) {
  var zone = STATUS_ZONE[status];
  return zone ? ZONE_COLORS[zone] : ZONE_COLORS.spec;
}

// ── STAGE TRANSITIONS ─────────────────────────────────────────────────────────
// Single source of truth for "current status → next status → action label."
// Used by TrackerView's per-row Action button and any other place that needs
// to advance an item one step. Cancelled is terminal — no next, no action.
//
// Shape: { key, next, action, variant, scan }
//   key      — the source status
//   next     — the status we move to
//   action   — button label
//   variant  — visual style hint (secondary | warning | blue | purple | success | primary)
//   scan     — true when the action should open a confirmation/scan modal first

export var STAGES = [
  { key: 'Specifying',          next: 'Quote Received',      action: 'Quote Received',    variant: 'secondary', scan: false },
  { key: 'Quote Received',      next: 'Presented to Client', action: 'Present to Client', variant: 'warning',   scan: false },
  { key: 'Presented to Client', next: 'On Proposal',         action: 'Add to Proposal',   variant: 'blue',      scan: false },
  // On Proposal has no manual next-stage button. The only legitimate path into
  // Client Approved is `approveProposal` in ProposalsTab2, which assigns SKUs,
  // walks assembly+parent peers, sets proposal.approved_at, and queues the QB
  // Product/Service push. A direct-advance button here would orphan the item
  // (no SKU, no peer cascade, no QB push). See LIFECYCLES.md Appendix B #1.
  { key: 'On Proposal',         next: null,                  action: null,                variant: null,        scan: false },
  { key: 'Client Approved',     next: 'Deposit Paid',        action: 'Deposit Paid',      variant: 'success',   scan: false },
  { key: 'Deposit Paid',        next: 'PO Issued',           action: 'Issue PO',          variant: 'secondary', scan: false },
  // The physical chain (2026-07-28). Each advance stamps its stage date —
  // see TrackerView's advance + the tracking tab. Direct-to-site items
  // skip At Receiver via the stage dropdown; this linear chain is the
  // one-click default. Invoiced is GONE from the chain — FIN state lives
  // on the Thread's derived money rail (locked decision #3), so Installed
  // advances straight to Complete.
  { key: 'PO Issued',           next: 'Ordered',             action: 'Mark Ordered',      variant: 'secondary', scan: false },
  { key: 'Ordered',             next: 'Shipped',             action: 'Mark Shipped',      variant: 'secondary', scan: false },
  { key: 'Shipped',             next: 'At Receiver',         action: 'At Receiver',       variant: 'warning',   scan: true  },
  { key: 'At Receiver',         next: 'Delivered',           action: 'Mark Delivered',    variant: 'secondary', scan: false },
  { key: 'Delivered',           next: 'Installed',           action: 'Mark Installed',    variant: 'success',   scan: false },
  { key: 'Installed',           next: 'Complete',            action: 'Mark Complete',     variant: 'success',   scan: false },
  { key: 'Complete',            next: null,                  action: null,                variant: null,        scan: false },
  { key: 'Cancelled',           next: null,                  action: null,                variant: null,        scan: false },
];

export var STAGE_KEYS = STAGES.map(function(s) { return s.key; });

// Statuses that may be picked manually from a dropdown (item form, bulk
// reassign, Tracker overflow menu). Every status from Client Approved
// onward is owned by a cascade — proposal approval, deposit-invoice paid,
// PO issued, vendor receipt approved, etc. — and bypassing the cascade
// orphans the item (no SKU, no peers, no ledger entries, no QB push).
// See LIFECYCLES.md Appendix B #1 and __tests__/clientApprovedGuard.test.js.
export var MANUALLY_SELECTABLE_STATUSES = STATUSES.slice(0, STATUSES.indexOf('Client Approved'));

// Stages where the Tracker action opens a verification scan modal before
// advancing. Derived from STAGES so it auto-updates if a stage's scan flag
// changes — no parallel hardcoded list to drift.
export var SCAN_STAGES = STAGES.filter(function(s) { return s.scan; }).map(function(s) { return s.key; });

// Returns the STAGES entry for the item's current status, or null if no
// further action is possible (Complete / Cancelled / unknown status).

// Returns the items on a proposal whose status is still BEHIND 'On Proposal'.
// These are items that need to be advanced when the proposal moves Sent (or
// the user clicks Sync). Cancelled items and items already at On Proposal
// or beyond are excluded. Used by ProposalsTab2's sync banner + cascade.
export function itemsBehindOnProposal(proposalItemIds, allItems) {
  if (!Array.isArray(proposalItemIds)) return [];
  var onProposalIdx = STATUSES.indexOf('On Proposal');
  return proposalItemIds.map(function(id) {
    return (allItems || []).find(function(it) { return it.id === id; });
  }).filter(function(it) {
    if (!it) return false;
    if (it.status === 'Cancelled') return false;
    var i = STATUSES.indexOf(canonicalItemStatus(it.status));
    return i >= 0 && i < onProposalIdx;
  });
}

// ── VENDOR RECEIPT CASCADE — PURE LOGIC ──────────────────────────────────────
// Called from AppContext.applyVendorReceiptSideEffects. Splitting the rule
// logic out so it can be unit-tested without mocking Supabase.

// vendor_full_payment (added 2026-05-12) — in-stock POs where the vendor
// charges 100% upfront. Cascades the same item-status side effects as
// vendor_deposit (orderPlacedDate stamp + Deposit Paid → PO Issued) but
// records payment_type 'Full' in vendor_payments. See migration
// 20260512_vendor_payments_receipt_integration.sql.
export var VENDOR_RECEIPT_KINDS = ['vendor_deposit', 'vendor_final', 'vendor_full_payment', 'vendor_fee'];

// Principal payment_types — count toward "paid against the PO total".
// 'Fee' is excluded (freight, crating, tariff, CC processing fees live in
// separate vendor_payments rows that don't reduce the principal owed).
//
// Lifted to module-level on 2026-06-26 so isPoFullyPaid + the Bug 1 fix
// in computePoItemPaymentHistory + VendorPaymentsSection.poTotalPaid all
// read the same set. Previously two divergent copies lived locally
// (utils.js had 6 entries; VendorPaymentsSection had 7 with Partial
// Payment + Adjustment). Union shipped — broader set is more defensive
// against legacy data, and every member here is canonically NOT a fee.
//
// Canonical (set at vendorPaymentTypeForReceiptKind, utils.js:331):
//   'Deposit'         — vendor_deposit  receipts (partial principal)
//   'Final'           — vendor_final    receipts (remaining principal)
//   'Full'            — vendor_full_payment receipts (100% upfront)
// Legacy / manual variants (Record Payment modal + pre-canonical data):
//   'Deposit Payment' — early data shape alias for Deposit
//   'Final Payment'   — early data shape alias for Final
//   'Full Payment'    — early data shape alias for Full
//   'Partial Payment' — Record Payment modal manual entry
//   'Adjustment'      — owner-override row (2026-05-12)
//
// 'Fee' is intentionally absent — see CLAUDE.md "vendor_payments tagging
// integrity rule" for the canonical mapping and the 100%-upfront-paid-
// by-CC data debt warning.
export var PRINCIPAL_PAYMENT_TYPES = [
  'Deposit', 'Deposit Payment',
  'Final',   'Final Payment',
  'Full',    'Full Payment',
  'Partial Payment',
  'Adjustment',
];

// PO payment reconciliation tolerance — used by vendorPaymentStatus /
// poTotalPaid in VendorPaymentsSection to decide when sum(payments) is
// "close enough" to po.totalNet to call paid-in-full. Picks whichever is
// greater (so small POs aren't held to an unreasonable percentage and
// huge POs aren't held to a trivial dollar floor). Revisit after one
// billing cycle of live use.
export var PO_PAYMENT_TOLERANCE_DOLLARS = 5;
export var PO_PAYMENT_TOLERANCE_PCT     = 0.01;

// Reconciles paid principal vs. PO total against the tolerance band.
//   { state: 'paid',     delta: 0 }           strict or within-tolerance match
//   { state: 'overpaid', delta: <positive> }  sum exceeds total beyond tolerance
//   { state: 'partial',  delta: <positive> }  sum short of total beyond tolerance
//   { state: 'unpaid',   delta: total }       sum is zero (no rows)
// Caller decides how to color/label these. The 'unpaid' branch is
// distinct from 'partial' so the UI can render "Awaiting payment" vs.
// "Partially paid · $X.XX remaining" — but both have a non-zero delta.
export function poPaidReconciliation(paid, total) {
  var p = parseFloat(paid) || 0;
  var t = parseFloat(total) || 0;
  if (p === 0) return { state: 'unpaid', delta: t };
  var tolerance = Math.max(PO_PAYMENT_TOLERANCE_DOLLARS, t * PO_PAYMENT_TOLERANCE_PCT);
  var diff = p - t;
  if (Math.abs(diff) <= tolerance) return { state: 'paid',     delta: 0 };
  if (diff > 0)                    return { state: 'overpaid', delta: Math.round(diff * 100) / 100 };
  return                                   { state: 'partial',  delta: Math.round(-diff * 100) / 100 };
}

// Returns vendor_payments rows whose source receipt is missing or
// non-approved — the "orphan banner" trigger. A row is orphaned when:
//   - created_via === 'receipt_cascade' (it came from a receipt
//     originally — manual rows are excluded), AND
//   - either source_receipt_id is null/empty (FK ON DELETE SET NULL
//     fired because the receipt was hard-deleted), OR the referenced
//     receipt is no longer at status 'approved'.
// Caller can layer on additional filters (e.g., dismissed-banner state).
export function findOrphanReceiptPayments(payments, receipts) {
  return (payments || []).filter(function(p) {
    if (!p) return false;
    var via = p.created_via || p.createdVia || '';
    if (via !== 'receipt_cascade') return false;
    var sid = p.source_receipt_id || p.sourceReceiptId || '';
    if (!sid) return true;
    var src = (receipts || []).find(function(r) { return r && r.id === sid; });
    if (!src) return true;
    return (src.status || '') !== 'approved';
  });
}

// Builds the synthetic vendor_payments row for the owner-only "Mark PO
// as Paid in Full" action. Pure function — caller persists via supabase
// insert. Returns null if the note is empty (caller surfaces error).
export function buildOwnerAdjustmentRow(po, paid, total, note) {
  var trimmed = String(note || '').trim();
  if (!trimmed) return null;
  var gap = Math.max((parseFloat(total) || 0) - (parseFloat(paid) || 0), 0);
  if (gap <= 0) return null;
  var poRef = (po && po.po_ref) || '';
  return {
    vendor_name:        (po && po.vendorName) || '',
    project_label:      (po && po.project) || '',
    amount:             Math.round(gap * 100) / 100,
    cc_fee:             0,
    payment_type:       'Adjustment',
    payment_date:       new Date().toISOString().slice(0, 10),
    reference:          [poRef, 'Owner adjustment: ' + trimmed].filter(Boolean).join(' \u00b7 '),
    notes:              trimmed,
    payment_method:     '',
    source_receipt_id:  null,
    po_id:              (po && po.id) || null,
    created_via:        'adjustment',
  };
}

// Kind/id guard for the cascade. Returns null to proceed, or a
// {ok, skipped, reason} object for skip. Per-row idempotency (handling the
// two-row split — principal + cc fee — and partial-rerun recovery) lives
// in findCascadePaymentRow below; the cascade in
// applyVendorReceiptSideEffects calls it before each insert.
//
// vendorPayments is unused by this function but retained in the signature
// so legacy callers (tests, AppContext) don't break. The check it used to
// do (substring tokenization of `reference` to find RECEIPT:<id>) is
// folded into findCascadePaymentRow as a fallback for pre-2026-05-12
// rows that don't have source_receipt_id populated.
export function shouldApplyVendorReceiptCascade(receipt /*, vendorPayments */) {
  if (!receipt) return { ok: false, reason: 'no receipt' };
  // Manual records (Record Payment modal without a file attachment) carry
  // no DB-backed receipts row and signal with `_manual: true`. They proceed
  // through the cascade but source_receipt_id stays null and created_via
  // flips to 'manual' (see buildVendorPaymentFromReceipt below). The id
  // requirement applies only to real receipt-cascade entries.
  if (!receipt.id && !receipt._manual) return { ok: false, reason: 'no receipt' };
  if (VENDOR_RECEIPT_KINDS.indexOf(receipt.kind || '') < 0) {
    return { ok: true, skipped: true, reason: 'not a vendor receipt' };
  }
  return null; // proceed
}

// Per-row idempotency lookup for the receipt → vendor_payments cascade.
// Returns the existing vendor_payments row that matches the (receiptId,
// paymentType, isCcFeeRow) tuple, or null if none exists. The cascade
// inserts only when this returns null.
//
// Match rules:
//   1. New path: vp.source_receipt_id === receiptId AND vp.payment_type
//      === paymentType. For payment_type 'Fee', the isCcFeeRow flag
//      disambiguates the cc-fee row (reference contains 'CC processing
//      fee') from a vendor_fee receipt's principal row (it doesn't).
//   2. Legacy path: vp has no source_receipt_id but its `reference`
//      contains the RECEIPT:<receiptId> token. Pre-2026-05-12 rows
//      packed cc_fee onto the principal row, so any token match implies
//      the whole cascade was already applied — we return that row
//      regardless of paymentType to prevent duplicate inserts.
export function findCascadePaymentRow(vendorPayments, receiptId, paymentType, isCcFeeRow) {
  if (!receiptId) return null;
  var refTag = 'RECEIPT:' + receiptId;
  var list = vendorPayments || [];
  for (var i = 0; i < list.length; i++) {
    var p = list[i];
    if (!p) continue;
    var sourceId = p.source_receipt_id || p.sourceReceiptId || '';
    var pt = p.payment_type || p.paymentType || '';
    // 1. New-style match on (source_receipt_id, payment_type).
    if (sourceId && sourceId === receiptId && pt === paymentType) {
      if (paymentType === 'Fee') {
        var isCc = /cc processing fee/i.test(String(p.reference || ''));
        if (!!isCcFeeRow === isCc) return p;
      } else {
        return p;
      }
      continue;
    }
    // 2. Legacy fallback for rows without source_receipt_id.
    if (!sourceId && p.reference) {
      var tokens = String(p.reference).split(/[\s·]+/);
      for (var j = 0; j < tokens.length; j++) {
        if (tokens[j] === refTag) return p;
      }
    }
  }
  return null;
}

// Maps a vendor receipt kind to the principal vendor_payments.payment_type.
//   vendor_deposit       → 'Deposit'
//   vendor_final         → 'Final'
//   vendor_full_payment  → 'Full'        (in-stock 100%-upfront vendor invoice)
//   vendor_fee           → 'Fee'         (freight / crating / tariff)
export function vendorPaymentTypeForReceiptKind(kind) {
  if (kind === 'vendor_deposit')      return 'Deposit';
  if (kind === 'vendor_final')        return 'Final';
  if (kind === 'vendor_full_payment') return 'Full';
  return 'Fee';
}

// Builds a vendor_payments row (snake_case) from a receipt.
//
// opts.rowPurpose:
//   'principal' (default) — the main payment row. Amount = receipt.totalAmount.
//                           payment_type derived from receipt.kind.
//   'cc_fee'              — the optional second row when the firm paid the
//                           vendor by card and the bank/processor charged a
//                           surcharge. Amount = receipt.ccFee. payment_type
//                           'Fee'. Reference includes 'CC processing fee' so
//                           the buildFinalInvoicePayload roll-up can pick it
//                           out from vendor_fee receipts (which also use
//                           payment_type 'Fee').
//
// opts.paymentMethod — written to the payment_method column. The cascade
//                       passes 'Credit card' when receipt.paidWithCardId is
//                       set, blank otherwise.
//
// All rows from this builder carry:
//   source_receipt_id  = receipt.id  (FK lets the orphan banner detect
//                                     deleted/unapproved receipts)
//   po_id              = po.id       (indexed FK; legacy reference-substring
//                                     joins keep working alongside)
//   created_via        = 'receipt_cascade'
//   cc_fee column      = 0           (legacy cc_fee column carried on the
//                                     principal row pre-2026-05-12; new rows
//                                     put the fee on its own row instead)
export function buildVendorPaymentFromReceipt(receipt, po, opts) {
  opts = opts || {};
  var rowPurpose    = opts.rowPurpose || 'principal';
  var paymentMethod = opts.paymentMethod || '';
  var vendorName    = receipt.vendorName  || (po && po.vendorName) || '';
  // The PO is the AUTHORITATIVE project link for procurement receipts
  // (2026-07-11, the Mehraban cross-project fix): the same vendor sells
  // into multiple projects, and the OCR project guess on the receipt can
  // name the wrong one. When a PO is linked, its project wins — a stale
  // receipt.projectLabel must never route the vendor payment into
  // another project's ledger.
  var projectLabel  = (po && po.project) || receipt.projectLabel || '';
  // Payment date is REQUIRED — every UI caller surfaces a today-prefilled,
  // editable, required date field to the user BEFORE invoking this cascade.
  // The silent today-fallback that previously lived here was removed
  // 2026-06-14 (Part A): writing vendor_payments.payment_date with a
  // value the user never saw is the exact class of bug the change fixes.
  // If this throws, the calling surface is missing a date-required gate.
  if (!receipt.receiptDate || String(receipt.receiptDate).trim() === '') {
    throw new Error(
      'buildVendorPaymentFromReceipt: receipt.receiptDate is required. ' +
      'The calling UI must enforce a non-empty payment date before invoking the cascade.'
    );
  }
  // Date paid wins when recorded (2026-08-10 — Casa dates every invoice
  // with the ORDER date, so receipt_date said May 8 for money wired in
  // August); receipt date remains the fallback for legacy rows.
  var paymentDate   = receipt.paidDate || receipt.receiptDate;
  var poRef         = (po && po.po_ref) || '';
  var poId          = (po && po.id) || receipt.poId || null;
  // Manual rows (Record Payment without a file) carry no DB receipt id.
  // When receipt.id is absent:
  //   - source_receipt_id stays null (uuid column is nullable; FK skips)
  //   - created_via flips to 'manual' so the orphan-receipt filter
  //     (findOrphanReceiptPayments) doesn't classify the row as orphaned
  //   - the RECEIPT:<id> tag is omitted from `reference` (legacy
  //     idempotency parser looks for valid ids only; "RECEIPT:undefined"
  //     would clutter the reference field without serving any consumer)
  var hasReceiptId  = !!receipt.id;
  var refTag        = hasReceiptId ? 'RECEIPT:' + receipt.id : '';
  var createdVia    = hasReceiptId ? 'receipt_cascade' : 'manual';

  if (rowPurpose === 'cc_fee') {
    return {
      vendor_name:        vendorName,
      project_label:      projectLabel,
      amount:             parseFloat(receipt.ccFee) || 0,
      payment_type:       'Fee',
      payment_date:       paymentDate,
      reference:          [poRef, vendorName, 'CC processing fee', refTag].filter(Boolean).join(' · '),
      notes:              '',
      cc_fee:             0,
      source_receipt_id:  receipt.id || null,
      po_id:              poId,
      payment_method:     paymentMethod,
      created_via:        createdVia,
    };
  }

  return {
    vendor_name:        vendorName,
    project_label:      projectLabel,
    amount:             parseFloat(receipt.totalAmount) || 0,
    payment_type:       vendorPaymentTypeForReceiptKind(receipt.kind),
    payment_date:       paymentDate,
    reference:          [poRef, refTag].filter(Boolean).join(' · '),
    notes:              receipt.notes || '',
    cc_fee:             0,
    source_receipt_id:  receipt.id || null,
    po_id:              poId,
    payment_method:     paymentMethod,
    created_via:        createdVia,
  };
}

// ── RUNNING-TAB HELPER ───────────────────────────────────────────────────
// computePoPaidBefore — sum of principal vendor payments against a PO,
// EXCLUDING:
//   1. Fee-type rows (CC fees and other ancillary Fee rows — they aren't
//      part of the running principal owed to the vendor)
//   2. The receipt being edited's own existing principal row (idempotency
//      on edits — without this, re-opening an already-approved receipt
//      double-counts itself in the running tab)
//
// Returns a number rounded to cents. Used by the Receipts tab's running-
// tab summary panel and (going forward) by any other consumer that needs
// "what's been paid to this PO so far" semantics.
//
// vendorPayments: array of vp rows (either camelCase or snake_case keys
//                 tolerated — defensive against direct-from-DB rows)
// poId:           string id of the PO to filter by
// excludeReceiptId: optional receipt id whose own principal vp row should
//                 be excluded (the receipt being edited)
export function computePoPaidBefore(vendorPayments, poId, excludeReceiptId) {
  if (!poId) return 0;
  var poIdStr = String(poId);
  var excludeIdStr = excludeReceiptId ? String(excludeReceiptId) : null;
  var sum = (vendorPayments || []).reduce(function(s, vp) {
    if (!vp) return s;
    var paymentType = vp.paymentType || vp.payment_type || '';
    if (paymentType === 'Fee') return s;
    var vpPoId = vp.poId || vp.po_id;
    if (String(vpPoId) !== poIdStr) return s;
    if (excludeIdStr) {
      var srcId = vp.sourceReceiptId || vp.source_receipt_id;
      if (srcId && String(srcId) === excludeIdStr) return s;
    }
    return s + (parseFloat(vp.amount) || 0);
  }, 0);
  return Math.round(sum * 100) / 100;
}

// isPoFullyPaid — predicate behind the fuchsia "Create Invoice" dot on
// Draft FINs whose PO has been fully paid to the vendor (FIN restructure
// Commit 2, 2026-06-26). Returns:
//
//   {
//     isFullyPaid:   boolean,
//     paidTotal:     number,   // sum of principal payments minus refund/writeoff
//     expectedTotal: number,   // poSubtotalLive over non-cancelled items
//     tolerance:     number,   // max($0.50, expectedTotal * 0.005)
//   }
//
// Math (the contract locked in the prior placeholder, refined per the
// 2026-06-26 spec):
//
//   expectedTotal = poSubtotalLive(po, items) over non-cancelled items
//                   only — items at status='Cancelled' have terminal
//                   credit-ledger entries and are no longer owed.
//
//   paidTotal     = SUM(vp.amount where vp.po_id = po.id)
//                 - SUM(vcl.amount where vcl.sourcePoId = po.id AND
//                       vcl.entryType IN ('refund', 'writeoff'))
//
//   Why every payment_type counts (refined 2026-06-27, 1-PO-=-1-FIN
//   enforcement commit): pre-2026-06-27 this reducer filtered Fee rows
//   out via PRINCIPAL_PAYMENT_TYPES, as defense against the 26HOB-PO-020
//   mis-classification class (principal tagged Fee). The write-side block +
//   read-side lock shipped 2026-06-26 (CLAUDE.md "vendor_payments tagging
//   integrity rule") now prevent new mis-tags at receipt-approve time and
//   block post-hoc kind edits. With those guards in place, Fee rows are
//   canonically freight / crating / tariff / customs — they MUST count
//   toward closing the PO, because expectedTotal (poSubtotalLive) already
//   includes those fee categories. The pre-2026-06-27 filter created a
//   structural gap: a PO whose freight bill arrived as vendor_fee never
//   closed the math, so the "ready to invoice" signal never fired. Audit
//   the production DB via the SQL query in CLAUDE.md if you suspect stale
//   mis-tagged rows; hand-correct via SQL before the predicate can settle.
//
//   Vendor refund handling (locked 2026-06-26):
//     refund + writeoff entries represent cash actually wired back from
//     the vendor (or written off as uncollectible). They subtract from
//     the running paid total — a $200 overpayment that was later
//     refunded should bring the PO back to its expected paid line, not
//     show as "$200 over." cancelled_deposit entries do NOT subtract:
//     they're accounting-only artifacts from an item cancellation (the
//     deposit becomes a CLIENT-side credit; no cash flowed back from
//     the vendor). applied_to_po entries are out of scope — they're
//     about reusing existing credits forward, not adjusting prior
//     payment totals.
//
//   Tolerance band: max($0.50, expectedTotal × 0.005). Same shape as
//   vendorPaymentReceiptMatch (utils.js:8612).
//
//   isFullyPaid = paidTotal >= (expectedTotal - tolerance)
//                 AND expectedTotal > 0
//                 AND paidTotal > 0
//   (the >0 guards return false for the all-cancelled and no-payments
//   edge cases — per the 2026-06-26 spec, those POs are not "ready for
//   invoice" because nothing is actually owed or paid).
//
// Data debt note (the 26HOB-PO-020 class): some vendor_payments rows in
// production may still carry payment_type='Fee' when they should be 'Full' or
// 'Deposit' (100%-upfront-paid-by-CC misclassification). Post-2026-06-27,
// this predicate sums ALL vendor_payments for the PO regardless of
// payment_type — so a mis-tagged row contributes to paidTotal the same as
// a correctly-tagged one. The risk shifts: instead of "fuchsia dot never
// fires," it becomes "fuchsia dot fires on a PO whose freight bill is
// secretly principal and the actual principal is still outstanding."
// Practically: the write-side block + read-side lock prevent new mis-tags;
// the 26HOB-PO-020 row was hand-corrected; run the CLAUDE.md audit SQL if
// production behavior surprises (see "vendor_payments tagging integrity
// rule").
//
// The function tolerates camelCase + snake_case vp row shapes (direct-
// from-DB rows come through as snake_case in some call paths).
export function isPoFullyPaid(po, items, vendorPayments, vendorPaymentItems, vendorCreditLedger) {
  // Default shape returned on every short-circuit so callers can read
  // the fields uniformly without typeof checks.
  var blank = { isFullyPaid: false, paidTotal: 0, expectedTotal: 0, tolerance: 0 };
  if (!po || !po.id) return blank;

  var poId = String(po.id);

  // Resolve PO items minus cancellations. items.status === 'Cancelled'
  // are terminal — their cost basis isn't owed (credit-ledger writes
  // recorded the deposit return separately).
  var poItemIds = Array.isArray(po.items) ? po.items
                : Array.isArray(po.item_ids) ? po.item_ids
                : [];
  if (poItemIds.length === 0) return blank;
  var poItems = (items || []).filter(function(it) {
    if (!it) return false;
    if (poItemIds.indexOf(it.id) < 0) return false;
    return String(it.status || '').trim() !== 'Cancelled';
  });
  if (poItems.length === 0) return blank;

  // expectedTotal — vendor-facing subtotal over the surviving items.
  var expectedTotal = Math.round(poSubtotalLive(po, poItems) * 100) / 100;
  if (expectedTotal <= 0) return blank;

  // paidVendorTotal — sum of ALL vendor_payments rows for this PO.
  // Pre-2026-06-27 this filtered to principal-only via PRINCIPAL_PAYMENT_TYPES;
  // see the header comment for why every payment_type now counts.
  var paidVendorTotal = (vendorPayments || []).reduce(function(s, vp) {
    if (!vp) return s;
    var vpPoId = vp.poId || vp.po_id;
    if (String(vpPoId) !== poId) return s;
    return s + (parseFloat(vp.amount) || 0);
  }, 0);

  // refundOffsets — vendor_credit_ledger entries whose entryType is
  // 'refund' or 'writeoff' AND whose sourcePoId points at this PO.
  // cancelled_deposit + applied_to_po explicitly excluded (see header).
  var refundOffsets = (vendorCreditLedger || []).reduce(function(s, vcl) {
    if (!vcl) return s;
    var entryType = vcl.entryType || vcl.entry_type || '';
    if (entryType !== 'refund' && entryType !== 'writeoff') return s;
    var srcPo = vcl.sourcePoId || vcl.source_po_id;
    if (String(srcPo) !== poId) return s;
    return s + Math.abs(parseFloat(vcl.amount) || 0);
  }, 0);

  var paidTotal = Math.round((paidVendorTotal - refundOffsets) * 100) / 100;
  var tolerance = Math.max(0.50, expectedTotal * 0.005);
  var withinTolerance = paidTotal >= (expectedTotal - tolerance);

  return {
    isFullyPaid: withinTolerance && paidTotal > 0,
    paidTotal:   paidTotal,
    expectedTotal: expectedTotal,
    tolerance:   Math.round(tolerance * 100) / 100,
  };
}

// receiptDeletionBlocked — guard for deleting a receipt (2026-06-12,
// AUDIT_FINANCIAL_2026-06 DR-2). An approved (or pushed) receipt's cascade
// has already written a vendor_payments row and advanced item statuses.
// Deleting the receipt orphans that payment row (FK ON DELETE SET NULL),
// and — worse — re-uploading the same vendor invoice mints a NEW receipt
// id, which defeats the cascade's idempotency key and records a SECOND
// vendor payment for the same real-world wire (the June duplicate-payment
// incident class). Deletion is therefore blocked past approval; reversing
// an approved receipt is an owner-level operation.
// Returns { blocked, reason } — reason is user-facing copy.
export function receiptDeletionBlocked(receipt) {
  var status = String((receipt && receipt.status) || '').trim().toLowerCase();
  if (status === 'approved' || status === 'pushed') {
    return {
      blocked: true,
      reason:
        'This receipt has been approved' +
        ', so its vendor payment is already on the books. Deleting it now would orphan that ' +
        'payment record — and re-uploading the same invoice later would record the payment twice. ' +
        'If this receipt is wrong, contact Cimon to reverse it properly instead of deleting.',
    };
  }
  return { blocked: false, reason: '' };
}

// findPotentialDuplicateVendorPayment — pre-approval duplicate check
// (2026-06-12, AUDIT_FINANCIAL_2026-06 DR-2 secondary). Surfaces an
// existing principal vendor payment that looks like the same real-world
// wire as the receipt about to be approved: same PO, same amount (within
// a cent), payment date within 7 days of the receipt date. Fee rows and
// the receipt's own prior cascade row are excluded. Missing/unparseable
// dates do NOT suppress the match — a PO+amount collision is strong
// enough to warrant the (non-blocking) warning on its own.
// Returns the first matching vendor_payments object, or null.
export function findPotentialDuplicateVendorPayment(receipt, vendorPayments) {
  if (!receipt || !receipt.poId) return null;
  var poIdStr = String(receipt.poId);
  var amt = parseFloat(receipt.totalAmount) || 0;
  if (amt <= 0) return null;
  var rcptDate = receipt.receiptDate ? new Date(receipt.receiptDate) : null;
  var rcptTime = rcptDate && !isNaN(rcptDate.getTime()) ? rcptDate.getTime() : null;
  var SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;
  var match = (vendorPayments || []).find(function(vp) {
    if (!vp) return false;
    var paymentType = vp.paymentType || vp.payment_type || '';
    if (paymentType === 'Fee') return false;
    var vpPoId = vp.poId || vp.po_id;
    if (String(vpPoId) !== poIdStr) return false;
    var srcId = vp.sourceReceiptId || vp.source_receipt_id;
    if (srcId && receipt.id && String(srcId) === String(receipt.id)) return false;
    if (Math.abs((parseFloat(vp.amount) || 0) - amt) > 0.01) return false;
    if (rcptTime != null) {
      var vpDateRaw = vp.paymentDate || vp.payment_date;
      var vpDate = vpDateRaw ? new Date(vpDateRaw) : null;
      var vpTime = vpDate && !isNaN(vpDate.getTime()) ? vpDate.getTime() : null;
      if (vpTime != null && Math.abs(vpTime - rcptTime) > SEVEN_DAYS) return false;
    }
    return true;
  });
  return match || null;
}

// FEE VERIFICATION CASCADE HELPERS — PURE LOGIC (2026-06-10)
// ── Decisions 1 + 2 cascade implementation. Extracted from
// ReceiptsTab.jsx applyFeeChoicesToItemsFees / applyFeeChoicesToPoAdjustments
// for unit-testability. These helpers do NOT touch the DB or React
// state — caller is responsible for persisting the returned patches.

// computeFeeSumForCategory — sum a fee category across items on a PO.
// Custom category sums the items' fees.custom[].amount; other
// categories sum the scalar fees[category]. Returns 0 for empty
// inputs or unknown categories.
export function computeFeeSumForCategory(poItems, category) {
  if (!Array.isArray(poItems) || poItems.length === 0) return 0;
  return poItems.reduce(function(s, it) {
    var fees = (it && it.fees) || {};
    if (category === 'custom') {
      var arr = fees.custom || [];
      return s + arr.reduce(function(t, x) { return t + (parseFloat(x && x.amount) || 0); }, 0);
    }
    return s + (parseFloat(fees[category]) || 0);
  }, 0);
}

// distributeFeeDeltaAcrossItems — Decision 1 even-split distribution
// of a per-category delta across items on a PO. Returns an array of
// { itemId, fees } patches the caller can hand to updateItem.
//
// - Scalar categories (freight/crating/installation/tariff): the
//   per-item portion (delta/N) is ADDED to the item's existing
//   fees[category] value.
// - 'custom' category: a NEW entry is APPENDED to fees.custom[]
//   tagged with the PO ref so the source stays traceable (matching
//   value type — items.fees has heterogenous custom shape).
//
// Returns empty array on empty input, single-item PO with 100% on
// that item, zero delta (no-op), or unknown category.
export function distributeFeeDeltaAcrossItems(poItems, category, delta, poRef) {
  if (!Array.isArray(poItems) || poItems.length === 0) return [];
  var d = parseFloat(delta) || 0;
  if (Math.abs(d) < 0.01) return [];
  var deltaPerItem = Math.round((d / poItems.length) * 100) / 100;
  var allowed = ['freight','crating','installation','tariff','custom'];
  if (allowed.indexOf(category) < 0) return [];
  var ref = poRef || 'PO';
  return poItems.map(function(it) {
    var fees = Object.assign({}, (it && it.fees) || {});
    if (category === 'custom') {
      var customArr = (fees.custom || []).slice();
      customArr.push({
        label: 'Vendor adj. (' + ref + ')',
        amount: deltaPerItem,
      });
      fees.custom = customArr;
    } else {
      var current = parseFloat(fees[category]) || 0;
      fees[category] = Math.round((current + deltaPerItem) * 100) / 100;
    }
    return { itemId: it.id, fees: fees };
  });
}

// buildPoAdjustmentEntry — Decision 2 shape for an entry queued on
// purchase_orders.pending_fin_adjustments. The cascade picks this
// when the bookkeeper chooses "Bill as adj." on a non-zero delta.
// Tariff is pass-through (not taxable); other categories default
// to taxable=true. applyMarkup defaults to true at markupPct=30
// (matches standard item markup); bookkeeper can edit before the
// FIN is generated.
export function buildPoAdjustmentEntry(receipt, category, label, delta, poRef) {
  var d = Math.round((parseFloat(delta) || 0) * 100) / 100;
  return {
    description: 'Vendor ' + (label || category).toLowerCase() + ' adjustment (' + (poRef || 'PO') + ')',
    amount:      d,
    taxable:     category !== 'tariff',
    applyMarkup: true,
    markupPct:   30,
    reason:      'Vendor invoice ' + (label || category).toLowerCase() + ' exceeded PO estimate by '
                 + (d < 0 ? '-' : '') + '$' + Math.abs(d).toFixed(2),
    category:    category || '',
    source_ref:  (receipt && receipt.id) ? ('RECEIPT:' + receipt.id) : '',
    queued_at:   new Date().toISOString(),
  };
}

// detectKindFromContext — given running-tab math, pick the canonical
// receipt kind. Used by the receipt detail UI as a hint + the
// Approve hard guard against the Hewn-style double-count bug (Full
// Payment uploaded on top of an existing Deposit, 2026-06-07).
//
// Rules (all comparisons against a $0.01 epsilon):
//   paid_before == 0  AND this ≈ poTotal  →  vendor_full_payment
//   paid_before == 0  AND this <  poTotal  →  vendor_deposit
//   paid_before >  0  AND paid_after ≈ poTotal  →  vendor_final
//   paid_before >  0  AND paid_after >  poTotal  →  vendor_fee (overage)
//   paid_before == 0  AND this >  poTotal  →  vendor_fee (single overage)
//   paid_before >  0  AND paid_after <  poTotal  →  vendor_deposit (continued partial)
//   poTotal == 0 (no PO total) → null (can't decide; manual)
//
// Returns null when no rule fires (defensive — caller should respect
// user's manual selection in that case).
export function detectKindFromContext(paidBefore, thisAmount, poTotal) {
  var eps = 0.01;
  var b = parseFloat(paidBefore) || 0;
  var t = parseFloat(thisAmount) || 0;
  var p = parseFloat(poTotal)    || 0;
  if (p <= eps) return null;
  var paidAfter = b + t;
  if (b <= eps) {
    if (Math.abs(t - p) < eps) return 'vendor_full_payment';
    if (t > p + eps)            return 'vendor_fee';
    if (t < p - eps && t > eps) return 'vendor_deposit';
    return null;
  }
  if (Math.abs(paidAfter - p) < eps) return 'vendor_final';
  if (paidAfter > p + eps)           return 'vendor_fee';
  return 'vendor_deposit';
}

// kindIsDestructive — flag when the user's manual kind selection
// would create the Hewn-style double-count bug. Returns a short
// message describing the conflict, or null if safe to approve.
//
// The hard rule today: vendor_full_payment with paid_before > 0 is
// the catastrophic case (system adds the full PO total on top of
// existing payments, inflating the running tab). Other mismatches
// (e.g. user picked vendor_final but math says vendor_full_payment)
// don't cause double-counting and are allowed with a soft hint.
export function kindIsDestructive(pickedKind, paidBefore) {
  // Wording refresh 2026-06-27 per copy audit row #5: bookkeeper-friendly
  // language with explicit next-step options. No more "double-count" jargon.
  var b = parseFloat(paidBefore) || 0;
  if (pickedKind === 'vendor_full_payment' && b > 0.01) {
    var amt = '$' + (Math.round(b * 100) / 100).toFixed(2);
    return "Heads up — this PO already has " + amt + " recorded. " +
      "Marking this one as 'Full payment' would record a second full " +
      "payment, which would double-count what you've paid the vendor.\n\n" +
      "If this is the FINAL installment on the goods → pick 'Vendor — Final' instead.\n" +
      "If this is a shipping, freight, customs, or tariff bill → pick 'Vendor — Fee' instead.\n\n" +
      "Or fix the existing payment first (contact Cimon).";
  }
  return null;
}

// ── PHASE 2A CASCADE HELPERS — PURE LOGIC ────────────────────────────────
// Two helpers extracted from applyVendorReceiptSideEffects for unit
// testing. Phase 2A introduced an atomic RPC for parent + per-item child
// writes; these helpers do the pre-RPC validation + allocation math
// without any DB or React-state dependencies.

// resolveCascadeItems — every item_id on the PO must resolve to an item
// in the catalog. Returns either the resolved items array OR a structured
// error object. The cascade aborts with `failure_at='item_lookup'` when
// any items are missing (per Phase 2A's hard-fail-on-missing-item rule —
// a behavioral change from the prior soft-skip pattern).
//
// Returns: { ok: true, items: [resolved...] }
//          | { ok: false, missingItemIds: [...] }
export function resolveCascadeItems(po, items) {
  var poItemIds = (po && po.items) || [];
  if (poItemIds.length === 0) {
    // PO has no items: legitimate state (e.g., manual Record Payment
    // without a PO link). Return ok with empty list; cascade writes
    // parent with no children. NOT a missing-item failure.
    return { ok: true, items: [] };
  }
  var resolved = [];
  var missing = [];
  for (var i = 0; i < poItemIds.length; i++) {
    var found = (items || []).find(function(x) { return x.id === poItemIds[i]; });
    if (found) resolved.push(found);
    else       missing.push(poItemIds[i]);
  }
  if (missing.length > 0) {
    return { ok: false, missingItemIds: missing };
  }
  return { ok: true, items: resolved };
}

// FEE_CATEGORIES_PHASE_5C — the five fee categories carried by
// vendor_payment_items, in cascade-order. Phase 5C (2026-06-24) ships
// the first two (freight, tariff) as primary-read; the remaining
// three (crating, installation, custom) extend the same pattern in
// follow-up phases. Order matches the per-category ordering in
// BILLING_REDESIGN_SPEC.md §8 + the suggested operational-pain order.
export var FEE_CATEGORIES_ALL = ['freight', 'tariff', 'crating', 'installation', 'custom'];
export var FEE_CATEGORIES_PHASE_5C = ['freight', 'tariff'];

// ── Fee vocabulary — the fixed list, and what each one marks up at ────
//
// 2026-09-08 (Cimon's rule): every fee carries a category from a fixed
// list and an explicit markup. No free-text-only fees. Before this, a
// custom fee was a label and an amount, and production held `Tax`,
// `VAT`, `COM Fee`, `custom fee` and `ENGINEERING FEE FOR CANCELED
// ITEMS` side by side — two of them entering sales tax as a custom fee,
// which routes around computeItemTax entirely.
//
// The vocabulary is deliberately small and matches the pricing rules
// already in this file (CLAUDE.md "Financial Architecture"):
//
//   freight / crating / installation  x 1.10  (applyFreightMarkup)
//   storage / travel                  x 1.15  (applyReimbursableMarkup)
//   tariff / salesTax                 x 1.00  pass-through, at cost
//   other                             the entry's own markupPct
//
// `other` is the escape hatch — it still requires an explicit
// markupPct, so a fee's margin is a decision on the record rather than
// a default filling silence. `salesTax` is in the vocabulary so the
// normalizer can NAME the problem when a custom fee is really tax, but
// FEE_CATEGORIES_CUSTOM_ALLOWED excludes it: sales tax belongs to
// computeItemTax and the project tax config, never to a hand-entered
// custom row.
export var FEE_VOCABULARY = ['freight', 'crating', 'installation', 'storage', 'travel', 'tariff', 'salesTax', 'other'];
export var FEE_CATEGORIES_CUSTOM_ALLOWED = ['freight', 'crating', 'installation', 'storage', 'travel', 'tariff', 'other'];

// Canonical markup percentage per category. Returns null for `other`,
// meaning "the entry must say" — callers fall back to the entry's own
// markupPct (customFeeClient's default of 10 remains the last resort,
// for the legacy rows that predate this rule).
export var FEE_CATEGORY_MARKUP_PCT = {
  freight:      10,
  crating:      10,
  installation: 10,
  storage:      15,
  travel:       15,
  tariff:        0,
  salesTax:      0,
  other:      null,
};

// `item` (2026-10-06): the freight-class categories bill at the item's
// own fee rate, not the table's legacy 10. storage / travel / tariff /
// salesTax keep their fixed rates.
export function feeCategoryMarkupPct(category, item) {
  var c = String(category || '').trim();
  if (!Object.prototype.hasOwnProperty.call(FEE_CATEGORY_MARKUP_PCT, c)) return null;
  if (item != null && (c === 'freight' || c === 'crating' || c === 'installation')) return itemFeeMarkupPct(item);
  return FEE_CATEGORY_MARKUP_PCT[c];
}

// isFeeCategory — vocabulary membership test. Kept separate from the
// custom-allowed list so a caller can distinguish "not a fee category
// at all" from "a real category that a custom row may not claim".
export function isFeeCategory(category) {
  return FEE_VOCABULARY.indexOf(String(category || '').trim()) >= 0;
}

// Labels that mean sales tax no matter what they are typed into. Used
// to FLAG, never to reclassify: moving a $119.21 custom fee from
// `other` (10% markup) to `salesTax` (pass-through) would silently drop
// the client price, so the normalizer reports and a human decides.
var TAX_LIKE_LABEL = /^(sales\s*)?tax(es)?$|\bvat\b|\bgst\b|\bhst\b|sales\s*tax/i;

// feeVocabularyIssues — audit one item's fees against the rule. Returns
// an array of { kind, label, detail } — empty when the item complies.
// Kinds:
//   'tax_as_custom'      a custom row whose label means sales tax
//   'unknown_category'   a category outside FEE_VOCABULARY
//   'forbidden_category' salesTax claimed by a custom row
//   'missing_markup'     category 'other' (or absent) with no markupPct
//   'non_numeric_amount' an amount that will not parse to a number
// The buckets the Reconciliation drawer itemizes into. `custom` is its
// own bucket rather than being folded into its rows' categories, because
// Dan reads the label ("SITE SURVEY") not the category.
export var RECON_FEE_CATEGORIES = ['freight', 'crating', 'installation', 'tariff', 'salesTax', 'custom'];

export function emptyFeeCategories() {
  var out = {};
  RECON_FEE_CATEGORIES.forEach(function(k) { out[k] = { cost: 0, client: 0, markup: 0 }; });
  return out;
}

export function feeVocabularyIssues(item) {
  var out = [];
  if (!item) return out;
  var fees = item.fees || {};
  var custom = Array.isArray(fees.custom) ? fees.custom : [];
  custom.forEach(function(c) {
    if (!c || typeof c !== 'object') return;
    var label = String(c.label == null ? '' : c.label).trim();
    var raw   = String(c.amount == null ? '' : c.amount).replace(/[$,]/g, '').trim();
    var amt   = parseFloat(raw);
    var cat   = String(c.category == null ? '' : c.category).trim();
    if (raw !== '' && isNaN(amt)) {
      out.push({ kind: 'non_numeric_amount', label: label, detail: String(c.amount) });
    }
    if (label && TAX_LIKE_LABEL.test(label)) {
      out.push({ kind: 'tax_as_custom', label: label, detail: 'routes around computeItemTax' });
    }
    if (cat && !isFeeCategory(cat)) {
      out.push({ kind: 'unknown_category', label: label, detail: cat });
    }
    if (cat === 'salesTax') {
      out.push({ kind: 'forbidden_category', label: label, detail: 'sales tax is not a custom fee' });
    }
    var pct = parseFloat(c.markupPct);
    if ((!cat || cat === 'other') && isNaN(pct)) {
      out.push({ kind: 'missing_markup', label: label, detail: 'no markupPct and no category to imply one' });
    }
  });
  return out;
}

// distributeFeeShareNetWeighted — single-category net-weighted
// distribution. Mirrors allocateVendorPaymentToItems's principal-
// allocation math: per-item weight = item_net / SUM(item_net); the
// largest item absorbs the rounding residual so SUM(shares) === total
// to the cent. Equal-split fallback when SUM(item_net) === 0. Cancelled
// items already excluded by caller; the helper assumes its `items`
// argument is eligible-only.
//
// Returns an array of { item_id, share } aligned to the items passed
// in (NOT re-sorted — caller chooses ordering). When total <= 0,
// returns an array of zero shares.
export function distributeFeeShareNetWeighted(items, total) {
  var t = parseFloat(total) || 0;
  if (!Array.isArray(items) || items.length === 0) return [];
  if (t <= 0) {
    return items.map(function(it) { return { item_id: it.id, share: 0 }; });
  }
  var nets = items.map(function(it) {
    var netPrice = Math.max(0, parseFloat(it.netPrice || 0));
    var qty      = Math.max(1, parseFloat(it.qty || 1));
    return netPrice * qty;
  });
  var totalNet = nets.reduce(function(s, n) { return s + n; }, 0);
  var shares;
  if (totalNet > 0) {
    shares = nets.map(function(n) {
      return Math.round(t * n / totalNet * 100) / 100;
    });
  } else {
    // Equal split fallback: all items zero-net.
    var perItem = Math.round(t / items.length * 100) / 100;
    shares = items.map(function() { return perItem; });
  }
  // Residual to the largest-net item (or index 0 if all equal).
  var residual = Math.round((t - shares.reduce(function(s, n) { return s + n; }, 0)) * 100) / 100;
  if (residual !== 0) {
    var residualIdx = 0;
    var maxNet = -1;
    for (var i = 0; i < items.length; i++) {
      if (nets[i] > maxNet) { maxNet = nets[i]; residualIdx = i; }
    }
    shares[residualIdx] = Math.round((shares[residualIdx] + residual) * 100) / 100;
  }
  return items.map(function(it, i) { return { item_id: it.id, share: shares[i] }; });
}

// deriveFeeShareTotalsFromVerification — given the receipt's actual
// totals + the panel's fee choices + the PO items' estimate sums,
// returns the per-category fee_share TOTALS to distribute across
// items. The output object's keys are the categories in
// FEE_CATEGORIES_PHASE_5C; values are the total amount to bill the
// client for that category for this receipt's PO.
//
// Interpretation (Phase 5C semantics):
//   fee_share_<cat> represents "the amount to bill the client for
//   this fee category for items on the receipt's PO, after the
//   verification choice." Under the architecturally-coherent
//   interpretation we ship in Phase 5C:
//
//     Match (delta < $0.01, no panel choice):
//                       total = estimate sum  (= actual sum)
//     "Bill as fee":    total = actual sum    (estimate + delta)
//                       items.fees ALSO updated via the existing
//                       applyFeeChoicesToItemsFees cascade so both
//                       primary + secondary read paths agree
//     "Bill as adj.":   total = estimate sum  (delta surfaces on
//                       FIN as a separate Adjustments line)
//     "Don't bill":     total = 0             (firm absorbs every-
//                       thing — strongest interpretation, matches
//                       the spec's literal "fee_share = 0")
//
// NOTE on spec interpretation. BILLING_REDESIGN_SPEC.md §8's literal
// wording said "fee_share = delta" for Bill as fee and "fee_share = 0"
// for Bill as adj. The literal reading makes the read path's
// "primary, when populated" semantics incoherent (the delta isn't
// the full bill; 0 drops the estimate baseline). The interpretation
// above keeps fee_share as the authoritative "amount to bill" and
// is documented in ARCHITECTURE.md §7's Phase 5C status note. The
// shadow comparison in buildFinalInvoicePayload catches drift
// between fee_share and items.fees during the rollout window.
//
// opts:
//   feeBreakdown   — receipt.feeBreakdown (actuals): {freight, tariff, ...}
//   feeChoices     — verification panel choices: {freight: 'bill_as_fee' | 'bill_as_adj' | 'dont_bill' | undefined}
//   estimateSums   — per-category estimate sums on the PO: {freight, tariff, ...}
//   categories     — defaults to FEE_CATEGORIES_PHASE_5C
//
// Returns: { freight: <total>, tariff: <total>, ... } with only the
// categories in `categories`.
export function deriveFeeShareTotalsFromVerification(opts) {
  var fb = (opts && opts.feeBreakdown) || {};
  var fc = (opts && opts.feeChoices) || {};
  var es = (opts && opts.estimateSums) || {};
  var cats = (opts && Array.isArray(opts.categories)) ? opts.categories : FEE_CATEGORIES_PHASE_5C;
  var out = {};
  cats.forEach(function(cat) {
    var actual = parseFloat(fb[cat]) || 0;
    var estimate = parseFloat(es[cat]) || 0;
    var delta = Math.round((actual - estimate) * 100) / 100;
    // No delta — match-estimate auto-state; capture what was paid.
    if (Math.abs(delta) < 0.01) { out[cat] = estimate; return; }
    var choice = fc[cat];
    if (choice === 'bill_as_fee')  { out[cat] = actual;   return; }
    if (choice === 'bill_as_adj')  { out[cat] = estimate; return; }
    if (choice === 'dont_bill')    { out[cat] = 0;        return; }
    // Choice missing despite a non-zero delta — defensive fallback.
    // Treat as match (estimate billed); the receipt-approve gate
    // should have blocked this state via feeChoicesUnmet, so seeing
    // it at runtime indicates either an edge case or a stale UI.
    out[cat] = estimate;
  });
  return out;
}

// computeFeeSharePatchesForReceipt — combines the two helpers above
// into the final per-item per-category patch list ready for UPDATE
// on the vendor_payment_items rows tied to this receipt's principal
// payment. Caller takes responsibility for the DB UPDATE.
//
// opts:
//   receipt         — the receipt row (reads feeBreakdown)
//   feeChoices      — verification panel choices
//   poItems         — eligible items on the PO (Cancelled already filtered)
//   categories      — defaults to FEE_CATEGORIES_PHASE_5C
//
// Returns array of { item_id, fee_share_<cat>: <share>, ... } with
// only the requested categories populated.
export function computeFeeSharePatchesForReceipt(opts) {
  var receipt = (opts && opts.receipt) || {};
  var feeChoices = (opts && opts.feeChoices) || {};
  var poItems = (opts && Array.isArray(opts.poItems)) ? opts.poItems : [];
  var cats = (opts && Array.isArray(opts.categories)) ? opts.categories : FEE_CATEGORIES_PHASE_5C;
  // Compute estimate sums by walking PO items' fees once per category.
  var estimateSums = {};
  cats.forEach(function(cat) {
    estimateSums[cat] = poItems.reduce(function(sum, it) {
      var fees = (it && it.fees) || {};
      if (cat === 'custom') {
        return sum + ((fees.custom || []).reduce(function(t, c) {
          return t + (parseFloat(c && c.amount) || 0);
        }, 0));
      }
      return sum + (parseFloat(fees[cat]) || 0);
    }, 0);
  });
  var totals = deriveFeeShareTotalsFromVerification({
    feeBreakdown: receipt.feeBreakdown,
    feeChoices: feeChoices,
    estimateSums: estimateSums,
    categories: cats,
  });
  // Build per-item patches. Each item gets one row in the output, with
  // fee_share_<cat> populated for every requested category.
  var patches = poItems.map(function(it) {
    var p = { item_id: it.id };
    cats.forEach(function(cat) { p['fee_share_' + cat] = 0; });
    return p;
  });
  cats.forEach(function(cat) {
    var distributed = distributeFeeShareNetWeighted(poItems, totals[cat]);
    distributed.forEach(function(d, i) {
      patches[i]['fee_share_' + cat] = d.share;
    });
  });
  return patches;
}

// sumFeeShareForCategory — read-path helper. Sums
// vendor_payment_items.fee_share_<cat> across vpi rows whose item_id
// belongs to the given items (typically a FIN's items). Empty array
// or zero sum returns 0; caller decides whether to fall back to
// items.fees.
//
// NOTE: a non-zero return value means the cascade DID populate this
// category for at least one item on this PO. A zero return value is
// ambiguous — could be "cascade explicitly chose zero (Don't bill)"
// or "cascade never ran (pre-Phase-5C data)." The read path resolves
// this ambiguity by reading items.fees in parallel and comparing;
// see buildFinalInvoicePayload's shadow-mode block.
export function sumFeeShareForCategory(category, items, vendorPaymentItems) {
  if (!category) return 0;
  if (!Array.isArray(items) || items.length === 0) return 0;
  if (!Array.isArray(vendorPaymentItems) || vendorPaymentItems.length === 0) return 0;
  var col = 'fee_share_' + category;
  var itemIdSet = {};
  items.forEach(function(it) { if (it && it.id) itemIdSet[String(it.id)] = true; });
  return vendorPaymentItems.reduce(function(s, vpi) {
    if (!vpi) return s;
    var iid = vpi.item_id != null ? vpi.item_id : vpi.itemId;
    if (!itemIdSet[String(iid)]) return s;
    var v = parseFloat(vpi[col] != null ? vpi[col] : vpi[toCamel(col)]) || 0;
    return s + v;
  }, 0);
}

// Tiny helper for the snake_case → camelCase fallback inside
// sumFeeShareForCategory. The DB returns snake_case; some local-state
// mappers may camelCase. Keep both paths working.
function toCamel(snake) {
  return String(snake || '').replace(/_([a-z])/g, function(_, c) { return c.toUpperCase(); });
}

// allocateVendorPaymentToItems — net-weighted split of the parent
// payment's principal + cc_fee across the items on the PO. Mirrors the
// Phase 1 backfill SQL logic (supabase/migrations/20260601_option_a_phase_1_backfill.sql);
// both must stay in sync if either changes.
//
// Inputs (opts):
//   amount           — parent vendor_payments.amount to allocate
//   ccFee            — the resolved CC fee amount to allocate as cc_fee_share
//                      across principal children (Option P: cc_fee_share
//                      on principal children carries the CC fee allocation
//                      derived from the separate CC fee vendor_payments
//                      row's amount; the principal row's cc_fee column
//                      itself stays 0 post-2026-05-12)
//   items            — array of { id, netPrice, qty, status } items on the PO
//   allocationMethod — defaults to 'cascade_net_weighted'
//
// Output: array of vendor_payment_items row shapes ready for INSERT.
//         Empty array when no eligible items (PO empty / all Cancelled).
//
// Allocation rules:
//   - Cancelled items excluded.
//   - item_net = max(0, netPrice) × max(1, qty).
//   - weight   = item_net / SUM(item_net) (even split if SUM=0).
//   - Largest item (by item_net) absorbs the rounding remainder so
//     SUM(children.amount_share) = parent.amount exactly (within 0.01)
//     and SUM(children.cc_fee_share) = ccFee exactly.
//   - Sort tiebreak on id asc for deterministic residual placement.
//   - fee_share_* columns all set to 0 (vendor_payments doesn't carry
//     per-fee detail at parent level; per-fee allocation is deferred
//     to Phase 2.5 when allocation_breakdown JSONB shape is locked).
export function allocateVendorPaymentToItems(opts) {
  var amount = parseFloat(opts && opts.amount) || 0;
  var ccFee  = parseFloat(opts && opts.ccFee)  || 0;
  var allocationMethod = (opts && opts.allocationMethod) || 'cascade_net_weighted';

  var eligible = ((opts && opts.items) || []).filter(function(it) {
    return it && (it.status || '') !== 'Cancelled';
  });
  if (eligible.length === 0) return [];

  var withNets = eligible.map(function(it) {
    var netPrice = Math.max(0, parseFloat(it.netPrice || 0));
    var qty      = Math.max(1, parseFloat(it.qty || 1));
    return { id: it.id, item_net: netPrice * qty };
  });
  var totalNet = withNets.reduce(function(s, x) { return s + x.item_net; }, 0);

  // Sort by item_net desc, tie-break on id asc. Largest item is index 0
  // and absorbs the rounding remainder.
  withNets.sort(function(a, b) {
    if (b.item_net !== a.item_net) return b.item_net - a.item_net;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });

  function naiveShare(total, weight) {
    if (totalNet > 0) return Math.round(total * weight / totalNet * 100) / 100;
    return Math.round(total / withNets.length * 100) / 100;
  }

  var amountShares = withNets.map(function(x) { return naiveShare(amount, x.item_net); });
  var ccFeeShares  = withNets.map(function(x) { return naiveShare(ccFee,  x.item_net); });

  // Residual = parent total − sum(naive shares). Applied to index 0
  // (the largest item) so SUM(children) = parent exactly.
  var amountResidual = Math.round((amount - amountShares.reduce(function(s, n) { return s + n; }, 0)) * 100) / 100;
  var ccFeeResidual  = Math.round((ccFee  - ccFeeShares.reduce(function(s, n) { return s + n; }, 0))  * 100) / 100;
  amountShares[0] = Math.round((amountShares[0] + amountResidual) * 100) / 100;
  ccFeeShares[0]  = Math.round((ccFeeShares[0]  + ccFeeResidual)  * 100) / 100;

  return withNets.map(function(x, i) {
    return {
      item_id:                x.id,
      amount_share:           amountShares[i],
      cc_fee_share:           ccFeeShares[i],
      fee_share_freight:      0,
      fee_share_crating:      0,
      fee_share_installation: 0,
      fee_share_custom:       0,
      fee_share_tariff:       0,
      allocation_method:      allocationMethod,
      notes:                  '',
    };
  });
}

// allocateVendorPaymentEvenly — equal split of the payment's principal +
// cc_fee across the items on the PO. Mirrors allocateVendorPaymentToItems
// but ignores netPrice/qty entirely; every eligible (non-Cancelled) item
// gets the same share. Largest item by id (descending) absorbs the
// rounding residual so SUM(children) = parent exactly.
//
// Used by Phase 2B's Record Payment modal "Even split" button — for
// cases where net-weighted allocation isn't right (e.g., a freight bill
// that the user wants to split evenly across two of three items because
// only those two were in the shipment, even though the netPrice ratio
// would suggest a different split).
//
// Inputs (opts):
//   amount           — parent vendor_payments.amount to allocate
//   ccFee            — CC fee total to allocate across children
//   items            — array of { id, status } items on the PO
//   allocationMethod — defaults to 'manual_override' since even-split
//                      is, by definition, a deliberate user choice
//
// Output: array of vendor_payment_items row shapes ready for INSERT.
//         Empty array when no eligible items.
export function allocateVendorPaymentEvenly(opts) {
  var amount = parseFloat(opts && opts.amount) || 0;
  var ccFee  = parseFloat(opts && opts.ccFee)  || 0;
  var allocationMethod = (opts && opts.allocationMethod) || 'manual_override';

  var eligible = ((opts && opts.items) || []).filter(function(it) {
    return it && (it.status || '') !== 'Cancelled';
  });
  if (eligible.length === 0) return [];

  // Deterministic order — id desc so the "largest" residual target is
  // stable across runs. Mirrors allocateVendorPaymentToItems's sort
  // direction (highest first) so residual placement is consistent.
  var sorted = eligible.slice().sort(function(a, b) {
    return a.id < b.id ? 1 : a.id > b.id ? -1 : 0;
  });

  var n = sorted.length;
  var perAmount = Math.round((amount / n) * 100) / 100;
  var perCcFee  = Math.round((ccFee  / n) * 100) / 100;
  var amountShares = sorted.map(function() { return perAmount; });
  var ccFeeShares  = sorted.map(function() { return perCcFee; });

  // Residual on index 0 (highest id, deterministic).
  var amountResidual = Math.round((amount - perAmount * n) * 100) / 100;
  var ccFeeResidual  = Math.round((ccFee  - perCcFee  * n) * 100) / 100;
  amountShares[0] = Math.round((amountShares[0] + amountResidual) * 100) / 100;
  ccFeeShares[0]  = Math.round((ccFeeShares[0]  + ccFeeResidual)  * 100) / 100;

  return sorted.map(function(x, i) {
    return {
      item_id:                x.id,
      amount_share:           amountShares[i],
      cc_fee_share:           ccFeeShares[i],
      fee_share_freight:      0,
      fee_share_crating:      0,
      fee_share_installation: 0,
      fee_share_custom:       0,
      fee_share_tariff:       0,
      allocation_method:      allocationMethod,
      notes:                  '',
    };
  });
}

// ── VENDOR_PAYMENTS row mappers ──────────────────────────────────────────
// vendor_payments rows historically were consumed raw (snake_case) because
// buildVendorPaymentFromReceipt returns a snake_case row for direct insert.
// Phase 5 needs camelCase mappers so the Bill push can write back qb_bill_id
// + qb_pushed_at + qb_error after a successful push (and so the bookkeeper
// worklist can read them via the same shape the rest of the app uses).
export function rowToVendorPayment(row) {
  if (!row) return null;
  return {
    id:               row.id,
    vendorName:       row.vendor_name        || '',
    projectLabel:     row.project_label      || '',
    amount:           parseFloat(row.amount) || 0,
    ccFee:            parseFloat(row.cc_fee) || 0,
    paymentType:      row.payment_type       || '',
    paymentDate:      row.payment_date       || '',
    reference:        row.reference          || '',
    notes:            row.notes              || '',
    paymentMethod:    row.payment_method     || '',
    sourceReceiptId:  row.source_receipt_id  || '',
    poId:             row.po_id              || '',
    // The client invoice that recovered this payment's card fee (2026-09-07).
    // '' = not yet billed, so the fee is available to the next FIN.
    billedInvoiceId:  row.billed_invoice_id  || '',
    createdVia:       row.created_via        || 'manual',
    qbBillId:         row.qb_bill_id         || '',
    qbPushedAt:       row.qb_pushed_at       || '',
    qbError:          row.qb_error           || '',
    createdAt:        row.created_at         || '',
    updatedAt:        row.updated_at         || '',
  };
}

// Description shown on the client's billing summary cover for a reimbursable
// receipt row. Reads LIVE from the current receipt at render time — the
// selection state on billing_summaries.selected_receipt_ids stores ONLY
// ids, never descriptions or amounts. So editing a receipt's notes on
// the Receipts tab AFTER it was selected for a summary updates the next
// render of that summary. One truth: the receipt.
//
// Fallback chain: notes → category → vendor_name → '(no description)'.
export function receiptSummaryDescription(receipt) {
  var r = receipt || {};
  var notes = (r.notes || '').trim();
  if (notes) return notes;
  var category = (r.category || '').trim();
  if (category) return category;
  var vendor = (r.vendorName || r.vendor_name || '').trim();
  if (vendor) return vendor;
  return '(no description)';
}

// ── Absorbed receipts (2026-08-23 billing simplification) ─────────────────
// A project_reimbursable receipt the firm decided to eat instead of
// billing (samples, tiny charges that read as nickel-and-diming). It
// stays status='approved' — it's a real firm cost, visible internally —
// but it never bills: excluded from the summary receipt picker AND from
// both cover/draw sum paths (buildSummaryReceiptLines +
// calcBillingSummaryAmount — the two must apply the SAME predicate or
// the bsSubtotal lockstep breaks). The absorb action is gated in
// ReceiptsTab to receipts with no billed_on_summary_id, so a sent
// summary can never reference an absorbed receipt and historical
// statements never reinterpret.
export function receiptIsAbsorbed(r) {
  return !!(r && (r.absorbedAt || r.absorbed_at));
}

// Build the cover-PDF receipt-line array for a billing summary's
// selected_receipt_ids. Looks each id up against the current receipts
// state — descriptions and amounts are NEVER snapshotted on the summary
// row. Receipts deleted between selection and render silently drop out
// (defensive .filter(Boolean)); absorbed receipts drop out the same way
// (a stale ticked id on a draft stops billing the moment it's absorbed).
//
// Returns an array of { receiptId, date, vendor, description, cost, amount }
// where `amount` is the marked-up total the client sees (cost × 1.15).
export function buildSummaryReceiptLines(selectedIds, allReceipts) {
  var ids = Array.isArray(selectedIds) ? selectedIds : [];
  var rcpts = Array.isArray(allReceipts) ? allReceipts : [];
  return ids
    .map(function(id) {
      return rcpts.find(function(r) { return r && r.id === id; }) || null;
    })
    .filter(Boolean)
    .filter(function(r) { return !receiptIsAbsorbed(r); })
    .map(function(r) {
      var cost = Math.round((parseFloat(r.totalAmount) || 0) * 100) / 100;
      return {
        receiptId:   r.id,
        date:        r.receiptDate || '',
        vendor:      r.vendorName || '',
        description: receiptSummaryDescription(r),
        cost:        cost,
        markupPct:   receiptMarkupPct(r),
        amount:      applyReimbursableMarkup(cost, receiptMarkupPct(r)),
      };
    });
}

// Computes the per-item patches the cascade should apply.
//   vendor_deposit / vendor_full_payment: set orderPlacedDate (lead-time clock)
//                   if not already set, and advance Deposit Paid → PO Issued.
//                   Both kinds represent "the firm has paid the vendor enough
//                   to start the order" — full_payment just happens to also
//                   be the final payment.
//   vendor_final / vendor_fee: no item-status changes.
// Returns an array of { id, patch } objects.
// ── Receipts linked to an item (derived, never attached) ──────────────────
// (2026-07-12, Cimon: "automatically attach receipts to items" — the
// receipt file already exists once in storage with links to its PO and
// items, so the item's documents panel DERIVES its receipts instead of
// duplicating uploads. One source of truth: the receipt row.)
//
// Linkage, strongest first:
//   1. receipt.itemId — a direct item link (fee receipts can carry one)
//   2. per-item payment shares — the receipt's vendor_payments children
//      (vendor_payment_items) name exactly which items the money split
//      across, i.e. the receipt wizard's "which items" checkboxes. When
//      shares exist for a receipt, they are AUTHORITATIVE: an item not in
//      them was deliberately unchecked (shipping in a later batch), so
//      the PO fallback below must NOT re-include it.
//   3. PO fallback — no narrowing recorded: every item on the receipt's
//      PO is related.
// Rejected receipts never show. vendorPayments / vendorPaymentItems come
// through in their RAW AppContext shapes (snake_case rows).
export function receiptsForItem(itemId, refs) {
  var r = refs || {};
  var receipts = r.receipts || [];
  var vendorPayments = r.vendorPayments || [];
  var vendorPaymentItems = r.vendorPaymentItems || [];
  var purchaseOrders = r.purchaseOrders || [];
  var id = String(itemId || '');
  if (!id) return [];

  var vpById = {};
  vendorPayments.forEach(function(vp) { if (vp && vp.id) vpById[String(vp.id)] = vp; });
  var itemsByReceipt = {};
  vendorPaymentItems.forEach(function(ch) {
    if (!ch) return;
    var vp = vpById[String(ch.vendor_payment_id || ch.vendorPaymentId || '')];
    var rid = vp && (vp.source_receipt_id || vp.sourceReceiptId);
    if (!rid) return;
    var key = String(rid);
    if (!itemsByReceipt[key]) itemsByReceipt[key] = {};
    itemsByReceipt[key][String(ch.item_id || ch.itemId || '')] = true;
  });

  var poItemsById = {};
  purchaseOrders.forEach(function(po) {
    if (!po || !po.id) return;
    var ids = Array.isArray(po.items) ? po.items : (Array.isArray(po.item_ids) ? po.item_ids : []);
    poItemsById[String(po.id)] = ids.map(String);
  });

  return receipts
    .filter(function(rc) {
      if (!rc || rc.status === 'rejected') return false;
      // A direct item link is itself a narrowing: a fee receipt pinned
      // to one item belongs to THAT item only — never spread to PO
      // siblings via the fallback.
      if (rc.itemId) return String(rc.itemId) === id;
      var shares = itemsByReceipt[String(rc.id)];
      if (shares) return !!shares[id];
      var poItems = rc.poId ? poItemsById[String(rc.poId)] : null;
      return !!(poItems && poItems.indexOf(id) >= 0);
    })
    .slice()
    .sort(function(a, b) { return String(b.receiptDate || '').localeCompare(String(a.receiptDate || '')); });
}

// The individual FILES a receipt carries — one receipt can hold several
// (the vendor's invoice plus the card-transaction printout, Phase 4B
// multi-file attachments). When attachment rows exist they are the
// complete file list (the backfill made the primary an attachment too);
// otherwise fall back to the receipt row's own storage file. Never both —
// that would double-tile the same PDF.
export function receiptFileEntries(receipt, receiptAttachments) {
  if (!receipt) return [];
  var atts = (receiptAttachments || []).filter(function(a) {
    return a && String(a.receiptId || '') === String(receipt.id) && a.storagePath;
  });
  if (atts.length > 0) {
    return atts.map(function(a) {
      return {
        storagePath: a.storagePath,
        filename: a.filename || receipt.filename || 'receipt',
        mimeType: a.mimeType || '',
        role: a.role || 'invoice',
        isPrimary: !!a.isPrimary,
      };
    });
  }
  if (!receipt.storagePath) return [];
  return [{
    storagePath: receipt.storagePath,
    filename: receipt.filename || 'receipt',
    mimeType: receipt.mimeType || '',
    role: 'invoice',
    isPrimary: true,
  }];
}

// PO auto-confirm on principal receipt approval (2026-07-12 — Cimon:
// "I just recorded a receipt and naturally the order is confirmed with
// the vendor"). Same reasoning as the 2026-07-05 item rule: money to the
// vendor commits the order, and the vendor's invoice IS their
// confirmation. Deliberately conservative:
//   - principal receipts only (deposit / full payment / final) — a
//     fee-only receipt (freight bill) confirms nothing
//   - forward-only, ONE step: Issued → Confirmed. Draft POs are never
//     touched (the Issue step carries its own ceremony — the quote
//     check modal, the item cascade, the PDF); Confirmed and Complete
//     never move.
// Returns the new status string, or null when nothing should change.
export function poStatusPatchForReceiptCascade(receiptKind, po) {
  var PRINCIPAL = { vendor_deposit: true, vendor_full_payment: true, vendor_final: true };
  if (!po || !PRINCIPAL[receiptKind]) return null;
  return po.status === 'Issued' ? 'Confirmed' : null;
}

export function itemPatchesForReceiptCascade(receipt, po, items) {
  if (!receipt || !po) return [];
  // PRINCIPAL receipts only — money to the vendor commits the order
  // (Cimon 2026-07-05: "once we upload a receipt showing we paid for an
  // item on a PO that item should be marked order confirmed"). Fee-only
  // receipts (freight bills etc.) never advance items — paying freight
  // is not the vendor accepting an order. vendor_final joined the set
  // 2026-07-05: paying the final wire is principal money too (pre-fix
  // only deposit/full fired; a final against a never-deposited PO left
  // items unstamped).
  var principal = receipt.kind === 'vendor_deposit'
    || receipt.kind === 'vendor_full_payment'
    || receipt.kind === 'vendor_final';
  if (!principal) return [];
  var poItemIds = Array.isArray(po.items) ? po.items : [];
  var poItems = (items || []).filter(function(it) { return poItemIds.indexOf(it.id) >= 0; });
  var orderDate = receipt.receiptDate || new Date().toISOString().slice(0, 10);
  var patches = [];
  poItems.forEach(function(it) {
    var patch = {};
    if (!it.orderPlacedDate) patch.orderPlacedDate = orderDate;
    // Forward-only advance to Order Confirmed via an explicit
    // from-status whitelist: Deposit Paid / PO Issued step up;
    // Received / Installed / anything later never walks back; items
    // still below Deposit Paid (owner-bypass orders where the client
    // hasn't paid) are left alone — the Deposit Paid stage tracks
    // CLIENT money and must not be implied by a vendor-side event.
    // The vendor's invoice is the confirmation; the firm's payment
    // closes it ("Order Confirmed" wording confirmed by Cimon over
    // "Order Placed" — PO Issued already means "we placed it").
    if (it.status === 'Deposit Paid' || it.status === 'PO Issued') patch.status = 'Ordered';
    if (Object.keys(patch).length > 0) patches.push({ id: it.id, patch: patch });
  });
  return patches;
}

// Estimated ship date for an item — orderPlacedDate + leadWeeks×7 days.
//
// Canonical computation used by every consumer that needs to surface
// "when is the vendor expected to ship this item?" The cascade
// (itemPatchesForReceiptCascade above) writes the orderPlacedDate;
// this helper reads it together with lead-time data and projects
// forward. Returns an ISO YYYY-MM-DD string, or null when any
// input is missing/invalid (consumers render '—' or similar).
//
// Lead-time input precedence:
//   item.leadWeeks (numeric) — preferred, set by ItemForm + DB column.
//   item.leadTime  (text)    — fallback. Parses the LAST numeric
//                              token so "6-8 weeks" → 8 (worst-case
//                              for planning); "10 weeks" → 10.
//   Anything else            — returns null.
//
// Timezone handling: parses the orderPlacedDate as a LOCAL calendar
// date (NOT UTC midnight) to avoid the off-by-one shift the legacy
// addWeeks() pattern in VendorPaymentsSection introduced. The legacy
// pattern was `new Date(iso)` which parses ISO date strings as UTC
// midnight — equivalent to previous-day 16:00 PST — so subsequent
// `setDate` arithmetic operated from the wrong start date, producing
// results one day earlier than the user-visible Mar 26 / Apr 26 / etc.
// math would suggest. This helper fixes that by treating the stored
// date as a pure local-calendar value: "2026-02-01" means Feb 1,
// not "Feb 1 UTC ≈ Jan 31 evening PST." Output is also local-date
// components (not toISOString) so consumers re-parsing the result
// via `new Date(iso + 'T00:00:00')` round-trip without TZ drift.
//
// item: { orderPlacedDate, leadWeeks?, leadTime? }
// Returns: 'YYYY-MM-DD' or null.
export function estimatedShipDate(item) {
  if (!item) return null;
  var od = String(item.orderPlacedDate || '').trim();
  if (!od) return null;

  // Lead weeks: numeric first, text-regex fallback (mirrors leadWeeksOf
  // in VendorPaymentsSection.jsx so PO list view + per-item helper
  // agree on the same parse).
  var wks = 0;
  if (item.leadWeeks) {
    wks = parseInt(item.leadWeeks, 10) || 0;
  } else {
    var lt = String(item.leadTime || '').toLowerCase();
    var m = lt.match(/(\d+)\s*-?\s*(\d+)?/);
    if (m) wks = parseInt(m[2] || m[1], 10) || 0;
  }
  if (!wks || wks <= 0) return null;

  try {
    // Parse YYYY-MM-DD as a local calendar date (constructor with
    // year/monthIdx/day components, NOT new Date(string) which would
    // treat it as UTC midnight).
    var d;
    var isoMatch = od.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
      d = new Date(parseInt(isoMatch[1], 10),
                   parseInt(isoMatch[2], 10) - 1,
                   parseInt(isoMatch[3], 10));
    } else {
      // Fallback for non-ISO formats (e.g., the legacy PurchaseOrderGenerator
      // wrote "5/28/2026" — Date can parse those as local).
      d = new Date(od);
    }
    if (isNaN(d.getTime())) return null;
    d.setDate(d.getDate() + wks * 7);
    // Return local-date components, not toISOString (which would be UTC).
    function pad2(n) { return String(n).padStart(2, '0'); }
    return d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-' + pad2(d.getDate());
  } catch (e) {
    return null;
  }
}

// Order Status string for the workroom schedule's right-most column
// (2026-06-29 — Cimon). Reads:
//   - 'Delivered'  when status is Received / Installed / Complete
//   - 'ETA: <date>' computed from orderPlacedDate + leadWeeks*7
//   - '\u2014' (em-dash) when neither path resolves
// estimatedShipDate already handles the parse + tz-safe date math.
export function formatOrderStatus(item) {
  if (!item) return '';
  var status = canonicalItemStatus(item.status);
  if (status === 'At Receiver' || status === 'Delivered' || status === 'Installed' || status === 'Complete') {
    return 'Delivered';
  }
  var iso = estimatedShipDate(item);
  if (!iso) return '\u2014';
  var m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return '\u2014';
  try {
    var d = new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
    if (isNaN(d.getTime())) return '\u2014';
    return 'ETA: ' + d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch (e) {
    return '\u2014';
  }
}

// ── DELIVERY CALENDAR (Calendar page, 2026-07-12) ────────────────────────────
//
// Pure derivation layer for the Calendar page (tab 26, /calendar) — the
// lead-time / expected-delivery view that lives alongside the Tracker.
// Everything here is Date.now()-free: callers pass `todayIso` so tests
// are deterministic. All date strings are local-calendar YYYY-MM-DD
// (same convention as estimatedShipDate above — never UTC-midnight).

// Parse YYYY-MM-DD as a LOCAL calendar date. Returns Date or null.
function parseLocalIso(iso) {
  var m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  var d = new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
  return isNaN(d.getTime()) ? null : d;
}

function toIso(d) {
  function pad2(n) { return String(n).padStart(2, '0'); }
  return d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-' + pad2(d.getDate());
}

// Whole days from a → b (positive when b is after a). Null on bad input.
export function daysBetweenIso(a, b) {
  var da = parseLocalIso(a), db = parseLocalIso(b);
  if (!da || !db) return null;
  return Math.round((db.getTime() - da.getTime()) / 86400000);
}

export function addDaysIso(iso, n) {
  var d = parseLocalIso(iso);
  if (!d) return null;
  d.setDate(d.getDate() + (n || 0));
  return toIso(d);
}

// Monday of the ISO week containing `iso`.
export function startOfIsoWeek(iso) {
  var d = parseLocalIso(iso);
  if (!d) return null;
  var dayNum = (d.getDay() + 6) % 7; // Mon=0 … Sun=6
  d.setDate(d.getDate() - dayNum);
  return toIso(d);
}

// ISO-8601 week number (the week containing the year's first Thursday
// is week 1). Matches the W27–W31 labels in the calendar month grid.
export function isoWeekNumber(iso) {
  var d = parseLocalIso(iso);
  if (!d) return null;
  var dayNum = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - dayNum + 3); // Thursday of this week
  var firstThursday = new Date(d.getFullYear(), 0, 4);
  var ftDayNum = (firstThursday.getDay() + 6) % 7;
  firstThursday.setDate(firstThursday.getDate() - ftDayNum + 3);
  return 1 + Math.round((d.getTime() - firstThursday.getTime()) / (7 * 86400000));
}

// Monday-start month grid. Returns an array of week rows covering the
// month; each row is { isoWeek, days: [{ iso, day, inMonth, weekend }] }.
// month is 1-12.
export function calendarMonthGrid(year, month) {
  var first = new Date(year, month - 1, 1);
  if (isNaN(first.getTime())) return [];
  var cursorIso = startOfIsoWeek(toIso(first));
  var lastIso = toIso(new Date(year, month, 0)); // last day of month
  var weeks = [];
  while (cursorIso <= lastIso) {
    var days = [];
    for (var i = 0; i < 7; i++) {
      var iso = addDaysIso(cursorIso, i);
      var d = parseLocalIso(iso);
      days.push({
        iso: iso,
        day: d.getDate(),
        inMonth: d.getMonth() === month - 1 && d.getFullYear() === year,
        weekend: d.getDay() === 0 || d.getDay() === 6,
      });
    }
    weeks.push({ isoWeek: isoWeekNumber(cursorIso), days: days });
    cursorIso = addDaysIso(cursorIso, 7);
  }
  return weeks;
}

// Statuses that appear on the Calendar. "Ordered" = vendor money is out
// and the item is somewhere between the factory and the receiver;
// "arrived" = physically landed (plotted on receivedDate when known).
export var CAL_ORDERED_STATUSES = ['PO Issued', 'Ordered', 'Shipped'];
export var CAL_ARRIVED_STATUSES = ['At Receiver', 'Delivered', 'Installed', 'Complete'];

// Physical-location axis for the calendar chips' icon (independent of
// the timing/color axis — the two never mix). The 'transit' and
// 'installScheduled' states went LIVE 2026-07-28 with the Shipped +
// Delivered statuses — 'installScheduled' is Cimon's lighting example:
// off the receiver, on site, awaiting planned installation.
export function itemLocationState(item) {
  var s = normalizeItemStatus((item && item.status), item && item.receivedDate);
  if (s === 'Installed' || s === 'Complete') return 'installed';
  if (s === 'Delivered') return 'installScheduled';
  if (s === 'At Receiver') return 'receiver';
  if (s === 'Shipped') return 'transit';
  return 'production';
}

// Timing classification for one item, judged against `todayIso`:
//   { kind, date, daysLate, reason }
//   kind: 'delivered' — arrived statuses; date = receivedDate (may be null)
//         'overdue'   — expected date passed, not delivered; daysLate > 0
//         'dueSoon'   — expected within the next 7 days (today counts)
//         'onTrack'   — expected more than 7 days out
//         'undated'   — on order but can't be projected; reason says why
//                       ('no_order_date' | 'no_lead_time'). Never guessed.
//         null        — item isn't on the calendar (not ordered yet)
export function itemDeliveryTiming(item, todayIso) {
  if (!item) return null;
  var status = normalizeItemStatus(item.status, item.receivedDate);
  if (CAL_ARRIVED_STATUSES.indexOf(status) >= 0) {
    // Plot on the most site-ward date we have: delivered-to-site beats
    // at-receiver when both exist (Delivered/Installed items).
    return { kind: 'delivered', date: item.deliveredDate || item.receivedDate || null, daysLate: 0, reason: null };
  }
  if (CAL_ORDERED_STATUSES.indexOf(status) < 0) return null;
  var expected = estimatedShipDate(item);
  if (!expected) {
    var reason = String(item.orderPlacedDate || '').trim() ? 'no_lead_time' : 'no_order_date';
    return { kind: 'undated', date: null, daysLate: 0, reason: reason };
  }
  var late = daysBetweenIso(expected, todayIso);
  if (late === null) return { kind: 'undated', date: null, daysLate: 0, reason: 'no_order_date' };
  if (late > 0) return { kind: 'overdue', date: expected, daysLate: late, reason: null };
  if (late >= -7) return { kind: 'dueSoon', date: expected, daysLate: 0, reason: null };
  return { kind: 'onTrack', date: expected, daysLate: 0, reason: null };
}

// Full schedule for the Calendar page. Item-level granularity — one
// event per item, per Cimon's call (2026-07-11). Assembly PARENTS are
// excluded (they're grouping shells per Component Independence; their
// components carry the real vendor/PO/lead-time and appear themselves).
// ABD and cancelled items are excluded like everywhere else.
//   { events, overdue, undated }
//   events:  [{ id, dateIso, timing, daysLate, item }] — expected
//            deliveries + delivered items plotted on receivedDate.
//            Arrived items with no receivedDate have nothing to plot.
//   overdue: the overdue subset, sorted most-late first (the chase list).
//   undated: [{ item, reason }] — on order but unprojectable.
export function buildDeliverySchedule(items, todayIso) {
  var all = items || [];
  var hasComponents = {};
  all.forEach(function(it) { if (it && it.parentId) hasComponents[it.parentId] = true; });
  var events = [], overdue = [], undated = [];
  all.forEach(function(it) {
    if (!it || isABD(it) || isCancelled(it)) return;
    if (hasComponents[it.id]) return;
    // by_others = installed by another trade, never purchased by the firm —
    // no order to track (Cimon, 2026-07-13).
    if (it.procurementClass === 'by_others') return;
    var t = itemDeliveryTiming(it, todayIso);
    if (!t) return;
    if (t.kind === 'undated') { undated.push({ item: it, reason: t.reason }); return; }
    if (!t.date) return;
    var ev = { id: it.id, dateIso: t.date, timing: t.kind, daysLate: t.daysLate, item: it };
    events.push(ev);
    if (t.kind === 'overdue') overdue.push(ev);
  });
  overdue.sort(function(a, b) { return b.daysLate - a.daysLate; });
  return { events: events, overdue: overdue, undated: undated };
}

// Plain-text chase list grouped by vendor — powers "Copy chase list" on
// the overdue rail and "Copy list for vendor email" on the selection bar.
export function buildChaseListText(events) {
  var byVendor = {};
  (events || []).forEach(function(ev) {
    if (!ev || !ev.item) return;
    var v = ev.item.vendor || 'Unknown vendor';
    (byVendor[v] = byVendor[v] || []).push(ev);
  });
  return Object.keys(byVendor).sort().map(function(v) {
    var lines = byVendor[v].map(function(ev) {
      var it = ev.item;
      var name = it.productName || it.item || 'Item';
      var po = it.poNumber ? ' (' + it.poNumber + ')' : '';
      var when = ev.dateIso ? ' — expected ' + ev.dateIso : '';
      var late = ev.daysLate > 0 ? ', ' + ev.daysLate + ' day' + (ev.daysLate === 1 ? '' : 's') + ' late' : '';
      return '  - ' + name + po + when + late;
    });
    return v + ':\n' + lines.join('\n');
  }).join('\n\n');
}

// ── THE THREAD — spine + fork tracking derivation (Phase 1, 2026-07-13) ──────
//
// The Thread is the per-item progress rail from the item-tracking rebuild
// (design handoff 2026-07-13; Cimon's four decisions recorded in CLAUDE.md
// "The Thread"). Model: one record-backed spine of 8 nodes that forks after
// vendor deposit into an INVENTORY rail (human-marked) and a MONEY rail
// (fully DERIVED from payment records — never clicked, so it cannot drift
// from the books; Rule 10 applied to display).
//
// Everything here is pure. Callers pass a `bundle` of collections from
// AppContext — { items, invoices, allocations, vendorPayments,
// purchaseOrders } — plus `todayIso` for aging, so tests are deterministic.
//
// Transition-period mappings (until the Phase 3 migrations run):
//   Pricing Check / Quote Requested → spine node 0 (statuses being deleted)
//   Received  → inventory "Delivered" node (rename pending)
//   Invoiced  → physical position from receivedDate (status being dropped;
//               its meaning lives on the money rail as Client FIN sent/paid)
//   Shipped   → already mapped (status arrives with the migration)

export var THREAD_SPINE_NODES = [
  'Specifying', 'Quote received', 'Presented', 'On proposal',
  'Client approved', 'Client deposit', 'PO issued', 'Vendor deposit',
];
export var THREAD_INVENTORY_NODES = ['In production', 'Shipped', 'Delivered', 'Installed'];
export var THREAD_MONEY_NODES = ['Vendor balance', 'Client FIN sent', 'Client FIN paid'];

// The item's PO document. Membership in po.items wins (authoritative);
// among multiple matches prefer a non-Draft PO. Falls back to matching
// item.poNumber against po_ref for legacy rows that predate po.items.
export function poForItem(item, purchaseOrders) {
  if (!item) return null;
  var pos = purchaseOrders || [];
  var member = pos.filter(function(po) {
    return po && Array.isArray(po.items) && po.items.indexOf(item.id) >= 0;
  });
  var best = member.find(function(po) { return po.status !== 'Draft'; }) || member[0];
  if (best) return best;
  var ref = String(item.poNumber || '').trim();
  if (!ref) return null;
  return pos.find(function(po) { return po && po.po_ref === ref; }) || null;
}

// vendor_payments rows that belong to a PO (reference carries the po_ref —
// the cascade writes '<po_ref> · RECEIPT:<id>'). Lifted from
// VendorPaymentsSection so the thread and the Vendor Bills page share one
// formula; the component now imports these.
export function paymentsForPO(po, vendorPayments) {
  if (!po) return [];
  var ref = po.po_ref || '';
  return (vendorPayments || []).filter(function(p) {
    return p && p.reference && ref && String(p.reference).indexOf(ref) >= 0;
  });
}

// Sum of PRINCIPAL payments against the PO (excludes 'Fee' rows).
export function poTotalPaid(po, vendorPayments) {
  return paymentsForPO(po, vendorPayments).reduce(function(s, p) {
    if (PRINCIPAL_PAYMENT_TYPES.indexOf(p.payment_type) < 0) return s;
    return s + (parseFloat(p.amount) || 0);
  }, 0);
}

// Spine position, 0–8. 8 = spine complete (the fork has begun).
// "Current node" = last milestone REACHED, matching the design's stage
// configs. Legacy committed statuses (Ordered, Payment Received, …) map
// conservatively: 8 when an order/receipt date proves procurement ran,
// else 6 (PO issued).
export function threadSpineIndex(item) {
  if (!item) return 0;
  // Canonicalize so legacy names (un-normalized paths, frozen payloads)
  // read identically to their canonical form.
  var s = normalizeItemStatus(item.status, item.receivedDate);
  if (s === 'Quote Received') return 1;
  if (s === 'Presented to Client') return 2;
  if (s === 'On Proposal') return 3;
  if (s === 'Client Approved') return 4;
  if (s === 'Deposit Paid') return 5;
  if (s === 'PO Issued') return 6;
  var idx = STATUSES.indexOf(s);
  if (idx >= STATUSES.indexOf('Ordered') && s !== 'Cancelled') return 8;
  if (STATUS_ZONE[s] === 'committed' && idx === -1) {
    // Ancient Design-Manager-era statuses (Order Placed, PO Verified, …)
    return (item.orderPlacedDate || item.receivedDate) ? 8 : 6;
  }
  return 0; // Specifying (incl. folded Pricing Check / Quote Requested), unknown
}

// Inventory-rail position. -1 = fork not reached. 0 In production,
// 1 Shipped, 2 Delivered, 3 Installed, 4 past-installed (Complete).
// 'At Receiver' plots at the Delivered node until the rail gains its own
// receiver stop (tracker rebuild item 5).
export function threadInventoryIndex(item) {
  if (!item) return -1;
  var s = normalizeItemStatus(item.status, item.receivedDate);
  if (s === 'Ordered') return 0;
  if (s === 'Shipped') return 1;
  if (s === 'At Receiver' || s === 'Delivered') return 2;
  if (s === 'Installed') return 3;
  if (s === 'Complete') return 4;
  if (STATUS_ZONE[s] === 'committed' && STATUSES.indexOf(s) === -1) {
    return item.receivedDate ? 2 : (item.orderPlacedDate ? 0 : -1);
  }
  return -1;
}

// The item's client FINAL invoice, if one exists. Prefers a non-draft
// (sent/paid) FIN; falls back to a draft so "in preparation" is visible.
export function finInvoiceForItem(item, invoices) {
  if (!item) return null;
  var fins = (invoices || []).filter(function(inv) {
    if (!inv || (inv.invoiceType || inv.invoice_type) !== 'final') return false;
    var ids = inv.itemIds || inv.item_ids || [];
    return Array.isArray(ids) && ids.indexOf(item.id) >= 0;
  });
  return fins.find(function(inv) {
    return String(inv.status || '').trim().toLowerCase() !== 'draft';
  }) || fins[0] || null;
}

// Money-record states for one item. Every node is one of:
//   'done'   — the record exists / the money moved
//   'due'    — open money action (amber)
//   'alert'  — money BEHIND the merchandise (red; FIN-sent node only)
//   'future' — not expected yet
// bundle: { items, invoices, allocations, vendorPayments, purchaseOrders }
export function threadMoneyState(item, bundle, todayIso) {
  bundle = bundle || {};
  var spine = threadSpineIndex(item);
  var inv = threadInventoryIndex(item);

  // Client FIN — resolved early because the deposit node needs to know
  // whether a sent FIN has taken over the item's open balance.
  var fin = finInvoiceForItem(item, bundle.invoices);
  var finIsSent = !!(fin && String(fin.status || '').trim().toLowerCase() !== 'draft');

  // Client deposit (spine node 5) — allocations are the authority
  // (Rule 10), at EVERY committed stage. Vendor-side cascades advance
  // items past Deposit Paid without implying client money (the Kumiko
  // FX case: partial deposit, item walked to Order Confirmed by the
  // vendor-receipt cascade, $4.6K still owed — the old spine>=5
  // trust-the-status shortcut rendered that as "payments current").
  //
  // Past Deposit Paid the shortfall check is deliberately narrower than
  // itemUnpaidDepositShare alone: it fires ONLY when a matched deposit
  // invoice with a real recorded ask (amount_due > 0) is short. The
  // helper's no-record fallback returns the FULL expected deposit by
  // design (defensive over-collect for FIN math) — flagging on that
  // would false-alarm every legacy item whose deposit predates DEP
  // records. Once a FIN is sent, the gap rides the FIN's open balance
  // (finPaid below) — don't double-flag it here.
  var clientDeposit;
  if (spine >= 5) {
    clientDeposit = { state: 'done', amount: 0, agingDays: 0 };
    if (!finIsSent) {
      var depInvId5 = item.clientDepositInvoiceId;
      var depInv5 = depInvId5 ? (bundle.invoices || []).find(function(i2) {
        return i2 && i2.id === depInvId5 && (i2.invoiceType === 'deposit' || i2.invoice_type === 'deposit');
      }) : null;
      var depAsk5 = depInv5 ? (parseFloat(depInv5.amountDue != null ? depInv5.amountDue : depInv5.amount_due) || 0) : 0;
      if (depAsk5 > 0) {
        var unpaid5 = itemUnpaidDepositShare(item, bundle.invoices, bundle.allocations, bundle.items);
        if (unpaid5 > 0.005) {
          var aging5 = item.clientApprovedDate ? (daysBetweenIso(item.clientApprovedDate, todayIso) || 0) : 0;
          clientDeposit = { state: 'due', amount: unpaid5, agingDays: Math.max(0, aging5) };
        }
      }
    }
  } else if (spine === 4) {
    var unpaid = itemUnpaidDepositShare(item, bundle.invoices, bundle.allocations, bundle.items);
    if (unpaid <= 0.005) {
      clientDeposit = { state: 'done', amount: 0, agingDays: 0 };
    } else {
      var aging = item.clientApprovedDate ? (daysBetweenIso(item.clientApprovedDate, todayIso) || 0) : 0;
      clientDeposit = { state: 'due', amount: unpaid, agingDays: Math.max(0, aging) };
    }
  } else {
    clientDeposit = { state: 'future', amount: 0, agingDays: 0 };
  }

  // Vendor balance (money rail node 0) — PO-level principal reconciliation.
  // Becomes "due" once the merchandise ships; while still in production the
  // unpaid balance is expected, not actionable (matches the design's
  // production_ok state).
  var vendorBalance = { state: 'future', amount: 0 };
  var po = poForItem(item, bundle.purchaseOrders);
  if (po && spine >= 8) {
    var recon = poPaidReconciliation(
      poTotalPaid(po, bundle.vendorPayments),
      poSubtotalLive(po, bundle.items)
    );
    if (recon.state === 'paid' || recon.state === 'overpaid') {
      vendorBalance = { state: 'done', amount: 0 };
    } else if (inv >= 1) {
      vendorBalance = { state: 'due', amount: recon.delta };
    }
  }

  // Client FIN sent / paid (money rail nodes 1–2). fin / finIsSent are
  // resolved at the top of the function (the deposit node reads them).
  var finSent;
  if (finIsSent) {
    finSent = { state: 'done', invoiceId: fin.id };
  } else if (inv >= 2 && inv < 4) {
    finSent = { state: 'alert', invoiceId: fin ? fin.id : null }; // delivered, no FIN — red
  } else {
    finSent = { state: 'future', invoiceId: fin ? fin.id : null };
  }

  var finPaid = { state: 'future', amount: 0, agingDays: 0 };
  if (finIsSent) {
    var amountDue = parseFloat(fin.amountDue != null ? fin.amountDue : fin.amount_due) || 0;
    var paidStatus = derivePaidStatus(bundle.allocations, 'invoice', fin.id, amountDue, fin.status);
    if (paidStatus === 'paid') {
      finPaid = { state: 'done', amount: 0, agingDays: 0 };
    } else {
      var open = Math.max(0, Math.round((amountDue - calcPaidForDocument(bundle.allocations, 'invoice', fin.id)) * 100) / 100);
      var sentDate = fin.sentAt || fin.sent_at || fin.date || '';
      var finAging = sentDate ? (daysBetweenIso(String(sentDate).slice(0, 10), todayIso) || 0) : 0;
      finPaid = { state: 'due', amount: open, agingDays: Math.max(0, finAging) };
    }
  }

  return { clientDeposit: clientDeposit, vendorBalance: vendorBalance, finSent: finSent, finPaid: finPaid };
}

// Money-health ring + summary line. Priority ladder:
//   grey (pre-approval) → yellow (client deposit due — blocks ordering) →
//   red (merchandise ahead of money) → amber (client final outstanding) →
//   amber (vendor balance due) → green (settled to stage / complete).
// Copy rule: every money label names its side — two ledgers share words.
export function threadHealth(item, money, inventoryIndex, spineIndex) {
  if (!item || isCancelled(item)) {
    return { ring: 'grey', label: 'Cancelled', summary: 'Cancelled — credits ledgered', complete: false };
  }
  // ABD / Pre-App items (2026-07-28): bought by the PRIOR owner — no
  // client billing ever happens (no DEP, no FIN, isBillable false), so
  // no money state can be 'behind'. Pre-fix, a delivered ABD item lit
  // the red 'Client FIN not sent' alert for an invoice that will never
  // exist. Grey + silent: physical tracking only.
  if (isABD(item)) {
    return { ring: 'grey', label: 'No client billing (ABD)',
      summary: 'ABD / prior-owner item — no client billing expected',
      silent: true, complete: false };
  }
  // Owner Furnished / By Others (2026-07-29): the firm never bills or
  // pays for these — the money rail is permanently silent. Pre-fix, a
  // delivered OF item raised the red 'Client FIN not sent' alert for an
  // invoice that can never exist (shouldBillItem excludes OF/BO).
  if (isNonProcured(item.procurementClass)) {
    var npLabel = item.procurementClass === 'owner_furnished' ? 'Owner Furnished' : 'By Others';
    return { ring: 'grey', label: 'No firm procurement (' + npLabel + ')',
      summary: npLabel + ' — no firm money on this item; physical tracking only',
      silent: true, complete: false };
  }
  if (spineIndex < 4) {
    return { ring: 'grey', label: 'No money expected', summary: 'Pre-approval · no money expected', complete: false };
  }
  function dollars(n) { return '$' + (Math.round(n).toLocaleString('en-US')); }
  var m = money;
  if (m.clientDeposit.state === 'due') {
    var dTail = m.clientDeposit.agingDays > 0 ? ' · ' + m.clientDeposit.agingDays + ' days' : '';
    // "blocks PO" only while the PO hasn't been issued — past that the
    // shortfall is real but the PO gate is history (the Kumiko shape:
    // partial deposit, owner-bypassed PO, item already in production).
    var dGate = spineIndex < 6 ? ' — blocks PO' : '';
    return { ring: 'yellow', label: 'Client deposit due',
      summary: 'Client deposit due · ' + dollars(m.clientDeposit.amount) + dTail + dGate, complete: false };
  }
  if (m.finSent.state === 'alert') {
    return { ring: 'red', label: 'Client FIN not sent',
      summary: 'Client FIN not sent — merchandise ahead of money', complete: false };
  }
  if (m.finPaid.state === 'due') {
    var fTail = m.finPaid.agingDays > 0 ? ' · ' + m.finPaid.agingDays + ' days' : '';
    return { ring: 'amber', label: 'Client final outstanding',
      summary: 'Client final outstanding · ' + dollars(m.finPaid.amount) + fTail, complete: false };
  }
  if (m.vendorBalance.state === 'due') {
    return { ring: 'amber', label: 'Vendor balance due',
      summary: 'Vendor balance due · ' + dollars(m.vendorBalance.amount), complete: false };
  }
  var complete = inventoryIndex >= 3 && m.vendorBalance.state === 'done' && m.finPaid.state === 'done';
  if (complete) {
    return { ring: 'green', label: 'Settled', summary: 'Installed · both ledgers settled', complete: true };
  }
  // Green = SILENT (Cimon, 2026-07-14: "there's a way to indicate this
  // with color instead of words"). The ring alone carries the all-good
  // state in the UI; label/summary exist only for tooltips and the
  // legend. Words are reserved for exception states that need action.
  // (Design handoff called this "Settled to stage"; rejected 2026-07-13.)
  return { ring: 'green', label: 'Payments current', summary: 'Payments current — nothing owed at this stage', silent: true, complete: false };
}

// One-call composition for components: everything the Thread needs.
export function threadStateFor(item, bundle, todayIso) {
  var spineIndex = threadSpineIndex(item);
  var inventoryIndex = threadInventoryIndex(item);
  var money = threadMoneyState(item, bundle, todayIso);
  var health = threadHealth(item, money, inventoryIndex, spineIndex);
  return {
    spineIndex: spineIndex,
    inventoryIndex: inventoryIndex,
    money: money,
    health: health,
    complete: health.complete,
  };
}

// The "Did this ship?" queue (2026-07-14 — Cimon's status-inbox ask).
// Items sitting in production whose shipping needs a human answer:
//   'past_eta'          — expected ship date arrived/passed, still not shipped
//   'vendor_final_paid' — vendor is paid in full but the item hasn't been
//                         marked shipped. Money NEVER implies shipping
//                         (pay-in-full-before-ship vendors are common);
//                         this is a prompt to ask the vendor, not an
//                         auto-advance.
//   'no_eta'            — ordered but unprojectable (no lead time), so the
//                         queue can't watch it — prompt to add lead weeks.
// Returns [{ item, reason, expected, daysPast }], most-overdue first,
// then vendor-paid prompts, then no-eta. Same exclusions as the other
// queues (ABD / cancelled / by_others / assembly parents).
export function didThisShipQueue(items, bundle, todayIso) {
  var all = items || [];
  var hasComponents = {};
  all.forEach(function(it) { if (it && it.parentId) hasComponents[it.parentId] = true; });
  var out = [];
  all.forEach(function(it) {
    if (!it || isABD(it) || isCancelled(it) || hasComponents[it.id]) return;
    if (it.procurementClass === 'by_others') return;
    if (threadInventoryIndex(it) !== 0) return; // only "in production" needs the ship question
    var expected = estimatedShipDate(it);
    if (!expected) {
      out.push({ item: it, reason: 'no_eta', expected: null, daysPast: 0 });
      return;
    }
    var past = daysBetweenIso(expected, todayIso) || 0;
    if (past >= 0) {
      out.push({ item: it, reason: 'past_eta', expected: expected, daysPast: past });
      return;
    }
    var money = threadMoneyState(it, bundle || {}, todayIso);
    if (money.vendorBalance.state === 'done') {
      out.push({ item: it, reason: 'vendor_final_paid', expected: expected, daysPast: 0 });
    }
  });
  var rank = { past_eta: 0, vendor_final_paid: 1, no_eta: 2 };
  out.sort(function(a, b) {
    if (rank[a.reason] !== rank[b.reason]) return rank[a.reason] - rank[b.reason];
    return b.daysPast - a.daysPast;
  });
  return out;
}

// ── BUSINESS RULES ────────────────────────────────────────────────────────────

export function isFinanciallyCommitted(status) {
  // Canonicalize so legacy names (un-normalized paths, tests, frozen
  // payloads) keep their lock. 'Invoiced' needs the row-context map —
  // it's committed either way, so alias it to 'Ordered' here.
  var s = canonicalItemStatus(status);
  if (s === 'Invoiced') s = 'Ordered';
  var idx = STATUSES.indexOf(s);
  return idx !== -1 && idx >= LOCK_INDEX;
}

export function getMaterialFields() {
  return [
    'item',
    'productName',
    'vendor',
    'netPrice',
    'markupPct',
    'qty',
    'qtyType',
    'schedule',
    'dimensions',
    'finish',
    'specs',
    'children',
    'com',
    'project',
    'projectLabel',
    'manufacturer',
    'model',
    'depositPct',
  ];
}

export function isMaterialField(fieldName) {
  return getMaterialFields().indexOf(fieldName) !== -1;
}

// Canonical qtyType values — the only strings that should land in
// items.qty_type. Hardcoded duplicates anywhere else are a bug. See
// MATERIAL_FIELD_LABELS below.
export var QTY_TYPES = ['Each','Pair','Set','Linear Foot','Square Foot','Yard','Roll','Panel','Sheet','Lot'];

// Human-readable labels for the Change Order modal field picker.
export var MATERIAL_FIELD_LABELS = {
  item:         'Item Name',
  productName:  'Product Name',
  vendor:       'Vendor',
  netPrice:     'Net Price',
  markupPct:    'Markup %',
  qty:          'Quantity',
  qtyType:      'Qty Type',
  schedule:     'Schedule',
  dimensions:   'Dimensions',
  finish:       'Finish',
  specs:        'Specs',
  children:     'Components',
  com:          'COM',
  project:      'Project',
  projectLabel: 'Project',
  manufacturer: 'Manufacturer',
  model:        'Model / SKU',
  depositPct:   'Vendor Deposit %',
};

// Fields whose change has a dollar impact on the deposit / final invoice math.
// Phase 5 QB push reads this to decide whether the CO needs a Credit Memo /
// Adjustment in QB or just a metadata update.
//
// qtyType is intentionally NOT here. The pricing math is netPrice × qty ×
// (1 + markup/100) — no qtyType term. Changing Each→Pair without changing
// qty or netPrice is purely descriptive (a unit-of-measure correction);
// the dollar total stays the same. If the bookkeeper meant to reinterpret
// quantity (e.g. "2 Each" was actually "2 Pair = 4 lamps") they need to
// also change qty or netPrice — and those carry the dollar impact.
// Marking qtyType as billing-impacting would surface a misleading "$
// Impact" pill in the CO modal even when the delta is $0.
export var BILLING_IMPACTING_FIELDS = ['netPrice','markupPct','qty'];

export function isBillingImpactingField(field) {
  return BILLING_IMPACTING_FIELDS.indexOf(field) !== -1;
}

// ── CHANGE ORDERS ────────────────────────────────────────────────────────
// Structured audit trail for material-field edits to items past the Client
// Approved lock point. See supabase/migrations/20260502_create_change_orders.sql.
//
// field_changes shape: array of
//   { itemId, itemNumber, itemName, field, fieldLabel, before, after,
//     impactsBilling }

export function rowToChangeOrder(row) {
  return {
    id:             row.id,
    coRef:          row.co_ref          || '',
    projectLabel:   row.project_label   || '',
    proposalId:     row.proposal_id     || '',
    reason:         row.reason          || '',
    status:         row.status          || 'pending',
    filedBy:        row.filed_by        || '',
    filedAt:        row.filed_at        || '',
    approvedBy:     row.approved_by     || '',
    approvedAt:     row.approved_at     || '',
    rejectedBy:     row.rejected_by     || '',
    rejectedAt:     row.rejected_at     || '',
    rejectedReason: row.rejected_reason || '',
    fieldChanges:   jp(row.field_changes, []),
    notes:          row.notes           || '',
    deltaAmount:    parseFloat(row.delta_amount) || 0,
    // Owner's spec-vs-accounting decision at approval time. Three states:
    //   true  → roll delta into client billing (final invoice / QB push)
    //   false → audit only, no QB
    //   null  → not yet decided (status='pending' or 'rejected')
    // See supabase/migrations/20260505_change_orders_accounting_impact.sql
    accountingImpact: row.accounting_impact == null ? null : !!row.accounting_impact,
    // Stamped with the invoice.id once this CO's positive-delta adjustment
    // has been rolled into a final invoice. NULL = unbilled (in bookkeeper
    // worklist). Negative-delta COs use the client_credit_ledger entry's
    // own application status instead — this column stays NULL for them.
    billedInvoiceId: row.billed_invoice_id || '',
    qbCreditMemoId: row.qb_credit_memo_id || '',
    qbAdjustmentId: row.qb_adjustment_id  || '',
    qbPushedAt:     row.qb_pushed_at      || '',
    qbError:        row.qb_error          || '',
    createdAt:      row.created_at        || '',
    updatedAt:      row.updated_at        || '',
  };
}

export function changeOrderToRow(co) {
  var id = co.id;
  if (!id || /^\d+$/.test(String(id))) id = undefined;
  return {
    id:                 id,
    co_ref:             co.coRef          || '',
    project_label:      co.projectLabel   || '',
    proposal_id:        co.proposalId     || '',
    reason:             co.reason         || '',
    status:             co.status         || 'pending',
    filed_by:           co.filedBy        || '',
    filed_at:           co.filedAt        || null,
    approved_by:        co.approvedBy     || '',
    approved_at:        co.approvedAt     || null,
    rejected_by:        co.rejectedBy     || '',
    rejected_at:        co.rejectedAt     || null,
    rejected_reason:    co.rejectedReason || '',
    field_changes:      co.fieldChanges   || [],
    notes:              co.notes          || '',
    delta_amount:       parseFloat(co.deltaAmount) || 0,
    accounting_impact:  co.accountingImpact == null ? null : !!co.accountingImpact,
    billed_invoice_id:  co.billedInvoiceId || null,
    qb_credit_memo_id:  co.qbCreditMemoId || '',
    qb_adjustment_id:   co.qbAdjustmentId || '',
    qb_pushed_at:       co.qbPushedAt     || null,
    qb_error:           co.qbError        || '',
  };
}

// Find Change Orders affecting a specific item. Filters by:
//   - co.status === opts.status (default 'approved')
//   - co.accountingImpact === opts.accountingImpact (default true)
//   - co.fieldChanges contains an entry with itemId === target itemId
//   - opts.proposalId — when set, restricts to COs filed on that proposal
//   - opts.excludeBilled — when true, skips COs with billedInvoiceId set
// Returns an array of CO rows. Used by buildFinalInvoicePayload to roll
// price-delta COs inline onto the affected item's invoice line.
export function getChangeOrdersForItem(itemId, changeOrders, opts) {
  opts = opts || {};
  var status = opts.status || 'approved';
  var accountingImpact = opts.accountingImpact == null ? true : opts.accountingImpact;
  var proposalId = opts.proposalId || null;
  var excludeBilled = !!opts.excludeBilled;
  return (changeOrders || []).filter(function(co) {
    if (!co) return false;
    if (status && co.status !== status) return false;
    if (accountingImpact != null && co.accountingImpact !== accountingImpact) return false;
    if (proposalId && String(co.proposalId) !== String(proposalId)) return false;
    if (excludeBilled && co.billedInvoiceId) return false;
    var fcs = co.fieldChanges || [];
    return fcs.some(function(fc) { return fc && fc.itemId === itemId; });
  });
}

// Generate the next CO ref for a project. Pattern: <projectCode>-CO-<seq>
// e.g. "26HOB-CO-001", zero-padded 3 digits per project.
export function generateChangeOrderRef(projectCode, allChangeOrders) {
  if (!projectCode) return '';
  var prefix = projectCode + '-CO-';
  var existing = (allChangeOrders || []).filter(function(co) {
    return (co.coRef || '').indexOf(prefix) === 0;
  });
  var maxSeq = 0;
  existing.forEach(function(co) {
    var m = String(co.coRef || '').match(/-CO-(\d+)$/);
    if (m) { var n = parseInt(m[1], 10); if (n > maxSeq) maxSeq = n; }
  });
  var next = String(maxSeq + 1).padStart(3, '0');
  return prefix + next;
}

// Apply a CO's field_changes to a single item, returning the patch. Pure —
// caller decides when to call updateItem with the patch. Returns null if
// the item id isn't in this CO's field_changes.
export function applyChangeOrderToItem(changeOrder, itemId) {
  if (!changeOrder || !itemId) return null;
  var changes = (changeOrder.fieldChanges || []).filter(function(c) {
    return c.itemId === itemId;
  });
  if (changes.length === 0) return null;
  var patch = {};
  changes.forEach(function(c) {
    patch[c.field] = c.after;
  });
  return patch;
}

// Compute the dollar delta a CO will produce when approved — the change in
// client total (post-CO clientTotal − pre-CO clientTotal), summed across
// every billing-impacting field change in the CO. Pure; used by
// AppContext.approveChangeOrder to populate change_orders.delta_amount,
// which Phase 5 QB push reads to issue a Credit Memo (negative delta) or
// additional Invoice draw (positive delta).
//
// Algorithm: group field_changes by itemId. Per item, build a before-state
// from pricingSnapshot (frozen at CO filing) and an after-state by applying
// the CO's billing-impacting changes to that snapshot. One subtraction per
// item — afterTotal − beforeTotal where total = net × qty × (1 + markup/100).
//
// One subtraction per item (not per field) is what captures interaction
// terms. When both qty AND netPrice change, the cross-product
// Δqty × Δnet × markup lives in the diff of products, not in the sum of
// single-axis deltas. The earlier per-field summation missed that term.
//
// Returns a number rounded to cents. Returns 0 when the CO has no
// billing-impacting changes (spec-only CO) or invalid input.
// ── Stored FIN markup restatement on change order (2026-10-06) ──────────
//
// A FIN's markup_product_tot / markup_fees_tot are written once, at Save &
// Commit. The only thing that can legitimately change a committed FIN's
// margin afterwards is an Accounting change order on one of its items
// (Kumiko's FX delta at ship time is the live case). Cimon 2026-10-06:
// "we absolutely need for accounting to be accurate." So approval
// restates the stored columns on every committed FIN the CO touches, from
// the same BILLED basis the backfill used — the invoice's own snapshot
// minus the item's cost as the CO now states it. Spec-only COs skip this.
//
//   product = Σ line_items.total − Σ net × qty (parents only)
//   fees    = billed fees ex tax − item fee costs; nothing billed → 0
//     billed fees ex tax: per-fee columns when populated, else
//     subtotal − product − card fee − adjustments − tax (older FINs)
//
// Pure: takes the FIN and the items as they stand AFTER the CO patch.
export function restateFinMarkupFromBilled(inv, items) {
  var n = function(v) { return parseFloat(String(v == null ? '' : v).replace(/[$,]/g, '')) || 0; };
  var r2 = function(x) { return Math.round((parseFloat(x) || 0) * 100) / 100; };
  var byId = {}; (items || []).forEach(function(it) { if (it) byId[it.id] = it; });
  var sel = ((inv && inv.itemIds) || []).map(function(id) { return byId[id]; }).filter(Boolean);
  var lineItems = (inv && inv.lineItems) || [];
  var billedProduct = lineItems.length
    ? lineItems.reduce(function(s, l) { return s + n(l && l.total); }, 0)
    : n(inv && inv.productTotal);
  var costProduct = sel.filter(function(i) { return !i.parentId; })
    .reduce(function(s, i) { return s + n(i.netPrice) * (parseQty(i.qty) || 1); }, 0);
  var feeCols = n(inv.feeFreightTot) + n(inv.feeCratingTot) + n(inv.feeInstallationTot) + n(inv.feeTariffTot)
    + ((inv.feeCustom || []).reduce(function(s, c) { return s + n(c && c.amount); }, 0));
  var cc = n(inv.feeCcProcessingTot);
  var adj = (Array.isArray(inv.adjustments) ? inv.adjustments : []).reduce(function(s, a) { return s + n(a && a.amount); }, 0);
  var tax = n(inv.feeSalesTaxTot);
  var billedFees, source;
  if (feeCols > 0.009) { billedFees = feeCols; source = 'fee columns'; }
  else {
    billedFees = Math.max(0, n(inv.subtotal) - n(inv.productTotal) - cc - adj - tax);
    source = billedFees > 0.009 ? 'subtotal residual' : 'none';
  }
  var costFees = sel.reduce(function(s, i) {
    var f = i.fees || {};
    return s + n(f.freight) + n(f.crating) + n(f.installation) + n(f.tariff)
      + ((f.custom || []).reduce(function(x, c) { return x + n(c && c.amount); }, 0));
  }, 0);
  return {
    product: r2(billedProduct - costProduct),
    fees:    source === 'none' ? 0 : r2(billedFees - costFees),
    source:  source,
  };
}

// committedFinsForItems — the committed (non-void) final invoices that
// close any of `itemIds`. The set a change order must restate.
export function committedFinsForItems(itemIds, invoices) {
  var want = {}; (itemIds || []).forEach(function(id) { want[String(id)] = true; });
  return (invoices || []).filter(function(inv) {
    if (!inv || inv.invoiceType !== 'final') return false;
    var st = String(inv.status || '').toLowerCase();
    if (st === 'void' || st === 'cancelled') return false;
    return (inv.itemIds || []).some(function(id) { return want[String(id)]; });
  });
}

// restateCommittedFinMarkup — the approval-time cascade. DB access is
// injected (saveInvoice, setInvoices) so a test can EXECUTE it and see
// the row it writes — the lesson of the two "write that never ran" bugs.
// Returns { restated: [{ id, invoiceRef, product, fees }], failed: [id] }.
export async function restateCommittedFinMarkup(opts) {
  var o = opts || {};
  var fins = committedFinsForItems(o.itemIds, o.invoices);
  var out = { restated: [], failed: [] };
  for (var i = 0; i < fins.length; i++) {
    var inv = fins[i];
    var m = restateFinMarkupFromBilled(inv, o.items);
    var next = Object.assign({}, inv, { markupProductTot: m.product, markupFeesTot: m.fees });
    var ok = true;
    try { ok = o.saveInvoice ? await o.saveInvoice(next) : true; } catch (e) { ok = false; }
    if (ok === false) { out.failed.push(inv.id); continue; }
    if (typeof o.setInvoices === 'function') {
      o.setInvoices(function(prev) { return (prev || []).map(function(x) { return x && x.id === inv.id ? next : x; }); });
    }
    out.restated.push({ id: inv.id, invoiceRef: inv.invoiceRef || '', product: m.product, fees: m.fees, source: m.source });
  }
  return out;
}

export function calcChangeOrderDelta(co, items) {
  if (!co || !co.fieldChanges || co.fieldChanges.length === 0) return 0;
  var byItem = {};
  co.fieldChanges.forEach(function(c) {
    if (!c || !c.impactsBilling) return;
    if (!byItem[c.itemId]) {
      var snap = c.pricingSnapshot || {};
      var item = (items || []).find(function(x) { return x.id === c.itemId; }) || {};
      var qty       = parseFloat(snap.qty       != null ? snap.qty       : (item.qty       != null ? item.qty       : 1)) || 1;
      var markupPct = parseFloat(snap.markupPct != null ? snap.markupPct : (item.markupPct != null ? item.markupPct : 0)) || 0;
      var netPrice  = parseFloat(snap.netPrice  != null ? snap.netPrice  : (item.netPrice  != null ? item.netPrice  : 0)) || 0;
      byItem[c.itemId] = {
        before: { qty: qty, markupPct: markupPct, netPrice: netPrice },
        after:  { qty: qty, markupPct: markupPct, netPrice: netPrice },
      };
    }
    if (c.field === 'qty' || c.field === 'markupPct' || c.field === 'netPrice') {
      var v = parseFloat(c.after);
      if (!isNaN(v)) byItem[c.itemId].after[c.field] = v;
    }
  });
  var delta = 0;
  Object.keys(byItem).forEach(function(iid) {
    var d = byItem[iid];
    var beforeTotal = d.before.netPrice * d.before.qty * (1 + d.before.markupPct / 100);
    var afterTotal  = d.after.netPrice  * d.after.qty  * (1 + d.after.markupPct  / 100);
    delta += (afterTotal - beforeTotal);
  });
  return Math.round(delta * 100) / 100;
}

// Compute item-status cascade targets when an invoice transitions to paid.
// Pure — used by InvoicesTab.cascadePaidItems to decide which items to
// advance and to what status. Returns an array of { itemId, newStatus }.
//
// Rules:
// - Deposit invoice paid: items at "Client Approved" advance to "Deposit
//   Paid". Walks assembly peers so a peer added post-approval (and
//   therefore not in inv.itemIds) still advances together with siblings.
//   Without the peer walk, a late-added Workroom would be stuck at
//   Client Approved while its Frame/Fabric/Trim siblings reach Deposit
//   Paid, blocking the Workroom from PO eligibility.
// - Final invoice paid: items at "Order Confirmed" / "Received" /
//   "Installed" advance to "Invoiced". NO peer cascade — final invoices
//   are batched by delivery, so a peer not on this invoice hasn't shipped
//   yet. Cascading peers would silently mark un-shipped items as Invoiced.
// - Items already at the target status (or beyond) are skipped.
// Phase fix 2026-06-27 — partial-deposit cascade.
//
// computeCascadeTargets (below) only fires items when the invoice is
// FULLY paid. Per CLAUDE.md Rule 10's data-debt note: the Kumiko
// incident (Anne paid $115K of a $119K deposit, ~96%) left items stuck
// at Client Approved despite the per-item paid share covering each
// item's expected deposit. Three downstream consumers route through
// itemUnpaidDepositShare to read the authoritative allocation record
// instead of the status proxy — so billing math stays correct — but
// the item's STATUS badge still lies.
//
// This helper closes that gap. For each item on a deposit invoice
// still at Client Approved, compute its per-item paid share via
// itemPaidShare (Layer 1/2/3 attribution). If the paid share covers
// the item's expected deposit (within a tolerance band), the item is
// a partial-cascade target: advance to Deposit Paid.
//
// Args:
//   invoice          — the DEP being paid (must be invoiceType='deposit')
//   allItems         — full items collection (for the paid-share lookup)
//   allInvoices      — full invoices collection (Layer-2 line_items zip)
//   paymentAllocations — full allocations collection
//
// Returns: array of { itemId, newStatus } same shape as computeCascadeTargets.
// Empty array on non-deposit invoices.
//
// Tolerance: max($0.50, expectedDeposit × 0.005). Same band as
// vendorPaymentReceiptMatch + isPoFullyPaid for consistency.
//
// Why not on final invoices: final-invoice items advance via batched
// shipping (Order Confirmed → Invoiced) — that's the existing
// computeCascadeTargets path and is independent of partial-payment
// share math.
export function computePartialCascadeTargets(invoice, allItems, allInvoices, paymentAllocations) {
  if (!invoice || invoice.invoiceType !== 'deposit') return [];
  var itemIds = invoice.itemIds || invoice.item_ids || [];
  if (!Array.isArray(itemIds) || itemIds.length === 0) return [];
  var items = allItems || [];
  var invoices = allInvoices || [invoice];
  var allocs = paymentAllocations || [];

  var targets = [];
  itemIds.forEach(function(itemId) {
    var item = items.find(function(x) { return x.id === itemId; });
    if (!item) return;
    // Only advance items currently at Client Approved — Phase 1+ flow
    // never walks items backward.
    if (String(item.status || '').trim() !== 'Client Approved') return;

    // Item's expected deposit: client price × clientDepositPct.
    // Uses the same shape as buildDepositInvoicePayload's per-item
    // deposit calculation so the comparison is apples-to-apples.
    var netPrice = parseFloat(item.netPrice) || 0;
    var qty = parseFloat(item.qty) || 1;
    var markupPct = parseFloat(item.markupPct) || 0;
    var clientPrice = netPrice * (1 + markupPct / 100) * qty;
    var vdp = parseFloat(item.depositPct) || 50;
    var cdpPct = clientDepositPct(vdp) / 100;
    var expectedDeposit = Math.round(clientPrice * cdpPct * 100) / 100;
    if (expectedDeposit <= 0) return;

    // Item's paid share (Layer 1/2/3 attribution chain — itemPaidShare).
    var paid = itemPaidShare(item, invoices, allocs, items);
    if (paid <= 0) return;

    var tolerance = Math.max(0.50, expectedDeposit * 0.005);
    if (paid >= expectedDeposit - tolerance) {
      targets.push({ itemId: itemId, newStatus: 'Deposit Paid' });
    }
  });
  return targets;
}

export function computeCascadeTargets(invoice, allItems) {
  if (!invoice) return [];
  var itemIds = invoice.itemIds || invoice.item_ids || [];
  if (!Array.isArray(itemIds) || itemIds.length === 0) return [];
  var items = allItems || [];

  var targetIds = new Set(itemIds);

  // Per-item deposit invoices (since 2026-05-26): the invoice has `itemId`
  // populated and `itemIds` is a singleton — this invoice covers exactly
  // one item (one assembly peer, in the assembly case). Each peer carries
  // its OWN deposit invoice now, so we must NOT cascade peers based on
  // this single invoice — peers 2/3/4 still owe their own invoices and
  // remain at Client Approved until each is independently paid.
  //
  // Legacy proposal-level deposit invoices (no itemId) keep the peer walk:
  // a late-added peer not in inv.itemIds still advances together with
  // siblings since the one shared invoice covers them all.
  var isPerItemInvoice = !!(invoice.itemId || invoice.item_id);
  if (invoice.invoiceType === 'deposit' && !isPerItemInvoice) {
    var asmIds = new Set();
    itemIds.forEach(function(id) {
      var it = items.find(function(x) { return x.id === id; });
      if (it && it.assemblyId) asmIds.add(it.assemblyId);
    });
    if (asmIds.size > 0) {
      items.forEach(function(it) {
        if (it.assemblyId && asmIds.has(it.assemblyId)) targetIds.add(it.id);
      });
    }
  }

  var targets = [];
  targetIds.forEach(function(itemId) {
    var it = items.find(function(x) { return x.id === itemId; });
    if (!it) return;
    if (invoice.invoiceType === 'deposit' && it.status === 'Client Approved') {
      targets.push({ itemId: itemId, newStatus: 'Deposit Paid' });
    }
    // FINAL invoices no longer write a status (2026-07-28, Thread locked
    // decision #3): 'Invoiced' left the status chain — the statuses past
    // Ordered are the PHYSICAL chain, and FIN/paid state is derived on
    // the Thread's money rail from invoices + payment_allocations
    // (Rule 10). A final payment landing must never move an item's
    // physical position.
  });
  return targets;
}

// Returns the distinct client ids referenced by a CO's field_changes,
// resolved through items + projects. Used by AppContext.fileChangeOrder
// to reject a CO that spans multiple clients (each client needs its own
// CO so credits route correctly when the CO is approved as accounting).
//
// Pure — no Supabase, no React. Items and projects are passed in so the
// caller controls the lookup data.
//
// Returns an array of unique client ids (in insertion order). An empty
// array means no items resolved to any client (orphan CO — handled
// elsewhere). A single-element array is the normal case. More than one
// element means the CO spans clients and should be rejected.
export function changeOrderClientIds(co, items, projects) {
  var fieldChanges = (co && co.fieldChanges) || [];
  var seen = {};
  var order = [];
  fieldChanges.forEach(function(c) {
    var iid = c && c.itemId;
    if (!iid) return;
    var item = (items || []).find(function(x){ return x.id === iid; });
    if (!item) return;
    var project = (projects || []).find(function(pr){
      return pr.label === (item.projectLabel || item.project);
    });
    var cid = project && project.clientId;
    if (!cid) return;
    if (!seen[cid]) { seen[cid] = true; order.push(cid); }
  });
  return order;
}

// Validates a CO before submission. Returns null if valid, or an error
// string describing what's wrong.
export function validateChangeOrder(co) {
  if (!co) return 'No change order data.';
  if (!co.reason || !co.reason.trim()) return 'Reason is required.';
  if (!co.fieldChanges || co.fieldChanges.length === 0) return 'At least one field change is required.';
  if (!co.projectLabel) return 'Project is required.';
  for (var i = 0; i < co.fieldChanges.length; i++) {
    var c = co.fieldChanges[i];
    if (!c.itemId) return 'Each field change must reference an item.';
    if (!c.field) return 'Each field change must specify a field.';
    if (String(c.before || '') === String(c.after || '')) {
      return 'Field "' + (c.fieldLabel || c.field) + '" was not changed.';
    }
  }
  return null;
}

// ── PRICING ──────────────────────────────────────────────────────────────────

// Canonical currency formatter — every UI surface should use this.
// Returns `$X.XX` always with 2 decimals; null/empty/NaN render as `$0.00`
// (per user-stated standard 2026-05-06: "all currency should be in format
// $0.00"). Earlier versions of various component-local fmtMoney variants
// returned '—' (em dash) for empty values, which created a mixed UI; this
// helper is the source of truth and replaces those local copies.
export function fmtMoney(n) {
  if (n == null || n === '') return '$0.00';
  var v = Number(n);
  if (isNaN(v)) return '$0.00';
  var sign = v < 0 ? '-' : '';
  return sign + '$' + Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function calcClientPrice(netPrice, markupPct) {
  var n = parseFloat(netPrice || 0);
  var m = parseFloat(markupPct || 30);
  return n > 0 ? Math.round(n * (1 + (m / 100)) * 100) / 100 : 0;
}

export function calcNetTotal(netPrice, qty) {
  var n = parseFloat(netPrice || 0);
  var q = parseFloat(qty || 1);
  return Math.round(n * q * 100) / 100;
}

export function calcClientTotal(netPrice, markupPct, qty) {
  var clientUnit = calcClientPrice(netPrice, markupPct);
  var q = parseFloat(qty || 1);
  return Math.round(clientUnit * q * 100) / 100;
}

// depositPct defaults to 80; pass 100 for in-stock items (vendor deposit >= 100%)
export function calcClientDeposit(netPrice, markupPct, qty, depositPct) {
  var pct = (depositPct !== undefined && depositPct !== null) ? parseFloat(depositPct) / 100 : 0.80;
  return Math.round(calcClientTotal(netPrice, markupPct, qty) * pct * 100) / 100;
}

// Client deposit % mirrors vendor deposit: 100% vendor → 100% client; otherwise 80%
export function clientDepositPct(vendorDepositPct) {
  return parseFloat(vendorDepositPct || 50) >= 100 ? 100 : 80;
}

export function calcVendorDeposit(netPrice, qty, vendorDepositPct) {
  var pct = parseFloat(vendorDepositPct || 50) / 100;
  return Math.round(calcNetTotal(netPrice, qty) * pct * 100) / 100;
}

export function defaultVendorDepositPct(leadTime) {
  if (!leadTime) return 50;
  var s = String(leadTime).toLowerCase().trim();
  if (/in\s*stock/i.test(s)) return 100;
  var matches = s.match(/(\d+)\s*(?:-\s*\d+\s*)?weeks?/);
  if (matches) {
    var weeks = parseInt(matches[1], 10);
    return weeks <= 4 ? 100 : 50;
  }
  return 50;
}

// ── Markup rate defaults (2026-10-06) ─────────────────────────────────
//
// Cimon: "it might be time to increase the shipping markup to 15% due to
// high costs of everything." Rates now live on the ITEM (stamped at
// creation from project → client → these defaults) instead of a global
// constant, so raising a client's rate moves only items created after
// the change. Nothing already proposed, billed or paid can restate.
//
// LEGACY_FEE_MARKUP_PCT is what every item billed at before the column
// existed; the migration backfills it, and itemFeeMarkupPct falls back
// to it for any row the backfill has not reached. DEFAULT_* are what a
// NEW item gets when neither project nor client says otherwise.
export var DEFAULT_PRODUCT_MARKUP_PCT = 30;
export var DEFAULT_FEE_MARKUP_PCT     = 15;
export var LEGACY_FEE_MARKUP_PCT      = 10;

// itemFeeMarkupPct — the percent THIS item bills freight / crating /
// installation (and custom rows without their own percent) at. Accepts
// an item, a bare number (callers that already resolved the rate), or
// nothing (legacy 10).
export function itemFeeMarkupPct(src) {
  if (src == null) return LEGACY_FEE_MARKUP_PCT;
  var v = (typeof src === 'object') ? src.feeMarkupPct : src;
  var n = parseFloat(v);
  return isFinite(n) ? n : LEGACY_FEE_MARKUP_PCT;
}

// resolveMarkupDefaults — what a NEW item on `project` should be stamped
// with. Project wins over client wins over firm defaults. Blank / NaN at
// a level means "inherit".
export function resolveMarkupDefaults(project, clients) {
  function num(v) { var n = parseFloat(v); return isFinite(n) ? n : null; }
  var client = null;
  if (project && project.clientId != null && project.clientId !== '') {
    client = (clients || []).find(function(c) { return c && String(c.id) === String(project.clientId); }) || null;
  }
  var product = num(project && project.markupPct);
  if (product == null) product = num(client && client.defaultMarkupPct);
  if (product == null) product = DEFAULT_PRODUCT_MARKUP_PCT;
  var fee = num(project && project.feeMarkupPct);
  if (fee == null) fee = num(client && client.defaultFeeMarkupPct);
  if (fee == null) fee = DEFAULT_FEE_MARKUP_PCT;
  return { markupPct: product, feeMarkupPct: fee };
}

// canRestampMarkup — the gate for "apply new client/project rates to
// existing items". Only items the client has never seen a number for:
// no proposal link of any kind and not financially committed. Anything
// past that keeps the rate it was quoted at.
export function canRestampMarkup(item) {
  if (!item) return false;
  if (item.parentId) return false;
  if (item.proposalId || item.proposalRef) return false;
  if (isFinanciallyCommitted(item.status)) return false;
  if (item.finalInvoiceId || item.clientDepositInvoiceId || item.poNumber) return false;
  return true;
}

// applyFreightMarkup(cost, src) — src is the item (or a resolved rate)
// whose fee markup applies. Omitted → legacy 10%.
export function applyFreightMarkup(cost, src) {
  var pct = itemFeeMarkupPct(src);
  return Math.round(parseFloat(cost || 0) * (1 + pct / 100) * 100) / 100;
}

// Per-entry CUSTOM fee markup (2026-07-17, Cimon). Custom fees default
// to the +10% freight-class markup; an entry may carry its own
// markupPct — e.g. 30 for fees unbundled out of a 30%-markup net price
// (the Amuneal engineering/site-survey/templates case). Every consumer
// of a custom fee's CLIENT value routes through here; the entry's raw
// amount stays the firm's cost everywhere.
export function customFeeClient(entry, item) {
  var amt = parseFloat(String((entry && entry.amount) == null ? '' : entry.amount).replace(/[$,]/g, '')) || 0;
  if (!amt) return 0;
  return Math.round(amt * (1 + customFeeMarkupPct(entry, item) / 100) * 100) / 100;
}

// customFeeMarkupPct — the markup a custom fee actually bills at, in one
// place so the client price and the margin can never disagree (they did:
// itemFeeProfit hardcoded 10% while this function honored the entry's
// own markupPct, so a 30% SITE SURVEY billed $8,450 and reported $650 of
// margin instead of $1,950 — fixed 2026-09-08).
//
// Precedence: the entry's explicit markupPct, then its category's
// canonical rate, then 10. The trailing 10 is the legacy default and
// exists only for rows written before the fee vocabulary; the normalizer
// stamps an explicit value on every row it touches, so new data never
// reaches it.
export function customFeeMarkupPct(entry, item) {
  var pct = parseFloat(entry && entry.markupPct);
  if (!isNaN(pct)) return pct;
  var byCat = feeCategoryMarkupPct(entry && entry.category, item);
  if (byCat != null) return byCat;
  return itemFeeMarkupPct(item);
}

// ccFeeMul — return (1 + ccFeePct/100) for an item, clamped to a
// floor of 1.0. Used by the per-item payload builders to silently
// multiply each billable line so the firm recovers the CC processing
// fee from the client without a separate "CC Fee" line on the PDF.
// 2026-06-10 (Decision 7, Cimon's design):
//   - Stored per item as items.ccFeePct (0 = not baked in)
//   - Applied to lineClient + each marked-up fee (and per-item tax)
//   - PDF renders the silently-multiplied values; subtotal math holds
//     line-by-line because every line carries the same multiplier
//   - When ccFeePct > 0 on any item bundled into a FIN, the existing
//     receipt-cc_fee auto-aggregation is suppressed (otherwise client
//     gets billed twice — once in the lines, once in silent absorption)
// Sanity ceiling on the CC fee percentage. Real card-processing fees are
// 2-4%; even an extreme private-label markup wouldn't realistically
// exceed 25%. A value above CC_FEE_PCT_MAX (50%) is almost certainly a
// data-entry error — e.g., "549" typed when "5.49" was intended, which
// silently produces a 6.49x multiplier on every billable line of the
// proposal/DEP/FIN PDFs. That happened in production 2026-06-28 on
// 26HOB-P-039 (the CAPTAIN'S DRESSER item carried ccFeePct=549, making
// the proposal PDF show $176,908.96 deposit against a true $25,007.98).
// The clamp here treats out-of-range values as no-op + console.warn so
// future data-entry errors degrade gracefully instead of catastrophically.
export var CC_FEE_PCT_MAX = 50;

export function ccFeeMul(item) {
  if (!item) return 1;
  var pct = parseFloat(item.ccFeePct) || 0;
  if (pct <= 0) return 1;
  if (pct > CC_FEE_PCT_MAX) {
    console.warn('[ccFeeMul] ccFeePct=' + pct + ' on item ' + (item.id || item.itemNumber || '?') + ' exceeds ' + CC_FEE_PCT_MAX + '% sanity threshold; treating as 0 (no-op). Likely a data-entry error — check the item.');
    return 1;
  }
  return 1 + (pct / 100);
}

// Second arg (2026-10-09): the markup percent. Omitted → 15, the firm
// default. Receipt-driven callers pass receiptMarkupPct(receipt).
export var DEFAULT_REIMBURSABLE_MARKUP_PCT = 15;
export function applyReimbursableMarkup(cost, pct) {
  var p = (pct == null || pct === '') ? DEFAULT_REIMBURSABLE_MARKUP_PCT : parseFloat(pct);
  if (!isFinite(p)) p = DEFAULT_REIMBURSABLE_MARKUP_PCT;
  return Math.round(parseFloat(cost || 0) * (1 + p / 100) * 100) / 100;
}

// The client markup percent stored on a receipt (receipts.markup_pct),
// defaulting to 15 for rows that predate the column. Editable per
// receipt by admins in the receipt reader; every billing surface reads
// through here so the picker, cover, ledger and PDF agree.
export function receiptMarkupPct(receipt) {
  if (!receipt) return DEFAULT_REIMBURSABLE_MARKUP_PCT;
  var v = receipt.markupPct != null ? receipt.markupPct : receipt.markup_pct;
  if (v == null || v === '') return DEFAULT_REIMBURSABLE_MARKUP_PCT;
  var p = parseFloat(v);
  return isFinite(p) ? p : DEFAULT_REIMBURSABLE_MARKUP_PCT;
}

// ── Vendor search — canonical helper ─────────────────────────────────────────
//
// Single source of truth for "does this vendor match the user's typed
// search string?" across VendorDirectory, VendorPicker, and any future
// vendor-list UI. Inline filter logic is no longer permitted — same
// pattern as computeSidemark and buildDepositInvoicePayloadPure.
//
// Why this isn't Fuse.js: Fuse's relevance scoring is overkill for live
// search and actively hostile to two of the spec's requirements —
// single-character queries and punctuation-tolerant matching. Fuse's
// `minMatchCharLength` defaults to 1 but practical thresholds gate to
// 2+; `&`/`+`/connector handling requires a normalize pass anyway. So
// we do the normalize pass directly and run three substring passes
// across each searchable field. ImportWizard's `fuzzyMatchesFor` keeps
// its Fuse instance — different use case (find top-3 closest matches
// to an arbitrary OCR'd string, not "does this candidate match what
// the user typed").
//
// Three passes per field:
//   - raw lowercased: preserves punctuation so "O+G" matches "O+G Studios"
//   - normalized: collapses `&`/`+`/`and` to a single space and strips
//     remaining non-word punctuation, so "O and G" or "O & G" both
//     match "O+G Studios"
//   - squashed: normalized + whitespace removed, so "OG" or
//     "ogstudios" match "O+G Studios"
//
// Empty / whitespace-only query matches every vendor (no filter).
function normalizeForVendorSearch(s) {
  return String(s || '')
    .toLowerCase()
    .replace(/[&+]/g, ' ')          // & / + → space
    .replace(/\band\b/g, ' ')        // standalone "and" → space
    .replace(/[^\w\s]/g, ' ')        // strip remaining punctuation (incl. apostrophes, commas, periods)
    .replace(/\s+/g, ' ')
    .trim();
}

function squashForVendorSearch(s) {
  return normalizeForVendorSearch(s).replace(/\s+/g, '');
}

// Fields searched on every vendor record. Name is canonical; the rest
// are auxiliary so a directory search by phone/email/city still works.
// VendorPicker effectively searches name too — typing a phone into the
// typeahead is harmless (it just won't match the vendor names) and
// power-users searching the directory benefit from the breadth.
function vendorSearchHaystacks(vendor) {
  var v = vendor || {};
  var fields = [
    v.name, v.contact, v.email, v.phone, v.notes, v.website,
    v.address, v.address2, v.city, v.state, v.zip,
    v.vendorType, v.shipAttn,
  ];
  (v.contacts || []).forEach(function(c) {
    if (!c) return;
    fields.push(c.name);
    fields.push(c.email);
    fields.push(c.phone);
  });
  return fields.filter(function(f) { return f != null && f !== ''; });
}

// Multi-field vendor search. Searches name + contact + email + phone +
// notes + address + city + state + zip + vendorType + shipAttn + every
// contact in contacts[].
//
// Currently unused after 2026-05-14 decision to make directory and
// picker search name-only — multi-field matching surfaced false
// positives ("O+" returned 89 matches because 'o' appears in many
// cities/emails). Kept exported because multi-field substring may be
// useful for future admin/reporting surfaces where searching beyond
// name is the point (e.g., "find every vendor in NYC", "find every
// vendor with a Gmail contact"). Use `matchesVendorNameOnly` below
// for typeahead and directory filtering.
export function matchesVendorSearch(vendor, query) {
  var qRaw = String(query == null ? '' : query).toLowerCase().trim();
  if (!qRaw) return true;
  var qNorm = normalizeForVendorSearch(query);
  var qSquash = squashForVendorSearch(query);
  var fields = vendorSearchHaystacks(vendor);
  for (var i = 0; i < fields.length; i++) {
    var fStr = String(fields[i]);
    var fRaw = fStr.toLowerCase();
    if (fRaw.indexOf(qRaw) >= 0) return true;
    if (qNorm) {
      var fNorm = normalizeForVendorSearch(fStr);
      if (fNorm.indexOf(qNorm) >= 0) return true;
    }
    if (qSquash) {
      var fSquash = squashForVendorSearch(fStr);
      if (fSquash.indexOf(qSquash) >= 0) return true;
    }
  }
  return false;
}

// Name-only vendor prefix match. Same three-pass normalization shape
// as matchesVendorSearch above (raw / collapsed-punctuation /
// squashed-whitespace) but restricted to vendor.name AND using
// startsWith instead of substring. Used by VendorDirectory's main
// search and VendorPicker's typeahead — the directory is for finding
// vendors by name, and a directory typeahead matches how phone
// contacts work: type the first letter(s), get a narrowed list.
//
// History:
//  - 2026-05-14 substring multi-field (matchesVendorSearch) →
//    name-only substring (matchesVendorNameOnly v1): scope
//    tightening, false-positive cleanup.
//  - 2026-05-14 same day name-only substring → name-only prefix
//    (this version): UX refinement. "O" matches vendors whose name
//    STARTS WITH O, not vendors with O somewhere in the name.
//
// Punctuation tolerance is preserved by normalizing both query and
// name first, then prefix-checking on the normalized forms. "OG" /
// "O+G" / "O & G" / "O and G" all match "O+G Studios" because the
// normalized/squashed query is a prefix of the normalized/squashed
// name.
export function matchesVendorNameOnly(vendor, query) {
  var qRaw = String(query == null ? '' : query).toLowerCase().trim();
  if (!qRaw) return true;
  var name = String((vendor && vendor.name) || '');
  if (!name) return false;
  var fRaw = name.toLowerCase();
  if (fRaw.indexOf(qRaw) === 0) return true;
  var qNorm = normalizeForVendorSearch(query);
  if (qNorm) {
    var fNorm = normalizeForVendorSearch(name);
    if (fNorm.indexOf(qNorm) === 0) return true;
  }
  var qSquash = squashForVendorSearch(query);
  if (qSquash) {
    var fSquash = squashForVendorSearch(name);
    if (fSquash.indexOf(qSquash) === 0) return true;
  }
  return false;
}

export function applyTaxMarkup(cost) {
  return Math.round(parseFloat(cost || 0) * 100) / 100;
}

// ── PROJECT-LEVEL TAX CONFIG (Fix C, Phase 1 — 2026-05-27) ──────────────
//
// Tax is moving from a stored per-item dollar amount (item.fees.salesTax,
// computed once at item-creation by calcAutoSalesTax then frozen) to a
// derived value computed at document-generation time from project-level
// configuration:
//
//   project.taxRate            — single blended rate (e.g. 0.09 for 9%)
//   project.taxesMerchandise   — default true; whether merchandise is taxed
//   project.taxesFreight       — whether freight fees are taxed
//   project.taxesCrating       — whether crating fees are taxed
//   project.taxesInstall       — whether installation fees are taxed
//   project.taxesTariff        — whether tariff fees are taxed
//   project.taxesStorage       — whether storage fees are taxed
//   project.taxesTime          — whether design-hours / time is taxed
//                                (not applicable to per-item; reserved)
//
// Phase 1 ships the helpers + wires them into the payload builders with
// a graceful fallback: when project.taxRate is null/undefined, callers
// continue reading the legacy item.fees.salesTax dollar value. Projects
// migrate to the new shape in Phase 4 (backfill Hoback/Ptarmigan/Moskito).
// TaxCloud and project_tax_rates were removed 2026-10-17.
//
// Sentinel semantics:
//   project.taxRate === undefined / null → no new config → legacy
//     fallback (read item.fees.salesTax as before)
//   project.taxRate === 0 (numeric)      → configured zero rate
//     (e.g. BVI export); compute returns 0
//   project.taxRate > 0                  → fresh per-item computation

// Returns true when the project carries the Phase-1 tax-config fields.
// Used by every payload builder to decide between fresh computation and
// the legacy stored-fees-salesTax fallback.
export function hasProjectTaxConfig(project) {
  if (!project) return false;
  if (project.taxRate == null) return false;  // undefined OR null
  var n = parseFloat(project.taxRate);
  return !isNaN(n) && n >= 0;                 // 0 is a valid configured rate
}

// Per-item tax dollar amount derived from the project's blended rate +
// category flags. Falls back to the legacy stored item.fees.salesTax when
// the project lacks the new config (Phase 1 backwards compat).
//
// Markups follow CLAUDE.md "Fee markup rules":
//   freight / crating / installation / custom → × 1.10
//   tariff / sales tax                        → pass-through (no markup)
//   storage                                   → × 1.15 (reimbursable rate)
//
// Tax is computed on the MARKED-UP fee amounts (what the client sees on
// the invoice), so a 9% rate on $100 freight applies to $110 (= $9.90),
// matching what a brick-and-mortar register would compute.
//
// item     — in-memory item object with fees.{freight, crating,
//            installation, tariff, salesTax, storage, custom[]}
// project  — resolved project with optional taxRate + taxes* booleans
// allItems — full catalog, passed to assemblyClient for assembly-aware
//            merchandise total. Defaults to [item] when omitted.
export function computeItemTax(item, project, allItems) {
  if (!item) return 0;
  if (!hasProjectTaxConfig(project)) {
    var stored = parseFloat((item.fees || {}).salesTax || 0);
    if (isNaN(stored) || stored <= 0) return 0;
    return applyTaxMarkup(stored);  // identity, but documents intent
  }
  var rate = parseFloat(project.taxRate) || 0;
  if (rate === 0) return 0;
  var fees = item.fees || {};
  function fee(v) {
    var n = parseFloat(String(v == null ? '' : v).replace(/[$,]/g, ''));
    return isNaN(n) ? 0 : n;
  }
  var taxable = 0;
  // Merchandise defaults TRUE when the flag is undefined — the universal
  // taxable category. Explicit `false` opts out (rare, e.g. wholesale).
  if (project.taxesMerchandise !== false) {
    taxable += parseFloat(assemblyClient(item, allItems || [item])) || 0;
  }
  if (project.taxesFreight)  taxable += applyFreightMarkup(fee(fees.freight), item);
  if (project.taxesCrating)  taxable += applyFreightMarkup(fee(fees.crating), item);
  if (project.taxesInstall)  taxable += applyFreightMarkup(fee(fees.installation), item);
  if (project.taxesTariff)   taxable += applyTaxMarkup(fee(fees.tariff));
  if (project.taxesStorage)  taxable += applyReimbursableMarkup(fee(fees.storage));
  // taxesTime is for designer-hours documents and doesn't apply per-item.
  return Math.round(taxable * rate * 100) / 100;
}

// computeItemTaxBreakdown — the SAME walk as computeItemTax, but it
// keeps the per-category bases instead of collapsing them. Returns
//   { taxable:    { merchandise, freight, crating, installation, tariff, storage },
//     nonTaxable: { merchandise, freight, crating, installation, tariff, storage },
//     tax }
// where every value is a client-side dollar figure (marked up the way
// the client is billed) and `tax` equals computeItemTax to the cent.
// A category lands in `taxable` when the project flag says the state
// taxes it, otherwise in `nonTaxable` — so the two maps together are
// the item's full pre-tax price, split by what the return asks for.
//
// Exists for the Sales Tax report: a reseller's return wants gross
// sales, then deductions BY REASON (freight not taxed here, labor not
// taxed here), not one anonymous "non-taxable" number.
export var TAX_CATEGORY_KEYS = ['merchandise', 'freight', 'crating', 'installation', 'tariff', 'storage'];
export var TAX_CATEGORY_LABELS = {
  merchandise: 'Merchandise', freight: 'Freight', crating: 'Crating',
  installation: 'Installation / labor', tariff: 'Tariff / duty', storage: 'Storage',
};
export function computeItemTaxBreakdown(item, project, allItems) {
  var zero = function() {
    var o = {}; TAX_CATEGORY_KEYS.forEach(function(k) { o[k] = 0; }); return o;
  };
  var out = { taxable: zero(), nonTaxable: zero(), tax: 0 };
  if (!item) return out;
  var fees = item.fees || {};
  function fee(v) {
    var n = parseFloat(String(v == null ? '' : v).replace(/[$,]/g, ''));
    return isNaN(n) ? 0 : n;
  }
  var bases = {
    merchandise:  parseFloat(assemblyClient(item, allItems || [item])) || 0,
    freight:      applyFreightMarkup(fee(fees.freight), item),
    crating:      applyFreightMarkup(fee(fees.crating), item),
    installation: applyFreightMarkup(fee(fees.installation), item),
    tariff:       applyTaxMarkup(fee(fees.tariff)),
    storage:      applyReimbursableMarkup(fee(fees.storage)),
  };
  var configured = hasProjectTaxConfig(project) && (parseFloat(project.taxRate) || 0) > 0;
  var flags = {
    merchandise:  configured && project.taxesMerchandise !== false,
    freight:      configured && !!project.taxesFreight,
    crating:      configured && !!project.taxesCrating,
    installation: configured && !!project.taxesInstall,
    tariff:       configured && !!project.taxesTariff,
    storage:      configured && !!project.taxesStorage,
  };
  TAX_CATEGORY_KEYS.forEach(function(k) {
    var v = Math.round(bases[k] * 100) / 100;
    if (flags[k]) out.taxable[k] = v; else out.nonTaxable[k] = v;
  });
  out.tax = computeItemTax(item, project, allItems);
  return out;
}

// ── FIN tax snapshot (2026-09-13) ──────────────────────────────────────────
// The taxable transaction is the CLOSED final invoice, and the amount is
// what that document billed (Cimon, 2026-09-13). Items keep changing —
// COs, fee actuals, deletions — so the Sales Tax report cannot read live
// items for a filed figure. At Save & Commit the FIN builder emits one
// row per item and finalize_invoice_atomic writes them into
// final_invoice_items in the same transaction as the invoice. The report
// reads those rows first and falls back to live items only for finals
// that predate the snapshot (and were not backfilled).
//
// Every dollar figure is the CLIENT-side value (marked up, × ccFeeMul),
// exactly what the FIN's own totals were built from.
export function buildFinTaxSnapshotRows(items, project, allItems) {
  var r2 = function(x) { return Math.round((x || 0) * 100) / 100; };
  var rate = hasProjectTaxConfig(project) ? (parseFloat(project.taxRate) || 0) : 0;
  return (items || []).filter(Boolean).map(function(it) {
    var mul = ccFeeMul(it);
    var bd = computeItemTaxBreakdown(it, project, allItems || items);
    var taxable = 0, nonTaxable = 0;
    var cats = { taxable: {}, nonTaxable: {} };
    TAX_CATEGORY_KEYS.forEach(function(k) {
      cats.taxable[k]    = r2(bd.taxable[k] * mul);
      cats.nonTaxable[k] = r2(bd.nonTaxable[k] * mul);
      taxable    += bd.taxable[k] * mul;
      nonTaxable += bd.nonTaxable[k] * mul;
    });
    return {
      itemId:        it.id,
      productAmount: r2((parseFloat(assemblyClient(it, allItems || items)) || 0) * mul),
      taxable:       r2(taxable),
      nonTaxable:    r2(nonTaxable),
      tax:           r2(bd.tax * mul),
      taxRate:       rate,
      cats:          cats,
    };
  });
}

// Snapshot rows travel to the RPC as jsonb in the table's own column
// names, so the SQL side is a plain jsonb_to_recordset.
export function finTaxSnapshotRowToDb(row, finalInvoiceId) {
  return {
    final_invoice_id:   finalInvoiceId,
    item_id:            row.itemId,
    product_amount:     row.productAmount,
    taxable_amount:     row.taxable,
    non_taxable_amount: row.nonTaxable,
    tax:                row.tax,
    tax_rate:           row.taxRate,
    tax_cats:           row.cats || {},
    source:             row.source || 'finalize',
  };
}

export function rowToFinalInvoiceItem(row) {
  var n = function(v) { var f = parseFloat(v); return isNaN(f) ? 0 : f; };
  var cats = row.tax_cats;
  if (typeof cats === 'string') { try { cats = JSON.parse(cats); } catch (e) { cats = {}; } }
  return {
    id:             row.id,
    finalInvoiceId: row.final_invoice_id,
    itemId:         row.item_id,
    productAmount:  n(row.product_amount),
    taxable:        n(row.taxable_amount),
    nonTaxable:     n(row.non_taxable_amount),
    tax:            n(row.tax),
    taxRate:        n(row.tax_rate),
    cats:           cats || {},
    source:         row.source || '',
    notes:          row.notes || '',
    createdAt:      row.created_at || '',
  };
}

// Aggregator: sum of computeItemTax across a list of items.
// Returns a number rounded to cents.
export function proposalTax(items, project, allItems) {
  var pool = allItems || items;
  return Math.round((items || []).reduce(function(s, it) {
    // ccFeeMul scales EVERY billable line (Decision 7, 2026-06-10) —
    // tax included, matching itemDepositAmount and itemLifecycleOwed.
    // 2026-08-05: the un-scaled tax here made the proposal's SALES TAX
    // line + PROPOSAL TOTAL disagree with DEPOSIT REQUESTED by the CC
    // uplift on tax ($51.56 on 26HOB-P-041).
    return s + computeItemTax(it, project, pool) * ccFeeMul(it);
  }, 0) * 100) / 100;
}

// feeRateSrc (2026-10-06): the item (or its fee markup percent) so fees
// bill at the item's own rate; omitted → legacy 10%.
export function calcDepositInvoiceTotal(netPrice, markupPct, qty, fees, vendorDepositPct, feeRateSrc) {
  var item = feeRateSrc;
  var cdp = clientDepositPct(vendorDepositPct);
  var base = calcClientDeposit(netPrice, markupPct, qty, cdp);
  if (cdp < 100) return base; // fees go to final invoice
  fees = fees || {};
  var feeTotal = 0;
  if (fees.freight)      feeTotal += applyFreightMarkup(fees.freight, item);
  if (fees.crating)      feeTotal += applyFreightMarkup(fees.crating, item);
  if (fees.installation) feeTotal += applyFreightMarkup(fees.installation, item);  // labor at +10%
  if (fees.salesTax)     feeTotal += applyTaxMarkup(fees.salesTax);
  if (fees.tariff)       feeTotal += applyTaxMarkup(fees.tariff);
  (fees.custom || []).forEach(function(c) { if (c.amount) feeTotal += customFeeClient(c, item); });
  return Math.round((base + feeTotal) * 100) / 100;
}

export function calcFinalInvoiceTotal(netPrice, markupPct, qty, fees, vendorDepositPct, feeRateSrc) {
  var item = feeRateSrc;
  var cdp = clientDepositPct(parseFloat(vendorDepositPct || 50));
  if (cdp >= 100) return 0; // everything collected at deposit
  var clientTotal = calcClientTotal(netPrice, markupPct, qty);
  var finalBase = Math.round(clientTotal * 0.20 * 100) / 100;
  fees = fees || {};
  var feeTotal = 0;
  if (fees.freight)      feeTotal += applyFreightMarkup(fees.freight, item);
  if (fees.crating)      feeTotal += applyFreightMarkup(fees.crating, item);
  if (fees.installation) feeTotal += applyFreightMarkup(fees.installation, item);
  if (fees.salesTax)     feeTotal += applyTaxMarkup(fees.salesTax);
  if (fees.tariff)       feeTotal += applyTaxMarkup(fees.tariff);
  (fees.custom || []).forEach(function(c) { if (c.amount) feeTotal += customFeeClient(c, item); });
  return Math.round((finalBase + feeTotal) * 100) / 100;
}

// When vendor deposit is 100%, the vendor invoice includes freight, crating,
// tariff, custom fees, AND installation labor at net cost (each item's
// fees.installation contributes when > 0). Sales tax is excluded — we hold
// a resale certificate and do not pay sales tax to vendors on procurement
// items.
//
// Installation labor — per-item rule (since 2026-06-03):
//   If item.fees.installation > 0, that item's vendor installs that item;
//   the installation fee rides on the vendor PO at net cost. The fee on
//   the item IS the marker. No vendor-level flag.
//
// Pre-2026-06-03 this routed through a vendor-level `installsOwnGoods`
// flag (5th param + vendors.installs_own_goods column). The flag was the
// wrong abstraction — installation is a property of the item, not the
// vendor (a vendor might install some items but not others). The column
// remains on the vendors table as deprecated; no JS code reads it.
export function calcVendorDepositWithFees(netPrice, qty, vendorDepositPct, fees) {
  var base = calcVendorDeposit(netPrice, qty, vendorDepositPct);
  if (parseFloat(vendorDepositPct || 50) < 100) return base;
  fees = fees || {};
  function fp(s) { var n = parseFloat((s || '').toString().replace(/[$,]/g, '')); return isNaN(n) ? 0 : n; }
  var feeTotal =
    fp(fees.freight)
    + fp(fees.crating)
    + fp(fees.tariff)
    + fp(fees.installation);
  // fees.salesTax intentionally omitted — sales tax never rides on a
  // vendor PO (header above). Custom-fee entries whose label looks
  // like sales tax are also filtered for the same reason; legacy
  // data sometimes migrated salesTax into the custom array.
  (fees.custom || []).forEach(function(c) {
    if (!c) return;
    if (isSalesTaxLikeCustomLabel(c.label)) return;
    feeTotal += fp(c.amount || 0);
  });
  return Math.round((base + feeTotal) * 100) / 100;
}

// ── ITEM-LEVEL PRICING HELPERS ───────────────────────────────────────────────
// Use these everywhere a component needs to price an item object.
// They handle string parsing (strips $, commas) and default values so
// each component doesn't need its own parsing logic.

export function parsePrice(v) {
  return parseFloat(String(v || '').replace(/[$,]/g, '')) || 0;
}

export function parseQty(v) {
  return parseFloat(String(v || '')) || 1;
}

// Net total for one item: netPrice × qty
export function itemNet(it) {
  return calcNetTotal(parsePrice(it.netPrice), parseQty(it.qty));
}

// Client total for one item: netPrice × qty × (1 + markupPct%)
export function itemClient(it) {
  return calcClientTotal(parsePrice(it.netPrice), parseFloat(it.markupPct || 30), parseQty(it.qty));
}

// Live PO net total from current item state. Replaces reads of the stored
// `po.total_net` field which goes stale when items are re-priced after PO
// save. Use this everywhere a PO total is displayed or used in math.
export function poNetLive(po, allItems) {
  if (!po || !po.items) return 0;
  return Math.round(po.items.reduce(function(s, id) {
    var it = (allItems || []).find(function(x) { return x.id === id; });
    return it ? s + itemNet(it) : s;
  }, 0) * 100) / 100;
}

// Helper — identify custom-fee entries that actually represent sales
// tax. Some legacy data migrations dropped fees.salesTax into the
// fees.custom array as {label: 'Sales Tax', amount: …}. Vendor-side
// totals must reject those entries because:
//   (a) Sales tax is a client-side obligation collected by the firm
//       and remitted to the state — never owed to the vendor.
//   (b) The firm holds a resale certificate; vendors don't charge
//       sales tax on procurement items.
// Without this filter, isPoFullyPaid would mark a PO as "not fully
// paid" by exactly the salesTax amount even though the vendor has
// been paid in full (the 26HOB-PO-020 / Holland and Sherry case
// surfaced 2026-06-26).
//
// Match is case-insensitive and tolerates separator variants ("sales
// tax", "sales-tax", "sales_tax", "salestax", or bare "tax").
function isSalesTaxLikeCustomLabel(label) {
  var s = String(label || '').toLowerCase().replace(/[\s_-]+/g, '');
  return s === 'salestax' || s === 'tax';
}

// linkablePosForReceipt — the PO choices the receipt modal offers
// (2026-10-21). Pre-fix the dropdown listed every Draft / Issued /
// Confirmed / Complete PO in the firm, paid or not, across projects.
// Now:
//   - open only: Draft / Issued / Confirmed and not fully paid
//     (isPoFullyPaid, the same rule Vendor Payments uses); Complete and
//     paid POs come back only with showAll
//   - project narrowing when opts.project is set
//   - the receipt's CURRENT po is always kept so an existing link never
//     renders as a blank select
// Vendor is a hint, never a filter: freight / receiver / customs paper
// names a different vendor than the PO. Each entry carries
// vendorMatch so the UI can group matches first.
export function linkablePosForReceipt(opts) {
  opts = opts || {};
  var pos = Array.isArray(opts.purchaseOrders) ? opts.purchaseOrders : [];
  var project = opts.project || '';
  var showAll = !!opts.showAll;
  var currentPoId = opts.currentPoId ? String(opts.currentPoId) : '';
  var vendorName = String(opts.vendorName || '').trim();
  var resolved = vendorName ? resolveVendorByName(vendorName, opts.vendors || []) : null;
  var wantedNames = {};
  if (vendorName) wantedNames[vendorName.toLowerCase()] = true;
  if (resolved && resolved.name) wantedNames[String(resolved.name).toLowerCase()] = true;

  var out = [];
  for (var i = 0; i < pos.length; i++) {
    var po = pos[i];
    if (!po || !po.id) continue;
    var isCurrent = currentPoId && String(po.id) === currentPoId;
    if (!isCurrent) {
      var st = po.status;
      var openStatus = st === 'Draft' || st === 'Issued' || st === 'Confirmed';
      if (!openStatus && !(showAll && st === 'Complete')) continue;
      if (!showAll && openStatus) {
        var paid = isPoFullyPaid(po, opts.items || [], opts.vendorPayments || [], opts.vendorPaymentItems || [], opts.vendorCreditLedger || []);
        if (paid && paid.isFullyPaid) continue;
      }
      if (project && (po.project || '') !== project) continue;
    }
    var poVendorLc = String(po.vendorName || '').toLowerCase();
    var poResolved = po.vendorName ? resolveVendorByName(po.vendorName, opts.vendors || []) : null;
    var vendorMatch = !!vendorName && (
      !!wantedNames[poVendorLc]
      || (poResolved && resolved && String(poResolved.id) === String(resolved.id))
    );
    out.push({ po: po, vendorMatch: !!vendorMatch, isCurrent: !!isCurrent });
  }
  out.sort(function(a, b) {
    if (a.vendorMatch !== b.vendorMatch) return a.vendorMatch ? -1 : 1;
    return String(b.po.po_ref || '').localeCompare(String(a.po.po_ref || ''));
  });
  return out;
}

// Live PO SUBTOTAL — what the vendor invoices (= what the PO PDF
// shows as "Subtotal" / what the vendor wires). Product net + freight
// + crating + tariff + sum(custom) + per-item installation (when
// item.fees.installation > 0). Mirrors buildPoPdfPayload's subtotal math.
//
// SALES TAX EXCLUSION (locked 2026-06-26): the scalar fees.salesTax
// is never added (and never has been — see the field list below).
// Custom-fee entries whose label looks like sales tax are ALSO
// filtered out via isSalesTaxLikeCustomLabel; legacy data sometimes
// migrated salesTax into the custom array, which would otherwise
// silently fold into the vendor total and break the
// isPoFullyPaid closure check.
//
// Two PO total notions coexist:
//   poNetLive(po, allItems)            — product NET only (just the
//                                        goods, no fees). Use for
//                                        columns explicitly labeled
//                                        "Net Total" / "Net" — they
//                                        answer "what's the goods
//                                        value on this PO?"
//   poSubtotalLive(po, allItems)       — full PO subtotal (matches
//                                        PDF). Use for columns
//                                        labeled "PO Total" /
//                                        "Vendor Deposit" / vendor
//                                        payment reconciliation —
//                                        they answer "what does
//                                        the vendor expect on this
//                                        PO?"
//
// Installation rule (since 2026-06-03): per-item check via
// fees.installation > 0. If an item has an installation fee recorded,
// that item's vendor installs that item — the fee rides on the PO at
// net cost. Pre-2026-06-03 this was gated by a vendor-level
// `installsOwnGoods` flag (3rd param + vendors.installs_own_goods
// column). The column remains as deprecated; no JS code reads it.
//
// Cascade-pairing: buildVendorPaymentFromReceipt writes principal
// `amount: receipt.totalAmount` (= full receipt = PDF subtotal). So
// principal-payment sum reconciles against poSubtotalLive, not
// poNetLive. Confirmed via §6 query 2026-06-02:
// 26MOS-PO-007 (vdp=100, freight $124.20, tariff $108.42) has a
// single principal vendor_payments row at $1,668 (= product +
// freight + tariff), plus a separate 'Fee'-type CC processing
// fee row at $184.44.
export function poSubtotalLive(po, allItems) {
  if (!po || !po.items) return 0;

  function fp(s) {
    var n = parseFloat(String(s == null ? '' : s).replace(/[$,]/g, ''));
    return isNaN(n) ? 0 : n;
  }

  var subtotal = 0;
  for (var i = 0; i < po.items.length; i++) {
    var it = (allItems || []).find(function(x) { return x.id === po.items[i]; });
    if (!it) continue;
    subtotal += itemNet(it);
    var fees = it.fees || {};
    subtotal += fp(fees.freight);
    subtotal += fp(fees.crating);
    subtotal += fp(fees.tariff);
    subtotal += fp(fees.installation);
    // fees.salesTax intentionally omitted — see header.
    var custom = fees.custom || [];
    for (var j = 0; j < custom.length; j++) {
      var c = custom[j];
      if (!c) continue;
      if (isSalesTaxLikeCustomLabel(c.label)) continue;  // defensive — sales tax never rides on vendor PO
      subtotal += fp(c.amount);
    }
  }
  return Math.round(subtotal * 100) / 100;
}

// Live PO vendor-deposit total. Mirrors the math in PurchaseOrdersTab's
// previewPdf so the displayed and PDF totals agree without depending on
// the stored po.total_deposit value.
//
// Per-item contribution routes through calcVendorDepositWithFees: for
// vdp < 100 it's product × dPct only; for vdp >= 100 (in-stock) it's
// product × dPct + freight + crating + tariff + custom + installation
// (when item.fees.installation > 0). The PO builder save path
// (PurchaseOrdersTab.jsx itemDep) and the PDF render path
// (totalProductDeposit) BOTH route through the same helper as of Fix F
// (2026-05-28); this list-view helper also routes through it to keep
// the three surfaces (save / PDF / list) consistent. Pre-Fix-F this
// helper used inline product × dPct / 100 math which under-instructed
// for 100%-vdp items with fees.
//
// Installation rule (since 2026-06-03): per-item via fees.installation.
// No vendor-level flag. See calcVendorDepositWithFees header.
export function poDepositLive(po, allItems) {
  if (!po || !po.items) return 0;
  return Math.round(po.items.reduce(function(s, id) {
    var it = (allItems || []).find(function(x) { return x.id === id; });
    if (!it) return s;
    var dp = parseFloat(it.depositPct || 50) || 0;
    return s + calcVendorDepositWithFees(it.netPrice, it.qty, dp, it.fees);
  }, 0) * 100) / 100;
}

// Gross margin for one item: client total − net total
export function itemMargin(it) {
  return itemClient(it) - itemNet(it);
}

// Returns true if item should be excluded from all financial totals.
// ABD/Pre-App items were purchased by the prior project owner — not by the firm.
export function isABD(it) {
  var src = it.importSource || '';
  return src.indexOf('Pre-App') >= 0 || src.indexOf('ABD') >= 0;
}

// Returns true if the item is in the cancelled terminal state.
// Cancelled items keep their financial history via the credit ledgers but
// are excluded from POs, tracker, schedules, and billing calculations.
export function isCancelled(it) {
  return !!(it && it.status === 'Cancelled');
}

// Returns true if item should be included in billing calculations.
// Excludes child items (have a parentId), ABD-tagged items, and cancelled items.
export function isBillable(it) {
  return !it.parentId && !isABD(it) && !isCancelled(it);
}

// ── PROCUREMENT CLASS ────────────────────────────────────────────────────────
//
// Parallel axis to status. Tracks how an item is treated by the
// procurement/financial pipeline, not where it sits in its lifecycle.
//
//   'standard'        — default; current 14-status pipeline, full
//                       financials, SKU, QB push.
//   'owner_furnished' — client supplies the item; firm specs +
//                       installs. Truncated status pipeline,
//                       $0 contribution to all totals, no SKU,
//                       no QB.
//   'by_others'       — firm specs but another trade purchases +
//                       installs (plumber, electrician, GC). Same
//                       financial inertness as owner_furnished.
//
// Architectural note: this is parallel to ABD, not an extension.
// ABD = items purchased by the prior project owner (historical).
// Procurement class = current decision about who buys the item.
// isABD/isBillable stay untouched; financial-exclusion sites swap
// to shouldBillItem which composes both gates.

// Returns true when the item's procurement class is owner_furnished
// or by_others — i.e., the item should be excluded from all
// financial math, SKU assignment, and QB push.
export function isNonProcured(procurement_class) {
  return procurement_class === 'owner_furnished'
      || procurement_class === 'by_others';
}

// Composed financial gate: item is billable AND procurement class is
// not owner_furnished / by_others. Use this at every financial-
// exclusion site (subtotal computation, invoice generation, SKU
// assignment, QB push validation). Leaves isBillable + isABD
// untouched; just adds the OF/BO gate on top.
export function shouldBillItem(it) {
  if (!it) return false;
  if (!isBillable(it)) return false;
  if (isNonProcured(it.procurementClass)) return false;
  return true;
}

// Truncated status pipeline for OF/BO items. They skip Pricing
// Check / Quote* / On Proposal / Deposit Paid / PO Issued /
// Order Confirmed / Received / Invoiced — they don't get quoted,
// don't get proposed (financially), don't get POed, don't get
// final-invoiced. They DO get specified, presented, approved,
// installed, and completed (or cancelled).
var OF_BO_STATUSES = [
  'Specifying',
  'Presented to Client',
  'Client Approved',
  'Installed',
  'Complete',
  'Cancelled',
];

// Picker-friendly subset — statuses BEFORE Client Approved that a
// user can pick from the dropdown manually. From Client Approved
// onward, status transitions are owned by cascades (approveProposal,
// deposit-invoice paid, PO issued, etc.) — bypassing the cascade
// orphans the item. OF/BO items reach Client Approved via the same
// approveProposal cascade (their proposal subtotal is $0 but they
// still ride the approval flow for the cascade to fire).
export function manuallySelectableStatusesForProcurementClass(procurement_class) {
  if (procurement_class === 'owner_furnished' || procurement_class === 'by_others') {
    var idx = OF_BO_STATUSES.indexOf('Client Approved');
    return OF_BO_STATUSES.slice(0, idx);
  }
  return MANUALLY_SELECTABLE_STATUSES;
}

// ── PROPOSAL HELPERS ──────────────────────────────────────────────────────────

// Item is eligible for inclusion in a proposal if it is not a component
// (parentId must be falsy) and has not reached the financial lock point.
// Block at Client Approved and later — isFinanciallyCommitted returns true there.
export function proposalEligible(item) {
  if (item.parentId) return false;
  // OF/BO (2026-07-29): physical stages don't block proposal inclusion —
  // an owner-furnished piece can sit At Receiver AND later ride a
  // proposal for placement sign-off ($0 line). Approval or cancellation
  // is what ends eligibility for them.
  if (isNonProcured(item.procurementClass)) {
    return !isCancelled(item) && item.status !== 'Client Approved' && !item.clientApprovedDate;
  }
  return !isFinanciallyCommitted(item.status);
}

// Proposal-level deposit: 80% of the total client price, rounded to cents.
// Used for the proposal summary and for the PDF deposit line.
export function calcProposalDeposit(totalClient) {
  return Math.round(totalClient * 0.80 * 100) / 100;
}

// ── ITEM NUMBERING ────────────────────────────────────────────────────────────
// Format: 26HOB-101-001 (standalone/assembly), 26HOB-101-001A (component)
// projectCode — full slug stored on the project record (e.g. "26HOB")
// roomNumber  — raw value from item.roomNumber, zero-padded to 3 digits.
//               May carry ONE leading building letter (2026-07-08, the
//               Moskito convention: A201 = building A, room 201) —
//               normalized to uppercase, digits padded ("a21" → "A021").
//               SKU shape becomes 26MOS-A201-001. The letter is part of
//               the room segment; the OPTIONAL TRAILING letter on the seq
//               segment remains the parentId-component suffix (001A) and
//               the two never collide.
// seq         — 1-based integer from item_number_sequences table

export function formatItemNumber(projectCode, roomNumber, seq) {
  var raw = String(roomNumber == null ? '' : roomNumber).trim();
  var m = raw.match(/^([A-Za-z]?)(\d+)$/);
  var room = m
    ? m[1].toUpperCase() + m[2].padStart(3, '0')
    : raw.padStart(3, '0');
  var s = String(seq).padStart(3, '0');
  return projectCode + '-' + room + '-' + s;
}

// letterIndex: 0 → A, 1 → B, … 25 → Z
export function formatComponentNumber(parentItemNumber, letterIndex) {
  return parentItemNumber + String.fromCharCode(65 + letterIndex);
}

// Validates that a string conforms to the standard SKU format used as the QB SKU.
// Valid:   26HOB-101-001  /  26HOB-101-001A  /  26PTM-203-015C
//          26MOS-A201-001 (building-letter room, Moskito convention)
// Invalid: anything without the exact PROJECT-[B]RRR-SSS[L] structure.
export function isValidItemNumber(str) {
  if (!str) return false;
  return /^[A-Z0-9]+-[A-Z]?\d{3}-\d{3}[A-Z]?$/.test(str);
}

// Duplicate check against an array of item objects — excludes the item being edited.
// Used in the inline SKU editor and will be reused in the QB export step.
export function isDuplicateItemNumberInItems(num, items, excludeId) {
  if (!num) return false;
  return (items || []).some(function(it) {
    return it.id !== excludeId && it.itemNumber === num;
  });
}

// Async — sequence and component count are injected via opts to keep utils.js pure.
// opts.getNextSeq(projectCode, roomNumber)  → Promise<number>  (standalone only)
// opts.getComponentCount(parentItemNumber)  → Promise<number>  (component only)
export async function generateItemNumber(projectCode, roomNumber, isComponent, parentItemNumber, opts) {
  if (isComponent) {
    var count = await opts.getComponentCount(parentItemNumber);
    return formatComponentNumber(parentItemNumber, count);
  }
  var seq = await opts.getNextSeq(projectCode, roomNumber);
  return formatItemNumber(projectCode, roomNumber, seq);
}

// ── ASSEMBLY-AWARE PRICING ────────────────────────────────────────────────────
// Assembly components are separate DB rows with parentId set.
// The parent item itself has netPrice: 0 — all pricing lives in the components.
// Use these functions to get the true cost of any item (standalone or assembly).

// Returns all non-ABD components for a given parent id
export function assemblyComponents(parentId, allItems) {
  return (allItems || []).filter(function(it) {
    return it.parentId === parentId && !isABD(it);
  });
}

// Net total: sum of components if assembly, otherwise item's own price
export function assemblyNet(parent, allItems) {
  var components = assemblyComponents(parent.id, allItems);
  if (components.length === 0) return itemNet(parent);
  return components.reduce(function(s, c) { return s + itemNet(c); }, 0);
}

// Client total: sum of components if assembly, otherwise item's own price
export function assemblyClient(parent, allItems) {
  var components = assemblyComponents(parent.id, allItems);
  if (components.length === 0) return itemClient(parent);
  return components.reduce(function(s, c) { return s + itemClient(c); }, 0);
}

// Gross margin: client total − net total
export function assemblyMargin(parent, allItems) {
  return assemblyClient(parent, allItems) - assemblyNet(parent, allItems);
}

// Returns whether an Approved proposal can be reverted to Sent for editing.
// Blocked when any item (parent or assembly component) has advanced past
// Client Approved into financial-activity statuses (Deposit Paid, PO Issued,
// etc.) — those require a Change Order, not a status flip.
export function canRevertProposal(proposalItemIds, allItems) {
  var pItems = (allItems || []).filter(function(it) {
    return (proposalItemIds || []).indexOf(it.id) >= 0;
  });
  var blockers = [];
  pItems.forEach(function(parent) {
    var comps = (allItems || []).filter(function(c) { return c.parentId === parent.id; });
    var pool = [parent].concat(comps);
    pool.forEach(function(it) {
      var idx = STATUSES.indexOf(it.status);
      if (idx > LOCK_INDEX) blockers.push({ id: it.id, item: it.item || it.productName || '', status: it.status });
    });
  });
  return { canRevert: blockers.length === 0, blockers: blockers };
}

// Returns the two sets needed by approveProposal():
//   toApprove  — all items whose status should move to Client Approved
//   toSku      — subset that still need an item number assigned
//
// Walks BOTH child models so partial selection still pulls in the rest:
//  1. parentId children (lighting/bulb pattern) -- a parent's children
//     come along automatically.
//  2. assemblyId peers (drapery/furniture/millwork/plumbing) -- if the
//     bookkeeper picked one peer of an assembly into p.items, the other
//     peers in that assembly come along too. Without this, a forgotten
//     workroom or fabric would silently never advance to Client Approved
//     and would block the rest of the proposal from issuing POs.
//
// Items with an existing itemNumber are included in toApprove but skipped in toSku.
export function collectApprovalTargets(proposalItemIds, allItems) {
  var pItems = (allItems || []).filter(function(it) {
    return (proposalItemIds || []).indexOf(it.id) >= 0;
  });
  var seen = {};
  var toApprove = [];
  var toSku = [];
  function add(it) {
    if (!it || seen[it.id]) return;
    seen[it.id] = true;
    toApprove.push(it);
    if (!it.itemNumber) toSku.push(it);
  }
  // Build a set of assembly ids referenced by anything in p.items so
  // we can pull in peers without re-scanning per item.
  var asmIds = {};
  pItems.forEach(function(it) { if (it.assemblyId) asmIds[it.assemblyId] = true; });
  pItems.forEach(function(parent) {
    add(parent);
    // 1. Walk parentId-children of this parent.
    (allItems || []).forEach(function(c) {
      if (c.parentId === parent.id) add(c);
    });
  });
  // 2. Walk assemblyId-peers across the whole catalog. Anything in
  //    asmIds with this assemblyId joins toApprove.
  Object.keys(asmIds).forEach(function(asmId) {
    (allItems || []).forEach(function(it) {
      if (it.assemblyId === asmId) add(it);
    });
  });
  return { toApprove: toApprove, toSku: toSku };
}

// Items at these statuses are eligible to land on a Purchase Order.
//
// SPEC 1 (2026-05-21) — widened from 'Deposit Paid' only to include
// 'Client Approved'. Previously a hard block ("no client deposit
// recorded → can't PO"); now a soft warning that fires at PO issuance
// (Draft → Issued) when shortfall > 0. See computePoBypassShortfall
// below and PurchaseOrdersTab.advanceStatus.
//
// The financial reasoning (CLAUDE.md "Vendor deposit paid → PO issued")
// still holds in the default case — most POs SHOULD wait for client
// deposit. The bypass exists for legitimate exceptions: an acquired
// firm's prior deposit, an emergency rush order, a client whose payment
// terms are net-30 by contract. The bookkeeper sees bypassed POs in
// the QB-tab worklist via purchase_orders.client_payment_status.
//
// Single source of truth: PurchaseOrdersTab and tests both import this.
export function isPoEligibleStatus(status) {
  return status === 'Client Approved' || status === 'Deposit Paid';
}

// How much of THIS item's expected client deposit has not yet been paid.
//
// Architectural rule (CLAUDE.md "Payment state comes from payment
// records"): consumers asking "has this been paid?" must read
// payment_allocations, not item-status or static-percentage proxies.
// This helper is the single source of truth for that question at the
// item level.
//
// Math:
//   expected   = itemDepositAmount(item, allItems)
//                — uses per-item depositPct, so manual overrides (e.g.
//                70%) propagate correctly; the canonical 80% is not
//                hardcoded anywhere.
//   inv        = the matched deposit invoice on item.clientDepositInvoiceId
//   paidOnInv  = SUM(payment_allocations.amount where docType='invoice'
//                AND docId = inv.id) — actual cash received against
//                the invoice
//   paidRatio  = min(1, paidOnInv / inv.amountDue) — clamped at 1 so
//                over-payment can't generate negative unpaid balances
//   unpaid     = expected × (1 - paidRatio) — pro-rata across all items
//                covered by the invoice, since real-world allocations
//                are invoice-level not item-level
//
// Edge cases (all return `expected` rounded — defensive: when in doubt
// treat as fully unpaid so downstream consumers fail toward over-
// collecting rather than under-collecting):
//   - item has no clientDepositInvoiceId      → full expected unpaid
//   - matched invoice missing from invoices[] → full expected unpaid
//   - invoice.amountDue <= 0 AND status≠paid  → full expected unpaid
//                                              (malformed-invoice case)
//
// Migrated-invoice fallbacks (Fix D, 2026-05-27):
//   Phase 1 + 1.1 — amtDue > 0, allocations empty, status='paid':
//     paid===0 AND inv.status (lowercased, trimmed) === 'paid'
//     → return 0 (fully paid). Catches Design Manager imports where
//     payment_log JSONB was absent at 20260429 backfill time, so no
//     allocation rows were ever written.
//   Phase 1.2 — amtDue=0, status='paid' (the close-out shorthand):
//     amtDue<=0 AND inv.status (lowercased, trimmed) === 'paid'
//     → return 0 (fully paid). Disambiguates the close-out pattern
//     (amount_due zeroed at payment time) from the malformed-invoice
//     case (amount_due NULL because the invoice was never billed).
//
// Both phases match status case-insensitively (Design Manager stamps
// capital 'Paid', Gilda-native writes lowercase 'paid'). Phase 2
// (deferred) backfills allocation rows so both fallbacks become dead
// code; until then they belt-and-suspenders the migration data shape.
//
// Returns a number rounded to cents.
export function itemUnpaidDepositShare(item, invoices, allocations, allItems) {
  if (!item) return 0;
  var expected = parseFloat(itemDepositAmount(item, allItems)) || 0;
  if (expected <= 0) return 0;

  var depInvId = item.clientDepositInvoiceId;
  if (!depInvId) return Math.round(expected * 100) / 100;

  var inv = (invoices || []).find(function(i) {
    return i && i.id === depInvId && (i.invoiceType === 'deposit' || i.invoice_type === 'deposit');
  });
  if (!inv) return Math.round(expected * 100) / 100;

  var amtDue = parseFloat(inv.amountDue != null ? inv.amountDue : inv.amount_due) || 0;
  if (amtDue <= 0) {
    // Fix D Phase 1.2 (2026-05-27) — disambiguate two meanings of
    // amtDue=0:
    //   (a) Invoice not yet billed / malformed state — defensive
    //       behavior: return full expected unpaid (over-collect rather
    //       than under-collect).
    //   (b) Deposit fully paid; amount_due zeroed at close-out (the
    //       operational shorthand used to mark migrated paid invoices).
    //       Return 0 unpaid so the final-invoice math correctly bills
    //       lineClient − expectedDeposit, not the full lineClient.
    // Status (case-insensitive, whitespace-trimmed) is the discriminator.
    if (String(inv.status || '').trim().toLowerCase() === 'paid') {
      return 0;
    }
    return Math.round(expected * 100) / 100;
  }

  var paid = (allocations || []).reduce(function(s, a) {
    if (!a) return s;
    if (a.documentType !== 'invoice' && a.document_type !== 'invoice') return s;
    var docId = a.documentId != null ? a.documentId : a.document_id;
    if (String(docId) !== String(depInvId)) return s;
    return s + (parseFloat(a.amount) || 0);
  }, 0);

  // Fix D Phase 1 (2026-05-27) — migrated-invoice fallback. Design Manager
  // imports populated invoices.status='paid' but the 20260429 client-
  // payments backfill could only write payment_allocations rows for
  // invoices that carried the legacy `payment_log` JSONB column. Design
  // Manager imports lacked that source, so their relational allocation
  // shape is empty — `paid` computes as 0 against the canonical query.
  //
  // CLAUDE.md Rule 10 ("Payment state comes from payment records, not
  // proxies") treats invoice status as non-authoritative because Gilda-
  // native invoices derive status from allocations — status goes stale
  // on partial payments. The rule's premise inverts for migrated data:
  // status was set BY the import and allocations were never written.
  //
  // Narrow condition: paid===0 AND inv.status (case-insensitive,
  // whitespace-trimmed) matches 'paid'. Can't fire on Gilda-native data
  // because (a) any Gilda payment writes an allocation row first →
  // paid > 0, and (b) Gilda-native invoices only reach status='paid'
  // via derivePaidStatus, which itself reads allocations. For Gilda-
  // native, paid===0 and status==='paid' is structurally impossible.
  // Phase 2 (deferred) backfills allocation rows for these invoices so
  // the fallback becomes dead code; until then it stops the final-
  // invoice over-bill.
  //
  // Phase 1.1 (2026-05-27): condition normalized to case + whitespace.
  // Design Manager imports stamp `status='Paid'` (capital P; see header
  // comment of 20260425_create_invoices_table.sql) while Gilda-native
  // writes lowercase 'paid' via derivePaidStatus. Pre-1.1 the condition
  // only matched the lowercase form, so migrated invoices fell through
  // and the over-bill persisted in production. The Ledger and Billing-
  // Summary surfaces have the same casing-blindness against migrated
  // data — filed as Phase 1.3 follow-up.
  //
  // Phase 1.2 (2026-05-27): added a parallel fallback in the amtDue<=0
  // branch above. Some migrated invoices were marked paid by zeroing
  // amount_due rather than (or in addition to) flipping status='paid'.
  // That triggered the malformed-invoice early-exit before this point.
  // Phase 1.2 disambiguates by checking status inside that branch.
  // Phase 1.1 + 1.2 together cover both close-out shapes the migration
  // data carries (amount_due unchanged vs amount_due zeroed); either
  // path reaches the same outcome (return 0 unpaid).
  //
  // Partial-status branch NOT implemented: the legacy `paid_amount`
  // column was dropped in 20260429, so there's no source for the
  // partial-paid scalar. A migrated invoice with status='partial' and
  // no allocations falls through to the existing path → full expected
  // returned as unpaid (defensive, fail-toward-over-collect, current
  // behavior). If this case surfaces in production, the bookkeeper
  // records the actual payments as Gilda allocations and the path
  // resolves naturally.
  if (paid === 0 && String(inv.status || '').trim().toLowerCase() === 'paid') {
    return 0;
  }

  // Per-item deposit invoices (since 2026-05-26): the invoice is 1:1 with
  // this item — itemId on the invoice matches this item's id. Math
  // simplifies to "unpaid = amountDue - paid" (no pro-rata; the invoice
  // covers THIS item alone). Pre-2026-05-26 proposal-level shared invoices
  // continue through the pro-rata path below.
  var isPerItem = inv.itemId
    ? String(inv.itemId) === String(item.id)
    : (inv.item_id ? String(inv.item_id) === String(item.id) : false);
  if (isPerItem) {
    var unpaidDirect = amtDue - paid;
    if (unpaidDirect < 0) unpaidDirect = 0;
    return Math.round(unpaidDirect * 100) / 100;
  }

  var paidRatio = paid > 0 ? Math.min(1, paid / amtDue) : 0;
  var unpaid = expected * (1 - paidRatio);
  return Math.round(unpaid * 100) / 100;
}

// ── MODEL B PER-ITEM HELPERS (2026-06-03) ────────────────────────────────
//
// Investigation surfaced that buildFinalInvoicePayload was computing
// `expectedDeposit` FRESH at FIN-build time from current item state,
// then comparing against payments to derive balance. That math drops
// the cash gap silently when the deposit basis changes between
// DEP-issuance and FIN-build (vdp via CO, manual price edits, FX
// delta, etc.). See /tmp/fin-deposit-basis-investigation-20260603.md
// for the full bug analysis.
//
// Model B per-item replaces the basis-comparison math with simple
// per-item cash math:
//
//   itemBalance = itemLifecycleOwed(item) − itemPaidShare(item)
//
// Where:
//   itemLifecycleOwed — total client-facing value for this item
//                       across DEP + FIN (product × markup + all
//                       applicable fees marked up + per-item tax).
//                       Doesn't consult depositPct at all. Fixed by
//                       item state.
//   itemPaidShare     — actual cash received against this item's
//                       deposit invoice. Per-item DEP: direct sum.
//                       Proposal-level DEP fallback: proportional
//                       split of shared-DEP payments based on each
//                       item's expected share within the shared DEP.
//
// Whatever rule change happens between DEP-issue and FIN-build is
// captured automatically: the lifecycle total is fixed, the paid
// amount is fixed, the FIN bills the gap. depositPct stays relevant
// at DEP-build time (still uses clientDepositPct(vdp) there) but
// FIN-time no longer consults it.
//
// CancelItemModal also routes through itemPaidShare for its credit
// amount calculation, replacing the equivalent clientDepositPct(vdp)
// × clientTotal math that has the same drift bug shape.

// Returns the total dollar value the client owes for this item across
// the proposal's whole lifecycle (sum of DEP + FIN). Includes:
//   - product (lineClient = netPrice × markup × qty, assembly-aware)
//   - freight × 1.10
//   - crating × 1.10
//   - installation × 1.10 (when item.fees.installation > 0; the client
//     owes this regardless of who performs the install. The vendor PO
//     inclusion is a SEPARATE concern handled by calcVendorDepositWithFees
//     and the PO subtotal helpers — both also gate on per-item
//     fees.installation since 2026-06-03)
//   - tariff × 1.00 (pass-through)
//   - custom fees × 1.10
//   - per-item sales tax (via computeItemTax when project tax config
//     present; else stored fees.salesTax × 1.00 as legacy fallback)
//
// Notably DOES NOT depend on depositPct — this is the lifecycle total,
// not what's billed at DEP vs FIN. depositPct only determines TIMING
// at DEP-build, not the lifecycle total.
//
// `project` is optional (only needed for fresh sales-tax computation
// when project has tax config). Backward compatible — when omitted,
// falls back to fees.salesTax × 1.00 (legacy field, treated as
// pass-through).
export function itemLifecycleOwed(item, allItems, project) {
  if (!item) return 0;

  function fp(v) {
    var n = parseFloat(String(v == null ? '' : v).replace(/[$,]/g, ''));
    return isNaN(n) ? 0 : n;
  }

  var lineClient = parseFloat(assemblyClient(item, allItems || [item])) || 0;
  var fees = item.fees || {};

  var feesTotal = 0;
  if (fees.freight)      feesTotal += applyFreightMarkup(fp(fees.freight), item);
  if (fees.crating)      feesTotal += applyFreightMarkup(fp(fees.crating), item);
  if (fees.installation) feesTotal += applyFreightMarkup(fp(fees.installation), item);
  if (fees.tariff)       feesTotal += applyTaxMarkup(fp(fees.tariff));
  (fees.custom || []).forEach(function(c) {
    feesTotal += customFeeClient(c, item);
  });

  var tax = 0;
  if (hasProjectTaxConfig(project)) {
    tax = computeItemTax(item, project, allItems || [item]);
  } else if (fees.salesTax) {
    tax = applyTaxMarkup(fp(fees.salesTax));
  }

  return Math.round((lineClient + feesTotal + tax) * 100) / 100;
}

// Returns the actual cash received against this item's deposit invoice.
// Reads payment_allocations (canonical payment record per CLAUDE.md
// Rule 10). No re-computation from depositPct or any other rules.
//
// Per-item DEP (post-2026-05-26 default): item.clientDepositInvoiceId
// points to a DEP invoice with itemId === item.id. Allocations against
// that invoice are this item's paid share, directly.
//
// Proposal-level shared DEP (legacy, pre-2026-05-26): multiple items
// share one DEP. The shared DEP's amount_due was originally
// SUM(per-item expected deposits) at DEP-issuance. To attribute
// allocations per-item, split proportionally by each item's expected
// deposit share within the shared DEP. Uses itemDepositAmount on the
// item (which reads current depositPct — best available approximation
// for legacy data; if vdp changed since DEP-issuance, this becomes a
// rough estimate, not exact).
//
// Edge cases:
//   - item has no clientDepositInvoiceId      → returns 0 (item hasn't
//                                                 been deposit-invoiced
//                                                 yet; FIN bills the
//                                                 full lifecycle total)
//   - matched DEP invoice missing from invoices[] → 0 (defensive — same
//                                                 as no clientDepositInvoiceId)
//   - migrated invoice with allocations missing
//     but status='paid' (Fix D shape)        → returns the matched
//                                                DEP's amount_due
//                                                (treats as fully paid)
//
// Returns a number rounded to cents.
export function itemPaidShare(item, invoices, allocations, allItems) {
  if (!item) return 0;
  var depInvId = item.clientDepositInvoiceId;
  if (!depInvId) return 0;

  var inv = (invoices || []).find(function(i) {
    return i && i.id === depInvId && (i.invoiceType === 'deposit' || i.invoice_type === 'deposit');
  });
  if (!inv) return 0;

  var amtDue = parseFloat(inv.amountDue != null ? inv.amountDue : inv.amount_due) || 0;
  var statusLc = String(inv.status || '').trim().toLowerCase();
  var isPerItem = inv.itemId
    ? String(inv.itemId) === String(item.id)
    : (inv.item_id ? String(inv.item_id) === String(item.id) : false);

  // Sum allocations against this DEP invoice.
  var paid = (allocations || []).reduce(function(s, a) {
    if (!a) return s;
    var dt = a.documentType != null ? a.documentType : a.document_type;
    if (dt !== 'invoice') return s;
    var docId = a.documentId != null ? a.documentId : a.document_id;
    if (String(docId) !== String(depInvId)) return s;
    return s + (parseFloat(a.amount) || 0);
  }, 0);

  // Fix D migrated-invoice fallback (parity with itemUnpaidDepositShare).
  // Two close-out shapes Design Manager imports use to mark fully-paid:
  //
  //   Phase 1.2: amtDue=0 + status='paid' (amount_due zeroed at payment time)
  //     → treat as fully paid; itemPaidShare returns item's expected DEP
  //       amount (the closest we can compute to "what was billed when
  //       the DEP was issued"). Without this, balance = lifecycleOwed
  //       and the FIN double-bills.
  //
  //   Phase 1   : amtDue>0 + status='paid' + paid=0 (no allocation rows
  //     written by the 20260429 backfill because the DM payment_log
  //     column was absent) → treat as fully paid against the matched DEP.
  //
  // Phase 1.1 — case-insensitive 'paid' match (DM stamps 'Paid' capital P;
  // Gilda-native writes lowercase).
  if (paid === 0 && statusLc === 'paid') {
    if (amtDue <= 0) {
      // Phase 1.2 close-out shorthand: amount_due zeroed at payment time.
      // Best estimate of paid: the item's expected deposit under current
      // depositPct. If vdp drifted since DEP-issuance this is a rough
      // approximation, but for migrated DM data vdp typically hasn't
      // changed.
      var phase12Paid = parseFloat(itemDepositAmount(item, allItems || [item])) || 0;
      return Math.round(phase12Paid * 100) / 100;
    }
    // Phase 1: amtDue>0 + status='paid' + paid=0. Same treatment: full
    // paid on this item. Per-item or pro-rata.
    if (isPerItem) {
      return Math.round(amtDue * 100) / 100;
    }
    var itemExpectedP1 = parseFloat(itemDepositAmount(item, allItems || [item])) || 0;
    return Math.round(itemExpectedP1 * 100) / 100;
  }

  // Per-item DEP: invoice has itemId === item.id. Direct attribution.
  if (isPerItem) {
    // Cap at amtDue (can't have paid more than what was billed against
    // this item — though Anne P-013 surfaces a real overpay case where
    // the wire exceeded amtDue; that overflow is recorded as an
    // allocation against THIS FIN rather than against the DEP, so it
    // shouldn't appear in this sum to begin with).
    var attributed = Math.min(paid, amtDue);
    return Math.round(attributed * 100) / 100;
  }

  // Proposal-level shared DEP. Three-layer attribution chain
  // (2026-06-03, post-Kumiko diagnosis):
  //
  // Layer 1 — single-item shared DEP. inv.item_ids has 0 or 1 entries
  //   (legacy proposals where only one item rode the DEP). All paid is
  //   for this item. Capped at amtDue defensively.
  //
  // Layer 2 — multi-item shared DEP with line_items snapshot. Zip
  //   inv.item_ids[i] to inv.line_items[i].depositAmt (frozen at DEP
  //   issuance; verified on 26HOB-INV-001-DEP + 26HOB-INV-003-DEP).
  //   Per-item share = paid × (this item's billed depositAmt / sum of
  //   billed depositAmts on this DEP). Preserves historical basis even
  //   if items drift via CO after DEP issuance — this is the Kumiko-
  //   shape fix.
  //
  // Layer 3 — fallback. Multi-item with no usable line_items snapshot
  //   (defensive path for malformed rows). Current-basis proportional
  //   via itemDepositAmount; drift-prone but the only realistic option
  //   when historical data is gone.
  //
  // Pre-fix the only branch was layer 3, and it produced the Kumiko
  // $117,770 (vs actual cash $115,198) drift bug when the post-CO
  // clientTotal × cdp inflated the numerator above the historical
  // denominator (amtDue).
  var itemIdsRaw = inv.itemIds != null ? inv.itemIds : inv.item_ids;
  var itemIds = Array.isArray(itemIdsRaw) ? itemIdsRaw : [];

  // Layer 1 — single-item shared DEP
  if (itemIds.length <= 1) {
    var capped = paid > amtDue ? amtDue : paid;
    return Math.round(capped * 100) / 100;
  }

  // Layer 2 — multi-item with line_items snapshot zipped by index
  var lineItemsRaw = inv.lineItems != null ? inv.lineItems : inv.line_items;
  var lineItemsArr = Array.isArray(lineItemsRaw) ? lineItemsRaw : [];
  if (lineItemsArr.length === itemIds.length) {
    var idx = -1;
    for (var i = 0; i < itemIds.length; i++) {
      if (String(itemIds[i]) === String(item.id)) { idx = i; break; }
    }
    if (idx >= 0 && lineItemsArr[idx]) {
      var thisDepAmt = lineItemsArr[idx];
      var billed = parseFloat(thisDepAmt.depositAmt != null ? thisDepAmt.depositAmt : thisDepAmt.deposit_amt) || 0;
      var totalBilled = 0;
      for (var j = 0; j < lineItemsArr.length; j++) {
        var li = lineItemsArr[j] || {};
        totalBilled += parseFloat(li.depositAmt != null ? li.depositAmt : li.deposit_amt) || 0;
      }
      if (totalBilled > 0 && billed > 0) {
        return Math.round(paid * (billed / totalBilled) * 100) / 100;
      }
    }
  }

  // Layer 3 — fallback (current-basis proportional)
  if (amtDue <= 0) return 0;
  var itemExpected = parseFloat(itemDepositAmount(item, allItems || [item])) || 0;
  if (itemExpected <= 0) return 0;
  var itemShareOfPaid = paid * (itemExpected / amtDue);
  return Math.round(itemShareOfPaid * 100) / 100;
}

// ── LEDGER HELPERS (since 2026-05-24) ────────────────────────────────────────
//
// Pure data resolvers backing the Ledger workspace. Each helper takes
// raw collections + an item/po/bs and returns derived state for one
// row or one cell. The Ledger UI (LedgerProcurement, LedgerBillingSummaries,
// LedgerReceipts) composes these without any new SQL or state.
//
// Architectural rule (CLAUDE.md Rule 10): payment state comes from
// payment records. These helpers read payment_allocations, vendor_payments,
// and (for paid-at dates) client_payments — never from item-status proxies
// or static percentages.

// Forward-looking unpaid balance on the FINAL invoice for an item.
//
// Two paths:
//   (a) Item has a finalInvoiceId set AND the matching invoice exists →
//       pro-rata of the linked invoice's unpaid balance, scaled by this
//       item's contribution share. Same model as itemUnpaidDepositShare
//       above: paidRatio = min(1, paid / amountDue); item's contribution
//       is its (clientTotal − expectedDeposit) post-deposit balance.
//   (b) Item has no finalInvoiceId yet → return the PROJECTED final
//       balance (what a final invoice would bill if generated today).
//       This is the forward-looking value the Ledger needs for its
//       Outstanding-Client column — bookkeepers care about "what's
//       still owed across all in-flight work," not just "what's owed
//       against an already-issued final."
//
// Returns 0 (not undefined) for items where no final liability exists:
//   - Component items (parentId set)
//   - ABD / Pre-App items (isBillable === false)
//   - Items at status < Client Approved (no commitment yet)
//   - Items already on a final invoice that was fully paid
//
// Defensively returns the projected amount when the matched final invoice
// is missing from the invoices array (data-loading races) — same posture
// as itemUnpaidDepositShare's "fail toward over-collect" stance.
//
// item: { finalInvoiceId, netPrice, markupPct, qty, fees, depositPct, ... }
// invoices: from AppContext.invoices
// allocations: from AppContext.paymentAllocations
// allItems: from AppContext.items (for assembly lookups)
//
// Returns a number rounded to cents.
export function itemProjectedFinalBalance(item, invoices, allocations, allItems) {
  if (!item || item.parentId) return 0;
  if (!shouldBillItem(item)) return 0;

  var finalInvId = item.finalInvoiceId;
  if (!finalInvId) {
    // Path (b): no final invoice yet. Return projected balance.
    return parseFloat(estimatedFinalInvoiceAmount(item, allItems || [item], invoices, allocations)) || 0;
  }

  // Path (a): final invoice exists. Pro-rata its unpaid against this
  // item's contribution.
  var inv = (invoices || []).find(function(i) {
    return i && i.id === finalInvId && (i.invoiceType === 'final' || i.invoice_type === 'final');
  });
  if (!inv) {
    // Orphan finalInvoiceId — points to a final invoice that no longer
    // exists (deleted draft, archived row, replication lag). Treat as
    // "no final yet" and project forward. estimatedFinalInvoiceAmount
    // refuses to project when finalInvoiceId is set, so we strip the
    // field on a synthetic item to bypass the guard for this one call.
    var defensiveItem = Object.assign({}, item, { finalInvoiceId: null });
    return parseFloat(estimatedFinalInvoiceAmount(defensiveItem, allItems || [item], invoices, allocations)) || 0;
  }
  var amtDue = parseFloat(inv.amountDue != null ? inv.amountDue : inv.amount_due) || 0;
  if (amtDue <= 0) return 0;

  // This item's contribution to the final invoice = post-deposit balance.
  var clientTotal     = parseFloat(assemblyClient(item, allItems || [item])) || 0;
  var expectedDeposit = parseFloat(itemDepositAmount(item, allItems || [item])) || 0;
  var itemFinalContribution = Math.max(0, clientTotal - expectedDeposit);
  if (itemFinalContribution <= 0) return 0;

  // Sum allocations against this invoice.
  var paid = (allocations || []).reduce(function(s, a) {
    if (!a) return s;
    if (a.documentType !== 'invoice' && a.document_type !== 'invoice') return s;
    var docId = a.documentId != null ? a.documentId : a.document_id;
    if (String(docId) !== String(finalInvId)) return s;
    return s + (parseFloat(a.amount) || 0);
  }, 0);

  var paidRatio = paid > 0 ? Math.min(1, paid / amtDue) : 0;
  var unpaid = itemFinalContribution * (1 - paidRatio);
  return Math.round(unpaid * 100) / 100;
}

// Find the PO that contains this item, using purchase_orders.items[] as
// the source of truth (more reliable than items.po_number, which can be
// blank for items added via the PO picker without a per-row stamp).
//
// Returns the PO row or null if not on a PO. Walks the array, so O(N×M)
// at the call site; callers building a full Ledger should iterate items
// once and build a lookup map for performance.
export function findPoForItem(item, purchaseOrders) {
  if (!item || !item.id) return null;
  var pos = purchaseOrders || [];
  for (var i = 0; i < pos.length; i++) {
    var po = pos[i];
    if (!po) continue;
    var ids = po.items || [];
    if (Array.isArray(ids) && ids.indexOf(item.id) >= 0) return po;
  }
  return null;
}

// Internal helper — most-recent payment date across the allocations for
// a single document. Walks allocation.payment_id → client_payments[].
// Returns ISO date string or '' if no allocation found.
function mostRecentPaidAt(allocations, payments, docType, docId) {
  if (!docId) return '';
  var match = (allocations || []).filter(function(a) {
    if (!a) return false;
    var t = a.documentType || a.document_type;
    var d = a.documentId != null ? a.documentId : a.document_id;
    return t === docType && String(d) === String(docId);
  });
  if (match.length === 0) return '';
  var byId = {};
  (payments || []).forEach(function(p) { if (p && p.id) byId[p.id] = p; });
  var dates = match
    .map(function(a) {
      var p = byId[a.paymentId || a.payment_id] || {};
      return p.paymentDate || p.payment_date || a.createdAt || a.created_at || '';
    })
    .filter(Boolean);
  if (dates.length === 0) return '';
  return dates.sort().reverse()[0];  // ISO strings sort lexicographically
}

// Per-item resolved status for the matched DEPOSIT invoice.
//
// Returns null if the item has no clientDepositInvoiceId or the invoice
// can't be found. Otherwise returns the document's stored fields + the
// derived paid state (status / amount paid / paid-at date). Callers use
// this to populate Ledger cells without re-deriving the join on every
// render.
//
// item: { clientDepositInvoiceId, ... }
// invoices, allocations, payments: from AppContext
export function depositStatusForItem(item, invoices, allocations, payments) {
  if (!item || !item.clientDepositInvoiceId) return null;
  var inv = (invoices || []).find(function(i) {
    return i && i.id === item.clientDepositInvoiceId
      && (i.invoiceType === 'deposit' || i.invoice_type === 'deposit');
  });
  if (!inv) return null;
  var amtDue = parseFloat(inv.amountDue != null ? inv.amountDue : inv.amount_due) || 0;
  var paid = calcPaidForDocument(allocations, 'invoice', inv.id);
  var derived = derivePaidStatus(allocations, 'invoice', inv.id, amtDue, inv.status);
  return {
    invoice:     inv,
    invoiceRef:  inv.invoiceRef || inv.invoice_ref || '',
    sentAt:      inv.sentAt || inv.sent_at || '',
    amountDue:   amtDue,
    paidAmount:  Math.round(paid * 100) / 100,
    paidStatus:  derived,             // 'paid' | 'partial' | 'sent' | 'draft'
    paidAt:      mostRecentPaidAt(allocations, payments, 'invoice', inv.id),
    qbInvoiceId:  inv.qbInvoiceId  || '',
    qbEstimateId: inv.qbEstimateId || '',
    qbPushedAt:   inv.qbPushedAt   || '',
    qbError:      inv.qbError      || '',
  };
}

// Per-item resolved status for the matched FINAL invoice. Same shape as
// depositStatusForItem.
export function finalStatusForItem(item, invoices, allocations, payments) {
  if (!item || !item.finalInvoiceId) return null;
  var inv = (invoices || []).find(function(i) {
    return i && i.id === item.finalInvoiceId
      && (i.invoiceType === 'final' || i.invoice_type === 'final');
  });
  if (!inv) return null;
  var amtDue = parseFloat(inv.amountDue != null ? inv.amountDue : inv.amount_due) || 0;
  var paid = calcPaidForDocument(allocations, 'invoice', inv.id);
  var derived = derivePaidStatus(allocations, 'invoice', inv.id, amtDue, inv.status);
  return {
    invoice:     inv,
    invoiceRef:  inv.invoiceRef || inv.invoice_ref || '',
    sentAt:      inv.sentAt || inv.sent_at || '',
    amountDue:   amtDue,
    paidAmount:  Math.round(paid * 100) / 100,
    paidStatus:  derived,
    paidAt:      mostRecentPaidAt(allocations, payments, 'invoice', inv.id),
    qbInvoiceId:  inv.qbInvoiceId  || '',
    qbEstimateId: inv.qbEstimateId || '',
    qbPushedAt:   inv.qbPushedAt   || '',
    qbError:      inv.qbError      || '',
  };
}

// Aggregated vendor-payment status for a PO. Vendor payments are PO-scoped
// (not item-scoped) so this returns one rollup per PO, shared across every
// item on that PO.
//
// payment_type canonical values (vendorPaymentTypeForReceiptKind):
//   'Deposit'         — vendor_deposit principal
//   'Deposit Payment' — legacy alias for 'Deposit' (early data shape)
//   'Final'           — vendor_final principal
//   'Full'            — vendor_full_payment principal (in-stock 100% upfront)
//   'Fee'             — freight / crating / tariff (vendor_fee receipts)
//   'Adjustment'      — owner-entered correction (can be + or −)
//
// Returns:
//   {
//     po: <po row>,
//     depositPaid: sum of Deposit + Deposit Payment + Full,
//     finalPaid:   sum of Final,
//     feesPaid:    sum of Fee,
//     adjustments: sum of Adjustment (signed),
//     depositAt:   most-recent payment date in the deposit bucket,
//     finalAt:     most-recent payment date in the final bucket,
//     qbBillIds:   array of qb_bill_id values populated on the bucket,
//     qbErrors:    array of distinct qb_error messages (empty when clean)
//   }
//
// Returns null when po is falsy.
export function vendorPaymentStatusForPo(po, vendorPayments) {
  if (!po || !po.id) return null;
  var rows = (vendorPayments || []).filter(function(vp) { return vp && vp.poId === po.id; });
  function inBucket(types) {
    return rows.filter(function(vp) { return types.indexOf(vp.paymentType) >= 0; });
  }
  function sumAmt(list) {
    return Math.round(list.reduce(function(s, vp) { return s + (parseFloat(vp.amount) || 0); }, 0) * 100) / 100;
  }
  function mostRecent(list) {
    var dates = list.map(function(vp) { return vp.paymentDate || ''; }).filter(Boolean);
    if (dates.length === 0) return '';
    return dates.sort().reverse()[0];
  }
  var depRows = inBucket(['Deposit', 'Deposit Payment', 'Full']);
  var finRows = inBucket(['Final']);
  var feeRows = inBucket(['Fee']);
  var adjRows = inBucket(['Adjustment']);
  return {
    po:          po,
    depositPaid: sumAmt(depRows),
    finalPaid:   sumAmt(finRows),
    feesPaid:    sumAmt(feeRows),
    adjustments: sumAmt(adjRows),
    depositAt:   mostRecent(depRows),
    finalAt:     mostRecent(finRows),
    qbBillIds:   rows.map(function(vp) { return vp.qbBillId || ''; }).filter(Boolean),
    qbErrors:    rows.map(function(vp) { return vp.qbError || ''; }).filter(Boolean),
  };
}

// ── Per-item lifecycle helpers (since 2026-05-26) ────────────────────────
//
// Each helper returns THIS item's specific dollar share of a lifecycle
// stage. The Ledger Procurement view shows per-line-item amounts so the
// bookkeeper can reconcile against QB (which records per-line-item too).
//
// Pattern: each "paid" helper derives from existing "expected total" and
// "unpaid" helpers so the invariant `expected = paid + unpaid` holds at
// the cent. Vendor-side per-item math pro-rates by net-cost share of the
// PO.

// itemFeesTotal — sum of THIS item's own fees with canonical markups.
// freight/crating/installation/custom = cost × 1.10; tariff + salesTax
// = pass-through. Reads from item.fees jsonb only; no aggregation across
// peers or proposal-level fees.
export function itemFeesTotal(item) {
  if (!item) return 0;
  var fees = item.fees || {};
  function n(v) { var x = parseFloat(String(v || '').replace(/[$,]/g, '')); return isNaN(x) ? 0 : x; }
  var t = 0;
  if (n(fees.freight)      > 0) t += applyFreightMarkup(n(fees.freight), item);
  if (n(fees.crating)      > 0) t += applyFreightMarkup(n(fees.crating), item);
  if (n(fees.installation) > 0) t += applyFreightMarkup(n(fees.installation), item);
  if (n(fees.tariff)       > 0) t += applyTaxMarkup(n(fees.tariff));
  if (n(fees.salesTax)     > 0) t += applyTaxMarkup(n(fees.salesTax));
  (fees.custom || []).forEach(function(c) { if (n(c.amount) > 0) t += customFeeClient(c, item); });
  return Math.round(t * 100) / 100;
}

// itemFeeProfit — sum of fee markup margins for THIS item's own fees.
// freight/crating/installation/custom apply a 10% markup; the profit
// portion is cost × 0.10. tariff + salesTax are pass-through (no
// margin). storage/travel are not in item.fees (they live on
// project_reimbursable receipts), so they don't contribute here.
// 2026-09-08: derived by SUBTRACTION from the same helpers that bill the
// client (applyFreightMarkup / applyTaxMarkup / customFeeClient) rather
// than by re-stating the rates. Before this it multiplied by a hardcoded
// 0.10 for every category including custom, so a custom fee carrying
// markupPct 30 billed at 1.30 and reported margin at 1.10 — on
// 26HOB-105-001's SITE SURVEY that was $650 reported against $1,950
// actually charged. Subtraction cannot drift from the billed number.
export function itemFeeProfit(item) {
  if (!item) return 0;
  var b = itemFeeBreakdown(item);
  return b.totals.markup;
}

// itemFeeBreakdown — one item's fees, per category, as
// { cost, client, markup }. The single shape Reconciliation itemizes
// from and the invoice builders store their markup split from, so the
// numbers on Dan's screen and the numbers in the invoice row come from
// the same arithmetic.
//
// Categories are the vocabulary's, with `custom` kept as its own bucket
// (its rows can each declare a different category and markup, so
// collapsing them into their categories would lose the label Dan reads).
// tariff + salesTax are pass-through: client === cost, markup 0.
export function itemFeeBreakdown(item) {
  function n(v) { var x = parseFloat(String(v == null ? '' : v).replace(/[$,]/g, '')); return isNaN(x) ? 0 : x; }
  var r2 = function(x) { return Math.round(x * 100) / 100; };
  var out = { byCategory: {}, custom: [], totals: { cost: 0, client: 0, markup: 0 } };
  if (!item) return out;
  var fees = item.fees || {};

  [['freight', applyFreightMarkup], ['crating', applyFreightMarkup],
   ['installation', applyFreightMarkup], ['tariff', applyTaxMarkup],
   ['salesTax', applyTaxMarkup]].forEach(function(pair) {
    var key = pair[0], bill = pair[1];
    var cost = n(fees[key]);
    if (cost <= 0) { out.byCategory[key] = { cost: 0, client: 0, markup: 0 }; return; }
    var client = r2(bill(cost, item));
    out.byCategory[key] = { cost: r2(cost), client: client, markup: r2(client - cost) };
  });

  (Array.isArray(fees.custom) ? fees.custom : []).forEach(function(c) {
    var cost = n(c && c.amount);
    if (cost <= 0) return;
    var client = customFeeClient(c, item);
    out.custom.push({
      label:     String((c && c.label) == null ? '' : c.label),
      category:  String((c && c.category) == null ? '' : c.category) || 'other',
      markupPct: customFeeMarkupPct(c, item),
      cost: r2(cost), client: r2(client), markup: r2(client - cost),
    });
  });

  var cost = 0, client = 0;
  Object.keys(out.byCategory).forEach(function(k) {
    cost += out.byCategory[k].cost; client += out.byCategory[k].client;
  });
  out.custom.forEach(function(c) { cost += c.cost; client += c.client; });
  out.totals = { cost: r2(cost), client: r2(client), markup: r2(client - cost) };
  return out;
}

// itemMarkupBreakdown — the markup DOLLARS for one item, split the way
// Cimon named it 2026-09-08: "markup dollars include the percentage from
// marked up shipping crating etc plus markup on product."
//
//   product = client price − net cost        (markupPct on merchandise)
//   fees    = billed fees − fee cost         (10% / 15% / per-entry)
//   total   = the two, which is itemTotalProfit
//
// Sales tax is excluded from both — it is collected, not earned.
export function itemMarkupBreakdown(item, allItems) {
  var zero = { product: 0, fees: 0, total: 0 };
  if (!item || item.parentId) return zero;
  if (!shouldBillItem(item)) return zero;
  var pool        = allItems || [item];
  var clientTotal = parseFloat(assemblyClient(item, pool)) || 0;
  var netUnit     = parseFloat(item.netPrice) || 0;
  var qty         = parseQty(item.qty) || 1;
  var product     = Math.round((clientTotal - netUnit * qty) * 100) / 100;
  var fees        = itemFeeBreakdown(item).totals.markup;
  return { product: product, fees: fees, total: Math.round((product + fees) * 100) / 100 };
}

// itemTotalProfit — item margin (client − net) + fee margin. The
// margin % implied by this number against item net total matches the
// item's markupPct in the no-fee case (e.g. markupPct 30 → 30%).
// Delegates to itemMarkupBreakdown (2026-09-08) so the total and the
// product/fees split are the same arithmetic, not two copies of it.
export function itemTotalProfit(item, allItems) {
  return itemMarkupBreakdown(item, allItems).total;
}

// proposalTotalProfit — sum of itemTotalProfit across every item id
// on the proposal. Caller passes the proposal's items[] (array of
// ids) and the full items list. Excludes components (parentId set)
// because itemTotalProfit short-circuits on them.
export function proposalTotalProfit(proposalItemIds, allItems) {
  var ids = Array.isArray(proposalItemIds) ? proposalItemIds : [];
  var pool = allItems || [];
  var total = 0;
  ids.forEach(function(id) {
    var it = pool.find(function(x) { return x.id === id; });
    if (it) total += parseFloat(itemTotalProfit(it, pool)) || 0;
  });
  return Math.round(total * 100) / 100;
}

// Off-cycle proposals for a given calendar month — used by the
// Billing Summaries Ledger view. Returns proposals that:
//   - are Approved (lifecycle status — they have a deposit invoice)
//   - have a deposit invoice (clientDepositInvoiceId on their items,
//     or equivalent — checked by approveProposal creating the record)
//   - are NOT tagged onto any monthly billing summary
//     (proposal.billedOnSummaryId is falsy)
//   - have proposal.approved_at landing in the given (year, month)
//
// month is 1-12 (calendar month, not zero-indexed). year is a full
// four-digit year. allProposals is the full proposals list.
//
// These appear under their project for the corresponding month in the
// BS view, alongside the monthly BS rows.
export function offCycleProposalsForMonth(year, month, allProposals) {
  var yyyy = String(year);
  var mm = String(month).padStart(2, '0');
  var prefix = yyyy + '-' + mm + '-';
  return (allProposals || []).filter(function(p) {
    if (!p) return false;
    if (p.status !== 'Approved') return false;
    var billed = p.billedOnSummaryId || p.billed_on_summary_id || '';
    if (billed) return false;
    var approvedAt = String(p.approved_at || p.approvedAt || '').slice(0, 10);
    if (!approvedAt) return false;
    return approvedAt.indexOf(prefix) === 0;
  });
}

// itemDepositPaid — paid portion of THIS item's expected deposit.
// Derives from itemDepositAmount − itemUnpaidDepositShare so the
// invariant `expected = paid + unpaid` holds for any payment state.
export function itemDepositPaid(item, invoices, allocations, allItems) {
  if (!item || item.parentId || !shouldBillItem(item)) return 0;
  var expected = parseFloat(itemDepositAmount(item, allItems || [item])) || 0;
  var unpaid   = parseFloat(itemUnpaidDepositShare(item, invoices, allocations, allItems)) || 0;
  var paid = expected - unpaid;
  if (paid < 0) paid = 0;
  return Math.round(paid * 100) / 100;
}

// itemFinalTotal — gross expected contribution to the final invoice.
// Same projection as estimatedFinalInvoiceAmount but bypasses the
// finalInvoiceId guard so it returns a value whether the invoice exists
// yet or not (display value for the FINAL INVOICE cell).
export function itemFinalTotal(item, allItems, invoices, allocations) {
  if (!item || item.parentId || !shouldBillItem(item)) return 0;
  var defensiveItem = Object.assign({}, item, { finalInvoiceId: null });
  return parseFloat(estimatedFinalInvoiceAmount(defensiveItem, allItems || [item], invoices, allocations)) || 0;
}

// itemFinalPaid — paid portion of THIS item's final contribution.
// expected (itemFinalTotal) − unpaid (itemProjectedFinalBalance).
// Returns 0 when no final invoice exists yet (paid against a doc that
// doesn't exist is meaningless).
export function itemFinalPaid(item, invoices, allocations, allItems) {
  if (!item || item.parentId || !shouldBillItem(item)) return 0;
  if (!item.finalInvoiceId) return 0;
  var total  = parseFloat(itemFinalTotal(item, allItems, invoices, allocations)) || 0;
  var unpaid = parseFloat(itemProjectedFinalBalance(item, invoices, allocations, allItems)) || 0;
  var paid = total - unpaid;
  if (paid < 0) paid = 0;
  return Math.round(paid * 100) / 100;
}

// Item's share of vendor payments (deposit + final + fees) for the
// matched PO. Pro-rata by netTotal share of PO.total_net.
function itemPoShareRatio(item, po) {
  if (!po || !item) return 0;
  var poTotal = parseFloat(po.total_net) || 0;
  if (poTotal <= 0) return 0;
  var netUnit = parseFloat(item.netPrice) || 0;
  var qty     = parseQty(item.qty) || 1;
  var itemNet = netUnit * qty;
  return itemNet / poTotal;
}

// itemVendorDepositPaid — pro-rata of vendor deposit payments. Routes
// through vendorPaymentStatusForPo so 'Deposit' / 'Deposit Payment' /
// 'Full' payment types all land here (matches the bucket aggregation).
export function itemVendorDepositPaid(item, po, vendorPayments) {
  if (!item || !po) return 0;
  var status = vendorPaymentStatusForPo(po, vendorPayments);
  if (!status) return 0;
  var share = itemPoShareRatio(item, po) * (status.depositPaid || 0);
  return Math.round(share * 100) / 100;
}

// itemVendorFinalPaid — same shape, 'Final' bucket.
export function itemVendorFinalPaid(item, po, vendorPayments) {
  if (!item || !po) return 0;
  var status = vendorPaymentStatusForPo(po, vendorPayments);
  if (!status) return 0;
  var share = itemPoShareRatio(item, po) * (status.finalPaid || 0);
  return Math.round(share * 100) / 100;
}

// Top-level row aggregator for the Ledger Procurement view. One call
// per item; returns the full state object the row renderer needs.
//
// Composition: depositStatusForItem + finalStatusForItem +
// vendorPaymentStatusForPo + itemProjectedFinalBalance + project /
// proposal lookups. No new joins not already in the individual helpers.
//
// item: the row's item
// ctx: { items, proposals, invoices, paymentAllocations, vendorPayments,
//        purchaseOrders, payments, changeOrders }
//
// Returns:
//   {
//     // Lifecycle cells (each `null` when that stage hasn't been reached)
//     proposal:               { ref, sentAt, id, qbEstimateId, qbError } | null,
//     depositInvoice:         <depositStatusForItem result> | null,
//     po:                     { ref, sentAt, id, qbPoId, qbError, depositRequested } | null,
//     vendorPaymentStatus:    <vendorPaymentStatusForPo result> | null,
//     finalInvoice:           <finalStatusForItem result> | null,
//     // Item flags
//     receivedDate:           item.receivedDate || '',
//     isCancelled:            isCancelled(item),
//     assemblyPeerIds:        [...]  (other items in the same assembly),
//     changeOrders:           [...]  (COs whose field_changes reference this item),
//     // Financial summary (per-item, rounded to cents)
//     net:                    clientTotal − vendor cost,
//     clientTotal,            // marked-up client total for this item
//     profit$, profitPct,
//     clientOutstanding:      itemUnpaidDepositShare + itemProjectedFinalBalance,
//     vendorOutstanding:      vendor PO portion not yet paid (deposit + final − paid)
//   }
//
// Heavyish — call once per item per render and memoize at the view layer.
export function procurementRowState(item, ctx) {
  ctx = ctx || {};
  if (!item) return null;
  var items              = ctx.items              || [];
  var proposals          = ctx.proposals          || [];
  var invoices           = ctx.invoices           || [];
  var paymentAllocations = ctx.paymentAllocations || [];
  var vendorPayments     = ctx.vendorPayments     || [];
  var purchaseOrders     = ctx.purchaseOrders     || [];
  var payments           = ctx.payments           || [];
  var changeOrdersAll    = ctx.changeOrders       || [];

  // ── Proposal lookup ──
  var proposalRow = item.proposalId
    ? proposals.find(function(p) { return p.id === item.proposalId; })
    : null;
  var proposalCell = proposalRow ? {
    id:            proposalRow.id,
    ref:           proposalRow.proposal_ref || '',
    sentAt:        proposalRow.sent_at || proposalRow.sentAt || '',
    qbEstimateId:  proposalRow.qbEstimateId || proposalRow.qb_estimate_id || '',
    qbError:       proposalRow.qbError       || proposalRow.qb_error       || '',
  } : null;

  // ── Deposit invoice + payment ──
  var depCell = depositStatusForItem(item, invoices, paymentAllocations, payments);

  // ── PO + vendor payments ──
  var poRow = findPoForItem(item, purchaseOrders);
  var poCell = poRow ? {
    id:               poRow.id,
    ref:              poRow.po_ref || '',
    sentAt:           poRow.issued_at || poRow.issuedAt || '',
    qbPoId:           poRow.qbPoId || poRow.qb_po_id || '',
    qbError:          poRow.qbError || poRow.qb_error || '',
    depositRequested: parseFloat(poRow.total_deposit) || 0,
    totalNet:         parseFloat(poRow.total_net) || 0,
  } : null;
  var vendorCell = poRow ? vendorPaymentStatusForPo(poRow, vendorPayments) : null;

  // ── Final invoice + payment ──
  var finalCell = finalStatusForItem(item, invoices, paymentAllocations, payments);

  // ── Assembly peers (siblings on same assemblyId, excluding self) ──
  var assemblyPeerIds = [];
  if (item.assemblyId) {
    assemblyPeerIds = items
      .filter(function(x) { return x.assemblyId === item.assemblyId && x.id !== item.id; })
      .map(function(x) { return x.id; });
  }

  // ── Change Orders touching this item ──
  // CO.field_changes is jsonb [{ itemId, ... }, ...]. Walk for membership.
  var cos = changeOrdersAll.filter(function(co) {
    var changes = co && co.field_changes;
    if (typeof changes === 'string') {
      try { changes = JSON.parse(changes); } catch (e) { changes = []; }
    }
    if (!Array.isArray(changes)) return false;
    return changes.some(function(c) { return c && c.itemId === item.id; });
  });

  // ── Financial summary ──
  var clientTotal = parseFloat(assemblyClient(item, items)) || 0;
  var netUnit     = parseFloat(item.netPrice) || 0;
  var qty         = parseQty(item.qty) || 1;
  var netTotal    = netUnit * qty;
  var profitDollars = Math.round((clientTotal - netTotal) * 100) / 100;
  var profitPct     = netTotal > 0 ? Math.round((profitDollars / netTotal) * 1000) / 10 : 0;

  // Outstanding to client: unpaid deposit share + projected/actual final balance.
  // Round the sum, not the parts, so $0.01 rounding errors can't compound.
  var depUnpaid   = parseFloat(itemUnpaidDepositShare(item, invoices, paymentAllocations, items)) || 0;
  var finUnpaid   = parseFloat(itemProjectedFinalBalance(item, invoices, paymentAllocations, items)) || 0;
  var clientOutstanding = Math.round((depUnpaid + finUnpaid) * 100) / 100;

  // Outstanding to vendor: this item's expected vendor cost − vendor payments
  // received against the PO (pro-rata by item.netTotal share of PO.total_net).
  var vendorOutstanding = 0;
  if (poRow && vendorCell) {
    var poTotal = parseFloat(poRow.total_net) || 0;
    var itemShareOfPo = poTotal > 0 ? netTotal / poTotal : 0;
    var paidTotalForPo = (vendorCell.depositPaid || 0) + (vendorCell.finalPaid || 0) + (vendorCell.feesPaid || 0);
    var itemSharePaid = paidTotalForPo * itemShareOfPo;
    vendorOutstanding = Math.round((netTotal - itemSharePaid) * 100) / 100;
    if (vendorOutstanding < 0) vendorOutstanding = 0;
  } else {
    // No PO yet — vendor obligation is the full netTotal still ahead.
    vendorOutstanding = Math.round(netTotal * 100) / 100;
  }

  // Per-item lifecycle dollar shares — drives the Procurement view's
  // per-line-item display. Each value is THIS item's specific share of
  // the corresponding lifecycle stage so the bookkeeper can reconcile
  // per-item against QB Online's per-line records.
  var depExpected    = Math.round((parseFloat(itemDepositAmount(item, items)) || 0) * 100) / 100;
  var depPaid        = itemDepositPaid(item, invoices, paymentAllocations, items);
  var finExpected    = itemFinalTotal(item, items, invoices, paymentAllocations);
  var finPaid        = itemFinalPaid(item, invoices, paymentAllocations, items);
  var fees           = itemFeesTotal(item);
  var venDepositPaid = poRow ? itemVendorDepositPaid(item, poRow, vendorPayments) : 0;
  var venFinalPaid   = poRow ? itemVendorFinalPaid(item, poRow, vendorPayments) : 0;

  return {
    proposal:            proposalCell,
    depositInvoice:      depCell,
    po:                  poCell,
    vendorPaymentStatus: vendorCell,
    finalInvoice:        finalCell,
    receivedDate:        item.receivedDate || '',
    isCancelled:         isCancelled(item),
    assemblyPeerIds:     assemblyPeerIds,
    changeOrders:        cos,
    net:                 Math.round(netTotal * 100) / 100,
    clientTotal:         Math.round(clientTotal * 100) / 100,
    profit$:             profitDollars,
    profitPct:           profitPct,
    // Per-item lifecycle dollar shares
    itemDepositExpected: depExpected,
    itemDepositPaid:     depPaid,
    itemDepositUnpaid:   depUnpaid,
    itemFinalExpected:   finExpected,
    itemFinalPaid:       finPaid,
    itemFinalUnpaid:     finUnpaid,
    itemFees:            fees,
    itemVendorDepositPaid: venDepositPaid,
    itemVendorFinalPaid:   venFinalPaid,
    // Roll-ups
    clientOutstanding:   clientOutstanding,
    vendorOutstanding:   vendorOutstanding,
  };
}

// Billing Summary rollup — drives the BS view's expanded line-item list.
//
// Walks `*.billed_on_summary_id` on proposals, invoices, and receipts to
// find every document tagged onto this summary. Looks up the matching
// Designer Hours invoice via the BS's stored hours_invoice_number text.
//
// Note (data shape gap): hours_invoices has NO billed_on_summary_id
// column — only the forward link (BS → hours_invoice) works.
// `hoursInvoices.find(hi => hi.invoice_number === bs.hours_invoice_number)`
// is the only direction. See scope report.
//
// bs: a billing_summaries row (raw or camelCase)
// ctx: { proposals, invoices, paymentAllocations, payments, receipts,
//        hoursInvoices, items, projects }
//
// Returns:
//   {
//     designerHoursInvoice: { invoiceNumber, amount, paidStatus, paidAt, ref } | null,
//     proposals:            [{ proposal, depositInvoice, items: [item, ...] }, ...],
//     reimbursableReceipts: [{ receipt, billedAmount, paidStatus, paidAt }, ...],
//     totals:               { issued, paid, outstanding }
//   }
export function billingSummaryRollup(bs, ctx) {
  ctx = ctx || {};
  if (!bs) return null;
  var proposalsAll  = ctx.proposals          || [];
  var invoicesAll   = ctx.invoices           || [];
  var allocations   = ctx.paymentAllocations || [];
  var payments      = ctx.payments           || [];
  var receiptsAll   = ctx.receipts           || [];
  var hoursInvAll   = ctx.hoursInvoices      || [];
  var itemsAll      = ctx.items              || [];

  // ── Designer Hours (forward-only link via bs.hours_invoice_number) ──
  var hoursNum = bs.hours_invoice_number || bs.hoursInvoiceNumber || '';
  var hoursInv = hoursNum
    ? hoursInvAll.find(function(hi) { return hi && (hi.invoice_number === hoursNum || hi.invoiceNumber === hoursNum); })
    : null;
  var designerHoursInvoice = null;
  if (hoursInv) {
    var hiAmt = parseFloat(hoursInv.total_amount != null ? hoursInv.total_amount : hoursInv.totalAmount) || 0;
    designerHoursInvoice = {
      invoiceNumber: hoursInv.invoice_number || hoursInv.invoiceNumber || '',
      amount:        Math.round(hiAmt * 100) / 100,
      paidStatus:    derivePaidStatus(allocations, 'hours_invoice', hoursInv.invoice_number || hoursInv.invoiceNumber, hiAmt, hoursInv.status),
      paidAt:        mostRecentPaidAt(allocations, payments, 'hours_invoice', hoursInv.invoice_number || hoursInv.invoiceNumber),
      sentAt:        hoursInv.sent_at || hoursInv.sentAt || '',
      hoursInv:      hoursInv,
    };
  }

  // ── Proposals tagged on this BS ──
  var taggedProposals = proposalsAll
    .filter(function(p) { return p && (p.billedOnSummaryId === bs.id || p.billed_on_summary_id === bs.id); });
  var proposalRollup = taggedProposals.map(function(p) {
    // The deposit invoice for this proposal (if any).
    var depInv = invoicesAll.find(function(inv) {
      return inv && (inv.proposalId === p.id || inv.proposal_id === p.id)
        && (inv.invoiceType === 'deposit' || inv.invoice_type === 'deposit');
    }) || null;
    var depCell = null;
    if (depInv) {
      var depAmt = parseFloat(depInv.amountDue != null ? depInv.amountDue : depInv.amount_due) || 0;
      depCell = {
        invoice:     depInv,
        invoiceRef:  depInv.invoiceRef || depInv.invoice_ref || '',
        amountDue:   depAmt,
        paidAmount:  Math.round(calcPaidForDocument(allocations, 'invoice', depInv.id) * 100) / 100,
        paidStatus:  derivePaidStatus(allocations, 'invoice', depInv.id, depAmt, depInv.status),
        paidAt:      mostRecentPaidAt(allocations, payments, 'invoice', depInv.id),
      };
    }
    // Items on this proposal.
    var pItemIds = p.items || [];
    var pItems = pItemIds
      .map(function(id) { return itemsAll.find(function(it) { return it.id === id; }); })
      .filter(Boolean);
    return { proposal: p, depositInvoice: depCell, items: pItems };
  });

  // ── Reimbursable receipts tagged on this BS ──
  var taggedReceipts = receiptsAll.filter(function(r) {
    if (!r) return false;
    if (r.kind !== 'project_reimbursable') return false;
    var tag = r.billedOnSummaryId || r.billed_on_summary_id || '';
    return tag === bs.id;
  });
  var receiptRollup = taggedReceipts.map(function(r) {
    var cost = parseFloat(r.totalAmount != null ? r.totalAmount : r.total_amount) || 0;
    var billed = applyReimbursableMarkup(cost, receiptMarkupPct(r));
    // Paid state of the receipt = paid state of its parent BS (the BS is
    // the asking doc; allocations land against the BS as a whole, not
    // per-receipt). Pro-rata not meaningful at receipt level.
    return {
      receipt:      r,
      rawCost:      Math.round(cost * 100) / 100,
      markupPct:    receiptMarkupPct(r),
      billedAmount: billed,
    };
  });

  // ── Totals ──
  // Issued = sum of bundled invoice amounts + tagged receipts (billed) +
  // designer hours amount. NOT bs.subtotal — that's the cover-doc
  // representation. The Ledger surfaces the true sum of what's tagged.
  var depositIssued = proposalRollup.reduce(function(s, pr) {
    return s + (pr.depositInvoice ? pr.depositInvoice.amountDue : 0);
  }, 0);
  var receiptsIssued = receiptRollup.reduce(function(s, r) { return s + r.billedAmount; }, 0);
  var hoursIssued = designerHoursInvoice ? designerHoursInvoice.amount : 0;
  var totalIssued = Math.round((depositIssued + receiptsIssued + hoursIssued) * 100) / 100;

  var depositPaid = proposalRollup.reduce(function(s, pr) {
    return s + (pr.depositInvoice ? pr.depositInvoice.paidAmount : 0);
  }, 0);
  var hoursPaid = hoursInv
    ? calcPaidForDocument(allocations, 'hours_invoice', hoursInv.invoice_number || hoursInv.invoiceNumber)
    : 0;
  // Receipts don't have their own allocations — they fold into the BS's
  // payment (via billing_summary documentType). To avoid double-counting,
  // pull BS-level allocations and treat them as separate from the per-doc
  // allocations above.
  var bsPaid = calcPaidForDocument(allocations, 'billing_summary', bs.id);
  var totalPaid = Math.round((depositPaid + hoursPaid + bsPaid) * 100) / 100;
  var totalOutstanding = Math.round((totalIssued - totalPaid) * 100) / 100;

  return {
    designerHoursInvoice: designerHoursInvoice,
    proposals:            proposalRollup,
    reimbursableReceipts: receiptRollup,
    totals: {
      issued:      totalIssued,
      paid:        totalPaid,
      outstanding: totalOutstanding,
    },
  };
}

// Sum of unpaid client deposit balances for the items going onto a PO.
// Drives the soft-warning bypass modal at PO issuance (Draft → Issued).
// Zero means every item on the PO has had its full client deposit
// recorded — no warning, no bypass needed.
//
// SPEC: payment state comes from payment_allocations (via
// itemUnpaidDepositShare), not from item.status strings. Pre-2026-05-25
// this function used `item.status === 'Client Approved'` as a binary
// proxy — it reported the full deposit obligation when items were
// PARTIALLY paid (cascadePaidItems only fires on full payment, so
// status stays at Client Approved through partial). The Kumiko case
// surfaced the bug: $115K of $119K paid → warning still said
// "$119,823.26 floating" instead of the real $4,624.79 shortfall.
//
// allInvoices + allocations are required for the allocation lookup.
// Callers that don't have them in scope are getting "no payments
// recorded anywhere" (which gives the same answer as the old proxy for
// items at Client Approved with no deposit invoice yet — full expected
// deposit shows as unpaid).
export function computePoBypassShortfall(items, allItems, allInvoices, allocations) {
  if (!Array.isArray(items)) return 0;
  var sum = items.reduce(function(s, it) {
    if (!it) return s;
    return s + itemUnpaidDepositShare(it, allInvoices, allocations, allItems);
  }, 0);
  return Math.round(sum * 100) / 100;
}

// ── UNIT CONVERTERS ──────────────────────────────────────────────────────────

export function cmToIn(n) {
  var inches = Math.round(parseFloat(n) * 0.393701 * 8) / 8;
  var ft = Math.floor(inches / 12);
  var rem = Math.round((inches % 12) * 8) / 8;
  return ft > 0 ? ft + "' " + rem + '"' : inches + '"';
}

export function mmToIn(n) {
  return (Math.round(parseFloat(n) * 0.0393701 * 8) / 8) + '"';
}

export function fmtDim(str) {
  if (!str || typeof str !== 'string') return str || '';
  try {
    var s = str.trim();
    var trailingCm = s.match(/^([\d.\s x\u00d7\/\-]+)\s*cm$/i);
    if (trailingCm) {
      var converted = trailingCm[1].replace(/([\d.]+)/g, function(_, n) { return cmToIn(n); });
      return converted.trim() + ' (converted)';
    }
    var trailingMm = s.match(/^([\d.\s x\u00d7\/\-]+)\s*mm$/i);
    if (trailingMm) {
      var converted2 = trailingMm[1].replace(/([\d.]+)/g, function(_, n) { return mmToIn(n); });
      return converted2.trim() + ' (converted)';
    }
    var hasMetric = /([\d.]+)\s*(cm|mm)/i.test(s);
    if (!hasMetric) return s;
    s = s.replace(/([\d.]+)\s*cm/gi, function(_, n) { return cmToIn(n); });
    s = s.replace(/([\d.]+)\s*mm/gi, function(_, n) { return mmToIn(n); });
    return s + ' (converted)';
  } catch (e) {
    return str;
  }
}

export function fmtInput(n) {
  if (n === null || n === undefined || n === '') return '—';
  var num = parseFloat(String(n).replace(/[$,]/g, ''));
  if (isNaN(num) || num === 0) return '—';
  return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function convertDimensions(raw) {
  return raw.replace(/([\d.]+)\s*(mm|cm|m)(?=\W|[xX]|$)/gi, function(match, num, unit) {
    var n = parseFloat(num);
    var inches;
    if (unit.toLowerCase() === 'mm') inches = n / 25.4;
    else if (unit.toLowerCase() === 'cm') inches = n / 2.54;
    else inches = n * 39.3701;
    if (inches >= 12) {
      var ft = Math.floor(inches / 12);
      var inn = Math.round((inches % 12) * 8) / 8;
      return ft + "'" + (inn > 0 ? inn + '"' : '');
    }
    return (Math.round(inches * 8) / 8) + '"';
  });
}

// ── DATA MAPPING (Supabase DB rows ↔ React item objects) ─────────────────────

function jp(v, d) {
  if (v == null) return d;
  if (typeof v !== 'string') return v;
  try { return JSON.parse(v); } catch (e) { return d; }
}

// normalizeItemFees — clean the items.fees jsonb before save. Filters
// the empty-string + empty-custom-row debt the ItemForm BLANK template
// has been regenerating (CLAUDE.md "Data quality follow-ups on
// items.fees"). Pre-fix: a new item saved without touching fees writes
// every scalar as "" and any "+ Add Custom Fee" click leaves a phantom
// {label:'', amount:''} row in the array. Post-fix: scalars default to
// "0" (still string-typed for backward compat with the legacy storage
// pattern); empty custom rows are filtered out. 2026-06-11 — closes
// the loop on the 2026-06-01 + 2026-06-11 SQL cleanup cycle by stopping
// the regression at the write boundary.
function normalizeItemFees(fees) {
  if (!fees || typeof fees !== 'object') fees = {};
  var out = {};
  var scalars = ['freight', 'crating', 'installation', 'tariff', 'salesTax'];
  // Copy any non-scalar / non-custom keys verbatim (defensive — preserves
  // future fields without explicit handling)
  Object.keys(fees).forEach(function(k) {
    if (scalars.indexOf(k) >= 0 || k === 'custom') return;
    out[k] = fees[k];
  });
  // Normalize scalars: empty string + trailing dot + comma-formatted →
  // clean string number. NaN/undefined → "0".
  scalars.forEach(function(k) {
    var raw = fees[k];
    if (raw == null || raw === '') { out[k] = '0'; return; }
    var s = String(raw).replace(/,/g, '').replace(/\.+$/, '');
    var n = parseFloat(s);
    out[k] = isNaN(n) ? '0' : s;
  });
  // Filter custom array: drop entries with empty label AND empty amount.
  // Preserve entries with either a label or an amount (bookkeeper may
  // have entered one but not the other; trust their intent).
  var customArr = Array.isArray(fees.custom) ? fees.custom : [];
  out.custom = customArr.filter(function(c) {
    if (!c || typeof c !== 'object') return false;
    var hasLabel  = (c.label != null && String(c.label).trim() !== '');
    var hasAmount = (c.amount != null && String(c.amount).trim() !== '');
    return hasLabel || hasAmount;
  }).map(function(c) {
    // Also clean each custom entry's amount the same way as scalars
    var amt = c.amount;
    var cleaned;
    if (amt == null || amt === '') {
      cleaned = Object.assign({}, c, { amount: '0' });
    } else {
      var s = String(amt).replace(/,/g, '').replace(/\.+$/, '');
      var n = parseFloat(s);
      cleaned = Object.assign({}, c, { amount: isNaN(n) ? '0' : s });
    }
    // 2026-09-08 fee vocabulary: every custom row carries a category
    // from FEE_CATEGORIES_CUSTOM_ALLOWED and an EXPLICIT markupPct, so a
    // fee's margin is on the record instead of inherited from a default.
    //
    // Deliberately money-neutral for legacy rows: a row with neither
    // field lands on category 'other' + markupPct 10, which is exactly
    // what customFeeClient already charged it. Nothing here reclassifies
    // by label — a `Tax` row keeps billing at 10% until a human moves it,
    // because 'salesTax' is pass-through and the reclassification would
    // quietly cut the client price. feeVocabularyIssues() surfaces those.
    var cat = String(cleaned.category == null ? '' : cleaned.category).trim();
    if (FEE_CATEGORIES_CUSTOM_ALLOWED.indexOf(cat) < 0) cat = 'other';
    var pct = parseFloat(cleaned.markupPct);
    if (isNaN(pct)) {
      var byCat = feeCategoryMarkupPct(cat);
      pct = (byCat == null) ? 10 : byCat;
    }
    return Object.assign({}, cleaned, { category: cat, markupPct: pct });
  });
  return out;
}

export function itemToRow(it) {
  var id = it.id;
  if (!id || String(id).startsWith('import-') || /^\d+$/.test(String(id))) id = undefined;
  return {
    id: id,
    parent_id: it.parentId || null,
    project_label: it.projectLabel || it.project || '',
    project: it.projectLabel || it.project || '',
    room_name: it.roomName || '',
    room_number: it.roomNumber || '',
    item: it.item || '',
    product_name: it.productName || '',
    category: it.category || it.schedule || 'Furniture',
    schedule: it.schedule || it.category || 'Furniture',
    vendor: it.vendor || '',
    qty: it.qty || '1',
    qty_type: it.qtyType || 'Each',
    status: it.status || 'Specifying',
    net_price: parseFloat(it.netPrice || 0) || 0,
    unit_cost: it.netPrice || '',
    client_price: calcClientPrice(it.netPrice, it.markupPct),
    notes: it.notes || '',
    specs: it.specs || {},
    com: it.com || {},
    child_type: it.childType || '',
    sidemark: it.sidemark || '',
    tag: it.tag || '',
    dimensions: it.dimensions || '',
    manufacturer: it.manufacturer || '',
    model: it.model || '',
    finish: it.finish || '',
    spec_notes: it.specNotes || '',
    internal_notes: it.internalNotes || '',
    source_url: it.sourceUrl || '',
    image_url: it.imageUrl || '',
    images: it.images || [],
    hero_index: it.heroIndex || 0,
    fees: normalizeItemFees(it.fees),
    lead_time: it.leadTime || '',
    lead_weeks: it.leadWeeks ? parseInt(it.leadWeeks, 10) : null,
    po_number: it.poNumber || '',
    import_source: it.importSource || '',
    import_date: it.importDate || '',
    order_placed_date: it.orderPlacedDate || '',
    received_date: it.receivedDate || '',
    shipped_date: it.shippedDate || '',
    delivered_date: it.deliveredDate || '',
    install_planned_date: it.installPlannedDate || '',
    install_days: it.installDays || '',
    install_event_id: it.installEventId || '',
    install_date: it.installDate || '',
    install_schedule_notes: it.installScheduleNotes || '',
    // 2026-10-16 — delivery-basis sales tax date (date column: '' → NULL)
    delivered_to_state_on: it.deliveredToStateOn ? String(it.deliveredToStateOn).slice(0, 10) : null,
    tracking_number: it.trackingNumber || '',
    carrier: it.carrier || '',
    installer: it.installer || '',
    status_notes: it.statusNotes || '',
    date_added: it.date || '',
    proposal_id: it.proposalId || '',
    proposal_ref: it.proposalRef || '',
    markup_pct: (it.markupPct != null && it.markupPct !== '' && isFinite(parseFloat(it.markupPct))) ? parseFloat(it.markupPct) : 30,
    fee_markup_pct: (it.feeMarkupPct != null && isFinite(parseFloat(it.feeMarkupPct))) ? parseFloat(it.feeMarkupPct) : null,
    cc_fee_pct: parseFloat(it.ccFeePct) || 0,
    deposit_pct: parseFloat(it.depositPct || 50) || 50,
    client_approved_date: it.clientApprovedDate || '',
    payment_received_date: it.paymentReceivedDate || '',
    children: it.children || [],
    assembly_id: it.assemblyId || null,
    ship_to_vendor_id: it.shipToVendorId || null,
    vendor_contact_name: it.vendorContactName || '',
    vendor_contact_email: it.vendorContactEmail || '',
    wr_entries: JSON.stringify(it.wrEntries || []),
    item_number: it.itemNumber || null,
    // Invoice FK columns: write NULL (not '') when unset. Empty string in
    // a uuid column is a third-state footgun — neither NULL-checks nor
    // uuid-equality checks see it cleanly. Symmetric with rowToItem
    // returning null below. Cleanup migration
    // 20260607_items_invoice_id_null_normalize.sql converts any
    // legacy '' rows to NULL.
    client_deposit_invoice_id: it.clientDepositInvoiceId || null,
    final_invoice_id:          it.finalInvoiceId          || null,
    final_invoice_sent_at:     it.finalInvoiceSentAt      || null,
    // Phase 5 / QB readiness — added 2026-05-04 via 20260504_qb_id_columns.sql
    qb_product_id:       it.qbProductId       || '',
    qb_estimate_id:      it.qbEstimateId      || '',
    qb_deposit_invoiced: !!it.qbDepositInvoiced,
    qb_final_invoiced:   !!it.qbFinalInvoiced,
    qb_delivery_batch:   it.qbDeliveryBatch   || '',
    // Phase 5a — push-state columns added 2026-05-05 via
    // 20260505_qb_error_columns.sql. Set on every successful/failed push.
    qb_pushed_at:        it.qbPushedAt        || null,
    qb_error:            it.qbError           || '',
    // Sales-tax override flag (added 2026-05-25 via
    // 20260525_items_tax_manual_override.sql). When false, fees.salesTax
    // auto-recomputes from client_price × qty × project_tax_rate on
    // save. When true, the stored value is sticky — user explicitly
    // opted out of auto-calc via the lock toggle in FeesPanel.
    tax_manual_override: !!it.taxManualOverride,
    // Procurement class (added 2026-05-28 via
    // 20260528_items_procurement_class.sql). Defaults to 'standard' for
    // new items; OF/BO items are financially inert (see isNonProcured
    // and shouldBillItem). Phase 1 of the OF/BO feature — schema +
    // helpers ship before any UI exposes the field.
    procurement_class: it.procurementClass || 'standard',
    // Off schedule by default (added 2026-06-16 via
    // 20260616_items_off_schedule_default.sql). Pure UI default — when
    // true, the schedule item picker still lists this item but leaves
    // it unchecked. No cascade, no financial effect.
    off_schedule_default: !!it.offScheduleDefault,
  };
}

// ── STALE-WRITE GUARD (2026-08-25) ───────────────────────────────────────
// One conditional write of a full item row. Item saves write EVERY column
// from the calling session's in-memory copy, so a session that loaded
// before someone else's save would silently revert it on its next save
// (2026-08-24: a schedules session's full-row saves reverted the
// just-approved 26HOB-CO-025 qty change 34 seconds after it landed).
//
// The guard: UPDATE ... WHERE id = ? AND updated_at = <the updated_at this
// session last saw for the row>. The items_updated_at DB trigger bumps
// updated_at on every UPDATE, so a row touched by anyone else since we
// last saw it matches zero rows and the write is REFUSED — loud failure,
// nothing overwritten. Pure + dependency-injected (client passed in) so
// staleWriteGuard.test.js can exercise every outcome without the app.
//
// expectedRev == null falls back to the legacy unguarded upsert — that is
// the INSERT path for brand-new rows and the escape hatch for sessions
// that never learned a rev (localStorage-backup boot). Both paths chain
// .select('id,updated_at') so the caller can record the fresh rev.
//
// Returns { outcome:'saved', id, rev } | { outcome:'conflict' }
//       | { outcome:'error', error }.  Never throws on a structured
// PostgREST error; transport exceptions propagate to the caller's retry
// loop like they always have.
// Column-level diff of two itemToRow-shaped rows. Returns {col: nextValue}
// for every column whose value differs (JSON-compared; undefined ≡ null).
// `id` is never included. This is what turns a full-row save into a
// minimal patch: write only what THIS session changed, so concurrent
// sessions editing different fields of the same item can never clobber
// each other (2026-08-26 — the field-level rework of the stale-write
// guard, after day-one row-level blocking proved unusable: any remote
// touch of the row forced hard-refresh + redo).
export function diffRowCols(baseRow, nextRow) {
  var out = {};
  Object.keys(nextRow || {}).forEach(function (k) {
    if (k === 'id') return;
    var a = baseRow ? baseRow[k] : undefined;
    var b = nextRow[k];
    var sa = JSON.stringify(a === undefined ? null : a);
    var sb = JSON.stringify(b === undefined ? null : b);
    if (sa !== sb) out[k] = b;
  });
  return out;
}

export async function guardedItemWrite(client, row, expectedRev) {
  if (row && row.id && expectedRev) {
    var r = await client.from('items')
      .update(row)
      .eq('id', row.id)
      .eq('updated_at', expectedRev)
      .select('id,updated_at');
    if (r.error) return { outcome: 'error', error: r.error };
    if (r.data && r.data.length === 1) {
      return { outcome: 'saved', id: r.data[0].id, rev: r.data[0].updated_at };
    }
    return { outcome: 'conflict' };
  }
  var r2 = await client.from('items')
    .upsert(row, { onConflict: 'id' })
    .select('id,updated_at');
  if (r2.error) return { outcome: 'error', error: r2.error };
  var d = (r2.data && r2.data[0]) || {};
  return { outcome: 'saved', id: d.id, rev: d.updated_at };
}

export function rowToItem(row) {
  return {
    id: row.id,
    parentId: row.parent_id || null,
    projectLabel: row.project_label || row.project || '',
    project: row.project_label || row.project || '',
    roomName: row.room_name || '',
    roomNumber: row.room_number || '',
    item: row.item || '',
    productName: row.product_name || '',
    category: row.category || row.schedule || 'Furniture',
    schedule: row.schedule || row.category || 'Furniture',
    vendor: row.vendor || '',
    qty: row.qty || '1',
    qtyType: row.qty_type || 'Each',
    // Mapper-boundary normalization (2026-07-28, Fix D 1.3 pattern):
    // in-memory statuses are ALWAYS canonical; the DB may hold legacy
    // names until the status-flip migration applies.
    status: normalizeItemStatus(row.status, row.received_date) || 'Specifying',
    netPrice: String(row.unit_cost || row.net_price || '').replace(/,/g, ''),
    clientPrice: row.client_price ? String(row.client_price) : '',
    notes: row.notes || '',
    specs: jp(row.specs, {}),
    com: jp(row.com, {}),
    childType: row.child_type || '',
    sidemark: row.sidemark || '',
    tag: row.tag || '',
    dimensions: row.dimensions || '',
    manufacturer: row.manufacturer || '',
    model: row.model || '',
    finish: row.finish || '',
    specNotes: row.spec_notes || '',
    internalNotes: row.internal_notes || '',
    sourceUrl: row.source_url || '',
    imageData: '',
    imageUrl: row.image_url || '',
    images: jp(row.images, []),
    heroIndex: row.hero_index || 0,
    fees: jp(row.fees, {}),
    leadTime: row.lead_time || '',
    leadWeeks: row.lead_weeks ? parseInt(row.lead_weeks, 10) : null,
    poNumber: row.po_number || '',
    importSource: row.import_source || '',
    importDate: row.import_date || '',
    orderPlacedDate: row.order_placed_date || '',
    receivedDate: row.received_date || '',
    shippedDate: row.shipped_date || '',
    deliveredDate: row.delivered_date || '',
    installPlannedDate: row.install_planned_date || '',
    installDays: row.install_days || '',
    installEventId: row.install_event_id || '',
    installDate: row.install_date || '',
    installScheduleNotes: row.install_schedule_notes || '',
    deliveredToStateOn: row.delivered_to_state_on ? String(row.delivered_to_state_on).slice(0, 10) : '',
    trackingNumber: row.tracking_number || '',
    carrier: row.carrier || '',
    installer: row.installer || '',
    statusNotes: row.status_notes || '',
    date: row.date_added || '',
    proposalId: row.proposal_id || '',
    proposalRef: row.proposal_ref || '',
    // 0 is a real rate (a client/project may stamp 0% merchandise); only
    // NULL / non-numeric reads as 30 (2026-10-06).
    markupPct: (row.markup_pct != null && isFinite(parseFloat(row.markup_pct))) ? parseFloat(row.markup_pct) : 30,
    // Fee markup rate (2026-10-06). NULL = row predates the column; the
    // migration backfills 10, and itemFeeMarkupPct falls back to 10 too.
    feeMarkupPct: (row.fee_markup_pct != null && isFinite(parseFloat(row.fee_markup_pct))) ? parseFloat(row.fee_markup_pct) : null,
    ccFeePct:  parseFloat(row.cc_fee_pct) || 0,
    depositPct: String(parseFloat(row.deposit_pct) || 50),
    clientApprovedDate: row.client_approved_date || '',
    paymentReceivedDate: row.payment_received_date || '',
    children: jp(row.children, []),
    isAssembly: false,
    specSummary: '',
    assemblyName: '',
    assemblyId: row.assembly_id || null,
    shipToVendorId: row.ship_to_vendor_id || null,
    vendorContactName: row.vendor_contact_name || '',
    vendorContactEmail: row.vendor_contact_email || '',
    wrEntries: jp(row.wr_entries, []),
    itemNumber: row.item_number || null,
    clientDepositInvoiceId: row.client_deposit_invoice_id || null,
    finalInvoiceId:          row.final_invoice_id          || null,
    finalInvoiceSentAt:      row.final_invoice_sent_at     || '',
    // Phase 5 / QB readiness
    qbProductId:       row.qb_product_id       || '',
    qbEstimateId:      row.qb_estimate_id      || '',
    qbDepositInvoiced: !!row.qb_deposit_invoiced,
    qbFinalInvoiced:   !!row.qb_final_invoiced,
    qbDeliveryBatch:   row.qb_delivery_batch   || '',
    qbPushedAt:        row.qb_pushed_at        || '',
    qbError:           row.qb_error            || '',
    // Sales-tax override flag — see itemToRow comment.
    taxManualOverride: !!row.tax_manual_override,
    // Procurement class — see itemToRow comment. Defaults to 'standard'
    // when missing (legacy / pre-migration row); post-migration every
    // row carries a non-null value via the column DEFAULT.
    procurementClass: row.procurement_class || 'standard',
    // Off schedule by default — see itemToRow comment.
    offScheduleDefault: !!row.off_schedule_default,
  };
}

// ── RECEIPTS MAPPING ─────────────────────────────────────────────────────────
// receipts table — see supabase/migrations/20260425_create_receipts_table.sql
// extracted_json and allocation_breakdown are jsonb columns; pass objects through
// directly on save (Supabase JS client handles serialization). On read we use jp()
// to defensively handle both string and object representations.

export function rowToReceipt(row) {
  return {
    id:                  row.id,
    kind:                row.kind                  || '',
    status:              row.status                || 'pending',
    storagePath:         row.storage_path          || '',
    filename:            row.filename              || '',
    mimeType:            row.mime_type             || '',
    vendorName:          row.vendor_name           || '',
    receiptDate:         row.receipt_date          || '',
    paidDate:            row.paid_date             || '',
    totalAmount:         parseFloat(row.total_amount) || 0,
    ccFee:               parseFloat(row.cc_fee)     || 0,
    // Client markup on project_reimbursable receipts (20261009). 15 is
    // the default; null/'' in the column (pre-migration rows) → 15.
    markupPct:           (row.markup_pct == null || row.markup_pct === '') ? 15 : (parseFloat(row.markup_pct) || 0),
    feeBreakdown:        jp(row.fee_breakdown, {}),
    currency:            row.currency              || 'USD',
    extractedJson:       jp(row.extracted_json,    {}),
    matchConfidence:     parseFloat(row.match_confidence) || 0,
    poId:                row.po_id                 || '',
    itemId:              row.item_id               || '',
    projectLabel:        row.project_label         || '',
    createdBy:           row.created_by            || '',
    feeType:             row.fee_type              || '',
    category:            row.category              || '',
    allocationMethod:    row.allocation_method     || 'net_price_weighted',
    allocationBreakdown: jp(row.allocation_breakdown, []),
    billedOnInvoiceId:   row.billed_on_invoice_id  || '',
    billedOnSummaryId:   row.billed_on_summary_id  || '',
    paidWithCardId:      row.paid_with_card_id     || '',
    qbTarget:            row.qb_target             || '',
    qbId:                row.qb_id                 || '',
    qbExpenseId:         row.qb_expense_id         || '',
    qbPushedAt:          row.qb_pushed_at          || '',
    qbError:             row.qb_error              || '',
    notes:               row.notes                 || '',
    feeAbsorptions:      jp(row.fee_absorptions, []),
    absorbedAt:          row.absorbed_at           || '',
    absorbedBy:          row.absorbed_by           || '',
    approvedAt:          row.approved_at           || '',
    approvedBy:          row.approved_by           || '',
    rejectedAt:          row.rejected_at           || '',
    rejectedReason:      row.rejected_reason       || '',
    salesTaxFilingId:    row.sales_tax_filing_id   || '',   // 2026-10-16: the filing this receipt mirrors
    createdAt:           row.created_at            || '',
    updatedAt:           row.updated_at            || '',
  };
}

export function receiptToRow(r) {
  var id = r.id;
  if (!id || /^\d+$/.test(String(id))) id = undefined;  // let Postgres assign UUID
  return {
    id:                   id,
    kind:                 r.kind                || 'pending',
    status:               r.status              || 'pending',
    storage_path:         r.storagePath         || '',
    filename:             r.filename            || '',
    mime_type:            r.mimeType            || '',
    vendor_name:          r.vendorName          || '',
    receipt_date:         r.receiptDate         || null,
    paid_date:           r.paidDate            || null,   // date column — '' would fail   // date column — '' would fail
    total_amount:         parseFloat(r.totalAmount) || 0,
    cc_fee:               parseFloat(r.ccFee)   || 0,
    markup_pct:           (r.markupPct == null || r.markupPct === '') ? 15 : (parseFloat(r.markupPct) || 0),
    fee_breakdown:        r.feeBreakdown        || {},
    currency:             r.currency            || 'USD',
    extracted_json:       r.extractedJson       || {},
    match_confidence:     parseFloat(r.matchConfidence) || 0,
    po_id:                r.poId                || null,
    item_id:              r.itemId              || null,
    project_label:        r.projectLabel        || '',
    created_by:           r.createdBy           || '',
    fee_type:             r.feeType             || null,
    category:             r.category            || '',
    allocation_method:    r.allocationMethod    || 'net_price_weighted',
    allocation_breakdown: r.allocationBreakdown || [],
    billed_on_invoice_id: r.billedOnInvoiceId   || null,
    billed_on_summary_id: r.billedOnSummaryId   || null,
    paid_with_card_id:    r.paidWithCardId      || null,
    qb_target:            r.qbTarget            || null,
    qb_id:                r.qbId                || '',
    qb_expense_id:        r.qbExpenseId         || '',
    qb_pushed_at:         r.qbPushedAt          || null,
    qb_error:             r.qbError             || '',
    notes:                r.notes               || '',
    fee_absorptions:      Array.isArray(r.feeAbsorptions) ? r.feeAbsorptions : [],
    absorbed_at:          r.absorbedAt          || null,   // timestamptz — '' would fail
    absorbed_by:          r.absorbedBy          || '',
    approved_at:          r.approvedAt          || null,
    approved_by:          r.approvedBy          || '',
    rejected_at:          r.rejectedAt          || null,
    rejected_reason:      r.rejectedReason      || '',
    sales_tax_filing_id:  r.salesTaxFilingId    || null,   // uuid — '' would fail
    // created_at and updated_at managed by DB defaults + trigger
  };
}

// ── RECEIPT ATTACHMENTS ──────────────────────────────────────────────────
// Phase 4A (2026-06-27) — per-file attachments for a receipt. The
// existing receipts.filename + receipts.storage_path columns stay on
// the schema as the primary attachment's mirror (backward compat for
// code reading them directly); new attachments live as their own rows
// in receipt_attachments. See supabase/migrations/20260627_create_
// receipt_attachments_table.sql for the schema + RLS.
//
// Role values (CHECK constrained at DB):
//   'invoice', 'payment_confirmation', 'airline', 'hotel', 'meals',
//   'ground_transport', 'supporting', 'other'
//
// One attachment per receipt has is_primary=true. The primary is the
// OCR source + what single-file-aware code paths read.

export var RECEIPT_ATTACHMENT_ROLES = [
  'invoice',
  'payment_confirmation',
  'airline',
  'hotel',
  'meals',
  'ground_transport',
  'supporting',
  'other',
];

export function rowToReceiptAttachment(row) {
  if (!row) return null;
  return {
    id:           row.id,
    receiptId:    row.receipt_id || '',
    filename:     row.filename || '',
    storagePath:  row.storage_path || '',
    mimeType:     row.mime_type || '',
    sizeBytes:    row.size_bytes != null ? Number(row.size_bytes) : null,
    role:         row.role || 'invoice',
    isPrimary:    !!row.is_primary,
    uploadedAt:   row.uploaded_at || null,
    uploadedBy:   row.uploaded_by || '',
  };
}

// ── Receipts inbox search (2026-07-15) ─────────────────────────────────
// Cimon: "we need to be able to search numbers, vendor names, dates,
// POs, etc." The original inbox search matched only vendorName, the
// LINKED po_ref, and an exact-ish amount — dates, invoice numbers, and
// anything the scanner read off the paper were invisible.
//
// receiptMatchesSearch(receipt, po, query):
//   - splits the query on whitespace; EVERY token must match (AND), so
//     "sousa 184" narrows instead of widening.
//   - a token matches as a substring of the haystack below, or — when it
//     parses as a number — within $1 of the receipt total (so "1254"
//     finds a $1,254.44 receipt without typing cents).
//
// The haystack covers: vendor name (typed + scanner-extracted), linked
// PO ref, project label, filename, notes, category, fee type, kind
// words ("office", "reimbursable", "vendor deposit"…), the scanner's
// line-item descriptions AND full extracted receipt text (so invoice
// numbers, PO numbers printed on the paper, tracking numbers all hit),
// the receipt date + date-added in several human spellings
// (2026-06-12 · 6/12/2026 · 6/12 · june 12 · jun 12), and the total
// with/without $ and thousands commas.

var RECEIPT_SEARCH_MONTHS = ['january','february','march','april','may','june','july','august','september','october','november','december'];

function receiptSearchDateVariants(iso) {
  var m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  if (!m) return [];
  var y = m[1], mo = parseInt(m[2], 10), d = parseInt(m[3], 10);
  var name = RECEIPT_SEARCH_MONTHS[mo - 1] || '';
  return [
    m[0],
    mo + '/' + d + '/' + y,
    m[2] + '/' + m[3] + '/' + y,
    mo + '/' + d + '/' + y.slice(2),
    mo + '/' + d,
    name + ' ' + d,
    name.slice(0, 3) + ' ' + d,
    name + ' ' + y,
  ];
}

function receiptSearchAmountVariants(n) {
  var amt = parseFloat(n);
  if (!isFinite(amt) || amt === 0) return [];
  var fixed = amt.toFixed(2);
  var commas = Number(fixed).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return [fixed, commas, '$' + fixed, '$' + commas, String(Math.round(amt))];
}

var RECEIPT_KIND_SEARCH_WORDS = {
  vendor_deposit:      'vendor deposit',
  vendor_final:        'vendor final invoice',
  vendor_full_payment: 'vendor full payment',
  vendor_fee:          'vendor fee freight crating customs tariff',
  project_reimbursable:'reimbursable',
  personal:            'office overhead',
};

export function receiptSearchHaystack(receipt, po) {
  var r = receipt || {};
  var ex = r.extractedJson || {};
  var parts = [
    r.vendorName, (po && po.po_ref), r.projectLabel, r.filename,
    r.notes, r.category, r.feeType,
    RECEIPT_KIND_SEARCH_WORDS[r.kind] || '',
    ex.vendor_name, ex.extracted_text,
  ];
  (Array.isArray(ex.line_items) ? ex.line_items : []).forEach(function(li) {
    if (li && li.description) parts.push(li.description);
  });
  parts = parts.concat(receiptSearchDateVariants(r.receiptDate));
  parts = parts.concat(receiptSearchDateVariants(r.createdAt));
  parts = parts.concat(receiptSearchAmountVariants(r.totalAmount));
  if (parseFloat(r.ccFee) > 0) parts = parts.concat(receiptSearchAmountVariants(r.ccFee));
  return parts.filter(Boolean).join('\n').toLowerCase();
}

export function receiptMatchesSearch(receipt, po, query) {
  var q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  var hay = receiptSearchHaystack(receipt, po);
  var amt = parseFloat(receipt && receipt.totalAmount) || 0;
  return q.split(/\s+/).every(function(tok) {
    if (hay.indexOf(tok) >= 0) return true;
    var n = parseFloat(tok.replace(/[$,]/g, ''));
    return isFinite(n) && n !== 0 && Math.abs(amt - n) < 1;
  });
}

// receiptAttachmentsFor — filter the global attachments collection
// down to a single receipt's. Returns attachments sorted with the
// primary first, then by uploadedAt ascending.
export function receiptAttachmentsFor(receiptId, attachments) {
  if (!receiptId || !Array.isArray(attachments)) return [];
  return attachments
    .filter(function(a) { return a && a.receiptId === receiptId; })
    .slice()
    .sort(function(a, b) {
      if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
      var aT = a.uploadedAt || '';
      var bT = b.uploadedAt || '';
      return aT < bT ? -1 : aT > bT ? 1 : 0;
    });
}

// receiptPrimaryAttachment — pick the OCR source / single-file display.
// Returns the is_primary attachment if present, else the first attachment
// (defensive fallback for data without an explicit primary).
export function receiptPrimaryAttachment(receiptId, attachments) {
  var rows = receiptAttachmentsFor(receiptId, attachments);
  if (rows.length === 0) return null;
  var primary = rows.find(function(a) { return a.isPrimary; });
  return primary || rows[0];
}

// ── VENDORS ───────────────────────────────────────────────────────────────────
// Phase 1: the canonical vendor record still lives in app_storage.csid_vendors
// as JSON (camelCase, Date.now() integer `id`). The `vendors` SQL table is
// dual-written from VendorDirectory.save() and AppContext.addVendorInline so
// Phase 2 can flip QB-push lookups from name-match to legacy_id-based reads.
//
// vendorToRow is the single source of truth for the JSON → SQL column mapping.
// The integer `id` from the JSON blob lands in `legacy_id` (bigint); the SQL
// row's `id` is a server-side UUID. Helpers in api/_lib/vendors.js read by
// either column.

export function vendorToRow(v) {
  // Defensive guard against fractional legacy_id (post-2026-05-30
  // Phase 2 commit #1.5). The vendors SQL table column is bigint and
  // rejects fractional values; the JSON blob historically allowed
  // them from a legacy Date.now()+Math.random() creation path. If a
  // fractional id makes it here, console.warn loudly and floor — keeps
  // the SQL write succeeding (bigint accepts the integer) AND surfaces
  // the bug for follow-up. The blob-cleanup migration normalizes
  // existing fractional ids; this guard catches anything that slips
  // through future code paths.
  var legacyId = null;
  if (typeof v.id === 'number') {
    if (!Number.isInteger(v.id)) {
      console.warn(
        '[vendorToRow] fractional vendor.id (' + v.id + ') for "' +
        (v.name || '?') + '" \u2014 flooring to integer. Trace the ' +
        'caller and confirm no Date.now()+Math.random() creation path ' +
        'remains. See supabase/migrations/20260530_phase2_15_' +
        'orphans_and_fractional_cleanup.sql for the blob cleanup.'
      );
      legacyId = Math.floor(v.id);
    } else {
      legacyId = v.id;
    }
  }
  return {
    legacy_id:           legacyId,
    name:                v.name                || '',
    vendor_type:         v.vendorType          || 'Vendor',
    contact:             v.contact             || '',
    email:               v.email               || '',
    phone:               v.phone               || '',
    website:             v.website             || '',
    address:             v.address             || '',
    address2:            v.address2            || '',
    city:                v.city                || '',
    state:               v.state               || '',
    zip:                 v.zip                 || '',
    ship_attn:           v.shipAttn            || '',
    notes:               v.notes               || '',
    qb_vendor_id:        v.qbVendorId          || '',
    resale_cert_on_file: !!v.resaleCertOnFile,
    resale_cert_date:    v.resaleCertDate      || '',
    contacts:            Array.isArray(v.contacts) ? v.contacts : [],
    aliases:             Array.isArray(v.aliases)  ? v.aliases  : [],
    short_name:          (typeof v.shortName === 'string' && v.shortName.trim()) ? v.shortName.trim() : null,
    // DEPRECATED (2026-06-03): the vendor-level installs_own_goods flag
    // was the wrong abstraction. Installation fee on the item
    // (fees.installation > 0) is now the source of truth for whether
    // installation rides on the vendor PO. JS code no longer reads this
    // flag — mapper round-trip preserves the column value for any
    // external write source until the column is dropped. See migration
    // supabase/migrations/20260602_vendor_installs_own_goods.sql header.
    installs_own_goods:  !!v.installsOwnGoods,
  };
}

export function rowToVendor(row) {
  return {
    id:                row.legacy_id,                    // JSON-blob world reads numeric id
    uuid:              row.id,                            // available for Phase 2 consumers
    name:              row.name                || '',
    vendorType:        row.vendor_type         || 'Vendor',
    contact:           row.contact             || '',
    email:             row.email               || '',
    phone:             row.phone               || '',
    website:           row.website             || '',
    address:           row.address             || '',
    address2:          row.address2            || '',
    city:              row.city                || '',
    state:             row.state               || '',
    zip:               row.zip                 || '',
    shipAttn:          row.ship_attn           || '',
    notes:             row.notes               || '',
    qbVendorId:        row.qb_vendor_id        || '',
    resaleCertOnFile:  !!row.resale_cert_on_file,
    resaleCertDate:    row.resale_cert_date    || '',
    contacts:          Array.isArray(row.contacts) ? row.contacts : [],
    aliases:           Array.isArray(row.aliases)  ? row.aliases  : [],
    shortName:         row.short_name              || '',
    installsOwnGoods:  !!row.installs_own_goods,
  };
}

// Compute a vendor's short display label. Returns vendor.shortName when
// set (the curated override edited in VendorDirectory), otherwise falls
// back to the first word of vendor.name with a leading "The " stripped.
// Used by the PO payment-annotation pipeline and the backfill default
// rule — single source of truth so the two stay in lock-step.
//
// Examples:
//   { name: "Casa Design, LLC", shortName: "Casa" }     → "Casa"
//   { name: "Casa Design, LLC" }                        → "Casa"
//   { name: "The Future Perfect" }                      → "Future"
//   { name: "O+G Studio" }                              → "O+G"
//   { name: "Hewn SF" }                                 → "Hewn"
//   null / { name: "" }                                 → ""
export function vendorShortName(vendor) {
  if (!vendor) return '';
  var explicit = typeof vendor.shortName === 'string' ? vendor.shortName.trim() : '';
  if (explicit) return explicit;
  return firstWordShortName(vendor.name);
}

// Pure helper — first word of name, stripping a leading "The ".
// Exported for the backfill script + tests.
export function firstWordShortName(name) {
  var s = String(name || '').trim();
  if (!s) return '';
  // Strip a leading "The " (case-insensitive). Don't strip mid-name.
  s = s.replace(/^the\s+/i, '');
  // First token = run of non-whitespace, non-comma characters.
  var m = s.match(/^[^\s,]+/);
  return m ? m[0] : '';
}

// Date string for the invoice PDF header (DEPOSIT + FINAL). Priority:
//   sent_at      → when the document went to the client (load-bearing
//                  for issued docs — re-opening a sent FIN should show
//                  the send date, not today's calendar date)
//   createdAt    → when the invoice row was first written (right value
//                  for an unsent draft re-opened later)
//   today        → truly fresh (no row yet, fresh-generation flow)
//
// Returns "M/D/YYYY" (en-US locale) — matches the format the Python PDF
// renderer (api/generate-deposit-invoice.py) expects and renders verbatim.
// Both build paths (fresh generation via buildDepositInvoicePayloadPure
// and buildFinalInvoicePayload, regenerate-from-saved via
// InvoicesTab.jsx) route through this helper so the date semantic is
// consistent across surfaces.
//
// 2026-06-26 fix: added in response to user-reported "FIN PDF shows
// today's calendar date instead of the issue date." Mirrors the same
// fix shape applied to the PO PDF header earlier (issued_at fallback).
export function formatInvoiceDateForPdf(sentAt, createdAt) {
  var candidate = (sentAt && String(sentAt).trim())
                || (createdAt && String(createdAt).trim())
                || null;
  var d = candidate ? new Date(candidate) : new Date();
  if (isNaN(d.getTime())) d = new Date();
  return d.toLocaleDateString('en-US');
}

// Resolve a vendor by name, trying primary name then aliases, exact
// then case-insensitive (post-2026-05-30 aliases feature). Returns the
// matching vendor object or null.
//
// Resolution order:
//   1. exact match on vendor.name
//   2. case-insensitive match on vendor.name
//   3. exact match on any entry in vendor.aliases
//   4. case-insensitive match on any alias
//   5. null
//
// Aliases are populated automatically: when a user manually re-picks
// a vendor on a receipt where the OCR'd name didn't resolve, the
// OCR'd name is added to that vendor's aliases. Cimon can also
// add/remove aliases manually via the VendorDirectory edit modal.
//
// Edge cases:
//   - Empty/null name → null (no resolution attempt)
//   - Empty/null vendors list → null
//   - Vendor with no aliases field (legacy data) → treated as empty
//   - Whitespace-only name → trimmed; if empty after trim, null
//   - Aliases array containing non-string entries → those entries
//     skipped (defensive against malformed legacy data)
export function resolveVendorByName(name, vendors) {
  var needle = typeof name === 'string' ? name.trim() : '';
  if (!needle) return null;
  if (!Array.isArray(vendors) || vendors.length === 0) return null;
  var needleLc = needle.toLowerCase();

  // Pass 1 — exact name match.
  for (var i = 0; i < vendors.length; i++) {
    if ((vendors[i].name || '') === needle) return vendors[i];
  }
  // Pass 2 — case-insensitive name match.
  for (var i = 0; i < vendors.length; i++) {
    if (String(vendors[i].name || '').toLowerCase() === needleLc) return vendors[i];
  }
  // Pass 3 — exact alias match.
  for (var i = 0; i < vendors.length; i++) {
    var aliases = Array.isArray(vendors[i].aliases) ? vendors[i].aliases : [];
    for (var j = 0; j < aliases.length; j++) {
      if (typeof aliases[j] === 'string' && aliases[j] === needle) return vendors[i];
    }
  }
  // Pass 4 — case-insensitive alias match.
  for (var i = 0; i < vendors.length; i++) {
    var aliases = Array.isArray(vendors[i].aliases) ? vendors[i].aliases : [];
    for (var j = 0; j < aliases.length; j++) {
      if (typeof aliases[j] === 'string' && aliases[j].toLowerCase() === needleLc) return vendors[i];
    }
  }
  return null;
}

// ── FIRM CREDIT CARDS ─────────────────────────────────────────────────────────
// Owner-managed catalog of firm credit cards. Each receipt can reference a
// card via paid_with_card_id so the bookkeeper can match Gilda receipts to
// QB transactions during monthly reconciliation. We store last_4 only --
// never the full PAN (PCI compliance).

export function rowToCreditCard(row) {
  var allowed = row.allowed_kinds;
  if (typeof allowed === 'string') {
    try { allowed = JSON.parse(allowed); } catch (e) { allowed = []; }
  }
  var visibleTo = row.visible_to_emails;
  if (typeof visibleTo === 'string') {
    try { visibleTo = JSON.parse(visibleTo); } catch (e) { visibleTo = []; }
  }
  return {
    id:               row.id,
    label:            row.label             || '',
    cardType:         row.card_type         || 'other',
    last4:            row.last_4            || '',
    holderEmail:      row.holder_email      || '',
    allowedKinds:     Array.isArray(allowed) ? allowed : [],
    visibility:       row.visibility        || 'private',
    visibleToEmails:  Array.isArray(visibleTo) ? visibleTo : [],
    billingZip:       row.billing_zip       || '',
    statementEmail:   row.statement_email   || '',
    expiration:       row.expiration        || '',
    notes:            row.notes             || '',
    isActive:         row.is_active !== false,
    createdBy:        row.created_by        || '',
    createdAt:        row.created_at        || '',
    updatedAt:        row.updated_at        || '',
  };
}

export function creditCardToRow(c) {
  var id = c.id;
  if (!id || /^\d+$/.test(String(id))) id = undefined;
  var visibleTo = Array.isArray(c.visibleToEmails) ? c.visibleToEmails : [];
  visibleTo = visibleTo.map(function(e){ return String(e || '').toLowerCase().trim(); }).filter(Boolean);
  return {
    id:                 id,
    label:              c.label           || '',
    card_type:          c.cardType        || 'other',
    last_4:             c.last4           || '',
    holder_email:       (c.holderEmail || '').toLowerCase(),
    allowed_kinds:      Array.isArray(c.allowedKinds) ? c.allowedKinds : [],
    visibility:         c.visibility      || 'private',
    visible_to_emails:  visibleTo,
    billing_zip:        c.billingZip      || '',
    statement_email:    c.statementEmail  || '',
    expiration:         c.expiration      || '',
    notes:              c.notes           || '',
    is_active:          c.isActive !== false,
    created_by:         c.createdBy       || '',
  };
}

// Filter cards eligible for the current receipt context. A card is eligible
// for the user when:
//   1. it's active
//   2. visibility allows them to use it (private=holder only / team=in
//      visible_to_emails / firm=anyone)
//   3. allowed_kinds is empty OR includes this kind
// Used by the per-receipt "Paid with" dropdown.
//
// opts.includeAll (default false): when true, bypasses the per-user
// visibility check — admin/owner needs to see every active card so
// they can assign the correct payment source to staff-uploaded
// receipts (e.g., when Quinn forgets to set "Paid with" on her own
// receipt and the owner has to fix it post-upload). Active + kind
// filters still apply.
export function eligibleCardsForReceipt(cards, userEmail, receiptKind, opts) {
  var email = (userEmail || '').toLowerCase();
  var includeAll = !!(opts && opts.includeAll);
  return (cards || []).filter(function(c) {
    if (!c.isActive) return false;

    if (!includeAll) {
      var visibility = c.visibility || 'private';
      var canUse;
      if (visibility === 'firm') {
        canUse = true;
      } else if (visibility === 'team') {
        var list = (c.visibleToEmails || []).map(function(e){ return String(e || '').toLowerCase(); });
        canUse = list.indexOf(email) >= 0 || (c.holderEmail || '').toLowerCase() === email;
      } else {
        canUse = (c.holderEmail || '').toLowerCase() === email;
      }
      if (!canUse) return false;
    }

    if (!c.allowedKinds || c.allowedKinds.length === 0) return true;
    return c.allowedKinds.indexOf(receiptKind) >= 0;
  });
}

// ── ASSEMBLY HELPERS ─────────────────────────────────────────────────────────

export function isParent(it) {
  return !it.parentId;
}

// ── QUOTE SUMMARY HELPERS ────────────────────────────────────────────────────

export var QUOTE_FIELD_KEYS = [
  'price', 'leadTime', 'product', 'sku', 'colorway',
  'finish', 'dimensions', 'qty', 'vendor', 'notes',
];

export function extractQuoteFields(raw) {
  var result = {};
  QUOTE_FIELD_KEYS.forEach(function(k) {
    result[k] = raw && raw[k] ? String(raw[k]) : '';
  });
  return result;
}

// ── DOCUMENT EXPORT HELPERS ──────────────────────────────────────────────────

export var EXPORTABLE_DOC_TYPES = [
  'Spec Sheet',
  'Quote',
  'Purchase Order',
  'Proposal',
  'Drawing',
  'All',
];

export function groupDocsByType(docs, type) {
  if (!docs || !docs.length) return [];
  if (type === 'All') return docs.slice();
  return docs.filter(function(d) {
    var t = d.doc_type || d.type || '';
    return t === type;
  });
}

export function buildExportZipName(project, scheduleType, docType) {
  var date = new Date().toISOString().slice(0, 10);
  var p = (project || 'Project').replace(/[^a-zA-Z0-9-]/g, '');
  var s = (scheduleType || 'All').replace(/[^a-zA-Z0-9]/g, '');
  var d = (docType || 'Docs').replace(/[^a-zA-Z0-9]/g, '');
  return p + '_' + s + '_' + d + '_' + date + '.zip';
}

// ── Extracted from SchedulesTab ───────────────────────────────────────────────

// Window-treatment assemblies wrap several peers (Fabric, Workroom, Hardware,
// Trim, Roller Shades). On non-internal schedule outputs (Workroom Schedule,
// Vendor Schedule, etc.) the row identifies the assembly by the *face fabric*
// the consumer of the doc actually orders / cuts / matches — not by the
// "Custom Drapery" wrapper name. Returns null when:
//   - scheduleType is not 'Window Treatments' (other assemblies keep their
//     assembly-level name+image; only WT swaps to fabric)
//   - no Fabric peer exists (caller falls back to assembly name+image)
//
// Return shape:
//   {
//     productName,  // always — applies to every non-internal audience
//     imageUrl,     // always
//     workroomOverrides?: {  // present ONLY when audience === 'workroom'
//       notes,             // string (may be '') — labeled, newline-separated
//                          //   consolidation of every peer's notes in fixed
//                          //   order (Fabric → Trim → Workroom → Hardware;
//                          //   Motor folds in)
//       quantityDisplay?,  // "<qty> <UNIT>" from Fabric peer; omitted when
//                          //   either field is blank (legacy qty fallback)
//       source,            // string (may be '') — two-line "Mfr: <m>" /
//                          //   "Vendor: <v>" block from Fabric peer
//       panels,            // string (may be '') — three-line block from
//                          //   Workroom peer (numPanels+draw / pleat+fullness
//                          //   / mountType formatted as "X Mounted")
//       hardware,          // string (may be '') — three-line block from
//                          //   Hardware peer (name / Mfr / Vendor)
//     }
//   }
//
// Grouping under `workroomOverrides` makes it obvious at every call site
// which fields are "workroom audience magic" vs. shared fabric-face logic.
// Adding new workroom-only fields stays a one-line append rather than a
// new top-level key that callers have to remember to wire up.
export function pickWindowTreatmentScheduleDisplay(asmItems, scheduleType, audience) {
  if (scheduleType !== 'Window Treatments') return null;
  var items = asmItems || [];
  var fabric = items.find(function(it) { return it && it.childType === 'Fabric'; });
  if (!fabric) return null;
  var hero = fabric.imageUrl
    || (fabric.images && fabric.images.length > 0
        ? (fabric.images[fabric.heroIndex || 0] || {}).url
        : '')
    || '';
  var result = {
    productName: fabric.productName || fabric.item || '',
    imageUrl: hero || '',
  };
  if (audience === 'workroom') {
    var workroomPeer = items.find(function(it) { return it && it.childType === 'Workroom'; });
    var hardwarePeer = items.find(function(it) { return it && it.childType === 'Hardware'; });
    var overrides = {
      notes:    consolidateWindowTreatmentNotes(items),
      source:   formatWorkroomSourceBlock(fabric),
      panels:   formatWorkroomPanelsBlock(workroomPeer),
      hardware: formatWorkroomHardwareBlock(hardwarePeer),
    };
    var qDisp = formatFabricQuantity(fabric.qty, fabric.qtyType);
    if (qDisp) overrides.quantityDisplay = qDisp;
    result.workroomOverrides = overrides;
  }
  return result;
}

// Install schedule: collapse WT peers into one synthetic install row per
// assembly. WT assemblies live as peer rows (Fabric / Workroom / Hardware
// / Trim) with no parent record — the default install filter, which expects
// a parent item per row, drops every peer because each carries both
// assembly_id and child_type. This helper groups peers by assembly_id and
// emits one synthetic row per assembly that downstream `mapInstallItem`
// (in SchedulesTab.jsx) can consume directly.
//
// Field sourcing per synthetic row:
//   tag / room / sidemark  → assembly metadata, fall back to primary peer
//   item / product / hero  → Fabric peer (the visual identity of the WT)
//   vendor                 → Workroom peer (who ships the fabricated unit)
//   dimensions             → Workroom finishedWidth × finishedLength
//   qty / qty_type         → Workroom (panels), fall back to Fabric
//   orderPlacedDate / lead → Workroom (the fabricated drapery is the
//                             install unit), fall back to Fabric
//   status                 → Workroom, fall back to Fabric, fall back to primary
//
// Eligibility: an assembly is install-eligible if ANY peer's status is in
// INSTALL_STATUSES or its procurementClass is owner_furnished/by_others or
// its id is in installExtraIds.
export function buildWindowTreatmentInstallRows(items, opts) {
  var INSTALL_STATUSES = ['Ordered', 'Shipped', 'At Receiver', 'Delivered', 'Installed', 'Complete'];
  var o = opts || {};
  var assemblies = o.assemblies || [];
  var installExtraIds = o.installExtraIds || [];
  var projectLabel = o.projectLabel || '';

  function isPeerEligible(it) {
    return INSTALL_STATUSES.indexOf(canonicalItemStatus(it.status)) >= 0
      || it.procurementClass === 'owner_furnished'
      || it.procurementClass === 'by_others'
      || installExtraIds.indexOf(it.id) >= 0;
  }

  var byAsm = {};
  for (var i = 0; i < items.length; i++) {
    var it = items[i];
    if (!it) continue;
    if (it.schedule !== 'Window Treatments') continue;
    if (!it.assemblyId || !it.childType) continue;
    if (projectLabel && it.project !== projectLabel) continue;
    if (!byAsm[it.assemblyId]) byAsm[it.assemblyId] = [];
    byAsm[it.assemblyId].push(it);
  }

  // Any flagged peer (including the no-childType fabric primary, which
  // the byAsm grouping below never sees) defaults the assembly off.
  var flaggedAsmOff = {};
  for (var fi = 0; fi < items.length; fi++) {
    var fit = items[fi];
    if (fit && fit.assemblyId && fit.offScheduleDefault) flaggedAsmOff[fit.assemblyId] = true;
  }

  var rows = [];
  Object.keys(byAsm).forEach(function(asmId) {
    var asmItems = byAsm[asmId];
    if (flaggedAsmOff[asmId] && installExtraIds.indexOf('__wt_asm__:' + asmId) < 0) return;
    if (!asmItems.some(isPeerEligible)) return;

    var asm = assemblies.find(function(a) { return a && a.id === asmId; }) || {};
    var fabric   = asmItems.find(function(p) { return p.childType === 'Fabric'; });
    var workroom = asmItems.find(function(p) { return p.childType === 'Workroom'; });
    var hardware = asmItems.find(function(p) { return p.childType === 'Hardware'; });
    var primary  = workroom || fabric || asmItems[0];
    var ws = (workroom && workroom.specs) || {};

    var dim = '';
    if (ws.finishedWidth || ws.finishedLength) {
      dim = [ws.finishedWidth, ws.finishedLength].filter(Boolean).join('×');
    } else {
      dim = primary.dimensions || '';
    }

    var heroImg = '';
    if (fabric) {
      heroImg = fabric.imageUrl
        || (fabric.images && fabric.images.length > 0
            ? ((fabric.images[fabric.heroIndex || 0] || {}).url || '')
            : '');
    }
    if (!heroImg) {
      heroImg = primary.imageUrl
        || (primary.images && primary.images.length > 0
            ? ((primary.images[primary.heroIndex || 0] || {}).url || '')
            : '');
    }

    var wrEntries = [].concat(
      (workroom && workroom.wrEntries) || [],
      (fabric && fabric.wrEntries) || [],
      (hardware && hardware.wrEntries) || []
    );

    rows.push({
      // Synthetic id — won't collide with real item ids; used only for
      // de-duping inside the install schedule render pass.
      id: '__wt_asm__:' + asmId,
      assemblyId: asmId,
      childType: '',
      parentId: null,
      schedule: 'Window Treatments',
      project: primary.project || '',
      tag: asm.tag || primary.tag || '',
      item: (fabric && (fabric.productName || fabric.item)) || asm.name || primary.item || '',
      productName: (fabric && fabric.productName) || asm.name || primary.productName || '',
      manufacturer: (fabric && fabric.manufacturer) || '',
      model: (fabric && fabric.model) || '',
      dimensions: dim,
      qty: (workroom && workroom.qty) || (fabric && fabric.qty) || primary.qty || '',
      qtyType: (workroom && workroom.qtyType) || (fabric && fabric.qtyType) || primary.qtyType || '',
      vendor: (workroom && workroom.vendor) || (fabric && fabric.vendor) || primary.vendor || '',
      imageUrl: heroImg,
      images: (fabric && fabric.images) || primary.images || [],
      heroIndex: (fabric && fabric.heroIndex) || 0,
      wrEntries: wrEntries,
      orderPlacedDate: (workroom && workroom.orderPlacedDate) || (fabric && fabric.orderPlacedDate) || primary.orderPlacedDate || '',
      leadTime: (workroom && workroom.leadTime) || (fabric && fabric.leadTime) || primary.leadTime || '',
      leadWeeks: (workroom && workroom.leadWeeks) || (fabric && fabric.leadWeeks) || primary.leadWeeks || 0,
      roomName: asm.room_name || primary.roomName || '',
      roomNumber: asm.room_number || primary.roomNumber || '',
      sidemark: asm.sidemark || primary.sidemark || '',
      status: (workroom && workroom.status) || (fabric && fabric.status) || primary.status || '',
      specs: { installNotes: ws.installNotes || (fabric && fabric.specs && fabric.specs.installNotes) || '' },
      shipToVendorId: primary.shipToVendorId || '',
      procurementClass: primary.procurementClass || 'standard',
    });
  });

  return rows;
}

// SOURCE column on the Workroom Schedule: two-line labeled block sourced
// from the Fabric peer (or trim record on standalone trim rows). Each line
// is dropped if its underlying field is empty. Manufacturer = manufacturer
// are NOT deduped — when they happen to match, the workroom values seeing
// both is meaningful information. Returns '' when both fields are empty.
export function formatWorkroomSourceBlock(item) {
  if (!item) return '';
  var mfr = (item.manufacturer || '').toString().trim();
  var ven = (item.vendor || '').toString().trim();
  var lines = [];
  if (mfr) lines.push('Mfr: ' + mfr);
  if (ven) lines.push('Vendor: ' + ven);
  return lines.join('\n');
}

// PANELS column on the Workroom Schedule: three-line consolidated block
// sourced entirely from the Workroom peer's specs. Per spec:
//   Line 1: "{N} Panel(s) · {draw} Draw"  (Stationary drops the " Draw" suffix)
//   Line 2: "{pleat} · {fullness}% Fullness"
//   Line 3: mount value formatted as "X Mounted"
// Each line is dropped if all of its underlying fields are empty. Returns
// '' when all three lines are empty.
export function formatWorkroomPanelsBlock(workroomPeer) {
  if (!workroomPeer) return '';
  var specs = workroomPeer.specs || {};
  var lines = [];

  var nRaw = (specs.numPanels == null ? '' : String(specs.numPanels)).trim();
  var nNum = parseInt(nRaw, 10);
  var panelsPart = '';
  if (nRaw) {
    var unit = (nNum === 1) ? 'Panel' : 'Panels';
    panelsPart = nRaw + ' ' + unit;
  }
  var drawRaw = (specs.draw || '').toString().trim();
  var drawPart = '';
  if (drawRaw) {
    // "Stationary Draw" reads as a contradiction — strip the " Draw" suffix
    // for the one value where the panels don't draw at all.
    drawPart = drawRaw === 'Stationary' ? 'Stationary' : drawRaw + ' Draw';
  }
  var line1 = [panelsPart, drawPart].filter(Boolean).join(' · ');
  if (line1) lines.push(line1);

  var pleatRaw = (specs.pleat || '').toString().trim();
  // Strip any user-typed trailing '%' from the stored fullness value
  // before appending '% Fullness'. Pre-fix users sometimes typed
  // "60%" into the input — the renderer then output "60%% Fullness".
  // (2026-06-29 — Cimon).
  var fullRaw = (specs.fullness == null ? '' : String(specs.fullness)).trim().replace(/%\s*$/, '');
  var fullPart = fullRaw ? fullRaw + '% Fullness' : '';
  var line2 = [pleatRaw, fullPart].filter(Boolean).join(' · ');
  if (line2) lines.push(line2);

  var mountLine = formatMountValue(specs.mountType);
  if (mountLine) lines.push(mountLine);

  return lines.join('\n');
}

// HARDWARE column on the Workroom Schedule: three-line block sourced from
// the Hardware peer. Each line dropped when its field is empty. Returns ''
// when the hardware peer is missing or all three fields are empty (e.g.
// standalone trim rows: no hardware peer → empty cell).
export function formatWorkroomHardwareBlock(hardwarePeer) {
  if (!hardwarePeer) return '';
  var name = (hardwarePeer.productName || hardwarePeer.item || '').toString().trim();
  var mfr  = (hardwarePeer.manufacturer || '').toString().trim();
  var ven  = (hardwarePeer.vendor || '').toString().trim();
  var lines = [];
  if (name) lines.push(name);
  if (mfr)  lines.push('Mfr: ' + mfr);
  if (ven)  lines.push('Vendor: ' + ven);
  return lines.join('\n');
}

// Mount value formatter. Stored values include the " Mount" suffix today
// ("Ceiling Mount", "Outside Mount", "Inside Mount") but may also arrive
// bare ("Ceiling"). Strip a trailing " Mount" (case-insensitive) if
// present, then append " Mounted". Returns '' for empty input.
export function formatMountValue(mount) {
  var raw = (mount == null ? '' : String(mount)).trim();
  if (!raw) return '';
  var stripped = raw.replace(/\s+mount$/i, '').trim();
  return stripped + ' Mounted';
}

// Format a fabric/trim line's quantity for the Workroom Schedule QTY column.
// Returns '<qty> <UNIT_ABBREV>' (e.g. "18 YD", "12 M") or '' when either
// piece is missing — caller treats '' as "fall back to assembly qty, no
// unit suffix". Exported so standalone trim rows (not in an assembly) can
// reuse the same formatting without duplicating the unit mapping table.
export function formatFabricQuantity(qty, qtyType) {
  var q = (qty == null ? '' : String(qty)).trim();
  var t = (qtyType == null ? '' : String(qtyType)).trim();
  if (!q || !t) return '';
  return q + ' ' + abbreviateFabricUnit(t);
}

// Unit abbreviation table for fabric/trim quantities. Case-insensitive
// match; unmapped values pass through as upper-cased so the column still
// renders something legible (e.g. 'Each' → 'EACH'). Grow the table as new
// QTY_TYPES values surface on fabric/trim records.
function abbreviateFabricUnit(unit) {
  var key = String(unit || '').trim().toLowerCase();
  switch (key) {
    case 'yard':
    case 'yards':
    case 'yardage':
      return 'YD';
    case 'meter':
    case 'meters':
      return 'M';
    default:
      return String(unit).toUpperCase();
  }
}

// Collect the .notes string from every peer of the given childType (multiple
// peers — e.g. contrast banding — are joined with newlines), trimmed and
// dropping empties. Returns '' when none.
function gatherPeerNotes(asmItems, childType) {
  return (asmItems || [])
    .filter(function(it) { return it && it.childType === childType; })
    .map(function(it) { return (it && it.notes ? String(it.notes) : '').trim(); })
    .filter(Boolean)
    .join('\n');
}

// Build the labeled, fixed-order notes block for a WT workroom row. Sections
// with empty notes are omitted entirely (no "Workroom: " orphan label).
// Motor peers fold into the Hardware section per spec — motor notes are an
// install detail the workroom reads alongside hardware, not a separate column.
function consolidateWindowTreatmentNotes(asmItems) {
  var hardwareNotes = [
    gatherPeerNotes(asmItems, 'Hardware'),
    gatherPeerNotes(asmItems, 'Motor'),
  ].filter(Boolean).join('\n');
  var sections = [
    ['Fabric',   gatherPeerNotes(asmItems, 'Fabric')],
    ['Trim',     gatherPeerNotes(asmItems, 'Trim')],
    ['Workroom', gatherPeerNotes(asmItems, 'Workroom')],
    ['Hardware', hardwareNotes],
  ];
  return sections
    .filter(function(s) { return s[1]; })
    .map(function(s) { return s[0] + ': ' + s[1]; })
    .join('\n');
}

// ── Extracted from ProposalsTab2 ──────────────────────────────────────────────

// Deposit amount for one item: product at cdp% + all fees if vendor=100%.
// Uses assemblyClient so it works correctly for assemblies.
//
// project (optional, Fix C Phase 1 — 2026-05-27): when provided AND
// hasProjectTaxConfig(project), the sales-tax portion of the deposit
// (cdp=100 branch only) derives FRESH from computeItemTax instead of
// reading the stored item.fees.salesTax. Backwards compatible —
// existing call sites that don't pass project keep the legacy read.
export function itemDepositAmount(it, allItems, project) {
  // Reference pricing on OF/BO/ABD items must never become a client ask
  // (2026-07-29 — pricing fields restored for record-keeping; the money
  // gate moved from field-hiding to the math itself).
  if (!shouldBillItem(it)) return 0;
  var cdp = clientDepositPct(parseFloat(it.depositPct || 50));
  var prod = Math.round(assemblyClient(it, allItems) * (cdp / 100) * 100) / 100;
  if (cdp < 100) return prod;
  var fees = it.fees || {};
  var f = 0;
  if (fees.freight)      f += applyFreightMarkup(fees.freight, it);
  if (fees.crating)      f += applyFreightMarkup(fees.crating, it);
  if (fees.installation) f += applyFreightMarkup(fees.installation, it);
  // Sales tax: prefer fresh computation when project config is present.
  // computeItemTax already accounts for per-category taxable flags +
  // marked-up bases, so it returns the same number a register would.
  if (hasProjectTaxConfig(project)) {
    f += computeItemTax(it, project, allItems);
  } else if (fees.salesTax) {
    f += applyTaxMarkup(fees.salesTax);
  }
  if (fees.tariff)       f += applyTaxMarkup(fees.tariff);
  (fees.custom || []).forEach(function(c) { if (c.amount) f += customFeeClient(c, it); });
  return prod + Math.round(f * 100) / 100;
}

// Aggregates fee buckets across a list of items, applying the canonical
// markups (freight / crating / installation / custom = ×1.10; sales tax /
// tariff = pass-through). Returns the shape consumed by the Proposals tab
// summary panel — and reused inside buildProposalPayloadPure.
//
// Returns:
//   { freight, crating, installation, salesTax, tariff,
//     custom: { [label]: amount },
//     hasFees }
//
// Aggregates fees regardless of each item's vdp — the summary panel shows
// what the client sees on the proposal bottom line. Deposit-vs-final
// routing happens downstream in itemDepositAmount and the payload builders.
export function aggregateProposalFees(items) {
  function fpN(v) { return parseFloat(String(v == null ? '' : v).replace(/[$,]/g, '')) || 0; }
  var freight = 0, crating = 0, installation = 0, salesTax = 0, tariff = 0;
  var custom = {};
  (items || []).forEach(function(it) {
    if (!shouldBillItem(it)) return; // reference-priced OF/BO/ABD never bill
    var f = (it && it.fees) || {};
    // 2026-06-10 — items.ccFeePct silent line multiplier. Each fee
    // value gets multiplied by (1 + ccFeePct/100) on a per-item basis
    // so the proposal/billing-summary "Total" columns reflect the
    // CC fee recovery the client will see on their invoice. When
    // ccFeePct=0 (default), mul=1.0 and behavior unchanged.
    var mul = ccFeeMul(it);
    freight      += applyFreightMarkup(fpN(f.freight), it)      * mul;
    crating      += applyFreightMarkup(fpN(f.crating), it)      * mul;
    installation += applyFreightMarkup(fpN(f.installation), it) * mul;
    salesTax     += applyTaxMarkup(fpN(f.salesTax))         * mul;
    tariff       += applyTaxMarkup(fpN(f.tariff))           * mul;
    (f.custom || []).forEach(function(c) {
      var amt = fpN(c.amount);
      if (amt > 0) {
        var lbl = c.label || 'Custom fee';
        custom[lbl] = (custom[lbl] || 0) + customFeeClient(c, it) * mul;
      }
    });
  });
  function r(x) { return Math.round(x * 100) / 100; }
  Object.keys(custom).forEach(function(k) { custom[k] = r(custom[k]); });
  var hasFees = (freight + crating + installation + salesTax + tariff) > 0
    || Object.keys(custom).length > 0;
  return {
    freight:      r(freight),
    crating:      r(crating),
    installation: r(installation),
    salesTax:     r(salesTax),
    tariff:       r(tariff),
    custom:       custom,
    hasFees:      hasFees,
  };
}

// Sidemark — the short routing string the firm requests vendors print on
// shipping cartons so the receiver-warehouse can sort goods to the right
// project + room without opening the box. Format is mechanical:
//
//   CS/{projShort}/{roomName}/{roomNumber}
//
// projShort = projectLabel with year prefix stripped (e.g. "26-Hoback" →
// "Hoback"). Returns empty string when any input is missing so the
// renderer doesn't emit "CS///" garbage.
//
// Source of truth: every render surface (ItemForm save, PO PDF payload,
// deposit + final invoice payloads, vendor email panels, PO Generator,
// Schedules view, Tear Sheet) consumes this helper. The stored
// `item.sidemark` field is a cache — viewer surfaces recompute on every
// render rather than trusting it, so a stale stored value never reaches
// a vendor doc. The cache exists for legacy compatibility with rows
// written before the 2026-05-03 cleanup; backfill SQL exists separately.
//
// Pre-2026-05-03 the format included " 580" after the project segment
// (e.g. "CS/Hoback 580/Living Room/101"). 580 was a property unit number
// for one specific Hoback property that got conflated with a firm-level
// identifier; commit 56f5a4a removed it. Unit numbers belong on the
// project's address fields, not on the sidemark.
export function computeSidemark(project, roomName, roomNumber) {
  var projShort = (project || '').toString().replace(/^\d+-/, '');
  if (!projShort || !roomName || !roomNumber) return '';
  return 'CS/' + projShort + '/' + roomName + '/' + roomNumber;
}

// Full contract value for a proposal — products (assembly-aware) PLUS every
// marked-up fee bucket. This is what the client owes in total, distinct
// from the deposit (collected at signing) and from the products-only
// subtotal. Single source of truth consumed by:
//   - ProposalsTab2 summary panel's "Proposal Total" line
//   - buildProposalPayloadPure (proposal PDF)
//   - BillingSummarySection's per-proposal "Total" column
// All three previously had their own inline rollup; converging on this
// helper kills a class of drift bugs (latent installation omission, etc).
export function proposalGrandTotal(items, allItems, project) {
  // Product side is per-item × ccFeeMul. Fee side already multiplies
  // internally inside aggregateProposalFees. (2026-06-10)
  var prod = (items || []).reduce(function(s, it) {
    if (!shouldBillItem(it)) return s; // reference-priced OF/BO/ABD never bill
    return s + (assemblyClient(it, allItems) * ccFeeMul(it));
  }, 0);
  var agg = aggregateProposalFees(items || []);
  var customSum = Object.keys(agg.custom).reduce(function(s, k) { return s + agg.custom[k]; }, 0);
  // Sales tax: FRESH from project config when available (Fix C), else the
  // legacy stored per-item field via the aggregate. 2026-08-05 — the
  // 26HOB-P-041 bug: the PDF printed a fresh $4,228.02 SALES TAX line
  // while this helper's stored-field tax was $0, so PROPOSAL TOTAL
  // excluded the tax shown directly above it. Billable items only —
  // reference-priced OF/BO never contribute tax.
  var salesTax = hasProjectTaxConfig(project)
    ? proposalTax((items || []).filter(shouldBillItem), project, allItems)
    : agg.salesTax;
  return Math.round((prod + agg.freight + agg.crating + agg.installation + salesTax + agg.tariff + customSum) * 100) / 100;
}

// Derive a short project code from the project label when projectCode field is not set.
// "26-Hoback" → "26HOB", "26-Moskito" → "26MOS", "26-Ptarmigan" → "26PTR"
export function projectLabelToCode(label) {
  var m = (label || '').match(/^(\d+)-([A-Za-z]+)/);
  if (m) return m[1] + m[2].slice(0, 3).toUpperCase();
  return (label || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8);
}

// Auto-generate the next proposal reference for a project.
// Format: [projectCode]-P-[seq] e.g. 26HOB-P-003
// Sequence is per-project, 3-digit zero-padded, derived from existing proposals.
export function generateProposalRef(projectCode, allProposals) {
  var prefix = (projectCode || '').trim().toUpperCase() + '-P-';
  var max = 0;
  (allProposals || []).forEach(function(p) {
    var ref = (p.proposal_ref || '').toUpperCase();
    if (ref.indexOf(prefix) === 0) {
      var num = parseInt(ref.slice(prefix.length), 10);
      if (!isNaN(num) && num > max) max = num;
    }
  });
  return prefix + String(max + 1).padStart(3, '0');
}

// Auto-generate the next PO reference for a project.
// Format: [projectCode]-PO-[seq] e.g. 26HOB-PO-007
// Sequence is per-project, 3-digit zero-padded, derived from existing POs.
export function generatePORef(projectCode, allPOs) {
  var prefix = (projectCode || '').trim().toUpperCase() + '-PO-';
  var max = 0;
  (allPOs || []).forEach(function(p) {
    var ref = (p.po_ref || '').toUpperCase();
    if (ref.indexOf(prefix) === 0) {
      var num = parseInt(ref.slice(prefix.length), 10);
      if (!isNaN(num) && num > max) max = num;
    }
  });
  return prefix + String(max + 1).padStart(3, '0');
}

// Auto-generate the next credit-memo reference for a project.
// Format: [projectCode]-CM-[seq] e.g. 26HOB-CM-001
// Sequence is per-project, 3-digit zero-padded, derived from existing memos.
// A credit memo records cancelled/returned merchandise, a vendor refund, or a
// billing adjustment — never a client payment, which is a deposit.
export function generateCreditMemoRef(projectCode, allMemos) {
  var prefix = (projectCode || '').trim().toUpperCase() + '-CM-';
  var max = 0;
  (allMemos || []).forEach(function(m) {
    var ref = (m.memo_ref || m.memoRef || '').toUpperCase();
    if (ref.indexOf(prefix) === 0) {
      var num = parseInt(ref.slice(prefix.length), 10);
      if (!isNaN(num) && num > max) max = num;
    }
  });
  return prefix + String(max + 1).padStart(3, '0');
}

// Per-invoice-type prefix mapping. Each type maps to a token in the ref.
//   deposit                    -> [projectCode]-INV-NNN-DEP    (since 2026-05-23)
//   final                      -> [projectCode]-INV-NNN-FIN    (since 2026-05-23)
//   product (legacy alias)     -> [projectCode]-INV-NNN        (no suffix)
//   reimbursable               -> [projectCode]-RE-NNN
//   designer_hours             -> [projectCode]-DH-NNN
//
// Deposit + final share the -INV- counter pool. A deposit and its matched
// final on the same proposal carry the SAME numeric counter with different
// suffixes (e.g., 26HOB-INV-003-DEP pairs with 26HOB-INV-003-FIN). The
// pairing is enforced at the final-invoice build site
// (buildFinalInvoicePayload) which inherits the counter from the matched
// deposit row's invoiceRef rather than re-minting via this function.
// This function is still the source for fresh counters: deposit creation
// (always), and final creation when no matched deposit exists (Rule 3 edge
// case — final without deposit).
var INVOICE_REF_TOKEN = {
  deposit: 'INV',
  final: 'INV',
  product: 'INV',          // legacy alias when caller doesn't know which draw
  reimbursable: 'RE',
  designer_hours: 'DH',
};

var INVOICE_REF_SUFFIX = {
  deposit: '-DEP',
  final:   '-FIN',
};

// Auto-generate the next Invoice reference for a project + type.
// Sequence is per-project AND per-token — reimbursables have their own
// counter, designer hours have theirs, deposit+final share the INV pool.
// When scanning for max, -DEP / -FIN suffixes are stripped so a deposit
// at 005 and a final at 005 are recognized as the same counter (max=5).
//
// The returned ref carries the per-type suffix:
//   generateInvoiceRef('26HOB', [], 'deposit')  -> '26HOB-INV-001-DEP'
//   generateInvoiceRef('26HOB', [], 'final')    -> '26HOB-INV-001-FIN'
//   generateInvoiceRef('26HOB', [], 'reimbursable') -> '26HOB-RE-001'
//   generateInvoiceRef('26HOB', [])  // no type -> '26HOB-INV-001'  (legacy)
export function generateInvoiceRef(projectCode, allInvoices, invoiceType) {
  var token  = INVOICE_REF_TOKEN[invoiceType || 'product'] || 'INV';
  var suffix = INVOICE_REF_SUFFIX[invoiceType] || '';
  var prefix = (projectCode || '').trim().toUpperCase() + '-' + token + '-';
  var max = 0;
  (allInvoices || []).forEach(function(inv) {
    var ref = (inv.invoice_ref || inv.invoiceRef || '').toUpperCase();
    if (ref.indexOf(prefix) !== 0) return;
    // Strip optional -DEP / -FIN suffix so deposits and finals share the
    // same counter pool when finding max.
    var numericPart = ref.slice(prefix.length).replace(/-(DEP|FIN)$/, '');
    var num = parseInt(numericPart, 10);
    if (!isNaN(num) && num > max) max = num;
  });
  return prefix + String(max + 1).padStart(3, '0') + suffix;
}

// ── ORDERS IN PROCESS / FINAL INVOICE HELPERS ───────────────────────────────
// "In process" = past Client Approved, before Complete, not yet final-invoiced.
// Statuses included: Deposit Paid, PO Issued, Order Confirmed, Received, Installed.
// The bookkeeper's view in Billing -> Invoicing uses this to surface what's
// in flight and what to bill next.

var IN_PROCESS_STATUSES = [
  'Deposit Paid',
  'PO Issued',
  'Ordered',
  'Shipped',
  'At Receiver',
  'Delivered',
  'Installed',
];

// Returns true when this item should appear in the Orders-in-Process
// picker because it has billable balance remaining on a future final
// invoice.
//
// SPEC (CLAUDE.md "Payment state comes from payment records"): the
// in-process determination reads payment_allocations via
// estimatedFinalInvoiceAmount, not item-status proxies. An item is
// in-process if estimatedFinalInvoiceAmount > 0 AND it's a billable,
// non-component, not-yet-finalized item.
//
// allInvoices + allocations are required so the estimate can compute
// the post-deposit balance correctly. Callers without those in scope
// (e.g. legacy unit tests pre-2026-05-25) get "no payments anywhere"
// behavior, which matches the old proxy in the vdp<100 case but now
// also surfaces vdp>=100 items with positive unpaid balance — the
// collateral B fix (vendor=100% items with partial deposit payment
// are recoverable through the final-invoice path).
export function isItemInProcess(item, allInvoices, allocations, allItems) {
  if (!item) return false;
  if (item.parentId) return false;                     // components roll into parents
  if (!shouldBillItem(item)) return false;              // ABD / OF / BO + Cancelled never bills
  if (item.finalInvoiceId) return false;                // already on a final invoice
  // Status floor — the gate that admits an item to the picker. The
  // 2026-06-06 Adjustments patch removed the previous "remaining > 0.005"
  // early-return so 100%-upfront items (vdp=100 fully paid, $0 balance)
  // also surface here. Rationale: Cimon needs to be able to put those
  // items on a FIN to layer pass-through adjustments (late tariff,
  // freight overage, etc.) and send the document to Dan even though the
  // per-item balance is $0. Since 2026-08-15 the picker's est column is
  // Model B (owed − paid), so a fully-settled item shows $0 while one
  // with late-landed fees shows the real remaining balance — see
  // estimatedFinalInvoiceAmount.
  //
  // Standard path: status in IN_PROCESS_STATUSES (Deposit Paid through
  // Installed).
  // Collateral B path: vdp>=100 at Client Approved with unpaid
  // deposit balance — items stuck pre-cascade because partial payment
  // never tripped the Deposit Paid flip.
  if (IN_PROCESS_STATUSES.indexOf(canonicalItemStatus(item.status)) >= 0) return true;
  if (item.status === 'Client Approved' && parseFloat(item.depositPct || 50) >= 100) return true;
  // Partial-deposit path (2026-07-18 — the Kumiko FX case): a Client
  // Approved item whose matched deposit invoice has REAL cash recorded
  // is in process even though the full-payment cascade never fired
  // (cascadePaidItems flips status only when payment >= amount_due, so
  // a partial deposit strands the item below Deposit Paid forever).
  // The client has committed money; the FIN is where the remaining
  // balance (deposit shortfall + final %) gets billed — Model B reads
  // the cash gap via itemPaidShare, so admitting the item here is what
  // lets the shortfall ride the FIN. CLAUDE.md Rule 10: allocations are
  // the authority, not the status string a cascade never wrote. The
  // vdp>=100 branch above is the same idea for 100%-upfront items;
  // this branch covers every other deposit percentage.
  if (item.status === 'Client Approved'
      && itemPaidShare(item, allInvoices, allocations, allItems) > 0.005) return true;
  return false;
}

// True when ANY principal money has been paid to the vendor on this PO —
// deposit included. Cimon's rule (2026-07-05): "any time we give money to
// the vendor, the order is considered committed." Commitment is
// ORDER-level: one wired deposit commits every item on the PO. Fee-only
// rows (freight bills, CC fees) never count — same principal-only
// discipline as computePoPaidBefore / computePoItemPaymentHistory.
export function poHasVendorPrincipalPayment(po, vendorPayments) {
  if (!po || !po.id) return false;
  var poId = String(po.id);
  return (vendorPayments || []).some(function(vp) {
    if (!vp) return false;
    var vpPoId = vp.poId || vp.po_id;
    if (String(vpPoId) !== poId) return false;
    var t = vp.paymentType || vp.payment_type || '';
    if (PRINCIPAL_PAYMENT_TYPES.indexOf(t) < 0) return false;
    return (parseFloat(vp.amount) || 0) > 0;
  });
}

// Item-level wrapper: resolve the item's PO (membership in po.items),
// then ask whether that order has vendor money on it.
export function itemVendorCommitted(item, purchaseOrders, vendorPayments) {
  if (!item || !item.id) return false;
  var po = (purchaseOrders || []).find(function(p) {
    var ids = Array.isArray(p && p.items) ? p.items
            : Array.isArray(p && p.item_ids) ? p.item_ids
            : [];
    return ids.indexOf(item.id) >= 0;
  });
  return poHasVendorPrincipalPayment(po, vendorPayments);
}

// Ready = the vendor has COMMITTED. Two forms of evidence, either
// suffices (Cimon 2026-07-05: "if we have paid the vendor the vendor has
// committed — this includes a deposit"; money beats a manually-advanced
// status, the same Rule-10 instinct the payment math follows):
//   (a) status advanced to Order Confirmed / Received / Installed on the
//       Tracker (the vendor's acknowledgment was recorded by hand), or
//   (b) principal vendor money recorded on the item's PO — deposit
//       included; fee-only rows never count (poHasVendorPrincipalPayment).
// purchaseOrders + vendorPayments are OPTIONAL trailing args — callers
// without them get the status-only check (b is unknowable without the
// ledgers). All three display surfaces (Orders in Process picker,
// ItemDatabase BILL READY badge, ItemForm banner) pass them. Advisory
// only — never blocks or pre-selects anything.
//
// Signature mirrors isItemInProcess (allInvoices + allocations + allItems
// optional) since it delegates to isItemInProcess for the base eligibility
// check.
export function isItemReadyForFinalInvoice(item, allInvoices, allocations, allItems, purchaseOrders, vendorPayments) {
  if (!isItemInProcess(item, allInvoices, allocations, allItems)) return false;
  // Status-ready = the vendor's order is real (Ordered+ on the physical
  // chain). Shipped counts — vendor shipped means committed.
  var _frs = canonicalItemStatus(item.status);
  if (_frs === 'Ordered' || _frs === 'Shipped' || _frs === 'At Receiver' || _frs === 'Delivered' || _frs === 'Installed') return true;
  return itemVendorCommitted(item, purchaseOrders, vendorPayments);
}

// Estimated final-invoice amount for a single item — what the final
// invoice would bill today.
//
// SPEC (rebuilt 2026-08-15 — the 26MOS-PO-003 picker contradiction):
// EXACTLY the FIN generator's Model B per-item balance, so the picker
// can never disagree with the invoice it produces:
//
//   est = itemLifecycleOwed(item, allItems, project) × ccFeeMul(item)
//         − itemPaidShare(item)
//
// The previous derivation (clientTotal − expectedDeposit + unpaid, plus
// fees only when vdp<100) assumed a 100%-deposit item collected its
// fees inside the deposit. That's true when fees existed at approval —
// but the receipts pipeline writes fee ACTUALS as vendor invoices
// arrive, i.e. after the DEP was billed, and those late fees vanished
// from the estimate ($0 est beside a real "client final due"). Model B
// reads actual cash instead of expected-deposit assumptions, so late
// fees, partial payments (Kumiko), and per-item depositPct overrides
// all fall out of one subtraction.
//
// `project` (optional) supplies the tax config for fresh per-item tax;
// callers without it fall back to the item's stored fees.salesTax —
// the same fallback itemLifecycleOwed always had. The CC processing
// multiplier is included (owed × ccFeeMul) to match the FIN payload;
// paid stays raw cash (what the client actually wired).
export function estimatedFinalInvoiceAmount(item, allItems, allInvoices, allocations, project) {
  if (!item || item.parentId || !shouldBillItem(item)) return 0;
  if (item.finalInvoiceId) return 0;

  var owed = itemLifecycleOwed(item, allItems || [item], project) * ccFeeMul(item);
  var paid = itemPaidShare(item, allInvoices, allocations, allItems || [item]);
  var est = Math.round((owed - paid) * 100) / 100;
  if (est < 0) est = 0;
  // Rounding-residue clamp (2026-07-05) — mirrors the per-item balance
  // clamp in buildFinalInvoicePayload: a sub-2¢ "estimate" is a rounding
  // crumb from per-item deposit rounding, not money to collect. Without
  // this the picker shows a $0.01 est and generating would mint a
  // one-cent FIN line.
  if (est > 0 && est < 0.02) est = 0;
  return est;
}

// Group an array of items by PO ref (or by a synthetic 'no-po' bucket).
// Returns { groups: [{ poRef, poId, vendor, project, items, estimatedTotal }], }.
// Bookkeeper uses this to see at-a-glance which items will ship together.
//
// allInvoices + allocations are forwarded to estimatedFinalInvoiceAmount
// so per-group totals reflect actual payment state, not the static 20%
// proxy. Required since 2026-05-25. `projects` (optional, 2026-08-15)
// supplies per-item tax config so group estimates match the FIN.
export function groupItemsByPO(items, allItems, purchaseOrders, allInvoices, allocations, projects) {
  var poById = {};
  (purchaseOrders || []).forEach(function(po) { poById[po.id] = po; });

  // First pass: which PO does each item belong to?
  // Each item carries po_number on its row; we also walk through purchase_orders.items
  // to handle items added via the PO picker without po_number being set.
  var itemPoId = {};
  (purchaseOrders || []).forEach(function(po) {
    (po.items || []).forEach(function(itemId) {
      if (!itemPoId[itemId]) itemPoId[itemId] = po.id;
    });
  });

  var groups = {};
  (items || []).forEach(function(it) {
    var poId = itemPoId[it.id] || '';
    var po   = poId ? poById[poId] : null;
    var key  = poId || ('no-po:' + (it.project || it.projectLabel || 'unassigned'));
    if (!groups[key]) {
      groups[key] = {
        poId:     poId,
        poRef:    po ? (po.po_ref || '') : '',
        vendor:   po ? (po.vendorName || '') : (it.vendor || ''),
        project:  po ? (po.project    || '') : (it.project || it.projectLabel || ''),
        items:    [],
        estimatedTotal: 0,
      };
    }
    var projObj = (projects || []).find(function(pr) {
      return pr && pr.label === (it.project || it.projectLabel || '');
    });
    var est = estimatedFinalInvoiceAmount(it, allItems, allInvoices, allocations, projObj);
    groups[key].items.push(it);
    groups[key].estimatedTotal += est;
  });

  // Round group totals to cents
  Object.keys(groups).forEach(function(k) {
    groups[k].estimatedTotal = Math.round(groups[k].estimatedTotal * 100) / 100;
  });

  // Stable sort: PO refs alphabetically, no-PO buckets last
  var keys = Object.keys(groups).sort(function(a, b) {
    var aPO = groups[a].poRef, bPO = groups[b].poRef;
    if (aPO && !bPO) return -1;
    if (!aPO && bPO) return 1;
    return (aPO || a).localeCompare(bPO || b);
  });

  return { groups: groups, orderedKeys: keys };
}

// Build the payload for /api/generate-deposit-invoice with invoiceType='deposit'.
// Used by ProposalsTab2.previewDepositInvoice; extracted from there 2026-05-12
// so the function can be unit-tested. Same pure-function shape as
// buildFinalInvoicePayload below.
//
// opts: { proposal, allItems, allInvoices, projects, clients, firmSettings }
//
// Throws if any item on the proposal isn't at Client Approved (or later) with
// a SKU assigned — those items represent an incomplete approval cascade and
// would land on the deposit invoice without ever advancing through PO Issued
// / Order Confirmed / etc. The "Sync now" banner on the proposal toolbar
// invokes the recovery path.
//
// Cancelled items on the proposal are filtered out so a post-approval cancel
// doesn't bill the client for the killed line.
//
// Fee inclusion: per-item, fees ride with the deposit only when
// it.depositPct >= 100 (in-stock items where the vendor charges 100%
// upfront — no separate final invoice will be issued). Otherwise fees
// defer to the final invoice. The per-fee breakdown is surfaced as
// feeFreightTot / feeCratingTot / feeInstallationTot / feeTariffTot /
// feeSalesTaxTot / feeCustom[] so the PDF can render line items, and the
// caller persists those fields onto the invoice row so re-rendering from
// the saved record preserves the breakout (rather than collapsing to a
// single 'Fees & Sales Tax' line).
export function buildDepositInvoicePayload(opts) {
  var p             = opts.proposal || {};
  var allItems      = opts.allItems    || [];
  var allInvoices   = opts.allInvoices || [];
  var projects      = opts.projects    || [];
  var clients       = opts.clients     || [];
  var firmSettings  = opts.firmSettings || {};

  // Approval cascade guard. Mirrors the prior ProposalsTab2 inline version.
  var targets = collectApprovalTargets(p.items || [], allItems);
  var stuck      = targets.toApprove.filter(function(it) { return STATUSES.indexOf(it.status) < STATUSES.indexOf('Client Approved'); });
  var missingSku = targets.toApprove.filter(function(it) { return !it.itemNumber; });
  if (stuck.length > 0 || missingSku.length > 0) {
    var reasons = [];
    if (stuck.length > 0) {
      var stuckNames = stuck.map(function(x) { return (x.item || x.productName || x.id) + ' (' + x.status + ')'; }).join(', ');
      reasons.push(stuck.length + ' item' + (stuck.length>1?'s':'') + ' not yet at Client Approved: ' + stuckNames);
    }
    if (missingSku.length > 0) {
      var missingNames = missingSku.map(function(x) { return x.item || x.productName || x.id; }).join(', ');
      reasons.push(missingSku.length + ' item' + (missingSku.length>1?'s':'') + ' missing SKU: ' + missingNames);
    }
    throw new Error('Cannot build deposit invoice \u2014 ' + reasons.join('; ') + '. Use the "Sync now" banner on the proposal to fix.');
  }

  function resolveProject(label) {
    return projects.find(function(pr) { return pr.label === label; }) || null;
  }
  function resolveClient(id) {
    return clients.find(function(cl) { return cl.id === id; }) || null;
  }
  function clientForProposal(prop) {
    var proj = resolveProject(prop.project);
    return resolveClient(prop.clientId) || (proj ? resolveClient(proj.clientId) : null);
  }
  function fp(s) { var n = parseFloat((s || '').toString().replace(/[$,]/g, '')); return isNaN(n) ? 0 : n; }

  var pItems = (p.items || []).map(function(id) { return allItems.find(function(it) { return it.id === id; }); })
    .filter(function(it) { return it && !isCancelled(it); });
  var client = clientForProposal(p);
  var proj = resolveProject(p.project);
  var projShort = (p.project || '').replace(/^\d+-/, '');

  var productTotal = 0, feeFreightTot = 0, feeCratingTot = 0, feeInstallationTot = 0, feeTariffTot = 0, feeSalesTaxTot = 0;
  var feeCustomMap = {};

  var lineItems = pItems.map(function(it) {
    // ccFeePct multiplier (2026-06-10) — silent line multiplier per
    // Decision 7. Applies to every billable line on the DEP PDF when
    // set on the item. PO/proposal/FIN all multiply identically so
    // the client's lifecycle bill stays consistent.
    var mul = ccFeeMul(it);
    var billable = shouldBillItem(it);
    // Reference-priced OF/BO/ABD items never bill (2026-07-29) — they
    // appear on the DEP as $0 sign-off lines exactly as when unpriced.
    var lineClient = billable ? assemblyClient(it, allItems) * mul : 0;
    productTotal += lineClient;

    var qty = parseQty(it.qty) || 1;
    var unitClient = lineClient / qty;

    var vdp = parseFloat(it.depositPct || 50);
    var includeFees = vdp >= 100;  // fees ride with deposit only on 100% vendor items
    // Pass `proj` so the cdp=100 branch of itemDepositAmount uses the
    // freshly-computed project-derived tax (Fix C Phase 1). Falls back
    // to legacy fees.salesTax when project lacks tax config.
    // itemDepositAmount is the deposit amount the bookkeeper actually
    // bills the client at deposit time — multiply by ccFeePct so the
    // DEP amount_due reflects the silent CC fee recovery on the
    // portion being billed.
    var depositAmt = itemDepositAmount(it, allItems, proj) * mul;
    var depositPctClient = clientDepositPct(vdp);

    var fees = it.fees || {};
    var liFreight      = includeFees ? fp(fees.freight)      : 0;
    var liCrating      = includeFees ? fp(fees.crating)      : 0;
    var liInstallation = includeFees ? fp(fees.installation) : 0;
    var liTariff       = includeFees ? fp(fees.tariff)       : 0;
    if (liFreight      > 0) { feeFreightTot      += applyFreightMarkup(liFreight, it) * mul; }
    if (liCrating      > 0) { feeCratingTot      += applyFreightMarkup(liCrating, it) * mul; }
    if (liInstallation > 0) { feeInstallationTot += applyFreightMarkup(liInstallation, it) * mul; }
    if (liTariff       > 0) { feeTariffTot       += applyTaxMarkup(liTariff) * mul; }
    // Sales tax: prefer fresh computation when project tax config
    // present. Otherwise legacy per-item stored value (only when
    // includeFees i.e. cdp=100).
    if (includeFees) {
      if (hasProjectTaxConfig(proj)) {
        feeSalesTaxTot += computeItemTax(it, proj, allItems) * mul;
      } else if (fp(fees.salesTax) > 0) {
        feeSalesTaxTot += applyTaxMarkup(fp(fees.salesTax)) * mul;
      }
    }

    if (includeFees) {
      (fees.custom || []).forEach(function(c) {
        var amt = fp(c.amount);
        if (amt > 0) {
          var lbl = c.label || 'Custom Fee';
          feeCustomMap[lbl] = (feeCustomMap[lbl] || 0) + customFeeClient(c, it) * mul;
        }
      });
    }

    var sidemark = computeSidemark(p.project, it.roomName, it.roomNumber);
    return {
      // Stamped since 2026-09-11 so refreshInvoiceSnapshotSpecs can join a
      // stored line back to its item exactly, instead of falling back to
      // position against item_ids. Ignored by the PDF renderer.
      itemId: String(it.id || ''),
      item: it.item || 'Untitled',
      productName: it.productName || '',
      description: it.specNotes || '',
      finish: it.finish || '',
      dimensions: it.dimensions || '',
      qty: String(qty),
      qtyType: it.qtyType || 'Each',
      unitPrice: Math.round(unitClient * 100) / 100,
      total: Math.round(lineClient * 100) / 100,
      depositAmt: Math.round(depositAmt * 100) / 100,
      depositPct: depositPctClient,
      leadTime: it.leadTime || '',
      sidemark: sidemark,
      images: it.images || [],
      heroIndex: it.heroIndex || 0,
    };
  });

  var feeCustomList = Object.keys(feeCustomMap).map(function(lbl) {
    return { label: lbl, amount: Math.round(feeCustomMap[lbl] * 100) / 100 };
  });
  var feesTotalRounded = Math.round((feeFreightTot + feeCratingTot + feeInstallationTot + feeTariffTot + feeSalesTaxTot + feeCustomList.reduce(function(s, c) { return s + c.amount; }, 0)) * 100) / 100;
  var productTotalRounded = Math.round(productTotal * 100) / 100;
  var subtotal = Math.round((productTotalRounded + feesTotalRounded) * 100) / 100;

  // depositTotal = sum of the RENDERED per-line depositAmt values — the
  // exact numbers on the DEP PDF lines. itemDepositAmount already includes
  // the marked-up fees for vdp>=100 items (and excludes them for vdp<100
  // items, where fees ride to the final invoice), so nothing is added back
  // here (feesTotalRounded on top would double-count vdp=100 fees).
  //
  // 2026-07-08 — MUST sum lineItems[].depositAmt, never recompute from a
  // separate reduce. The pre-fix reduce called itemDepositAmount WITHOUT
  // `proj`, so the invoice's amount_due used the stale stored
  // fees.salesTax while the rendered lines (6212 above) used the fresh
  // project-derived tax. Every DEP minted at approval carried an
  // amount_due that disagreed with its own PDF lines AND with the billing
  // summary cover the client paid against — Billing History's ask changed
  // the moment approval switched its source from the frozen cover to DEP
  // amount_due. Summing the rendered lines makes the identity
  // depositTotal === Σ lineItems.depositAmt structural.
  var depositTotal = Math.round(lineItems.reduce(function(s, li) { return s + (li.depositAmt || 0); }, 0) * 100) / 100;
  var balanceRemaining = Math.round((subtotal - depositTotal) * 100) / 100;
  if (balanceRemaining < 0) balanceRemaining = 0;

  var billToAddrLines = [];
  if (client) {
    if (client.billingAddress) billToAddrLines.push(client.billingAddress);
    if (client.city || client.state || client.zip) {
      billToAddrLines.push([client.city, client.state, client.zip].filter(Boolean).join(', '));
    }
  }
  var siteAddrLines = [];
  if (proj) {
    if (proj.address)  siteAddrLines.push(proj.address);
    if (proj.address2) siteAddrLines.push(proj.address2);
    if (proj.city || proj.state || proj.zip) {
      siteAddrLines.push([proj.city, proj.state, proj.zip].filter(Boolean).join(', '));
    }
  }

  var projObj = projects.find(function(pr) { return pr.label === p.project; });
  var projectCode = (projObj && projObj.projectCode) || projectLabelToCode(p.project);
  var invoiceRef = generateInvoiceRef(projectCode, allInvoices, 'deposit');

  return {
    payload: {
      invoiceRef:      invoiceRef,
      invoiceType:     'deposit',
      projectLabel:    p.project || '',
      projectDisplay:  (projObj && projObj.displayName) || p.project || '',
      billToName:      (client && client.name) || '',
      billToAddr:      billToAddrLines.join('\n'),
      siteAddr:        siteAddrLines.join('\n'),
      lineItems:       lineItems,
      productTotal:    productTotalRounded,
      feeFreightTot:      Math.round(feeFreightTot      * 100) / 100,
      feeCratingTot:      Math.round(feeCratingTot      * 100) / 100,
      feeInstallationTot: Math.round(feeInstallationTot * 100) / 100,
      feeTariffTot:       Math.round(feeTariffTot       * 100) / 100,
      feeSalesTaxTot:     Math.round(feeSalesTaxTot     * 100) / 100,
      feeCustom:          feeCustomList,
      subtotal:        subtotal,
      depositTotal:    depositTotal,
      balanceRemaining: balanceRemaining,
      notes:           p.notes || '',
      // Date priority: sent_at > createdAt > today. Fresh-generation
      // typically has neither sentAt nor createdAt available (the row
      // isn't written yet); helper resolves to today in that case.
      date:            formatInvoiceDateForPdf(p.sentAt || null, p.createdAt || null),
      firmName:   firmSettings.name    || 'Cimon Starr LLC',
      firmStreet: firmSettings.address || '3516 Sacramento Street',
      firmCity:   firmSettings.city    || 'San Francisco',
      firmState:  firmSettings.state   || 'CA',
      firmZip:    firmSettings.zip     || '94118',
    },
    invoiceRef:           invoiceRef,
    productTotalRounded:  productTotalRounded,
    feesTotalRounded:     feesTotalRounded,
    subtotal:             subtotal,
    depositTotal:         depositTotal,
    itemIds:              p.items || [],
  };
}

// Compose the full `invoices` row record for a proposal's deposit invoice
// from the same pure builder that produces the PDF payload.
//
// INITIAL CREATION ONLY. The only call site (`approveProposal` in
// ProposalsTab2) invokes this without `opts.existing` — the record
// gets a fresh id, fresh ref, default status, and the current item
// snapshot baked in. The result is the canonical deposit-invoice
// commitment record between firm and client.
//
// The `opts.existing` branch is preserved for two reasons:
//   1. Defensive — if approveProposal is ever re-triggered (manual
//      recovery path) and a row already exists for the proposal, the
//      branch reuses the existing id so the upsert lands in place
//      instead of inserting a duplicate that the partial unique
//      index would reject with 23505.
//   2. Future "Revise Deposit Invoice" spec (post-CO revisions) is
//      not yet defined — when that spec lands, it should produce a
//      NEW revision row rather than mutating the original snapshot.
//      Until then, the existing branch should NOT be reached from a
//      regenerate-PDF flow. PDF regeneration in the Invoices tab
//      reads inv.lineItems (the stored snapshot) and posts that to
//      /api/generate-deposit-invoice without touching the record.
//
// 2026-05-25: removed the previewDepositInvoice flow (ProposalsTab2
// toolbar) that was the second call site. That flow regenerated the
// record's content from current proposal state on every click —
// silently overwriting the immutable snapshot when CO drift had
// shifted item values.
//
// Returns `{ built, record }`:
//   - `built` is the full output of buildDepositInvoicePayload (payload +
//     totals + itemIds), passed through unchanged so the caller can POST
//     `built.payload` to the PDF endpoint.
//   - `record` is the row shape consumed by `saveInvoice`. When `opts.existing`
//     is provided, id / invoiceRef / status / qb* / pdfStoragePath / sentAt /
//     createdBy are taken from the existing row so the upsert lands on the
//     same id and doesn't reset audit fields. Everything else (lineItems,
//     totals, per-fee breakdown, amountDue) is recomputed from the
//     proposal's current item state.
//
// Throws the same errors as buildDepositInvoicePayload — items not at
// Client Approved with SKU set will surface a "Cannot build deposit
// invoice" error. approveProposal swallows via fire-and-forget
// try/catch; the proposal stays Approved if the builder throws.
//
// opts: { proposal, allItems, allInvoices, projects, clients, firmSettings, existing? }
export function buildDepositInvoiceRecord(opts) {
  var proposal     = opts.proposal     || {};
  var allItems     = opts.allItems     || [];
  var allInvoices  = opts.allInvoices  || [];
  var projects     = opts.projects     || [];
  var clients      = opts.clients      || [];
  var firmSettings = opts.firmSettings || {};
  var existing     = opts.existing     || null;

  var built = buildDepositInvoicePayload({
    proposal:     proposal,
    allItems:     allItems,
    allInvoices:  allInvoices,
    projects:     projects,
    clients:      clients,
    firmSettings: firmSettings,
  });

  // Client resolution mirrors the pure builder's clientForProposal closure:
  // direct on proposal.clientId, else via the project's clientId.
  var client = null;
  if (proposal.clientId) {
    client = clients.find(function(c) { return String(c.id) === String(proposal.clientId); }) || null;
  }
  if (!client) {
    var proj = projects.find(function(pr) { return pr.label === proposal.project; });
    if (proj && proj.clientId) {
      client = clients.find(function(c) { return String(c.id) === String(proj.clientId); }) || null;
    }
  }

  var newId;
  if (existing && existing.id) {
    newId = existing.id;
  } else if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    newId = crypto.randomUUID();
  } else {
    newId = Date.now() + '-' + Math.random();
  }

  var record = {
    id:           newId,
    invoiceRef:   existing ? existing.invoiceRef : built.invoiceRef,
    invoiceType:  'deposit',
    status:       existing ? existing.status : 'draft',
    proposalId:   proposal.id,
    project:      proposal.project || '',
    clientId:     proposal.clientId || (client ? client.id : ''),
    clientName:   client ? client.name : '',
    itemIds:      built.itemIds,
    lineItems:    built.payload.lineItems,
    productTotal: built.productTotalRounded,
    feesTotal:    built.feesTotalRounded,
    // Per-fee breakdown — persisted so re-render doesn't collapse to a
    // single 'Fees & Sales Tax' line. Migration 20260512.
    feeFreightTot:      built.payload.feeFreightTot      || 0,
    feeCratingTot:      built.payload.feeCratingTot      || 0,
    feeInstallationTot: built.payload.feeInstallationTot || 0,
    feeTariffTot:       built.payload.feeTariffTot       || 0,
    feeSalesTaxTot:     built.payload.feeSalesTaxTot     || 0,
    feeCustom:          built.payload.feeCustom          || [],
    subtotal:     built.subtotal,
    amountDue:    built.depositTotal,
    qbInvoiceId:  existing ? existing.qbInvoiceId  : '',
    qbEstimateId: existing ? existing.qbEstimateId : '',
    qbPushedAt:   existing ? existing.qbPushedAt   : '',
    qbError:      existing ? existing.qbError      : '',
    notes:        proposal.notes || '',
    pdfStoragePath: existing ? existing.pdfStoragePath : '',
    sentAt:         existing ? existing.sentAt         : '',
    createdBy:      existing ? existing.createdBy      : '',
  };

  return { built: built, record: record };
}

// Excel-column-style letter for a 0-based index.
//   0 -> 'A', 25 -> 'Z', 26 -> 'AA', 51 -> 'AZ', 52 -> 'BA',
//   701 -> 'ZZ', 702 -> 'AAA'.
// Used to label per-item deposit invoice refs (INV-001-A-DEP, -B-DEP, ...).
export function letterFor(index) {
  if (index == null || index < 0 || !isFinite(index)) return '';
  var n = Math.floor(index);
  var s = '';
  do {
    s = String.fromCharCode(65 + (n % 26)) + s;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return s;
}

// PLURAL builder — per-item deposit invoice records.
//
// New deposit-invoice model (2026-05-26): when a proposal is Client
// Approved, generate ONE invoice record PER item on the proposal,
// each with its own letter-suffixed ref (26HOB-INV-001-A-DEP,
// -B-DEP, ...). Each record carries that item's pro-rata share of
// the proposal's deposit: product + per-item-routed fees per the
// vdp-<-100 vs vdp->=-100 rule (itemDepositAmount handles the
// routing; no double-counting).
//
// This sub-commit (1 of 3) ADDS the plural builder without
// changing the call site. approveProposal still calls the singular
// buildDepositInvoiceRecord; sub-commit 2 switches it over.
//
// opts: {
//   proposal,           // proposal row (id, items[], project, clientId, notes)
//   allItems,           // full items catalog
//   allInvoices,        // existing invoices (for counter dispense)
//   projects, clients,  // directories
//   firmSettings,
//   existingPerItem?,   // optional map { [itemId]: existing invoice row }
//                       // — when present, that item's record reuses the
//                       //   existing id / invoiceRef / qb* / status fields
//                       //   so an upsert lands on the same row.
// }
//
// Returns: [{ built, record, item, letter }, ...] in proposal.items order.
//   - built  — full output of buildDepositInvoicePayload for the single
//              item (payload + totals + itemIds), ref overridden with
//              the letter-suffixed per-item ref.
//   - record — row shape consumed by saveInvoice (rowToInvoice's input).
//              Carries itemId set to the single item's id.
//   - item   — the resolved item object (for the caller's tagging needs).
//   - letter — the letter assigned (A, B, C, ...).
//
// Throws the same errors as buildDepositInvoicePayload — items not at
// Client Approved with SKU set surface a "Cannot build deposit invoice"
// error. The caller (approveProposal in sub-commit 2) wraps in
// fire-and-forget try/catch so a builder throw doesn't unwind the
// proposal approval itself.
export function buildDepositInvoiceRecords(opts) {
  var proposal       = opts.proposal       || {};
  var allItems       = opts.allItems       || [];
  var allInvoices    = opts.allInvoices    || [];
  var projects       = opts.projects       || [];
  var clients        = opts.clients        || [];
  var firmSettings   = opts.firmSettings   || {};
  var existingPerItem = opts.existingPerItem || {};

  // Resolve proposal items preserving curated order. Cancelled items are
  // dropped (they shouldn't be billed) but their slot in the letter
  // sequence is preserved so post-cancel re-runs land on consistent
  // letters for the surviving items.
  var pItemIds = proposal.items || [];
  var resolved = pItemIds.map(function(id, i) {
    var it = allItems.find(function(x) { return x.id === id; });
    return { item: it, letter: letterFor(i) };
  }).filter(function(r) { return r.item && !isCancelled(r.item); });

  // Determine the proposal-level base ref ONCE. Subsequent per-item refs
  // share the same numeric counter — only the letter differs. We do NOT
  // re-call generateInvoiceRef inside the loop because the in-memory
  // allInvoices snapshot doesn't update mid-build; calling N times would
  // return the same value anyway.
  var projObj     = projects.find(function(pr) { return pr.label === proposal.project; });
  var projectCode = (projObj && projObj.projectCode) || projectLabelToCode(proposal.project);
  var baseRef     = generateInvoiceRef(projectCode, allInvoices, 'deposit');
  // Strip the per-type suffix so we can re-append the item letter + suffix.
  // baseRef is "26HOB-INV-001-DEP"; baseNumeric is "26HOB-INV-001".
  var baseNumeric = baseRef.replace(/-DEP$/, '');

  // Resolve client once (same algorithm as the singular builder).
  var client = null;
  if (proposal.clientId) {
    client = clients.find(function(c) { return String(c.id) === String(proposal.clientId); }) || null;
  }
  if (!client) {
    var proj = projects.find(function(pr) { return pr.label === proposal.project; });
    if (proj && proj.clientId) {
      client = clients.find(function(c) { return String(c.id) === String(proj.clientId); }) || null;
    }
  }

  return resolved.map(function(r) {
    var item   = r.item;
    var letter = r.letter;

    // Synthesize a single-item proposal for the pure payload builder.
    // assemblyClient / itemDepositAmount inside the builder use allItems
    // for lookups; we pass the full catalog so assembly peers (priced
    // independently per the Component Independence rule) resolve
    // correctly even when this builder is invoked one item at a time.
    var synth = Object.assign({}, proposal, { items: [item.id] });
    var built = buildDepositInvoicePayload({
      proposal:     synth,
      allItems:     allItems,
      allInvoices:  allInvoices,
      projects:     projects,
      clients:      clients,
      firmSettings: firmSettings,
    });

    // Override the singular ref with the per-item letter-suffixed ref.
    var itemRef = baseNumeric + '-' + letter + '-DEP';
    built.invoiceRef = itemRef;
    built.payload.invoiceRef = itemRef;

    var existing = existingPerItem[item.id] || null;
    var newId;
    if (existing && existing.id) {
      newId = existing.id;
    } else if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      newId = crypto.randomUUID();
    } else {
      newId = Date.now() + '-' + Math.random();
    }

    var record = {
      id:           newId,
      invoiceRef:   existing ? existing.invoiceRef : itemRef,
      invoiceType:  'deposit',
      status:       existing ? existing.status     : 'draft',
      proposalId:   proposal.id,
      itemId:       item.id,
      project:      proposal.project || '',
      clientId:     proposal.clientId || (client ? client.id : ''),
      clientName:   client ? client.name : '',
      itemIds:      [item.id],
      lineItems:    built.payload.lineItems,
      productTotal: built.productTotalRounded,
      feesTotal:    built.feesTotalRounded,
      feeFreightTot:      built.payload.feeFreightTot      || 0,
      feeCratingTot:      built.payload.feeCratingTot      || 0,
      feeInstallationTot: built.payload.feeInstallationTot || 0,
      feeTariffTot:       built.payload.feeTariffTot       || 0,
      feeSalesTaxTot:     built.payload.feeSalesTaxTot     || 0,
      feeCustom:          built.payload.feeCustom          || [],
      subtotal:     built.subtotal,
      amountDue:    built.depositTotal,
      qbInvoiceId:  existing ? existing.qbInvoiceId  : '',
      qbEstimateId: existing ? existing.qbEstimateId : '',
      qbPushedAt:   existing ? existing.qbPushedAt   : '',
      qbError:      existing ? existing.qbError      : '',
      notes:        proposal.notes || '',
      pdfStoragePath: existing ? existing.pdfStoragePath : '',
      sentAt:         existing ? existing.sentAt         : '',
      createdBy:      existing ? existing.createdBy      : '',
    };

    return { built: built, record: record, item: item, letter: letter };
  });
}

// Build the payload for /api/generate-proposal. Mirrors
// buildDepositInvoicePayload above so the Python generator can be a pure
// renderer: it reads pre-computed totals + per-item pricing instead of
// duplicating the JS math.
//
// opts: { proposal, items, client, project, allItems }
//   proposal — raw proposal row
//   items    — the curated subset of allItems on this proposal (component
//              materialization already done by the caller). Used for the
//              line-by-line page rendering.
//   client   — resolved client object (may be null)
//   project  — resolved project object (may be null)
//   allItems — full catalog (for assemblyClient lookups on assembly parents)
//
// Each item in the returned `items` array carries the original descriptive
// fields PLUS three computed pricing fields: unitPrice (assembly-aware
// per-unit client price), total (assembly-aware line total), and
// depositAmt (matches itemDepositAmount, fees-included for vdp>=100).
//
// Totals at the top level: productTotal, feeFreightTot, feeCratingTot,
// feeInstallationTot, feeTariffTot, feeSalesTaxTot, feeCustom[], grandTotal,
// depositTotal. All marked-up where applicable, rounded to cents.
//
// Unlike buildDepositInvoicePayload, this function does NOT throw on
// non-Client-Approved items — proposal PDFs are previewed throughout the
// approval flow (Draft / Ready / Sent / Approved). Cancelled items are
// filtered out so a post-approval cancel doesn't render the killed line.
export function buildProposalPayloadPure(opts) {
  var proposal = opts.proposal || {};
  var items    = opts.items    || [];
  var client   = opts.client   || null;
  var project  = opts.project  || null;
  var allItems = opts.allItems || items;

  var renderItems = items.filter(function(it) { return it && !isCancelled(it); });

  function r(x) { return Math.round(x * 100) / 100; }

  var enriched = renderItems.map(function(it) {
    // 2026-06-10 — apply items.ccFeePct silent line multiplier per
    // Decision 7. unit, total, depositAmt all scale by (1 +
    // ccFeePct/100). When ccFeePct=0 (default), mul=1.0 and behavior
    // unchanged. The displayed PDF lines + the proposal grand total
    // (which aggregateProposalFees / proposalGrandTotal also now
    // multiply) stay in sync.
    var mul   = ccFeeMul(it);
    var qty   = parseQty(it.qty) || 1;
    // Non-billable items (OF/BO/ABD) ride proposals as sign-off lines:
    // their stored reference price never prints to the client.
    var billable = shouldBillItem(it);
    var total = billable ? assemblyClient(it, allItems) * mul : 0;
    var unit  = qty > 0 ? total / qty : total;
    // Pass project so cdp=100 items pick up the fresh project-derived
    // tax in their deposit basis (Fix C Phase 1). When project lacks
    // tax config, itemDepositAmount falls back to stored fees.salesTax.
    var dep   = itemDepositAmount(it, allItems, project) * mul;
    return Object.assign({}, it, {
      unitPrice:  r(unit),
      total:      r(total),
      depositAmt: r(dep),
    });
  });

  var productTotal = r(enriched.reduce(function(s, it) { return s + it.total; }, 0));
  var depositTotal = r(enriched.reduce(function(s, it) { return s + it.depositAmt; }, 0));

  var agg = aggregateProposalFees(renderItems);
  // Fix C Phase 1: when the project carries the new tax config, override
  // aggregateProposalFees's stored-fees-salesTax sum with the fresh
  // computeItemTax aggregate. aggregateProposalFees stays signature-
  // compatible (no project param) — easier than threading project
  // through every existing consumer.
  if (hasProjectTaxConfig(project)) {
    agg.salesTax = proposalTax(renderItems.filter(shouldBillItem), project, allItems);
    agg.hasFees = (agg.freight + agg.crating + agg.installation
      + agg.salesTax + agg.tariff) > 0
      || Object.keys(agg.custom).length > 0;
  }
  // Per-line "Sales Tax" / "Tariff" lines that the Python rendered as a
  // single labeled line each. aggregateProposalFees already returns these
  // as flat scalars; expose them in the payload as feeSalesTaxTot etc.
  // so Python can render them with no math.
  var feeCustomList = Object.keys(agg.custom).map(function(lbl) {
    return { label: lbl, amount: agg.custom[lbl] };
  });
  var customSubtotal = feeCustomList.reduce(function(s, c) { return s + c.amount; }, 0);

  // Shared with the billing-summary "Total" column via proposalGrandTotal.
  // Equivalent to: productTotal + freight + crating + installation +
  // salesTax + tariff + customSubtotal — the long form is preserved here
  // as an internal sanity check that nothing else got summed in.
  var grandTotal = proposalGrandTotal(renderItems, allItems, project);
  // Defensive: assert the long-form sum matches the helper. Drift here
  // would mean the helper and this builder disagree on what a grand
  // total means — a class of bug we've already hit twice on this file.
  var grandTotalLongForm = r(productTotal + agg.freight + agg.crating + agg.installation
    + agg.salesTax + agg.tariff + customSubtotal);
  if (Math.abs(grandTotal - grandTotalLongForm) >= 0.01) {
    console.warn('buildProposalPayloadPure: grandTotal drift', { helper: grandTotal, longForm: grandTotalLongForm });
  }

  return {
    proposal:           proposal,
    client:             client,
    project:            project,
    items:              enriched,
    productTotal:       productTotal,
    feeFreightTot:      agg.freight,
    feeCratingTot:      agg.crating,
    feeInstallationTot: agg.installation,
    feeTariffTot:       agg.tariff,
    feeSalesTaxTot:     agg.salesTax,
    feeCustom:          feeCustomList,
    grandTotal:         grandTotal,
    depositTotal:       depositTotal,
    hasFees:            agg.hasFees,
  };
}

// ── Customs invoice (2026-07-19) ───────────────────────────────────────
// A one-page "INVOICE — PAID" print for customs / export paperwork
// (the Moskito BVI shipments): the proposal's items at client prices +
// the marked-up fee lines, closing on TOTAL / PAID / BALANCE DUE $0.00.
//
// PRESENTATION ONLY — this document is NOT an invoice record. No
// `invoices` row is created, no ref is minted in any sequence, no
// payment records are read or written, nothing reaches QB or the
// Reconciliation view. The PAID line is the operator's deliberate
// customs-presentation choice (clicking the button IS the decision),
// never a derivation from allocations. It wraps buildProposalPayloadPure
// so every number is byte-identical to the proposal the client approved.
export function buildCustomsInvoicePayload(opts) {
  var payload = buildProposalPayloadPure(opts);
  return Object.assign({}, payload, {
    customsInvoice: true,
    docTitle:       'INVOICE — PAID',
    paidTotal:      payload.grandTotal,
    balanceDue:     0,
  });
}

// ── PHASE 3: CC fee aggregation — two paths, shadow comparison ─────────
//
// computeCcFeeTotalLegacy: ref-string parsing against vendor_payments.
// Pre-Phase 3 default path. Two CC fee shapes coexist in the data
// (2026-05-12 split-row migration):
//   Legacy:  cc_fee column on the principal vendor_payments row
//   New:     separate 'Fee'-type vendor_payments row whose reference
//            contains 'CC processing fee' (principal cc_fee = 0)
// Both shapes sum into ccFeeTotal. Disambiguates from vendor_fee receipts
// (also payment_type='Fee') via the reference-string test.
//
// computeCcFeeTotalFromItems: reads vendor_payment_items.cc_fee_share.
// Phase 3 target path. Populated by:
//   - Phase 1 backfill (allocates parent vp.cc_fee across PO items by
//     net weight) for legacy-shape historical rows
//   - Phase 2A cascade (Option P: principal children carry cc_fee_share
//     derived from the resolved CC fee amount) for new-shape rows
// Equivalent to the legacy aggregator for all known data shapes; the
// shadow comparison in buildFinalInvoicePayload catches drift.
//
// Both helpers return a single rounded $ total. Same call-site shape.
export function computeCcFeeTotalLegacy(opts) {
  var items          = (opts && opts.items)          || [];
  var vendorPayments = (opts && opts.vendorPayments) || [];
  var purchaseOrders = (opts && opts.purchaseOrders) || [];
  // Consumption (2026-09-07). A fee stamped with another invoice's id has
  // already been charged to the client and must never be charged again —
  // the split-shipment double-bill. `currentInvoiceId` lets a re-save of
  // THIS invoice keep the fees it already consumed.
  var currentInvoiceId = (opts && opts.currentInvoiceId) || '';

  var invoiceItemIds = items.map(function(it) { return it && it.id; }).filter(Boolean);
  var poRefsForInvoice = purchaseOrders
    .filter(function(po) {
      return po && Array.isArray(po.items)
        && po.items.some(function(iid) { return invoiceItemIds.indexOf(iid) >= 0; });
    })
    .map(function(po) { return po.po_ref || ''; })
    .filter(Boolean);

  var ccFeeTotal = vendorPayments.reduce(function(sum, vp) {
    if (!vp) return sum;
    var ref = String(vp.reference || '').split('\u00b7')[0].trim();
    if (poRefsForInvoice.indexOf(ref) < 0) return sum;
    if (ccFeeIsConsumed(vp, currentInvoiceId)) return sum;
    var legacyFee = parseFloat(vp.cc_fee != null ? vp.cc_fee : vp.ccFee) || 0;
    var pt = vp.payment_type || vp.paymentType || '';
    var isCcRow = pt === 'Fee' && /cc processing fee/i.test(String(vp.reference || ''));
    var splitFee = isCcRow ? (parseFloat(vp.amount) || 0) : 0;
    return sum + legacyFee + splitFee;
  }, 0);
  return Math.round(ccFeeTotal * 100) / 100;
}

// ── ccFeeIsConsumed — has this fee already been charged to a client? ───
//
// `vendor_payments.billed_invoice_id` (migration 20260907) is the fee's
// receipt of recovery, mirroring `change_orders.billed_invoice_id`. NULL
// / '' means the fee is still owed to the firm and available to the next
// final invoice. Charging each fee at its own transaction means charging
// it ONCE: a PO accumulates a fee per card payment (vendor deposit, then
// vendor final), and matching by PO ref used to sweep the whole history
// onto every FIN cut against that PO.
//
// `currentInvoiceId` is the re-save case: an invoice re-rendered or
// re-committed must keep the fees IT consumed, or the second save would
// drop them from a document already sent.
export function ccFeeIsConsumed(vp, currentInvoiceId) {
  if (!vp) return false;
  var billed = vp.billed_invoice_id != null ? vp.billed_invoice_id : vp.billedInvoiceId;
  billed = billed == null ? '' : String(billed);
  if (billed === '') return false;
  return !(currentInvoiceId && billed === String(currentInvoiceId));
}

// ── ccFeeSources — the card fees behind a FIN, each with its date ──────
//
// computeCcFeeTotalLegacy answers "how much"; this answers "which fees,
// from when, off whose paper". Same matching rules as the legacy
// aggregator (both fee shapes, filtered to the POs carrying the
// invoice's items), so SUM(amount) === computeCcFeeTotalLegacy for the
// same inputs — pinned in ccFeeTaxAndStorage.test.js.
//
// DATE PRECEDENCE, and why. The date Cimon wants is when the fee was
// captured, which is a fact about the receipt, not the payment row:
//   1. receipt.paidDate   — when the card was actually charged
//   2. receipt.receiptDate — the paper's own date. Note this can be the
//      ORDER date rather than the payment date (see
//      buildVendorPaymentFromReceipt), which is exactly why paidDate
//      leads.
//   3. vendor_payment.paymentDate — for payments entered by hand, with
//      no source receipt behind them
//   4. created_at — last resort
// `dateFrom` reports which rule fired so the UI can say "receipt" vs
// "payment entry" instead of implying paperwork that does not exist.
//
// Returns [] rather than throwing on missing inputs: this feeds a
// display panel that renders while the operator is still picking items.
export function ccFeeSources(opts) {
  var items          = (opts && opts.items)          || [];
  var vendorPayments = (opts && opts.vendorPayments) || [];
  var purchaseOrders = (opts && opts.purchaseOrders) || [];
  var receipts       = (opts && opts.receipts)       || [];

  var invoiceItemIds = items.map(function(it) { return it && it.id; }).filter(Boolean);
  if (invoiceItemIds.length === 0) return [];

  var poRefsForInvoice = purchaseOrders
    .filter(function(po) {
      return po && Array.isArray(po.items)
        && po.items.some(function(iid) { return invoiceItemIds.indexOf(iid) >= 0; });
    })
    .map(function(po) { return po.po_ref || ''; })
    .filter(Boolean);

  var receiptById = {};
  receipts.forEach(function(r) { if (r && r.id != null) receiptById[String(r.id)] = r; });

  var out = [];
  vendorPayments.forEach(function(vp) {
    if (!vp) return;
    var ref = String(vp.reference || '').split('\u00b7')[0].trim();
    if (poRefsForInvoice.indexOf(ref) < 0) return;

    var legacyFee = parseFloat(vp.cc_fee != null ? vp.cc_fee : vp.ccFee) || 0;
    var pt = vp.payment_type || vp.paymentType || '';
    var isCcRow = pt === 'Fee' && /cc processing fee/i.test(String(vp.reference || ''));
    var amount = legacyFee + (isCcRow ? (parseFloat(vp.amount) || 0) : 0);
    if (Math.abs(amount) < 0.005) return;

    var billedInvoiceId = vp.billed_invoice_id != null ? vp.billed_invoice_id : vp.billedInvoiceId;
    var rid = vp.source_receipt_id != null ? vp.source_receipt_id : vp.sourceReceiptId;
    var rcpt = rid != null && rid !== '' ? receiptById[String(rid)] : null;
    var date = '', dateFrom = '';
    if (rcpt && rcpt.paidDate)          { date = rcpt.paidDate;    dateFrom = 'receipt'; }
    else if (rcpt && rcpt.receiptDate)  { date = rcpt.receiptDate; dateFrom = 'receipt'; }
    else if (vp.payment_date || vp.paymentDate) {
      date = vp.payment_date || vp.paymentDate; dateFrom = 'payment';
    } else if (vp.created_at || vp.createdAt) {
      date = String(vp.created_at || vp.createdAt).slice(0, 10); dateFrom = 'created';
    }

    out.push({
      amount:     Math.round(amount * 100) / 100,
      date:       date ? String(date).slice(0, 10) : '',
      dateFrom:   dateFrom,
      vendorName: vp.vendor_name || vp.vendorName || '',
      reference:  String(vp.reference || ''),
      poRef:      ref,
      receiptId:  rcpt ? rcpt.id : null,
      paymentId:  vp.id != null ? vp.id : null,
      // '' when still owed to the firm. Set = some invoice already
      // recovered it, and this FIN must not recover it again. The panel
      // shows these greyed rather than hiding them, so the operator can
      // see the fee exists and where it went.
      billedInvoiceId: billedInvoiceId == null ? '' : String(billedInvoiceId),
    });
  });

  out.sort(function(a, b) {
    if (a.date && b.date && a.date !== b.date) return a.date < b.date ? -1 : 1;
    if (a.date && !b.date) return -1;
    if (!a.date && b.date) return 1;
    return 0;
  });
  return out;
}

export function computeCcFeeTotalFromItems(opts) {
  var items              = (opts && opts.items)              || [];
  var vendorPaymentItems = (opts && opts.vendorPaymentItems) || [];
  var vendorPayments     = (opts && opts.vendorPayments)     || [];
  var currentInvoiceId   = (opts && opts.currentInvoiceId)   || '';

  var invoiceItemIds = items.map(function(it) { return it && it.id; }).filter(Boolean);
  if (invoiceItemIds.length === 0) return 0;

  // Consumption is a property of the PAYMENT, not of the per-item share,
  // so a consumed parent takes all of its shares out of play.
  //
  // AND of the RECEIPT behind it. In the split-row shape (2026-05-12) the
  // fee's AMOUNT lives on a separate 'Fee' payment while its per-item
  // SHARES hang off the PRINCIPAL payment (Option P). Billing stamps the
  // row that carries the amount — the Fee row — so checking only the
  // share's own parent would see the principal unstamped and offer an
  // already-recovered fee to the next invoice. The two rows are one
  // transaction, joined by source_receipt_id, so consuming either
  // consumes the pair. Found on 26MOS-PO-007 (De Sousa Hughes), whose
  // Fee row is billed on 26MOS-INV-008-B-FIN while the shares sit on an
  // unstamped principal.
  var consumedPaymentIds = {};
  var consumedReceiptIds = {};
  vendorPayments.forEach(function(vp) {
    if (!vp || !ccFeeIsConsumed(vp, currentInvoiceId)) return;
    if (vp.id != null) consumedPaymentIds[String(vp.id)] = true;
    var rid = vp.source_receipt_id != null ? vp.source_receipt_id : vp.sourceReceiptId;
    if (rid != null && rid !== '') consumedReceiptIds[String(rid)] = true;
  });
  var paymentById = {};
  vendorPayments.forEach(function(vp) {
    if (vp && vp.id != null) paymentById[String(vp.id)] = vp;
  });
  function shareIsConsumed(pid) {
    if (pid == null) return false;
    if (consumedPaymentIds[String(pid)]) return true;
    var parent = paymentById[String(pid)];
    if (!parent) return false;
    var rid = parent.source_receipt_id != null ? parent.source_receipt_id : parent.sourceReceiptId;
    return !!(rid != null && rid !== '' && consumedReceiptIds[String(rid)]);
  }

  var ccFeeTotal = vendorPaymentItems.reduce(function(sum, vpi) {
    if (!vpi) return sum;
    var iid = vpi.item_id != null ? vpi.item_id : vpi.itemId;
    if (invoiceItemIds.indexOf(iid) < 0) return sum;
    var pid = vpi.payment_id != null ? vpi.payment_id : vpi.paymentId;
    if (shareIsConsumed(pid)) return sum;
    return sum + (parseFloat(vpi.cc_fee_share != null ? vpi.cc_fee_share : vpi.ccFeeShare) || 0);
  }, 0);
  return Math.round(ccFeeTotal * 100) / 100;
}

// Build the payload for /api/generate-deposit-invoice with invoiceType='final'.
// `items` is the subset selected for this delivery batch; `allItems` is the
// full app state (for assembly pricing). Mirrors buildDepositInvoicePayload
// above but for the 20%-balance-plus-fees case.
export function buildFinalInvoicePayload(opts) {
  // Model B per-item (2026-06-03): include items whose lifecycle-owed
  // exceeds paid-share. The pre-Model-B filter used the
  // `clientTotal - expectedDeposit + unpaid` derivation which silently
  // dropped items affected by mid-flight basis changes (vdp via CO,
  // FX delta, etc.). Model B reads cash directly via itemPaidShare —
  // no basis drift possible.
  var rawItems = opts.items || [];
  var allItems = opts.allItems || [];
  var allInvoices = opts.allInvoices || [];
  var paymentAllocations = opts.paymentAllocations || [];
  var projects = opts.projects || [];
  var firmSettings = opts.firmSettings || {};
  var clients = opts.clients || [];
  var proposals = opts.proposals || [];
  var vendorPayments = opts.vendorPayments || [];
  var purchaseOrders = opts.purchaseOrders || [];
  var projectLabel = opts.projectLabel || (rawItems[0] && (rawItems[0].project || rawItems[0].projectLabel)) || '';

  var projObj = projects.find(function(pr) { return pr.label === projectLabel; });
  var projectCode = (projObj && projObj.projectCode) || projectLabelToCode(projectLabel);
  var projShort = (projectLabel || '').replace(/^\d+-/, '');

  // ── Adjustments (2026-06-06 patch) ──────────────────────────────────
  // FIN-scoped pass-through adjustments — late tariff, freight overage,
  // crating overage, etc. Per-row shape (matches BILLING_REDESIGN_SPEC.md
  // Section 3): { description, amount, taxable, source_ref }. Taxable
  // rows contribute to the taxable base (so sales tax recomputes against
  // the new base). Non-taxable rows land between Tax and Total on the
  // PDF. source_ref is null in this patch; Phase 4 FIN Builder populates
  // from receipt UUIDs.
  //
  // Math identity (Fix E, extended): subtotal + adjustmentsTotal +
  // adjustmentsTax = depositPaid + appliedCredit + finalDue. Both
  // subtotal and amountDue absorb the adjustments equally, so
  // depositPaid (= subtotal − amountDue) is unchanged — adjustments
  // don't reduce historical deposit credit.
  var adjustmentsIn = Array.isArray(opts.adjustments) ? opts.adjustments : [];

  // 2026-06-10 — extended Adjustment shape (Decision 2): adj entries may
  // carry applyMarkup, markupPct, reason. At payload-build time, the
  // marked-up amount is computed and used as the displayed/billable
  // value; the raw cost-basis amount is preserved as `amountCost` for
  // audit. markupPct is internal data — never rendered on the PDF.
  // Reason is carried through for future PDF rendering as a sub-line
  // (renderer enhancement pending).
  var adjustmentsTaxable    = 0;
  var adjustmentsNonTaxable = 0;
  var adjustmentsNormalized = adjustmentsIn.map(function(adj) {
    var amtCost = parseFloat(adj && adj.amount) || 0;
    var taxable = !!(adj && adj.taxable);
    var applyMarkup = !!(adj && adj.applyMarkup);
    var markupPct = parseFloat(adj && adj.markupPct) || 0;
    var displayAmt = applyMarkup
      ? Math.round(amtCost * (1 + markupPct / 100) * 100) / 100
      : Math.round(amtCost * 100) / 100;
    if (taxable) adjustmentsTaxable    += displayAmt;
    else         adjustmentsNonTaxable += displayAmt;
    var out = {
      description: String((adj && adj.description) || '').trim(),
      amount:      displayAmt,                                       // BILLABLE — what client pays
      amountCost:  Math.round(amtCost * 100) / 100,                  // cost basis (audit)
      applyMarkup: applyMarkup,
      markupPct:   markupPct,                                         // internal only, not on PDF
      reason:      (adj && adj.reason) ? String(adj.reason) : '',
      category:    (adj && adj.category) ? String(adj.category) : '',
      taxable:     taxable,
      source_ref:  (adj && adj.source_ref) ? String(adj.source_ref) : null,
    };
    // Preserve _sourcePoId tag through normalization so the FIN
    // saveAndCommit clearing flow knows which PO's queue to clear.
    // Audit bug A (2026-06-10) — was being stripped here, leaving
    // pendingFinAdjustments stale + risking double-bill on the next
    // FIN built for the same items.
    if (adj && adj._sourcePoId) out._sourcePoId = String(adj._sourcePoId);
    return out;
  });
  var adjustmentsTotal = Math.round((adjustmentsTaxable + adjustmentsNonTaxable) * 100) / 100;

  // Drop null entries from the selection; keep zero-balance items so the
  // 2026-06-06 Adjustments patch can render them on the PDF for context
  // (Cimon's "issue FIN for a 100%-upfront item to layer late-tariff
  // adjustments" workflow). Pre-2026-06-06 this filter also dropped
  // items where (owed − paid) ≤ $0.005; that worked for the FIN-as-
  // balance-collection model but breaks the FIN-as-adjustment-vehicle
  // model the spec moves to.
  var items = rawItems.filter(function(it) { return !!it; });

  // Try to find the client via the proposal that owns these items, falling back
  // to the project's clientId. ID comparisons are type-tolerant.
  var firstProposalId = items.find(function(it) { return it.proposalId; });
  firstProposalId = firstProposalId ? firstProposalId.proposalId : '';
  var proposal = firstProposalId ? proposals.find(function(p) { return String(p.id) === String(firstProposalId); }) : null;
  var clientId = (proposal && proposal.clientId) || (projObj && projObj.clientId) || '';
  var client = clientId
    ? (clients.find(function(c) { return String(c.id) === String(clientId); }) || null)
    : null;
  if (!client && typeof console !== 'undefined') {
    console.warn('[buildFinalInvoicePayload] client lookup failed', {
      projectLabel: projectLabel,
      projObjFound: !!projObj,
      projObjClientId: projObj && projObj.clientId,
      proposalId: firstProposalId,
      proposalFound: !!proposal,
      proposalClientId: proposal && proposal.clientId,
      resolvedClientId: clientId,
      clientsArrayLength: clients.length,
      firstClientIds: clients.slice(0, 3).map(function(c) { return c.id; }),
    });
  }

  function n(v) { var x = parseFloat(String(v || '').replace(/[$,]/g, '')); return isNaN(x) ? 0 : x; }

  var productTotal = 0, feeFreightTot = 0, feeCratingTot = 0, feeInstallationTot = 0, feeTariffTot = 0, feeSalesTaxTot = 0;
  var feeCustomMap = {};

  // CO discovery moved BEFORE items.map so per-item inline CO adjustments
  // (2026-05-27) can decorate each lineItem in a single pass. Each
  // supported CO is removed from the standalone-block pool and tracked
  // in coIdsConsumed so the caller can stamp billed_invoice_id on it.
  var changeOrdersIn = opts.changeOrders || [];
  var coIdsConsumed = [];

  // 2026-06-10 — items.ccFeePct silent line multiplier. When set, every
  // billable line for that item (product + each fee + per-item tax) is
  // multiplied by (1 + ccFeePct/100). No separate CC fee line on the
  // PDF. Suppression rule below kills the receipt-cc_fee auto-aggregation
  // when ANY item has ccFeePct > 0 (otherwise client bills twice — once
  // in the lines, once in silent absorption).
  var anyCcFeePctItem = items.some(function(it) { return ccFeeMul(it) > 1.0; });

  var lineItems = items.map(function(it) {
    var qty = parseQty(it.qty) || 1;
    var mul = ccFeeMul(it);
    var lineClient = assemblyClient(it, allItems) * mul;
    var unitClient = lineClient / qty;

    // ── Inline CO price adjustment (2026-05-27) ─────────────────────
    // Look up approved-accounting COs filed against THIS item, not yet
    // billed. The supported case is a SINGLE CO with exactly ONE
    // field_change matching this item, field === 'netPrice'. Anything
    // else (qty change, multiple COs, fees field, etc.) emits a
    // console.warn and is NOT applied here — Cimon manually verifies.
    //
    // Math (per the design spec):
    //   netUnitDelta    = field_change.after - field_change.before
    //   clientUnitDelta = netUnitDelta × (1 + markupPct/100)
    //   adjusted unitClient  = unitClient + clientUnitDelta
    //   adjusted lineClient  = adjusted unitClient × qty
    //
    // The item.netPrice in scope here may already be the post-CO value
    // (approveChangeOrder applies the field_changes via updateItem),
    // so `unitClient` already reflects the adjusted price. We surface
    // unitPriceBase + unitPriceDelta on the lineItem so the PDF can
    // display the breakdown, but we do NOT re-add the delta to
    // unitClient/lineClient — they already include it.
    var coAdjustment = null;
    if (firstProposalId) {
      var matchingCos = getChangeOrdersForItem(it.id, changeOrdersIn, {
        status: 'approved',
        accountingImpact: true,
        proposalId: firstProposalId,
        excludeBilled: true,
      });
      if (matchingCos.length === 1) {
        var co = matchingCos[0];
        var fcs = (co.fieldChanges || []).filter(function(fc) { return fc && fc.itemId === it.id; });
        if (fcs.length === 1 && fcs[0].field === 'netPrice') {
          var fc = fcs[0];
          var snap = fc.pricingSnapshot || {};
          var snapMarkup = snap.markupPct != null ? parseFloat(snap.markupPct) : parseFloat(it.markupPct || 0);
          var markup = isNaN(snapMarkup) ? 0 : snapMarkup;
          var netBefore = parseFloat(fc.before);
          var netAfter  = parseFloat(fc.after);
          if (!isNaN(netBefore) && !isNaN(netAfter)) {
            var netUnitDelta    = netAfter - netBefore;
            var clientUnitDelta = netUnitDelta * (1 + markup / 100);
            var clientUnitBase  = netBefore * (1 + markup / 100);
            coAdjustment = {
              coRef: co.coRef || '',
              coId:  co.id,
              field: 'netPrice',
              netBefore: netBefore,   // pre-CO net price (used for original-deposit basis)
              netAfter:  netAfter,
              unitPriceBase:  Math.round(clientUnitBase  * 100) / 100,
              unitPriceDelta: Math.round(clientUnitDelta * 100) / 100,
              unitPriceDeltaLabel: clientUnitDelta >= 0 ? 'price increase' : 'price decrease',
            };
            coIdsConsumed.push(co.id);
          } else {
            // eslint-disable-next-line no-console
            console.warn('[buildFinalInvoicePayload] CO ' + (co.coRef || co.id) +
              ' has non-numeric netPrice before/after; not applied inline. Manual review.');
          }
        } else {
          // eslint-disable-next-line no-console
          console.warn('[buildFinalInvoicePayload] CO ' + (co.coRef || co.id) +
            ' affects item ' + (it.itemNumber || it.id) +
            ' via field(s) ' + fcs.map(function(f){return f.field;}).join(',') +
            ' — only single field=netPrice is supported inline. Not applied; manual review.');
        }
      } else if (matchingCos.length > 1) {
        // eslint-disable-next-line no-console
        console.warn('[buildFinalInvoicePayload] Item ' + (it.itemNumber || it.id) +
          ' has ' + matchingCos.length + ' applicable COs (' +
          matchingCos.map(function(x){return x.coRef||x.id;}).join(',') +
          '); inline stacking is out-of-scope. Not applied; manual review.');
      }
    }

    productTotal += lineClient;

    // Model B per-item balance (2026-06-03): lifecycle owed minus
    // actual paid against this item's DEP invoice. Replaces the
    // pre-Model-B `lineClient - expectedDeposit + unpaidShare`
    // derivation which dropped the cash gap silently when the deposit
    // basis drifted between DEP-issuance and FIN-build (vdp via CO,
    // FX delta, manual edits). Under Model B the depositPct field is
    // NOT consulted here; the FIN bills whatever the client still
    // owes from cash math. The Kumiko FX-delta CO override at
    // utils.js (formerly around line 5682) is no longer needed —
    // itemPaidShare reads the actual DEP allocation and the lifecycle
    // owed reflects the current state, so the gap is captured
    // automatically for any field change.
    //
    // Inline CO `coAdjustment` is still surfaced on the lineOut object
    // below (for the PDF "$base + $delta price increase" label on the
    // unit-price row) — that's purely display, not math.
    // owed is the per-item lifecycle total (lineClient + marked-up fees
    // + per-item tax). Apply ccFeePct multiplier here so the client
    // balance reflects the CC-fee-baked-in value. paid stays at actual
    // cash (no multiplier — what the client actually wired).
    var owed = itemLifecycleOwed(it, allItems, projObj) * mul;
    var paid = itemPaidShare(it, allInvoices, paymentAllocations, allItems);
    var balanceRaw = owed - paid;
    if (balanceRaw < 0) balanceRaw = 0;  // defensive; over-payment guard
    var balance = Math.round(balanceRaw * 100) / 100;
    // Rounding-residue clamp (2026-07-05): per-item deposit amounts round
    // at 2dp independently of the lifecycle total, so a fully-collected
    // 100%-upfront item can carry a 1¢ phantom balance (the 26HOB-PO-026
    // Balcony Door drapery). A real client balance is never under 2¢ —
    // clamp it rather than bill a one-cent line. The cent lands in the
    // displayed deposit-credit side (depositPaid derives from subtotal −
    // amountDue), so the Fix E identity is unaffected.
    if (balance > 0 && balance < 0.02) balance = 0;

    // Per-fee-category aggregates for the FIN PDF totals block.
    // Display-only decomposition of the Subtotal — each item's FULL
    // marked-up fee contributes (no vdp<100 filter). The Subtotal
    // (= sum of per-fee + product) equals SUM(itemLifecycleOwed),
    // which equals SUM(balance) + depositPaid — the Fix E identity.
    //
    // Pre-Model-B these aggregates were filtered by `vdp < 100`,
    // showing "fees deferred from DEP." Under Model B they show
    // LIFECYCLE fees (what the item owes in total). The math identity
    // holds: amountDue is computed from sum-of-balances, not
    // sum-of-aggregates — so per-fee-category lines on the PDF are
    // pure decomposition, not double-counted.
    // Per-fee category aggregates — each multiplied by ccFeePct
    // (`mul`) so the FIN PDF's totals block matches the line values
    // displayed above (which were also multiplied). When ccFeePct=0,
    // mul=1.0 and behavior is unchanged.
    var fees = it.fees || {};
    if (n(fees.freight)      > 0) feeFreightTot       += applyFreightMarkup(n(fees.freight), it) * mul;
    if (n(fees.crating)      > 0) feeCratingTot       += applyFreightMarkup(n(fees.crating), it) * mul;
    if (n(fees.installation) > 0) feeInstallationTot  += applyFreightMarkup(n(fees.installation), it) * mul;
    if (n(fees.tariff)       > 0) feeTariffTot        += applyTaxMarkup(n(fees.tariff)) * mul;
    if (hasProjectTaxConfig(projObj)) {
      feeSalesTaxTot += computeItemTax(it, projObj, allItems) * mul;
    } else if (n(fees.salesTax) > 0) {
      feeSalesTaxTot += applyTaxMarkup(n(fees.salesTax)) * mul;
    }
    (fees.custom || []).forEach(function(c) {
      var amt = n(c.amount);
      if (amt > 0) {
        var lbl = c.label || 'Custom Fee';
        feeCustomMap[lbl] = (feeCustomMap[lbl] || 0) + customFeeClient(c, it) * mul;
      }
    });

    var sidemark = computeSidemark(it.project || projectLabel, it.roomName, it.roomNumber);
    // depositPct displayed on the PDF — % of clientTotal being billed
    // on this invoice. Derived (not hardcoded) so an item with a
    // 70%-deposit override shows "30%" instead of always "20%".
    var displayPct = lineClient > 0 ? Math.round((balance / lineClient) * 100) : 0;
    var lineOut = {
      // See buildDepositInvoicePayload — the snapshot's own item pointer.
      itemId: String(it.id || ''),
      item: it.item || 'Untitled',
      productName: it.productName || '',
      description: it.specNotes || '',
      finish: it.finish || '',
      dimensions: it.dimensions || '',
      qty: String(qty),
      qtyType: it.qtyType || 'Each',
      unitPrice: Math.round(unitClient * 100) / 100,
      total: Math.round(lineClient * 100) / 100,
      depositAmt: balance,                             // re-using slot — the PDF labels this
                                                       // "Due now" when invoiceType=final
      depositPct: displayPct,
      leadTime: it.leadTime || '',
      sidemark: sidemark,
      images: it.images || [],
      heroIndex: it.heroIndex || 0,
    };
    // Inline CO adjustment fields — surfaced to the PDF renderer so it
    // can display the "$base + $delta price increase \n = $adjusted"
    // breakdown on the UNIT PRICE row. Client never sees the CO ref or
    // "Change Order" terminology — coRef stays in the payload for the
    // bookkeeper-side audit only.
    if (coAdjustment) {
      lineOut.unitPriceBase       = coAdjustment.unitPriceBase;
      lineOut.unitPriceDelta      = coAdjustment.unitPriceDelta;
      lineOut.unitPriceDeltaLabel = coAdjustment.unitPriceDeltaLabel;
    }
    return lineOut;
  });

  // ── Phase 5C: fee_share_* read path — shadow mode (2026-06-24) ──────
  // Mirrors the Phase 3 CC-fee shadow pattern. Gated by
  // opts.usePerItemFeeShares (resolved from
  // VITE_FEATURE_FEE_SHARE_PRIMARY_READS at the call site).
  //
  //   flag off (default):  legacy items.fees aggregation renders (above).
  //                        If vendorPaymentItems is provided, the new
  //                        path is computed in shadow and any divergence
  //                        > $0.01 per category logs a structured
  //                        [phase-5c-fee-share-divergence] console.warn.
  //   flag on (cutover):   new fee_share_<cat> path overrides legacy
  //                        for the Phase-5C-shipped categories (freight,
  //                        tariff). Crating / installation / custom
  //                        still use the legacy items.fees path until
  //                        their respective phases ship.
  //
  // Per-item iteration mirrors the legacy loop's per-item ccFeePct (`mul`)
  // and markup application: fee_share_<cat> × markup × mul, summed
  // across items. Items with no matching vpi rows contribute 0 to the
  // new path (caller decides whether to fall back to items.fees via the
  // shadow divergence + the eventual flag flip).
  var hasPerItemFeeShareData = opts && Array.isArray(opts.vendorPaymentItems);
  var usePerItemFeeShares    = !!(opts && opts.usePerItemFeeShares);
  var newFeeFreightTot = 0;
  var newFeeTariffTot  = 0;
  if (hasPerItemFeeShareData) {
    items.forEach(function(it) {
      var mul = ccFeeMul(it);
      var freightShare = 0;
      var tariffShare = 0;
      (opts.vendorPaymentItems || []).forEach(function(vpi) {
        if (!vpi) return;
        var iid = vpi.item_id != null ? vpi.item_id : vpi.itemId;
        if (String(iid) !== String(it.id)) return;
        freightShare += parseFloat(vpi.fee_share_freight != null ? vpi.fee_share_freight : vpi.feeShareFreight) || 0;
        tariffShare  += parseFloat(vpi.fee_share_tariff  != null ? vpi.fee_share_tariff  : vpi.feeShareTariff)  || 0;
      });
      if (freightShare > 0) newFeeFreightTot += applyFreightMarkup(freightShare, it) * mul;
      if (tariffShare  > 0) newFeeTariffTot  += applyTaxMarkup(tariffShare)      * mul;
    });
  }

  // Shadow comparison — log divergence only when both paths actually ran.
  if (hasPerItemFeeShareData && (
        Math.abs(feeFreightTot - newFeeFreightTot) >= 0.01
     || Math.abs(feeTariffTot  - newFeeTariffTot)  >= 0.01)) {
    var divergenceRecord5c = {
      projectLabel:        projectLabel,
      invoiceItemIds:      items.map(function(it) { return it && it.id; }).filter(Boolean),
      legacyFreight:       Math.round(feeFreightTot     * 100) / 100,
      newFreight:          Math.round(newFeeFreightTot  * 100) / 100,
      freightDiff:         Math.round((newFeeFreightTot - feeFreightTot) * 100) / 100,
      legacyTariff:        Math.round(feeTariffTot      * 100) / 100,
      newTariff:           Math.round(newFeeTariffTot   * 100) / 100,
      tariffDiff:          Math.round((newFeeTariffTot - feeTariffTot) * 100) / 100,
      mode:                usePerItemFeeShares ? 'flag_on' : 'shadow',
      vendorPaymentItemsTotal: (opts.vendorPaymentItems || []).length,
    };
    if (typeof console !== 'undefined') {
      console.warn('[phase-5c-fee-share-divergence]', divergenceRecord5c);
    }
    if (opts && typeof opts.onFeeShareDivergence === 'function') {
      try { opts.onFeeShareDivergence(divergenceRecord5c); } catch (_e) {}
    }
  }

  // Cutover: when the flag is on AND we have the per-item data,
  // override the legacy totals for the two Phase-5C-shipped categories.
  // Crating / installation / custom continue to read from items.fees
  // until their respective phases (5C.2 / 5C.3 / 5C.4) flip.
  if (usePerItemFeeShares && hasPerItemFeeShareData) {
    feeFreightTot = newFeeFreightTot;
    feeTariffTot  = newFeeTariffTot;
  } else if (usePerItemFeeShares && !hasPerItemFeeShareData) {
    if (typeof console !== 'undefined') {
      console.warn('[phase-5c-fee-share] usePerItemFeeShares=true but vendorPaymentItems not provided — falling back to legacy. Caller bug.');
    }
  }

  var feeCustomList = Object.keys(feeCustomMap).map(function(lbl) {
    return { label: lbl, amount: Math.round(feeCustomMap[lbl] * 100) / 100 };
  });

  // ── CO ROLL-IN — INLINE ON ITEMS (since 2026-05-27) ─────────────
  // Per the redesign, approved-accounting COs that modify a single
  // item's netPrice fold INLINE into that item's lineItem (UNIT PRICE
  // breakdown). The standalone "Change Order 26HOB-CO-001 — Reason..."
  // line at the bottom of the invoice is gone — clients now see the
  // adjustment as natural pricing breakdown on the affected line.
  //
  // The per-item loop above handles the discovery + consumption.
  // coIdsConsumed (now populated inside the loop) is returned so the
  // caller stamps billed_invoice_id on each consumed CO at save time,
  // preventing double-billing on regenerate.
  //
  // Negative-delta COs and out-of-scope cases (qty changes, multiple
  // COs per item, fees field) write console.warn from the per-item
  // loop and don't get consumed. They remain available for manual
  // resolution via the credit ledger or future iterations.

  // ── Adjustments sales tax (2026-06-06 patch) ────────────────────────
  // Taxable adjustments contribute to the taxable base, so when the
  // project has tax config, recompute the adjustment-bucket's tax and
  // fold it into feeSalesTaxTot BEFORE feesTotalRounded so the per-fee
  // breakdown stays accurate. When the project has no tax config (legacy
  // path that uses items.fees.salesTax fallback), the adjustment tax is
  // 0 — there's no FIN-level salesTax field to add to, and Cimon should
  // enter any taxable adjustment as a single grossed-up non-taxable row.
  var adjustmentsTax = 0;
  if (adjustmentsTaxable > 0 && hasProjectTaxConfig(projObj)) {
    var projTaxRate = parseFloat(projObj && projObj.taxRate) || 0;
    adjustmentsTax = Math.round(adjustmentsTaxable * projTaxRate * 100) / 100;
    feeSalesTaxTot += adjustmentsTax;
  }

  var feesTotalRounded = Math.round((feeFreightTot + feeCratingTot + feeInstallationTot + feeTariffTot + feeSalesTaxTot
    + feeCustomList.reduce(function(s, c) { return s + c.amount; }, 0)) * 100) / 100;
  var productTotalRounded = Math.round(productTotal * 100) / 100;
  // Model B per-item balance total (2026-06-03): SUM of per-item
  // (lifecycle owed − paid). Mirrors the per-line balance computed
  // inside the items.map above. The pre-Model-B CO-aware
  // expectedDeposit override is gone — Model B captures CO-driven
  // cash gaps automatically via the lifecycle-vs-paid math.
  var balanceTotal = Math.round(items.reduce(function(s, it) {
    // ccFeePct multiplier applies to owed (lifecycle bill side)
    // but not to paid (actual cash side). 2026-06-10 Decision 7.
    var mul = ccFeeMul(it);
    var owed = itemLifecycleOwed(it, allItems, projObj) * mul;
    var paid = itemPaidShare(it, allInvoices, paymentAllocations, allItems);
    var bal  = owed - paid;
    if (bal < 0) bal = 0;
    // Round + clamp PER ITEM, exactly like the per-line balance in the
    // items.map above (2026-07-05): the sub-2¢ rounding-residue clamp
    // must hit both computations or amountDue could disagree with the
    // sum of the rendered lines by a phantom cent.
    bal = Math.round(bal * 100) / 100;
    if (bal > 0 && bal < 0.02) bal = 0;
    return s + bal;
  }, 0) * 100) / 100;

  // ── Phase 3: CC fee aggregation — shadow mode (2026-06-02) ───────────
  // Two paths, gated by opts.usePerItemCcFee (resolved from
  // VITE_FEATURE_VENDOR_PAYMENT_ITEMS_CC_FEE_READS at the call site):
  //
  //   flag off (default):  legacy renders. If vendorPaymentItems is
  //                        provided, the new path is computed in shadow
  //                        and any divergence > $0.01 logs a structured
  //                        [phase-3-cc-fee-divergence] console.warn (does
  //                        NOT fail the build — log-and-continue).
  //   flag on (cutover):   new per-item path renders. Legacy not run.
  //
  // Both helpers live above as pure exports (computeCcFeeTotalLegacy +
  // computeCcFeeTotalFromItems). Backward compatibility: when
  // vendorPaymentItems is undefined (existing call sites / tests that
  // pre-date Phase 3), shadow comparison silently skips — only the
  // legacy path runs.
  var usePerItemCcFee = !!(opts && opts.usePerItemCcFee);
  var hasPerItemData  = opts && Array.isArray(opts.vendorPaymentItems);

  // currentInvoiceId (2026-09-07): empty on a fresh generation, so only
  // UNCONSUMED fees are picked up. Passed through on a re-save so the
  // invoice keeps the fees it already billed.
  var ccCurrentInvoiceId = (opts && opts.currentInvoiceId) || '';
  var legacyCcFee = computeCcFeeTotalLegacy({
    items:            items,
    vendorPayments:   vendorPayments,
    purchaseOrders:   purchaseOrders,
    currentInvoiceId: ccCurrentInvoiceId,
  });
  var newCcFee = hasPerItemData
    ? computeCcFeeTotalFromItems({
        items:              items,
        vendorPaymentItems: opts.vendorPaymentItems,
        vendorPayments:     vendorPayments,
        currentInvoiceId:   ccCurrentInvoiceId,
      })
    : null;

  // Shadow comparison — log divergence only when both paths actually ran.
  // Threshold $0.01 mirrors the per-line balance tolerance elsewhere.
  if (hasPerItemData && Math.abs(legacyCcFee - newCcFee) >= 0.01) {
    // Build the divergence record with staleness-diagnostic fields. The
    // counts let a reader distinguish (a) stale local state (empty vpi
    // array → vendorPaymentItemsTotal=0) from (b) cascade-write-in-
    // another-tab (vendorPayments rows match the PO but no children
    // for the FIN's items) from (c) real allocation drift (both counts
    // present, dollar values disagree).
    var invoiceItemIdsArr = items.map(function(it) { return it && it.id; }).filter(Boolean);
    var poRefsForInvoice = (purchaseOrders || [])
      .filter(function(po) {
        return po && Array.isArray(po.items)
          && po.items.some(function(iid) { return invoiceItemIdsArr.indexOf(iid) >= 0; });
      })
      .map(function(po) { return po.po_ref || ''; })
      .filter(Boolean);
    var vpForInvoiceCount = (vendorPayments || []).reduce(function(c, vp) {
      if (!vp) return c;
      var ref = String(vp.reference || '').split('\u00b7')[0].trim();
      return poRefsForInvoice.indexOf(ref) >= 0 ? c + 1 : c;
    }, 0);
    var vpiForInvoiceCount = (opts.vendorPaymentItems || []).reduce(function(c, vpi) {
      if (!vpi) return c;
      var iid = vpi.item_id != null ? vpi.item_id : vpi.itemId;
      return invoiceItemIdsArr.indexOf(iid) >= 0 ? c + 1 : c;
    }, 0);

    var divergenceRecord = {
      projectLabel:                projectLabel,
      invoiceItemIds:              invoiceItemIdsArr,
      legacyCcFee:                 legacyCcFee,
      newCcFee:                    newCcFee,
      diff:                        Math.round((newCcFee - legacyCcFee) * 100) / 100,
      mode:                        usePerItemCcFee ? 'flag_on' : 'shadow',
      vendorPaymentsForInvoice:    vpForInvoiceCount,
      vendorPaymentItemsForInvoice: vpiForInvoiceCount,
      vendorPaymentItemsTotal:     (opts.vendorPaymentItems || []).length,
    };

    if (typeof console !== 'undefined') {
      console.warn('[phase-3-cc-fee-divergence]', divergenceRecord);
    }
    // Optional caller hook — InvoicesTab routes this to Sentry so the
    // 7-day-clean monitoring criterion has a centralized log. utils.js
    // stays pure (no Sentry import); the caller decides what to do.
    if (opts && typeof opts.onDivergence === 'function') {
      try { opts.onDivergence(divergenceRecord); } catch (_e) {}
    }
  }

  // Choose render path. Defensive: if flag on but no per-item data
  // supplied, fall back to legacy and warn — better than rendering $0.
  var ccFeeTotal;
  if (usePerItemCcFee) {
    if (hasPerItemData) {
      ccFeeTotal = newCcFee;
    } else {
      if (typeof console !== 'undefined') {
        console.warn('[phase-3-cc-fee] usePerItemCcFee=true but vendorPaymentItems not provided — falling back to legacy. Caller bug.');
      }
      ccFeeTotal = legacyCcFee;
    }
  } else {
    ccFeeTotal = legacyCcFee;
  }

  // 2026-06-10 — suppression rule for the items.ccFeePct silent
  // multiplier (Decision 7). When ANY item on this FIN has
  // ccFeePct > 0, the per-line multiplier already recovered the CC
  // fee from the client. Adding the receipt-aggregated ccFeeTotal
  // on top would double-bill. Zero it out — the actual cc_fee
  // values still live on vendor_payments / receipts for QB
  // reconciliation; just don't ALSO bill them through the FIN.
  if (anyCcFeePctItem) {
    ccFeeTotal = 0;
  }

  // CC processing fee folds silently into amountDue + subtotal under
  // Model B (2026-06-03). No per-line "Credit Card Processing" item is
  // appended to the FIN PDF — the 15% markup absorbs card costs from
  // the client's perspective. No "Credit Card" line in the totals
  // block either. The ccFeeTotal still aggregates so the Python
  // renderer + downstream consumers (QB push, etc.) can read the firm-
  // side cost via the `feeCcProcessingTot` payload field, but the
  // client-facing PDF doesn't surface it as a separate line.
  //
  // Model B per-item (2026-06-03):
  //   balanceTotal = SUM(lifecycleOwed - paid) — already includes the
  //                  fee portion that's still unbilled for each item
  //   feesTotalRounded = display-only decomposition of subtotal (NOT
  //                  added separately to amountDue — that would
  //                  double-count under Model B)
  //   ccFeeTotal = firm-paid-bank reimbursement, added separately
  //
  //   amountDue = balanceTotal + ccFeeTotal
  //   subtotal  = productTotalRounded + feesTotalRounded + ccFeeTotal
  //             = SUM(lifecycleOwed) + ccFee = full lifecycle for items
  //
  // Math identity (Fix E): subtotal = depositPaid + appliedCredit +
  // finalDue, where depositPaid = subtotal − amountDue = SUM(itemPaidShare).
  // Adjustments fold into BOTH subtotal and amountDue (2026-06-06 patch).
  // adjustmentsTax already lives inside feesTotalRounded (via the
  // feeSalesTaxTot += adjustmentsTax above), so don't double-count it
  // here — only the principal taxable + non-taxable amounts get added.
  // The math identity (subtotal − amountDue = depositPaid) is preserved
  // because adjustmentsTotal lands on both sides equally.
  // ── Which fee rows this invoice is billing (2026-09-07) ─────────────
  // Returned so the caller can stamp vendor_payments.billed_invoice_id in
  // the same transaction that writes the invoice — the change-order
  // pattern (coIdsConsumed) exactly. Computed AFTER suppression: when a
  // retired ccFeePct zeroes the fee, nothing was billed, so nothing may
  // be consumed, or the fee would be silently lost instead of waiting
  // for an invoice that actually charges it.
  var ccFeeIdsConsumed = ccFeeTotal > 0
    ? ccFeeSources({
        items:          items,
        vendorPayments: vendorPayments,
        purchaseOrders: purchaseOrders,
      })
        .filter(function(src) { return !src.billedInvoiceId; })
        .map(function(src) { return src.paymentId; })
        .filter(function(id) { return id != null; })
    : [];

  // ── Card-fee sales tax (2026-09-07) ─────────────────────────────────
  // A card / payment-processing fee is part of the sales price of the
  // merchandise, so it is taxable (Cimon, 2026-09-07). This CORRECTS the
  // same-day assumption that the fee was structurally untaxable:
  // computeItemTax reads only items.fees, the fee has no items.fees field
  // by design, and the renderer added it to subtotal AFTER Sales Tax — so
  // nothing anywhere taxed it. (The retired ccFeePct path did tax it, via
  // proposalTax × ccFeeMul; on that one point it was right.)
  //
  // Taxed at DOCUMENT level, not per item, because the fee is
  // payment-level: it arrives from vendor_payments / receipts and has no
  // per-item home by deliberate design (see the Phase 1 backfill notes).
  //
  // It follows MERCHANDISE taxability, not the per-fee flags — it is part
  // of the product's final cost, not a service of its own.
  // taxesMerchandise defaults TRUE when undefined, matching computeItemTax.
  //
  // amountDue and subtotal both take it, exactly as they both take
  // ccFeeTotal, so the Fix E identity (subtotal − amountDue = depositPaid
  // = SUM(itemPaidShare)) is untouched: the term cancels on both sides.
  var ccFeeTax = 0;
  if (ccFeeTotal > 0 && hasProjectTaxConfig(projObj)
      && !(projObj && projObj.taxesMerchandise === false)) {
    var ccTaxRate = parseFloat(projObj && projObj.taxRate) || 0;
    ccFeeTax = Math.round(ccFeeTotal * ccTaxRate * 100) / 100;
    feeSalesTaxTot   = Math.round((feeSalesTaxTot   + ccFeeTax) * 100) / 100;
    feesTotalRounded = Math.round((feesTotalRounded + ccFeeTax) * 100) / 100;
  }

  var amountDue = Math.round((balanceTotal + ccFeeTotal + ccFeeTax + adjustmentsTotal) * 100) / 100;
  // feesTotalRounded already carries ccFeeTax (folded in above), so it is
  // not added again here.
  var subtotal = Math.round((productTotalRounded + feesTotalRounded + ccFeeTotal + adjustmentsTotal) * 100) / 100;

  // Markup dollars for the items this FIN closes, split product vs fees
  // (2026-09-08). Summed from itemMarkupBreakdown so it is the same
  // arithmetic Reconciliation itemizes with; components are skipped there
  // (parentId short-circuits) because assemblyClient already rolls a
  // parent's children into the parent's client total.
  var markupSplit = items.reduce(function(acc, it) {
    var m = itemMarkupBreakdown(it, items);
    acc.product += m.product;
    acc.fees    += m.fees;
    return acc;
  }, { product: 0, fees: 0 });
  markupSplit.product = Math.round(markupSplit.product * 100) / 100;
  markupSplit.fees    = Math.round(markupSplit.fees    * 100) / 100;

  // Fix E (2026-05-27): PDF payment breakdown for the final-invoice
  // totals section. Fresh-generation path — the new invoice doesn't
  // have an id yet, so appliedCredit is trivially 0. depositPaid is
  // the implicit subtotal − amountDue subtraction (the deposit math
  // baked into balanceTotal). The regenerate-from-saved path in
  // InvoicesTab.jsx computes the same fields via the shared helper
  // with the real invoice id, picking up any allocations recorded
  // against this final since it was created.
  var paymentBreakdown = computeInvoicePaymentBreakdown(null, subtotal, amountDue, paymentAllocations);
  var depositPaidPdf   = paymentBreakdown.depositPaid;
  var appliedCreditPdf = paymentBreakdown.appliedCredit;

  var billToAddrLines = [];
  if (client) {
    if (client.billingAddress) billToAddrLines.push(client.billingAddress);
    if (client.city || client.state || client.zip) {
      billToAddrLines.push([client.city, client.state, client.zip].filter(Boolean).join(', '));
    }
  }

  var siteAddrLines = [];
  if (projObj) {
    if (projObj.address) siteAddrLines.push(projObj.address);
    if (projObj.address2) siteAddrLines.push(projObj.address2);
    if (projObj.city || projObj.state || projObj.zip) {
      siteAddrLines.push([projObj.city, projObj.state, projObj.zip].filter(Boolean).join(', '));
    }
  }

  // Pair the final invoice's ref to its matched deposit's counter, so a
  // deposit at 26HOB-INV-003-DEP yields a final at 26HOB-INV-003-FIN
  // (same numeric counter, swapped suffix). When no matched deposit
  // exists for the parent proposal — Rule 3 edge case, final without
  // deposit — mint a fresh counter via generateInvoiceRef(..., 'final').
  // Defensive: if the matched deposit predates the suffix convention
  // (legacy refs not yet backfilled), append -FIN directly so the new
  // final still carries the suffix.
  //
  // Multi-PO letter suffix (2026-06-27, Rule 4 enforcement): Under the
  // 1-PO-=-1-FIN model, a multi-vendor proposal produces one FIN per
  // PO. The first FIN against a proposal mints the base shape
  // (-FIN, no letter — matches legacy single-PO shape and Rule 4
  // statement #1). Subsequent FINs walk A → Z, picking the first
  // unused letter (so PO #2's FIN is -A-FIN, PO #3's is -B-FIN, etc.).
  // This avoids retroactive renaming of the first FIN if the proposal
  // ends up spawning more POs than expected at first-FIN-build time.
  var invoiceRef;
  var matchedDeposit = (allInvoices || []).find(function(inv) {
    return inv && inv.invoiceType === 'deposit'
      && String(inv.proposalId || '') === String(firstProposalId || '')
      && firstProposalId;
  });
  var baseFin;
  if (matchedDeposit && matchedDeposit.invoiceRef) {
    // Strip the per-item letter suffix (if any) before deriving baseFin.
    // Per-item DEPs are a legacy shape (buildDepositInvoiceRecords mints
    // one row per item with refs like 26HOB-INV-001-A-DEP, -B-DEP, ...)
    // that predates Rule 2's "1 DEP per proposal" model and will be
    // retired in Phase 3. Until then, the matched-deposit lookup above
    // returns whichever per-item DEP is first in the array — its
    // letter is per-item, NOT per-PO. The multi-PO FIN suffix walker
    // (below) needs the proposal-level base counter, not the per-item
    // letter. Without this strip, PO #1's FIN would mint with a bogus
    // -A-FIN inherited from a DEP letter, and subsequent FINs would
    // walk to -A-B-FIN, -A-C-FIN, etc. (broken refs).
    var depRef = String(matchedDeposit.invoiceRef);
    if (/-[A-Z]-DEP$/.test(depRef)) {
      depRef = depRef.replace(/-[A-Z]-DEP$/, '-DEP');
    }
    if (/-DEP$/.test(depRef)) {
      baseFin = depRef.replace(/-DEP$/, '-FIN');
    } else {
      baseFin = depRef + '-FIN';
    }
  } else {
    baseFin = generateInvoiceRef(projectCode, allInvoices, 'final');
  }
  // Scan prior FINs against the same proposal to choose the suffix.
  var priorFinsForProposal = firstProposalId
    ? (allInvoices || []).filter(function(inv) {
        return inv && inv.invoiceType === 'final'
          && String(inv.proposalId || '') === String(firstProposalId);
      })
    : [];
  if (priorFinsForProposal.length === 0) {
    invoiceRef = baseFin;
  } else {
    // Map existing FINs to their suffix state.
    var baseClaimed = false;
    var usedLetters = {};
    priorFinsForProposal.forEach(function(inv) {
      var ref = String(inv.invoiceRef || '');
      if (ref === baseFin) { baseClaimed = true; return; }
      var m = ref.match(/-([A-Z])-FIN$/);
      if (m) usedLetters[m[1]] = true;
    });
    if (!baseClaimed) {
      // Edge case: prior FIN exists for the proposal but didn't claim
      // the base shape (legacy data, different counter, etc.). Use base.
      invoiceRef = baseFin;
    } else {
      // Walk A → Z for the first unused letter.
      var letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
      var chosen = null;
      for (var i = 0; i < letters.length; i++) {
        if (!usedLetters[letters[i]]) { chosen = letters[i]; break; }
      }
      if (chosen) {
        invoiceRef = baseFin.replace(/-FIN$/, '-' + chosen + '-FIN');
      } else {
        // 27+ FINs against one proposal — astronomically unlikely.
        // Fall back to a fresh per-project counter to avoid collision.
        invoiceRef = generateInvoiceRef(projectCode, allInvoices, 'final');
      }
    }
  }

  // PO refs bundled into this FIN — surfaced in the PDF header under
  // the invoice ref so the client / bookkeeper can cross-reference
  // against vendor purchase orders. Going-forward 1 FIN = 1 PO; legacy
  // multi-PO FINs render comma-joined. (2026-06-13)
  var finPoRefSet = {};
  rawItems.forEach(function(it) {
    if (it && it.poNumber) finPoRefSet[it.poNumber] = true;
  });
  var finPoRefs = Object.keys(finPoRefSet);

  // Internal reference block (2026-07-19, Dan's QB reconciliation) —
  // SKUs + POs + proposal refs render in muted gray at the bottom left
  // of the PDF, above Payment Terms. Internal cross-reference, not a
  // client-facing feature.
  var finCrossRefs = invoiceCrossRefs(
    { itemIds: rawItems.map(function(it) { return it && it.id; }).filter(Boolean) },
    { items: (allItems && allItems.length) ? allItems : rawItems, proposals: proposals }
  );

  return {
    payload: {
      invoiceRef: invoiceRef,
      poRefs: finPoRefs,
      internalRefs: {
        skus: finCrossRefs.skus,
        poRefs: finPoRefs,
        proposalRefs: finCrossRefs.proposalRefs,
        // PROPOSAL line with totals (2026-09-03) — bookkeeper anchor.
        proposalLine: proposalRefLineWithTotals(finCrossRefs.proposalRefs, proposals),
      },
      invoiceType: 'final',
      projectLabel: projectLabel,
      projectDisplay: (projObj && projObj.displayName) || projectLabel,
      billToName: (client && client.name) || '',
      billToAddr: billToAddrLines.join('\n'),
      siteAddr: siteAddrLines.join('\n'),
      lineItems: lineItems,
      productTotal: productTotalRounded,
      feeFreightTot:      Math.round(feeFreightTot * 100) / 100,
      feeCratingTot:      Math.round(feeCratingTot * 100) / 100,
      feeInstallationTot: Math.round(feeInstallationTot * 100) / 100,
      feeTariffTot:       Math.round(feeTariffTot * 100) / 100,
      feeSalesTaxTot:     Math.round(feeSalesTaxTot * 100) / 100,
      feeCcProcessingTot: ccFeeTotal,
      // The tax charged ON the card fee. Already inside feeSalesTaxTot and
      // the subtotal — surfaced only so callers and tests can see the
      // split without re-deriving it.
      feeCcProcessingTax: ccFeeTax,
      // ── Markup dollars (2026-09-08) ───────────────────────────────────
      // Cimon: "markup dollars include the percentage from marked up
      // shipping crating etc plus markup on product." Stored on the FIN
      // because the FIN is the document that closes an item's money — its
      // subtotal is the full lifecycle, so this is the full margin.
      //
      // Deliberately EXCLUDES the card fee and adjustments. Both already
      // have their own stored columns (fee_cc_processing_tot, adjustments),
      // and Reconciliation adds them to markup separately; folding them in
      // here would double-count them on Dan's screen.
      //
      // Sales tax and tariff contribute 0 by construction — pass-through,
      // collected rather than earned.
      markupProductTot: markupSplit.product,
      markupFeesTot:    markupSplit.fees,
      feeCustom: feeCustomList,
      // Adjustments (2026-06-06 patch). Renderer reads these to draw
      // the per-row taxable list above Subtotal and the non-taxable
      // list between Tax and Total. Empty array → no Adjustments
      // section renders. adjustmentsTax is the sales-tax contribution
      // from the taxable bucket; it's already INSIDE feeSalesTaxTot and
      // the Subtotal — surfaced separately here only so the renderer
      // can label the Subtotal line as recomputed when adjustments
      // shift the taxable base. See BILLING_REDESIGN_SPEC.md §3.
      adjustments:            adjustmentsNormalized,
      adjustmentsTaxableTot:  Math.round(adjustmentsTaxable    * 100) / 100,
      adjustmentsNonTaxableTot: Math.round(adjustmentsNonTaxable * 100) / 100,
      adjustmentsTax:         adjustmentsTax,
      subtotal: subtotal,
      depositTotal: amountDue,                         // PDF labels this "Final Balance Due"
      balanceRemaining: 0,
      // Fix E (2026-05-27) — PDF totals section breakdown. The Python
      // generator reads these two scalars to render "Less deposit paid"
      // + "Less credit applied" between Subtotal and Final Balance Due.
      // Fresh-generation: appliedCredit is 0 (no allocations against a
      // not-yet-created invoice id). depositPaid is the implicit
      // subtotal − amountDue subtraction.
      depositPaid:   depositPaidPdf,
      appliedCredit: appliedCreditPdf,
      // Deposit-payment dates (2026-09-03): preformatted display strings
      // the renderer appends to the "Less deposit paid" label ("received
      // May 6, 2026" / "received May 6 – Jun 2, 2026"). Dates only —
      // the amount above stays the implicit Kumiko-safe figure.
      depositPaymentDates: Array.isArray(opts.depositPaymentDates) ? opts.depositPaymentDates : [],
      notes: '',
      // Date priority: sent_at > createdAt > today. Fresh-generation
      // typically has neither (the FIN row hasn't been finalized yet);
      // helper resolves to today in that case. If a future caller wants
      // to render an already-issued FIN through this pure builder
      // (instead of the InvoicesTab regenerate inline path), they pass
      // sentAt / createdAt through opts and the helper picks the right
      // value.
      date: formatInvoiceDateForPdf(opts.sentAt || null, opts.createdAt || null),
      firmName:   firmSettings.name    || 'Cimon Starr LLC',
      firmStreet: firmSettings.address || '3516 Sacramento Street',
      firmCity:   firmSettings.city    || 'San Francisco',
      firmState:  firmSettings.state   || 'CA',
      firmZip:    firmSettings.zip     || '94118',
    },
    invoiceRef: invoiceRef,
    productTotalRounded: productTotalRounded,
    feesTotalRounded: feesTotalRounded,
    subtotal: subtotal,
    amountDue: amountDue,
    // Normalized adjustments — caller persists these onto invoices.adjustments
    // so the regenerate-from-saved path can rebuild the same PDF.
    adjustments: adjustmentsNormalized,
    itemIds: items.map(function(it) { return it.id; }),
    clientId: clientId,
    clientName: client ? client.name : '',
    proposalId: firstProposalId,
    // CO ids whose deltas were folded INLINE into the affected items'
    // UNIT PRICE breakdown on this invoice. Caller stamps each with
    // billed_invoice_id after the invoice persists, so a future
    // re-generate of the same final invoice batch won't double-bill
    // the same CO. Out-of-scope COs (qty changes, multiple per item,
    // fees field, non-numeric deltas) are NOT in this list — they
    // emit a console.warn from the per-item loop and remain available
    // for manual handling.
    coIdsConsumed: coIdsConsumed,
    // Card-fee rows this invoice bills; the caller stamps
    // vendor_payments.billed_invoice_id on them at save time.
    ccFeeIdsConsumed: ccFeeIdsConsumed,
    // Per-item tax snapshot (2026-09-13): what THIS document taxes, per
    // item, frozen at Save & Commit into final_invoice_items. The Sales
    // Tax report files from these rows, never from the live item.
    taxSnapshot: buildFinTaxSnapshotRows(items, projObj, allItems),
  };
}

// ── RETAINER LEDGER ──────────────────────────────────────────────────────────
// Per-project ledger of deposits received and draws made against the retainer.
// Current balance = sum(amount). Deposits are positive, draws are negative.
//
// See supabase/migrations/20260425_create_retainer_ledger_table.sql.

export function rowToRetainerEntry(row) {
  return {
    id:            row.id,
    projectLabel:  row.project_label || '',
    entryDate:     row.entry_date    || '',
    entryType:     row.entry_type    || 'deposit',
    amount:        parseFloat(row.amount) || 0,
    reference:     row.reference     || '',
    notes:         row.notes         || '',
    paymentMethod: row.payment_method || '',
    bankReference: row.bank_reference || '',
    sourceType:    row.source_type   || 'manual',
    sourceId:      row.source_id     || '',
    createdBy:     row.created_by    || '',
    createdAt:     row.created_at    || '',
    updatedAt:     row.updated_at    || '',
    // Phase 5 / QB readiness
    qbPaymentId:   row.qb_payment_id || '',
    qbTarget:      row.qb_target     || '',
    qbPushedAt:    row.qb_pushed_at  || '',
    qbError:       row.qb_error      || '',
  };
}

export function retainerEntryToRow(e) {
  var id = e.id;
  if (!id || /^\d+$/.test(String(id))) id = undefined;
  return {
    id:             id,
    project_label:  e.projectLabel || '',
    entry_date:     e.entryDate    || new Date().toISOString().slice(0, 10),
    entry_type:     e.entryType    || 'deposit',
    amount:         parseFloat(e.amount) || 0,
    reference:      e.reference    || '',
    notes:          e.notes        || '',
    payment_method: e.paymentMethod || '',
    bank_reference: e.bankReference || '',
    source_type:    e.sourceType   || 'manual',
    source_id:      e.sourceId     || null,
    created_by:     e.createdBy    || '',
    // Phase 5 / QB
    qb_payment_id:  e.qbPaymentId  || '',
    qb_target:      e.qbTarget     || '',
    qb_pushed_at:   e.qbPushedAt   || null,
    qb_error:       e.qbError      || '',
  };
}

// Sum every entry for the given project to get current balance.
// Deposits are positive amounts, draws are negative — same sign convention as
// the ledger column.
export function calcRetainerBalance(entries, projectLabel) {
  return Math.round((entries || [])
    .filter(function(e) { return !projectLabel || e.projectLabel === projectLabel; })
    .reduce(function(s, e) { return s + (parseFloat(e.amount) || 0); }, 0)
    * 100) / 100;
}

// Returns entries sorted by date (oldest first) with running_balance per row.
// Used for the Billing → Retainers ledger table display.
export function retainerEntriesWithRunningBalance(entries, projectLabel) {
  var filtered = (entries || [])
    .filter(function(e) { return !projectLabel || e.projectLabel === projectLabel; })
    .slice()
    .sort(function(a, b) {
      // primary: entry_date asc; secondary: created_at asc to break ties
      var ad = String(a.entryDate || ''), bd = String(b.entryDate || '');
      if (ad !== bd) return ad.localeCompare(bd);
      return String(a.createdAt || '').localeCompare(String(b.createdAt || ''));
    });
  var running = 0;
  return filtered.map(function(e) {
    running = Math.round((running + (parseFloat(e.amount) || 0)) * 100) / 100;
    return Object.assign({}, e, { runningBalance: running });
  });
}

// ── CLIENT CREDIT LEDGER ─────────────────────────────────────────────────────
// Per-client credit balance from cancelled-item deposits. + entries when an
// item is cancelled (deposit becomes available credit), − entries when applied
// to another item or refunded. Balance = sum(amount). Mirrors retainer ledger
// shape so the UI patterns are consistent.

// client_credit_ledger.client_id is `text NOT NULL` in Postgres, but
// csid_clients (JSON blob in app_storage) gives clients a numeric `id`
// from Date.now(). Every render-time filter comparing the two sides
// (`e.clientId === c.id`) silently failed strict equality because one
// was a string and the other a number — a credit ledger row would load
// fine and then disappear at filter time.
//
// Fix: coerce clientId to a string at the mapper boundary so downstream
// comparisons can rely on consistent types regardless of how the row
// landed in the DB (app-side write, SQL insert, migration backfill).
// The five surfaces that already wrap in String(...) (utils.js
// clientForProposal, BankDepositModal, ProposalsTab2, QuickBooksTab)
// remain correct — String(string) is a no-op.
export function rowToClientCredit(row) {
  return {
    id:              row.id,
    clientId:        row.client_id != null ? String(row.client_id) : '',
    entryDate:       row.entry_date         || '',
    entryType:       row.entry_type         || 'cancelled_deposit',
    amount:          parseFloat(row.amount) || 0,
    sourceItemId:    row.source_item_id     || '',
    sourceInvoiceId: row.source_invoice_id  || '',
    sourceCreditId:  row.source_credit_id   || '',  // 2026-05-22: links applied_to_billing_summary rows back to the originating credit
    targetItemId:    row.target_item_id     || '',
    targetInvoiceId: row.target_invoice_id  || '',
    targetSummaryId: row.target_summary_id  || '',  // 2026-05-22: billing summary an applied credit was consumed against
    reference:       row.reference          || '',
    notes:           row.notes              || '',
    createdBy:       row.created_by         || '',
    qbJournalId:     row.qb_journal_id      || '',  // legacy (journal-only)
    qbId:            row.qb_id              || '',  // generic QB record id
    qbTarget:        row.qb_target          || '',  // 'journal_entry' | 'refund_receipt'
    qbError:         row.qb_error           || '',
    qbPushedAt:      row.qb_pushed_at       || '',
    createdAt:       row.created_at         || '',
    updatedAt:       row.updated_at         || '',
  };
}

export function clientCreditToRow(e) {
  var id = e.id;
  if (!id || /^\d+$/.test(String(id))) id = undefined;
  // qb_id / qb_target / qb_error are NOT written here — migration
  // 20260504_credit_ledgers_qb_target.sql adds those columns for Phase 5
  // QB push prep, but the migration is not yet live in every Supabase
  // environment. Writing them blocks BS save (which directly inserts to
  // client_credit_ledger when applying credits, bypassing the
  // makeLedgerSaver retry-strip). The QB push endpoint
  // (qb-push-credit-ledger.js) writes these fields directly via
  // .update(...) when it actually pushes — so the values are set by the
  // push code, not by the row mapper. When the migration is live and
  // QB push runs, the columns will populate from there.
  return {
    id:                 id,
    client_id:          e.clientId          || '',
    entry_date:         e.entryDate         || new Date().toISOString().slice(0, 10),
    entry_type:         e.entryType         || 'cancelled_deposit',
    amount:             parseFloat(e.amount) || 0,
    source_item_id:     e.sourceItemId      || null,
    source_invoice_id:  e.sourceInvoiceId   || null,
    source_credit_id:   e.sourceCreditId    || null,
    target_item_id:     e.targetItemId      || null,
    target_invoice_id:  e.targetInvoiceId   || null,
    target_summary_id:  e.targetSummaryId   || null,
    reference:          e.reference         || '',
    notes:              e.notes             || '',
    created_by:         e.createdBy         || '',
    qb_journal_id:      e.qbJournalId       || '',  // legacy — pre-migration column
  };
}

// Per-credit available balance — how much of this credit row remains
// unconsumed across all applications (applied_to_item / applied_to_invoice
// / applied_to_billing_summary). The original credit row carries the
// positive amount; each application writes a negative row tagged with
// source_credit_id pointing back at the original.
//
// Math: balance = original.amount + SUM(applied.amount) for applied
// rows where applied.sourceCreditId === original.id. Since applied
// amounts are negative, this works as a subtraction.
//
// opts.excludeSummaryId — when computing how much is available to
// (re)apply to a specific draft summary, pass the draft's id so this
// helper excludes that summary's own application from the deduction.
// The picker UI can then show "available to apply here = remaining +
// what's already applied to this summary", letting the user adjust
// upward.
//
// The entry types that ORIGINATE client credit (positive rows a client
// can spend). Everything else in client_credit_ledger consumes balance
// (applied_to_* / refund, negative rows). Single source of truth for
// every "which credits can be offered?" filter — the billing summary's
// credit picker omitted 'overpayment' for two months because the pair
// was hardcoded there (found 2026-09-04: a payment-wizard leftover
// saved as credit showed on the Credits tab but never appeared on the
// billing summary).
export var CLIENT_CREDIT_SOURCE_TYPES = ['cancelled_deposit', 'co_credit', 'overpayment'];

// What a credit is CALLED on the client's statement (2026-09-07, Cimon).
// "Credit" is the wrong word for most of them: a cancelled deposit or a
// payment-wizard overpayment is money the client already handed over,
// now applied to different merchandise — nothing was written off, and
// the client's bookkeeper reads that line as "Deposit previously
// received". Only a change-order credit is a genuine billing adjustment.
// The label is a DEFAULT: the billing summary's credit picker lets the
// bookkeeper type any wording, which persists on the applied ledger row
// and travels to the renderer as the row's `description`.
export var CREDIT_PRINT_LABEL_DEFAULT = 'Credit applied';
export function creditPrintLabel(entryType) {
  switch (String(entryType || '')) {
    case 'cancelled_deposit':
    case 'overpayment':
      return 'Deposit previously received';
    default:
      return CREDIT_PRINT_LABEL_DEFAULT;
  }
}

// Name for the statement's deduction SECTION, from the source entry types
// of the credits applied to it. Categorical and short — it is shouted in
// letterspaced caps as a heading, so it must never be the free text the
// bookkeeper typed for a row ("LESS: TEMPORARY BED FRAMES AND MATTRESSES
// (ORDER NOT PLACED)" was the first cut, and a heading is a category, not
// a sentence). One kind of money → that kind's name; mixed → the neutral
// default, with each row still carrying its own wording.
export function creditsSectionLabel(entryTypes) {
  var labels = (entryTypes || []).map(creditPrintLabel)
    .filter(function(v, i, a) { return a.indexOf(v) === i; });
  return labels.length === 1 ? labels[0] : CREDIT_PRINT_LABEL_DEFAULT;
}

// creditEntry must be an ORIGINAL credit row (CLIENT_CREDIT_SOURCE_TYPES).
// Calling with an `applied_to_*` row returns its own signed amount
// (no recursion).
export function calcCreditAvailableBalance(creditEntry, ledger, opts) {
  if (!creditEntry || !creditEntry.id) return 0;
  var excludeSummaryId = opts && opts.excludeSummaryId ? String(opts.excludeSummaryId) : '';
  var original = parseFloat(creditEntry.amount) || 0;
  var applied = (ledger || []).reduce(function(s, e) {
    if (!e || !e.sourceCreditId) return s;
    if (String(e.sourceCreditId) !== String(creditEntry.id)) return s;
    if (excludeSummaryId && String(e.targetSummaryId || '') === excludeSummaryId) return s;
    return s + (parseFloat(e.amount) || 0);
  }, 0);
  return Math.round((original + applied) * 100) / 100;
}

// Defensive String(...) on both sides of the clientId comparison —
// rowToClientCredit normalizes at the mapper boundary, but this helper
// is also called with raw inputs from tests and possibly future
// callers, so we don't rely on upstream coercion. String('') === ''
// and String(undefined) === 'undefined', so we guard the empty-filter
// case (`!clientId`) before normalizing.
export function calcClientCreditBalance(entries, clientId) {
  var match = clientId != null && clientId !== '' ? String(clientId) : '';
  return Math.round((entries || [])
    .filter(function(e) { return !match || String(e.clientId || '') === match; })
    .reduce(function(s, e) { return s + (parseFloat(e.amount) || 0); }, 0)
    * 100) / 100;
}

export function clientCreditEntriesWithRunningBalance(entries, clientId) {
  var match = clientId != null && clientId !== '' ? String(clientId) : '';
  var filtered = (entries || [])
    .filter(function(e) { return !match || String(e.clientId || '') === match; })
    .slice()
    .sort(function(a, b) {
      var ad = String(a.entryDate || ''), bd = String(b.entryDate || '');
      if (ad !== bd) return ad.localeCompare(bd);
      return String(a.createdAt || '').localeCompare(String(b.createdAt || ''));
    });
  var running = 0;
  return filtered.map(function(e) {
    running = Math.round((running + (parseFloat(e.amount) || 0)) * 100) / 100;
    return Object.assign({}, e, { runningBalance: running });
  });
}

// ── VENDOR CREDIT LEDGER ─────────────────────────────────────────────────────
// Per-vendor credit balance for deposits we've paid that need re-allocation:
// items dropped from a PO, vendor refund issued, or write-off when the vendor
// won't refund. Same ledger shape as the client credit ledger.

export function rowToVendorCredit(row) {
  return {
    id:              row.id,
    vendorName:      row.vendor_name        || '',
    entryDate:       row.entry_date         || '',
    entryType:       row.entry_type         || 'cancelled_deposit',
    amount:          parseFloat(row.amount) || 0,
    sourcePoId:      row.source_po_id       || '',
    sourceItemId:    row.source_item_id     || '',
    targetPoId:      row.target_po_id       || '',
    targetItemId:    row.target_item_id     || '',
    reference:       row.reference          || '',
    notes:           row.notes              || '',
    createdBy:       row.created_by         || '',
    qbJournalId:     row.qb_journal_id      || '',  // legacy (journal-only)
    qbId:            row.qb_id              || '',  // generic QB record id
    qbTarget:        row.qb_target          || '',  // 'journal_entry' | 'vendor_credit'
    qbError:         row.qb_error           || '',
    qbPushedAt:      row.qb_pushed_at       || '',
    createdAt:       row.created_at         || '',
    updatedAt:       row.updated_at         || '',
  };
}

export function vendorCreditToRow(e) {
  var id = e.id;
  if (!id || /^\d+$/.test(String(id))) id = undefined;
  // qb_id / qb_target / qb_error omitted — see clientCreditToRow above
  // for the reasoning (Phase 5 migration not yet live in every Supabase
  // environment; qb-push-credit-ledger.js writes those fields directly
  // when the push fires).
  return {
    id:               id,
    vendor_name:      e.vendorName         || '',
    entry_date:       e.entryDate          || new Date().toISOString().slice(0, 10),
    entry_type:       e.entryType          || 'cancelled_deposit',
    amount:           parseFloat(e.amount) || 0,
    source_po_id:     e.sourcePoId         || null,
    source_item_id:   e.sourceItemId       || null,
    target_po_id:     e.targetPoId         || null,
    target_item_id:   e.targetItemId       || null,
    reference:        e.reference          || '',
    notes:            e.notes              || '',
    created_by:       e.createdBy          || '',
    qb_journal_id:    e.qbJournalId        || '',  // legacy — pre-migration column
  };
}

export function calcVendorCreditBalance(entries, vendorName) {
  return Math.round((entries || [])
    .filter(function(e) { return !vendorName || e.vendorName === vendorName; })
    .reduce(function(s, e) { return s + (parseFloat(e.amount) || 0); }, 0)
    * 100) / 100;
}

// ── OWNER DRAWS ──────────────────────────────────────────────────────────────
// Cimon's personal draw history. Read by owner + bookkeeper, write by owner.
// Maps to QB Journal Entry: debit cash, credit Owner's Draw equity account.

export function rowToOwnerDraw(row) {
  return {
    id:           row.id,
    drawDate:     row.draw_date          || '',
    amount:       parseFloat(row.amount) || 0,
    category:     row.category           || '',
    reference:    row.reference          || '',
    notes:        row.notes              || '',
    createdBy:    row.created_by         || '',
    qbJournalId:  row.qb_journal_id      || '',
    qbPushedAt:   row.qb_pushed_at       || '',
    createdAt:    row.created_at         || '',
    updatedAt:    row.updated_at         || '',
  };
}

export function ownerDrawToRow(d) {
  var id = d.id;
  if (!id || /^\d+$/.test(String(id))) id = undefined;
  return {
    id:             id,
    draw_date:      d.drawDate          || new Date().toISOString().slice(0, 10),
    amount:         parseFloat(d.amount) || 0,
    category:       d.category          || '',
    reference:      d.reference         || '',
    notes:          d.notes             || '',
    created_by:     d.createdBy         || '',
    qb_journal_id:  d.qbJournalId       || '',
  };
}

export function calcOwnerDrawsTotal(draws) {
  return Math.round((draws || [])
    .reduce(function(s, d) { return s + (parseFloat(d.amount) || 0); }, 0)
    * 100) / 100;
}

export function calcOwnerDrawsYearToDate(draws) {
  var thisYear = new Date().getFullYear();
  return Math.round((draws || [])
    .filter(function(d) { return String(d.drawDate || '').slice(0, 4) === String(thisYear); })
    .reduce(function(s, d) { return s + (parseFloat(d.amount) || 0); }, 0)
    * 100) / 100;
}

// ── STATE TAX RECORDS ────────────────────────────────────────────────────────
// Per-state credentials and resale-cert metadata. Owner-only at the DB level.

export function rowToStateTax(row) {
  return {
    id:                 row.id,
    stateCode:          row.state_code            || '',
    jurisdiction:       row.jurisdiction          || '',   // '' = the state; 'Vail' = home-rule city under it
    stateName:          row.state_name            || '',
    status:             row.status                || 'active',
    registrationId:     row.registration_id       || '',
    businessPartnerId:  row.business_partner_id   || '',
    accountEmail:       row.account_email         || '',
    username:           row.username              || '',
    password:           row.password              || '',
    pin:                row.pin                   || '',   // 2026-10-16: WYIFS sign-in PIN (owner-only, never on the view)
    portalUrl:          row.portal_url            || '',
    certFormLabel:      row.cert_form_label       || '',
    dueRule:            row.due_rule              || '',   // 'last_day_next_month' | '20_next_month' | ''
    // 2026-10-16 filing rules (design handoff v2). Period basis decides
    // WHICH MONTH an item's tax lands in: 'payment' = the client's final
    // payment (the 2026-09-13 rule), 'delivery' = delivered into the state
    // (Wyoming — goods wait in Idaho storage until install).
    taxBasis:           row.tax_basis === 'delivery' ? 'delivery' : 'payment',
    filingFrequency:    ['monthly', 'quarterly', 'annual'].indexOf(row.filing_frequency) >= 0 ? row.filing_frequency : 'monthly',
    registeredFrom:     row.registered_from ? String(row.registered_from).slice(0, 10) : '',
    supportPhone:       row.support_phone         || '',
    notes:              row.notes                 || '',
    createdAt:          row.created_at            || '',
    updatedAt:          row.updated_at            || '',
  };
}

export function stateTaxToRow(s) {
  var id = s.id;
  if (!id || /^\d+$/.test(String(id))) id = undefined;
  return {
    id:                  id,
    state_code:          (s.stateCode || '').toUpperCase(),
    jurisdiction:        String(s.jurisdiction || '').trim(),
    state_name:          s.stateName            || '',
    status:              s.status               || 'active',
    registration_id:     s.registrationId       || '',
    business_partner_id: s.businessPartnerId    || '',
    account_email:       s.accountEmail         || '',
    username:            s.username             || '',
    password:            s.password             || '',
    pin:                 s.pin                  || '',
    portal_url:          s.portalUrl            || '',
    cert_form_label:     s.certFormLabel        || '',
    due_rule:            s.dueRule              || '',
    tax_basis:           s.taxBasis === 'delivery' ? 'delivery' : 'payment',
    filing_frequency:    ['monthly', 'quarterly', 'annual'].indexOf(s.filingFrequency) >= 0 ? s.filingFrequency : 'monthly',
    registered_from:     s.registeredFrom ? String(s.registeredFrom).slice(0, 10) : null,
    support_phone:       s.supportPhone         || '',
    notes:               s.notes                || '',
  };
}

// ── SALES TAX FILINGS ────────────────────────────────────────────────────────
// What was actually filed and paid to a state for one period
// (sales_tax_filings, 2026-09-20). The report computes tax DUE; this is
// the ledger of what left the bank, keyed the same way the report keys
// its rows (stateCode + periodKey), plus an optional home-rule
// jurisdiction so a Colorado month can carry a DOR row and a Vail row.
export function rowToSalesTaxFiling(row) {
  return {
    id:             row.id,
    stateCode:      row.state_code       || '',
    jurisdiction:   row.jurisdiction     || '',
    returnKey:      String(row.return_key || '').toUpperCase(),   // '' = the state return (2026-10-16)
    periodKey:      row.period_key       || '',
    grouping:       row.grouping         || 'month',
    taxDueSnapshot: Number(row.tax_due_snapshot || 0),
    amountPaid:     Number(row.amount_paid || 0),
    paidDate:       row.paid_date        || '',
    filedDate:      row.filed_date       || '',
    confirmationNo: row.confirmation_no  || '',
    method:         row.method           || '',
    notes:          row.notes            || '',
    recordedBy:     row.recorded_by      || '',
    createdAt:      row.created_at       || '',
    updatedAt:      row.updated_at       || '',
  };
}

export function salesTaxFilingToRow(f) {
  var id = f.id;
  if (!id || /^\d+$/.test(String(id))) id = undefined;
  var r2 = function(n) { return Math.round((Number(n) || 0) * 100) / 100; };
  return {
    id:               id,
    state_code:       (f.stateCode || '').toUpperCase(),
    jurisdiction:     String(f.jurisdiction || '').trim(),
    return_key:       String(f.returnKey || '').trim().toUpperCase(),
    period_key:       f.periodKey || '',
    grouping:         f.grouping === 'quarter' ? 'quarter' : 'month',
    tax_due_snapshot: r2(f.taxDueSnapshot),
    amount_paid:      r2(f.amountPaid),
    paid_date:        f.paidDate  || null,
    filed_date:       f.filedDate || null,
    confirmation_no:  f.confirmationNo || '',
    method:           f.method || '',
    notes:            f.notes  || '',
    recorded_by:      (f.recordedBy || '').toLowerCase(),
    updated_at:       new Date().toISOString(),
  };
}

// Filings that belong to one report row: same state, same period key.
// Every jurisdiction under the state counts toward the state's paid
// figure (the report keys by state; Vail rides under CO there too).
export function salesTaxFilingsFor(filings, stateCode, periodKey, returnKey) {
  var rk = returnKey == null ? null : String(returnKey).toUpperCase();
  return (filings || []).filter(function(f) {
    if (f.stateCode !== stateCode || f.periodKey !== periodKey) return false;
    return rk == null || String(f.returnKey || '').toUpperCase() === rk;
  });
}

export function sumSalesTaxFilings(filings) {
  return Math.round((filings || []).reduce(function(s, f) { return s + (Number(f.amountPaid) || 0); }, 0) * 100) / 100;
}

// ── SALES TAX CODES ──────────────────────────────────────────────────────────
// Firm-level jurisdiction codes (Finance → State Tax → Sales Tax Codes,
// 2026-09-13). One per place the firm delivers into. Each carries the
// authorities that stack into the blended rate and the taxable-category
// defaults. The project form COPIES a code into the project's own tax
// fields; nothing downstream reads the code table. See
// supabase/migrations/20260913_sales_tax_codes.sql.

export var TAX_CODE_FLAG_KEYS = ['taxesMerchandise', 'taxesFreight', 'taxesCrating', 'taxesInstall', 'taxesTariff', 'taxesStorage', 'taxesTime'];

function taxCodeAuthorities(list) {
  return (Array.isArray(list) ? list : []).map(function(a) {
    var rate = parseFloat(a && a.rate);
    // returnKey (2026-10-16): which RETURN this authority is paid on.
    // '' = the state return. See salesTaxCodeReturns.
    return { name: String((a && a.name) || ''), rate: isNaN(rate) ? 0 : rate, returnKey: String((a && a.returnKey) || '').trim().toUpperCase() };
  });
}
// The extra returns a code files beyond the state return (2026-10-16):
// [{ key: 'VAIL', label: 'Town of Vail', jurisdiction: 'Vail' }].
// jurisdiction names the state_tax_records row (state + jurisdiction)
// that carries the return's portal credentials and due rule.
function taxCodeReturns(list) {
  return (Array.isArray(list) ? list : []).map(function(r) {
    return {
      key:          String((r && r.key) || '').trim().toUpperCase(),
      label:        String((r && r.label) || '').trim(),
      jurisdiction: String((r && r.jurisdiction) || '').trim(),
    };
  }).filter(function(r) { return !!r.key; });
}

export function rowToSalesTaxCode(row) {
  return {
    id:               row.id,
    code:             row.code              || '',
    name:             row.name              || '',
    stateCode:        row.state_code        || '',
    authorities:      taxCodeAuthorities(row.authorities),
    returns:          taxCodeReturns(row.returns),
    taxesMerchandise: row.taxes_merchandise !== false,
    taxesFreight:     !!row.taxes_freight,
    taxesCrating:     !!row.taxes_crating,
    taxesInstall:     !!row.taxes_install,
    taxesTariff:      !!row.taxes_tariff,
    taxesStorage:     !!row.taxes_storage,
    taxesTime:        !!row.taxes_time,
    printDescription: row.print_description || 'Sales Tax',
    active:           row.active !== false,
    notes:            row.notes             || '',
    createdAt:        row.created_at        || '',
    updatedAt:        row.updated_at        || '',
  };
}

export function salesTaxCodeToRow(c) {
  var id = c.id;
  if (!id || /^\d+$/.test(String(id))) id = undefined;
  return {
    id:                id,
    code:              String(c.code || '').trim().toUpperCase(),
    name:              c.name || '',
    state_code:        String(c.stateCode || '').trim().toUpperCase(),
    authorities:       taxCodeAuthorities(c.authorities),
    returns:           taxCodeReturns(c.returns),
    taxes_merchandise: c.taxesMerchandise !== false,
    taxes_freight:     !!c.taxesFreight,
    taxes_crating:     !!c.taxesCrating,
    taxes_install:     !!c.taxesInstall,
    taxes_tariff:      !!c.taxesTariff,
    taxes_storage:     !!c.taxesStorage,
    taxes_time:        !!c.taxesTime,
    print_description: c.printDescription || 'Sales Tax',
    active:            c.active !== false,
    notes:             c.notes || '',
  };
}

// Blended rate = the authorities added together, as a DECIMAL (0.09).
// Rounded to 6 places so 0.029 + 0.01 + 0.045 + 0.005 + 0.005 lands on
// exactly 0.094 and not 0.09399999999999999.
export function salesTaxCodeBlendedRate(code) {
  var sum = taxCodeAuthorities(code && code.authorities).reduce(function(s, a) { return s + a.rate; }, 0);
  return Math.round(sum * 1000000) / 1000000;
}

// ── Returns (2026-10-16). Which portals a code's tax is paid on. ─────────
// Always begins with the state return (key ''), then each extra return
// the code declares. Each carries the rate of the authorities assigned
// to it. Rates are DECIMALS; an authority whose returnKey names no
// declared return falls back to the state return (never lost).
export function salesTaxCodeReturns(code) {
  var auths = taxCodeAuthorities(code && code.authorities);
  var extras = taxCodeReturns(code && code.returns);
  var known = {};
  extras.forEach(function(r) { known[r.key] = true; });
  var stateName = US_STATE_NAMES[String((code && code.stateCode) || '').toUpperCase()] || String((code && code.stateCode) || '');
  var list = [{ key: '', label: (stateName ? stateName + ' ' : '') + 'state return', jurisdiction: '', rate: 0, authorities: [] }]
    .concat(extras.map(function(r) { return { key: r.key, label: r.label || r.key, jurisdiction: r.jurisdiction, rate: 0, authorities: [] }; }));
  auths.forEach(function(a) {
    var k = known[a.returnKey] ? a.returnKey : '';
    var ret = list.find(function(r) { return r.key === k; });
    ret.rate = Math.round((ret.rate + a.rate) * 1000000) / 1000000;
    ret.authorities.push(a);
  });
  return list;
}
// Split a tax figure across a code's returns, by rate share of the
// blended rate, to the cent; rounding residue lands on the state return
// so the shares ALWAYS sum to the figure handed in (the frozen snapshot
// tax — never recomputed from today's code). No code → one state return
// carrying the whole figure.
export function salesTaxReturnShares(tax, code) {
  var r2 = function(x) { return Math.round((Number(x) || 0) * 100) / 100; };
  var total = r2(tax);
  var returns = code ? salesTaxCodeReturns(code) : [{ key: '', label: 'State return', jurisdiction: '', rate: 0, authorities: [] }];
  var blended = returns.reduce(function(s, r) { return s + r.rate; }, 0);
  var out = returns.map(function(r) {
    return { key: r.key, label: r.label, jurisdiction: r.jurisdiction, rate: r.rate, authorities: r.authorities, tax: blended > 0 ? r2(total * (r.rate / blended)) : 0 };
  });
  var placed = out.reduce(function(s, r) { return s + r.tax; }, 0);
  out[0].tax = r2(out[0].tax + (total - placed));
  if (blended <= 0) out[0].tax = total;
  return out;
}
// The returns a STATE files, across every active code for that state —
// so a zero-activity Colorado period still offers both returns. Keys are
// unioned; the first code naming a key supplies its label/jurisdiction.
export function salesTaxStateReturns(stateCode, codes) {
  var code = String(stateCode || '').toUpperCase();
  var seen = {}; var out = [];
  // Authorities ride along (2026-10-16 form guides): the first code
  // naming a key supplies them; the implicit state entry takes the first
  // code's state-return authorities.
  var push = function(r) {
    var auths = (r.authorities || []).slice();
    if (seen[r.key]) { var have = out.find(function(x) { return x.key === r.key; }); if (have && !have.authorities.length && auths.length) { have.authorities = auths; have.rate = r.rate; } return; }
    seen[r.key] = true; out.push({ key: r.key, label: r.label, jurisdiction: r.jurisdiction, rate: r.rate, authorities: auths });
  };
  var stateName = US_STATE_NAMES[code] || code;
  push({ key: '', label: (stateName ? stateName + ' ' : '') + 'state return', jurisdiction: '', rate: 0 });
  (codes || []).forEach(function(c) {
    if (!c || String(c.stateCode || '').toUpperCase() !== code || c.active === false) return;
    salesTaxCodeReturns(c).forEach(push);
  });
  return out;
}

// ── Return form guides (2026-10-16) ─────────────────────────────────────
// Line-by-line "enter this on the return" rows for the File & Record
// modal, Step 2. Keyed by state + return: Colorado's DR 0100 and the
// Town of Vail return are known forms (Cimon filed both 2026-10-16); any
// other return gets the generic four lines. Figures are THIS return's
// share (fig = {grossSales, nonTaxable, taxable, tax, cats}); the
// deduction lines name the categories the project does not tax, so the
// return and the client invoice always use the same base — that is the
// whole reason gross goes on line 1 and freight comes off as a deduction
// instead of line 1 being the taxable figure.
// Row: { line, label, value (number → copyable money) | text (prose) |
// pct (rate), strong }.
export function salesTaxReturnFormLines(stateCode, returnKey, fig, ret) {
  var r2 = function(x) { return Math.round((Number(x) || 0) * 100) / 100; };
  var f = fig || {};
  var gross = r2(f.grossSales), nonTax = r2(f.nonTaxable), taxable = r2(f.taxable), tax = r2(f.tax);
  var dedNames = [];
  TAX_CATEGORY_KEYS.forEach(function(k) {
    var v = f.cats && f.cats.nonTaxable ? f.cats.nonTaxable[k] : 0;
    if (v > 0.005) dedNames.push((TAX_CATEGORY_LABELS[k] || k).toLowerCase());
  });
  if ((f.cats && f.cats.other > 0.005) || (!f.cats && f.other > 0.005)) dedNames.push('other');
  var dedText = dedNames.length ? dedNames.join(', ') + ' — not taxed on the client invoice' : 'none';
  var st = String(stateCode || '').toUpperCase();
  var key = String(returnKey || '').toUpperCase();
  var auths = (ret && ret.authorities) || [];
  var rate = ret && ret.rate ? ret.rate : (f.rate || 0);

  if (st === 'CO' && key === 'VAIL') {
    return [
      { line: '1', label: 'Gross Sales & Service', value: gross },
      { line: '3N', label: 'Other Deductions', value: nonTax, note: dedText },
      { line: '', label: 'Total Deductions', value: nonTax },
      { line: '4', label: 'Total Town Net Taxable Sales & Service', value: taxable },
      { line: '5', label: 'Town Sales Tax (' + (rate * 100).toFixed(1) + '% of line 4)', value: tax, strong: true },
      { line: '6', label: 'Excess Tax Collected', value: 0, note: 'you collected exactly the blended rate on the taxable base' },
      { line: '7', label: 'Adjusted Town Tax', value: tax },
      { line: '14', label: 'Carryout bags dispersed', text: '0' },
      { line: '13', label: 'Total tax, penalty & interest due', value: tax, strong: true },
    ];
  }
  if (st === 'CO' && key === '') {
    // DR 0100 columns. Each authority is placed by name; the column's
    // tax is the portal's own multiply (rate × line 5), so a cent of
    // rounding against Gilda's share is expected and the step says so.
    var COLS = ['State', 'RTD/CD', 'RTA', 'County/MTS', 'City/LID'];
    var colOf = function(name) {
      var n = String(name || '').toLowerCase();
      if (/colorado|\bstate\b/.test(n)) return 'State';
      if (/\brtd\b|cultural|scientific/.test(n)) return 'RTD/CD';
      if (/county|\bmts\b/.test(n)) return 'County/MTS';   // before RTA: "Eagle County Mass Transit (MTS)" is the county column
      if (/\brta\b|transit|transportation|evrt/.test(n)) return 'RTA';
      return 'City/LID';
    };
    var byCol = {};
    auths.forEach(function(a) { var c = colOf(a.name); byCol[c] = (byCol[c] || []).concat([a]); });
    var rows = [
      { line: '1', label: 'Gross sales of goods and services', value: gross },
      { line: '2', label: 'Schedule A deductions (line 14)', value: nonTax, note: dedText },
      { line: '3 · 5', label: 'Net taxable sales — each column with a rate', value: taxable },
    ];
    COLS.forEach(function(c) {
      var list = byCol[c]; if (!list) return;
      var colRate = list.reduce(function(s, a) { return s + (a.rate || 0); }, 0);
      rows.push({ line: '7', label: c + ' column · ' + list.map(function(a) { return a.name + ' ' + (a.rate * 100).toFixed(1) + '%'; }).join(' + '), value: r2(taxable * colRate), pct: colRate });
    });
    rows.push({ line: '12', label: 'Net tax due — all columns', value: tax, strong: true });
    rows.push({ line: '13', label: 'Credit for tax previously paid', text: 'any overpayment from an earlier period goes here' });
    return rows;
  }
  if (st === 'WY' && key === '') {
    // WYIFS Form 41 (Cimon's first return, 2026-10-16): page 2 is one row
    // per jurisdiction (Teton Village = 2290 TVRD) with taxable sales,
    // use-tax purchases and excess; page 3 is the summary where lines 1
    // and 2 must net to page 2's taxable figure or the portal refuses.
    var juris = auths.length ? auths.map(function(a) { return a.name; }).join(' + ') : 'the jurisdiction row';
    return [
      { line: 'p.2', label: 'Jurisdiction row · Amount of Taxable Sales', value: taxable, note: juris + (rate ? ' · ' + (rate * 100).toFixed(2) + '%' : '') },
      { line: 'p.2', label: 'Amount of Purchases Subject to Use Tax', value: 0 },
      { line: 'p.2', label: 'Excess Tax Due', value: 0, note: 'you collected exactly the jurisdiction rate on the taxable base' },
      { line: '1', label: 'Gross Sales and Services', value: gross, note: 'no commas' },
      { line: '2', label: 'Total Deductions', value: nonTax, note: dedText + ' · lines 1 − 2 must equal page 2\u2019s taxable sales' },
      { line: '3', label: 'Net Sales and Services (portal computes)', value: taxable },
      { line: '4 · 6', label: 'Total Taxes Due (portal computes, rate × line 3)', value: tax, strong: true },
      { line: '7', label: 'Vendor Compensation', text: '1.95% up to $500, only when filed AND paid by the 15th; otherwise 0' },
      { line: '8', label: 'Penalties, Interest or Dept. of Revenue Billing', value: 0 },
      { line: '9', label: 'Credit Memo', value: 0 },
      { line: '10', label: 'Total Amount Due', value: tax, strong: true },
    ];
  }
  var generic = [
    { line: '', label: 'Gross sales', value: gross },
    { line: '', label: 'Deductions', value: nonTax, note: dedText },
    { line: '', label: 'Taxable sales', value: taxable },
    { line: '', label: 'Tax' + (rate ? ' (' + (rate * 100).toFixed(rate * 100 % 1 ? 2 : 0) + '%)' : ''), value: tax, strong: true },
  ];
  return generic;
}

// The project-field patch a code produces. Spreading this over a project
// form is the whole "copy into the project" step; the stamp of taxCodeId
// is what lets the form say "matches VAIL" later.
export function projectFieldsFromTaxCode(code) {
  if (!code) return {};
  var out = { taxCodeId: code.id, taxRate: salesTaxCodeBlendedRate(code) };
  TAX_CODE_FLAG_KEYS.forEach(function(k) {
    out[k] = k === 'taxesMerchandise' ? code[k] !== false : !!code[k];
  });
  return out;
}

// Does this project's stored tax config still agree with the code it was
// built from? Rate compared at 6 places; every flag compared as boolean.
export function projectMatchesTaxCode(project, code) {
  if (!project || !code) return false;
  var want = projectFieldsFromTaxCode(code);
  var have = parseFloat(project.taxRate);
  if (isNaN(have) || Math.round(have * 1000000) / 1000000 !== want.taxRate) return false;
  return TAX_CODE_FLAG_KEYS.every(function(k) {
    var pv = k === 'taxesMerchandise' ? project[k] !== false : !!project[k];
    return pv === want[k];
  });
}

// The code a project files under (2026-10-16 fix): the stamp when it has
// one; otherwise, for projects set up before Sales Tax Codes existed
// (26-Ptarmigan), the state's code whose blended rate equals the
// project's rate; otherwise the state's only active code. null = no code
// → the whole tax sits on the state return, which was the bug Cimon saw
// (Town of Vail $0.00).
export function salesTaxCodeForProject(project, codes, stateCode) {
  var list = (codes || []).filter(Boolean);
  if (!list.length) return null;
  if (project && project.taxCodeId) {
    var stamped = list.find(function(c) { return c.id === project.taxCodeId; });
    if (stamped) return stamped;
  }
  var st = String(stateCode || (project && project.state) || '').toUpperCase();
  var inState = list.filter(function(c) { return String(c.stateCode || '').toUpperCase() === st && c.active !== false; });
  var rate = project ? parseFloat(project.taxRate) : NaN;
  if (!isNaN(rate)) {
    var r6 = Math.round(rate * 1000000) / 1000000;
    var byRate = inState.filter(function(c) { return salesTaxCodeBlendedRate(c) === r6; });
    if (byRate.length === 1) return byRate[0];
  }
  return inState.length === 1 ? inState[0] : null;
}

// ── INVOICES MAPPING ─────────────────────────────────────────────────────────
// invoices table — see supabase/migrations/20260425_create_invoices_table.sql
// item_ids and line_items are jsonb; pass arrays directly on save.

export function rowToInvoice(row) {
  return {
    id:             row.id,
    invoiceRef:     row.invoice_ref     || '',
    invoiceType:    row.invoice_type    || 'deposit',
    // Fix D Phase 1.3 — trim + lowercase status at the mapper boundary.
    // Design Manager imports stamp `'Paid'` (capital P) while Gilda-native
    // writes lowercase via derivePaidStatus. Normalizing here lets every
    // consumer use a plain `=== 'paid'` check without thinking about
    // casing. The Phase 1.1 defensive lowercase inside
    // itemUnpaidDepositShare becomes belt-and-suspenders after this.
    status:         String(row.status || 'draft').trim().toLowerCase(),
    proposalId:     row.proposal_id     || '',
    // itemId — set on per-item deposit invoices (sub-commit 1 of the
    // deposit refactor, 2026-05-26). Legacy proposal-level deposits
    // and all finals/reimbursables leave this empty.
    itemId:         row.item_id         || '',
    project:        row.project         || '',
    clientId:       row.client_id       || '',
    clientName:     row.client_name     || '',
    itemIds:        jp(row.item_ids,    []),
    lineItems:      jp(row.line_items,  []),
    productTotal:   parseFloat(row.product_total) || 0,
    feesTotal:      parseFloat(row.fees_total)    || 0,
    // Per-fee breakdown (added 2026-05-12). Legacy rows return 0/[] and
    // re-render falls back to the lump 'Fees & Sales Tax' line.
    feeFreightTot:      parseFloat(row.fee_freight_tot)      || 0,
    feeCratingTot:      parseFloat(row.fee_crating_tot)      || 0,
    feeInstallationTot: parseFloat(row.fee_installation_tot) || 0,
    feeTariffTot:       parseFloat(row.fee_tariff_tot)       || 0,
    feeSalesTaxTot:     parseFloat(row.fee_sales_tax_tot)    || 0,
    feeCustom:          jp(row.fee_custom, []),
    // Card fee — its own column since 2026-09-07 (migration 20260907).
    // NULL is preserved as null rather than coerced to 0: invoiceCcFeeTot
    // needs to tell "this row predates the column" (fall back to the
    // residual derivation) from "this invoice genuinely had no card fee".
    feeCcProcessingTot: row.fee_cc_processing_tot == null
      ? null
      : (parseFloat(row.fee_cc_processing_tot) || 0),
    // Markup dollars (2026-09-08). NULL is preserved as null, not 0 —
    // it means either "row predates the columns" or "this document does
    // not close margin" (deposit invoices never carry a value). 0 would
    // claim the document earned nothing, which is a different fact.
    markupProductTot: row.markup_product_tot == null
      ? null
      : (parseFloat(row.markup_product_tot) || 0),
    markupFeesTot: row.markup_fees_tot == null
      ? null
      : (parseFloat(row.markup_fees_tot) || 0),
    // FIN-scoped adjustments (2026-06-06 patch). Pass-through deltas
    // entered at FIN-generation time. Empty array on rows that predate
    // the patch (default '[]' in the migration handles this server-side).
    adjustments:        jp(row.adjustments, []),
    subtotal:       parseFloat(row.subtotal)      || 0,
    amountDue:      parseFloat(row.amount_due)    || 0,
    // Legacy `amount` column. Populated on Design Manager-migrated rows
    // (Class C) with the original total when `amount_due` was zeroed by
    // close-out shorthand; null/0 on Gilda-native rows. Three consumers
    // already use the `inv.amountDue || inv.amount` fallback pattern;
    // surfacing the field here makes that pattern actually work for
    // mapped invoices (it was dead defensive code without this).
    amount:         parseFloat(row.amount)        || 0,
    billedOnSummaryId: row.billed_on_summary_id || '',
    qbInvoiceId:    row.qb_invoice_id   || '',
    qbEstimateId:   row.qb_estimate_id  || '',
    qbPushedAt:     row.qb_pushed_at    || '',
    qbError:        row.qb_error        || '',
    notes:          row.notes           || '',
    pdfStoragePath: row.pdf_storage_path || '',
    sentAt:         row.sent_at         || '',
    createdBy:      row.created_by      || '',
    createdAt:      row.created_at      || '',
    updatedAt:      row.updated_at      || '',
  };
}

export function invoiceToRow(inv) {
  var id = inv.id;
  if (!id || /^\d+$/.test(String(id))) id = undefined;
  return {
    id:                id,
    invoice_ref:       inv.invoiceRef     || '',
    invoice_type:      inv.invoiceType    || 'deposit',
    status:            inv.status         || 'draft',
    proposal_id:       inv.proposalId     || null,
    item_id:           inv.itemId         || null,
    project:           inv.project        || '',
    client_id:         inv.clientId       || '',
    client_name:       inv.clientName     || '',
    item_ids:          inv.itemIds        || [],
    line_items:        inv.lineItems      || [],
    product_total:     parseFloat(inv.productTotal) || 0,
    fees_total:        parseFloat(inv.feesTotal)    || 0,
    // Per-fee breakdown (added 2026-05-12). Persisted so re-render from
    // the saved row doesn't have to re-derive (and lose) the breakout.
    fee_freight_tot:      parseFloat(inv.feeFreightTot)      || 0,
    fee_crating_tot:      parseFloat(inv.feeCratingTot)      || 0,
    fee_installation_tot: parseFloat(inv.feeInstallationTot) || 0,
    fee_tariff_tot:       parseFloat(inv.feeTariffTot)       || 0,
    fee_sales_tax_tot:    parseFloat(inv.feeSalesTaxTot)     || 0,
    fee_custom:           inv.feeCustom     || [],
    // Card fee (2026-09-07). The sixth per-fee column: without it the
    // re-render had to back-derive the fee, and the sales tax now folded
    // into fee_sales_tax_tot would have no matching frozen fee.
    fee_cc_processing_tot: parseFloat(inv.feeCcProcessingTot) || 0,
    // Markup dollars (2026-09-08) — null-preserving, unlike the fee
    // columns above. Only the final-invoice builder produces these, so a
    // deposit invoice writes NULL rather than a misleading 0.
    markup_product_tot: inv.markupProductTot == null
      ? null : (parseFloat(inv.markupProductTot) || 0),
    markup_fees_tot: inv.markupFeesTot == null
      ? null : (parseFloat(inv.markupFeesTot) || 0),
    adjustments:          inv.adjustments   || [],
    subtotal:          parseFloat(inv.subtotal)     || 0,
    amount_due:        parseFloat(inv.amountDue)    || 0,
    billed_on_summary_id: inv.billedOnSummaryId || null,
    qb_invoice_id:     inv.qbInvoiceId    || '',
    qb_estimate_id:    inv.qbEstimateId   || '',
    qb_pushed_at:      inv.qbPushedAt     || null,
    qb_error:          inv.qbError        || '',
    notes:             inv.notes          || '',
    pdf_storage_path:  inv.pdfStoragePath || '',
    sent_at:           inv.sentAt         || null,
    created_by:        inv.createdBy      || '',
  };
}

// ── CLIENT PAYMENTS + ALLOCATIONS ────────────────────────────────────────
// client_payments is the symmetric counterpart of vendor_payments — every
// real-world bank deposit / wire / check received from a client lives in one
// row. payment_allocations splits a payment across one or more documents
// (invoice, hours_invoice, billing_summary), letting one wire pay several
// things in one event with full audit + QB-native shape.
//
// See supabase/migrations/20260429_create_client_payments.sql.

// Identity-stability comparator for the workbench's applied-credits map
// (2026-08-16). persistRecord rebuilds the map on EVERY autosave; when
// nothing actually changed, the state setter must keep the previous
// object — bsAppliedCredits is a dependency of both the autosave and
// live-preview effects, so an always-fresh identity re-arms the save
// every 1.5s and aborts every preview render before it can finish (the
// infinite save/abort loop that pinned the July 2026 Moskito preview).
// Shape: { [sourceCreditId]: { amount, ledgerRowId } }.
export function appliedCreditsStateEqual(a, b) {
  var ka = Object.keys(a || {});
  var kb = Object.keys(b || {});
  if (ka.length !== kb.length) return false;
  return ka.every(function(k) {
    var x = (a || {})[k];
    var y = (b || {})[k];
    if (!x || !y) return false;
    return String(x.ledgerRowId || '') === String(y.ledgerRowId || '')
      && (parseFloat(x.amount) || 0) === (parseFloat(y.amount) || 0)
      // Statement wording is part of the application (2026-09-07) — a
      // label-only edit must not be swallowed by the identity-stable keep.
      && String(x.label || '') === String(y.label || '');
  });
}

export function rowToClientPayment(row) {
  return {
    id:             row.id,
    clientId:       row.client_id      || '',
    projectLabel:   row.project_label  || '',
    paymentDate:    row.payment_date   || '',
    amountTotal:    parseFloat(row.amount_total) || 0,
    method:         row.method         || '',
    bankReference:  row.bank_reference || '',
    reference:      row.reference      || '',
    notes:          row.notes          || '',
    recordedBy:     row.recorded_by    || '',
    qbPaymentId:    row.qb_payment_id  || '',
    qbPushedAt:     row.qb_pushed_at   || '',
    qbError:        row.qb_error       || '',
    createdAt:      row.created_at     || '',
    updatedAt:      row.updated_at     || '',
  };
}

export function clientPaymentToRow(p) {
  var id = p.id;
  if (!id || /^\d+$/.test(String(id))) id = undefined;
  return {
    id:              id,
    client_id:       p.clientId      || '',
    project_label:   p.projectLabel  || '',
    payment_date:    p.paymentDate   || new Date().toISOString().slice(0, 10),
    amount_total:    parseFloat(p.amountTotal) || 0,
    method:          p.method        || '',
    bank_reference:  p.bankReference || '',
    reference:       p.reference     || '',
    notes:           p.notes         || '',
    recorded_by:     p.recordedBy    || '',
    qb_payment_id:   p.qbPaymentId   || '',
    qb_pushed_at:    p.qbPushedAt    || null,
    qb_error:        p.qbError       || '',
  };
}

export function rowToPaymentAllocation(row) {
  return {
    id:           row.id,
    paymentId:    row.payment_id    || '',
    documentType: row.document_type || '',
    documentId:   row.document_id   || '',
    amount:       parseFloat(row.amount) || 0,
    qbLinkId:     row.qb_link_id    || '',
    qbPushedAt:   row.qb_pushed_at  || '',
    qbError:      row.qb_error      || '',
    createdAt:    row.created_at    || '',
    updatedAt:    row.updated_at    || '',
  };
}

export function paymentAllocationToRow(a) {
  var id = a.id;
  if (!id || /^\d+$/.test(String(id))) id = undefined;
  return {
    id:            id,
    payment_id:    a.paymentId,
    document_type: a.documentType,
    document_id:   a.documentId,
    amount:        parseFloat(a.amount) || 0,
    qb_link_id:    a.qbLinkId || '',
    qb_pushed_at:  a.qbPushedAt || null,
    qb_error:      a.qbError || '',
  };
}

// Sum allocations against a specific document. Returns total paid in dollars.
export function calcPaidForDocument(allocations, documentType, documentId) {
  if (!documentId) return 0;
  return (allocations || [])
    .filter(function(a) { return a.documentType === documentType && a.documentId === String(documentId); })
    .reduce(function(s, a) { return s + (parseFloat(a.amount) || 0); }, 0);
}

// Fix E (2026-05-27) — payment breakdown for the final-invoice PDF.
//
// Surfaces the two subtractions the PDF totals section needs to render
// between Subtotal and Final Balance Due:
//
//   depositPaid   — the implicit deposit subtraction baked into the
//                   per-line balanceTotal math inside buildFinalInvoicePayload.
//                   Computed here as (subtotal − amountDue) so the invariant
//                   subtotal = depositPaid + appliedCredit + finalDue holds
//                   exactly. Using the implicit subtraction (rather than
//                   the raw allocation sum against the matched DEP) preserves
//                   the Kumiko-partial semantic: when the deposit was
//                   under-paid, balanceTotal already over-bills the
//                   shortfall on the final, and subtotal − amountDue
//                   reflects what the client effectively paid via deposit
//                   (= the expected deposit minus the unpaid carry-forward
//                   already added back into amountDue). The raw allocation
//                   sum would lie by the shortfall.
//
//   appliedCredit — sum of payment_allocations recorded against THIS
//                   final invoice's id. The Anne/P-013 shape: deposit
//                   wire was for more than the deposit owed, surplus was
//                   allocated forward to the FIN. Independent of the
//                   deposit math; subtracted again from the displayed
//                   bottom line.
//
// Final Balance Due (rendered bold on the PDF) = subtotal − depositPaid
//                                              − appliedCredit
//                                              = amountDue − appliedCredit
//
// invoiceId argument is the FINAL invoice's id (i.e. the one whose
// allocations we want to sum). For fresh-generation calls the invoice
// doesn't exist yet — pass null/empty and appliedCredit returns 0.
//
// Returns { depositPaid, appliedCredit } — both numbers rounded to cents.
export function computeInvoicePaymentBreakdown(invoiceId, subtotal, amountDue, allocations) {
  var sub = parseFloat(subtotal) || 0;
  var amt = parseFloat(amountDue) || 0;
  var depositPaid = Math.max(0, Math.round((sub - amt) * 100) / 100);

  var appliedCredit = 0;
  if (invoiceId) {
    appliedCredit = (allocations || []).reduce(function(s, a) {
      if (!a) return s;
      var dt = a.documentType != null ? a.documentType : a.document_type;
      if (dt !== 'invoice') return s;
      var docId = a.documentId != null ? a.documentId : a.document_id;
      if (String(docId) !== String(invoiceId)) return s;
      return s + (parseFloat(a.amount) || 0);
    }, 0);
    appliedCredit = Math.round(appliedCredit * 100) / 100;
  }
  return { depositPaid: depositPaid, appliedCredit: appliedCredit };
}

// ── FIN deposit payment history (2026-09-03) ────────────────────────────
// Cimon: the FIN preview must show WHEN the client's deposit money
// actually arrived — on the digital panel (full list) and on the PDF
// (dates on the "Less deposit paid" label). Derivation is pure Rule 10:
// walk the FIN's items → their client_deposit_invoice_id DEPs → the
// payment_allocations recorded against those DEPs → each allocation's
// client_payments row for date + method. NOTE: display-only. The PDF's
// deposit AMOUNT stays computeInvoicePaymentBreakdown's implicit
// subtotal − amountDue (the Kumiko-partial semantic) — this history is
// information about the money, never a replacement for that math.
// Returns [{date:'YYYY-MM-DD', amount, method, depRef}] sorted by date.
// Proposal reference line with totals (2026-09-03, Cimon: "the client
// always pays on proposals, that $ is helpful" — the bookkeeper anchors
// on the proposal's total + deposit ask). Builds the PROPOSAL line for
// the FIN PDF's gray internal-reference block: single proposal with
// stored totals → "26HOB-P-042 · total $2,426.88 · deposit asked
// $2,425.68"; several proposals or no totals → the plain ref join.
// Tolerates raw and mapped proposal row shapes.
export function proposalRefLineWithTotals(proposalRefs, proposals) {
  var refs = (proposalRefs || []).filter(Boolean);
  if (refs.length === 0) return '';
  var plain = refs.join(' · ');
  if (refs.length !== 1) return plain;
  var prop = (proposals || []).find(function(p) {
    return p && (p.proposal_ref === refs[0] || p.proposalRef === refs[0]);
  });
  if (!prop) return plain;
  var tot = parseFloat(prop.total_client != null ? prop.total_client : prop.totalClient) || 0;
  var dep = parseFloat(prop.total_deposit != null ? prop.total_deposit : prop.totalDeposit) || 0;
  if (tot <= 0) return plain;
  var line = plain + ' · total ' + fmtMoney(tot);
  if (dep > 0) line += ' · deposit asked ' + fmtMoney(dep);
  return line;
}

export function finDepositPaymentHistory(inv, allItems, invoices, allocations, clientPayments) {
  if (!inv) return [];
  var itemIds = (inv.itemIds || inv.item_ids || []).map(String);
  var depIds = {};
  (allItems || []).forEach(function(it) {
    if (!it || itemIds.indexOf(String(it.id)) < 0) return;
    var depId = it.clientDepositInvoiceId || it.client_deposit_invoice_id;
    if (depId) depIds[String(depId)] = true;
  });
  var rows = [];
  (allocations || []).forEach(function(a) {
    if (!a) return;
    var dt = a.documentType != null ? a.documentType : a.document_type;
    if (dt !== 'invoice') return;
    var docId = String(a.documentId != null ? a.documentId : a.document_id);
    if (!depIds[docId]) return;
    var payId = a.paymentId != null ? a.paymentId : a.payment_id;
    var pay = (clientPayments || []).find(function(cp) { return cp && String(cp.id) === String(payId); });
    var dep = (invoices || []).find(function(x) { return x && String(x.id) === docId; });
    rows.push({
      date:   String((pay && (pay.paymentDate || pay.payment_date)) || '').slice(0, 10),
      amount: Math.round((parseFloat(a.amount) || 0) * 100) / 100,
      method: (pay && pay.method) || '',
      depRef: (dep && (dep.invoiceRef || dep.invoice_ref)) || '',
    });
  });
  rows.sort(function(x, y) { return String(x.date).localeCompare(String(y.date)); });
  return rows;
}

// Returns the lifecycle status implied by the allocations against this doc.
//   'paid'    when sum >= amountDue
//   'partial' when 0 < sum < amountDue
//   'draft'   when sum is 0 and the doc was already 'draft'
//   'sent'    otherwise
export function derivePaidStatus(allocations, documentType, documentId, amountDue, currentStatus) {
  var paid = calcPaidForDocument(allocations, documentType, documentId);
  var amt = parseFloat(amountDue || 0);
  if (paid >= amt - 0.005 && amt > 0) return 'paid';
  if (paid > 0) return 'partial';
  // Preserve a deliberate $0 closure. A FIN explicitly closed at $0 by
  // the bookkeeper (100%-upfront proposal where DEP collected everything;
  // closeFinAtZero in InvoicesTab) sits at amount_due=0, status='paid'.
  // Without this clause, any later re-derivation (e.g., an unrelated
  // allocation event triggering syncInvoiceStatusFromAllocations) would
  // walk it back to 'sent'. 2026-06-13.
  if (currentStatus === 'paid' && amt === 0) return 'paid';
  return currentStatus === 'draft' ? 'draft' : 'sent';
}

// Resolves the per-document payment view shown in the PaymentsPanel by
// joining allocations to their parent client_payments. Returns a list of
// entries shaped like the legacy payment_log entries (PaymentsPanel reads
// this shape), one per allocation.
export function paymentEntriesForDocument(allocations, payments, documentType, documentId) {
  if (!documentId) return [];
  var byPaymentId = {};
  (payments || []).forEach(function(p) { byPaymentId[p.id] = p; });
  return (allocations || [])
    .filter(function(a) { return a.documentType === documentType && a.documentId === String(documentId); })
    .map(function(a) {
      var p = byPaymentId[a.paymentId] || {};
      return {
        id:            a.id,
        paymentId:     a.paymentId,
        amount:        a.amount,
        date:          p.paymentDate || '',
        method:        p.method || '',
        reference:     p.reference || '',
        bankReference: p.bankReference || '',
        notes:         p.notes || '',
        recordedBy:    p.recordedBy || '',
        qbPaymentId:   p.qbPaymentId || '',
      };
    });
}

// ── Record Client Payment wizard — allocation core ─────────────────────────
//
// The unified payment wizard records ONE client_payments row and splits it
// across the underlying documents a billing-summary / off-cycle package
// bundles (deposit invoices, final invoices, hours invoices) — never against
// the container itself (CLAUDE.md: allocate to underlying docs). These two
// pure helpers are the wizard's brain; the component renders them.
//
// A `target` is one allocatable document:
//   { documentType, documentId, label, asked, balance }
// where balance = asked − calcPaidForDocument(allocations, type, id).

// Greedy auto-fill: walk targets in order, filling each up to its remaining
// balance until the payment is exhausted. Returns the allocation array
// shaped for recordClientPayment: [{ documentType, documentId, amount }].
export function autoAllocatePayment(amountTotal, targets) {
  var remaining = Math.round((parseFloat(amountTotal) || 0) * 100) / 100;
  var out = [];
  (targets || []).forEach(function(t) {
    if (!t || remaining <= 0.005) return;
    var bal = Math.round((parseFloat(t.balance) || 0) * 100) / 100;
    if (bal <= 0.005) return;
    var take = Math.round(Math.min(remaining, bal) * 100) / 100;
    if (take > 0.005) {
      out.push({ documentType: t.documentType, documentId: String(t.documentId), amount: take });
      remaining = Math.round((remaining - take) * 100) / 100;
    }
  });
  return out;
}

// Resolve the wizard's allocatable targets from a list of raw candidates.
// Each candidate is { documentType, documentId, label, asked } plus an
// optional `ref` (the invoice reference, shown muted under the friendly
// name so the bookkeeper can cross-reference — names first per Cimon's
// 2026-07-01 direction). This computes the remaining balance (asked −
// already-allocated, via calcPaidForDocument) and drops anything already
// fully paid. Keeps the component thin + the balance/filter logic tested
// in one place.
// ── Credit-funded client payments (2026-09-27) ────────────────────────────
// A client credit applied to a billing summary lives in client_credit_ledger
// (applied_to_billing_summary, stored negative) and is netted from the
// package's ASKED. To settle the underlying invoices it must ALSO reach
// payment_allocations, and every allocation hangs off a client_payments
// row — so the wizard records the placed credit as its own payment with
// this method. It is not cash: resolveBillingPackage nets it back out of
// the package's PAID, and the Payments ledger shows the method so Dan can
// tell it from a wire.
export var CLIENT_CREDIT_PAYMENT_METHOD = 'Client credit';

export function isCreditFundedPayment(payment) {
  return !!payment && String(payment.method || '') === CLIENT_CREDIT_PAYMENT_METHOD;
}

// Sum of allocations on the given documents (keys 'documentType|documentId')
// whose parent payment is credit-funded.
export function calcCreditFundedPaid(allocations, payments, docKeys) {
  var creditIds = {};
  (payments || []).forEach(function(p) { if (isCreditFundedPayment(p) && p.id) creditIds[String(p.id)] = true; });
  var keys = {};
  (docKeys || []).forEach(function(k) { keys[k] = true; });
  return (allocations || []).reduce(function(sum, a) {
    if (!a || !creditIds[String(a.paymentId || '')]) return sum;
    if (!keys[a.documentType + '|' + String(a.documentId)]) return sum;
    return sum + (parseFloat(a.amount) || 0);
  }, 0);
}

// Split one wizard allocation list into the credit-funded part and the
// bank-funded part. The credit takes documents in list order (the wizard
// lists invoices first — the credit was netted from the procurement ask)
// until it runs out; the bank payment takes whatever is left of each.
// Returns { credit: [...], bank: [...] } with the same allocation shape.
export function splitAllocationsByFunding(allocations, creditAmount) {
  function r2(x) { return Math.round((parseFloat(x) || 0) * 100) / 100; }
  var left = Math.max(0, r2(creditAmount));
  var credit = [], bank = [];
  (allocations || []).forEach(function(a) {
    if (!a) return;
    var amt = r2(a.amount);
    if (amt <= 0.005) return;
    var take = Math.min(left, amt);
    if (take > 0.005) {
      credit.push(Object.assign({}, a, { amount: r2(take) }));
      left = r2(left - take);
    }
    var rest = r2(amt - take);
    if (rest > 0.005) bank.push(Object.assign({}, a, { amount: rest }));
  });
  return { credit: credit, bank: bank };
}

export function buildPaymentTargets(candidates, allocations) {
  return (candidates || []).map(function(c) {
    var asked = Math.round((parseFloat(c.asked) || 0) * 100) / 100;
    var paid = calcPaidForDocument(allocations, c.documentType, c.documentId);
    var balance = Math.round((asked - paid) * 100) / 100;
    return {
      documentType: c.documentType,
      documentId: String(c.documentId),
      label: c.label,
      ref: c.ref || '',
      asked: asked,
      balance: balance,
    };
  }).filter(function(t) { return t.balance > 0.005; });
}

// Asking amount on a billing_summaries row — what the client owes for the
// period it covers. Used by the Bank Deposit modal (open balance list)
// and the InvoicesTab onBankDepositSaved auto-draw fallback. The row
// argument is the raw snake_case row from supabase.
//
// For non-retainer projects: design_amount + additional_services + expenses.
//   Reimbursable receipts selected via the picker appear in the cover's
//   "Services & Expenses" block as informational only — they don't fold
//   into the asking amount because the client isn't billed for them
//   through the retainer (the picker on non-retainer projects is hidden
//   anyway; this branch is defensive).
//
// For retainer projects (since 2026-05-18): design_amount + additional_services
//   + expenses + the sum of selected reimbursable receipts (marked up
//   cost × 1.15). On retainer projects the cover folds reimbursables
//   into the "Services & Expenses" block — they draw from the retainer
//   like Design Services and Additional Services. So the summary's
//   asking amount must include them to match what the client sees.
//
// `receipts` is the full receipts array (typically AppContext.receipts).
// `projectHasRetainer` toggles whether reimbursables fold in. Defaults
// (undefined receipts, projectHasRetainer=false) preserve the
// non-retainer-project behavior for any caller that doesn't have the
// receipts state on hand.
//
// Receipt-selection model (since 2026-05-18): the billing_summaries row
// carries `selected_receipt_ids` (jsonb array of receipt UUIDs the
// bookkeeper ticked on the draft picker). The asking amount sums the
// marked-up totals (cost × 1.15) of those receipts at the current
// receipts state — so editing a receipt's notes/amount post-selection
// updates the asking amount on next render. The selection stores ids
// only, never snapshots.
//
// LOCKSTEP INVARIANT — this helper and BillingSummarySection's
// `bsSubtotal` must agree to the cent for the same summary. The retainer
// auto-draw uses both: BankDepositModal + InvoicesTab.onBankDepositSaved
// read this helper, BillingSummarySection.onPanelRecordPayment reads
// bsSubtotal. Drift = wrong draw amount silently. Guard test:
// __tests__/calcBillingSummaryAmountReceipts.test.js.
// ── Statement expense presentation (2026-08-23) ────────────────────────────
// Per-summary presentation setting stored on billing_summaries.
// expenses_display (jsonb). When consolidate is true the statement's
// PROJECT EXPENSES group renders as ONE line (label + summed amount)
// instead of itemized receipt/expense rows — presentation only, every
// total is computed from the same underlying lines. Tolerates strings
// (legacy jsonb round-trips) and absent/null (the default: itemized).
export var DEFAULT_CONSOLIDATED_EXPENSES_LABEL = 'Project expenses';

// Since 2026-08-30 expenses_display also carries `groups` — named lines
// each folding a chosen set of ticked receipts into one statement row
// ("Travel — market trip" = airline + hotel; storage stays its own row).
// Cimon: combine some receipts but keep others separate. groups apply
// when consolidate is FALSE (consolidate=true still folds EVERYTHING
// into the single line and wins). Shape: [{id, label, receiptIds:[]}].
export function normalizeExpensesDisplay(raw) {
  var v = raw;
  if (typeof v === 'string') {
    try { v = JSON.parse(v); } catch (e) { v = null; }
  }
  if (!v || typeof v !== 'object') return { consolidate: false, label: '', groups: [] };
  var groups = Array.isArray(v.groups) ? v.groups : [];
  return {
    consolidate: !!v.consolidate,
    label: String(v.label || '').trim(),
    groups: groups
      .filter(function(g) { return g && typeof g === 'object'; })
      .map(function(g, i) {
        return {
          // Derived ids use an 'auto' prefix so a legacy row whose FIRST
          // group has an explicit 'g1' can't collide with a derived id.
          id: String(g.id || 'auto' + i),
          label: String(g.label || '').trim(),
          receiptIds: (Array.isArray(g.receiptIds) ? g.receiptIds : [])
            .map(function(id) { return String(id); }).filter(Boolean),
        };
      }),
  };
}

// Fold expense groups into the statement's receipt lines. Pure —
// presentation only: the sum of the returned lines' amounts equals the
// sum of the input lines' amounts exactly (the invariant the guard test
// pins). Each group with at least one matching ticked receipt renders as
// ONE line (label + summed amount); lines no group claims stay itemized
// in their original order. A receipt claimed by two groups counts only
// in the first (normalize order); empty labels fall back to
// DEFAULT_CONSOLIDATED_EXPENSES_LABEL so a nameless group can't print a
// blank row.
export function applyExpenseGroupsToReceiptLines(lines, groups) {
  var src = Array.isArray(lines) ? lines : [];
  var gs = Array.isArray(groups) ? groups : [];
  if (gs.length === 0) return src.slice();
  // Object.create(null), not {} — a receipt id of 'constructor' (or any
  // Object.prototype key) would otherwise read as already-claimed off the
  // prototype chain and silently drop that receipt from the itemized
  // remainder, breaking the sum-preservation invariant below. Ids are uuids
  // in practice; the invariant should not depend on that.
  var claimed = Object.create(null);
  var groupLines = [];
  gs.forEach(function(g, gi) {
    var members = src.filter(function(ln) {
      return ln && !claimed[ln.receiptId]
        && (g.receiptIds || []).indexOf(String(ln.receiptId)) >= 0;
    });
    if (members.length === 0) return;
    members.forEach(function(ln) { claimed[ln.receiptId] = true; });
    var amount = 0, cost = 0;
    members.forEach(function(ln) {
      amount += parseFloat(ln.amount) || 0;
      cost   += parseFloat(ln.cost)   || 0;
    });
    groupLines.push({
      receiptId:   '',
      // groupId carries the source group's id onto the folded line. Two
      // reasons: it is the only stable react key for a grouped row (every
      // folded line has receiptId '', so keying on receiptId collides the
      // moment a summary has 2+ groups), and it lets a consumer tie the
      // line back to expenses_display.groups to resolve its members.
      //
      // Falls back to the positional 'auto{i}' id — the same convention
      // normalizeExpensesDisplay uses — so an id-less group can never emit
      // groupId '' and be mistaken by a consumer for an itemized row (which
      // would re-open the very key collision groupId exists to close).
      groupId:     String(g.id || ('auto' + gi)),
      date:        '',
      vendor:      '',
      description: (g.label || '').trim() || DEFAULT_CONSOLIDATED_EXPENSES_LABEL,
      cost:        Math.round(cost * 100) / 100,
      amount:      Math.round(amount * 100) / 100,
      groupedReceiptIds: members.map(function(ln) { return ln.receiptId; }),
    });
  });
  var itemized = src.filter(function(ln) { return ln && !claimed[ln.receiptId]; });
  return groupLines.concat(itemized);
}

// Resolve the individual receipts behind one folded expense-group line, for
// the bookkeeper-facing disclosure in Billing History (2026-09-04).
//
// The client's statement shows a group as ONE named line. The bookkeeper has
// to be able to answer "which receipts were combined into that, and which
// billing summary paid them" months later — so this returns the members.
//
// TWO SOURCES, and the difference is load-bearing:
//   - LIVE   — read from the receipts table via buildSummaryReceiptLines.
//              Carries vendor + cost, but descriptions are live-read from
//              receipts.notes, so a later edit changes what this shows.
//   - FROZEN — the as-sent `servicesAndExpenses.receiptLinesDetail` off the
//              document_sends payload. Immutable: what the client was
//              ACTUALLY billed. The key first shipped 2026-08-30, so sends
//              before that have NO frozen detail at all (not a partial
//              shape); rows frozen between then and 2026-09-04 carry
//              {receiptId, description, date, amount} without vendor/cost.
//              Both cases degrade to the live read with no drift detection.
//
// Live wins for display (it is the richer row); frozen is the fallback when a
// receipt has since been deleted or untagged, and the referee when the two
// disagree on money. A disagreement is surfaced, never silently reconciled —
// same honesty rule as resolveBillingPackage's manifestMismatch.
//
// `frozenGroup` is the SAME group as it was frozen at send time, off the
// payload's `expensesDisplay.groups`. It is what makes drift detectable and
// it must be passed for any package that has a send record. Without it the
// caller's group membership is derived from LIVE receipts, so a receipt
// deleted or absorbed after the send has already vanished from
// `line.groupedReceiptIds` before this function is reached — the member list
// silently shrinks, and comparing the live sum against the live line's own
// amount compares a number to itself and always agrees.
//
// Returns { members, memberTotal, memberCostTotal, foldedTotal, asSentTotal,
//           totalsAgree, hasDrift }.
//   memberCostTotal — the raw vendor cost behind the group. The billed
//                 figure is cost x 1.15, so the bookkeeper reconciles
//                 against THIS number, not the billed one.
//   asSentTotal — what the client was actually billed for this group, or
//                 null when there is no frozen manifest to say.
//   totalsAgree — memberTotal vs asSentTotal. Only meaningful when a frozen
//                 manifest exists; true by default when it does not, so an
//                 unknowable case never renders as an alarm.
//   hasDrift    — a member's amount disagrees with the as-sent figure, or a
//                 member is no longer on file. Deliberately NOT "came from
//                 the frozen manifest" — resolving from frozen with no
//                 disagreement is a clean read, not a problem.
export function expenseGroupMembers(line, allReceipts, frozenReceiptLinesDetail, frozenGroup) {
  var liveIds = (line && Array.isArray(line.groupedReceiptIds)) ? line.groupedReceiptIds : [];
  var frozen = Array.isArray(frozenReceiptLinesDetail) ? frozenReceiptLinesDetail : [];
  var frozenFor = function(id) {
    return frozen.find(function(d) { return d && String(d.receiptId) === String(id); }) || null;
  };
  // The as-sent membership: the frozen group's ids, narrowed to those the
  // frozen manifest actually carries. The narrowing matters — unticking a
  // receipt in the picker never removes it from expenses_display.groups, so
  // groups accumulate stale ids that were never billed on this summary.
  var frozenIds = (frozenGroup && Array.isArray(frozenGroup.receiptIds) ? frozenGroup.receiptIds : [])
    .map(function(id) { return String(id); })
    .filter(function(id) { return !!frozenFor(id); });
  // Union, as-sent order first: a member that has since been deleted or
  // absorbed is still part of this group's history and must still show.
  var ids = frozenIds.slice();
  liveIds.forEach(function(id) {
    if (ids.indexOf(String(id)) < 0) ids.push(String(id));
  });
  var members = ids.map(function(id) {
    var live = buildSummaryReceiptLines([id], allReceipts)[0] || null;
    var fz   = frozenFor(id);
    if (!live && !fz) {
      // Neither source knows this id — the receipt was hard-deleted before
      // any send froze it. Name the gap rather than dropping the row.
      return {
        receiptId: String(id), date: '', vendor: '', description: '(receipt no longer on file)',
        cost: 0, amount: 0, source: 'missing', mismatch: false,
      };
    }
    if (!live) {
      return {
        receiptId: String(id),
        date: fz.date || '', vendor: fz.vendor || '',
        description: fz.description || 'Reimbursable expense',
        cost: parseFloat(fz.cost) || 0,
        amount: parseFloat(fz.amount) || 0,
        source: 'frozen', mismatch: false,
      };
    }
    var drift = !!fz && Math.abs((parseFloat(fz.amount) || 0) - (parseFloat(live.amount) || 0)) > 0.005;
    return {
      receiptId: live.receiptId, date: live.date, vendor: live.vendor,
      description: live.description, cost: live.cost, amount: live.amount,
      source: 'live', mismatch: drift,
      asSentAmount: drift ? (parseFloat(fz.amount) || 0) : null,
    };
  });
  var memberTotal = Math.round(members.reduce(function(s, m) {
    return s + (parseFloat(m.amount) || 0);
  }, 0) * 100) / 100;
  // Raw vendor cost alongside the billed figure. The billed number is the
  // receipt at cost × 1.15, which is NOT what the vendor's paperwork says —
  // the bookkeeper matches on the cost, so it has to be first-class here,
  // not a faint annotation. 0 when no member carries a cost (frozen rows
  // from before vendor/cost joined the manifest on 2026-09-04).
  var memberCostTotal = Math.round(members.reduce(function(s, m) {
    return s + (parseFloat(m.cost) || 0);
  }, 0) * 100) / 100;
  var foldedTotal = Math.round((parseFloat(line && line.amount) || 0) * 100) / 100;
  // Sum the as-sent figures over the as-sent membership only. null (not 0)
  // when there is no frozen manifest — 0 would read as "the client was
  // billed nothing", which is a different and false claim.
  var asSentTotal = frozenIds.length
    ? Math.round(frozenIds.reduce(function(s, id) {
        var fz = frozenFor(id);
        return s + (parseFloat(fz && fz.amount) || 0);
      }, 0) * 100) / 100
    : null;
  return {
    members:         members,
    memberTotal:     memberTotal,
    memberCostTotal: memberCostTotal,
    foldedTotal:     foldedTotal,
    asSentTotal: asSentTotal,
    totalsAgree: asSentTotal == null ? true : Math.abs(memberTotal - asSentTotal) < 0.005,
    hasDrift:    members.some(function(m) { return m.mismatch || m.source === 'missing'; }),
  };
}

export function calcBillingSummaryAmount(row, receipts, projectHasRetainer) {
  if (!row) return 0;
  var design = parseFloat(row.design_amount) || 0;
  var addl = 0;
  var addlList = row.additional_services;
  if (typeof addlList === 'string') {
    try { addlList = JSON.parse(addlList); } catch (e) { addlList = []; }
  }
  (addlList || []).forEach(function(s) {
    addl += parseFloat(s && s.amount) || 0;
  });
  var exp = 0;
  var expList = row.expenses;
  if (typeof expList === 'string') {
    try { expList = JSON.parse(expList); } catch (e) { expList = []; }
  }
  (expList || []).forEach(function(e) {
    exp += parseFloat(e && e.amount) || 0;
  });
  var reimb = 0;
  // Receipts count for BOTH billing models (2026-07-01) — see the
  // bsSubtotal comment in BillingSummarySection.jsx. projectHasRetainer
  // stays in the signature for caller compatibility; it no longer gates
  // the receipt sum.
  if (Array.isArray(receipts)) {
    var ids = row.selected_receipt_ids;
    if (typeof ids === 'string') {
      try { ids = JSON.parse(ids); } catch (e) { ids = []; }
    }
    if (!Array.isArray(ids)) ids = [];
    ids.forEach(function(rid) {
      var r = receipts.find(function(x) { return x && x.id === rid; });
      if (!r) return;
      // Same predicate as buildSummaryReceiptLines — the cover lines and
      // this sum must agree to the cent (retainer-draw lockstep).
      if (receiptIsAbsorbed(r)) return;
      var cost = parseFloat(r.totalAmount) || 0;
      reimb += applyReimbursableMarkup(cost, receiptMarkupPct(r));
    });
  }
  return Math.round((design + addl + exp + reimb) * 100) / 100;
}

// Total Due on the Billing Summary cover. The math differs by billing model:
//
//   Retainer projects → Total Due = retainerRequest only. The work
//     itself (Subtotal: Design + Additional + Project Expenses) is paid
//     out of the existing retainer balance, not billed on top of the
//     retainer top-up. The client owes only the replenishment when the
//     balance falls below threshold.
//
//   Non-retainer projects → Total Due = subtotal. No retainer to draw
//     from; the work is billed directly.
//
// Pre-2026-05-09: the formula was `subtotal + retainerRequest` for
// retainer projects, which double-billed (the worked example from the
// audit: $51,750.30 + $60,000 = $111,750.30 displayed, vs. the correct
// $60,000). Guard test: __tests__/billingSummaryRetainer.test.jsx.
export function calcBillingSummaryTotalDue(args) {
  var a = args || {};
  var subtotal = parseFloat(a.subtotal) || 0;
  var retainerRequest = parseFloat(a.retainerRequest) || 0;
  if (a.projectHasRetainer) return retainerRequest;
  return subtotal;
}

// Effective category data for a Designer Hours invoice. Applies per-entry
// adjusted_hours overrides (keyed `${cat.name}__${entry.togglName}`) on top
// of the raw Toggl-derived categories_snapshot. Returns the per-category
// effective hours/amount + the totals.
//
// Single source of truth for the EFFECTIVE numbers shown on the PDF, the
// billing summary cover, and (since 2026-05-11) the QB Invoice line items
// pushed from api/qb-push-hours-invoice.js. Without this shared helper the
// PDF and QB Invoice could disagree by a few decimals when adjustments are
// applied — and Dan would never know which surface to trust.
//
// Pure: no React state, no DB call. Inputs are the raw arrays already
// stored on `hours_invoices.categories_snapshot` + `.adjusted_hours`.
export function computeEffectiveCategoryData(args) {
  var a = args || {};
  var snapshot = Array.isArray(a.categoriesSnapshot) ? a.categoriesSnapshot : [];
  var adj      = a.adjustedHours && typeof a.adjustedHours === 'object' ? a.adjustedHours : {};
  var categories = snapshot.map(function(cat) {
    var entries = Array.isArray(cat && cat.entries) ? cat.entries : [];
    var effEntries = entries.map(function(e) {
      var key  = (cat && cat.name) + '__' + (e && e.togglName);
      var raw  = e && e.hours;
      var over = adj[key];
      var hrs  = (over != null && over !== '') ? Number(over) : Number(raw);
      if (!isFinite(hrs)) hrs = 0;
      var rate = Number(e && e.rate) || 0;
      return Object.assign({}, e, {
        effHrs:    hrs,
        effAmount: Math.round(hrs * rate * 100) / 100,
      });
    });
    var effHours  = Math.round(effEntries.reduce(function(s, e) { return s + (e.effHrs    || 0); }, 0) * 100) / 100;
    var effAmount = Math.round(effEntries.reduce(function(s, e) { return s + (e.effAmount || 0); }, 0) * 100) / 100;
    return Object.assign({}, cat, { effEntries: effEntries, effHours: effHours, effAmount: effAmount });
  });
  var totalHours  = Math.round(categories.reduce(function(s, c) { return s + (c.effHours  || 0); }, 0) * 100) / 100;
  var totalAmount = Math.round(categories.reduce(function(s, c) { return s + (c.effAmount || 0); }, 0) * 100) / 100;
  return { categories: categories, totalHours: totalHours, totalAmount: totalAmount };
}

// ── Person-grouped hours billing (2026-08-23 — the Kubik switch) ──────────
// Some clients get their Designer Hours invoice grouped BY PERSON (the
// "Detail of Time Charges" format Kubik receives from other firms: one
// row per team member — name · short description · hours · rate ·
// amount) instead of by project phase. Per-client setting:
// client.hoursGrouping === 'person' (default/absent = 'phase').
//
// This pivots computeEffectiveCategoryData output into the SAME
// {categories, totalHours, totalAmount} shape, so the editor, the PDF
// payload, and the save path all consume it unchanged:
//   - one "category" per person (name = the person's name, per Cimon:
//     real names, like the exemplar), sorted rate desc then hours desc;
//   - each carries ONE entry (togglName = the person) so the editor's
//     per-entry hour-adjustment input works: its key is
//     `${name}__${name}`, read here as the person-level override;
//   - timeEntries concatenate across phases (feeds the narrative
//     summarizer); rate/role carry through; amounts recompute
//     hours × rate and round once.
// Category-level adjustments already applied by
// computeEffectiveCategoryData flow in via effHrs; a person-level
// override then replaces that person's summed hours.
export function groupHoursByPersonAsCategories(effCategories, adjustedHours) {
  var adj = adjustedHours && typeof adjustedHours === 'object' ? adjustedHours : {};
  var byName = {};
  var order = [];
  (effCategories || []).forEach(function(cat) {
    var entries = (cat && (cat.effEntries || cat.entries)) || [];
    entries.forEach(function(e) {
      if (!e || !e.togglName) return;
      var hrs = e.effHrs != null ? Number(e.effHrs) : (Number(e.hours) || 0);
      if (!(hrs > 0)) return;
      var name = String(e.togglName);
      var row = byName[name];
      if (!row) {
        row = byName[name] = {
          name: name,
          role: e.role || '',
          rate: Number(e.rate) || 0,
          rawHours: 0,
          timeEntries: [],
        };
        order.push(name);
      }
      row.rawHours += hrs;
      (e.timeEntries || []).forEach(function(t) { row.timeEntries.push(t); });
    });
  });
  var categories = order.map(function(name) {
    var r = byName[name];
    var raw = Math.round(r.rawHours * 100) / 100;
    var over = adj[name + '__' + name];
    var hrs = (over != null && over !== '') ? Number(over) : raw;
    if (!isFinite(hrs)) hrs = 0;
    var amt = Math.round(hrs * r.rate * 100) / 100;
    var entry = {
      togglName: name,
      role: r.role,
      rate: r.rate,
      hours: raw,
      effHrs: hrs,
      effAmount: amt,
      timeEntries: r.timeEntries,
    };
    return {
      name: name,
      rate: r.rate,
      entries: [entry],
      effEntries: [entry],
      effHours: hrs,
      effAmount: amt,
      // Raw-pull parity: the editor's visibility filter reads
      // cat.totalHours (the pull's per-category field) — without it,
      // every person row filtered out and the pull looked dead
      // (Hoback, 2026-08-23).
      totalHours: raw,
      totalAmount: amt,
    };
  });
  categories.sort(function(a, b) {
    if (b.rate !== a.rate) return b.rate - a.rate;
    if (b.effHours !== a.effHours) return b.effHours - a.effHours;
    return a.name.localeCompare(b.name);
  });
  var totalHours  = Math.round(categories.reduce(function(s, c) { return s + (c.effHours  || 0); }, 0) * 100) / 100;
  var totalAmount = Math.round(categories.reduce(function(s, c) { return s + (c.effAmount || 0); }, 0) * 100) / 100;
  return { categories: categories, totalHours: totalHours, totalAmount: totalAmount };
}

// The per-client grouping setting, tolerant of legacy client rows.
export function clientHoursGrouping(client) {
  return client && client.hoursGrouping === 'person' ? 'person' : 'phase';
}

// Items eligible for inclusion in a proposal's item picker.
// Always re-includes items already on this proposal (re-editing case).
// Excludes items locked to a different active proposal.
// Otherwise: "Presented to Client" status + matching project only.
export function eligibleForProject(projectLabel, allItems, currentProposalItemIds, currentProposalId, proposals) {
  var originalItems = currentProposalItemIds || [];
  return (allItems || []).filter(function(it) {
    if (it.parentId) return false;
    if (originalItems.indexOf(it.id) >= 0) return true;
    // An item that already points at THIS proposal is always re-addable,
    // whatever its status — recovers orphans left when a proposal's
    // item_ids was wiped but items kept proposal_id (26HOB-P-069, 2026-10-05).
    if (currentProposalId && it.proposalId === currentProposalId) return true;
    if (it.proposalId && it.proposalId !== currentProposalId) {
      var owningProposal = (proposals || []).find(function(p) { return p.id === it.proposalId; });
      if (owningProposal && (owningProposal.items || []).indexOf(it.id) >= 0) return false;
    }
    var matchesProject = !projectLabel || it.project === projectLabel;
    // OF/BO (2026-07-29): their identity is the classification, not a
    // workflow status — they join proposals ($0 sign-off lines) at any
    // point before approval, physical stages included. Standard items
    // keep the Presented-to-Client gate.
    if (isNonProcured(it.procurementClass)) {
      return matchesProject && proposalEligible(it);
    }
    return it.status === 'Presented to Client' && matchesProject;
  });
}

// ── Extracted from ItemDatabase ───────────────────────────────────────────────

function _getItemDate(it, field) {
  if (field === 'created')  return it.date || it.importDate || '';
  if (field === 'ordered')  return it.orderPlacedDate || '';
  if (field === 'received') return it.receivedDate || '';
  if (field === 'status')   return it.receivedDate || it.orderPlacedDate || it.paymentReceivedDate || it.clientApprovedDate || it.sentToAustinDate || it.date || '';
  return '';
}

function _dateInRange(dateStr, quick, from, to) {
  if (!dateStr) return quick === '' && from === '' && to === '';
  var d = new Date(dateStr);
  if (isNaN(d.getTime())) return true;
  var now = new Date();
  if (quick === 'week')    { var w = new Date(now); w.setDate(now.getDate() - 7);  return d >= w; }
  if (quick === 'month')   { var m = new Date(now); m.setDate(now.getDate() - 30); return d >= m; }
  if (quick === 'quarter') { var q = new Date(now); q.setDate(now.getDate() - 90); return d >= q; }
  if (from && to) return d >= new Date(from) && d <= new Date(to);
  if (from) return d >= new Date(from);
  if (to)   return d <= new Date(to);
  return true;
}

// Filter a flat items array to parent items matching the given filter criteria.
// Automatically excludes component items (parentId set).
export function filterItems(items, filters) {
  var f            = filters || {};
  var schedFilter  = f.schedFilter  !== undefined ? f.schedFilter  : 'All';
  var projFilter   = f.projFilter   || '';
  var roomFilter   = f.roomFilter   || '';
  var statusFilter = f.statusFilter !== undefined ? f.statusFilter : 'All';
  var search       = f.search       || '';
  var hasDateFilter = f.hasDateFilter || false;
  var dateField    = f.dateField    || 'all';
  var dateQuick    = f.dateQuick    || '';
  var dateFrom     = f.dateFrom     || '';
  var dateTo       = f.dateTo       || '';

  return (items || []).filter(function(it) {
    if (it.parentId) return false;
    var matchSched  = schedFilter === 'All' || it.schedule === schedFilter;
    var matchProj   = !projFilter   || it.project   === projFilter;
    var matchRoom   = !roomFilter   || it.roomName  === roomFilter;
    var matchStatus = statusFilter === 'All' ||
      (statusFilter === '__spec__'      ? ZONE_SPEC.indexOf(it.status)      >= 0 :
       statusFilter === '__quote__'     ? ZONE_QUOTE.indexOf(it.status)     >= 0 :
       statusFilter === '__committed__' ? ZONE_COMMITTED.indexOf(it.status) >= 0 :
       it.status === statusFilter);
    var _s = search.toLowerCase();
    var matchSearch = !search || [
      it.item, it.vendor, it.productName, it.notes, it.roomName, it.roomNumber,
      it.project, it.status, it.sidemark, it.schedule, it.poNumber, it.leadTime,
      it.installer, it.statusNotes, it.sourceUrl, it.netPrice, it.clientPrice,
      it.importSource, it.itemNumber, it.tag, it.manufacturer, it.model,
      it.finish, it.dimensions, (it.specs && JSON.stringify(it.specs)) || '',
    ].some(function(v) { return v && String(v).toLowerCase().indexOf(_s) >= 0; });
    var matchDate = (!hasDateFilter || dateField === 'all') ? true :
      _dateInRange(_getItemDate(it, dateField), dateQuick, dateFrom, dateTo);
    return matchSched && matchProj && matchRoom && matchStatus && matchSearch && matchDate;
  });
}

// Group a filtered items array into a map keyed by room or schedule.
// Returns keys in sorted order: room groups sort numerically, schedule groups alphabetically.
export function groupItems(filteredItems, groupBy) {
  var groups = {};
  if (groupBy === 'room') {
    (filteredItems || []).forEach(function(it) {
      var key = it.roomNumber || (it.roomName ? 'n:' + it.roomName : '__unassigned__');
      if (!groups[key]) groups[key] = [];
      groups[key].push(it);
    });
    Object.keys(groups).forEach(function(k) {
      groups[k].sort(function(a, b) {
        var sa = a.schedule || '', sb = b.schedule || '';
        if (sa !== sb) return sa.localeCompare(sb);
        return (a.item || '').localeCompare(b.item || '');
      });
    });
    var sorted = {};
    Object.keys(groups).sort(function(a, b) {
      if (a === '__unassigned__') return  1;
      if (b === '__unassigned__') return -1;
      var na = parseFloat(a), nb = parseFloat(b);
      if (!isNaN(na) && !isNaN(nb)) return na - nb;
      if (!isNaN(na)) return -1;
      if (!isNaN(nb)) return  1;
      return String(a).localeCompare(String(b));
    }).forEach(function(k) { sorted[k] = groups[k]; });
    return sorted;
  } else {
    (filteredItems || []).forEach(function(it) {
      var s = it.schedule || 'Unassigned';
      if (!groups[s]) groups[s] = [];
      groups[s].push(it);
    });
    Object.keys(groups).forEach(function(s) {
      groups[s].sort(function(a, b) {
        var an = parseFloat(a.roomNumber) || 0, bn = parseFloat(b.roomNumber) || 0;
        return an !== bn ? an - bn : (a.roomName || '').localeCompare(b.roomName || '');
      });
    });
    var sorted = {};
    Object.keys(groups).sort().forEach(function(k) { sorted[k] = groups[k]; });
    return sorted;
  }
}

// ── ADMIN ACCESS / DISPLAY NAME HELPERS ──────────────────────────────────────
// Single source of truth for the admin-access model. Replaces ad-hoc
// `userEmail === firmSettings.bookkeeperEmail` checks scattered across the
// codebase. The admin-access flag lives on each studioTeam member's record
// (set in Firm Settings UI). Owner is always admin and is checked separately
// — the canonical pattern at every call site is:
//
//     isOwner || hasAdminAccess(userEmail, firmSettings)
//
// Why owner is NOT included here: the owner email is a hardcoded constant
// (`c@cimonstarr.com`) and may not be present in studioTeam at all. Mixing
// the two checks would require this helper to know about the owner literal,
// which couples concerns that the call sites already separate cleanly.
//
// displayNameFor() returns a user's first-name token for greeting copy in
// confirmation modals. Falls back to '' (empty string) so callers can render
// "Hi {name}!" only when a name is known and skip the greeting otherwise.
//
// Counterpart on the SQL side: current_admin_emails() RLS helper in
// supabase/migrations/20260510_admin_rls_pivot.sql.

export function hasAdminAccess(userEmail, firmSettings) {
  if (!userEmail || !firmSettings) return false;
  var lower = String(userEmail).toLowerCase().trim();
  if (!lower) return false;
  var team = firmSettings.studioTeam || [];
  return team.some(function(m) {
    return m
      && m.hasAdminAccess === true
      && String(m.email || '').toLowerCase().trim() === lower;
  });
}

export function displayNameFor(userEmail, firmSettings) {
  if (!userEmail || !firmSettings) return '';
  var lower = String(userEmail).toLowerCase().trim();
  if (!lower) return '';
  // Owner — drives first name from firmSettings.principalName, fallback 'Cimon'.
  if (lower === 'c@cimonstarr.com') {
    var principal = String(firmSettings.principalName || '').trim();
    if (principal) return principal.split(/\s+/)[0];
    return 'Cimon';
  }
  // Studio team match — first token of the member's name field.
  var team = firmSettings.studioTeam || [];
  var member = team.find(function(m) {
    return m && String(m.email || '').toLowerCase().trim() === lower;
  });
  if (member && member.name) {
    var first = String(member.name).trim().split(/\s+/)[0];
    if (first) return first;
  }
  return '';
}

// ── NOTES UTILITIES ───────────────────────────────────────────────────────────

export function resolveAuthorName(email, studioTeam) {
  var team = studioTeam || [];
  var m = team.find(function(t) { return (t.email || '').toLowerCase() === (email || '').toLowerCase(); });
  return m && m.name ? m.name : (email || 'Unknown');
}

export function filterNotes(notes, searchQuery) {
  if (!searchQuery || !searchQuery.trim()) return notes || [];
  var q = searchQuery.trim().toLowerCase();
  return (notes || []).filter(function(n) { return (n.body || '').toLowerCase().indexOf(q) !== -1; });
}

export function sortNotesDesc(notes) {
  return (notes || []).slice().sort(function(a, b) { return new Date(b.created_at) - new Date(a.created_at); });
}

export function noteInsertRow(body, projectCode, entityType, entityId, authorEmail) {
  return {
    project_code: projectCode || '',
    entity_type:  entityType  || 'project',
    entity_id:    entityId    || null,
    author_email: authorEmail || '',
    body:         (body || '').trim(),
  };
}

// ── PURCHASE ORDER HELPERS ────────────────────────────────────────────────────

// Pure extract of deletePO revert logic -- returns { id, patch } ops for items
// that should revert when a PO is deleted. Items revert to Deposit Paid (NOT
// Client Approved) so the client-deposit-already-collected fact is preserved
// in the item's status history -- otherwise the next PO attempt would
// require collecting that deposit again. Items already past Order Confirmed
// (Received, Installed, etc.) are left alone.
//
// orderPlacedDate is cleared because the lead-time clock was started by this
// (now-deleted) PO's vendor receipt cascade or by manual entry alongside it.
// Leaving it set would propagate a phantom estimated ship date through every
// consumer of estimatedShipDate() until the next PO restamps it.
export function itemsToRevertOnPODelete(po, allItems) {
  // Items go back to the status they held before Issue PO stamped them.
  // Normally that is Deposit Paid. When the owner issued past the
  // no-deposit soft warning (client_payment_status = 'principal_bypass'),
  // no deposit was ever recorded, so Deposit Paid would assert a payment
  // that never happened; those items return to Client Approved.
  var target = (po && po.clientPaymentStatus === 'principal_bypass') ? 'Client Approved' : 'Deposit Paid';
  return (po.items || []).reduce(function(acc, itemId) {
    var it = (allItems || []).find(function(x) { return x.id === itemId; });
    if (it && (it.status === 'PO Issued' || it.status === 'Ordered')) {
      acc.push({ id: itemId, patch: { status: target, poNumber: '', orderPlacedDate: '' } });
    }
    return acc;
  }, []);
}

// canRevertPoToDraft — the PO counterpart of canRevertProposal
// (2026-10-21). An Issued or Confirmed PO made incorrectly can go back
// to Draft for editing, re-issue, or deletion, as long as nothing
// financial has happened against it:
//   - no vendor_payments row names it (po_id, or legacy reference text)
//   - no receipt is linked to it
//   - no item on it has moved past Ordered (Shipped onward)
// Returns { canRevert, reasons: [string], blockers: [{ id, item, status }] }.
export function canRevertPoToDraft(po, allItems, vendorPayments, receipts) {
  var out = { canRevert: false, reasons: [], blockers: [] };
  if (!po || !po.id) { out.reasons.push('PO not found.'); return out; }
  if (po.status === 'Draft') { out.reasons.push('Already a draft.'); return out; }
  if (po.status === 'Complete') { out.reasons.push('A Complete PO cannot be reverted.'); return out; }

  var poRef = po.po_ref || '';
  var pays = (vendorPayments || []).filter(function(vp) {
    if (!vp) return false;
    if (vp.poId && String(vp.poId) === String(po.id)) return true;
    return !!poRef && String(vp.reference || '').indexOf(poRef) >= 0;
  });
  if (pays.length > 0) {
    var paid = pays.reduce(function(sum, vp) { return sum + (parseFloat(vp.amount) || 0); }, 0);
    out.reasons.push('$' + paid.toFixed(2) + ' in vendor payments recorded. Reverse them first.');
  }
  var rcpts = (receipts || []).filter(function(r) { return r && r.poId === po.id; });
  if (rcpts.length > 0) {
    out.reasons.push(rcpts.length + ' receipt' + (rcpts.length === 1 ? '' : 's') + ' linked. Unlink or delete first.');
  }
  var orderedIdx = STATUSES.indexOf('Ordered');
  (po.items || []).forEach(function(itemId) {
    var it = (allItems || []).find(function(x) { return x.id === itemId; });
    if (!it || it.status === 'Cancelled') return;
    var idx = STATUSES.indexOf(it.status);
    if (idx > orderedIdx) out.blockers.push({ id: it.id, item: it.item || it.productName || it.id, status: it.status });
  });
  if (out.blockers.length > 0) {
    out.reasons.push('Past Ordered: ' + out.blockers.map(function(b) { return b.item + ' (' + b.status + ')'; }).join(', ') + '.');
  }
  out.canRevert = out.reasons.length === 0;
  return out;
}

// finReceiptStatus — detect Final Invoices about to be finalized
// against PO(s) with no approved vendor receipt. Receipts are the
// surface where vendor-side fees (install, freight, customs) that
// weren't on the proposal get caught via the fee verification panel
// and queued as Adjustments. If a FIN is finalized before the vendor
// final invoice arrives, any unexpected fees have no path into the
// client bill — and the FIN closes paid before anyone notices.
// (The 2026-06-13 install-fee incident.)
//
// Returns { posWithoutReceipts: [{ poRef, vendorName }, ...] }. A FIN
// whose POs all have at least one approved vendor_* receipt returns
// an empty array. Items not associated with any PO (Designer Hours,
// Reimbursable) skip the check by construction (no poNumber stamp).
//
// Orphan po_number stamps (item.poNumber pointing at a PO row that
// doesn't exist — INTEGRITY_SWEEP §1.5) are skipped silently; that's
// a separate detection concern.
export function finReceiptStatus(invoice, allItems, purchaseOrders, receipts) {
  var emptyResult = { posWithoutReceipts: [] };
  if (!invoice) return emptyResult;
  var itemIds = invoice.itemIds || invoice.item_ids || [];
  if (!Array.isArray(itemIds) || itemIds.length === 0) return emptyResult;

  var poRefSet = {};
  itemIds.forEach(function(id) {
    var it = (allItems || []).find(function(x) { return x.id === id; });
    if (it && it.poNumber) poRefSet[it.poNumber] = true;
  });
  var poRefs = Object.keys(poRefSet);
  if (poRefs.length === 0) return emptyResult;

  var VENDOR_KINDS = ['vendor_deposit', 'vendor_final', 'vendor_fee', 'vendor_full_payment'];
  var APPROVED_STATUSES = ['approved', 'pushed'];
  var posWithoutReceipts = [];

  poRefs.forEach(function(poRef) {
    var po = (purchaseOrders || []).find(function(p) { return p.po_ref === poRef; });
    if (!po) return; // orphan stamp — separate concern
    var hasReceipt = (receipts || []).some(function(r) {
      if (!r) return false;
      var rPoId = r.poId || r.po_id;
      if (String(rPoId) !== String(po.id)) return false;
      if (VENDOR_KINDS.indexOf(r.kind) < 0) return false;
      if (APPROVED_STATUSES.indexOf(r.status) < 0) return false;
      return true;
    });
    if (!hasReceipt) {
      posWithoutReceipts.push({
        poRef: poRef,
        vendorName: (po.vendorName || po.vendor_name) || '',
      });
    }
  });

  return { posWithoutReceipts: posWithoutReceipts };
}

// finPoSummary — derive the unique PO refs + vendor names for a Final
// Invoice by walking its itemIds → items → poNumber → purchase_orders.
// Going-forward FINs (1 FIN = 1 vendor = 1 PO discipline since
// 2026-06-07) return single-element arrays; legacy multi-PO FINs return
// the full set. Empty arrays when the FIN has no items or no items
// carry a poNumber stamp. Returns { poRefs:[], vendorNames:[] } —
// callers join + render as they wish.
export function finPoSummary(invoice, allItems, purchaseOrders) {
  var emptyResult = { poRefs: [], vendorNames: [] };
  if (!invoice) return emptyResult;
  var itemIds = invoice.itemIds || invoice.item_ids || [];
  if (!Array.isArray(itemIds) || itemIds.length === 0) return emptyResult;
  var poRefSet = {};
  itemIds.forEach(function(id) {
    var it = (allItems || []).find(function(x) { return x.id === id; });
    if (it && it.poNumber) poRefSet[it.poNumber] = true;
  });
  var poRefs = Object.keys(poRefSet);
  var vendorNames = poRefs.map(function(ref) {
    var po = (purchaseOrders || []).find(function(p) { return p.po_ref === ref; });
    return (po && (po.vendorName || po.vendor_name)) || '';
  }).filter(Boolean);
  return { poRefs: poRefs, vendorNames: vendorNames };
}

// ── PO ledger (2026-07-19) ─────────────────────────────────────────────
// "Search a PO and see every transaction against it" (Cimon). One pure
// derivation joining both sides of the ledger for a single PO:
//   vendor side — vendor_payments rows + receipts linked to the PO
//   client side — the DEP/FIN invoices its items ride on, with paid
//                 state from payment_allocations (Rule 10) and the
//                 client payments behind them
// Bank/card statement lines assigned to the PO are fetched async by the
// caller (they live behind admin-only RLS) and rendered alongside.
export function buildPoLedger(po, refs) {
  if (!po) return null;
  var r = refs || {};
  var allItems = r.items || [];
  var allocations = r.paymentAllocations || [];
  var poItems = (Array.isArray(po.items) ? po.items : [])
    .map(function(id) { return allItems.find(function(x) { return x && x.id === id; }); })
    .filter(Boolean);

  // Vendor payments: po_id match OR the reference-tag convention —
  // union, deduped (rows may satisfy both).
  var ref = po.po_ref || '';
  var seenVp = {};
  var vpRows = (r.vendorPayments || []).filter(function(vp) {
    if (!vp || !vp.id || seenVp[vp.id]) return false;
    var hit = (vp.poId || vp.po_id) === po.id
      || (ref && String(vp.reference || '').indexOf(ref) >= 0);
    if (hit) seenVp[vp.id] = 1;
    return hit;
  }).map(function(vp) {
    return {
      id: vp.id,
      date: vp.paymentDate || vp.payment_date || '',
      type: vp.paymentType || vp.payment_type || '',
      amount: parseFloat(vp.amount) || 0,
      reference: vp.reference || '',
    };
  }).sort(function(a, b) { return String(a.date).localeCompare(String(b.date)); });

  var receiptRows = (r.receipts || []).filter(function(rc) {
    return rc && rc.poId === po.id && rc.status !== 'rejected';
  }).map(function(rc) {
    return {
      id: rc.id, date: rc.receiptDate || '', kind: rc.kind || '',
      amount: parseFloat(rc.totalAmount) || 0,
      ccFee: parseFloat(rc.ccFee) || 0, status: rc.status || '',
      vendorName: rc.vendorName || '',
    };
  }).sort(function(a, b) { return String(a.date).localeCompare(String(b.date)); });

  // Client side: the invoices this PO's items are stamped onto.
  var invIds = [];
  poItems.forEach(function(it) {
    [it.clientDepositInvoiceId, it.finalInvoiceId].forEach(function(iid) {
      if (iid && invIds.indexOf(iid) < 0) invIds.push(iid);
    });
  });
  var invoiceRows = invIds.map(function(iid) {
    var inv = (r.invoices || []).find(function(x) { return x && x.id === iid; });
    if (!inv) return null;
    var due = parseFloat(inv.amountDue) || parseFloat(inv.amount) || 0;
    return {
      id: inv.id,
      ref: inv.invoiceRef || inv.invoice_ref || '',
      type: inv.invoiceType || inv.invoice_type || '',
      amountDue: due,
      paid: calcPaidForDocument(allocations, 'invoice', inv.id),
      status: inv.status || '',
    };
  }).filter(Boolean).sort(function(a, b) {
    // DEPs before FINs, then by ref.
    if (a.type !== b.type) return a.type === 'deposit' ? -1 : 1;
    return a.ref.localeCompare(b.ref);
  });

  var invById = {};
  invoiceRows.forEach(function(ir) { invById[ir.id] = ir; });
  var clientPaymentRows = [];
  allocations.forEach(function(a) {
    if (!a) return;
    var dt = a.documentType || a.document_type;
    var did = a.documentId || a.document_id;
    if (dt !== 'invoice' || !invById[did]) return;
    var payId = a.paymentId || a.payment_id;
    var pay = (r.clientPayments || []).find(function(pp) { return pp && pp.id === payId; });
    clientPaymentRows.push({
      date: (pay && (pay.paymentDate || pay.payment_date)) || '',
      amount: parseFloat(a.amount) || 0,
      method: (pay && pay.method) || '',
      invoiceRef: invById[did].ref,
    });
  });
  clientPaymentRows.sort(function(a, b) { return String(a.date).localeCompare(String(b.date)); });

  var poTotal = poSubtotalLive(po, allItems);
  var vendorPaid = vpRows.reduce(function(sum, vp) {
    return PRINCIPAL_PAYMENT_TYPES.indexOf(vp.type) >= 0 ? sum + vp.amount : sum;
  }, 0);
  return {
    po: { id: po.id, ref: ref, vendor: po.vendorName || po.vendor_name || '',
          project: po.project || '', status: po.status || '' },
    items: poItems,
    vendorPayments: vpRows,
    receipts: receiptRows,
    invoices: invoiceRows,
    clientPayments: clientPaymentRows,
    totals: {
      poTotal: Math.round(poTotal * 100) / 100,
      vendorPaid: Math.round(vendorPaid * 100) / 100,
      vendorRemaining: Math.round(Math.max(0, poTotal - vendorPaid) * 100) / 100,
      clientBilled: Math.round(invoiceRows.reduce(function(sum, ir) { return sum + ir.amountDue; }, 0) * 100) / 100,
      clientPaid: Math.round(invoiceRows.reduce(function(sum, ir) { return sum + ir.paid; }, 0) * 100) / 100,
    },
  };
}

// ── Invoice cross-references (2026-07-19) ──────────────────────────────
// Dan's QB reconciliation asks "what is this invoice, in every numbering
// system we have?" — SKUs, PO refs, proposal ref, the billing summary
// month it rode on, and the sibling DEP/FIN refs. One derivation feeds
// the FIN PDF's gray reference block, the invoice list columns, and the
// detail side panel, so the three can never disagree.
//
// refs: { items, proposals, purchaseOrders, invoices, billingSummaries }
// (pass what you have; missing collections just yield empty fields).
export function invoiceCrossRefs(inv, refs) {
  var r = refs || {};
  var out = { skus: [], poRefs: [], proposalRef: '', proposalRefs: [],
              summaryMonthYear: '', summarySortKey: 0, depRefs: [], finRefs: [] };
  if (!inv) return out;

  var itemIds = (Array.isArray(inv.itemIds) && inv.itemIds.length) ? inv.itemIds
    : (Array.isArray(inv.item_ids) && inv.item_ids.length) ? inv.item_ids
    : (inv.itemId || inv.item_id) ? [inv.itemId || inv.item_id] : [];
  var invItems = itemIds
    .map(function(id) { return (r.items || []).find(function(x) { return x && x.id === id; }); })
    .filter(Boolean);

  var seen = {};
  invItems.forEach(function(it) {
    if (it.itemNumber && !seen['s' + it.itemNumber]) { seen['s' + it.itemNumber] = 1; out.skus.push(it.itemNumber); }
  });
  invItems.forEach(function(it) {
    if (it.poNumber && !seen['p' + it.poNumber]) { seen['p' + it.poNumber] = 1; out.poRefs.push(it.poNumber); }
  });

  // Proposal ref: the invoice's own proposalId when stamped (DEPs), else
  // every proposal that carries one of the billed items (FINs).
  function propRef(pp) { return (pp && (pp.proposal_ref || pp.proposalRef || pp.ref)) || ''; }
  var pid = inv.proposalId || inv.proposal_id || '';
  if (pid) {
    var pp = (r.proposals || []).find(function(x) { return x && x.id === pid; });
    if (propRef(pp)) out.proposalRefs.push(propRef(pp));
  } else if (invItems.length) {
    (r.proposals || []).forEach(function(pp) {
      var pids = (pp && (pp.items || pp.itemIds || pp.item_ids)) || [];
      if (!Array.isArray(pids)) return;
      var hit = invItems.some(function(it) { return pids.indexOf(it.id) >= 0; });
      var ref = propRef(pp);
      if (hit && ref && out.proposalRefs.indexOf(ref) < 0) out.proposalRefs.push(ref);
    });
  }
  out.proposalRef = out.proposalRefs.join(', ');

  // Billing summary month — the month this invoice rode on a cover.
  var sid = inv.billedOnSummaryId || inv.billed_on_summary_id || '';
  if (sid) {
    var bs = (r.billingSummaries || []).find(function(x) { return x && x.id === sid; });
    if (bs) {
      out.summaryMonthYear = billingSummaryMonthYear(bs);
      var y = parseInt(bs.year, 10) || 0;
      var m = parseInt(bs.month, 10) || 0;
      out.summarySortKey = y * 100 + m;
      if (!out.summarySortKey) {
        var d = String(bs.sent_at || bs.sentAt || '').slice(0, 7).split('-');
        out.summarySortKey = (parseInt(d[0], 10) || 0) * 100 + (parseInt(d[1], 10) || 0);
      }
    }
  }

  // Sibling documents on the same proposal — a FIN names its DEP(s), a
  // DEP names its FIN(s). Per-item DEPs collapse to their shared ref.
  var pids2 = pid ? [pid] : [];
  if (!pids2.length) {
    (r.proposals || []).forEach(function(pp) {
      var itl = (pp && (pp.items || pp.itemIds || pp.item_ids)) || [];
      if (Array.isArray(itl) && invItems.some(function(it) { return itl.indexOf(it.id) >= 0; })) pids2.push(pp.id);
    });
  }
  (r.invoices || []).forEach(function(other) {
    if (!other || other.id === inv.id) return;
    var opid = other.proposalId || other.proposal_id || '';
    if (!opid || pids2.indexOf(opid) < 0) return;
    var oref = other.invoiceRef || other.invoice_ref || '';
    if (!oref) return;
    var otype = other.invoiceType || other.invoice_type || '';
    if (otype === 'deposit' && out.depRefs.indexOf(oref) < 0) out.depRefs.push(oref);
    if (otype === 'final' && out.finRefs.indexOf(oref) < 0) out.finRefs.push(oref);
  });
  out.depRefs.sort(); out.finRefs.sort();
  return out;
}

// computePoStampChanges — pure helper for keeping items.poNumber in sync
// with a PO's items array when Edit Items reshuffles membership. Returns
// an array of { id, patch: { poNumber } } ops the caller dispatches.
//
// Symmetric handling:
//   - Items REMOVED (in prev, not next): clear poNumber if and only if
//     it points at THIS PO. Defensive against rare data where two POs
//     claim the same item.
//   - Items ADDED (in next, not prev): stamp poNumber with this PO's
//     ref. Only when the PO is past Draft — Draft POs don't stamp
//     items (the Draft → Issued transition in advanceStatus handles
//     that path). Overwrites a stale stamp from another PO (that's the
//     bug-fix path; the prior PO should have cleared it via the remove
//     branch above, but pre-2026-06-11 history may leave stale stamps).
//
// Closes the gap that allowed the 26-Moskito stamp-drift incident
// discovered 2026-06-12: 5 items moved from PO-004 to PO-003 via Edit
// Items pre-2026-06-11; the remove-path Bug 2 fix shipped 2026-06-11
// closed phantom stamps on future removals, but the add path had no
// symmetric write so the items kept their stale 'PO-004' stamps through
// every downstream status advance (some reaching Received). This helper
// closes the symmetry.
export function computePoStampChanges(prevPO, nextPO, allItems) {
  if (!prevPO || !nextPO) return [];
  var prevRef = prevPO.po_ref || '';
  var nextRef = nextPO.po_ref || '';
  var prevItems = prevPO.items || [];
  var nextItems = nextPO.items || [];
  var ops = [];

  // Remove path: clear stamp if it points at THIS PO.
  prevItems.filter(function(id) { return nextItems.indexOf(id) < 0; })
    .forEach(function(itemId) {
      var it = (allItems || []).find(function(x) { return x.id === itemId; });
      if (!it) return;
      if (it.poNumber && it.poNumber === prevRef) {
        ops.push({ id: itemId, patch: { poNumber: '' } });
      }
    });

  // Add path: stamp this PO's ref. Only when PO is past Draft.
  if (nextPO.status && nextPO.status !== 'Draft' && nextRef) {
    nextItems.filter(function(id) { return prevItems.indexOf(id) < 0; })
      .forEach(function(itemId) {
        var it = (allItems || []).find(function(x) { return x.id === itemId; });
        if (!it) return;
        if (it.poNumber === nextRef) return; // already correct
        ops.push({ id: itemId, patch: { poNumber: nextRef } });
      });
  }

  return ops;
}

// Pure extract of maybeAdvanceItem — returns targetStatus if item should
// advance, null if it should stay put (already at or past target, or unknown).
export function forwardOnlyItemStatusUpdate(currentStatus, targetStatus, statuses) {
  var currentIdx = (statuses || []).indexOf(currentStatus);
  var targetIdx  = (statuses || []).indexOf(targetStatus);
  if (currentIdx < 0 || targetIdx < 0) return null;
  return currentIdx < targetIdx ? targetStatus : null;
}

// ── PROPOSAL APPROVAL HELPERS ─────────────────────────────────────────────────

// Evaluates per-item SKU assignment results from autoAssignItemNumber promises.
// Each entry: { item, ok: boolean }. Returns allSucceeded flag and failed items.
export function allSkusAssigned(results) {
  var failed = (results || []).filter(function(r) { return !r.ok; });
  return { allSucceeded: failed.length === 0, failed: failed.map(function(r) { return r.item; }) };
}

// ── PURCHASE ORDER MAPPERS ────────────────────────────────────────────────────
// Single source of truth for PO ↔ DB row translation. Used by AppContext
// (live save) and proposalPOSave.test.js + schemaDriftGuard.test.js.

export function rowToPO(row) {
  var ids = row && row.item_ids;
  if (typeof ids === 'string') { try { ids = JSON.parse(ids); } catch (_e) { ids = []; } }
  var pendingAdj = row && row.pending_fin_adjustments;
  if (typeof pendingAdj === 'string') { try { pendingAdj = JSON.parse(pendingAdj); } catch (_e) { pendingAdj = []; } }
  return {
    id:            row && row.id,
    po_ref:        (row && row.po_ref) || '',
    project:       (row && row.project) || '',
    vendorId:      (row && row.vendor_id != null) ? String(row.vendor_id) : '',
    vendorName:    (row && row.vendor_name) || '',
    status:        (row && row.status) || 'Draft',
    items:         Array.isArray(ids) ? ids : [],
    total_net:     (row && row.total_net) || 0,
    total_deposit: (row && row.total_deposit) || 0,
    notes:         (row && row.notes) || '',
    version:       (row && row.version) || 1,
    created_at:    (row && row.created_at) || '',
    issued_at:     (row && row.issued_at) || null,
    qbPoId:        (row && row.qb_po_id) || '',
    qbPushedAt:    (row && row.qb_pushed_at) || '',
    qbError:       (row && row.qb_error) || '',
    // SPEC 1 — soft-warning bypass audit columns. Populated only when the
    // owner clicks "Issue anyway" on a PO whose items haven't had their
    // client deposit recorded.
    clientPaymentStatus: (row && row.client_payment_status) || '',
    bypassedBy:          (row && row.bypassed_by) || '',
    bypassedAt:          (row && row.bypassed_at) || '',
    // Queued FIN adjustments — entries the bookkeeper picked "Bill as
    // adjustment line" on at receipt approval time. Merged into the
    // FIN's invoices.adjustments at generation, then cleared from this
    // PO. Added 2026-06-10 via 20260610_purchase_orders_pending_fin_adjustments.sql.
    pendingFinAdjustments: Array.isArray(pendingAdj) ? pendingAdj : [],
  };
}

// IMPORTANT: vendor_id was migrated from text to BIGINT on 2026-05-07.
// Vendor records in csid_vendors carry slug/uuid ids. Coerce to null if
// the value isn't a positive integer string — sending '' triggers Postgres
// 'invalid input syntax for type bigint' and the upsert silently fails.
export function poToRow(p) {
  var v = p && p.vendorId;
  var vendorIdNum = (v != null && /^\d+$/.test(String(v))) ? String(v) : null;
  return {
    id:            p && p.id,
    po_ref:        (p && p.po_ref) || '',
    project:       (p && p.project) || '',
    vendor_id:     vendorIdNum,
    vendor_name:   (p && p.vendorName) || '',
    status:        (p && p.status) || 'Draft',
    item_ids:      (p && p.items) || [],
    total_net:     parseFloat(p && p.total_net) || 0,
    total_deposit: parseFloat(p && p.total_deposit) || 0,
    notes:         (p && p.notes) || '',
    version:       (p && p.version) || 1,
    created_at:    (p && p.created_at) || new Date().toISOString(),
    issued_at:     (p && p.issued_at) || null,
    qb_po_id:      (p && p.qbPoId) || '',
    qb_pushed_at:  (p && p.qbPushedAt) || null,
    qb_error:      (p && p.qbError) || '',
    // SPEC 1 bypass audit. NULL when not bypassed; the BEFORE-INSERT
    // trigger in 20260521_po_bypass_columns.sql rejects writes that set
    // client_payment_status='principal_bypass' unless the caller is the
    // firm owner. Defense in depth alongside the client-side button gate.
    client_payment_status: (p && p.clientPaymentStatus) || null,
    bypassed_by:           (p && p.bypassedBy) || null,
    bypassed_at:           (p && p.bypassedAt) || null,
    pending_fin_adjustments: (p && Array.isArray(p.pendingFinAdjustments)) ? p.pendingFinAdjustments : [],
  };
}

// ── PO SHIP-TO RESOLUTION ─────────────────────────────────────────────────────
//
// PO ship-to is computed at render time, not stored on the PO row. The
// resolution rules (one PO carries one ship-to):
//
//   1. If any item on the PO has an item-level override (shipToAddress text
//      or shipToVendorId pointing to a directory vendor), use it.
//   2. If items have multiple distinct overrides, that's a separate problem
//      (the items should be on separate POs). Report the conflict in the
//      return value and use the first override so the PDF still renders
//      one valid address — caller is responsible for surfacing the warning.
//   3. If no items have overrides, fall back to the project receiver
//      (project.receiverId → vendor in directory).
//   4. If neither, fall back to the project site address.
//
// `shipToVendorId === 'install_on_site'` is a recognized sentinel (mirrors
// documentTabs.jsx) — resolves to the literal text "Install on site".

function formatVendorAddressForShipTo(v) {
  if (!v) return '';
  return [
    v.name,
    v.shipAttn ? 'Attn: ' + v.shipAttn : null,
    v.address, v.address2,
    [v.city, v.state, v.zip].filter(Boolean).join(', '),
  ].filter(Boolean).join('\n');
}

// Resolves the effective ship-to for a single item.
// Returns null when the item has no override (caller falls back to project).
export function resolveItemShipTo(item, vendors) {
  if (!item) return null;
  var freeText = item.shipToAddress && String(item.shipToAddress).trim();
  if (freeText) {
    return { kind: 'address', text: freeText };
  }
  if (item.shipToVendorId === 'install_on_site') {
    return { kind: 'install_on_site', text: 'Install on site' };
  }
  if (item.shipToVendorId) {
    var v = (vendors || []).find(function(x) {
      return String(x.id) === String(item.shipToVendorId);
    });
    if (v) return { kind: 'vendor', vendor: v, text: formatVendorAddressForShipTo(v) };
    // shipToVendorId pointed at a vendor that no longer exists in the
    // directory — treat as no override (caller falls back).
    return null;
  }
  return null;
}

// Resolves the PO ship-to from its items + project fallback.
// Returns { text, source, conflict? } where:
//   source ∈ 'item_override' | 'project_receiver' | 'project_site' | 'empty'
//   conflict (optional) — array of distinct override texts when items
//     disagree. Present only when source === 'item_override' AND >1 distinct.
export function resolvePoShipTo(po, items, vendors, projects) {
  var itemIds = (po && po.items) || [];
  var poItems = (items || []).filter(function(it) {
    return itemIds.indexOf(it.id) >= 0;
  });
  var overrides = [];
  poItems.forEach(function(it) {
    var o = resolveItemShipTo(it, vendors);
    if (o) overrides.push(o);
  });
  // Deduplicate by rendered text.
  var distinct = [];
  overrides.forEach(function(o) {
    if (!distinct.some(function(d) { return d.text === o.text; })) distinct.push(o);
  });

  if (distinct.length === 1) {
    return { text: distinct[0].text, source: 'item_override' };
  }
  if (distinct.length > 1) {
    return {
      text: distinct[0].text,
      source: 'item_override',
      conflict: distinct.map(function(d) { return d.text; }),
    };
  }

  // No item-level overrides — fall back to project receiver / site.
  var proj = (projects || []).find(function(pr) {
    return pr.label === (po && po.project);
  }) || {};
  if (proj.receiverId) {
    var receiver = (vendors || []).find(function(v) {
      return String(v.id) === String(proj.receiverId);
    });
    if (receiver) {
      return { text: formatVendorAddressForShipTo(receiver), source: 'project_receiver' };
    }
  }
  var siteLines = [
    proj.attn ? 'Attn: ' + proj.attn : null,
    proj.address, proj.address2,
    [proj.city, proj.state, proj.zip].filter(Boolean).join(', '),
  ].filter(Boolean);
  if (siteLines.length === 0) {
    return { text: '', source: 'empty' };
  }
  return { text: siteLines.join('\n'), source: 'project_site' };
}

// ── Reconciliation helpers (Part B, 2026-06-14) ─────────────────────────────
//
// Pure functions consumed by ReconciliationSection.jsx for table sort + the
// totals bar. Lifted out of the component so they can be unit-tested without
// React. Names are reconciliation-specific so future surfaces can keep their
// own sort/totals helpers without naming collisions.

// Returns the comparable value for a reconciliation row under a given sort
// key. FIN-level keys (project / date / amount / status / finRef) return the
// same value for every row of a FIN — useful for the multi-key cascade
// behavior where ties on the primary key fall through to finId + poRef and
// the renderer detects adjacency for FIN-column rendering.
export function reconciliationSortKey(row, sortBy) {
  if (!row) return '';
  switch (sortBy) {
    case 'project':         return String(row.project || '');
    case 'date':            return String(row.createdAt || '');
    case 'amount':          return Number(row.finAmount || 0);
    case 'status':          return String(row.finStatus || '');
    case 'vendor':          return String(row.vendorName || '');
    case 'po':              return String(row.poRef || '');
    case 'fin':             return String(row.finRef || '');
    case 'paid_date':       return String(row.paidDateForQb || '');
    case 'po_amount':       return Number(row.poAmount || 0);
    case 'vendor_paid':     return Number(row.actualVendorPayment || 0);
    case 'vendor_balance':  return Number(row.vendorBalance || 0);
    case 'client_balance':  return Number(row.clientBalance || 0);
    case 'items_fees':      return Number(row.itemsAndFeesNet || 0);
    case 'markup':          return Number(row.markup || 0);
    case 'taxes':           return Number(row.salesTax || 0);
    default:                return '';
  }
}

// 3-key cascade comparator: primary user-chosen sort, secondary finId, tertiary
// poRef. The secondary key keeps multi-PO FIN rows adjacent when the primary
// key is FIN-level (project/date/amount/status/finRef — every row of a FIN
// ties on primary, finId breaks the tie deterministically, poRef orders POs
// within). When the primary key is per-row (vendor/po/per-PO money), rows of
// the same FIN may scatter across the table; the renderer's adjacency
// detection handles "first row of FIN" rendering in either case.
//
// `dir` flips the PRIMARY key direction only. Secondary (finId) + tertiary
// (poRef) stay ascending under either direction so the tie-break is stable
// and predictable — flipping the whole comparator would scramble PO order
// within a group when the primary key ties.
export function compareReconciliationRows(a, b, sortBy, dir) {
  var ka = reconciliationSortKey(a, sortBy);
  var kb = reconciliationSortKey(b, sortBy);
  var primary = 0;
  if (typeof ka === 'number' || typeof kb === 'number') {
    primary = (Number(ka) || 0) - (Number(kb) || 0);
  } else {
    primary = String(ka).localeCompare(String(kb));
  }
  if (dir === 'desc') primary = -primary;
  if (primary !== 0) return primary;
  // Secondary: finId — keeps multi-PO FIN rows adjacent when primary ties.
  var fa = String(a.finId || ''); var fb = String(b.finId || '');
  if (fa !== fb) return fa.localeCompare(fb);
  // Tertiary: poRef — within a FIN, sort POs alphabetically by ref.
  return String(a.poRef || '').localeCompare(String(b.poRef || ''));
}

// Strip the internal RECEIPT:<uuid> idempotency tag from a vendor_payments
// reference string before showing it to the bookkeeper. The cascade
// (buildVendorPaymentFromReceipt) writes references in the canonical
// format `{poRef} · {vendorName} · [extra tags ·] RECEIPT:<uuid>` — the
// trailing RECEIPT:<uuid> exists so re-running the cascade can detect
// the prior row by reference substring. It's a system marker, not
// bookkeeper-facing data.
//
// Match positionally (not just trailing) so legacy + cc-fee references
// in non-canonical order also clean correctly. Collapse orphaned
// separators left behind by the strip.
export function formatVendorPaymentReference(ref) {
  if (!ref) return '';
  // Strip the tag whether it sits after a separator (canonical) or
  // standalone (the manual / corrupted-data edge case where the
  // reference is just RECEIPT:<uuid>).
  var s = String(ref).replace(/(?:\s*·\s*)?RECEIPT:[a-zA-Z0-9-]+/gi, '');
  s = s.replace(/^\s*·\s*/, '').replace(/\s*·\s*$/, '').trim();
  return s;
}

// Soft amount-match between a vendor_payment and its linked receipt.
// Used by the Reconciliation drawer (Part B B2) to flag when a payment's
// principal differs noticeably from the receipt's totalAmount — could
// signal a data entry slip or a vendor adjustment that didn't flow back
// to the receipt. The signal is informational only; never blocking.
//
// Tolerance band: max($0.50, payment × 0.5%). The $0.50 floor catches
// typical human cent-level slips (extracted $1234.56 vs paid $1234.06)
// without tripping on rounding; the 0.5% growth handles larger payments
// where a few-dollar gap is genuine noise (e.g. $50 on a $10K wire).
// Returns { matches, paymentAmount, receiptAmount, delta, tolerance }
// so the caller can render a side-by-side comparison when matches=false.
export function vendorPaymentReceiptMatch(paymentAmount, receiptAmount) {
  var p = Number(paymentAmount) || 0;
  var r = Number(receiptAmount) || 0;
  var delta = Math.abs(p - r);
  var tolerance = Math.max(0.50, Math.abs(p) * 0.005);
  return {
    matches:       delta <= tolerance,
    paymentAmount: Math.round(p * 100) / 100,
    receiptAmount: Math.round(r * 100) / 100,
    delta:         Math.round(delta * 100) / 100,
    tolerance:     Math.round(tolerance * 100) / 100,
  };
}

// Sum the visible rows for the Reconciliation totals bar. The "visible" set
// is whatever the caller hands us — project filter + search box already
// applied upstream. Multi-PO FIN handling: PO-level dollar fields sum across
// every row; FIN-level fields dedupe by finId so a 3-PO FIN doesn't 3x its
// client-side numbers in the bottom-line total.
//
//   PO-level (sum across every row): poAmount, actualVendorPayment, vendorBalance
//   FIN-level (dedupe by finId):     finAmount, itemsAndFeesNet, markup,
//                                    salesTax, clientBalance
export function computeReconciliationTotals(rows) {
  var poAmountSum            = 0;
  var actualVendorPaymentSum = 0;
  var vendorBalanceSum       = 0;
  var finAmountSum           = 0;
  var itemsAndFeesNetSum     = 0;
  var markupSum              = 0;
  var salesTaxSum            = 0;
  var clientBalanceSum       = 0;
  var seenFinIds             = Object.create(null);
  // Fee itemization + markup split totals (2026-09-08). Deduped by finId
  // exactly like the other client-side sums — a FIN spanning two POs
  // renders two rows and must contribute its fees once.
  var feeCatSums   = emptyFeeCategories();
  var markupSplits = { product: 0, fees: 0, ccFee: 0, adjustments: 0 };
  var derivedFinCount = 0;
  (rows || []).forEach(function(r) {
    if (!r) return;
    poAmountSum            += parseFloat(r.poAmount)            || 0;
    actualVendorPaymentSum += parseFloat(r.actualVendorPayment) || 0;
    vendorBalanceSum       += parseFloat(r.vendorBalance)       || 0;
    var fid = r.finId;
    if (fid && !seenFinIds[fid]) {
      seenFinIds[fid] = true;
      finAmountSum       += parseFloat(r.finAmount)        || 0;
      itemsAndFeesNetSum += parseFloat(r.itemsAndFeesNet)  || 0;
      markupSum          += parseFloat(r.markup)           || 0;
      salesTaxSum        += parseFloat(r.salesTax)         || 0;
      clientBalanceSum   += parseFloat(r.clientBalance)    || 0;
      var fc = r.feesByCategory || {};
      RECON_FEE_CATEGORIES.forEach(function(k) {
        var b = fc[k] || {};
        feeCatSums[k].cost   += parseFloat(b.cost)   || 0;
        feeCatSums[k].client += parseFloat(b.client) || 0;
        feeCatSums[k].markup += parseFloat(b.markup) || 0;
      });
      var ms = r.markupSplit || {};
      markupSplits.product     += parseFloat(ms.product)     || 0;
      markupSplits.fees        += parseFloat(ms.fees)        || 0;
      markupSplits.ccFee       += parseFloat(ms.ccFee)       || 0;
      markupSplits.adjustments += parseFloat(ms.adjustments) || 0;
      if (r.markupSource === 'derived') derivedFinCount++;
    }
  });
  var r2 = function(x) { return Math.round(x * 100) / 100; };
  RECON_FEE_CATEGORIES.forEach(function(k) {
    feeCatSums[k] = { cost: r2(feeCatSums[k].cost), client: r2(feeCatSums[k].client), markup: r2(feeCatSums[k].markup) };
  });
  Object.keys(markupSplits).forEach(function(k) { markupSplits[k] = r2(markupSplits[k]); });
  markupSplits.total = r2(markupSplits.product + markupSplits.fees + markupSplits.ccFee + markupSplits.adjustments);
  return {
    feesByCategory:      feeCatSums,
    markupSplit:         markupSplits,
    // How many of the summed FINs had no stored markup and fell back to
    // live derivation. Nonzero is expected until every FIN in the window
    // was issued after 20260908; the UI says so rather than implying the
    // whole column is authoritative.
    derivedFinCount:     derivedFinCount,
    poAmount:            Math.round(poAmountSum            * 100) / 100,
    actualVendorPayment: Math.round(actualVendorPaymentSum * 100) / 100,
    vendorBalance:       Math.round(vendorBalanceSum       * 100) / 100,
    finAmount:           Math.round(finAmountSum           * 100) / 100,
    itemsAndFeesNet:     Math.round(itemsAndFeesNetSum     * 100) / 100,
    markup:              Math.round(markupSum              * 100) / 100,
    salesTax:            Math.round(salesTaxSum            * 100) / 100,
    clientBalance:       Math.round(clientBalanceSum       * 100) / 100,
    finCount:            Object.keys(seenFinIds).length,
    rowCount:            (rows || []).length,
  };
}

// Vendor-payment ↔ PO match. Used by buildReconciliationRows and by the
// Reconciliation drawer to attribute a vendor_payments row to its PO.
// Two paths (OR'd):
//   (a) vendor_payments.po_id matches the PO's id (post-2026-05-12 rows)
//   (b) vendor_payments.reference contains the PO's po_ref substring
//       (legacy + canonical "{po_ref} · ... · RECEIPT:<uuid>" format)
// Path (b) is load-bearing for legacy Hoback data whose vendor_payments
// rows pre-date the 2026-05-12 receipt-integration migration. Fee-type
// rows (CC processing) are filtered out — only principal rows attribute
// to a PO for reconciliation purposes.
export function vpMatchesPo(vp, po) {
  if (!vp || !po) return false;
  if ((vp.paymentType || '') === 'Fee') return false;
  var vpPoId = vp.poId || vp.po_id || '';
  if (vpPoId && po.id && String(vpPoId) === String(po.id)) return true;
  var ref   = String(vp.reference || '');
  var poRef = po.po_ref || '';
  if (poRef && ref.indexOf(poRef) >= 0) return true;
  return false;
}

// finPaidDate — the date a final invoice's money actually landed.
//
// Two paths, in order:
//   (a) the FIN is 'paid' → the latest client-payment date allocated to it.
//   (b) the FIN asks for nothing (amountDue ≈ 0) because the deposit
//       already collected the whole transaction — the in-stock case where
//       the vendor wanted 100% up front, so buildDepositInvoicePayload
//       billed fees + sales tax at deposit time. Those FINs carry the full
//       subtotal and the full tax but have no allocations of their own, so
//       the date comes from the deposit that closed them.
//
// Path (b) has two readings, chosen by args.zeroFinBy:
//   - default ('deposit'): the deposit's payment date — when the money
//     landed. Reconciliation's Paid Date column (QB cash basis) reads it.
//   - 'close': the FIN's own close (sent_at, else created_at) — the
//     taxable transaction. The Sales Tax report reads THIS (Cimon,
//     2026-09-13 + 2026-09-20: "we pay tax at the time the client pays
//     us the final invoice and not before"; a $0 final is closed the
//     day it issues, and the deposit date is information only). Still
//     gated on the deposit being fully paid — no money, no transaction.
//
// Returns { date: 'YYYY-MM-DD' | '', basis: 'fin' | 'deposit' | 'closed' | '' }.
// basis lets a caller show why a date is what it is rather than making
// the reader guess.
export function finPaidDate(fin, args) {
  var empty = { date: '', basis: '' };
  if (!fin) return empty;
  var invoices           = (args && args.invoices)           || [];
  var paymentAllocations = (args && args.paymentAllocations) || [];
  var clientPayments     = (args && args.clientPayments)     || [];

  function allocsFor(invId) {
    return paymentAllocations.filter(function(pa) {
      if (!pa) return false;
      var docType = pa.documentType || pa.document_type;
      var docId   = pa.documentId   || pa.document_id;
      return docType === 'invoice' && docId === invId;
    });
  }
  function dateOf(pa) {
    if (!pa) return '';
    var pid = pa.paymentId || pa.payment_id;
    if (!pid) return pa.createdAt || pa.created_at || '';
    var cp = clientPayments.find(function(p) { return p && p.id === pid; });
    if (cp && (cp.paymentDate || cp.payment_date)) {
      return cp.paymentDate || cp.payment_date;
    }
    return pa.createdAt || pa.created_at || '';
  }
  function latestOf(allocs) {
    return allocs.reduce(function(latest, pa) {
      var ts = dateOf(pa);
      return ts > latest ? ts : latest;
    }, '');
  }

  var status    = String(fin.status || 'draft').trim().toLowerCase();
  var amountDue = parseFloat(fin.amountDue || fin.amount_due) || 0;
  var finLatest = latestOf(allocsFor(fin.id));

  if (status === 'paid' && finLatest) {
    return { date: String(finLatest).slice(0, 10), basis: 'fin' };
  }
  if (amountDue > 0.005) return empty;

  var propId = String(fin.proposalId || fin.proposal_id || '');
  if (!propId) return empty;
  var dep = invoices.find(function(inv) {
    return inv
      && (inv.invoiceType || inv.invoice_type) === 'deposit'
      && String(inv.proposalId || inv.proposal_id || '') === propId;
  });
  if (!dep) return empty;
  var depAllocs = allocsFor(dep.id);
  var depPaid   = depAllocs.reduce(function(sum, pa) {
    return sum + (parseFloat(pa.amount) || 0);
  }, 0);
  var depAmount = parseFloat(dep.amountDue || dep.amount_due) || 0;
  if (!(depAmount > 0 && depPaid >= depAmount - 0.005)) return empty;
  var depLatest = latestOf(depAllocs);
  if (!depLatest) return empty;
  if (args && args.zeroFinBy === 'close') {
    var closed = fin.sentAt || fin.sent_at || fin.createdAt || fin.created_at || '';
    // Every DB row carries created_at; a FIN with no close timestamp at
    // all falls through to the deposit date, LABELED as such — never
    // dropped from a return, never mislabeled as a close.
    if (closed) return { date: String(closed).slice(0, 10), basis: 'closed' };
  }
  return { date: String(depLatest).slice(0, 10), basis: 'deposit' };
}

// PO statuses that show up on the Reconciliation page when the PO has
// no FIN yet. Excludes 'Draft' (not yet committed — bookkeeper hasn't
// issued the PO so no vendor money has moved). The full PO_STATUSES
// progression is 'Draft' → 'Issued' → 'Confirmed' → 'Complete'; all
// three post-Draft states represent committed spend the bookkeeper
// needs to reconcile, whether or not the FIN has issued yet.
var RECONCILIATION_PO_VISIBLE_STATUSES = {
  'Issued':    true,
  'Confirmed': true,
  'Complete':  true,
};

// Build the row set the Reconciliation page renders. Two passes:
//
//   1. FIN-driven pass — iterates final invoices, resolves bundled
//      items → PO set, emits one row per (FIN × PO) pair. Multi-PO
//      FINs naturally expand to N rows; orphan FINs (no matched PO)
//      get a single row with PO columns dashed.
//
//   2. PO-only pass (Part B follow-up, 2026-06-16) — iterates POs
//      with status in Issued/Confirmed/Complete that are NOT already
//      covered by pass 1, and emits one row per PO with FIN-side
//      columns blank. Lets the bookkeeper see vendor-side
//      reconciliation immediately when a PO is issued, before the
//      FIN that closes it exists. Pre-Part-B follow-up the page
//      only surfaced POs once their FIN existed.
//
// The PO-only row's `finId` is null; the renderer's adjacency map
// treats every null-finId row as its own "first row of group" (no
// multi-row suppression). Sort fields: createdAt is populated from
// po.created_at so date-based sorts interleave PO-only rows with
// FIN-driven rows correctly.
export function buildReconciliationRows(args) {
  var invoices            = (args && args.invoices)            || [];
  var items               = (args && args.items)               || [];
  var purchaseOrders      = (args && args.purchaseOrders)      || [];
  var vendorPayments      = (args && args.vendorPayments)      || [];
  var paymentAllocations  = (args && args.paymentAllocations)  || [];
  var proposals           = (args && args.proposals)           || [];
  var clientPayments      = (args && args.clientPayments)      || [];
  var projects            = (args && args.projects)            || [];

  function paymentDateFromAlloc(pa) {
    if (!pa) return '';
    var pid = pa.paymentId || pa.payment_id;
    if (!pid) return pa.createdAt || pa.created_at || '';
    var cp = clientPayments.find(function(p) { return p && p.id === pid; });
    if (cp && (cp.paymentDate || cp.payment_date)) {
      return cp.paymentDate || cp.payment_date;
    }
    return pa.createdAt || pa.created_at || '';
  }
  var finalInvoices = invoices.filter(function(inv) {
    return inv && (inv.invoiceType || inv.invoice_type) === 'final';
  });
  var itemsById = {};
  items.forEach(function(it) { if (it && it.id) itemsById[it.id] = it; });
  var posById = {};
  purchaseOrders.forEach(function(po) { if (po && po.id) posById[po.id] = po; });
  var proposalsById = {};
  proposals.forEach(function(p) { if (p && p.id) proposalsById[String(p.id)] = p; });
  var projectsByLabel = {};
  projects.forEach(function(pr) { if (pr && pr.label) projectsByLabel[pr.label] = pr; });

  var rows = [];
  // PO ids emitted via the FIN pass — used to skip duplicates in the
  // PO-only pass below.
  var coveredPoIds = {};

  // Per-PO vendor-side computation. Shared between the FIN-pair branch
  // and the PO-only branch so both stay aligned (and the bug fix at
  // poAmount = poSubtotalLive, 2026-06-07, applies in both).
  function buildPoVendorSide(po) {
    var matchedVPs = vendorPayments.filter(function(vp) { return vpMatchesPo(vp, po); });
    var actualVendorPayment = matchedVPs.reduce(function(s, vp) {
      return s + (parseFloat(vp.amount) || 0);
    }, 0);
    var hasFinalVendorPayment = matchedVPs.some(function(vp) {
      var t = vp.paymentType || '';
      return t === 'Final' || t === 'Full';
    });
    var latestVendorPaymentDate = matchedVPs.reduce(function(latest, vp) {
      var d = String((vp && (vp.paymentDate || vp.payment_date)) || '');
      return d > latest ? d : latest;
    }, '');
    var poAmount = poSubtotalLive(po, items);
    var vendorBalance = Math.max(0, poAmount - actualVendorPayment);
    return {
      poAmount: poAmount,
      actualVendorPayment: actualVendorPayment,
      vendorBalance: vendorBalance,
      hasFinalVendorPayment: hasFinalVendorPayment,
      latestVendorPaymentDate: latestVendorPaymentDate,
    };
  }

  finalInvoices.forEach(function(fin) {
    var finItemIds = Array.isArray(fin.itemIds) ? fin.itemIds : (Array.isArray(fin.item_ids) ? fin.item_ids : []);
    var project    = fin.project || fin.project_label || '';
    var propId     = fin.proposalId || fin.proposal_id || '';
    var proposal   = propId ? proposalsById[String(propId)] : null;
    var proposalRef = proposal ? (proposal.proposal_ref || proposal.proposalRef || '—') : '—';
    var finRef     = fin.invoiceRef || fin.invoice_ref || '—';
    var amountDue  = parseFloat(fin.amountDue || fin.amount_due) || 0;
    var clientPaid = calcPaidForDocument(paymentAllocations, 'invoice', fin.id);
    var clientBalance = Math.max(0, amountDue - clientPaid);
    var finStatus  = fin.status || 'draft';

    var finAllocs = paymentAllocations.filter(function(pa) {
      if (!pa) return false;
      var docType = pa.documentType || pa.document_type;
      var docId   = pa.documentId   || pa.document_id;
      return docType === 'invoice' && docId === fin.id;
    });
    var lastPaymentDate = finAllocs.reduce(function(latest, pa) {
      var ts = paymentDateFromAlloc(pa);
      return ts > latest ? ts : latest;
    }, '');
    // Paid Date — money-landed date for QB cash-basis. Extracted to
    // finPaidDate (2026-09-12) so the sales-tax report dates a FIN by
    // exactly the same rule this page shows in its Paid Date column.
    var paidDateForQb = finPaidDate(fin, {
      invoices:           invoices,
      paymentAllocations: paymentAllocations,
      clientPayments:     clientPayments,
    }).date;

    // Client-side breakdown for QB entry — Items+Fees, Markup, Tax,
    // Full Payment (lifecycle revenue).
    var finProject = projectsByLabel[fin.project || fin.project_label] || null;
    var fNum = function(v) {
      var n = parseFloat(String(v == null ? '' : v).replace(/[$,]/g, ''));
      return isNaN(n) ? 0 : n;
    };

    var itemsNetCost    = 0;
    var itemsClientCost = 0;
    var feesNetCost     = 0;
    var feesClientCost  = 0;
    var taxTot          = 0;

    // Per-category fee itemization (2026-09-08). Dan could see COGS,
    // Markup and Taxes but never what the fees were — this table had zero
    // mentions of freight, crating, tariff or installation. Each bucket
    // carries cost / billed / margin so the drawer can show the three
    // numbers he actually reconciles against a vendor invoice.
    var feeCats = emptyFeeCategories();
    var customFeeRows = [];
    var productMarkup = 0;

    finItemIds.forEach(function(iid) {
      var it = itemsById[iid];
      if (!it) return;
      var netPrice  = parseFloat(it.netPrice) || 0;
      var qty       = parseFloat(it.qty)      || 1;
      var markupPct = parseFloat(it.markupPct) || 30;
      itemsNetCost    += netPrice * qty;
      itemsClientCost += netPrice * (1 + markupPct / 100) * qty;
      productMarkup   += netPrice * (markupPct / 100) * qty;

      // itemFeeBreakdown is the same helper the invoice builders store
      // their markup split from, so the drawer and the stored column can
      // never disagree about what a fee earned.
      var fb = itemFeeBreakdown(it);
      ['freight', 'crating', 'installation', 'tariff', 'salesTax'].forEach(function(k) {
        var b = fb.byCategory[k] || { cost: 0, client: 0, markup: 0 };
        feeCats[k].cost   += b.cost;
        feeCats[k].client += b.client;
        feeCats[k].markup += b.markup;
      });
      fb.custom.forEach(function(c) {
        feeCats.custom.cost   += c.cost;
        feeCats.custom.client += c.client;
        feeCats.custom.markup += c.markup;
        customFeeRows.push(Object.assign({ itemId: it.id, itemNumber: it.itemNumber || '' }, c));
      });

      // salesTax lives in feeCats for completeness but must NOT enter the
      // COGS / client sums here: project-level tax is derived below by
      // computeItemTax, and a stored item.fees.salesTax would double-count
      // it. The legacy loop excluded it for the same reason.
      feesNetCost    += fb.totals.cost   - (fb.byCategory.salesTax || {}).cost;
      feesClientCost += fb.totals.client - (fb.byCategory.salesTax || {}).client;

      taxTot += computeItemTax(it, finProject, items);
    });

    var r2 = function(x) { return Math.round(x * 100) / 100; };
    Object.keys(feeCats).forEach(function(k) {
      feeCats[k] = { cost: r2(feeCats[k].cost), client: r2(feeCats[k].client), markup: r2(feeCats[k].markup) };
    });
    productMarkup = r2(productMarkup);
    var feeMarkupLive = r2(['freight', 'crating', 'installation', 'tariff', 'custom']
      .reduce(function(s, k) { return s + feeCats[k].markup; }, 0));

    var itemsAndFeesNet = Math.round((itemsNetCost + feesNetCost) * 100) / 100;
    var itemsAndFeesClient = itemsClientCost + feesClientCost;

    var feeCcProcessingTot = parseFloat(fin.feeCcProcessingTot || fin.fee_cc_processing_tot) || 0;
    var adjustmentsRows    = Array.isArray(fin.adjustments) ? fin.adjustments : [];
    var adjustmentsTot     = adjustmentsRows.reduce(function(s, a) {
      return s + (parseFloat(a && a.amount) || 0);
    }, 0);
    var markup = Math.round(
      (itemsAndFeesClient - itemsAndFeesNet + feeCcProcessingTot + adjustmentsTot) * 100
    ) / 100;

    // ── Markup split: stored first, live only as fallback (2026-09-08) ──
    //
    // The columns exist so a margin number stops moving when someone edits
    // an item after the invoice went out. When this FIN carries them, they
    // are the truth of what it billed; NULL means the row predates the
    // columns and we fall back to deriving from current item data — the
    // old behavior, correct for untouched items and drifting for edited
    // ones. markupSource says which, so the drawer can be honest about it.
    //
    // The card fee and adjustments stay OUTSIDE the split on both paths
    // (they have their own stored columns) but remain inside `markup`
    // above, which is why product + fees + ccFee + adjustments == markup.
    var storedProduct = fin.markupProductTot;
    var storedFees    = fin.markupFeesTot;
    var hasStored     = (storedProduct != null || storedFees != null);
    var markupSplit = hasStored
      ? { product: parseFloat(storedProduct) || 0, fees: parseFloat(storedFees) || 0 }
      : { product: productMarkup, fees: feeMarkupLive };
    markupSplit.ccFee       = feeCcProcessingTot;
    markupSplit.adjustments = Math.round(adjustmentsTot * 100) / 100;
    // Stored path (2026-10-06): the Markup column IS the record. `markup`
    // above was derived live; when the FIN carries stored product + fees
    // (backfill on billed actuals, or a CO restatement), the column and
    // the split total both read the stored figure so product + fees +
    // ccFee + adjustments === markup holds on every row, and Full Payment
    // (= COGS + markup, below) follows the record too.
    if (hasStored) {
      markup = Math.round((markupSplit.product + markupSplit.fees + feeCcProcessingTot + adjustmentsTot) * 100) / 100;
    }
    markupSplit.total       = markup;
    var markupSource = hasStored ? 'stored' : 'derived';

    var lifecycleRevenue = Math.round((itemsAndFeesNet + markup) * 100) / 100;
    var salesTax = Math.round(taxTot * 100) / 100;

    // Resolve bundled items → unique PO set
    var poIdSet = {};
    finItemIds.forEach(function(iid) {
      var it = itemsById[iid];
      var poId = it && (it.po_id || it.poId);
      if (poId) poIdSet[poId] = true;
    });
    if (Object.keys(poIdSet).length === 0 && finItemIds.length > 0) {
      purchaseOrders.forEach(function(po) {
        if (!po || !Array.isArray(po.items)) return;
        var hit = po.items.some(function(piid) { return finItemIds.indexOf(piid) >= 0; });
        if (hit) poIdSet[po.id] = true;
      });
    }
    var matchedPOs = Object.keys(poIdSet)
      .map(function(id) { return posById[id]; })
      .filter(Boolean)
      .sort(function(a, b) {
        return (a.po_ref || '').localeCompare(b.po_ref || '');
      });

    var finBase = {
      finId:            fin.id,
      project:          project,
      proposalRef:      proposalRef,
      finRef:           finRef,
      finAmount:        lifecycleRevenue,
      itemsAndFeesNet:  itemsAndFeesNet,
      markup:           markup,
      // Fee itemization + markup split (2026-09-08). Row-level so the
      // drawer can render one FIN's detail without re-walking items.
      feesByCategory:   feeCats,
      customFeeRows:    customFeeRows,
      markupSplit:      markupSplit,
      markupSource:     markupSource,
      salesTax:         salesTax,
      clientBalance:    clientBalance,
      finStatus:        finStatus,
      clientPaidDate:   lastPaymentDate,
      paidDateForQb:    paidDateForQb,
      qbInvoiceId:      fin.qbInvoiceId || fin.qb_invoice_id || '',
      qbPushedAt:       fin.qbPushedAt  || fin.qb_pushed_at  || '',
      qbError:          fin.qbError     || fin.qb_error      || '',
      createdAt:        fin.createdAt   || fin.created_at    || '',
      totalPosForFin:   matchedPOs.length,
    };

    if (matchedPOs.length === 0) {
      rows.push(Object.assign({}, finBase, {
        kind:               'orphan',
        isFirstForFin:      true,
        poId:               null,
        poRef:              '—',
        vendorName:         '—',
        poAmount:           0,
        actualVendorPayment: 0,
        vendorBalance:      0,
        hasFinalVendorPayment: false,
      }));
      return;
    }

    matchedPOs.forEach(function(po, idx) {
      coveredPoIds[po.id] = true;
      var vs = buildPoVendorSide(po);
      rows.push(Object.assign({}, finBase, {
        kind:               'pair',
        isFirstForFin:      idx === 0,
        poId:               po.id,
        poRef:              po.po_ref || '—',
        vendorName:         po.vendorName || '—',
        poAmount:           vs.poAmount,
        actualVendorPayment: vs.actualVendorPayment,
        vendorBalance:      vs.vendorBalance,
        hasFinalVendorPayment: vs.hasFinalVendorPayment,
        latestVendorPaymentDate: vs.latestVendorPaymentDate,
      }));
    });
  });

  // ── PO-only pass — POs visible despite having no FIN yet ─────────
  purchaseOrders.forEach(function(po) {
    if (!po || !po.id) return;
    if (coveredPoIds[po.id]) return;
    if (!RECONCILIATION_PO_VISIBLE_STATUSES[po.status]) return;

    var vs = buildPoVendorSide(po);
    rows.push({
      kind:                    'po_only',
      finId:                   null,
      isFirstForFin:           true,
      project:                 po.project || '',
      proposalRef:             '—',
      finRef:                  '',
      finAmount:               0,
      itemsAndFeesNet:         0,
      markup:                  0,
      // PO-only rows have no FIN, so no fee itemization to show. Shapes
      // match the FIN rows' so the drawer never has to null-check.
      feesByCategory:          emptyFeeCategories(),
      customFeeRows:           [],
      markupSplit:             { product: 0, fees: 0, ccFee: 0, adjustments: 0, total: 0 },
      markupSource:            'none',
      salesTax:                0,
      clientBalance:           0,
      finStatus:               '',
      clientPaidDate:          '',
      paidDateForQb:           '',
      qbInvoiceId:             '',
      qbPushedAt:              '',
      qbError:                 '',
      // createdAt comes from the PO so date-based sorts interleave PO-only
      // rows with FIN-driven rows correctly. issued_at is the more
      // semantically right field but legacy POs may not have it; fall
      // back to created_at.
      createdAt:               po.issued_at || po.created_at || '',
      totalPosForFin:          0,
      poId:                    po.id,
      poRef:                   po.po_ref || '—',
      vendorName:              po.vendorName || '—',
      poAmount:                vs.poAmount,
      actualVendorPayment:     vs.actualVendorPayment,
      vendorBalance:           vs.vendorBalance,
      hasFinalVendorPayment:   vs.hasFinalVendorPayment,
      latestVendorPaymentDate: vs.latestVendorPaymentDate,
    });
  });

  return rows;
}

// ── SALES TAX REPORT (2026-09-12) ────────────────────────────────────────────
//
// "What do we owe each state this month?" Built entirely from data that
// already exists — no new table, no new column.
//
// SCOPE IS US SALES TAX. A project whose state is not a US state code is
// an export: 26-Moskito (BVI) ships on customs invoices and the client's
// customs agent calculates and pays the duty and tax at the destination.
// Nothing there is the firm's to remit. Those sales are reported as a
// footnote rather than dropped, so the money is still accounted for and
// nobody goes looking for a missing project. Cabo (MX) will behave the
// same way without any further change.
//
// Three rules decide the whole thing:
//
//   1. FINAL INVOICES ONLY. The final invoice is the accounting piece;
//      every sale passes through exactly one of them at full value, so
//      counting finals counts every taxable dollar once. Deposits are
//      never read — a deposit that collected the whole transaction is
//      still restated on its FIN at full subtotal with a zero balance.
//
//   2. TAX COMES FROM THE ITEMS, exactly as the Reconciliation page's
//      Taxes column derives it — sum of computeItemTax across the FIN's
//      items. NOT the stored `fee_sales_tax_tot` column. That column is
//      the document's billed fee LINE, and it is legitimately $0 when the
//      deposit already collected the tax (a 100%-vendor-deposit item bills
//      fees + tax at deposit time, so its FIN has no tax left to charge).
//      Reading it would miss that tax entirely: 26HOB-INV-018-FIN stores
//      $0.00 against $363.64 of real tax on its one item.
//
//   3. `subtotal` IS THE SALE, NOT `amount_due`. A FIN closed out by a
//      100%-up-front deposit reads amount_due = 0 while still carrying the
//      full subtotal — tax included, even though its fee line is $0.
//      Reading amount_due would report tax owed against $0 of sales.
//
// Rules 1 and 2 together are what make the count exact: tax lives on the
// ITEM, and each item reaches exactly one final invoice, so no item's tax
// can be missed or counted twice.
//
// Column derivation, per FIN:
//   invoiced    = subtotal                    (goods + fees + tax + card fee)
//   tax         = Σ computeItemTax(item)      (live, per item, as Reconciliation)
//   preTax      = invoiced − tax
//   taxable     = tax ÷ project.taxRate       (exact: one rate per project)
//   nonTaxable  = preTax − taxable            (freight/install a state exempts)
//
// taxable is BACK-DERIVED from the frozen tax rather than recomputed from
// items, so it can never disagree with the tax figure beside it. Cent-level
// rounding can push nonTaxable a penny negative; it is clamped at 0.
//
// A state return asks for gross sales, then exempt, then net taxable, then
// tax — which is why non-taxable is its own column and why FINs carrying
// $0 of tax still count toward invoiced. Exempt sales belong on the return.

// State code → name. `state_tax_records` carries a name for states the
// firm has registered in, but a project can sell into a state before the
// registration exists (which is exactly when the report matters most), so
// the code alone has to resolve to a place. Non-US codes fall through to
// the code itself — 26-Moskito's 'BVI' renders as 'BVI'.
export var US_STATE_NAMES = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', DC: 'District of Columbia',
  FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois',
  IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
  ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan',
  MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri', MT: 'Montana',
  NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
  NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota',
  OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania',
  RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee',
  TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia', WA: 'Washington',
  WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
};

// Period bucketing. 'month' → '2026-08'; 'quarter' → '2026-Q3'.
export function salesTaxPeriodKey(dateStr, grouping) {
  var d = String(dateStr || '').slice(0, 10);
  if (d.length < 7) return '';
  var year  = d.slice(0, 4);
  var month = parseInt(d.slice(5, 7), 10);
  if (!month) return '';
  if (grouping === 'quarter') {
    return year + '-Q' + String(Math.floor((month - 1) / 3) + 1);
  }
  return year + '-' + d.slice(5, 7);
}

var SALES_TAX_MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

export function salesTaxPeriodLabel(key) {
  var k = String(key || '');
  if (k.indexOf('-Q') > 0) {
    var qParts = k.split('-Q');
    return 'Q' + qParts[1] + ' ' + qParts[0];
  }
  var parts = k.split('-');
  if (parts.length < 2) return k;
  var idx = parseInt(parts[1], 10) - 1;
  return (SALES_TAX_MONTH_NAMES[idx] || parts[1]) + ' ' + parts[0];
}

// Every period key from `from` through `to` inclusive, so months with no
// activity still appear. A registered state files a zero return in a month
// it sold nothing; a report that silently omits those months is a report
// that lets a filing slip.
export function salesTaxPeriodRange(fromKey, toKey, grouping) {
  var out = [];
  if (!fromKey || !toKey || fromKey > toKey) return out;
  var per = grouping === 'quarter' ? 4 : 12;
  function parse(k) {
    if (grouping === 'quarter') {
      var qp = k.split('-Q');
      return { y: parseInt(qp[0], 10), n: parseInt(qp[1], 10) };
    }
    var mp = k.split('-');
    return { y: parseInt(mp[0], 10), n: parseInt(mp[1], 10) };
  }
  function fmt(y, n) {
    if (grouping === 'quarter') return y + '-Q' + n;
    return y + '-' + (n < 10 ? '0' + n : String(n));
  }
  var a = parse(fromKey), b = parse(toKey);
  var y = a.y, n = a.n, guard = 0;
  while ((y < b.y || (y === b.y && n <= b.n)) && guard++ < 600) {
    out.push(fmt(y, n));
    n += 1;
    if (n > per) { n = 1; y += 1; }
  }
  return out;
}

// ── Filing rules per state (2026-10-16, design handoff v2) ──────────────
// The STATE-level record (jurisdiction '') is the rule source; a city row
// (Vail under CO) stands in only when the state has no row of its own.
export function salesTaxStateRuleFor(stateTaxRecords, stateCode, jurisdiction) {
  var code = String(stateCode || '').toUpperCase();
  var j = String(jurisdiction || '').trim().toLowerCase();
  if (j) {
    var city = (stateTaxRecords || []).find(function(r) {
      return r && String(r.stateCode || '').toUpperCase() === code && String(r.jurisdiction || '').trim().toLowerCase() === j;
    });
    if (city) return city;
  }
  var rec = (stateTaxRecords || []).find(function(r) {
    return r && String(r.stateCode || '').toUpperCase() === code && !String(r.jurisdiction || '').trim();
  }) || (stateTaxRecords || []).find(function(r) { return r && String(r.stateCode || '').toUpperCase() === code; });
  return rec || null;
}

// salesTaxBucketDate — the date that decides WHICH PERIOD an item's tax
// lands in. Pure.
//   state: { taxBasis: 'payment' | 'delivery', registeredFrom }
//   item:  { deliveredToStateOn, installDate, installPlannedDate, installEventId }
//   fin:   { paidDate }
//   project (optional): for the planned-install fallback (install events)
// Returns { date, basis, missing, preRegistration }:
//   payment basis  → the FIN's paid date, always.
//   delivery basis → items.delivered_to_state_on; else the item's actual
//     install date; else its planned install (project install event or
//     manual plan — "the project's install date" in the handoff); else
//     the FIN paid date, flagged missing so the row can say so in amber.
//   preRegistration = the chosen date fell before state.registeredFrom.
//     Those items FILE ON THE FIRST RETURN (Cimon, 2026-10-16: Wyoming
//     would not license the firm until goods were installed, so the first
//     return pays everything to date): the date becomes registeredFrom
//     (basis 'first_return', originalDate kept) and the row says so in
//     amber. Never dropped.
export function salesTaxBucketDate(state, item, fin, project) {
  var basis = state && state.taxBasis === 'delivery' ? 'delivery' : 'payment';
  var paid = String((fin && fin.paidDate) || '').slice(0, 10);
  var out;
  if (basis === 'payment') {
    out = { date: paid, basis: 'payment', missing: false };
  } else {
    var d = String((item && item.deliveredToStateOn) || '').slice(0, 10);
    if (d) out = { date: d, basis: 'delivery', missing: false };
    else {
      var inst = String((item && item.installDate) || '').slice(0, 10);
      if (inst) out = { date: inst, basis: 'install', missing: false };
      else {
        var plan = item ? resolveInstallPlan(item, project || null) : null;
        var planned = plan ? String(plan.startDate || '').slice(0, 10) : '';
        if (planned) out = { date: planned, basis: 'planned', missing: false };
        else out = { date: paid, basis: 'fallback', missing: true };
      }
    }
  }
  var reg = String((state && state.registeredFrom) || '').slice(0, 10);
  out.preRegistration = !!(reg && out.date && out.date < reg);
  if (out.preRegistration) {
    out.originalDate = out.date;
    out.originalBasis = out.basis;
    out.date = reg;
    out.basis = 'first_return';
  }
  return out;
}

// The first return since registration: the period that CONTAINS
// registered_from. Blank registration → never "first".
export function salesTaxFirstReturn(periodKey, registeredFrom, grouping) {
  var reg = String(registeredFrom || '').slice(0, 10);
  if (!reg || !periodKey) return false;
  return salesTaxPeriodKey(reg, grouping || 'month') === periodKey;
}

// "Jan–Jul" / "Mar" — the span of FIN paid dates behind a delivery-basis
// row ("37 items paid Jan–Jul, delivered to WY this month").
export function salesTaxPaidDateRange(fins) {
  var dates = (fins || []).map(function(f) { return String((f && f.paidDate) || '').slice(0, 10); }).filter(Boolean).sort();
  if (!dates.length) return '';
  var mon = function(iso) { var d = new Date(iso + 'T00:00:00'); return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short' }); };
  var a = mon(dates[0]), b = mon(dates[dates.length - 1]);
  if (!a) return '';
  return a === b ? a : a + '–' + b;
}

// buildSalesTaxReport — the filing view.
//
// args: {
//   invoices, items, paymentAllocations, clientPayments, projects,
//   stateTaxRecords,
//   grouping: 'month' | 'quarter'   (default 'month')
//   year:     '2026' | ''           ('' = every period with activity)
//   today:    'YYYY-MM-DD'          (injectable for tests)
// }
//
// Returns {
//   periods: [{ key, label, invoiced, nonTaxable, taxable, tax,
//               states: [{ stateCode, stateName, registered,
//                          registrationStatus, rate, invoiced, nonTaxable,
//                          taxable, tax, hasActivity,
//                          projects: [{ project, rate, invoiced, nonTaxable,
//                                       taxable, tax, fins: [...] }] }] }],
//   pending:    { tax, count, byState: [{ stateCode, stateName, tax, count }] },
//   unassigned: [{ invoiceRef, project, tax, reason }],
//   grandTax:   number,
// }
export function buildSalesTaxReport(args) {
  var invoices           = (args && args.invoices)           || [];
  var items              = (args && args.items)              || [];
  var paymentAllocations = (args && args.paymentAllocations) || [];
  var clientPayments     = (args && args.clientPayments)     || [];
  var projects           = (args && args.projects)           || [];
  var stateTaxRecords    = (args && args.stateTaxRecords)    || [];
  // 2026-09-13 — snapshot + information rails. All optional; without them
  // the report reads live items and shows no deposit-date / open-PO notes.
  var finalInvoiceItems  = (args && args.finalInvoiceItems)  || [];
  var purchaseOrders     = (args && args.purchaseOrders)     || [];
  var vendorPayments     = (args && args.vendorPayments)     || [];
  var vendorPaymentItems = (args && args.vendorPaymentItems) || [];
  var vendorCreditLedger = (args && args.vendorCreditLedger) || [];
  var salesTaxCodes      = (args && args.salesTaxCodes)      || [];   // 2026-10-16 returns per code
  var grouping           = (args && args.grouping) === 'quarter' ? 'quarter' : 'month';
  var yearFilter         = String((args && args.year) || '');
  var today              = String((args && args.today) || new Date().toISOString().slice(0, 10));
  // 2026-10-16 handoff v3: the Returns-due view lists next month's
  // returns ("period opens Nov 1"), so the spine may run past today.
  var spineThrough       = String((args && args.spineThrough) || '');

  var r2 = function(x) { return Math.round((x || 0) * 100) / 100; };
  var num = function(v) {
    var n = parseFloat(String(v == null ? '' : v).replace(/[$,]/g, ''));
    return isNaN(n) ? 0 : n;
  };
  // snapshotsByFin[finId][itemId] = final_invoice_items row (camelCase)
  var snapshotsByFin = {};
  finalInvoiceItems.forEach(function(row) {
    if (!row || !row.finalInvoiceId || !row.itemId) return;
    (snapshotsByFin[row.finalInvoiceId] = snapshotsByFin[row.finalInvoiceId] || {})[row.itemId] = row;
  });
  // depPaidDates[depInvoiceId] = earliest client payment date against it
  var depPaidDates = {};
  (function() {
    var payDate = {};
    clientPayments.forEach(function(p) { if (p && p.id) payDate[p.id] = p.paymentDate || p.payment_date || ''; });
    paymentAllocations.forEach(function(a) {
      if (!a || (a.documentType || a.document_type) !== 'invoice') return;
      var d = payDate[a.paymentId || a.payment_id];
      if (!d) return;
      var docId = a.documentId || a.document_id;
      if (!depPaidDates[docId] || d < depPaidDates[docId]) depPaidDates[docId] = d;
    });
  })();
  // Category accumulators: { taxable: {cat: $}, nonTaxable: {cat: $}, other: $ }
  var emptyCats = function() {
    var t = {}, n = {};
    TAX_CATEGORY_KEYS.forEach(function(k) { t[k] = 0; n[k] = 0; });
    return { taxable: t, nonTaxable: n, other: 0 };
  };
  var addCats = function(into, from) {
    TAX_CATEGORY_KEYS.forEach(function(k) {
      into.taxable[k]    += from.taxable[k] || 0;
      into.nonTaxable[k] += from.nonTaxable[k] || 0;
    });
    into.other += from.other || 0;
  };
  var roundCats = function(c) {
    var out = emptyCats();
    TAX_CATEGORY_KEYS.forEach(function(k) {
      out.taxable[k] = r2(c.taxable[k]); out.nonTaxable[k] = r2(c.nonTaxable[k]);
    });
    out.other = r2(c.other);
    return out;
  };
  // Which categories the state taxes here — read off the accumulated
  // bases (a category is "taxed" when any taxable dollars landed in it),
  // falling back to the project flags for a zero-sales project.
  var taxedCategoryKeys = function(c, proj) {
    var keys = TAX_CATEGORY_KEYS.filter(function(k) { return c && c.taxable[k] > 0.005; });
    if (keys.length || !proj) return keys;
    var flags = {
      merchandise: proj.taxesMerchandise !== false, freight: !!proj.taxesFreight,
      crating: !!proj.taxesCrating, installation: !!proj.taxesInstall,
      tariff: !!proj.taxesTariff, storage: !!proj.taxesStorage,
    };
    return TAX_CATEGORY_KEYS.filter(function(k) { return flags[k]; });
  };

  var projByLabel = {};
  projects.forEach(function(pr) { if (pr && pr.label) projByLabel[pr.label] = pr; });
  var itemsById = {};
  items.forEach(function(it) { if (it && it.id) itemsById[it.id] = it; });

  // Registered states drive the row universe, not the projects that
  // happened to sell this month. 'closed' and 'not_required' registrations
  // are dropped — nothing to file.
  var registrations = {};
  stateTaxRecords.forEach(function(st) {
    if (!st) return;
    var code = String(st.stateCode || '').toUpperCase();
    if (!code) return;
    var status = String(st.status || 'active');
    if (status === 'closed' || status === 'not_required') return;
    // A city-level row (Vail under CO) registers the STATE for the report's
    // row universe, but never overrides the state's own row when one exists.
    var isCity = !!String(st.jurisdiction || '').trim();
    if (isCity && registrations[code] && !registrations[code].cityOnly) return;
    registrations[code] = { stateName: st.stateName || code, status: status, cityOnly: isCity };
  });

  // Per-state filing rules (2026-10-16): the STATE-level record wins
  // (jurisdiction ''), a city row stands in only when no state row exists.
  var stateRules = {};
  stateTaxRecords.forEach(function(st) {
    if (!st) return;
    var code = String(st.stateCode || '').toUpperCase();
    if (!code) return;
    var isCity = !!String(st.jurisdiction || '').trim();
    if (isCity && stateRules[code] && !stateRules[code].cityOnly) return;
    stateRules[code] = {
      taxBasis: st.taxBasis === 'delivery' ? 'delivery' : 'payment',
      registeredFrom: st.registeredFrom || '',
      filingFrequency: st.filingFrequency || 'monthly',
      cityOnly: isCity,
    };
  });

  var fins = invoices.filter(function(inv) {
    return inv && (inv.invoiceType || inv.invoice_type) === 'final';
  });

  // buckets[periodKey][stateCode][projectLabel] = accumulator
  var buckets = {};
  var pendingByState = {};
  var pendingTax = 0, pendingCount = 0;
  var unassigned = [];
  var exportsByPlace = {};
  var minKey = '', maxKey = '';
  // rolled[originalPeriodKey][stateCode] — pre-registration deliveries
  // that FILE on the first return. The figures still belong to the month
  // the goods arrived, and the Detail view prints that month with "in Sep
  // return" instead of pretending nothing happened (handoff v3 §5).
  var rolled = {};

  fins.forEach(function(fin) {
    var projectLabel = fin.project || fin.project_label || '';
    // '00-' prefixed projects are training sandboxes (00-TRAINING). Their
    // invoices are practice runs — real enough to look like sales, and a
    // state return is the last place they belong.
    if (projectLabel.indexOf('00-') === 0) return;
    var proj    = projByLabel[projectLabel] || null;
    // Live per-item tax — the Reconciliation Taxes column's own derivation.
    // The stored fee line is $0 whenever the deposit already collected the
    // tax, so it cannot be the source here (see rule 2 above).
    var finItemIds = Array.isArray(fin.itemIds) ? fin.itemIds
      : (Array.isArray(fin.item_ids) ? fin.item_ids : []);
    // Per-category split of the same walk (computeItemTaxBreakdown), so
    // the return can name WHY a dollar is not taxed — freight, labor,
    // storage — instead of one anonymous non-taxable figure.
    var cats = emptyCats();
    var tax = 0;
    var liveTax = 0;
    // ── Snapshot first (2026-09-13). The closed FIN is the taxable
    // transaction and the amount is what it billed. When every item on
    // the FIN has a final_invoice_items row, file from those rows; the
    // live walk below then only measures DRIFT (item edited / deleted
    // after billing). Without a complete snapshot, live is the figure.
    var snapRows = snapshotsByFin[fin.id] || {};
    var snapComplete = finItemIds.length > 0 && finItemIds.every(function(iid) { return !!snapRows[iid]; });
    var liveCats = emptyCats();
    // Per-item figures ride alongside the FIN sums (2026-10-16): a
    // delivery-basis state buckets each ITEM by the month it was
    // delivered into the state, so one FIN can straddle two periods.
    var perItem = [];
    finItemIds.forEach(function(iid) {
      var it = itemsById[iid];
      var itemTax = 0, itemCats = emptyCats();
      var liveItemTax = 0, liveItemCats = emptyCats();
      if (it) {
        var bd = computeItemTaxBreakdown(it, proj, items);
        var mulLive = ccFeeMul(it);
        liveItemTax = bd.tax * mulLive;
        liveTax += liveItemTax;
        TAX_CATEGORY_KEYS.forEach(function(k) {
          liveItemCats.taxable[k]    = bd.taxable[k] * mulLive;
          liveItemCats.nonTaxable[k] = bd.nonTaxable[k] * mulLive;
          liveCats.taxable[k]    += liveItemCats.taxable[k];
          liveCats.nonTaxable[k] += liveItemCats.nonTaxable[k];
        });
      }
      if (snapComplete) {
        var sr = snapRows[iid];
        itemTax = sr.tax;
        tax += sr.tax;
        var sc = sr.cats || {};
        TAX_CATEGORY_KEYS.forEach(function(k) {
          itemCats.taxable[k]    = num((sc.taxable    || {})[k]);
          itemCats.nonTaxable[k] = num((sc.nonTaxable || {})[k]);
          cats.taxable[k]    += itemCats.taxable[k];
          cats.nonTaxable[k] += itemCats.nonTaxable[k];
        });
      } else { itemTax = liveItemTax; itemCats = liveItemCats; }
      perItem.push({ itemId: iid, item: it || null, tax: itemTax, cats: itemCats });
    });
    if (!snapComplete) { tax = liveTax; cats = liveCats; }
    tax = r2(tax);
    liveTax = r2(liveTax);
    var taxSource = snapComplete ? 'snapshot' : 'live';
    // subtotal is the sale. amount_due/amount only stand in for legacy
    // rows that never carried a subtotal at all.
    var invoiced = num(fin.subtotal);
    if (invoiced <= 0) invoiced = num(fin.amountDue || fin.amount_due) || num(fin.amount);
    invoiced = r2(invoiced);

    // ── Drift: what the FIN billed vs what its items say NOW. The FIN
    // subtotal was built from these items, so a non-zero gap means an
    // item moved after billing. Information only — the filed figure is
    // the snapshot. (Live lifecycle: product + fees + tax, × card-fee
    // multiplier, plus the document-level card fee, its tax, and any
    // adjustment lines — the same terms the FIN summed.)
    var liveLifecycle = 0;
    finItemIds.forEach(function(iid) {
      var it = itemsById[iid];
      if (it) liveLifecycle += itemLifecycleOwed(it, items, proj) * ccFeeMul(it);
    });
    // invoiceCcFeeTot's residual derivation (subtotal − product − fees)
    // needs the stored totals to exist; on a row that never carried them
    // the whole subtotal would read as a card fee. Only trust it when the
    // stored column is present or product_total is populated.
    var ccFee = (fin.feeCcProcessingTot != null || num(fin.productTotal) > 0) ? invoiceCcFeeTot(fin) : 0;
    var ccTax = (proj && proj.taxesMerchandise !== false && ccFee > 0) ? r2(ccFee * (num(proj.taxRate))) : 0;
    var adjTot = (Array.isArray(fin.adjustments) ? fin.adjustments : []).reduce(function(s, a) { return s + num(a && a.amount); }, 0);
    liveLifecycle = r2(liveLifecycle + ccFee + ccTax + adjTot);
    var drift = invoiced > 0 && finItemIds.length > 0 ? r2(invoiced - liveLifecycle) : 0;
    if (Math.abs(drift) < 0.03) drift = 0;   // per-item cent rounding

    // ── Deposit date (information only, never the filing date). The
    // earliest client payment against any DEP that collected for these
    // items — "the client paid tax on this in June; we file it with the
    // FIN in August" is a legitimate accountant question.
    var depDate = '';
    finItemIds.forEach(function(iid) {
      var it = itemsById[iid];
      var depId = it && it.clientDepositInvoiceId;
      if (!depId) return;
      var d = depPaidDates[depId];
      if (d && (!depDate || d < depDate)) depDate = d;
    });

    // ── Open PO (information only). The vendor side is not the taxable
    // event, but "client paid, vendor still owed" is worth a word on the
    // row for reconciliation.
    var openPos = [];
    if (purchaseOrders.length) {
      var seenPo = {};
      finItemIds.forEach(function(iid) {
        var it = itemsById[iid];
        var po = it ? poForItem(it, purchaseOrders) : null;
        if (!po || seenPo[po.id]) return;
        seenPo[po.id] = true;
        var st = isPoFullyPaid(po, items, vendorPayments, vendorPaymentItems, vendorCreditLedger);
        if (!st.isFullyPaid && st.expectedTotal > 0) openPos.push(po.po_ref || po.poRef || String(po.id));
      });
    }

    var stateCode = proj ? String(proj.state || '').toUpperCase() : '';
    var rate      = proj ? num(proj.taxRate) : 0;
    // Jurisdiction = the delivery locality on the project record. WY files
    // by county and CO by home-rule city, so one blended state figure is
    // not fileable; naming the place on every row is the first step.
    var jurisdiction = proj ? salesTaxJurisdiction(proj) : '';
    var paid      = finPaidDate(fin, {
      invoices: invoices, paymentAllocations: paymentAllocations, clientPayments: clientPayments,
      zeroFinBy: 'close',   // a $0 final is dated by its own close, never the deposit
    });

    if (!paid.date) {
      // Invoiced but not yet collected — real money, not yet owed to
      // anyone. Kept off the totals and shown on its own line.
      if (tax > 0) {
        pendingTax += tax; pendingCount += 1;
        var pk = stateCode || '??';
        pendingByState[pk] = pendingByState[pk] || {
          stateCode: pk,
          stateName: (registrations[pk] || {}).stateName || US_STATE_NAMES[pk] || pk,
          tax: 0, count: 0,
        };
        pendingByState[pk].tax += tax;
        pendingByState[pk].count += 1;
      }
      return;
    }
    if (!stateCode) {
      if (tax > 0 || invoiced > 0) {
        unassigned.push({
          invoiceRef: fin.invoiceRef || fin.invoice_ref || '—',
          project: projectLabel || '—', tax: tax,
          reason: proj ? 'project has no state' : 'project not found',
        });
      }
      return;
    }

    // Outside the US — the client's customs agent handles it. Counted as
    // sales, never as tax owed. (Checked before any period math so an
    // export never needs a delivery date.)
    if (!US_STATE_NAMES[stateCode]) {
      var exKey = salesTaxPeriodKey(paid.date, grouping);
      if (!exKey) return;
      if (yearFilter && exKey.slice(0, 4) !== yearFilter) return;
      var ex = exportsByPlace[stateCode] || (exportsByPlace[stateCode] = {
        place: stateCode, invoiced: 0, count: 0, projects: {},
      });
      ex.invoiced += invoiced;
      ex.count += 1;
      ex.projects[projectLabel] = true;
      return;
    }

    var preTax  = invoiced - tax;
    // Whatever the invoice carries beyond the item categories (the
    // document-level card fee, a CO adjustment line, legacy lumps) is
    // real money on the return but has no item category — it reports as
    // "other" rather than vanishing or being forced into a bucket.
    var catTotal = 0;
    TAX_CATEGORY_KEYS.forEach(function(k) { catTotal += cats.taxable[k] + cats.nonTaxable[k]; });
    var residual = r2(preTax - catTotal);

    // ── Period basis (2026-10-16). 'payment' = the FIN's paid date, one
    // entry per FIN (the 2026-09-13 rule). 'delivery' = each item lands in
    // the period of its delivery into the state; the FIN splits into one
    // entry per delivery period, and the FIN-level residual (card fee,
    // adjustments — money with no item) rides with the LAST delivery, so
    // the sale completes where its last piece landed.
    var rule = stateRules[stateCode] || { taxBasis: 'payment', registeredFrom: '' };
    var groups = [];
    if (rule.taxBasis === 'delivery' && perItem.length) {
      var byDate = {};
      perItem.forEach(function(pi) {
        var b = salesTaxBucketDate(rule, pi.item, { paidDate: paid.date }, proj);
        var g = byDate[b.date] || (byDate[b.date] = { date: b.date, items: [], missing: 0, preReg: 0, basis: b.basis, preRegDates: [] });
        g.items.push(pi);
        if (b.missing) g.missing += 1;
        if (b.preRegistration) {
          g.preReg += 1; g.preRegDates.push(b.originalDate);
          var okey = salesTaxPeriodKey(b.originalDate, grouping);
          if (okey && (!yearFilter || okey.slice(0, 4) === yearFilter)) {
            var rs = (rolled[okey] = rolled[okey] || {});
            var rr = rs[stateCode] || (rs[stateCode] = { tax: 0, cats: emptyCats(), itemCount: 0, rate: rate, into: salesTaxPeriodKey(b.date, grouping) });
            rr.tax += pi.tax; addCats(rr.cats, pi.cats); rr.itemCount += 1;
          }
        }
      });
      groups = Object.keys(byDate).sort().map(function(d) { return byDate[d]; });
      groups[groups.length - 1].residual = residual;
      groups.forEach(function(g) { g.deliveryBasis = true; });
    } else {
      groups = [{ date: paid.date, items: perItem, missing: 0, preReg: 0, basis: 'payment', residual: residual, deliveryBasis: false }];
    }

    groups.forEach(function(g) {
      var key = salesTaxPeriodKey(g.date, grouping);
      if (!key) return;
      if (yearFilter && key.slice(0, 4) !== yearFilter) return;
      if (!minKey || key < minKey) minKey = key;
      if (!maxKey || key > maxKey) maxKey = key;

      var gTax = 0, gCats = emptyCats();
      g.items.forEach(function(pi) { gTax += pi.tax; addCats(gCats, pi.cats); });
      var gCatTotal = 0;
      TAX_CATEGORY_KEYS.forEach(function(k) { gCatTotal += gCats.taxable[k] + gCats.nonTaxable[k]; });
      gTax = r2(gTax);
      var gResidual = g.residual || 0;
      var gPreTax = g.deliveryBasis ? r2(gCatTotal + gResidual) : preTax;
      var gInvoiced = g.deliveryBasis ? r2(gPreTax + gTax) : invoiced;
      var taxable = rate > 0 ? gTax / rate : 0;
      if (taxable > gPreTax) taxable = gPreTax;           // cent-level rounding
      var nonTaxable = Math.max(0, gPreTax - taxable);
      gCats.other = r2(gPreTax - gCatTotal);

      buckets[key] = buckets[key] || {};
      buckets[key][stateCode] = buckets[key][stateCode] || {};
      var slot = buckets[key][stateCode][projectLabel];
      if (!slot) {
        slot = buckets[key][stateCode][projectLabel] = {
          project: projectLabel, rate: rate, jurisdiction: jurisdiction,
          invoiced: 0, grossSales: 0, nonTaxable: 0, taxable: 0, tax: 0, fins: [],
          cats: emptyCats(),
          driftCount: 0, driftTotal: 0, liveCount: 0,
          deliveryBasis: false, missingDeliveryCount: 0, preRegistrationCount: 0, itemCount: 0,
        };
      }
      // grossSales is the return's first line: the sale before tax
      // (taxable + non-taxable). `invoiced` stays as what the client was
      // billed INCLUDING tax — a reconciliation figure, never a sales figure.
      slot.invoiced   += gInvoiced;
      slot.grossSales += gPreTax;
      slot.nonTaxable += nonTaxable;
      slot.taxable    += taxable;
      slot.tax        += gTax;
      addCats(slot.cats, gCats);
      if (g.deliveryBasis) slot.deliveryBasis = true;
      slot.missingDeliveryCount += g.missing;
      slot.preRegistrationCount += g.preReg;
      slot.preRegistrationDates = (slot.preRegistrationDates || []).concat(g.preRegDates || []);
      slot.itemCount += g.items.length;
      slot.fins.push({
        invoiceRef: fin.invoiceRef || fin.invoice_ref || '—',
        invoiceId:  fin.id,
        paidDate:   paid.date,
        paidBasis:  paid.basis,
        invoiced:   gInvoiced,
        grossSales: r2(gPreTax),
        taxable:    r2(taxable),
        nonTaxable: r2(nonTaxable),
        tax:        gTax,
        cats:       roundCats(gCats),
        // 2026-09-13 information rails
        taxSource:       taxSource,          // 'snapshot' | 'live'
        liveTax:         liveTax,            // what the items say today
        drift:           drift,              // billed subtotal − live lifecycle; 0 = unchanged
        depositPaidDate: depDate,            // client's deposit payment, info only
        openPos:         openPos,            // PO refs still owed to the vendor
        // 2026-10-16 delivery basis: the items of THIS entry (a FIN may
        // split across periods), the date that placed them, and the
        // fallback counts the row prints in amber.
        itemIds:         g.items.map(function(pi) { return pi.itemId; }),
        deliveryBasis:   !!g.deliveryBasis,
        bucketDate:      g.date,
        bucketBasis:     g.basis,            // 'payment' | 'delivery' | 'install' | 'planned' | 'fallback'
        missingDeliveryCount: g.missing,
        preRegistrationCount: g.preReg,
        partial:         groups.length > 1,  // this FIN has items in another period too
      });
      if (drift) { slot.driftCount += 1; slot.driftTotal += drift; }
      if (taxSource === 'live') slot.liveCount += 1;
    });
  });

  // Period spine. Runs from the first period with activity to the later of
  // the last period with activity and today, so the current (unfiled) month
  // always has a row even before its first sale lands.
  var todayKey = salesTaxPeriodKey(today, grouping);
  var spineEnd = (todayKey && todayKey > maxKey) ? todayKey : maxKey;
  if (spineThrough && spineThrough > spineEnd) spineEnd = spineThrough;
  // Every month since the earliest registration gets rows (handoff v3
  // §5) — a registered state owes a return from its registration on,
  // sale or no sale.
  Object.keys(stateRules).forEach(function(c) {
    var rk = stateRules[c].registeredFrom ? salesTaxPeriodKey(stateRules[c].registeredFrom, grouping) : '';
    if (rk && (!minKey || rk < minKey)) minKey = rk;
  });
  Object.keys(rolled).forEach(function(k) { if (!minKey || k < minKey) minKey = k; });
  // A period with no taxable sale anywhere still needs its rows — that is
  // the whole point of a zero return. With no activity at all, start the
  // spine at the top of the filtered year (or at today when unfiltered)
  // so a quiet stretch reads as "nothing sold, still file" rather than as
  // an empty screen.
  var spineStart = minKey;
  if (!spineStart) {
    spineStart = yearFilter
      ? salesTaxPeriodKey(yearFilter + '-01-01', grouping)
      : todayKey;
  }
  var keys = salesTaxPeriodRange(spineStart, spineEnd, grouping);
  if (!keys.length) keys = Object.keys(buckets).sort();
  if (yearFilter) {
    keys = keys.filter(function(k) { return k.slice(0, 4) === yearFilter; });
  }

  var stateCodes = {};
  Object.keys(registrations).forEach(function(c) { stateCodes[c] = true; });
  Object.keys(buckets).forEach(function(k) {
    Object.keys(buckets[k]).forEach(function(c) { stateCodes[c] = true; });
  });

  var periods = keys.sort().reverse().map(function(key) {
    var byState = buckets[key] || {};
    var states = Object.keys(stateCodes).sort().map(function(code) {
      var projSlots = byState[code] || {};
      var projRows = Object.keys(projSlots).sort().map(function(label) {
        var sl = projSlots[label];
        return {
          project: sl.project, rate: sl.rate, jurisdiction: sl.jurisdiction || '',
          invoiced: r2(sl.invoiced), grossSales: r2(sl.grossSales), nonTaxable: r2(sl.nonTaxable),
          taxable: r2(sl.taxable), tax: r2(sl.tax),
          cats: roundCats(sl.cats),
          taxedCategories: taxedCategoryKeys(sl.cats, projByLabel[sl.project]),
          driftCount: sl.driftCount, driftTotal: r2(sl.driftTotal), liveCount: sl.liveCount,
          deliveryBasis: !!sl.deliveryBasis,
          missingDeliveryCount: sl.missingDeliveryCount, preRegistrationCount: sl.preRegistrationCount,
          preRegistrationRange: salesTaxPaidDateRange((sl.preRegistrationDates || []).map(function(d) { return { paidDate: d }; })),
          itemCount: sl.itemCount,
          paidDateRange: salesTaxPaidDateRange(sl.fins),
          // 2026-10-16: this project's tax split across the returns its
          // tax code files (the state return alone when no code).
          returns: salesTaxReturnShares(r2(sl.tax), salesTaxCodeForProject(projByLabel[sl.project], salesTaxCodes, sl.stateCode || code)),
          fins: sl.fins.slice().sort(function(a, b) {
            return String(a.invoiceRef).localeCompare(String(b.invoiceRef));
          }),
        };
      });
      var stateCats = emptyCats();
      projRows.forEach(function(pr) { addCats(stateCats, pr.cats); });
      var stateTaxed = {};
      projRows.forEach(function(pr) { pr.taxedCategories.forEach(function(k) { stateTaxed[k] = true; }); });
      var jurisdictions = {};
      projRows.forEach(function(pr) { if (pr.jurisdiction) jurisdictions[pr.jurisdiction] = true; });
      // One rate for the state only when every project agrees. Two
      // localities in one state (a home-rule city beside the state rate)
      // must not be averaged into a number nobody can file.
      var rates = {};
      projRows.forEach(function(pr) { if (pr.rate > 0) rates[pr.rate] = true; });
      var rateKeys = Object.keys(rates);
      // null means "two projects here disagree — do not file one number".
      // No rate at all (a project with taxRate unset) is 0, not mixed.
      var stateRate = rateKeys.length === 1 ? parseFloat(rateKeys[0])
        : (rateKeys.length === 0 ? 0 : null);
      var reg = registrations[code] || null;
      return {
        stateCode: code,
        stateName: (reg && reg.stateName) || US_STATE_NAMES[code] || code,
        registered: !!reg,
        registrationStatus: reg ? reg.status : '',
        rate: stateRate,
        invoiced:   r2(projRows.reduce(function(s, r) { return s + r.invoiced; }, 0)),
        grossSales: r2(projRows.reduce(function(s, r) { return s + r.grossSales; }, 0)),
        nonTaxable: r2(projRows.reduce(function(s, r) { return s + r.nonTaxable; }, 0)),
        taxable:    r2(projRows.reduce(function(s, r) { return s + r.taxable; }, 0)),
        tax:        r2(projRows.reduce(function(s, r) { return s + r.tax; }, 0)),
        cats: roundCats(stateCats),
        taxedCategories: TAX_CATEGORY_KEYS.filter(function(k) { return stateTaxed[k]; }),
        jurisdictions: Object.keys(jurisdictions).sort(),
        driftCount: projRows.reduce(function(s, r) { return s + r.driftCount; }, 0),
        driftTotal: r2(projRows.reduce(function(s, r) { return s + r.driftTotal; }, 0)),
        liveCount:  projRows.reduce(function(s, r) { return s + r.liveCount; }, 0),
        hasActivity: projRows.length > 0,
        // 2026-10-16 filing rules, read off the state record so a zero
        // row knows its basis / frequency too.
        taxBasis: (stateRules[code] || {}).taxBasis || 'payment',
        filingFrequency: (stateRules[code] || {}).filingFrequency || 'monthly',
        registeredFrom: (stateRules[code] || {}).registeredFrom || '',
        deliveryBasis: projRows.some(function(r) { return r.deliveryBasis; }),
        missingDeliveryCount: projRows.reduce(function(s, r) { return s + r.missingDeliveryCount; }, 0),
        preRegistrationCount: projRows.reduce(function(s, r) { return s + r.preRegistrationCount; }, 0),
        preRegistrationRange: (function() { var rs = projRows.map(function(r) { return r.preRegistrationRange; }).filter(Boolean); return rs.length ? (rs.length === 1 ? rs[0] : rs.join(', ')) : ''; })(),
        itemCount: projRows.reduce(function(s, r) { return s + r.itemCount; }, 0),
        paidDateRange: salesTaxPaidDateRange(projRows.reduce(function(a, r) { return a.concat(r.fins); }, [])),
        // 2026-10-16: the returns this state files this period — every
        // return any of its codes declares (so a quiet CO period still
        // lists Colorado + Town of Vail), each with the projects' shares
        // summed. Always sums to the state's tax.
        returns: (function() {
          var list = salesTaxStateReturns(code, salesTaxCodes).map(function(r) { return Object.assign({}, r, { tax: 0 }); });
          projRows.forEach(function(pr) {
            (pr.returns || []).forEach(function(sh) {
              var slot = list.find(function(r) { return r.key === sh.key; }) || list[0];
              slot.tax = r2(slot.tax + sh.tax);
              if (!slot.rate && sh.rate) slot.rate = sh.rate;
            });
          });
          return list;
        })(),
        projects: projRows,
      };
    });
    return {
      key: key,
      label: salesTaxPeriodLabel(key),
      states: states,
      invoiced:   r2(states.reduce(function(s, st) { return s + st.invoiced; }, 0)),
      grossSales: r2(states.reduce(function(s, st) { return s + st.grossSales; }, 0)),
      nonTaxable: r2(states.reduce(function(s, st) { return s + st.nonTaxable; }, 0)),
      taxable:    r2(states.reduce(function(s, st) { return s + st.taxable; }, 0)),
      tax:        r2(states.reduce(function(s, st) { return s + st.tax; }, 0)),
    };
  });

  // ── Quarterly / annual states fold into their own period (2026-10-16).
  // Cimon, on a monthly view showing California three times with three
  // "Record zero return" buttons: "maybe i'm confused by how this is being
  // presented." A state files at ITS frequency, so on the monthly spine a
  // quarterly state appears once per quarter — on the latest spine month
  // of that quarter — with the quarter's figures summed, periodKey
  // '2026-Q3' (what its filings are keyed on) and a label the row prints.
  // Annual states fold the same way onto 'YYYY'. The band's own totals
  // re-sum afterwards so nothing is counted twice.
  if (grouping === 'month') {
    var sumNum = function(list, k) { return r2(list.reduce(function(a, x) { return a + (Number(x[k]) || 0); }, 0)); };
    var union = function(list, k) { var seen = {}; list.forEach(function(x) { (x[k] || []).forEach(function(v) { seen[v] = true; }); }); return Object.keys(seen).sort(); };
    var mergeReturns = function(lists) {
      var out = [];
      lists.forEach(function(l) { (l || []).forEach(function(r) {
        var hit = out.find(function(o) { return o.key === r.key; });
        if (hit) { hit.tax = r2(hit.tax + r.tax); if (!hit.rate && r.rate) hit.rate = r.rate; }
        else out.push(Object.assign({}, r));
      }); });
      return out;
    };
    var mergeProjects = function(lists) {
      var out = [];
      lists.forEach(function(l) { (l || []).forEach(function(pr) {
        var hit = out.find(function(o) { return o.project === pr.project; });
        if (!hit) { out.push(Object.assign({}, pr, { fins: (pr.fins || []).slice(), returns: (pr.returns || []).slice() })); return; }
        ['invoiced', 'grossSales', 'nonTaxable', 'taxable', 'tax', 'driftTotal'].forEach(function(k) { hit[k] = r2(hit[k] + pr[k]); });
        ['driftCount', 'liveCount', 'missingDeliveryCount', 'preRegistrationCount', 'itemCount'].forEach(function(k) { hit[k] = (hit[k] || 0) + (pr[k] || 0); });
        hit.cats = (function() { var c = emptyCats(); addCats(c, hit.cats); addCats(c, pr.cats); return roundCats(c); })();
        hit.taxedCategories = union([hit, pr], 'taxedCategories');
        hit.deliveryBasis = hit.deliveryBasis || pr.deliveryBasis;
        hit.fins = hit.fins.concat(pr.fins || []);
        hit.returns = mergeReturns([hit.returns, pr.returns]);
        hit.paidDateRange = salesTaxPaidDateRange(hit.fins);
        if (hit.rate !== pr.rate) hit.rate = null;
      }); });
      return out;
    };
    Object.keys(stateRules).forEach(function(code) {
      var freq = (stateRules[code] || {}).filingFrequency;
      if (freq !== 'quarterly' && freq !== 'annual') return;
      var groups = {};
      periods.forEach(function(p) {
        if (!p.states.some(function(st) { return st.stateCode === code; })) return;
        var pk = freq === 'quarterly' ? salesTaxPeriodKey(p.key + '-01', 'quarter') : p.key.slice(0, 4);
        (groups[pk] = groups[pk] || []).push(p);
      });
      Object.keys(groups).forEach(function(pk) {
        var members = groups[pk];                     // newest first — members[0] hosts the fold
        var host = members[0];
        var parts = members.map(function(p) { return p.states.find(function(st) { return st.stateCode === code; }); });
        var base = Object.assign({}, parts[0]);
        ['invoiced', 'grossSales', 'nonTaxable', 'taxable', 'tax', 'driftTotal'].forEach(function(k) { base[k] = sumNum(parts, k); });
        ['driftCount', 'liveCount', 'missingDeliveryCount', 'preRegistrationCount', 'itemCount'].forEach(function(k) { base[k] = parts.reduce(function(a, x) { return a + (x[k] || 0); }, 0); });
        base.cats = (function() { var c = emptyCats(); parts.forEach(function(x) { addCats(c, x.cats); }); return roundCats(c); })();
        base.taxedCategories = union(parts, 'taxedCategories');
        base.jurisdictions = union(parts, 'jurisdictions');
        base.hasActivity = parts.some(function(x) { return x.hasActivity; });
        base.deliveryBasis = parts.some(function(x) { return x.deliveryBasis; });
        var rates = {}; parts.forEach(function(x) { if (x.rate) rates[x.rate] = true; });
        base.rate = Object.keys(rates).length === 1 ? parseFloat(Object.keys(rates)[0]) : (Object.keys(rates).length === 0 ? 0 : null);
        base.projects = mergeProjects(parts.map(function(x) { return x.projects; }));
        base.returns = mergeReturns(parts.map(function(x) { return x.returns; }));
        base.preRegistrationRange = parts.map(function(x) { return x.preRegistrationRange; }).filter(Boolean).join(', ');
        base.paidDateRange = salesTaxPaidDateRange(base.projects.reduce(function(a, pr) { return a.concat(pr.fins || []); }, []));
        var endKey = salesTaxPeriodEndDate(pk).slice(0, 7);
        var startMonth = freq === 'quarterly' ? (parseInt(pk.split('-Q')[1], 10) - 1) * 3 : 0;
        var endMonth = freq === 'quarterly' ? startMonth + 2 : 11;
        base.periodKey = pk;
        base.filingGrouping = freq === 'quarterly' ? 'quarter' : 'year';
        base.periodRange = SALES_TAX_MONTH_NAMES[startMonth].slice(0, 3) + '\u2013' + SALES_TAX_MONTH_NAMES[endMonth].slice(0, 3);
        base.periodLabel = salesTaxPeriodLabel(pk);
        base.periodOpen = host.key < endKey;          // the quarter has not ended yet
        members.forEach(function(p) { p.states = p.states.filter(function(st) { return st.stateCode !== code; }); });
        host.states.push(base);
        host.states.sort(function(a, b) { return a.stateCode < b.stateCode ? -1 : a.stateCode > b.stateCode ? 1 : 0; });
      });
    });
    periods.forEach(function(p) {
      ['invoiced', 'grossSales', 'nonTaxable', 'taxable', 'tax'].forEach(function(k) { p[k] = sumNum(p.states, k); });
    });
  }

  return {
    periods: periods,
    pending: {
      tax: r2(pendingTax),
      count: pendingCount,
      byState: Object.keys(pendingByState).sort().map(function(c) {
        var p = pendingByState[c];
        return { stateCode: p.stateCode, stateName: p.stateName, tax: r2(p.tax), count: p.count };
      }),
    },
    unassigned: unassigned,
    // Pre-registration months: what arrived, and which return it filed on.
    rolledForward: Object.keys(rolled).sort().reverse().reduce(function(acc, k) {
      Object.keys(rolled[k]).sort().forEach(function(c) {
        var r = rolled[k][c];
        var catTot = 0;
        TAX_CATEGORY_KEYS.forEach(function(ck) { catTot += r.cats.taxable[ck] + r.cats.nonTaxable[ck]; });
        var taxable = r.rate > 0 ? r.tax / r.rate : 0;
        if (taxable > catTot) taxable = catTot;
        acc.push({
          periodKey: k, stateCode: c, stateName: (registrations[c] || {}).stateName || US_STATE_NAMES[c] || c,
          tax: r2(r.tax), grossSales: r2(catTot), taxable: r2(taxable), nonTaxable: r2(Math.max(0, catTot - taxable)),
          rate: r.rate, itemCount: r.itemCount, intoPeriodKey: r.into,
        });
      });
      return acc;
    }, []),
    exports: Object.keys(exportsByPlace).sort().map(function(k) {
      var e = exportsByPlace[k];
      return {
        place: e.place, invoiced: r2(e.invoiced), count: e.count,
        projects: Object.keys(e.projects).sort(),
      };
    }),
    grandTax: r2(periods.reduce(function(s, p) { return s + p.tax; }, 0)),
  };
}

// salesTaxFilingText — plain-text block for one state-period, for pasting
// into a state portal or an email to the accountant. Mirrors the
// "Copy list for Dan" affordance on Card Statements: the people who file
// do not read Gilda, so the numbers have to travel as text.
export function salesTaxFilingText(period, stateRow) {
  if (!period || !stateRow) return '';
  var money = function(n) {
    return '$' + (Math.round((n || 0) * 100) / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };
  var lines = [];
  lines.push(stateRow.stateName + ' (' + stateRow.stateCode + ') — ' + period.label);
  lines.push('');
  // Return order: gross (before tax) − non-taxable = taxable × rate = tax.
  // The tax-inclusive billed total travels last, labeled, so nobody
  // files it as a sales figure.
  lines.push('Gross sales:           ' + money(stateRow.grossSales));
  lines.push('Non-taxable:           ' + money(stateRow.nonTaxable));
  lines.push('Taxable sales:         ' + money(stateRow.taxable));
  lines.push('Rate:                  ' + (stateRow.rate ? (stateRow.rate * 100).toFixed(3).replace(/0+$/, '').replace(/\.$/, '') + '%' : 'mixed — see projects'));
  lines.push('Tax due:               ' + money(stateRow.tax));
  lines.push('Billed incl. tax:      ' + money(stateRow.invoiced));
  if (stateRow.jurisdictions && stateRow.jurisdictions.length) {
    lines.push('Jurisdiction:          ' + stateRow.jurisdictions.join('; '));
  }
  if (stateRow.taxedCategories && stateRow.taxedCategories.length) {
    lines.push('Taxed here:            ' + stateRow.taxedCategories.map(function(k) { return TAX_CATEGORY_LABELS[k] || k; }).join(', '));
  }
  var cats = stateRow.cats;
  if (cats) {
    var ded = TAX_CATEGORY_KEYS.filter(function(k) { return cats.nonTaxable[k] > 0.005; });
    if (ded.length || cats.other > 0.005) {
      lines.push('');
      lines.push('Deductions (non-taxable):');
      ded.forEach(function(k) {
        lines.push('  ' + (TAX_CATEGORY_LABELS[k] + ':').padEnd(22) + money(cats.nonTaxable[k]));
      });
      if (cats.other > 0.005) lines.push('  ' + 'Other:'.padEnd(22) + money(cats.other));
    }
  }
  if (stateRow.projects && stateRow.projects.length) {
    lines.push('');
    lines.push('By project:');
    stateRow.projects.forEach(function(pr) {
      lines.push('  ' + pr.project + (pr.jurisdiction ? ' (' + pr.jurisdiction + ')' : '')
        + '  taxable ' + money(pr.taxable) + '  tax ' + money(pr.tax));
      (pr.fins || []).forEach(function(f) {
        lines.push('    ' + f.invoiceRef + '  paid ' + f.paidDate + '  tax ' + money(f.tax));
      });
    });
  }
  return lines.join('\n');
}

// salesTaxJurisdiction — the delivery locality named on the project
// record ("Vail, CO" / "Teton Village, WY"). Blank when the project has
// no city. A state return files by county or home-rule city, so this is
// what tells the filer which line a project's figures land on.
export function salesTaxJurisdiction(project) {
  if (!project) return '';
  var city  = String(project.city || '').trim();
  var state = String(project.state || '').trim().toUpperCase();
  if (city && state) return city + ', ' + state;
  return city || '';
}

// salesTaxReportCsv — one row per FINAL INVOICE with every figure the
// report knows, for the accountants' spreadsheet. Month/state/project
// subtotals are deliberately NOT emitted: a pivot table rebuilds them,
// and mixing subtotal rows into detail rows breaks every SUM.
export function salesTaxReportCsv(report, filings) {
  var esc = function(v) {
    var s = String(v == null ? '' : v);
    return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  };
  var m = function(n) { return (Math.round((n || 0) * 100) / 100).toFixed(2); };
  var head = [
    'Period', 'State', 'State name', 'Jurisdiction', 'Project', 'Invoice',
    'Transaction date', 'Date basis', 'Deposit paid', 'Rate',
    'Gross sales', 'Taxable', 'Non-taxable', 'Tax', 'Billed incl. tax',
  ];
  TAX_CATEGORY_KEYS.forEach(function(k) { head.push('Taxable: ' + TAX_CATEGORY_LABELS[k]); });
  TAX_CATEGORY_KEYS.forEach(function(k) { head.push('Non-taxable: ' + TAX_CATEGORY_LABELS[k]); });
  head.push('Non-taxable: Other');
  head.push('Tax source', 'Live tax today', 'Drift (billed - live)', 'Vendor PO open');
  // What was filed/paid to the state for this period (state-level; the
  // same three cells repeat on every invoice row of that state+period).
  head.push('Paid to state', 'Paid date', 'Confirmation');
  var rows = [head];
  (report && report.periods || []).forEach(function(p) {
    (p.states || []).forEach(function(st) {
      var filed = salesTaxFilingsFor(filings, st.stateCode, p.key);
      var filedCells = filed.length
        ? [m(sumSalesTaxFilings(filed)),
           filed.map(function(x) { return x.paidDate || ''; }).filter(Boolean).join('; '),
           filed.map(function(x) { return x.confirmationNo || ''; }).filter(Boolean).join('; ')]
        : ['', '', ''];
      (st.projects || []).forEach(function(pr) {
        (pr.fins || []).forEach(function(f) {
          var row = [
            p.label, st.stateCode, st.stateName, pr.jurisdiction || '', pr.project, f.invoiceRef,
            f.paidDate || '',
            f.paidBasis === 'closed' ? 'final closed at $0 (collected at deposit)'
              : f.paidBasis === 'deposit' ? 'deposit payment' : 'final payment',
            f.depositPaidDate || '',
            pr.rate ? (pr.rate * 100).toFixed(4).replace(/0+$/, '').replace(/\.$/, '') + '%' : '',
            m(f.grossSales), m(f.taxable), m(f.nonTaxable), m(f.tax), m(f.invoiced),
          ];
          var c = f.cats || { taxable: {}, nonTaxable: {}, other: 0 };
          TAX_CATEGORY_KEYS.forEach(function(k) { row.push(m(c.taxable[k])); });
          TAX_CATEGORY_KEYS.forEach(function(k) { row.push(m(c.nonTaxable[k])); });
          row.push(m(c.other));
          row.push(f.taxSource || 'live', m(f.liveTax != null ? f.liveTax : f.tax), m(f.drift || 0), (f.openPos || []).join('; '));
          row.push(filedCells[0], filedCells[1], filedCells[2]);
          rows.push(row);
        });
      });
    });
  });
  // UTF-8 BOM so Excel opens it as UTF-8 (the Card Statements convention);
  // CRLF row ends per RFC 4180 — what Excel itself writes — and ASCII only
  // in the header (2026-09-20: a downloaded file would not open cleanly).
  return '\uFEFF' + rows.map(function(r) { return r.map(esc).join(','); }).join('\r\n');
}

// ── DOCUMENT SENDS — Phase 5A (2026-06-24) ────────────────────────────────
// Versioned send history pattern. See ARCHITECTURE.md §4 "Document
// immutability and send history" + BILLING_REDESIGN_SPEC.md §1. Frozen
// payload + PDF storage path per send event. Sent records are conceptually
// immutable — RLS allows INSERT but not UPDATE/DELETE from the UI.
//
// Document types supported by the table: 'po' | 'fin' | 'billing_summary' |
// 'proposal'. Phase 5A wires 'billing_summary' first; FIN / PO / Proposal
// extend the same pattern in subsequent phases.

// Allowed document_type values. Mirrors the CHECK constraint on the
// migration so callers fail at the helper boundary if they pass a typo
// rather than at the DB write boundary.
export var DOCUMENT_SEND_TYPES = ['po', 'fin', 'billing_summary', 'proposal', 'schedule'];

// rowToDocumentSend — DB row → in-memory shape used by AppContext + UI.
// Pure mapper; matches the camelCase naming the rest of utils.js uses.
export function rowToDocumentSend(row) {
  if (!row) return null;
  return {
    id:            row.id,
    documentType:  row.document_type || '',
    documentId:    row.document_id || '',
    sentAt:        row.sent_at || '',
    sentTo:        row.sent_to || '',
    payload:       row.payload_jsonb || null,
    storagePath:   row.storage_path || '',
    createdBy:     row.created_by || '',
    createdAt:     row.created_at || '',
  };
}

// buildDocumentSendInsert — pure helper that produces the row shape to
// INSERT into document_sends. Callers supply the rendered payload + the
// storage path of the just-uploaded PDF; the helper rejects invalid
// document_type values and returns null so the call site can decide how
// to error.
//
// args: { documentType, documentId, payload, storagePath, sentTo, createdBy }
// Returns: row object ready for supabase.from('document_sends').insert(row),
// or null when documentType / documentId / payload are missing.
export function buildDocumentSendInsert(args) {
  if (!args) return null;
  var documentType = args.documentType || '';
  if (DOCUMENT_SEND_TYPES.indexOf(documentType) < 0) return null;
  if (!args.documentId) return null;
  if (args.payload == null) return null;
  return {
    document_type: documentType,
    document_id:   args.documentId,
    sent_to:       args.sentTo || '',
    payload_jsonb: args.payload,
    storage_path:  args.storagePath || '',
    created_by:    args.createdBy || '',
  };
}

// latestDocumentSend — read helper. Returns the most-recent document_sends
// row for a (documentType, documentId) pair, or null when no send has
// happened. Used by cover-regen paths (BillingHistorySection) to decide
// whether to read the frozen payload or fall back to live data.
//
// `documentSends` is the in-memory array carried on AppContext; rows are
// already mapped via rowToDocumentSend (camelCase). Sorted on createdAt
// descending; the head is the most recent.
export function latestDocumentSend(documentSends, documentType, documentId) {
  if (!Array.isArray(documentSends)) return null;
  if (!documentType || !documentId) return null;
  var matches = documentSends.filter(function(s) {
    return s
        && s.documentType === documentType
        && String(s.documentId) === String(documentId);
  });
  if (matches.length === 0) return null;
  matches.sort(function(a, b) {
    var ka = String(a.createdAt || a.sentAt || '');
    var kb = String(b.createdAt || b.sentAt || '');
    if (ka === kb) return 0;
    return ka < kb ? 1 : -1;  // descending
  });
  return matches[0];
}

// documentSendsFor — read helper. Returns ALL document_sends rows for a
// (documentType, documentId) pair, sorted newest-first. Used by the Send
// History UI panel to render every send event chronologically.
export function documentSendsFor(documentSends, documentType, documentId) {
  if (!Array.isArray(documentSends)) return [];
  if (!documentType || !documentId) return [];
  var matches = documentSends.filter(function(s) {
    return s
        && s.documentType === documentType
        && String(s.documentId) === String(documentId);
  });
  matches.sort(function(a, b) {
    var ka = String(a.createdAt || a.sentAt || '');
    var kb = String(b.createdAt || b.sentAt || '');
    if (ka === kb) return 0;
    return ka < kb ? 1 : -1;
  });
  return matches;
}

// validateAddItemToFinSelection — Phase 5B (2026-06-24) click-time gate
// for the FIN Builder picker. Enforces the "1 FIN = 1 vendor = 1 PO"
// operational rule per ARCHITECTURE.md §4 + BILLING_REDESIGN_SPEC.md §5.
//
// Called from toggleItem / toggleGroup BEFORE adding an item to the
// working selection. De-selection (caller's branch) never reaches this
// helper — it's only the ADD path that can violate the rule. First-item
// selection (empty selection beforehand) defines the project + PO
// scope; subsequent additions must match both.
//
// The same-project check inside this helper preserves the older
// generate-time gate so both rules fire at click time now. The
// generate-time same-project check (still present in OrdersInProcess
// for belt-and-suspenders) becomes redundant but harmless.
//
// Legacy multi-PO FINs (created before this gate landed) are NOT
// affected — they live in the invoices table with their items already
// stamped via final_invoice_id, so they no longer reach the picker's
// inProcess filter. Viewing legacy FINs happens in the detail surface,
// not this picker.
//
// args:
//   itemToAdd            — the item the user just clicked (full item shape;
//                          must carry .poNumber + .project/.projectLabel)
//   alreadySelectedItems — array of items currently in the working
//                          selection (filter inProcess by selectedIds at
//                          the call site; only same-shape items belong)
//
// Returns: { ok: true } when the add is allowed, or
//          { ok: false, error: <user-facing string> } when blocked.
export function validateAddItemToFinSelection(itemToAdd, alreadySelectedItems) {
  if (!itemToAdd) return { ok: true };
  if (!Array.isArray(alreadySelectedItems) || alreadySelectedItems.length === 0) {
    return { ok: true };
  }

  // Same-project check — preserves the existing generate-time error
  // text verbatim so the user sees consistent messaging across the
  // two failure modes.
  var firstProject = alreadySelectedItems[0].projectLabel
                  || alreadySelectedItems[0].project
                  || '';
  var newProject = itemToAdd.projectLabel || itemToAdd.project || '';
  if (firstProject !== newProject) {
    return {
      ok: false,
      error: 'All items in a single final invoice must be on the same project.',
    };
  }

  // Same-PO check — the Phase 5B enforcement.
  var firstPo = alreadySelectedItems[0].poNumber || '';
  var newPo = itemToAdd.poNumber || '';
  if (firstPo !== newPo) {
    var firstLabel = firstPo || '(no PO)';
    var newLabel = newPo || '(no PO)';
    return {
      ok: false,
      error: 'All items in a single final invoice must be on the same purchase order. '
           + 'This item is on ' + newLabel + '; previously selected items are on '
           + firstLabel + '. To invoice both, generate two separate FINs.',
    };
  }

  return { ok: true };
}

// purchaseOrderStoragePath — canonical path inside the purchase-orders
// bucket for a given (poId, sentAt) pair. Phase 5A.2 (2026-06-25). Same
// shape as billingSummaryStoragePath — every Draft -> Issued transition
// writes a new path (timestamp-suffix uniqueness), so re-issues never
// overwrite the prior snapshot.
export function purchaseOrderStoragePath(poId, sentAt) {
  var when = sentAt ? new Date(sentAt) : new Date();
  if (isNaN(when.getTime())) when = new Date();
  var yyyy = when.getFullYear();
  var mm   = String(when.getMonth() + 1).padStart(2, '0');
  var epoch = when.getTime();
  return 'purchase-orders/' + yyyy + '-' + mm + '/' + String(poId) + '__' + epoch + '.pdf';
}

// proposalStoragePath — canonical path inside the proposals bucket for a
// given (proposalId, sentAt) pair. Phase 5A.3 (2026-07-02): every proposal
// PDF a client receives is archived — at package send (sendPackage's
// proposal loop) and at off-cycle Mark Sent. Same timestamp-suffix shape
// as the PO/billing-summary helpers so re-sends never overwrite.
export function proposalStoragePath(proposalId, sentAt) {
  var when = sentAt ? new Date(sentAt) : new Date();
  if (isNaN(when.getTime())) when = new Date();
  var yyyy = when.getFullYear();
  var mm   = String(when.getMonth() + 1).padStart(2, '0');
  var epoch = when.getTime();
  return 'proposals/' + yyyy + '-' + mm + '/' + String(proposalId) + '__' + epoch + '.pdf';
}

// computePoItemPaymentHistory — per-item payment annotation source for
// the Live-annotated PO view (Phase 5A.2, 2026-06-25). For every item on
// a PO, walks vendor_payment_items.amount_share to determine:
//   - sum of payments against this item
//   - earliest payment_date (= deposit landing)
//   - latest payment_date (= final landing, if deposit + final exist)
//   - the references / invoice numbers for those payments
//   - whether the item is "closed" (paid in full per its depositPct policy)
//
// Returns a Map from itemId -> {
//   depositPaidAmount, depositPaidDate, depositReference,
//   finalPaidAmount, finalPaidDate, finalReference,
//   isClosed,             // true when total paid >= item.netPrice * qty
//   is100PercentUpfront,  // true when item.depositPct >= 100
//   hasAnyPayment,        // false -> no annotation rendered
// }
//
// "Closed" semantics: total amount_share paid >= item's net cost basis,
// within a $1 tolerance. 100%-upfront items close on a single deposit
// payment; standard 50%-deposit items close only after both deposit and
// final land.
//
// args:
//   po                  — the PO row (carries .items array of item ids)
//   items               — full items catalog (for netPrice + depositPct)
//   vendorPayments      — vendor_payments rows (for payment_date + reference)
//   vendorPaymentItems  — vendor_payment_items rows (for per-item amount_share)
//
// Pure helper; no side effects.
export function computePoItemPaymentHistory(po, items, vendorPayments, vendorPaymentItems, vendors) {
  var out = {};
  if (!po) return out;
  var poItemIds = Array.isArray(po.items) ? po.items
                : Array.isArray(po.item_ids) ? po.item_ids
                : [];
  if (poItemIds.length === 0) return out;

  // Index payments by id for fast lookup.
  var paymentById = {};
  (vendorPayments || []).forEach(function(vp) {
    if (vp && vp.id) paymentById[String(vp.id)] = vp;
  });

  // Resolve the PO's vendor short name once (one PO = one vendor).
  // Prefer the directory record (explicit shortName override OR first
  // word of canonical name), fall back to first word of the PO's stored
  // vendorName when no directory record matches.
  var poVendor = (vendors || []).find(function(v) {
    if (!v) return false;
    if (po.vendorName && v.name === po.vendorName) return true;
    if (po.vendorId != null && String(v.id) === String(po.vendorId)) return true;
    return false;
  });
  var poVendorShortName = poVendor
    ? vendorShortName(poVendor)
    : firstWordShortName(po.vendorName || '');

  // Strip cascade-internal noise from a vendor_payments reference: the
  // RECEIPT:<uuid> idempotency tag (via formatVendorPaymentReference) and
  // a leading `{poRef} · ` segment if present (the PO ref already lives
  // in the page header, so repeating it in the annotation is noise).
  var poRefForStrip = String((po && (po.po_ref || po.poRef)) || '');
  function cleanRef(raw) {
    var s = formatVendorPaymentReference(raw);
    if (poRefForStrip) {
      // Match poRef at the start, optionally followed by ` · ` separator.
      var prefix = poRefForStrip + ' · ';
      if (s === poRefForStrip) return '';
      if (s.indexOf(prefix) === 0) s = s.slice(prefix.length);
    }
    return s.trim();
  }

  // Principal payment types that drive deposit/final annotation lines.
  // Bug 1 fix (2026-06-26): the cascade RPC fans amount_share children
  // for EVERY vendor receipt kind, including vendor_fee — so a $1,495
  // freight receipt writes vpi children whose amount_share allocates
  // the $1,495 across items. Without this filter, the annotation
  // helper reads those freight allocations as deposit principal and
  // shows tiny per-item "Paid $X" numbers that sum to the freight
  // total. computePoPaidBefore (utils.js:455) has used this same
  // exclude-Fee discipline since it shipped; mirroring it here closes
  // the read-side gap.
  //
  // 2026-06-26 (Commit 2 of FIN restructure): lifted to the module-
  // level PRINCIPAL_PAYMENT_TYPES export (array) so isPoFullyPaid
  // shares the same set. Local object map kept here for O(1) lookup
  // in the per-allocation filter loop.
  var PRINCIPAL_PAYMENT_TYPES_MAP = {};
  PRINCIPAL_PAYMENT_TYPES.forEach(function(t) { PRINCIPAL_PAYMENT_TYPES_MAP[t] = true; });

  poItemIds.forEach(function(itemId) {
    var item = (items || []).find(function(it) { return it && String(it.id) === String(itemId); });
    if (!item) return;

    // Filter vpi rows to those allocated to this item with non-zero
    // share AND backed by a principal payment row (NOT a 'Fee' row).
    var allocations = (vendorPaymentItems || []).filter(function(vpi) {
      if (!vpi) return false;
      var iid = vpi.item_id != null ? vpi.item_id : vpi.itemId;
      if (String(iid) !== String(itemId)) return false;
      var share = parseFloat(vpi.amount_share != null ? vpi.amount_share : vpi.amountShare) || 0;
      if (Math.abs(share) < 0.01) return false;
      // Resolve parent and gate on payment_type.
      var paymentId = String(vpi.payment_id != null ? vpi.payment_id : vpi.paymentId);
      var vp = paymentById[paymentId];
      if (!vp) return false;  // orphan vpi without a parent — drop defensively
      var payType = vp.paymentType || vp.payment_type || '';
      return PRINCIPAL_PAYMENT_TYPES_MAP[payType] === true;
    });

    if (allocations.length === 0) {
      out[itemId] = {
        depositPaidAmount: 0, depositPaidDate: '', depositReference: '', depositVendorShortName: '',
        finalPaidAmount: 0,   finalPaidDate: '',   finalReference: '',   finalVendorShortName: '',
        totalPaid: 0,
        isClosed: false,
        is100PercentUpfront: parseFloat(item.depositPct || 50) >= 100,
        hasAnyPayment: false,
      };
      return;
    }

    // Resolve each allocation's parent payment for the date + reference.
    // Sort ascending by payment_date — earliest = deposit, later = final.
    var resolved = allocations.map(function(vpi) {
      var paymentId = String(vpi.payment_id != null ? vpi.payment_id : vpi.paymentId);
      var vp = paymentById[paymentId] || {};
      var payType = String(vp.paymentType || vp.payment_type || '').toLowerCase();
      var paymentDate = vp.paymentDate || vp.payment_date || '';
      var reference = cleanRef(vp.reference);
      var share = parseFloat(vpi.amount_share != null ? vpi.amount_share : vpi.amountShare) || 0;
      return { share: share, paymentDate: paymentDate, reference: reference, payType: payType };
    });
    resolved.sort(function(a, b) {
      var ka = String(a.paymentDate || '');
      var kb = String(b.paymentDate || '');
      if (ka === kb) return 0;
      return ka < kb ? -1 : 1;  // ascending
    });

    // Deposit = earliest payment_date allocation.
    // Final = any later allocation (sum, in case of multiple finals — rare).
    var depositSlot = resolved[0];
    var finalSlots = resolved.slice(1);
    var depositPaidAmount = depositSlot.share;
    var depositPaidDate = depositSlot.paymentDate;
    var depositReference = depositSlot.reference;
    var finalPaidAmount = finalSlots.reduce(function(s, r) { return s + r.share; }, 0);
    var finalPaidDate = finalSlots.length > 0 ? finalSlots[finalSlots.length - 1].paymentDate : '';
    var finalReference = finalSlots.length > 0 ? finalSlots[finalSlots.length - 1].reference : '';
    var totalPaid = depositPaidAmount + finalPaidAmount;

    // Closed when paid >= net cost basis (within $1 tolerance).
    var qty = parseFloat(item.qty || 1) || 1;
    var netCost = Math.max(0, parseFloat(item.netPrice || 0)) * Math.max(1, qty);
    var isClosed = totalPaid >= (netCost - 1.0);
    var is100PercentUpfront = parseFloat(item.depositPct || 50) >= 100;

    out[itemId] = {
      depositPaidAmount:   Math.round(depositPaidAmount * 100) / 100,
      depositPaidDate:     depositPaidDate,
      depositReference:    depositReference,
      depositVendorShortName: poVendorShortName,
      finalPaidAmount:     Math.round(finalPaidAmount * 100) / 100,
      finalPaidDate:       finalPaidDate,
      finalReference:      finalReference,
      finalVendorShortName: poVendorShortName,
      totalPaid:           Math.round(totalPaid * 100) / 100,
      isClosed:            isClosed,
      is100PercentUpfront: is100PercentUpfront,
      hasAnyPayment:       true,
    };
  });

  return out;
}

// computePoFreightHistory — per-PO freight charge history for the Live-
// annotated PO view (Phase 5A.2). Returns an array of charge events that
// each contributed freight to this PO, sorted chronologically. The annotation
// in the totals block reads from this.
//
// Resolution rules:
//   1. Prefer vendor_payment_items.fee_share_freight > 0 (Phase 5C
//      authoritative source). For each unique payment_id with
//      sum-of-fee_share_freight > 0, emit one entry.
//   2. Fallback for pre-Phase-5C historical receipts: walk receipts where
//      kind === 'vendor_fee' OR feeBreakdown.freight > 0 on this PO, emit
//      one entry per receipt.
//   3. Dedupe: if both signals fire for the same receipt, the fee_share
//      version wins (more authoritative).
//
// Returns array of { amount, date, reference, status, source } where:
//   amount    — freight charged on this event
//   date      — payment_date (from vendor_payments) or receiptDate (fallback)
//   reference — vendor invoice number or vendor_payments.reference
//   status    — 'approved' | 'pending' | 'rejected' (echoes the receipt's)
//   source    — 'fee_share' | 'fee_breakdown' (diagnostic; UI may surface)
export function computePoFreightHistory(po, vendorPayments, vendorPaymentItems, receipts, vendors) {
  if (!po || !po.id) return [];
  var poId = String(po.id);
  var poRefForStrip = String((po.po_ref || po.poRef) || '');
  function cleanRef(raw) {
    var s = formatVendorPaymentReference(raw);
    if (poRefForStrip) {
      var prefix = poRefForStrip + ' · ';
      if (s === poRefForStrip) return '';
      if (s.indexOf(prefix) === 0) s = s.slice(prefix.length);
    }
    return s.trim();
  }

  // Resolve the PO's vendor short name once (one PO = one vendor).
  // Same fallback chain as computePoItemPaymentHistory.
  var poVendor = (vendors || []).find(function(v) {
    if (!v) return false;
    if (po.vendorName && v.name === po.vendorName) return true;
    if (po.vendorId != null && String(v.id) === String(po.vendorId)) return true;
    return false;
  });
  var poVendorShortName = poVendor
    ? vendorShortName(poVendor)
    : firstWordShortName(po.vendorName || '');

  // Path 1 — fee_share_freight signal.
  // Group vpi rows by payment_id; sum fee_share_freight per group.
  var feeShareByPayment = {};
  (vendorPaymentItems || []).forEach(function(vpi) {
    if (!vpi) return;
    var fs = parseFloat(vpi.fee_share_freight != null ? vpi.fee_share_freight : vpi.feeShareFreight) || 0;
    if (fs <= 0) return;
    var pid = String(vpi.payment_id != null ? vpi.payment_id : vpi.paymentId);
    feeShareByPayment[pid] = (feeShareByPayment[pid] || 0) + fs;
  });

  // Tie feeShareByPayment entries to vendor_payments rows for this PO.
  var fromFeeShare = [];
  Object.keys(feeShareByPayment).forEach(function(pid) {
    var vp = (vendorPayments || []).find(function(v) { return v && String(v.id) === pid; });
    if (!vp) return;
    var vpPoId = String(vp.poId != null ? vp.poId : vp.po_id);
    if (vpPoId !== poId) return;
    fromFeeShare.push({
      amount:    Math.round(feeShareByPayment[pid] * 100) / 100,
      date:      vp.paymentDate || vp.payment_date || '',
      reference: cleanRef(vp.reference),
      vendorShortName: poVendorShortName,
      status:    'approved',
      source:    'fee_share',
      _receiptId: vp.sourceReceiptId || vp.source_receipt_id || '',
    });
  });

  // Path 2 — fallback for receipts not covered by Path 1.
  // Walk approved + pending receipts on this PO; emit an entry for each
  // where the receipt's feeBreakdown.freight > 0 OR kind === 'vendor_fee'.
  // Skip any whose source_receipt_id already appears in fromFeeShare.
  var feeShareReceiptIds = {};
  fromFeeShare.forEach(function(e) {
    if (e._receiptId) feeShareReceiptIds[String(e._receiptId)] = true;
  });

  var fromFeeBreakdown = [];
  (receipts || []).forEach(function(r) {
    if (!r) return;
    if (String(r.poId || r.po_id) !== poId) return;
    if (r.status === 'rejected') return;
    if (feeShareReceiptIds[String(r.id)]) return;
    var bd = r.feeBreakdown || r.fee_breakdown || {};
    var fb = parseFloat(bd.freight) || 0;
    var isExplicitFreightFee = r.kind === 'vendor_fee' && fb > 0;
    if (fb <= 0 && !isExplicitFreightFee) return;
    // Bug 2 fix (2026-06-26): vendorName fallback removed. The annotation
    // line is already prefixed with the vendor's short_name at render
    // time; falling back to the full directory name here produced
    // "Casa Casa Design, LLC" when no invoice number was captured. The
    // reference should hold a vendor invoice/wire identifier (or be
    // empty so ref_line collapses gracefully) — not the vendor identity.
    fromFeeBreakdown.push({
      amount:    Math.round(fb * 100) / 100,
      date:      r.receiptDate || r.receipt_date || '',
      reference: r.vendorInvoiceNumber || r.vendor_invoice_number || '',
      vendorShortName: poVendorShortName,
      status:    r.status === 'approved' ? 'approved' : 'pending',
      source:    'fee_breakdown',
    });
  });

  // Combine + sort chronologically.
  var all = fromFeeShare.map(function(e) {
    // Strip internal _receiptId from output
    return { amount: e.amount, date: e.date, reference: e.reference, vendorShortName: e.vendorShortName, status: e.status, source: e.source };
  }).concat(fromFeeBreakdown);
  all.sort(function(a, b) {
    var ka = String(a.date || '');
    var kb = String(b.date || '');
    if (ka === kb) return 0;
    return ka < kb ? -1 : 1;
  });
  return all;
}

// billingSummariesStoragePath — canonical path inside the
// billing-summaries bucket for a given (summaryId, sentAt) pair. Re-sends
// of the same summary get distinct paths (timestamp suffix), so the
// storage bucket carries every send's PDF separately for audit.
//
// Path shape:
//   billing-summaries/{yyyy-mm}/{summaryId}__{epochms}.pdf
//
// yyyy-mm comes from the sentAt date (the period in which the send
// occurred — not the summary's billing period — because the bookkeeper
// thinks chronologically when auditing). epochms guarantees uniqueness
// across re-sends within the same calendar day.
export function billingSummaryStoragePath(summaryId, sentAt) {
  var when = sentAt ? new Date(sentAt) : new Date();
  if (isNaN(when.getTime())) when = new Date();
  var yyyy = when.getFullYear();
  var mm   = String(when.getMonth() + 1).padStart(2, '0');
  var epoch = when.getTime();
  return 'billing-summaries/' + yyyy + '-' + mm + '/' + String(summaryId) + '__' + epoch + '.pdf';
}

// ═══════════════════════════════════════════════════════════════════════════
// BILLING HISTORY V2 — cross-project package archive (2026-07-02)
//
// Pure derivation layer for the Billing History surface: given a sent/paid
// billing_summaries row plus the live reference state (proposals, invoices,
// receipts, payment allocations, retainer ledger, hours invoices, credit
// ledger), resolve everything that rode the package and derive its money
// truthfully.
//
// ARCHITECTURAL RULE 10 COMPLIANCE — payment state comes from payment
// records. Every "paid" number in this section is a live derivation from
// payment_allocations via calcPaidForDocument / derivePaidStatus. No status
// string, no stored paid amount, no summary-row snapshot is ever treated as
// payment truth. The billing_summaries.status column is used ONLY as the
// archive-inclusion filter (sent/paid rows appear), never as money.
//
// HEURISTICS are labeled as such in comments below AND surfaced with
// `isHeuristic` flags so the UI can soften its copy honestly.
// ═══════════════════════════════════════════════════════════════════════════

var BH2_MONTHS = ['', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

function bh2Round(n) { return Math.round((parseFloat(n) || 0) * 100) / 100; }

// Period label for a billing_summaries row — "May 2026 · Monthly summary".
// Every billing_summaries row is a monthly summary today; off-cycle
// procurement packages don't write billing_summaries rows, so there is no
// kind column to branch on. If/when off-cycle packages land in this table,
// this label grows a kind argument.
// True when a billing_summaries row is an off-cycle (proposals-only)
// package rather than a monthly statement. Single source of truth for the
// summary_type read — labels, resolution, and UI copy all route through
// here (2026-07-04).
export function isOffCycleSummary(row) {
  return !!row && String(row.summary_type || row.summaryType || '') === 'off_cycle';
}

// "July 3, 2026" from an ISO date/timestamp — off-cycle packages are
// identified by their send DATE, not a billing month.
export function offCycleDateLabel(iso) {
  var s = String(iso || '').slice(0, 10);
  var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return '';
  var month = BH2_MONTHS[parseInt(m[2], 10)] || '';
  return month + ' ' + parseInt(m[3], 10) + ', ' + m[1];
}

export function billingSummaryPeriodLabel(row) {
  if (!row) return '';
  if (isOffCycleSummary(row)) {
    var d = offCycleDateLabel(row.sent_at || row.sentAt || row.generated_at || row.generatedAt);
    return (d ? d + ' ' : '') + '· Off-cycle package';
  }
  var m = BH2_MONTHS[parseInt(row.month, 10)] || '';
  return (m ? m + ' ' : '') + (row.year || '') + ' · Monthly summary';
}

// Month + year alone ("May 2026") — used for doc labels like
// "Design services — May 2026". Off-cycle packages label by their send
// date instead ("July 3, 2026") — they have no billing month.
export function billingSummaryMonthYear(row) {
  if (!row) return '';
  if (isOffCycleSummary(row)) {
    return offCycleDateLabel(row.sent_at || row.sentAt || row.generated_at || row.generatedAt)
      || ((BH2_MONTHS[parseInt(row.month, 10)] || '') + ' ' + (row.year || ''));
  }
  var m = BH2_MONTHS[parseInt(row.month, 10)] || '';
  return (m ? m + ' ' : '') + (row.year || '');
}

// Package-level paid state from derived asked/paid. Four states drive the
// archive's status dot + filter chips:
//   'none'    — nothing was asked (retainer covered the period, or an
//               informational summary). Grey.
//   'paid'    — paid >= asked (cent tolerance). Green.
//   'partial' — something recorded but short of asked. Fuchsia.
//   'unpaid'  — asked > 0, nothing recorded. Yellow.
export function billingPackagePaidState(asked, paid) {
  var a = parseFloat(asked) || 0;
  var p = parseFloat(paid) || 0;
  if (a <= 0.005) return 'none';
  if (p >= a - 0.005) return 'paid';
  if (p > 0.005) return 'partial';
  return 'unpaid';
}

// Plain-English label for a package paid state (per the approved mockup —
// no lifecycle jargon on this surface).
export function billingPaidStateLabel(state, opts) {
  var hasRetainer = !!(opts && opts.hasRetainer);
  if (state === 'paid') return 'Paid in full';
  if (state === 'partial') return 'Partially paid';
  if (state === 'unpaid') return 'Awaiting payment';
  if (state === 'none') {
    return hasRetainer
      ? 'No amount due — retainer covered the period'
      : 'No amount due';
  }
  return '';
}

// Plain-English label for a per-document derived status (the
// derivePaidStatus vocabulary: 'paid' | 'partial' | 'sent' | 'draft').
export function billingDocPaidLabel(status) {
  var s = String(status || '').toLowerCase();
  if (s === 'paid') return 'Paid in full';
  if (s === 'partial') return 'Partially paid';
  return 'Awaiting payment';
}

// Friendly display name for an invoice on the Billing History surface.
// Mirrors BillingSummarySection's local invoiceFriendlyName (2026-06-28:
// 'Final Invoice' alone is useless — lead with the parent proposal's name)
// with the document type appended so allocation sentences read naturally:
// "Custom Kumiko Art Screen — final invoice".
export function billingDocDisplayName(invoice, proposals) {
  if (!invoice) return 'Invoice';
  var t = String(invoice.invoiceType || invoice.invoice_type || '').toLowerCase();
  var pid = invoice.proposalId || invoice.proposal_id || '';
  var parent = pid
    ? (proposals || []).find(function(p) { return p && String(p.id) === String(pid); })
    : null;
  var pname = parent ? (parent.name || parent.proposal_ref || '') : '';
  if (t === 'deposit') return (pname || 'Proposal') + ' — deposit';
  if (t === 'final')   return pname ? pname + ' — final invoice' : 'Final invoice';
  if (t === 'reimbursable')   return 'Reimbursable expenses';
  if (t === 'designer_hours') return 'Designer hours';
  return invoice.invoiceRef || invoice.invoice_ref || 'Invoice';
}

// Natural key linking a billing_summaries row to its hours_invoices row:
// {project_label}-{year}-{month(2-digit)} — same shape as
// BillingSummarySection.buildInvoiceNumber and DesignerHoursSection's
// invoice_number mint. The summary row also snapshots hours_invoice_number
// at save time; prefer the snapshot when present (it's the row the
// bookkeeper actually attached), fall back to the natural key for older
// rows saved before the column existed.
export function billingSummaryHoursKey(row) {
  if (!row) return '';
  if (row.hours_invoice_number) return String(row.hours_invoice_number);
  if (!row.project_label || !row.year || !row.month) return '';
  return row.project_label + '-' + row.year + '-' + String(row.month).padStart(2, '0');
}

// ── resolveBillingPackage ──────────────────────────────────────────────────
// The core derivation. Resolves every document that rode a sent billing
// package and computes ASKED / PAID / BALANCE live.
//
// args:
//   summaryRow — RAW snake_case billing_summaries row (as loaded from
//                supabase in BillingHistorySection)
//   refs       — { proposals, invoices, receipts, allocations, payments,
//                  retainerLedger, hoursInvoices, clientCreditLedger,
//                  documentSends }
//                all in their AppContext (camelCase-mapped) shapes, except
//                hoursInvoices which are raw hours_invoices rows (that
//                table has no context mapper; the component queries it).
//
// PROPOSAL/DEP LIFECYCLE (Cimon, 2026-07-02 — load-bearing for decision 3;
// do NOT re-tighten the fallback chain without re-reading this):
//   package sent (proposal status 'Sent' — NO DEP invoice exists yet)
//     → client pays the deposit
//     → Cimon clicks "Approve + Assign SKU" (approveProposal in
//       ProposalsTab2) — THAT is what mints the per-item DEP invoices
//     → the payment is recorded against the fresh DEPs.
// So a package sitting in "Awaiting payment" NORMALLY has zero deposit
// invoices. That is the expected mid-lifecycle state, never an anomaly —
// deposit invoices are generated at the time of client payment.
//
// DERIVATION DECISIONS (each documented at its code site below):
//   1. Tagged docs = billed_on_summary_id tags — the same tags sendPackage
//      writes. The summary row's proposals_this_month / invoices_this_month
//      JSON snapshots are display artifacts, not resolution keys. The
//      FROZEN SEND PAYLOAD (document_sends) is different: it is the
//      authoritative manifest of what the client actually received, and
//      decisions 3b and 9 read it to fill resolution gaps.
//   2. Tagged invoices exclude draft/void/cancelled (defensive — those are
//      non-billable and should never carry a tag) but KEEP 'paid'. This is
//      the mirror image of bsInvoicesList's forward-looking filter: the
//      archive is backward-looking, and a since-paid FIN absolutely
//      belongs to the package it was billed on.
//   3. A proposal's deposit ask resolves through a fallback chain
//      (2026-07-02 field fix — per the lifecycle above, "no DEP yet" is
//      the NORMAL awaiting-payment state, so the ask must not depend on
//      the DEP existing):
//        (a) the hidden DEP invoice's amount_due when a DEP exists
//            (invoiceType 'deposit' + proposalId) — keeps the payment-
//            allocation linkage; askedSource 'dep'.
//        (b) else the frozen send payload's proposalsThisMonth entry
//            matched by proposal ref → totalDeposit — the number the
//            client actually saw on the cover; askedSource 'frozen'.
//        (c) else the proposal row's stored total_deposit — STALENESS
//            CAVEAT: this is live state and can drift from what was sent
//            (item edits, COs); last resort for pre-Phase-5A packages
//            with no send record; askedSource 'stored'.
//        (d) else 0; askedSource 'none'.
//      PAID stays DEP-allocations-only regardless of source — nothing
//      else is allocatable, and a sent-but-not-yet-approved proposal's
//      deposit is honestly unpaid (Rule 10).
//   4. DEP dedupe: a DEP reached via its tagged proposal AND directly
//      tagged with billed_on_summary_id counts once (proposal side wins).
//   5. Services ask: retainer projects ask only the stored
//      retainer_request (0 when none recorded — the period was covered);
//      non-retainer projects ask calcBillingSummaryAmount (design +
//      additional + expenses + selected receipt lines, live-read).
//   6. Credits applied to this summary (client_credit_ledger
//      applied_to_billing_summary rows, stored negative) reduce the
//      procurement ask, clamped at $0 per Architectural Rule 7.
//   7. PAID sums payment_allocations against the package's real document
//      ids: DEP + tagged invoice ids ('invoice'), the hours invoice
//      number ('hours_invoice', non-retainer only — on retainer projects
//      hours are retainer-funded and deliberately not payment targets),
//      and the summary id itself ('billing_summary' — the PaymentsPanel
//      legacy path allocates services money directly to the container).
//   8. HEURISTIC — retainer replenishment received: retainer_ledger has no
//      link from a deposit entry back to the summary that requested it, so
//      we count 'deposit' entries for the project dated on/after the
//      summary's sent_at, capped at the requested amount. A deposit that
//      actually answered a LATER summary's request can satisfy this filter.
//      Flagged retainerReceivedIsHeuristic:true; the UI copy must stay soft.
//   9. SEND-RECORD RECONCILIATION (2026-07-02 field fix): after tag-based
//      resolution, every invoicesThisMonth ref in the frozen payload that
//      is NOT among the resolved rows gets reconciled:
//        - a live invoice row matching by invoiceRef (non-draft/void/
//          cancelled) joins as a full row flagged linkedByRef:true —
//          asked from its row, paid from allocations. Self-heals packages
//          whose billed_on_summary_id tags were lost or never written
//          (the May 2026 Hoback shape: 9 FINs on the frozen manifest,
//          zero tags in Gilda).
//        - no matching row at all → a frozen-only DISPLAY row (asked from
//          the payload amount, paid unknowable — flagged frozenOnly +
//          paidUnknown) so the package never under-reports what the
//          client received. Its ask counts toward ASKED; its paid is 0,
//          so the balance may overstate until the row is restored/tagged.
//      Any reconciled row sets manifestMismatch:true so the UI can
//      quietly suggest fixing the tags.
export function resolveBillingPackage(summaryRow, refs) {
  var s = summaryRow || {};
  var r = refs || {};
  var proposals          = r.proposals          || [];
  var invoices           = r.invoices           || [];
  var receipts           = r.receipts           || [];
  var allocations        = r.allocations        || [];
  var retainerLedger     = r.retainerLedger     || [];
  var hoursInvoices      = r.hoursInvoices      || [];
  var clientCreditLedger = r.clientCreditLedger || [];
  var documentSends      = r.documentSends      || [];
  var payments           = r.payments           || [];

  var sid = String(s.id || '');
  // Off-cycle packages (2026-07-04) are proposals-only: no services ask,
  // no hours invoice, no retainer story — the row exists so the archive,
  // frozen-send machinery, and payment wizard work identically to a
  // monthly package. hasRetainer is forced off even if the row carried
  // the flag: procurement is never retainer-funded.
  var isOffCycle = isOffCycleSummary(s);
  var hasRetainer = !!s.has_retainer && !isOffCycle;

  // Frozen send manifest — decisions 3b + 9 read from the most recent
  // send's payload (the exact payload the delivered cover was rendered
  // from). Tolerate both camelCase (buildSummaryPayload) and legacy
  // snake_case keys defensively.
  var frozenSend = latestDocumentSend(documentSends, 'billing_summary', sid);
  var frozenPayload = frozenSend && frozenSend.payload ? frozenSend.payload : null;
  var frozenProposals = frozenPayload && Array.isArray(frozenPayload.proposalsThisMonth)
    ? frozenPayload.proposalsThisMonth : [];
  var frozenInvoices = frozenPayload && Array.isArray(frozenPayload.invoicesThisMonth)
    ? frozenPayload.invoicesThisMonth : [];

  // Decision 1 — tag-based resolution.
  var taggedProposals = proposals.filter(function(p) {
    return p && String(p.billedOnSummaryId || '') === sid;
  });
  // Decision 2 — exclude non-billable statuses, keep 'paid'.
  var taggedInvoices = invoices.filter(function(inv) {
    if (!inv || String(inv.billedOnSummaryId || '') !== sid) return false;
    var st = String(inv.status || '').toLowerCase();
    return st !== 'draft' && st !== 'void' && st !== 'cancelled';
  });
  var taggedReceipts = receipts.filter(function(rc) {
    return rc && String(rc.billedOnSummaryId || '') === sid;
  });

  // Hours invoice via the snapshot column / natural key. NEVER for
  // off-cycle rows — the natural-key fallback ({project}-{year}-{month})
  // would attach the MONTHLY hours invoice for whatever month the
  // off-cycle package happened to go out in, double-claiming it.
  var hoursKey = isOffCycle ? '' : billingSummaryHoursKey(s);
  var hoursInvoice = hoursKey
    ? (hoursInvoices.find(function(h) {
        return h && String(h.invoice_number || h.invoiceNumber || '') === hoursKey;
      }) || null)
    : null;

  // Decision 3 — proposal deposit rows. DEP invoices are PER-ITEM and
  // PLURAL (buildDepositInvoiceRecords mints one per item at approval
  // time — a 5-item proposal gets 5 DEP rows, letter-suffixed refs, each
  // carrying proposalId + itemId), so this FILTERS and aggregates across
  // ALL of the proposal's deposit invoices — a `.find` would silently
  // return only the first per-item DEP and under-report both asked and
  // paid. DEPs are internal allocation plumbing Cimon never looks at
  // ("they just sit in the system") — the proposal row IS the deposit on
  // every surface; the DEP ids exist here only to reach the allocations.
  var depositRows = taggedProposals.map(function(p) {
    var depInvoices = invoices.filter(function(inv) {
      return inv
        && String(inv.invoiceType || inv.invoice_type || '') === 'deposit'
        && String(inv.proposalId || inv.proposal_id || '') === String(p.id);
    });
    var asked = 0;
    var askedSource = 'none';
    if (depInvoices.length > 0) {
      // (a) real DEPs exist — the client has paid (or is mid-payment) and
      // approval minted the per-item invoices. amountDue is the canonical
      // original total (2026-05-27 audit); `amount` is the Fix-D Class-C
      // fallback for DM-migrated rows where close-out shorthand zeroed it.
      asked = bh2Round(depInvoices.reduce(function(sum, dep) {
        return sum + (parseFloat(dep.amountDue) || parseFloat(dep.amount) || 0);
      }, 0));
      askedSource = 'dep';
    } else {
      // No DEPs yet — the NORMAL awaiting-payment state (see lifecycle
      // note above). Fall back to what the client was actually asked.
      var pRef = String(p.proposal_ref || p.proposalRef || '');
      var frozenHit = pRef
        ? frozenProposals.find(function(fp) { return fp && String(fp.ref || '') === pRef; })
        : null;
      var frozenDeposit = frozenHit
        ? parseFloat(frozenHit.totalDeposit != null ? frozenHit.totalDeposit : frozenHit.total_deposit)
        : NaN;
      if (frozenHit && isFinite(frozenDeposit)) {
        // (b) the frozen send manifest — the number on the delivered cover.
        asked = bh2Round(frozenDeposit);
        askedSource = 'frozen';
      } else {
        // (c) the proposal row's stored total_deposit — live state, can
        // drift from what was sent; last resort for pre-Phase-5A sends.
        var stored = parseFloat(p.total_deposit != null ? p.total_deposit : p.totalDeposit);
        if (isFinite(stored) && stored > 0) {
          asked = bh2Round(stored);
          askedSource = 'stored';
        }
      }
    }
    // PAID is DEP-allocations-only regardless of askedSource (Rule 10) —
    // with no DEPs there is nothing allocatable and the deposit is
    // honestly unpaid.
    var paid = bh2Round(depInvoices.reduce(function(sum, dep) {
      return sum + calcPaidForDocument(allocations, 'invoice', dep.id);
    }, 0));
    // Aggregate status across the per-item DEP set (mirrors
    // derivePaidStatus semantics over the summed asked/paid; the $0
    // close-out shorthand counts as paid when every DEP row says so).
    var status = '';
    if (depInvoices.length > 0) {
      if (asked > 0 && paid >= asked - 0.005) status = 'paid';
      else if (paid > 0.005) status = 'partial';
      else if (asked === 0 && depInvoices.every(function(dep) {
        return String(dep.status || '').toLowerCase() === 'paid';
      })) status = 'paid';
      else status = 'sent';
    }
    return {
      proposal: p,
      depInvoices: depInvoices,
      asked: asked,
      paid: paid,
      status: status,
      askedSource: askedSource,
    };
  });

  // Decision 4 — dedupe EVERY per-item DEP already counted through its
  // proposal.
  var depIds = {};
  depositRows.forEach(function(row) {
    (row.depInvoices || []).forEach(function(dep) {
      depIds[String(dep.id)] = true;
    });
  });
  var invoiceRows = taggedInvoices
    .filter(function(inv) { return !depIds[String(inv.id)]; })
    .map(function(inv) {
      var asked = bh2Round(parseFloat(inv.amountDue) || parseFloat(inv.amount) || 0);
      var paid = bh2Round(calcPaidForDocument(allocations, 'invoice', inv.id));
      var status = derivePaidStatus(allocations, 'invoice', inv.id, asked, inv.status);
      return {
        invoice: inv,
        ref: inv.invoiceRef || inv.invoice_ref || '',
        type: String(inv.invoiceType || inv.invoice_type || ''),
        name: billingDocDisplayName(inv, proposals),
        asked: asked,
        paid: paid,
        status: status,
        linkedByRef: false,
        frozenOnly: false,
      };
    });

  // Decision 9 — reconcile the frozen send manifest's invoice list
  // against what tag-based resolution found. Refs already accounted for:
  // tagged rows + every per-item DEP (a DEP can legitimately appear in
  // invoicesThisMonth when it rode the cover's invoice activity table).
  var countedRefs = {};
  invoiceRows.forEach(function(row) { if (row.ref) countedRefs[row.ref] = true; });
  depositRows.forEach(function(row) {
    (row.depInvoices || []).forEach(function(dep) {
      var ref = dep.invoiceRef || dep.invoice_ref || '';
      if (ref) countedRefs[ref] = true;
    });
  });
  var manifestLinkedCount = 0;
  var frozenOnlyCount = 0;
  frozenInvoices.forEach(function(fi) {
    var ref = fi ? String(fi.ref || '') : '';
    if (!ref || countedRefs[ref]) return;
    countedRefs[ref] = true;   // also dedupes duplicate manifest entries
    var match = invoices.find(function(inv) {
      if (!inv || String(inv.invoiceRef || inv.invoice_ref || '') !== ref) return false;
      var st = String(inv.status || '').toLowerCase();
      return st !== 'draft' && st !== 'void' && st !== 'cancelled';
    }) || null;
    if (match && depIds[String(match.id)]) return;   // id-level DEP dedupe
    if (match) {
      // Live row exists but was never tagged (or the tag was lost) —
      // join it as a full money-bearing row. Asked from ITS row (the
      // canonical amount_due), paid from allocations.
      var asked = bh2Round(parseFloat(match.amountDue) || parseFloat(match.amount) || 0);
      var paid = bh2Round(calcPaidForDocument(allocations, 'invoice', match.id));
      invoiceRows.push({
        invoice: match,
        ref: ref,
        type: String(match.invoiceType || match.invoice_type || ''),
        name: billingDocDisplayName(match, proposals),
        asked: asked,
        paid: paid,
        status: derivePaidStatus(allocations, 'invoice', match.id, asked, match.status),
        linkedByRef: true,
        frozenOnly: false,
      });
      manifestLinkedCount += 1;
    } else {
      // No live row at all — display row from the frozen manifest so the
      // package never under-reports what the client received. Paid is
      // unknowable (no id for allocations to reference); its ask still
      // counts, so the balance may overstate until the row is restored.
      invoiceRows.push({
        invoice: null,
        ref: ref,
        type: String(fi.type || ''),
        name: fi.name || (fi.type ? String(fi.type) + ' invoice' : ref),
        asked: bh2Round(fi.amount),
        paid: 0,
        paidUnknown: true,
        status: '',
        linkedByRef: false,
        frozenOnly: true,
      });
      frozenOnlyCount += 1;
    }
  });
  var manifestMismatch = manifestLinkedCount + frozenOnlyCount > 0;

  // Decision 6 — credits applied to this summary. Stored negative; abs-sum.
  var creditsApplied = bh2Round(clientCreditLedger.reduce(function(sum, e) {
    if (!e || e.entryType !== 'applied_to_billing_summary') return sum;
    if (String(e.targetSummaryId || '') !== sid) return sum;
    return sum + Math.abs(parseFloat(e.amount) || 0);
  }, 0));

  // Decision 5 — services ask. Off-cycle packages ask nothing for
  // services by construction (proposals only).
  var retainerRequested = hasRetainer ? bh2Round(s.retainer_request) : 0;
  var servicesAsked = isOffCycle
    ? 0
    : (hasRetainer
      ? retainerRequested
      : bh2Round(calcBillingSummaryAmount(s, receipts, false)));

  // Procurement ask, credits netted, clamped at $0 (Rule 7).
  var depositAskedTotal = bh2Round(depositRows.reduce(function(sum, row) { return sum + row.asked; }, 0));
  var invoiceAskedTotal = bh2Round(invoiceRows.reduce(function(sum, row) { return sum + row.asked; }, 0));
  var procurementAskedGross = bh2Round(depositAskedTotal + invoiceAskedTotal);
  var procurementAsked = Math.max(0, bh2Round(procurementAskedGross - creditsApplied));

  var asked = bh2Round(servicesAsked + procurementAsked);

  // Hours row — decision 7's retainer carve-out. On retainer projects the
  // hours invoice is retainer-funded (a draw, not a client payment) and is
  // never a payment target; its derived status would always read
  // 'Awaiting payment', which would be a lie. Flag it retainerFunded and
  // exclude it from PAID.
  var hoursRow = null;
  if (hoursInvoice) {
    var hoursNum = String(hoursInvoice.invoice_number || hoursInvoice.invoiceNumber || '');
    var hoursAsked = bh2Round(hoursInvoice.total_amount != null ? hoursInvoice.total_amount : hoursInvoice.totalAmount);
    var hoursPaid = hasRetainer ? 0 : bh2Round(calcPaidForDocument(allocations, 'hours_invoice', hoursNum));
    hoursRow = {
      hoursInvoice: hoursInvoice,
      invoiceNumber: hoursNum,
      asked: hoursAsked,
      paid: hoursPaid,
      status: hasRetainer ? '' : derivePaidStatus(allocations, 'hours_invoice', hoursNum, hoursAsked, hoursInvoice.status),
      retainerFunded: hasRetainer,
    };
  }

  // Decision 8 — HEURISTIC retainer-replenishment received.
  var retainerReceived = 0;
  if (hasRetainer && retainerRequested > 0 && s.sent_at) {
    var sentDate = String(s.sent_at).slice(0, 10);
    var depositsSince = bh2Round(retainerLedger.reduce(function(sum, e) {
      if (!e || e.entryType !== 'deposit') return sum;
      if (String(e.projectLabel || '') !== String(s.project_label || '')) return sum;
      if (String(e.entryDate || '').slice(0, 10) < sentDate) return sum;
      return sum + (parseFloat(e.amount) || 0);
    }, 0));
    retainerReceived = Math.min(retainerRequested, depositsSince);
  }

  // Decision 7 — PAID from allocations against the package's real doc ids.
  var procurementPaid = bh2Round(
    depositRows.reduce(function(sum, row) { return sum + row.paid; }, 0)
    + invoiceRows.reduce(function(sum, row) { return sum + row.paid; }, 0));
  var summaryDirectPaid = bh2Round(calcPaidForDocument(allocations, 'billing_summary', sid));
  var servicesPaid = bh2Round(
    (hoursRow && !hoursRow.retainerFunded ? hoursRow.paid : 0)
    + summaryDirectPaid
    + retainerReceived);

  // Credit-funded allocations (2026-09-27). A credit applied to the
  // statement is netted from ASKED (Rule 7). When the payment wizard
  // places that credit on the package's documents it does so through a
  // client_payments row with method CLIENT_CREDIT_PAYMENT_METHOD, so the
  // invoices flip paid and the item/PO cascade fires — but that money is
  // not cash, and counting it in PAID against a net ASKED would read as
  // an overpayment of exactly the credit. Net it back out here; the
  // per-row paid (invoice status) keeps counting it.
  var packageDocKeys = [];
  depositRows.forEach(function(row) {
    (row.depInvoices || []).forEach(function(dep) { if (dep && dep.id) packageDocKeys.push('invoice|' + String(dep.id)); });
  });
  invoiceRows.forEach(function(row) {
    if (row && row.invoice && row.invoice.id) packageDocKeys.push('invoice|' + String(row.invoice.id));
  });
  if (hoursRow && hoursRow.invoiceNumber) packageDocKeys.push('hours_invoice|' + String(hoursRow.invoiceNumber));
  if (sid) packageDocKeys.push('billing_summary|' + sid);
  var creditsPlaced = bh2Round(calcCreditFundedPaid(allocations, payments, packageDocKeys));
  var creditToPlace = Math.max(0, bh2Round(creditsApplied - creditsPlaced));

  var paid = Math.max(0, bh2Round(procurementPaid + servicesPaid - creditsPlaced));
  var balance = Math.max(0, bh2Round(asked - paid));
  var state = billingPackagePaidState(asked, paid);

  // Docs the client actually received: cover + hours invoice + each
  // proposal + each invoice row (tagged, ref-linked, AND frozen-only —
  // the manifest rows were real attachments in the delivered zip).
  // Internal per-item DEPs never count as documents — the proposal is
  // the client-facing deposit vehicle.
  var docCount = 1 + (hoursInvoice ? 1 : 0) + taggedProposals.length + invoiceRows.length;

  return {
    summary: s,
    summaryId: sid,
    projectLabel: s.project_label || '',
    clientName: s.client_name || '',
    year: s.year,
    month: s.month,
    sentAt: s.sent_at || '',
    hasRetainer: hasRetainer,
    summaryType: isOffCycle ? 'off_cycle' : 'monthly',
    periodLabel: billingSummaryPeriodLabel(s),
    monthYear: billingSummaryMonthYear(s),

    taggedProposals: taggedProposals,
    taggedInvoices: taggedInvoices,
    taggedReceipts: taggedReceipts,
    hoursInvoice: hoursInvoice,
    hoursRow: hoursRow,
    depositRows: depositRows,
    invoiceRows: invoiceRows,

    retainer: hasRetainer ? {
      requested: retainerRequested,
      requestRecorded: s.retainer_request != null && retainerRequested > 0,
      received: retainerReceived,
      receivedIsHeuristic: true,   // decision 8 — keep the UI copy soft
    } : null,

    servicesAsked: servicesAsked,
    servicesPaid: servicesPaid,
    summaryDirectPaid: summaryDirectPaid,
    depositAskedTotal: depositAskedTotal,
    invoiceAskedTotal: invoiceAskedTotal,
    creditsApplied: creditsApplied,
    creditsPlaced: creditsPlaced,
    creditToPlace: creditToPlace,
    procurementAskedGross: procurementAskedGross,
    procurementAsked: procurementAsked,
    procurementPaid: procurementPaid,

    asked: asked,
    paid: paid,
    balance: balance,
    state: state,
    docCount: docCount,

    // Decision 9 — send-record reconciliation flags. manifestMismatch
    // means the frozen manifest lists documents that weren't tagged to
    // this summary in Gilda; the UI surfaces one quiet note suggesting
    // the tag fix.
    manifestMismatch: manifestMismatch,
    manifestLinkedCount: manifestLinkedCount,
    frozenOnlyCount: frozenOnlyCount,
    frozenSend: frozenSend,
  };
}

// ── resolveBillingPackagePayments ──────────────────────────────────────────
// "Recorded client payments" cards for a resolved package: one card per
// client_payments row that has at least one allocation touching the
// package's documents (DEP/tagged invoices by id, hours invoice by number,
// the summary container itself). Each allocation renders as a plain
// sentence line — amount → friendly document name.
//
// The card also reports whether the parent payment is fully applied across
// ALL its allocations (not just this package's) so the UI can note
// unallocated remainders honestly.
export function resolveBillingPackagePayments(pkg, refs) {
  if (!pkg) return [];
  var r = refs || {};
  var payments    = r.payments    || [];
  var allocations = r.allocations || [];
  var proposals   = r.proposals   || [];

  // Friendly label per allocatable document key. Every PER-ITEM DEP id
  // maps back to its proposal's name — DEPs are internal plumbing the
  // user never sees, so the allocation sentence reads as the proposal's
  // deposit, never as an invoice.
  var keys = {};
  (pkg.depositRows || []).forEach(function(row) {
    var p = row.proposal || {};
    var name = p.name || p.proposal_ref || 'Proposal';
    (row.depInvoices || []).forEach(function(dep) {
      keys['invoice|' + String(dep.id)] = {
        label: name + ' — deposit',
        ref: p.proposal_ref || '',
      };
    });
  });
  (pkg.invoiceRows || []).forEach(function(row) {
    if (!row.invoice) return;   // frozen-only display rows have no id to allocate against
    keys['invoice|' + String(row.invoice.id)] = {
      label: row.name || billingDocDisplayName(row.invoice, proposals),
      ref: row.ref || row.invoice.invoiceRef || row.invoice.invoice_ref || '',
    };
  });
  if (pkg.hoursRow && pkg.hoursRow.invoiceNumber) {
    keys['hours_invoice|' + pkg.hoursRow.invoiceNumber] = {
      label: 'Design services — ' + pkg.monthYear,
      ref: pkg.hoursRow.invoiceNumber,
    };
  }
  keys['billing_summary|' + pkg.summaryId] = {
    label: 'Billing summary — services & expenses',
    ref: '',
  };

  // Group matching allocations by payment; also total EVERY allocation per
  // payment so we can report unapplied remainders.
  var linesByPayment = {};
  var totalAllocatedByPayment = {};
  allocations.forEach(function(a) {
    if (!a || !a.paymentId) return;
    var amt = parseFloat(a.amount) || 0;
    totalAllocatedByPayment[a.paymentId] = (totalAllocatedByPayment[a.paymentId] || 0) + amt;
    var key = String(a.documentType || '') + '|' + String(a.documentId || '');
    var hit = keys[key];
    if (!hit) return;
    (linesByPayment[a.paymentId] = linesByPayment[a.paymentId] || []).push({
      amount: bh2Round(amt),
      label: hit.label,
      ref: hit.ref,
    });
  });

  var byId = {};
  payments.forEach(function(p) { if (p && p.id) byId[p.id] = p; });

  return Object.keys(linesByPayment).map(function(pid) {
    var payment = byId[pid] || { id: pid, amountTotal: 0 };
    var lines = linesByPayment[pid];
    var toPackage = bh2Round(lines.reduce(function(sum, ln) { return sum + ln.amount; }, 0));
    var unallocated = bh2Round((parseFloat(payment.amountTotal) || 0) - (totalAllocatedByPayment[pid] || 0));
    return {
      payment: payment,
      lines: lines,
      totalToPackage: toPackage,
      unallocated: unallocated,
      fullyApplied: Math.abs(unallocated) <= 0.005,
    };
  }).sort(function(a, b) {
    return String(a.payment.paymentDate || '').localeCompare(String(b.payment.paymentDate || ''));
  });
}

// ── buildPackagePaymentTargets ─────────────────────────────────────────────
// Allocatable targets for the Record Client Payment wizard, launched from
// Billing History's package preview (the recording surface moved here from
// the Billing Summary workbench on 2026-07-02 — Cimon: "Billing history
// should be the place to record payment"). Built from a resolved package
// (resolveBillingPackage output) so the wizard and the preview's money
// strip read the SAME derivation.
//
// Targets are the package's UNDERLYING documents — never the summary
// container (billing_summary is retired as a new-payment allocation
// target):
//   (a) every PER-ITEM DEP invoice across all depositRows — shown by the
//       PROPOSAL's name (+ the item's name when the proposal has several
//       DEPs), allocated to the hidden DEP id so the deposit→item→PO
//       cascade fires. Same label convention as the retired
//       BillingSummarySection builder; DEPs are internal plumbing, so the
//       label never leans on the DEP ref — when the item can't be
//       resolved, a positional "item N" stands in.
//   (b) every invoice row — tagged AND linkedByRef (send-record
//       reconciled rows are real invoices; payments should land on them).
//       Frozen-only rows are EXCLUDED: no live row means no id for an
//       allocation to reference (Rule 10 — no target without a document
//       payment records can point at).
//   (c) the hours invoice on NON-retainer projects only. On retainer
//       projects design hours are retainer-funded (a draw, not a client
//       payment) — deliberately not a payment target.
//
// buildPaymentTargets nets prior allocations into per-target balances and
// drops fully-paid documents, so the wizard only offers what's still open.
//
// args:
//   pkg  — resolveBillingPackage output
//   refs — { items, allocations } (AppContext shapes; items resolve the
//           per-item DEP labels)
export function buildPackagePaymentTargets(pkg, refs) {
  if (!pkg) return [];
  var r = refs || {};
  var items = r.items || [];
  var allocations = r.allocations || [];
  var candidates = [];

  // (a) per-item DEPs, labeled by proposal (+ item when plural).
  (pkg.depositRows || []).forEach(function(row) {
    var p = row.proposal || {};
    var deps = row.depInvoices || [];
    // Proposals carry their client-facing name on `name` (walkthrough
    // test caught a lookup of nonexistent proposal_name — which fell
    // back to the REF, exactly the internal-plumbing leak Cimon banned).
    var proposalName = p.name || p.proposal_ref || 'Proposal';
    deps.forEach(function(dep, i) {
      var itemBit = '';
      if (deps.length > 1) {
        var itemId = dep.itemId || dep.item_id;
        var it = itemId
          ? items.find(function(x) { return x && String(x.id) === String(itemId); })
          : null;
        var itemName = it ? (it.item || it.productName || '') : '';
        itemBit = ' · ' + (itemName || 'item ' + (i + 1));
      }
      candidates.push({
        documentType: 'invoice',
        documentId: dep.id,
        label: proposalName + ' — deposit' + itemBit,
        ref: p.proposal_ref || p.proposalRef || '',
        asked: parseFloat(dep.amountDue) || parseFloat(dep.amount) || 0,
      });
    });
  });

  // (b) tagged + ref-linked invoice rows; frozen-only rows have no live
  // record to allocate against.
  (pkg.invoiceRows || []).forEach(function(row) {
    if (!row || !row.invoice || row.frozenOnly) return;
    candidates.push({
      documentType: 'invoice',
      documentId: row.invoice.id,
      label: row.name || 'Invoice',
      ref: row.ref || '',
      asked: row.asked,
    });
  });

  // (c) hours invoice — non-retainer projects only.
  var servicesCoveredByHours = 0;
  if (pkg.hoursRow && !pkg.hoursRow.retainerFunded && pkg.hoursRow.invoiceNumber) {
    candidates.push({
      documentType: 'hours_invoice',
      documentId: pkg.hoursRow.invoiceNumber,
      label: 'Design services — ' + pkg.monthYear,
      ref: pkg.hoursRow.invoiceNumber,
      asked: pkg.hoursRow.asked,
    });
    servicesCoveredByHours = parseFloat(pkg.hoursRow.asked) || 0;
  }

  // (d) services & expenses remainder — non-retainer projects only. The
  // package's services ask (design amount + additional services + expenses
  // + reimbursable receipt lines) is part of ASKED whether or not an hours
  // invoice exists, but only the hours invoice portion has a document of
  // its own. Whatever the hours invoice doesn't cover (no hours invoice
  // resolved, or expenses/additional services beyond it) would otherwise be
  // money the wizard can never place — the bookkeeper enters the full wire
  // on Step 1 and Step 2's full-allocation gate locks with no row to put
  // the services dollars on (the 26-Moskito May 2026 blocked screen,
  // 2026-07-04). Target the summary row itself: resolveBillingPackage
  // already counts 'billing_summary' allocations in servicesPaid, and
  // InvoicesTab.onBankDepositSaved already dispatches the status flip for
  // that document type. This is the ONE sanctioned use of billing_summary
  // as a new-payment allocation target — a services ask with no underlying
  // document; procurement money always lands on real DEP/FIN ids.
  if (!pkg.hasRetainer) {
    var servicesRemainder = Math.round(
      (((parseFloat(pkg.servicesAsked) || 0) - servicesCoveredByHours)) * 100) / 100;
    if (servicesRemainder > 0.005 && pkg.summaryId) {
      candidates.push({
        documentType: 'billing_summary',
        documentId: pkg.summaryId,
        label: (servicesCoveredByHours > 0 ? 'Project expenses & additional services — ' : 'Design services & expenses — ') + pkg.monthYear,
        ref: '',
        asked: servicesRemainder,
      });
    }
  }

  return buildPaymentTargets(candidates, allocations);
}

// ── Payment wizard step helpers (2026-07-10 five-step redesign) ────────────
// The Record Client Payment wizard walks money categories as explicit
// steps (Cimon's design): invoices → design hours & reimbursements →
// retainer refill (retainer projects) → confirm. These pure helpers are
// the wizard's brain for splitting one bank deposit across those
// categories; the component stays presentational.

// Split the flat target list into the two allocation steps.
//   invoices — documentType 'invoice' (per-item DEPs + FINs)
//   services — everything else (hours_invoice + the billing_summary
//              services remainder; only exists on non-retainer projects)
export function splitPaymentTargets(targets) {
  var invoices = [];
  var services = [];
  (targets || []).forEach(function(t) {
    if (!t) return;
    if (t.documentType === 'invoice') invoices.push(t);
    else services.push(t);
  });
  return { invoiceTargets: invoices, serviceTargets: services };
}

// Step-1 preview: given the full bank figure, suggest where it will go.
// Greedy in step order — invoices, then services, then the retainer,
// then deposits AWAITING PROPOSAL APPROVAL (not payable yet — approval
// mints their invoice records; the preview names them so the money's
// destination is visible instead of reading as unexplained leftover,
// the 26PTA-P-003 test-drive confusion, 2026-07-10) — with whatever's
// truly left flagged for the confirm-step choice (save as credit /
// leave it alone). Pure display math: the per-step inputs stay the
// source of truth; nothing here writes anything.
export function buildPaymentSplitPreview(amountTotal, targets, retainerOutstanding, pendingDepositsTotal) {
  function r2(x) { return Math.round((parseFloat(x) || 0) * 100) / 100; }
  var total = r2(amountTotal);
  var split = splitPaymentTargets(targets);
  var invoicesOpen = r2(split.invoiceTargets.reduce(function(s, t) { return s + (parseFloat(t.balance) || 0); }, 0));
  var servicesOpen = r2(split.serviceTargets.reduce(function(s, t) { return s + (parseFloat(t.balance) || 0); }, 0));
  var retainerOpen = Math.max(0, r2(retainerOutstanding));
  var pendingOpen = Math.max(0, r2(pendingDepositsTotal));
  var rest = total;
  var toInvoices = Math.min(rest, invoicesOpen); rest = r2(rest - toInvoices);
  var toServices = Math.min(rest, servicesOpen); rest = r2(rest - toServices);
  var toRetainer = Math.min(rest, retainerOpen); rest = r2(rest - toRetainer);
  var toPending  = Math.min(rest, pendingOpen);  rest = r2(rest - toPending);
  return {
    invoicesOpen: invoicesOpen,
    servicesOpen: servicesOpen,
    retainerOutstanding: retainerOpen,
    pendingDepositsOpen: pendingOpen,
    toInvoices: r2(toInvoices),
    toServices: r2(toServices),
    toRetainer: r2(toRetainer),
    toPendingDeposits: r2(toPending),
    leftover: Math.max(0, rest),
  };
}

// Plain-language reason for money left over after every step has taken
// its share. alreadyPaidLines = [{ label, amount }] — package documents
// that carried payments from BEFORE this deposit (the cover bills each
// invoice at its full amount, so a client who pays the cover total pays
// any pre-paid portion twice — the 26HOB-INV-002-FIN March-credit shape).
// Returns '' when there's nothing left over.
export function explainPaymentLeftover(leftover, alreadyPaidLines, pendingDeposits) {
  var left = Math.round((parseFloat(leftover) || 0) * 100) / 100;
  if (left <= 0.005) return '';
  // Deposits awaiting proposal approval FIRST — this money has a known
  // destination (the deposit invoices approval will mint), it just isn't
  // payable yet. Steer to "leave it alone" + approve, never to a credit.
  var pend = (pendingDeposits || []).filter(function(d) {
    return d && (parseFloat(d.asked) || 0) > 0.005;
  });
  var pendTotal = Math.round(pend.reduce(function(s, d) { return s + (parseFloat(d.asked) || 0); }, 0) * 100) / 100;
  if (pend.length > 0 && left >= pendTotal - 0.02) {
    var pendBits = pend.map(function(d) {
      return d.name + ' (' + fmtMoney(d.asked) + ')';
    }).join(', ');
    return 'This matches the deposit' + (pend.length > 1 ? 's' : '') + ' awaiting proposal approval: ' + pendBits + '. '
      + 'Leave it alone for now — approve the proposal (Approve + Assign SKU on the Proposals tab), then record this money against the deposit. It should not become a credit.';
  }
  var lines = (alreadyPaidLines || []).filter(function(l) {
    return l && (parseFloat(l.amount) || 0) > 0.005;
  });
  var priorPaid = Math.round(lines.reduce(function(s, l) { return s + (parseFloat(l.amount) || 0); }, 0) * 100) / 100;
  if (lines.length > 0 && Math.abs(priorPaid - left) <= 0.02) {
    var bits = lines.map(function(l) {
      return l.label + ' (' + fmtMoney(l.amount) + ')';
    }).join(', ');
    return 'This matches money already paid before this deposit: ' + bits + '. '
      + 'The summary asked for those amounts in full, so paying the summary total pays them twice.';
  }
  return 'The client sent more than this billing summary asked, and nothing in the records explains the difference.';
}

// The wizard's final gate. Checks every piece of the split and names the
// first problem in plain language. leftoverChoice: '' | 'credit' | 'leave'.
// Returns { ok, error, needsLeftoverChoice, allocatedTotal, retainerAmount,
// leftover } — leftover is what the confirm step's credit choice governs.
export function validatePaymentWizardSplit(amountTotal, allocations, targets, retainerAmount, leftoverChoice) {
  function r2(x) { return Math.round((parseFloat(x) || 0) * 100) / 100; }
  var total = r2(amountTotal);
  var retainer = Math.max(0, r2(retainerAmount));
  var byKey = {};
  (targets || []).forEach(function(t) {
    if (!t) return;
    byKey[t.documentType + '|' + String(t.documentId)] = {
      balance: r2(t.balance), label: t.label || 'document',
    };
  });
  var allocated = 0;
  var firstError = '';
  (allocations || []).forEach(function(a) {
    if (!a) return;
    var amt = parseFloat(a.amount) || 0;
    if (amt <= 0) return;
    allocated += amt;
    var hit = byKey[a.documentType + '|' + String(a.documentId)];
    if (!hit) {
      if (!firstError) firstError = 'Allocation target not recognized.';
    } else if (amt > hit.balance + 0.005) {
      if (!firstError) firstError = fmtMoney(amt) + ' is more than the ' + fmtMoney(hit.balance) + ' still owed on ' + hit.label + '.';
    }
  });
  allocated = r2(allocated);
  var leftover = r2(total - allocated - retainer);
  var base = { allocatedTotal: allocated, retainerAmount: retainer, leftover: Math.max(0, leftover), needsLeftoverChoice: false };
  if (firstError) return Object.assign(base, { ok: false, error: firstError });
  if (total <= 0) return Object.assign(base, { ok: false, error: 'Enter a payment amount.' });
  if (leftover < -0.005) {
    return Object.assign(base, {
      ok: false,
      error: 'The pieces add up to ' + fmtMoney(-leftover) + ' more than the ' + fmtMoney(total) + ' received — reduce the retainer amount or an invoice line.',
    });
  }
  if (leftover > 0.005 && leftoverChoice !== 'credit' && leftoverChoice !== 'leave') {
    return Object.assign(base, {
      ok: false,
      needsLeftoverChoice: true,
      error: fmtMoney(leftover) + ' of this payment is left over — choose what to do with it below.',
    });
  }
  return Object.assign(base, { ok: true, error: '' });
}

// ── packagePendingDepositAsks ──────────────────────────────────────────────
// Proposals on the package whose deposit CANNOT take a payment yet: the
// client was asked for the deposit on the cover, but no per-item DEP
// invoices exist because the proposal hasn't been approved — approval is
// what mints them (Cimon's flow: client pays → Approve + Assign SKU →
// record the payment). Without this list the wizard silently offers no row
// for the deposit and the bookkeeper hits Step 2's full-allocation gate
// with money that has nowhere to go. The wizard renders these as an
// explanatory notice: approve the proposal first, then record.
export function packagePendingDepositAsks(pkg) {
  if (!pkg) return [];
  return (pkg.depositRows || [])
    .filter(function(row) {
      return row && (row.depInvoices || []).length === 0
        && (parseFloat(row.asked) || 0) > 0.005;
    })
    .map(function(row) {
      var p = row.proposal || {};
      return {
        proposalId: p.id || '',
        name: p.name || p.proposal_ref || 'Proposal',
        asked: Math.round((parseFloat(row.asked) || 0) * 100) / 100,
      };
    });
}

// ── Off-cycle packages (2026-07-04) ────────────────────────────────────────
// A proposals-only statement sent OUTSIDE the monthly cycle (urgent
// proposal that can't wait for month-end). Stored as a real
// billing_summaries row (summary_type='off_cycle', status 'sent' at
// insert — never a draft) so Billing History, document_sends, and the
// payment wizard work unchanged. These helpers are the pure core the
// OffCyclePackageModal builds on.

// Proposals a project can put on an off-cycle package:
//   - Ready for Review (the send IS the Mark Sent — the modal stamps
//     status 'Sent' + sent_via 'off_cycle' on send), or
//   - already Sent off-cycle / legacy-null sentVia but never packaged
//     (lets Cimon backfill history for proposals she emailed by hand).
// NEVER proposals waiting on a monthly summary (sentVia
// 'billing_summary'), never rows already tagged to a package, never
// Draft (not client-ready) or Approved (deposit already in motion).
export function offCycleEligibleProposals(proposals, projectLabel) {
  return (proposals || []).filter(function(p) {
    if (!p || String(p.project || '') !== String(projectLabel || '')) return false;
    if (p.billedOnSummaryId || p.billed_on_summary_id) return false;
    var status = String(p.status || '');
    var via = String(p.sentVia || p.sent_via || '');
    if (status === 'Ready for Review') return true;
    if (status === 'Sent' && via !== 'billing_summary') return true;
    return false;
  });
}

// Final invoices a project can put on an off-cycle package (2026-08-12 —
// the FIN half of the July client-payment scope; the proposals half
// shipped 2026-07-04). Eligible:
//   - status 'ready' (Mark Ready'd, awaiting delivery — this send IS the
//     delivery: the modal flips them to 'sent' + stamps sent_at), or
//   - already 'sent'/'partial' but never packaged (backfill for FINs
//     delivered by hand via the per-FIN Mark Sent button — mirrors the
//     proposal backfill rule).
// NEVER draft (work in progress — same contract as bsInvoicesList),
// never paid/void/cancelled (nothing to ask), never rows tagged to
// another summary, and only invoice_type 'final' — DEPs are internal
// records minted at approval and reimbursables ride monthly summaries.
export function offCycleEligibleFinalInvoices(invoices, projectLabel) {
  return (invoices || []).filter(function(inv) {
    if (!inv) return false;
    var proj = inv.project || inv.projectLabel || inv.project_label || '';
    if (String(proj) !== String(projectLabel || '')) return false;
    if (inv.billedOnSummaryId || inv.billed_on_summary_id) return false;
    if (String(inv.invoiceType || inv.invoice_type || '') !== 'final') return false;
    var st = String(inv.status || '').toLowerCase();
    return st === 'ready' || st === 'sent' || st === 'partial';
  });
}

// Names-first row label (mirrors BillingSummarySection's
// invoiceFriendlyName for the FIN case): the item name(s) off the
// invoice's own line_items snapshot — the same names the client sees on
// the invoice PDF itself. FINs are PO-scoped and rarely carry a
// proposal_id, so a type label is the only fallback.
function offCycleInvoiceName(inv) {
  var lineNames = ((inv && (inv.lineItems || inv.line_items)) || [])
    .map(function(l) { return String((l && (l.item || l.productName)) || '').trim(); })
    .filter(Boolean);
  if (lineNames.length === 1) return lineNames[0];
  if (lineNames.length > 1) return lineNames[0] + ' + ' + (lineNames.length - 1) + ' more';
  return 'Final Invoice';
}

// Cover-row math for one final invoice — Rule 10: the AMOUNT is the
// OUTSTANDING balance (full ask minus cash actually allocated), never
// the stored ask alone. Same definition as the monthly workbench's
// bsInvoicesList rows; the modal drops rows whose outstanding is $0.
export function buildOffCycleInvoiceRow(inv, allocations) {
  var fullAsk = parseFloat(
    inv.amountDue != null ? inv.amountDue
      : (inv.amount_due != null ? inv.amount_due : inv.amount)
  ) || 0;
  var paid = Math.round(calcPaidForDocument(allocations || [], 'invoice', inv.id) * 100) / 100;
  // Statement presentation (2026-09-07, Cimon) — mirrors the monthly
  // workbench's bsInvoicesList: ONE number per invoice, the amount still
  // due, with the deposit already netted inside it. The gross-value row
  // plus a LESS: DEPOSIT PREVIOUSLY PAID section shipped earlier the same
  // day and was reverted: it made the client add and subtract to find the
  // figure they actually owe.
  return {
    invoice: inv,
    ref:     inv.invoiceRef || inv.invoice_ref || '',
    name:    offCycleInvoiceName(inv),
    fullAsk: fullAsk,
    paid:    paid,
    amount:  Math.max(0, Math.round((fullAsk - paid) * 100) / 100),
  };
}

// Cover-row math for one proposal — LIVE from current item state, the
// same definition the monthly workbench's bsProposalsList uses
// (proposalGrandTotal for the full contract value; per-item
// itemDepositAmount summed for the deposit ask, project passed through
// for the fresh tax basis).
export function buildOffCycleProposalRow(p, allItems, project) {
  var liveItems = (p.items || [])
    .map(function(id) { return (allItems || []).find(function(it) { return it && it.id === id; }); })
    .filter(Boolean);
  var totalClient = proposalGrandTotal(liveItems, allItems, project);
  // Canonical deposit-ask formula (2026-07-08): itemDepositAmount with
  // project (fresh tax) × ccFeeMul, rounded PER ITEM — matches the
  // per-item DEP amount_due buildDepositInvoiceRecords mints at approval,
  // so the off-cycle cover's deposit ask equals the DEP sum later.
  var totalDeposit = Math.round(liveItems.reduce(function(s, it) {
    return s + Math.round(itemDepositAmount(it, allItems, project) * ccFeeMul(it) * 100) / 100;
  }, 0) * 100) / 100;
  return {
    proposal: p,
    ref: p.proposal_ref || p.proposalRef || '',
    name: p.name || p.proposal_name || '',
    totalClient: totalClient,
    totalDeposit: totalDeposit,
  };
}

// Renderer payload for the off-cycle cover — same shape as the monthly
// buildSummaryPayload (the /api/generate-billing-summary contract) with
// offCycle:true and every services/retainer field zeroed. The
// proposalsThisMonth entries double as the FROZEN SEND MANIFEST
// resolveBillingPackage's deposit-ask fallback reads (fp.ref →
// fp.totalDeposit), so the keys here are load-bearing.
export function buildOffCyclePackagePayload(args) {
  var a = args || {};
  var project = a.project || {};
  var client = a.client || {};
  var rows = a.rows || [];
  var invoiceRows = a.invoiceRows || [];   // buildOffCycleInvoiceRow shapes (2026-08-12)
  var dateIso = String(a.dateIso || '').slice(0, 10);
  var depositRequested = bh2Round(rows.reduce(function(s, r) { return s + (parseFloat(r.totalDeposit) || 0); }, 0));
  var proposalTotal = bh2Round(rows.reduce(function(s, r) { return s + (parseFloat(r.totalClient) || 0); }, 0));
  var invoicesDue = bh2Round(invoiceRows.reduce(function(s, r) { return s + (parseFloat(r.amount) || 0); }, 0));
  var procDue = bh2Round(depositRequested + invoicesDue);
  return {
    offCycle: true,
    date: offCycleDateLabel(dateIso),
    billTo: {
      name:    client.name           || '',
      address: client.billingAddress || '',
      city:    client.billingCity    || '',
      state:   client.billingState   || '',
      zip:     client.billingZip     || '',
    },
    forPeriod:      offCycleDateLabel(dateIso),
    projectLabel:   project.label || '',
    projectDisplay: project.displayName || project.label || '',
    servicesAndExpenses: { designHours: 0, designAmount: 0, receiptLines: [], amountDue: 0 },
    designServices: { hours: 0, amount: 0 },
    additionalServices: [],
    expenses: [],
    billingMethod: 'hourly',     // never the retainer rendering path
    retainerBalance: 0,
    retainerThreshold: 0,
    hoursNarratives: [],
    procurement: {
      proposalTotal:    proposalTotal,
      depositRequested: depositRequested,
      invoicesDue:      invoicesDue,
    },
    proposalsThisMonth: rows.map(function(r) {
      return {
        ref: r.ref || '',
        name: r.name || '',
        sentDate: offCycleDateLabel(dateIso),
        totalDeposit: r.totalDeposit,
        totalClient: r.totalClient,
        isOutstanding: true,
        amountOwed: r.totalDeposit,
      };
    }),
    // FIN rows (2026-08-12) — same shape as the monthly payload's
    // invoicesThisMonth; the renderer's FINAL INVOICES section and
    // resolveBillingPackage's decision-9 reconciliation read this as
    // the frozen send manifest.
    invoicesThisMonth: invoiceRows.map(function(r) {
      return {
        ref:    r.ref || '',
        type:   'final',
        name:   r.name || '',
        sentDate: offCycleDateLabel(dateIso),
        // `amount` is BOTH the printed figure and the frozen-manifest
        // outstanding balance — the statement prints the amount due.
        amount: r.amount,
      };
    }),
    hasHoursInvoice: false,
    creditsApplied: [],
    creditsAppliedTotal: 0,
    procAmountDueGross: procDue,
    procAmountDueNet: procDue,
    creditsExcess: 0,
  };
}

// The billing_summaries row an off-cycle send INSERTs (status 'sent'
// directly — no draft state, so the one-draft-per-project index never
// bites). year/month derive from the send date purely for column
// NOT-NULL satisfaction + coarse sorting; labels always use the date.
export function buildOffCycleSummaryRow(args) {
  var a = args || {};
  var project = a.project || {};
  var client = a.client || {};
  var dateIso = String(a.dateIso || '').slice(0, 10);
  var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateIso) || [];
  var rows = a.rows || [];
  var invoiceRows = a.invoiceRows || [];
  return {
    project_label: project.label || '',
    client_name:   client.name || '',
    year:          parseInt(m[1], 10) || new Date().getFullYear(),
    month:         parseInt(m[2], 10) || (new Date().getMonth() + 1),
    status:        'sent',
    summary_type:  'off_cycle',
    // Noon UTC keeps the DATE stable across timezones when the
    // timestamptz is rendered back as a local date.
    sent_at:       dateIso ? dateIso + 'T12:00:00.000Z' : new Date().toISOString(),
    has_retainer:  false,
    design_hours:  0,
    design_amount: 0,
    additional_services: [],
    expenses: [],
    procurement: {
      proposalTotal:    bh2Round(rows.reduce(function(s, r) { return s + (parseFloat(r.totalClient) || 0); }, 0)),
      depositRequested: bh2Round(rows.reduce(function(s, r) { return s + (parseFloat(r.totalDeposit) || 0); }, 0)),
      invoicesDue:      bh2Round(invoiceRows.reduce(function(s, r) { return s + (parseFloat(r.amount) || 0); }, 0)),
    },
    hours_invoice_number: '',
    notes: 'Off-cycle package',
  };
}

// ── Full-package zip plan ──────────────────────────────────────────────────
// Pure planner for the "Download full package" action. Returns the zip name
// (same shape sendPackage used for the original delivery:
// {yyyy-mm}_{project}_package.zip) plus one entry per document, each with
// an ORDERED attempts list the component executes top-down:
//
//   { via: 'stored', bucket, path }         — the exact bytes the client
//                                             received, fetched via signed
//                                             URL. ALWAYS FIRST when a
//                                             stored PDF exists.
//   { via: 'regen', endpoint, payload,      — re-render from a FROZEN
//     parse }                                 payload (document_sends /
//                                             line-item snapshot). Second.
//   { via: 'live', kind, ... }              — live-data rebuild, the last
//                                             resort; the component builds
//                                             the payload and labels the
//                                             result honestly.
//
// Anything whose attempts all fail is skipped and noted — never a silent
// drop. Filename numbering mirrors sendPackage (00 cover, 01 hours,
// 10+ invoices, 20+ proposals) so a re-assembled zip diff-matches an
// original delivery.

export function zipSafeFilename(s) {
  // Mirrors BillingSummarySection's local safeFilename.
  return String(s || '').replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 80);
}

// Pure regen payload for a deposit/final invoice from its line-item
// snapshot — mirrors sendPackage step 4's procPayload field-for-field so a
// regenerated PDF matches what the original zip contained.
// ── invoiceCcFeeTot — THE place a stored invoice's card fee comes from ──
//
// One function, five callers (buildInvoiceRegenPayload here, InvoicesTab,
// BillingSummarySection ×2, OffCyclePackageModal). Before 2026-09-07 each
// of those five inlined the same residual subtraction, so "the card fee on
// this invoice" had five independent derivations and no record of record.
//
// Stored column first. `invoices.fee_cc_processing_tot` (migration
// 20260907) is written at generation from the same ccFeeTotal the PDF was
// rendered with, so a regenerated document cannot disagree with the one
// the client received — and now that the fee is TAXED, the tax frozen in
// fee_sales_tax_tot always matches the fee frozen beside it.
//
// Residual only as the legacy fallback. Rows written before the column
// existed carry null (not 0 — rowToInvoice preserves the distinction), and
// for those the fee is recovered the old way, as the residue of the stored
// identity subtotal = product + fees + cc + adjustments. The migration
// backfills exactly this expression in SQL, so the fallback should fire
// only on a row written between the app deploy and the migration.
export function invoiceCcFeeTot(inv, opts) {
  if (!inv) return 0;
  function n(v) { var f = parseFloat(v); return isNaN(f) ? 0 : f; }
  if (inv.feeCcProcessingTot != null) {
    return Math.round(n(inv.feeCcProcessingTot) * 100) / 100;
  }
  var o = opts || {};
  var subtotal     = n(o.subtotal     != null ? o.subtotal     : inv.subtotal);
  var productTotal = n(o.productTotal != null ? o.productTotal : inv.productTotal);
  var feesTotal    = n(o.feesTotal    != null ? o.feesTotal    : inv.feesTotal);
  var adjustments  = Array.isArray(inv.adjustments)
    ? inv.adjustments.reduce(function(s, a) { return s + n(a && a.amount); }, 0)
    : 0;
  var residual = Math.round((subtotal - productTotal - feesTotal - adjustments) * 100) / 100;
  return residual > 0 ? residual : 0;
}

// ═══════════════════════════════════════════════════════════════════════════
// SNAPSHOT SPEC REFRESH (2026-09-11)
//
// An invoice freezes a copy of its line items at Save & Commit and every PDF
// surface renders from that copy. That is the right rule for money — what was
// billed was billed — but it also freezes the DESCRIPTIVE half of each line:
// the product name, the spec notes, dimensions, finish, lead time, sidemark
// and photos. Correct a lead time on the item afterwards and the invoice goes
// on saying the old one, with no way to fix it short of deleting the invoice
// (which does not unwind the final_invoice_id stamps it left on the items).
//
// These two helpers let an UNSENT invoice pull the descriptive half forward
// without touching a cent. The split is the whole point:
//
//   refreshed  item · productName · description · dimensions · finish ·
//              leadTime · sidemark · images · heroIndex
//   frozen     qty · qtyType · unitPrice · total · depositAmt · depositPct ·
//              unitPriceBase · unitPriceDelta · unitPriceDeltaLabel
//
// qtyType stays frozen with qty: they read as one phrase on the PDF ("2 EACH")
// and qty is money. The CO adjustment fields stay frozen because they describe
// a price change that was billed at a particular number.
//
// Field sources mirror the two payload builders line for line
// (buildDepositInvoicePayload and buildFinalInvoicePayload) — description
// comes from specNotes, sidemark is recomputed from the item's room, and so on
// — so a refreshed line is indistinguishable from one the builder would emit
// today.
// ═══════════════════════════════════════════════════════════════════════════

// Descriptive fields only, keyed by snapshot field name. Each entry reads a
// live item the way the payload builders do.
var SNAPSHOT_SPEC_FIELDS = [
  { key: 'item',        label: 'Item',        read: function(it) { return it.item || 'Untitled'; } },
  { key: 'productName', label: 'Product',     read: function(it) { return it.productName || ''; } },
  { key: 'description', label: 'Description', read: function(it) { return it.specNotes || ''; } },
  { key: 'dimensions',  label: 'Dimensions',  read: function(it) { return it.dimensions || ''; } },
  { key: 'finish',      label: 'Finish',      read: function(it) { return it.finish || ''; } },
  { key: 'leadTime',    label: 'Lead time',   read: function(it) { return it.leadTime || ''; } },
  { key: 'sidemark',    label: 'Sidemark',
    read: function(it, inv) {
      return computeSidemark(it.project || inv.project || inv.project_label || '',
                             it.roomName, it.roomNumber);
    } },
  { key: 'images',      label: 'Photos',      read: function(it) { return it.images || []; },
    isList: true },
  { key: 'heroIndex',   label: 'Hero photo',  read: function(it) { return it.heroIndex || 0; } },
];

// Can this invoice's snapshot be refreshed at all? Refusing is the default:
// once a document has left the office, the frozen copy IS the record of what
// the client was handed, and quietly rewriting it would make the archived PDF
// and the row disagree.
//
// allocations: the full payment_allocations list.
// Returns { ok:true } or { ok:false, reason } — reason is shown to the user.
export function canRefreshInvoiceSnapshot(inv, allocations) {
  if (!inv) return { ok: false, reason: 'No invoice.' };
  var status = String(inv.status || '').trim().toLowerCase();
  var lines = inv.lineItems || inv.line_items || [];
  if (!lines.length) {
    return { ok: false, reason: 'This invoice has no line-item snapshot to refresh.' };
  }
  if (inv.sentAt || inv.sent_at) {
    return { ok: false, reason: 'This invoice was sent to the client. The snapshot is the record of what they received.' };
  }
  if (status === 'paid') {
    return { ok: false, reason: 'This invoice is paid. The snapshot is the record of what was billed.' };
  }
  if (status !== 'draft' && status !== 'ready') {
    return { ok: false, reason: 'Only draft and ready invoices can be refreshed (this one is "' + status + '").' };
  }
  if (inv.billedOnSummaryId || inv.billed_on_summary_id) {
    return { ok: false, reason: 'This invoice rides a billing summary. Reopen that summary as a draft first.' };
  }
  var invId = String(inv.id);
  var hit = (allocations || []).filter(function(a) {
    if (!a) return false;
    var dt = a.documentType != null ? a.documentType : a.document_type;
    var did = a.documentId != null ? a.documentId : a.document_id;
    return dt === 'invoice' && String(did) === invId;
  });
  if (hit.length) {
    return { ok: false, reason: 'This invoice has ' + hit.length + ' recorded payment' +
      (hit.length === 1 ? '' : 's') + ' against it.' };
  }
  return { ok: true, reason: '' };
}

// Rebuild the descriptive half of every snapshot line from the live items.
//
// Lines join to items by `itemId` when the snapshot carries one (stamped since
// 2026-09-11), and positionally against invoices.item_ids otherwise — the same
// convention itemUnpaidDepositShare already relies on, and the reason the
// length check below is a hard failure rather than a best-effort skip.
//
// Returns { ok, error, lines, edits } where `lines` is the complete new
// snapshot (unchanged lines included) and `edits` is a per-field changelog the
// caller shows the user before writing anything.
export function refreshInvoiceSnapshotSpecs(inv, allItems) {
  var out = { ok: false, error: '', lines: [], edits: [] };
  if (!inv) { out.error = 'No invoice.'; return out; }
  var lines = inv.lineItems || inv.line_items || [];
  var itemIds = inv.itemIds || inv.item_ids || [];
  if (!lines.length) { out.error = 'This invoice has no line-item snapshot.'; return out; }

  var byId = {};
  (allItems || []).forEach(function(it) { if (it && it.id) byId[String(it.id)] = it; });

  var stamped = lines.every(function(l) { return l && l.itemId; });
  if (!stamped && lines.length !== itemIds.length) {
    out.error = 'This invoice has ' + lines.length + ' line' + (lines.length === 1 ? '' : 's') +
      ' but ' + itemIds.length + ' item id' + (itemIds.length === 1 ? '' : 's') +
      ', and the lines carry no item ids — there is no safe way to match them up.';
    return out;
  }

  var edits = [];
  var nextLines = [];
  for (var i = 0; i < lines.length; i++) {
    var line = lines[i] || {};
    var id = line.itemId || itemIds[i];
    var it = byId[String(id)];
    if (!it) {
      out.error = 'Line ' + (i + 1) + ' points at item ' + id + ', which is no longer in the app.';
      return out;
    }
    var next = Object.assign({}, line, { itemId: String(it.id) });
    SNAPSHOT_SPEC_FIELDS.forEach(function(f) {
      var to = f.read(it, inv);
      var from = line[f.key];
      var changed = f.isList
        ? JSON.stringify(from || []) !== JSON.stringify(to || [])
        : String(from == null ? '' : from) !== String(to == null ? '' : to);
      if (changed) {
        edits.push({
          index: i,
          itemId: String(it.id),
          sku: it.itemNumber || '',
          name: it.productName || it.item || '',
          field: f.key,
          label: f.label,
          from: f.isList ? ((from || []).length + ' photo' + ((from || []).length === 1 ? '' : 's')) : String(from == null ? '' : from),
          to:   f.isList ? ((to   || []).length + ' photo' + ((to   || []).length === 1 ? '' : 's')) : String(to   == null ? '' : to),
        });
      }
      next[f.key] = to;
    });
    nextLines.push(next);
  }

  out.ok = true;
  out.lines = nextLines;
  out.edits = edits;
  return out;
}

// ── invoicePartyChrome — the addresses a regenerated invoice used to lose ──
//
// The invoices table has never had a bill_to_addr or a site_addr column, so
// `inv.billToAddr` and `inv.siteAddr` are always undefined on a row read back
// from the DB. Both the InvoicesTab preview and buildInvoiceRegenPayload read
// exactly those fields, which meant every regenerated invoice PDF silently
// dropped the client's billing address and the project's site address, and
// printed the raw project LABEL ("26-Hoback") where the generate path prints
// the display name ("Hoback Residence"). That reached the client: the billing
// package zip serves the stored archive first but falls back to a regen, and
// "View Original" sat next to a preview that disagreed with it.
//
// The generate path builds these from the live client + project records
// (buildDepositInvoicePayload / buildFinalInvoicePayload). This is that same
// assembly, factored out, so a regen resolves them the same way instead of
// reading columns that do not exist. Line assembly is deliberately identical
// — same order, same comma-joining, same '\n' separator — because a
// regenerated PDF has to match the issued one byte for byte to be worth
// anything.
//
// opts { clients, projects, clientName }. A missing record yields an empty
// string, exactly as the generate path does for an unresolvable client.
export function invoicePartyChrome(inv, opts) {
  var o = opts || {};
  var projectLabel = (inv && (inv.project || inv.project_label)) || '';
  var projObj = (o.projects || []).find(function(pr) {
    return pr && pr.label === projectLabel;
  }) || null;

  var clientId = (inv && (inv.clientId != null ? inv.clientId : inv.client_id)) || '';
  var client = clientId
    ? ((o.clients || []).find(function(c) { return c && String(c.id) === String(clientId); }) || null)
    : null;
  // Legacy rows carry no client_id. Fall back to the stored client name —
  // the same name the PDF prints — rather than dropping the address.
  if (!client) {
    var nm = String((inv && (inv.clientName || inv.client_name)) || o.clientName || '').trim();
    if (nm) {
      client = (o.clients || []).find(function(c) {
        return c && String(c.name || '').trim() === nm;
      }) || null;
    }
  }

  var billToAddrLines = [];
  if (client) {
    if (client.billingAddress) billToAddrLines.push(client.billingAddress);
    if (client.city || client.state || client.zip) {
      billToAddrLines.push([client.city, client.state, client.zip].filter(Boolean).join(', '));
    }
  }

  var siteAddrLines = [];
  if (projObj) {
    if (projObj.address)  siteAddrLines.push(projObj.address);
    if (projObj.address2) siteAddrLines.push(projObj.address2);
    if (projObj.city || projObj.state || projObj.zip) {
      siteAddrLines.push([projObj.city, projObj.state, projObj.zip].filter(Boolean).join(', '));
    }
  }

  return {
    projectDisplay: (projObj && projObj.displayName) || projectLabel,
    billToName: (inv && (inv.clientName || inv.client_name)) || (client && client.name) || '',
    billToAddr: billToAddrLines.join('\n'),
    siteAddr:   siteAddrLines.join('\n'),
  };
}

export function buildInvoiceRegenPayload(inv, opts) {
  var o = opts || {};
  var snapshot = inv.lineItems || inv.line_items || [];
  var productTotal = parseFloat(inv.productTotal) || 0;
  var subtotal     = parseFloat(inv.subtotal)     || 0;
  var feesTotal    = parseFloat(inv.feesTotal)    || Math.round((subtotal - productTotal) * 100) / 100;
  var feeFreight      = parseFloat(inv.feeFreightTot)      || 0;
  var feeCrating      = parseFloat(inv.feeCratingTot)      || 0;
  var feeInstallation = parseFloat(inv.feeInstallationTot) || 0;
  var feeTariff       = parseFloat(inv.feeTariffTot)       || 0;
  var feeSalesTax     = parseFloat(inv.feeSalesTaxTot)     || 0;
  var feeCustom       = Array.isArray(inv.feeCustom) ? inv.feeCustom : [];
  var hasBreakdown    = feeFreight + feeCrating + feeInstallation + feeTariff + feeSalesTax > 0
    || feeCustom.length > 0;
  var amountDue = parseFloat(inv.amountDue) || parseFloat(inv.amount) || 0;

  // Addresses + display name come from the live client/project records —
  // the invoices table has no column for them. See invoicePartyChrome.
  var chrome = invoicePartyChrome(inv, {
    clients: o.clients, projects: o.projects, clientName: o.clientName,
  });

  // Cross-references: the header PO line, and the bookkeeper block on the
  // last page (SKU / PO / PROPOSAL). `poRefs` is a TOP-LEVEL payload field
  // the renderer maps to the per-page header ("PO x", or "Multiple POs");
  // internalRefs is the block. Both were absent from this payload until
  // 2026-09-11, so a regenerated invoice lost its PO header and its whole
  // cross-reference block relative to the PDF that was issued.
  var crossRefs = invoiceCrossRefs(inv, {
    items:            o.items            || [],
    proposals:        o.proposals        || [],
    purchaseOrders:   o.purchaseOrders   || [],
    invoices:         o.invoices         || [],
    billingSummaries: o.billingSummaries || [],
  });

  // Fix E payment breakdown against the real invoice id, so allocations
  // recorded since issuance surface as the appliedCredit subtraction.
  var breakdown = computeInvoicePaymentBreakdown(
    inv.id, subtotal, amountDue, o.paymentAllocations || []);

  return {
    invoiceRef:      inv.invoiceRef || inv.invoice_ref || '',
    invoiceType:     inv.invoiceType || inv.invoice_type || 'deposit',
    projectLabel:    inv.project || inv.project_label || '',
    projectDisplay:  o.projectDisplay || chrome.projectDisplay,
    billToName:      chrome.billToName || o.clientName || '',
    billToAddr:      chrome.billToAddr,
    siteAddr:        chrome.siteAddr,
    poRefs:          crossRefs.poRefs,
    internalRefs: {
      skus:         crossRefs.skus,
      poRefs:       crossRefs.poRefs,
      proposalRefs: crossRefs.proposalRefs,
      // PROPOSAL line with totals (2026-09-03) — bookkeeper anchor.
      proposalLine: proposalRefLineWithTotals(crossRefs.proposalRefs, o.proposals || []),
    },
    lineItems:       snapshot,
    productTotal:    productTotal,
    feeFreightTot:      feeFreight,
    feeCratingTot:      feeCrating,
    feeInstallationTot: feeInstallation,
    feeTariffTot:       feeTariff,
    feeSalesTaxTot:     feeSalesTax,
    feeCustom: hasBreakdown
      ? feeCustom
      : (feesTotal > 0 ? [{ label: 'Fees & Sales Tax', amount: feesTotal }] : []),
    // Legacy lump signal — this invoice predates the 2026-05-12 per-fee
    // columns, so the single 'Fees & Sales Tax' figure (which INCLUDES
    // sales tax) is all that was ever stored. The renderer prints it
    // verbatim and never splits it.
    feesLumped: !hasBreakdown && feesTotal > 0,
    // Card fee — one source, invoiceCcFeeTot: the stored column when the
    // row has one, the legacy residual only for rows that predate it.
    feeCcProcessingTot: invoiceCcFeeTot(inv, {
      subtotal: subtotal, productTotal: productTotal, feesTotal: feesTotal,
    }),
    // Adjustments round-trip from the row so the regenerated Adjustments
    // section matches the issued one (2026-06-06 patch).
    adjustments:     Array.isArray(inv.adjustments) ? inv.adjustments : [],
    subtotal:        subtotal,
    depositTotal:    amountDue,
    balanceRemaining: 0,
    depositPaid:     breakdown.depositPaid,
    appliedCredit:   breakdown.appliedCredit,
    // Deposit-payment dates on the "Less deposit paid" label (2026-09-03).
    depositPaymentDates: finDepositPaymentHistory(
        inv, o.items || [], o.invoices || [],
        o.paymentAllocations || [], o.clientPayments || []
      ).map(function(r) { return regenDateLabel(r.date); })
       .filter(function(d, i, arr) { return d && arr.indexOf(d) === i; }),
    notes:           inv.notes || '',
    // Date priority: sent_at > createdAt > today, formatted M/D/YYYY —
    // the same string the generate path produces. This used to emit a raw
    // ISO slice, so a regenerated invoice printed "2026-05-20" where the
    // issued one printed "5/20/2026".
    date: formatInvoiceDateForPdf(
      inv.sentAt || inv.sent_at, inv.createdAt || inv.created_at),
  };
}

// Short-date label for the deposit-payment history line. Mirrors the
// module-local fmtDate in InvoicesTab.jsx (which formatted these strings
// before the two regen payload builders were unified), minus its '—'
// placeholder — an unparseable date is dropped, not printed.
function regenDateLabel(iso) {
  if (!iso) return '';
  try {
    var s = String(iso);
    var m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    var d = m ? new Date(parseInt(m[1]), parseInt(m[2]) - 1, parseInt(m[3])) : new Date(s);
    if (isNaN(d.getTime())) return s.slice(0, 10);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch (e) { return String(iso).slice(0, 10); }
}

// Pure regen payload for a Designer Hours invoice from its own row
// (categories_snapshot + adjusted_hours + summaries) — the row is
// self-sufficient by design (DesignerHoursSection's finalize writes every
// column). Effective numbers via computeEffectiveCategoryData, the same
// helper the PDF + QB push use, so a regen can't drift from the original.
//
// opts { client, project, projectName } (2026-09-04): the saved row
// stores the RAW phase snapshot; person-grouping is a per-client
// PRESENTATION, so every regen surface must pivot exactly like the live
// editor — without opts.client this rebuilds the phase layout (the
// 2026-09-04 Hoback package shipped a stale phase-format hours PDF
// because sendPackage inlined its own payload instead of using this).
export function buildHoursInvoiceRegenPayload(hoursRow, billTo, opts) {
  var o = opts || {};
  var h = hoursRow || {};
  var adjusted = h.adjusted_hours || h.adjustedHours || {};
  var eff = computeEffectiveCategoryData({
    categoriesSnapshot: h.categories_snapshot || h.categoriesSnapshot || [],
    adjustedHours:      adjusted,
  });
  var personMode = clientHoursGrouping(o.client) === 'person';
  var billing = personMode
    ? groupHoursByPersonAsCategories(eff.categories, adjusted)
    : eff;
  var members = (o.project && o.project.teamMembers) || [];
  var summaries = h.summaries || {};
  return {
    invoiceDate: h.invoice_date || h.invoiceDate || '',
    invoiceNumber: h.invoice_number || h.invoiceNumber || '',
    projectName: o.projectName
      || (o.project && (o.project.displayName || o.project.label))
      || h.project_label || h.projectLabel || '',
    grouping: personMode ? 'person' : 'phase',
    billTo: billTo || { name: h.client_name || h.clientName || '' },
    categories: billing.categories
      .filter(function(c) { return (c.effHours || 0) > 0; })
      .map(function(c) {
        var m = personMode
          ? members.find(function(x) { return x && x.togglName === c.name; })
          : null;
        return {
          name: (m && String(m.invoiceName || '').trim()) || c.name,
          hours: c.effHours,
          amount: c.effAmount,
          narrative: summaries[c.name] || '',
        };
      }),
    totalHours: billing.totalHours,
    totalAmount: billing.totalAmount,
    adjustments: [],
  };
}

// refs { clients, projects } (optional, 2026-09-04): lets the hours
// regen fallback resolve the client's hoursGrouping + team display
// names so a person-grouped client's PDF rebuilds in person format.
// Stored bytes remain first in every attempt list, so the fallback is
// rare — but when it fires it must match what was saved.
export function buildPackageZipPlan(pkg, documentSends, refs) {
  if (!pkg) return { zipName: 'package.zip', entries: [] };
  var zr = refs || {};
  var zipProject = (zr.projects || []).find(function(p) { return p && p.label === pkg.projectLabel; }) || null;
  var zipClient = zipProject
    ? ((zr.clients || []).find(function(c) { return c && c.id === zipProject.clientId; }) || null)
    : null;
  var periodTag = pkg.year + '-' + String(pkg.month).padStart(2, '0');
  var projTag = zipSafeFilename(pkg.projectLabel);
  var entries = [];

  // 1. Cover — stored bytes first, frozen-payload regen second, live
  //    rebuild from the row snapshot last (pre-Phase-5A sends).
  var coverSends = documentSendsFor(documentSends, 'billing_summary', pkg.summaryId);
  var latestCover = coverSends[0] || null;
  var coverAttempts = [];
  if (latestCover && latestCover.storagePath) {
    coverAttempts.push({
      via: 'stored',
      bucket: 'billing-summaries',
      path: String(latestCover.storagePath).replace(/^billing-summaries\//, ''),
    });
  }
  if (latestCover && latestCover.payload) {
    coverAttempts.push({
      via: 'regen',
      endpoint: '/api/generate-billing-summary',
      payload: latestCover.payload,
      parse: 'json',
    });
  }
  coverAttempts.push({ via: 'live', kind: 'cover' });
  entries.push({
    kind: 'cover',
    label: 'Billing summary cover — ' + pkg.monthYear,
    filename: '00_' + periodTag + '_' + projTag + '_billing_summary.pdf',
    attempts: coverAttempts,
  });

  // 2. Hours invoice — the finalize path uploads the PDF to invoice-pdfs/
  //    {invoice_number}.pdf, so stored almost always hits; the regen
  //    fallback rebuilds from the row's own snapshot columns.
  if (pkg.hoursRow && pkg.hoursRow.invoiceNumber) {
    entries.push({
      kind: 'hours',
      label: 'Design services — ' + pkg.monthYear,
      filename: '01_' + periodTag + '_' + projTag + '_design_hours.pdf',
      attempts: [
        { via: 'stored', bucket: 'invoice-pdfs', path: pkg.hoursRow.invoiceNumber + '.pdf' },
        {
          via: 'regen',
          endpoint: '/api/generate-hours-invoice',
          payload: buildHoursInvoiceRegenPayload(pkg.hoursRow.hoursInvoice, null,
            { client: zipClient, project: zipProject }),
          parse: 'json',
        },
      ],
    });
  }

  // 3. Invoices (10+) — deposit/final only, matching the original zip's
  //    step-4 filter (reimbursables ride the cover, never as PDFs).
  //    Includes ref-linked and frozen-only reconciled rows (they were
  //    real attachments in the delivered zip). Stored as-sent archive
  //    (invoices bucket, pdf_storage_path) first; regen from the
  //    line-items snapshot second. Frozen-only rows have no live invoice
  //    row — no storage path and nothing to regenerate from — so their
  //    attempts list is EMPTY and the zip's skip-note names them.
  var zipInvoices = (pkg.invoiceRows || []).filter(function(row) {
    var t = String(row.type || (row.invoice && (row.invoice.invoiceType || row.invoice.invoice_type)) || '');
    return t === 'deposit' || t === 'final';
  });
  zipInvoices.forEach(function(row, i) {
    var inv = row.invoice;
    var attempts = [];
    if (inv && (inv.pdfStoragePath || inv.pdf_storage_path)) {
      attempts.push({ via: 'stored', bucket: 'invoices', path: inv.pdfStoragePath || inv.pdf_storage_path });
    }
    var snapshot = inv ? (inv.lineItems || inv.line_items || []) : [];
    if (inv && snapshot.length > 0) {
      attempts.push({
        via: 'regen',
        endpoint: '/api/generate-deposit-invoice',
        // Full refs so the fallback render matches the PDF that was
        // issued: addresses + display name (invoicePartyChrome), the
        // header PO and the SKU/PO/PROPOSAL block (invoiceCrossRefs),
        // and the payment breakdown. This is the document that reaches
        // the client whenever the stored archive is missing.
        payload: buildInvoiceRegenPayload(inv, {
          clientName:         pkg.clientName,
          clients:            zr.clients            || [],
          projects:           zr.projects           || [],
          items:              zr.items              || [],
          proposals:          zr.proposals          || [],
          purchaseOrders:     zr.purchaseOrders     || [],
          invoices:           zr.invoices           || [],
          billingSummaries:   zr.billingSummaries   || [],
          paymentAllocations: zr.paymentAllocations || [],
          clientPayments:     zr.clientPayments     || [],
        }),
        parse: 'blob',
      });
    }
    entries.push({
      kind: 'invoice',
      id: inv ? inv.id : 'ref:' + row.ref,
      label: row.name,
      filename: String(10 + i).padStart(2, '0') + '_' + (row.type || 'invoice') + '_'
        + zipSafeFilename(row.ref || 'inv') + '.pdf',
      attempts: attempts,
    });
  });

  // 4. Proposals (20+) — as-sent archive from document_sends type
  //    'proposal' first (only exists for packages sent after 2026-07-02),
  //    frozen-payload regen second, live rebuild last.
  (pkg.taggedProposals || []).forEach(function(p, i) {
    var sends = documentSendsFor(documentSends, 'proposal', p.id);
    var latest = sends[0] || null;
    var attempts = [];
    if (latest && latest.storagePath) {
      attempts.push({
        via: 'stored',
        bucket: 'proposals',
        path: String(latest.storagePath).replace(/^proposals\//, ''),
      });
    }
    if (latest && latest.payload) {
      attempts.push({
        via: 'regen',
        endpoint: '/api/generate-proposal',
        payload: latest.payload,
        parse: 'blob',
      });
    }
    attempts.push({ via: 'live', kind: 'proposal', proposalId: p.id });
    entries.push({
      kind: 'proposal',
      id: p.id,
      label: p.name || p.proposal_ref || 'Proposal',
      filename: String(20 + i).padStart(2, '0') + '_proposal_'
        + zipSafeFilename(p.proposal_ref || p.id || 'p') + '.pdf',
      attempts: attempts,
    });
  });

  return {
    zipName: periodTag + '_' + projTag + '_package.zip',
    entries: entries,
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// SCHEDULE FIELD REGISTRY — Schedules rebuild Phase 1
// docs/SCHEDULES_REBUILD_SPEC.md §4.1–§4.2
//
// Single source of truth for every value a schedule column can render.
// The row-assembly logic is a faithful port of the inline code in
// SchedulesTab.jsx `buildRooms()` (mapItem + assembly collapse + the two
// WT-expanded variants) so Phase 2's buildSchedulePayloadPure and the
// Phase 3 grid read the SAME extraction the legacy jsPDF renderers use
// today. The legacy tab is NOT wired through these helpers — it stays
// frozen until Phase 2 renders side-by-side; any drift between this port
// and the legacy inline code is caught by the Phase 2 column-for-column
// diff and the seed-template guard tests.
//
// Vocabulary:
//   row bag  — flat map of every addressable field value for ONE schedule
//              row (a standalone item, a collapsed assembly, or a WT
//              component sub-row). Keys are the registry keys below.
//              Underscore-prefixed keys (_itemId, _roomName, …) are
//              grouping/override plumbing, not renderable fields.
//   column   — template jsonb: { id, label, width, align?, wrap?, kind?,
//              blocks: [{kind:'field', path, prefix?, suffix?} |
//                       {kind:'text', value}] }. kind 'image' renders the
//              hero image; kind 'custom' reads schedules.custom_values.
//
// Editability (Phase 4 consumes; predicate ships now per spec §2 #1):
// a field is grid-editable only when it has an itemField write target AND
// the item isn't Cancelled AND (the target isn't a material field OR the
// item is pre-lock). Material-ness derives from isMaterialField() on the
// itemField's root — the registry never duplicates the lock list.
//
// Audience safety (spec §2 #2): fields flagged internalOnly are hard-
// refused on client/gc-audience templates for EVERY user — the open
// template-editing decision is safe because of this gate, not a role gate.
// ═══════════════════════════════════════════════════════════════════════════

export var SCHEDULE_COMP_ORDER = { '': 0, 'Fabric': 1, 'Trim': 2, 'Workroom': 3, 'Hardware': 4, 'Roller Shades': 5 };

var SCHEDULE_DRAW_MAP = {
  'Left': 'Left Drawn', 'Right': 'Right Drawn', 'Center': 'Center Split',
  'Stationary': 'Stationary', 'Stack Left': 'Left Drawn',
  'Stack Right': 'Right Drawn', 'Center Split': 'Center Split',
};

export function scheduleHeroImage(it) {
  if (!it) return '';
  return it.imageUrl
    || (it.images && it.images.length > 0 ? (it.images[it.heroIndex || 0] || {}).url : '')
    || '';
}

export function scheduleTagShort(tag) {
  var t = String(tag || '');
  return t.indexOf('/') !== -1 ? t.split('/').pop().trim() : t;
}

// Internal-only price roll-up. Lines name their side per the money-label
// rule: "/ea" (net unit), "net" (vendor-side total), "client" (client-side
// total after markup).
export function scheduleClientPriceSummary(netPerUnit, qty, markupPct) {
  var np = parseFloat(String(netPerUnit || '').replace(/[$,]/g, '')) || 0;
  if (!np) return '';
  var q = parseFloat(qty || '1') || 1;
  var m = parseFloat(markupPct || 30) || 30;
  var fmt = function(n) { return '$' + Math.round(n).toLocaleString('en-US'); };
  return fmt(np) + '/ea\n' + fmt(np * q) + ' net\n' + fmt(np * q * (1 + m / 100)) + ' client';
}

// Where the item IS, install-schedule ready (tracker rebuild item 6,
// Cimon's spec: "show current status of item — where it is — or when is
// estimated to be delivered and where. Estimated ship dates should be in
// () parenthesis, but delivered items should have a delivery date.")
//   Installed / Complete → 'Installed Jul 30' (install date when known)
//   Delivered            → 'Delivered Jul 22'
//   At Receiver          → 'At receiver — SF Warehouse · Jul 10'
//                          (name from ship-to when vendors provided)
//   Shipped              → 'In transit (est. Aug 14)'
//   Ordered / PO Issued  → '(Est. Aug 14)' — estimate, so parenthesized
//   pre-order            → '' (blank, never guessed)
// ── Project archive (2026-07-30) ─────────────────────────────────────────────
// Archived projects KEEP their row in the projects array — every lookup
// by label (tax config, receiver, rooms, site name) still resolves for
// existing items/invoices/history. They only stop being offered where
// NEW work starts. keepLabel keeps the currently-selected project
// visible in a picker even if archived (an old record must never render
// a blank select).
export function activeProjects(projects, keepLabel) {
  return (projects || []).filter(function(pr) {
    if (!pr) return false;
    if (!pr.archived) return true;
    return !!(keepLabel && pr.label === keepLabel);
  });
}

// The job-site name for "Delivered to {site}" — the project's display
// name (trailing unit marker stripped, the billing-renderer rule) or the
// address first line ("Hoback Club"), else the label.
export function projectSiteName(project) {
  if (!project) return '';
  var dn = String(project.displayName || '').replace(/\s*#\S+\s*$/, '').trim();
  if (dn) return dn;
  var addr = String(project.address || '').split(/[\n,]/)[0].trim();
  if (addr) return addr;
  return String(project.label || '').trim();
}

export function trackingWhereabouts(it, vendors, project) {
  if (!it) return '';
  // Non-procured items lead with WHO furnishes them (Cimon 2026-07-30:
  // "By Others" in the install schedule's SHIP STATUS) — the firm never
  // ships these, so a bare est-ship blank reads as missing data. Tracked
  // location still appends: 'By Others — Delivered Jul 22'.
  if (isNonProcured(it.procurementClass)) {
    var npLabel = it.procurementClass === 'owner_furnished' ? 'Owner Furnished' : 'By Others';
    var tracked = trackingWhereaboutsCore(it, vendors, project);
    return tracked ? npLabel + ' — ' + tracked : npLabel;
  }
  return trackingWhereaboutsCore(it, vendors, project);
}

function trackingWhereaboutsCore(it, vendors, project) {
  var s = normalizeItemStatus(it.status, it.receivedDate);
  function pretty(iso) {
    var m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return '';
    try {
      var d = new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
      // Year always prints — receipts span years (2025 pieces sit at the
      // receiver alongside 2026 ones) and 'Jul 22' alone is ambiguous.
      return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch (e) { return ''; }
  }
  if (s === 'Installed' || s === 'Complete') {
    var inst = pretty(it.installDate);
    return inst ? 'Installed\n' + inst : 'Installed';
  }
  if (s === 'Delivered') {
    // Delivered = at the FINAL place, the job site — name it (Cimon
    // 2026-07-30: "Delivered to Hoback Club").
    var site = projectSiteName(project);
    var delBase = site ? 'Delivered to ' + site : 'Delivered';
    var del = pretty(it.deliveredDate);
    return del ? delBase + '\n' + del : delBase;
  }
  if (s === 'At Receiver') {
    // Reads "Received at {receiver}" (Cimon 2026-07-30 — the status is
    // still canonically 'At Receiver'; this is only the print string).
    var name = '';
    if (it.shipToVendorId && Array.isArray(vendors)) {
      var v = vendors.find(function(x) { return x && String(x.id) === String(it.shipToVendorId); });
      name = (v && v.name) || '';
    }
    var base = name ? 'Received at ' + name : 'Received';
    var rec = pretty(it.receivedDate);
    return rec ? base + '\n' + rec : base;
  }
  // Pre-arrival lines name WHERE the thing is going (Cimon 2026-07-30):
  // the item's ship-to receiver, else the project's default receiver,
  // else the job site itself (direct-to-site).
  function shipDestination() {
    var vid = (it.shipToVendorId != null && it.shipToVendorId !== '')
      ? it.shipToVendorId
      : (project && project.receiverId);
    if (vid != null && vid !== '' && Array.isArray(vendors)) {
      var dv = vendors.find(function(x) { return x && String(x.id) === String(vid); });
      if (dv && dv.name) return dv.name;
    }
    return projectSiteName(project);
  }
  // In transit: NEVER print a computed ETA (Cimon 2026-07-30 — the
  // estimate is orderPlacedDate + lead weeks, i.e. when the vendor
  // SHIPS; once shipped it's stale, and the lead-weeks fallback scrapes
  // any number out of the free-text Lead Time field). Show the REAL
  // thing we know instead: the actual ship date.
  // Tracking number (Cimon 2026-09-17): prints as its OWN line under the
  // status on pre-arrival items — the installer/receiver looks the
  // carrier up from it. Number only (no "Tracking" label, no carrier —
  // the SHIP STATUS cell is narrow; a long UPS number already wraps).
  // Gone once the goods land: At Receiver/Delivered/Installed never
  // print it.
  var trk = String(it.trackingNumber || '').trim();
  function withTracking(line) {
    if (!trk) return line;
    return line ? line + '\n' + trk : trk;
  }
  if (s === 'Shipped') {
    var dest = shipDestination();
    var shpBase = dest ? 'In transit to ' + dest : 'In transit';
    var shp = pretty(it.shippedDate);
    return withTracking(shp ? shpBase + '\nShipped ' + shp : shpBase);
  }
  var est = pretty(estimatedShipDate(it));
  if (s === 'Ordered' || s === 'PO Issued') {
    if (!est) return withTracking('');
    var estDest = shipDestination();
    return withTracking(estDest ? 'To ' + estDest + '\n(Est. ' + est + ')' : '(Est. ' + est + ')');
  }
  return '';
}

// ── Planned install (2026-07-30) ─────────────────────────────────────────────
// Installs are planned per item OR per named project INSTALL EVENT
// ("Furniture install", "Lighting install" — project.installEvents:
// [{ id, label, startDate, days }], edited on the Project page). An item
// assigned to an event DERIVES its dates live from the event, so moving
// the event on the Project page updates every assigned item at once.
// Manual fallback: items.install_planned_date + items.install_days.
// An install event's LAST day (startDate + days − 1), ISO. '' when undated.
export function installEventEnd(ev) {
  if (!ev || !ev.startDate) return '';
  var m = String(ev.startDate).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return '';
  var d = new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
  if (isNaN(d.getTime())) return '';
  var days = parseInt(ev.days, 10) || 1;
  d.setDate(d.getDate() + days - 1);
  function pad2(n) { return String(n).padStart(2, '0'); }
  return d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-' + pad2(d.getDate());
}

// Fully in the past (last day before today). Undated events are never past.
export function installEventIsPast(ev, todayIso) {
  if (!todayIso) return false;
  var end = installEventEnd(ev);
  return !!end && end < todayIso;
}

// Install events sorted soonest-first (Cimon 2026-07-30); events without
// a date yet (just added on the Project page) sort last, in entry order.
// With todayIso: PAST events sink below the upcoming block (each block
// stays date-ascending) — the list and dropdown lead with what's next.
export function sortInstallEvents(events, todayIso) {
  function byDate(a, b) {
    var ad = (a && a.startDate) || '';
    var bd = (b && b.startDate) || '';
    if (ad && bd) return ad < bd ? -1 : ad > bd ? 1 : 0;
    if (ad) return -1;
    if (bd) return 1;
    return 0;
  }
  var all = (events || []).slice();
  if (!todayIso) return all.sort(byDate);
  var upcoming = all.filter(function(e) { return !installEventIsPast(e, todayIso); }).sort(byDate);
  var past = all.filter(function(e) { return installEventIsPast(e, todayIso); }).sort(byDate);
  return upcoming.concat(past);
}

// Planned install has fully passed but the item was never marked
// Installed — the advisory overdue cue (color only, never auto-advance).
export function installPlanOverdue(item, project, todayIso) {
  if (!item || !todayIso) return false;
  var st = canonicalItemStatus(item.status);
  if (st === 'Installed' || st === 'Complete' || st === 'Cancelled') return false;
  var plan = resolveInstallPlan(item, project);
  if (!plan) return false;
  var end = installEventEnd({ startDate: plan.startDate, days: plan.days });
  return !!end && end < todayIso;
}

export function resolveInstallPlan(item, project) {
  if (!item) return null;
  var evId = String(item.installEventId || '').trim();
  if (evId && project && Array.isArray(project.installEvents)) {
    var ev = project.installEvents.find(function(e) { return e && String(e.id) === evId; });
    if (ev && ev.startDate) {
      return { startDate: ev.startDate, days: parseInt(ev.days, 10) || 1, label: ev.label || '' };
    }
  }
  if (item.installPlannedDate) {
    return { startDate: item.installPlannedDate, days: parseInt(item.installDays, 10) || 1, label: '' };
  }
  return null;
}

// 'Aug 12, 2026' (one day) / 'Aug 12 – 15, 2026' / 'Aug 30 – Sep 2, 2026'.
// Multi-day = startDate through startDate + (days - 1).
export function installRangeDisplay(startDate, days) {
  var m = String(startDate || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return '';
  var start = new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
  if (isNaN(start.getTime())) return '';
  days = parseInt(days, 10) > 0 ? parseInt(days, 10) : 1;
  function md(d) { return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); }
  if (days === 1) {
    return md(start) + ', ' + start.getFullYear();
  }
  var end = new Date(start.getTime());
  end.setDate(end.getDate() + days - 1);
  var startStr = md(start);
  var endStr = end.getMonth() === start.getMonth() && end.getFullYear() === start.getFullYear()
    ? String(end.getDate())
    : md(end);
  var year = end.getFullYear();
  return startStr + ' \u2013 ' + endStr + ', ' + year;
}

export function plannedInstallDisplay(item, project) {
  var plan = resolveInstallPlan(item, project);
  if (!plan) return '';
  return installRangeDisplay(plan.startDate, plan.days);
}

// \u2500\u2500 Install-event filter (2026-08-24) \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
// The install schedule can be scoped to one or more named install events \u2014
// a per-run TRANSIENT choice (like the receiver filter): nothing persists
// on the schedules row; the published PDF's header + frozen payload are
// the record of what each issue covered.
export var INSTALL_EVENT_UNSCHEDULED = 'UNSCHEDULED';

// The event an item is assigned to, '' when unassigned or when the id
// points at an event that no longer exists (a stale assignment reads as
// unscheduled \u2014 the filter must never silently drop it into no group).
export function itemInstallEventId(item, installEvents) {
  var evId = String((item && item.installEventId) || '').trim();
  if (!evId) return '';
  var ev = (installEvents || []).find(function(e) { return e && String(e.id) === evId; });
  return ev ? evId : '';
}

// Header scope for an event-filtered run \u2014 rides the audienceLabel line
// (2026-07-30 document-identity rule: a partial run must never read as
// the full schedule). One event prints label + dates; several print
// their labels joined; Unscheduled names itself.
export function installEventScopeLabel(eventFilter, installEvents) {
  var filter = (eventFilter || []).map(String);
  if (filter.length === 0) return '';
  var parts = [];
  sortInstallEvents(installEvents).forEach(function(ev) {
    if (!ev || filter.indexOf(String(ev.id)) < 0) return;
    var label = ev.label || 'Install event';
    if (filter.length === 1 && ev.startDate) {
      label += ' \u00b7 ' + installRangeDisplay(ev.startDate, ev.days);
    }
    parts.push(label);
  });
  if (filter.indexOf(INSTALL_EVENT_UNSCHEDULED) >= 0) parts.push('Unscheduled');
  return parts.join(' + ');
}

// ── Row bag: standalone item ─────────────────────────────────────────────────
// Port of SchedulesTab buildRooms() mapItem(). Reads WT child specs off the
// legacy parentId/children model when present.
export function buildScheduleItemRowBag(it, vendors) {
  it = it || {};
  var specs = it.specs || {};
  var children = it.children || [];
  var wkrm = children.find(function(c) { return c && c.childType === 'Workroom'; });
  var hw = children.find(function(c) { return c && c.childType === 'Hardware'; });
  var shade = children.find(function(c) { return c && c.childType === 'Roller Shades'; });
  var ws = (wkrm && wkrm.specs) || {};
  var hs = (hw && hw.specs) || {};
  var ss = (shade && shade.specs) || {};
  var hwParts = [(hw && hw.item) || '', hs.hwType || '', hs.finish || '', hs.diameter ? hs.diameter + ' dia' : ''].filter(Boolean);
  var isDrapery = !!wkrm;
  var motorized = isDrapery ? ws.motorized : ss.motor;
  var motorBrand = isDrapery ? (ws.motorBrand || hs.motorBrand || '') : (ss.motorBrand || '');
  var wtDescParts = isDrapery
    ? [((ws.pleat ? ws.pleat + ' ' : '') + 'Drapery'), ws.lining && ws.lining !== 'Unlined' ? ws.lining + ' Lined' : ''].filter(Boolean)
    : (shade ? ['Roller Shade', ss.opacity || ''].filter(Boolean) : []);
  var tag = it.tag || '';
  var wtWidth = ws.finishedWidth || ss.finishedWidth || '';
  var wtLength = ws.finishedLength || ss.finishedLength || '';
  var wtPanels = ws.numPanels || '';
  var wtDraw = ws.draw || '';
  return {
    _itemId: it.id || '',
    _schedule: it.schedule || '',
    _roomName: it.roomName || 'No Room',
    _roomNumber: it.roomNumber || '',
    location: it.roomName || 'No Room',
    item: it.item || '',
    product_name: it.productName || it.item || '',
    tag: tag,
    tag_short: scheduleTagShort(tag),
    sidemark: it.sidemark || '',
    qty: it.qty || '1',
    qty_type: it.qtyType || '',
    unit: it.qtyType || '',
    vendor: it.vendor || '',
    manufacturer: it.manufacturer || specs.manufacturer || '',
    model: it.model || specs.model || '',
    size: it.dimensions || specs.dimensions || specs.size || '',
    finish: it.finish || specs.finish || '',
    technology: specs.technology || '',
    bulb: specs.bulb || '',
    base: specs.base || '',
    wattage: specs.wattage || '',
    lumens: specs.lumens || '',
    kelvins: specs.kelvins || '',
    cri: specs.cri || '',
    voltage: specs.voltage || '',
    circuitry: specs.circuitry || '',
    notes: it.notes || '',
    install_schedule_notes: it.installScheduleNotes || '',
    status_notes: it.statusNotes || '',
    internal_notes: it.internalNotes || '',
    status: it.status || '',
    net_price: it.netPrice || '',
    order_status: formatOrderStatus(it),
    est_ship: trackingWhereabouts(it, vendors),
    whereabouts: trackingWhereabouts(it, vendors),
    price_summary: scheduleClientPriceSummary(it.netPrice, it.qty, it.markupPct),
    lead_time: it.leadTime || '',
    source_url: it.sourceUrl || '',
    image_url: scheduleHeroImage(it),
    wt_mount: ws.mountType || '',
    wt_width: wtWidth,
    wt_length: wtLength,
    wt_size: [wtWidth, wtLength].filter(Boolean).join(' × '),
    wt_panels: wtPanels,
    wt_draw: wtDraw,
    wt_panels_draw: [wtPanels, wtDraw].filter(Boolean).join(' · '),
    wt_style: ws.pleat || ss.opacity || '',
    wt_lining: ws.lining || '',
    wt_hardware: hwParts.join(' · '),
    wt_motor: motorized === 'Yes' ? (motorBrand ? 'Motor: ' + motorBrand : 'Motorized') : '',
    wt_desc: wtDescParts.join(', ') || it.item || '',
    wt_panels_block: '',
    source: '',
    mw_material: specs.material || '',
    mw_species: specs.speciesGrade || '',
    mw_profile: specs.profileStyle || '',
    mw_paint: specs.paintStainColor || '',
    mw_linear: specs.linearFootage || '',
    mw_notes: [specs.installNotes, specs.shopDrawingRef, it.notes].filter(Boolean).join(' · '),
    gpm: it.gpm || specs.gpm || '',
    spec_notes: it.specNotes || '',
    voltage_fuel: specs.voltageFuel || '',
    btu: specs.btu || '',
    install_notes: specs.installNotes || '',
    ts_material: specs.material || '',
    ts_tile_size: specs.size || '',
    ts_thickness: specs.thickness || '',
    ts_coverage_notes: specs.coverageNotes || '',
    ts_grout_spec: specs.groutSpec || '',
    wr_numbers: (it.wrEntries || []).filter(function(e) { return e && e.wrNumber; })
      .map(function(e) { return e.wrNumber; }).join(' / '),
  };
}

// ── Row bag: collapsed assembly (row_model 'assembly') ──────────────────────
// Port of the standard client/workroom/gc assembly-collapse branch. One row
// per assembly, spec data from the primary peer; WT assemblies identify by
// the face fabric via pickWindowTreatmentScheduleDisplay, and the workroom
// audience swaps in the 3-line composite blocks. Cell overrides on a
// collapsed row key off the PRIMARY peer's item id (_itemId).
export function buildScheduleAssemblyRowBag(asmItems, assembly, opts) {
  opts = opts || {};
  var items = asmItems || [];
  var asm = assembly;
  if (!asm || items.length === 0) return null;
  // Lead peer = the FINISHED piece (ships to a receiver), so status /
  // whereabouts / vendor read the chair, not the fabric or trim that
  // went to the workroom (2026-11-02). Pre-fix: the peer with no
  // childType, else whichever had the longest vendor name.
  var primary = finishedPeerForAssembly(items, { vendors: opts.vendors, projectReceiverId: opts.projectReceiverId }) || items[0];
  var pspecs = primary.specs || {};
  var asmWkrm = items.find(function(it) { return it.childType === 'Workroom'; });
  // ALL hardware peers, not just the first (2026-07-13: WT assemblies
  // carry a rod/track AND a motor as separate Hardware peers — .find()
  // showed whichever happened to be first, hiding the Lutron track).
  var asmHwAll = items.filter(function(it) { return it.childType === 'Hardware'; });
  var asmHw = asmHwAll[0];
  var asmShade = items.find(function(it) { return it.childType === 'Roller Shades'; });
  var aws = (asmWkrm && asmWkrm.specs) || {};
  var ahs = (asmHw && asmHw.specs) || {};
  var ass = (asmShade && asmShade.specs) || {};
  var asmHwParts = asmHwAll.map(function(h) {
    var hsp = h.specs || {};
    return [h.item || h.productName || '', hsp.hwType || '', hsp.finish || '',
      hsp.diameter ? hsp.diameter + ' dia' : ''].filter(Boolean).join(' \u00b7 ');
  }).filter(Boolean);
  var asmIsDrapery = !!asmWkrm;
  var asmMotorized = asmIsDrapery ? aws.motorized : ass.motor;
  var asmMotorBrand = asmIsDrapery ? (aws.motorBrand || ahs.motorBrand || '') : (ass.motorBrand || '');
  var asmDescParts = asmIsDrapery
    ? [((aws.pleat ? aws.pleat + ' ' : '') + 'Drapery'), aws.lining && aws.lining !== 'Unlined' ? aws.lining + ' Lined' : ''].filter(Boolean)
    : (asmShade ? ['Roller Shade', ass.opacity || ''].filter(Boolean) : []);

  var asmParts = [];
  var fabricItem = items.find(function(it) { return !it.childType; }) || items[0];
  if (fabricItem) asmParts.push({ type: 'Fabric', name: fabricItem.productName || fabricItem.item || '', vendor: fabricItem.vendor || '', net_price: fabricItem.netPrice || '' });
  if (asmWkrm) asmParts.push({ type: 'Workroom', name: asmWkrm.productName || asmWkrm.item || '', vendor: asmWkrm.vendor || '', net_price: asmWkrm.netPrice || '' });
  if (asmHw) asmParts.push({ type: 'Hardware', name: asmHw.productName || asmHw.item || '', vendor: asmHw.vendor || '', net_price: asmHw.netPrice || '' });
  if (asmShade) asmParts.push({ type: 'Roller Shade', name: asmShade.productName || asmShade.item || '', vendor: asmShade.vendor || '', net_price: asmShade.netPrice || '' });

  var wtDisplay = pickWindowTreatmentScheduleDisplay(items, opts.scheduleType || '', opts.audience || 'client');
  var wtOver = wtDisplay && wtDisplay.workroomOverrides;

  // orderPlacedDate + lead time can live on DIFFERENT peers (the cascade
  // stamps whichever peer landed on a vendor PO). Scan every peer for each
  // independently; status stays assembly-level via the primary.
  var candidates = [primary].concat(items.filter(function(it) { return it !== primary; }));
  var dateItem = candidates.find(function(it) { return it && it.orderPlacedDate; });
  var leadItem = candidates.find(function(it) { return it && (it.leadWeeks || (it.leadTime && String(it.leadTime).match(/\d/))); });
  var orderStatus = formatOrderStatus({
    status: primary.status,
    orderPlacedDate: dateItem ? dateItem.orderPlacedDate : '',
    leadWeeks: leadItem ? leadItem.leadWeeks : '',
    leadTime: leadItem ? leadItem.leadTime : '',
  });

  var tag = asm.tag || '';
  var wtWidth = aws.finishedWidth || ass.finishedWidth || '';
  var wtLength = aws.finishedLength || ass.finishedLength || '';
  var wtPanels = aws.numPanels || '';
  var wtDraw = aws.draw || '';
  return {
    _itemId: primary.id || '',
    _assemblyId: asm.id || '',
    _roomName: asm.room_name || primary.roomName || 'No Room',
    _roomNumber: asm.room_number || primary.roomNumber || '',
    location: asm.room_name || primary.roomName || 'No Room',
    tag: tag,
    tag_short: scheduleTagShort(tag),
    item: wtDisplay ? wtDisplay.productName : (asm.name || ''),
    product_name: wtDisplay ? wtDisplay.productName : (primary.productName || asm.name || ''),
    sidemark: asm.sidemark || '',
    qty: (wtOver && wtOver.quantityDisplay) ? wtOver.quantityDisplay : (primary.qty || '1'),
    qty_type: primary.qtyType || '',
    unit: primary.qtyType || '',
    vendor: primary.vendor || '',
    source: wtOver ? wtOver.source : '',
    manufacturer: primary.manufacturer || pspecs.manufacturer || '',
    model: primary.model || pspecs.model || '',
    size: primary.dimensions || pspecs.dimensions || pspecs.size || '',
    finish: primary.finish || pspecs.finish || '',
    technology: pspecs.technology || '',
    bulb: pspecs.bulb || '',
    base: pspecs.base || '',
    wattage: pspecs.wattage || '',
    lumens: pspecs.lumens || '',
    kelvins: pspecs.kelvins || '',
    cri: pspecs.cri || '',
    voltage: pspecs.voltage || '',
    circuitry: pspecs.circuitry || '',
    gpm: primary.gpm || pspecs.gpm || '',
    spec_notes: primary.specNotes || '',
    voltage_fuel: pspecs.voltageFuel || '',
    btu: pspecs.btu || '',
    install_notes: pspecs.installNotes || '',
    mw_material: pspecs.material || '',
    mw_species: pspecs.speciesGrade || '',
    mw_profile: pspecs.profileStyle || '',
    mw_paint: pspecs.paintStainColor || '',
    mw_linear: pspecs.linearFootage || '',
    mw_notes: [pspecs.installNotes, pspecs.shopDrawingRef, primary.notes].filter(Boolean).join(' · '),
    ts_material: pspecs.material || '',
    ts_tile_size: pspecs.size || '',
    ts_thickness: pspecs.thickness || '',
    ts_coverage_notes: pspecs.coverageNotes || '',
    ts_grout_spec: pspecs.groutSpec || '',
    notes: wtOver ? wtOver.notes : (asm.notes || primary.notes || ''),
    install_schedule_notes: primary.installScheduleNotes || '',
    status_notes: primary.statusNotes || '',
    internal_notes: primary.internalNotes || '',
    status: primary.status || '',
    net_price: primary.netPrice || '',
    price_summary: scheduleClientPriceSummary(primary.netPrice, primary.qty, primary.markupPct),
    lead_time: primary.leadTime || '',
    source_url: primary.sourceUrl || '',
    image_url: wtDisplay ? (wtDisplay.imageUrl || '') : scheduleHeroImage(primary),
    order_status: orderStatus,
    est_ship: trackingWhereabouts(primary, (opts || {}).vendors),
    whereabouts: trackingWhereabouts(primary, (opts || {}).vendors),
    wt_desc: asmDescParts.join(', ') || asm.name || '',
    wt_mount: aws.mountType || '',
    wt_width: wtWidth,
    wt_length: wtLength,
    wt_size: [wtWidth, wtLength].filter(Boolean).join(' × '),
    wt_panels: wtPanels,
    wt_draw: wtDraw,
    wt_panels_draw: [wtPanels, wtDraw].filter(Boolean).join(' · '),
    wt_style: aws.pleat || ass.opacity || '',
    wt_lining: aws.lining || '',
    wt_hardware: wtOver ? wtOver.hardware : asmHwParts.join('; '),
    wt_panels_block: wtOver ? wtOver.panels : '',
    wt_motor: asmMotorized === 'Yes' ? (asmMotorBrand ? 'Motor: ' + asmMotorBrand : 'Motorized') : '',
    wr_numbers: (primary.wrEntries || []).filter(function(e) { return e && e.wrNumber; })
      .map(function(e) { return e.wrNumber; }).join(' / '),
    parts: asmParts,
  };
}

// Shared per-component spec/size derivation for the two WT-expanded row
// models (assemblyId peers + legacy parentId children).
function wtComponentSpecAndSize(compItem) {
  var ct = compItem.childType || '';
  var s = compItem.specs || {};
  var specsStr = '';
  if (ct === 'Workroom') {
    var dimStr = [s.finishedWidth, s.finishedLength].filter(Boolean).join('×');
    var drawLabel = SCHEDULE_DRAW_MAP[s.draw] || s.draw || '';
    var wl1 = [s.pleat, s.lining].filter(Boolean).join(' · ');
    var wl2 = [s.mountType, dimStr, s.numPanels ? s.numPanels + ' pnls' : '', drawLabel].filter(Boolean).join(' · ');
    specsStr = [wl1, wl2].filter(Boolean).join('\n');
  } else if (ct === 'Hardware') {
    var hl1 = [s.hwType, s.finish].filter(Boolean).join(' · ');
    var hl2 = [s.diameter ? s.diameter + '" dia' : '', s.motorBrand ? 'Motor: ' + s.motorBrand : ''].filter(Boolean).join(' · ');
    specsStr = [hl1, hl2].filter(Boolean).join('\n');
  } else if (ct === 'Roller Shades') {
    specsStr = [s.opacity, s.motor === 'Yes' ? (s.motorBrand || 'Motorized') : ''].filter(Boolean).join(' · ');
  } else {
    var fl1 = [compItem.model || s.model, s.color || s.colorway].filter(Boolean).join(' · ');
    var fl2 = [s.content, s.fabricWidth ? s.fabricWidth + '" wide' : ''].filter(Boolean).join(' · ');
    specsStr = [fl1, fl2].filter(Boolean).join('\n');
  }
  var sizeDim = '';
  if (ct === 'Workroom' || ct === 'Roller Shades') {
    sizeDim = [s.finishedWidth, s.finishedLength].filter(Boolean).join('×');
  } else if (ct === 'Hardware') {
    sizeDim = compItem.dimensions || s.diameter || '';
  } else {
    sizeDim = s.fabricWidth || compItem.dimensions || '';
  }
  return { specsStr: specsStr, sizeDim: sizeDim };
}

function formatScheduleNetPrice(netPrice) {
  var np = parseFloat(String(netPrice || '').replace(/[$,]/g, '')) || 0;
  return np > 0 ? '$' + np.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
}

// ── Row bags: WT expanded, assemblyId model (row_model 'wt_expanded') ────────
// Port of the WT-internal expansion: one sub-row per assembly peer, sorted
// by component order, first row flagged for the assembly header band.
export function buildWtExpandedComponentRowBags(asmItems, assembly) {
  var items = asmItems || [];
  var asm = assembly;
  if (!asm || items.length === 0) return [];
  var sorted = items.slice().sort(function(a, b) {
    return (SCHEDULE_COMP_ORDER[a.childType || ''] || 0) - (SCHEDULE_COMP_ORDER[b.childType || ''] || 0);
  });
  return sorted.map(function(it, idx) {
    var ct = it.childType || '';
    var derived = wtComponentSpecAndSize(it);
    return {
      _itemId: it.id || '',
      _assemblyId: asm.id || '',
      _roomName: asm.room_name || it.roomName || 'No Room',
      _roomNumber: asm.room_number || it.roomNumber || '',
      _assemblyName: asm.name || '',
      _isFirstInAsm: idx === 0,
      _compOrder: SCHEDULE_COMP_ORDER[ct] || 0,
      location: asm.room_name || it.roomName || 'No Room',
      comp_type: ct || 'Fabric',
      image_url: scheduleHeroImage(it),
      item: it.productName || it.item || '',
      size_dim: derived.sizeDim,
      vendor: it.vendor || '',
      specs: derived.specsStr,
      qty: it.qty || '',
      qty_type: it.qtyType || '',
      unit: it.qtyType || '',
      net_price: formatScheduleNetPrice(it.netPrice),
      price_summary: scheduleClientPriceSummary(it.netPrice, it.qty, it.markupPct),
      tag: asm.tag || it.tag || '',
      tag_short: scheduleTagShort(asm.tag || it.tag || ''),
    };
  });
}

// ── Row bags: WT expanded, legacy parentId/children model ────────────────────
// Port of the standalone-item-with-children expansion: parent renders as
// the Fabric row, each child as a component sub-row. Child price summaries
// use the PARENT's markupPct — legacy behavior, preserved deliberately.
export function buildWtExpandedChildRowBags(it) {
  it = it || {};
  var rows = [];
  var specs = it.specs || {};
  var asmName = it.productName || it.item || '';
  var fabricSpecs = [it.model, specs.colorway, specs.content].filter(Boolean).join(' · ');
  rows.push({
    _itemId: it.id || '',
    _roomName: it.roomName || 'No Room',
    _roomNumber: it.roomNumber || '',
    _assemblyName: asmName,
    _isFirstInAsm: true,
    _compOrder: 1,
    location: it.roomName || 'No Room',
    comp_type: 'Fabric',
    image_url: scheduleHeroImage(it),
    item: it.productName || it.item || '',
    size_dim: specs.fabricWidth || it.dimensions || '',
    vendor: it.vendor || '',
    specs: fabricSpecs,
    qty: it.qty || '',
    qty_type: it.qtyType || '',
    unit: it.qtyType || '',
    net_price: formatScheduleNetPrice(it.netPrice),
    price_summary: scheduleClientPriceSummary(it.netPrice, it.qty, it.markupPct),
    tag: it.tag || '',
    tag_short: scheduleTagShort(it.tag || ''),
  });
  (it.children || []).slice().sort(function(a, b) {
    return (SCHEDULE_COMP_ORDER[a.childType || ''] || 99) - (SCHEDULE_COMP_ORDER[b.childType || ''] || 99);
  }).forEach(function(child) {
    var cct = child.childType || '';
    var derived = wtComponentSpecAndSize(child);
    rows.push({
      _itemId: child.id || '',
      _roomName: it.roomName || 'No Room',
      _roomNumber: it.roomNumber || '',
      _assemblyName: asmName,
      _isFirstInAsm: false,
      _compOrder: SCHEDULE_COMP_ORDER[cct] || 99,
      location: it.roomName || 'No Room',
      comp_type: cct,
      image_url: '',
      item: child.productName || child.item || '',
      size_dim: derived.sizeDim,
      vendor: child.vendor || '',
      specs: derived.specsStr,
      qty: child.qty || '',
      qty_type: child.qtyType || '',
      unit: child.qtyType || '',
      net_price: formatScheduleNetPrice(child.netPrice),
      price_summary: scheduleClientPriceSummary(child.netPrice, child.qty, it.markupPct),
      tag: it.tag || '',
      tag_short: scheduleTagShort(it.tag || ''),
    });
  });
  return rows;
}

// ── The registry ─────────────────────────────────────────────────────────────
// itemField      — write target for two-way editing (dot path into the item;
//                  'specs.x' targets the specs jsonb). Absent = derived /
//                  getter-only; never editable from a schedule.
// internalOnly   — hard-refused on client/gc-audience templates.
// kind           — 'image' marks the special image column source.
// Editability of statuses is deliberately absent: every committed-zone
// status is owned by a cascade (CLAUDE.md "Status System"); schedules
// display status, never write it.
export var SCHEDULE_FIELDS = {
  image:          { label: '', kind: 'image' },
  product_name:   { label: 'ITEM', itemField: 'productName' },
  item:           { label: 'ITEM', itemField: 'item' },
  location:       { label: 'LOCATION' },
  tag:            { label: 'TAG', itemField: 'tag' },
  tag_short:      { label: 'TAG' },
  sidemark:       { label: 'SIDEMARK' },
  qty:            { label: 'QTY', itemField: 'qty' },
  qty_type:       { label: 'UNIT', itemField: 'qtyType' },
  unit:           { label: 'UNIT', itemField: 'qtyType' },
  vendor:         { label: 'VENDOR', itemField: 'vendor' },
  manufacturer:   { label: 'MANUFACTURER', itemField: 'manufacturer' },
  model:          { label: 'MODEL', itemField: 'model' },
  size:           { label: 'SIZE', itemField: 'dimensions' },
  size_dim:       { label: 'SIZE/DIM' },
  finish:         { label: 'FINISH', itemField: 'finish' },
  // notes = the EXTERNAL-document notes (Cimon 2026-07-28: "a Notes
  // section for external documents like the install schedule") — it
  // prints on any audience. status_notes / internal_notes are the
  // internal rails and are hard-refused outside internal templates.
  notes:          { label: 'NOTES', itemField: 'notes' },
  spec_notes:     { label: 'SPEC NOTES', itemField: 'specNotes' },
  // Install Schedule Notes (2026-07-29) — its own field; the install
  // template's NOTES column reads THIS, never the Details-tab notes.
  install_schedule_notes: { label: 'NOTES', itemField: 'installScheduleNotes' },
  status_notes:   { label: 'STATUS NOTES', itemField: 'statusNotes', internalOnly: true, internalStrict: true },
  internal_notes: { label: 'INTERNAL NOTES', itemField: 'internalNotes', internalOnly: true, internalStrict: true },
  status:         { label: 'STATUS' },
  order_status:   { label: 'ORDER STATUS' },
  // est_ship carries trackingWhereabouts since 2026-07-28: '(Est. Aug 14)'
  // while en route, 'Delivered Jul 22' once landed — Cimon's install-
  // schedule spec. Rename the printed column label per template in the
  // grid editor if 'EST. SHIP' reads stale; whereabouts is the same
  // value under a fresher default label for new templates.
  est_ship:       { label: 'EST. SHIP' },
  whereabouts:    { label: 'STATUS / LOCATION' },
  // Stamped with project context in scheduleRowBagsForTemplate (like
  // client_price_final) — event-assigned items derive from the project's
  // installEvents live; manual items from install_planned_date + days.
  install_planned: { label: 'PLANNED INSTALL' },
  lead_time:      { label: 'LEAD TIME', itemField: 'leadTime' },
  source_url:     { label: 'SOURCE URL', itemField: 'sourceUrl' },
  wr_numbers:     { label: 'WR #' },
  comp_type:      { label: 'TYPE' },
  specs:          { label: 'SPECS' },
  net_price:      { label: 'NET COST', itemField: 'netPrice', internalOnly: true },
  price_summary:  { label: 'PRICE', internalOnly: true },
  // Insurance audience (2026-07-13): the client's FINAL price including
  // tax — itemLifecycleOwed per row, stamped by stampScheduleClientPrices
  // (row bags alone lack project context for tax). insuranceOnly: the
  // number is fine for the insurer/owner's rep but has no place on
  // client/gc layouts; net cost stays blocked from insurance like any
  // outside audience.
  client_price_final: { label: 'CLIENT PRICE', insuranceOnly: true },
  technology:     { label: 'TECHNOLOGY', itemField: 'specs.technology' },
  bulb:           { label: 'BULB', itemField: 'specs.bulb' },
  base:           { label: 'BASE', itemField: 'specs.base' },
  wattage:        { label: 'WATTAGE', itemField: 'specs.wattage' },
  lumens:         { label: 'LUMENS', itemField: 'specs.lumens' },
  kelvins:        { label: 'KELVINS', itemField: 'specs.kelvins' },
  cri:            { label: 'CRI', itemField: 'specs.cri' },
  voltage:        { label: 'VOLTAGE', itemField: 'specs.voltage' },
  circuitry:      { label: 'CIRCUITRY', itemField: 'specs.circuitry' },
  gpm:            { label: 'GPM', itemField: 'specs.gpm' },
  voltage_fuel:   { label: 'VOLTAGE/FUEL', itemField: 'specs.voltageFuel' },
  btu:            { label: 'BTU', itemField: 'specs.btu' },
  install_notes:  { label: 'INSTALL NOTES', itemField: 'specs.installNotes' },
  mw_material:    { label: 'MATERIAL', itemField: 'specs.material' },
  mw_species:     { label: 'SPECIES/GRADE', itemField: 'specs.speciesGrade' },
  mw_profile:     { label: 'PROFILE/STYLE', itemField: 'specs.profileStyle' },
  mw_paint:       { label: 'PAINT/STAIN', itemField: 'specs.paintStainColor' },
  mw_linear:      { label: 'LIN. FT.', itemField: 'specs.linearFootage' },
  mw_notes:       { label: 'NOTES' },
  ts_material:    { label: 'MATERIAL', itemField: 'specs.material' },
  ts_tile_size:   { label: 'TILE SIZE', itemField: 'specs.size' },
  ts_thickness:   { label: 'THICKNESS', itemField: 'specs.thickness' },
  ts_coverage_notes: { label: 'COVERAGE/QTY', itemField: 'specs.coverageNotes' },
  ts_grout_spec:  { label: 'GROUT SPEC', itemField: 'specs.groutSpec' },
  // Window-treatment fields read across assembly peers; the write target is
  // ambiguous (which peer?), so they're getter-only in v1 — edit on the
  // item page. See spec §4.1.
  wt_desc:        { label: 'TYPE' },
  wt_mount:       { label: 'MOUNT' },
  wt_width:       { label: 'WIDTH' },
  wt_length:      { label: 'LENGTH' },
  wt_size:        { label: 'FINISHED SIZE' },
  wt_panels:      { label: 'PANELS' },
  wt_draw:        { label: 'DRAW' },
  wt_panels_draw: { label: 'PANELS/DRAW' },
  wt_style:       { label: 'STYLE' },
  wt_lining:      { label: 'LINING' },
  wt_hardware:    { label: 'HARDWARE' },
  wt_motor:       { label: 'MOTOR' },
  wt_panels_block:{ label: 'CONSTRUCTION' },
  source:         { label: 'SOURCE' },
};

// Grid editability, v1 (spec §2 #1): needs a write target; Cancelled items
// are read-only; material fields lock at Client Approved. Material-ness
// derives from isMaterialField() on the itemField's root so the lock list
// is never duplicated ('specs.x' → root 'specs' → material).
export function canEditScheduleField(key, item) {
  var f = SCHEDULE_FIELDS[key];
  if (!f || !f.itemField || !item) return false;
  if (isCancelled(item)) return false;
  var root = String(f.itemField).split('.')[0];
  if (isMaterialField(root) && isFinanciallyCommitted(item.status)
      && !isNonProcured(item.procurementClass)) return false;
  return true;
}

// ── Phase 4: cell editing helpers ────────────────────────────────────────────

// Build the updateItem patch for a grid cell edit. itemField is the
// registry write target ('notes' or 'specs.manufacturer'); dotted paths
// merge into the jsonb sub-object so an edit never clobbers sibling keys.
export function buildScheduleItemPatch(item, itemField, value) {
  var path = String(itemField || '');
  if (!path) return null;
  var dot = path.indexOf('.');
  if (dot < 0) {
    var patch = {};
    patch[path] = value;
    return patch;
  }
  var root = path.slice(0, dot);
  var sub = path.slice(dot + 1);
  var merged = Object.assign({}, (item && item[root]) || {});
  merged[sub] = value;
  var out = {};
  out[root] = merged;
  return out;
}

// Publish-time drift report (spec decision #4): every cell override whose
// value differs from what the item currently resolves to. Returns
// [{itemId, field, scheduleValue, itemValue}]. Overrides equal to the item
// value are not drift (harmless residue).
export function scheduleOverrideDrift(scheduleDoc, bags) {
  var out = [];
  var overrides = (scheduleDoc && scheduleDoc.cellOverrides) || {};
  var byId = {};
  (bags || []).forEach(function(b) { if (b && b._itemId) byId[b._itemId] = b; });
  Object.keys(overrides).forEach(function(itemId) {
    var fields = overrides[itemId] || {};
    Object.keys(fields).forEach(function(field) {
      var bag = byId[itemId];
      var itemValue = bag ? (bag[field] == null ? '' : String(bag[field])) : '';
      var schedValue = fields[field] == null ? '' : String(fields[field]);
      if (schedValue !== itemValue) {
        out.push({ itemId: itemId, field: field, scheduleValue: schedValue, itemValue: itemValue });
      }
    });
  });
  return out;
}

// Audience gate (spec §2 #2). Unknown keys are allowed through here —
// custom/text columns aren't registry fields; the field PICKER only offers
// registry keys via listScheduleFieldsForAudience.
export function scheduleFieldAllowedForAudience(key, audience) {
  var f = SCHEDULE_FIELDS[key];
  // 'insurance' (2026-07-13) is an OUTSIDE audience like client/gc —
  // net cost / internal pricing never reach it. Its one privileged
  // field is client_price_final (the client's tax-inclusive price),
  // which in turn stays off client/gc/workroom/install layouts.
  if (f && f.internalStrict && audience !== 'internal') return false;
  if (f && f.internalOnly && (audience === 'client' || audience === 'gc' || audience === 'insurance')) return false;
  if (f && f.insuranceOnly && !(audience === 'insurance' || audience === 'internal')) return false;
  return true;
}

export function listScheduleFieldsForAudience(audience) {
  return Object.keys(SCHEDULE_FIELDS).filter(function(key) {
    return scheduleFieldAllowedForAudience(key, audience);
  });
}

// ── Cell resolution ──────────────────────────────────────────────────────────
// Resolves one template column against one row bag, applying the schedule
// document's cell overrides ({itemId: {fieldKey: value}}) and custom-column
// values ({itemId: {columnId: value}}). Returns a string for text cells,
// {kind:'image', url} for image columns. Missing data resolves blank —
// never guessed (document data integrity rule).
export function resolveScheduleCell(column, rowBag, scheduleDoc) {
  if (!column) return '';
  var bag = rowBag || {};
  var sched = scheduleDoc || {};
  var itemId = bag._itemId || '';
  if (column.kind === 'image') {
    return { kind: 'image', url: bag.image_url || '' };
  }
  if (column.kind === 'custom') {
    var customForItem = (sched.customValues || {})[itemId] || {};
    var cv = customForItem[column.id];
    return cv == null ? '' : String(cv);
  }
  var overridesForItem = (sched.cellOverrides || {})[itemId] || {};
  var lines = [];
  (column.blocks || []).forEach(function(b) {
    if (!b) return;
    if (b.kind === 'text') {
      if (b.value) lines.push(String(b.value));
      return;
    }
    if (b.kind !== 'field' || !b.path) return;
    var raw = Object.prototype.hasOwnProperty.call(overridesForItem, b.path)
      ? overridesForItem[b.path]
      : bag[b.path];
    var s = raw == null ? '' : String(raw);
    if (!s) return;
    lines.push((b.prefix || '') + s + (b.suffix || ''));
  });
  // column.join: block separator. Default '\n' (stacked lines); inline
  // composites use e.g. ' ' (legacy "4 EA" qty) or ' · ' (GPM + spec notes).
  return lines.join(column.join != null ? column.join : '\n');
}

// ── Schedule table mappers ───────────────────────────────────────────────────
// Round-trip mappers for schedule_templates + schedules (migration
// 20260706_create_schedule_tables.sql). Same conventions as the rest of
// the mapper family: camelCase in memory, snake_case at rest; jsonb
// columns round-trip as arrays/objects with typed defaults; the uuid FK
// (schedules.template_id) writes NULL when unset, never '' (the
// 2026-06-07 empty-string-FK rule). toRow stamps updated_at (the
// buildHoursInvoiceRow pattern) so every save path refreshes it.

// ── Payload builder (Phase 2) ────────────────────────────────────────────────
// buildSchedulePayloadPure is the ONLY sanctioned way to build a request
// body for /api/generate-schedule (the "pure builder + render-only
// renderer" house rule). The renderer refuses payloads without the schema
// marker, loudly, per the 2026-05-14 incident rule.

export var SCHEDULE_PAYLOAD_SCHEMA = 'schedule-payload-v1';

var SCHEDULE_AUDIENCE_LABELS = {
  client: 'CLIENT',
  gc: 'CONTRACTOR / GC',
  internal: 'INTERNAL / PROCUREMENT',
  workroom: 'WORKROOM',
  install: 'INSTALL / DELIVERY',
  insurance: 'INSURANCE',
};

export function scheduleAudienceLabel(audience) {
  return SCHEDULE_AUDIENCE_LABELS[audience] || String(audience || '').toUpperCase();
}

// Publish filename convention (spec §4.5):
//   {YYYYMMDD}.{projectCode}.{TypeSlug}.{AudienceSlug}.pdf
// Slugs strip everything but letters + digits ("Window Treatments" →
// "WindowTreatments", "Plumbing / Fixtures" → "PlumbingFixtures").
// seq > 1 (same-day republish) appends "-2", "-3", …
export function scheduleNameSlug(s) {
  return String(s || '').replace(/[^A-Za-z0-9]/g, '');
}

export function buildSchedulePublishFilename(args) {
  args = args || {};
  var date = String(args.dateISO || '').replace(/-/g, '').slice(0, 8);
  var code = String(args.projectCode || '').toUpperCase();
  var type = scheduleNameSlug(args.scheduleType);
  var aud = scheduleNameSlug(args.audience);
  aud = aud ? aud.charAt(0).toUpperCase() + aud.slice(1) : '';
  if (!date || !code || !type || !aud) return '';
  var seq = parseInt(args.seq, 10) || 1;
  return date + '.' + code + '.' + type + '.' + aud + (seq > 1 ? '-' + seq : '') + '.pdf';
}

export function buildScheduleStoragePath(projectLabel, filename) {
  if (!projectLabel || !filename) return '';
  return String(projectLabel) + '/' + filename;
}

// Same-day republish sequence: 1 + count of prior sends for this schedule
// whose filename starts with the same YYYYMMDD stamp.
export function nextSchedulePublishSeq(documentSends, scheduleId, dateISO) {
  var stamp = String(dateISO || '').replace(/-/g, '').slice(0, 8);
  if (!scheduleId || !stamp) return 1;
  var n = (documentSends || []).filter(function(s) {
    if (!s || s.documentType !== 'schedule' || s.documentId !== scheduleId) return false;
    var base = String(s.storagePath || '').split('/').pop() || '';
    return base.indexOf(stamp + '.') === 0;
  }).length;
  return n + 1;
}

// Template resolution for a (scheduleType, audience) pair: exact type match
// first, then the audience's generic template (blank scheduleType — the
// "Standard" seeds), else null (caller surfaces "no template" guidance).
export function resolveScheduleTemplateFor(templates, scheduleType, audience) {
  var list = templates || [];
  return list.find(function(t) { return t && t.audience === audience && t.scheduleType === scheduleType; })
      || list.find(function(t) { return t && t.audience === audience && !t.scheduleType; })
      || null;
}

// Install-audience selection (spec decision #3: install is columns + a ROW
// FILTER, not a separate schedule). Mirrors the legacy buildInstallRooms
// eligibility exactly: statuses + procurement classes come from the
// template's rowFilter; per-run extras/excludes and the receiver filter
// come from the picker; WT assemblies are included when ANY peer is
// eligible — and ALL their peers return so the 'assembly' row model
// collapses them into one install row (the legacy synthetic-row shape).
// The FINISHED piece of an assembly is the peer that ships to a RECEIVER
// (Interwest, the project site) — never the peer that ships to a
// WORKROOM-type vendor, which is a component on its way to be made into
// something (2026-11-02, the daybed / lounge-chairs / banquette case).
// The directory's vendor_type decides, not the childType label: a
// manufacturer taking COM (Avenue Road's warehouse) and a workroom sewing
// cushions (Andrew Woodside Carter) are both typed Workroom and are
// handled identically. Blank ship-to = the project receiver default.
// Accessory peers (Hardware, Trim, Bulb) never lead while a real piece
// exists: a Lutron track ships straight to site from the AV vendor, but
// the install row is the DRAPERY. A peer whose ship-to is its own vendor
// (the Stitch workroom peers point at Stitch) is undecided, not a
// component — the maker is simply holding it.
// Fallback when ship-to cannot decide (no vendors list, unknown vendor
// type, several peers to receivers): the peer with no childType, then
// the Workroom peer, then the first — never a name-length tiebreak.
export var ASSEMBLY_ACCESSORY_CHILD_TYPES = ['Hardware', 'Trim', 'Bulb'];
export function finishedPeerForAssembly(peers, opts) {
  opts = opts || {};
  var vendors = Array.isArray(opts.vendors) ? opts.vendors : [];
  var list = (peers || []).filter(Boolean);
  if (list.length === 0) return null;
  var core = list.filter(function(it) { return ASSEMBLY_ACCESSORY_CHILD_TYPES.indexOf(it.childType) < 0; });
  if (core.length > 0) list = core;
  function shipsToReceiver(it) {
    var id = it.shipToVendorId;
    if (id === 'install_on_site') return true;
    if (id == null || id === '') return true;          // project receiver default
    var v = vendors.find(function(x) { return x && String(x.id) === String(id); });
    if (!v) return null;                               // unknown — undecided
    if (it.vendor && String(v.name || '').trim().toLowerCase() === String(it.vendor).trim().toLowerCase()) return null; // ships to itself
    if (String(v.vendorType || '') === 'Workroom') return false;
    if (String(v.vendorType || '') === 'Receiver') return true;
    return null;
  }
  var toReceiver = list.filter(function(it) { return shipsToReceiver(it) === true; });
  if (toReceiver.length === 1) return toReceiver[0];
  var pool = toReceiver.length > 0 ? toReceiver
    : list.filter(function(it) { return shipsToReceiver(it) !== false; });
  if (pool.length === 0) pool = list;
  return pool.find(function(it) { return !it.childType; })
    || pool.find(function(it) { return it.childType === 'Workroom'; })
    || pool[0];
}

export function computeInstallSelectionIds(items, opts) {
  opts = opts || {};
  var rf = opts.rowFilter || {};
  // Template-supplied status lists live as DATA on schedule_templates rows
  // (the install seed predates the 2026-07-28 rename), so both the list
  // AND the item's status canonicalize before comparing — an old
  // 'Order Confirmed' filter still matches an 'Ordered' item.
  var statuses = (rf.statuses || ['Ordered', 'Shipped', 'At Receiver', 'Delivered', 'Installed', 'Invoiced', 'Complete'])
    .map(canonicalItemStatus);
  var classes = rf.procurementClasses || ['owner_furnished', 'by_others'];
  var projectLabel = opts.projectLabel || '';
  var scheduleType = opts.scheduleType || 'ALL';
  var receiverFilter = String(opts.receiverFilter == null ? 'ALL' : opts.receiverFilter);
  var projectReceiverId = opts.projectReceiverId == null ? '' : String(opts.projectReceiverId);
  var extraIds = opts.extraIds || [];
  var excludeIds = opts.excludeIds || [];
  // Install-event filter (2026-08-24) — per-run transient, like the
  // receiver filter. Empty array / null = All Events (no filtering).
  // INSTALL_EVENT_UNSCHEDULED catches items with no (valid) event
  // assignment — manual planned dates included.
  var eventFilter = (opts.eventFilter || []).map(String);
  var installEvents = opts.installEvents || [];

  function matchesType(it) {
    if (scheduleType === 'ALL') return true;
    if (scheduleType === 'FF&E') return it.schedule === 'Furniture' || it.schedule === 'Equipment';
    if (scheduleType === 'Finish Schedule') return it.schedule === 'Finish Schedule' || it.schedule === 'Tile & Stone';
    return it.schedule === scheduleType;
  }
  function receiverOk(it) {
    if (receiverFilter === 'ALL') return true;
    return String(it.shipToVendorId || projectReceiverId) === receiverFilter;
  }
  // Like receiverOk, eventOk applies BEFORE extras — an explicit per-run
  // add still stays off a run scoped to a different install event.
  function eventOk(it) {
    if (eventFilter.length === 0) return true;
    var evId = itemInstallEventId(it, installEvents);
    if (evId) return eventFilter.indexOf(evId) >= 0;
    return eventFilter.indexOf(INSTALL_EVENT_UNSCHEDULED) >= 0;
  }
  function eligible(it) {
    return statuses.indexOf(canonicalItemStatus(it.status)) >= 0 || classes.indexOf(it.procurementClass) >= 0;
  }

  // offScheduleDefault (the item-page "keep off schedule" checkbox) is a
  // DEFAULT — flagged items drop out of auto-selection but can be added
  // back per run via extras (2026-07-29; pre-fix install ignored the flag).
  // ANY flagged assembly peer defaults the WHOLE assembly off: the
  // schedule row is the assembly, so the checkbox reads as assembly
  // intent regardless of which peer's form it was ticked on. (The fabric
  // primary carries no childType, so the set must scan every peer.)
  var flaggedAsmOff = {};
  (items || []).forEach(function(it) {
    if (it && it.assemblyId && it.offScheduleDefault) flaggedAsmOff[it.assemblyId] = true;
  });

  var ids = [];
  (items || []).forEach(function(it) {
    if (!it) return;
    if (it.parentId) return;                    // legacy child rows never select directly
    if (it.assemblyId) return;                  // every assembly is handled at assembly level below
    if (projectLabel && it.project !== projectLabel) return;
    if (!matchesType(it)) return;
    if (!receiverOk(it)) return;
    if (!eventOk(it)) return;
    if (excludeIds.indexOf(it.id) >= 0) return;
    if (extraIds.indexOf(it.id) >= 0) { ids.push(it.id); return; }
    if (it.assemblyId && extraIds.indexOf('__wt_asm__:' + it.assemblyId) >= 0) { ids.push(it.id); return; }
    if (it.assemblyId && flaggedAsmOff[it.assemblyId]) return;
    if (eligible(it) && !it.offScheduleDefault) ids.push(it.id);
  });

  // Every assembly (2026-11-02; was Window Treatments only, which silently
  // dropped Furniture / Lighting / Millwork peers — the daybed and lounge
  // chairs) prints as ONE row driven by its FINISHED peer
  // (finishedPeerForAssembly): eligibility, schedule type and receiver
  // read that peer, so fabric Delivered to the workroom never drags a
  // half-made chair onto the run. ALL peers return so the 'assembly' row
  // model collapses them. WT assemblies keep the legacy gate: they surface
  // only on ALL / Window Treatments (never bleed into FF&E / Lighting runs).
  var byAsm = {};
  (items || []).forEach(function(it) {
    if (!it || !it.assemblyId || it.parentId) return;
    if (projectLabel && it.project !== projectLabel) return;
    if (!byAsm[it.assemblyId]) byAsm[it.assemblyId] = [];
    byAsm[it.assemblyId].push(it);
  });
  var peerOpts = { vendors: opts.vendors || [], projectReceiverId: projectReceiverId };
  Object.keys(byAsm).forEach(function(asmId) {
    var peers = byAsm[asmId];
    var asmKey = '__wt_asm__:' + asmId;
    var fin = finishedPeerForAssembly(peers, peerOpts) || peers[0];
    var isWt = peers.some(function(p) { return p.schedule === 'Window Treatments'; });
    if (isWt) {
      if (scheduleType !== 'ALL' && scheduleType !== 'Window Treatments') return;
    } else if (!matchesType(fin)) return;
    var explicitlyAdded = extraIds.indexOf(asmKey) >= 0
      || peers.some(function(p) { return extraIds.indexOf(p.id) >= 0; });
    if (excludeIds.indexOf(asmKey) >= 0) return;
    if (peers.some(function(p) { return excludeIds.indexOf(p.id) >= 0; })) return;
    // Assembly-level flag intent — see flaggedAsmOff above. Explicitly
    // adding the assembly back per run overrides.
    if (flaggedAsmOff[asmId] && !explicitlyAdded) return;
    if (!eligible(fin) && !explicitlyAdded) return;
    if (!receiverOk(fin)) return;
    // Any-peer rule for events: one peer on the event brings the whole
    // assembly (the drapery installs as one unit).
    if (eventFilter.length > 0 && !peers.some(eventOk)) return;
    peers.forEach(function(p) { ids.push(p.id); });
  });
  return ids;
}

// Row-model dispatch: selection → row bags. Port of the legacy buildRooms
// branching (standalone vs assemblyId groups vs parentId children).
export function scheduleRowBagsForTemplate(args) {
  args = args || {};
  var template = args.template || {};
  var selectedItems = args.selectedItems || [];
  var assemblies = args.assemblies || [];
  var scheduleType = args.scheduleType || template.scheduleType || '';
  var audience = args.audience || template.audience || 'client';
  var vendors = args.vendors || null;  // receiver names for whereabouts (optional)
  var rowModel = template.rowModel || 'item';

  var standalone = selectedItems.filter(function(it) { return !it.assemblyId; });
  var asmComponents = selectedItems.filter(function(it) { return !!it.assemblyId; });
  var byAsmId = {};
  asmComponents.forEach(function(it) {
    if (!byAsmId[it.assemblyId]) byAsmId[it.assemblyId] = [];
    byAsmId[it.assemblyId].push(it);
  });

  var bags = [];
  if (rowModel === 'wt_expanded') {
    standalone.forEach(function(it) {
      if (it.children && it.children.length > 0) {
        bags = bags.concat(buildWtExpandedChildRowBags(it));
      } else {
        bags.push(buildScheduleItemRowBag(it, vendors));
      }
    });
    Object.keys(byAsmId).forEach(function(asmId) {
      var asm = assemblies.find(function(a) { return a.id === asmId; });
      if (!asm) return;
      bags = bags.concat(buildWtExpandedComponentRowBags(byAsmId[asmId], asm));
    });
  } else if (rowModel === 'assembly') {
    standalone.forEach(function(it) { bags.push(buildScheduleItemRowBag(it, vendors)); });
    Object.keys(byAsmId).forEach(function(asmId) {
      var asm = assemblies.find(function(a) { return a.id === asmId; });
      if (!asm) return;
      var bag = buildScheduleAssemblyRowBag(byAsmId[asmId], asm, { scheduleType: scheduleType, audience: audience, vendors: vendors, projectReceiverId: args.project && args.project.receiverId });
      if (bag) bags.push(bag);
    });
  } else {
    // 'item' — one row per selected item, no collapse.
    selectedItems.forEach(function(it) { bags.push(buildScheduleItemRowBag(it, vendors)); });
  }
  // client_price_final (insurance audience) needs project context for
  // tax — stamped here when the caller provides it (payload builder +
  // grid both do); bags stay blank on that field otherwise.
  stampScheduleClientPrices(bags, selectedItems, args.allItems || selectedItems, args.project || null);
  stampSchedulePlannedInstall(bags, selectedItems, args.project || null, args.todayIso || '');
  // Whereabouts strings need project context for "Delivered to {site}" —
  // the row-bag builders don't take a project, so restamp here.
  if (args.project) {
    var _wbById = {};
    (selectedItems || []).forEach(function(it) { if (it && it.id) _wbById[it.id] = it; });
    bags.forEach(function(bag) {
      var wbItem = bag._itemId && _wbById[bag._itemId];
      if (!wbItem) return;
      var wb = trackingWhereabouts(wbItem, vendors, args.project);
      bag.est_ship = wb;
      bag.whereabouts = wb;
    });
  }
  return bags;
}

// PLANNED INSTALL per row — needs project context (installEvents), so it
// stamps here rather than in the row-bag builders. Assembly rows take the
// first peer that carries a plan (an install event assignment on any peer
// is assembly intent — the drapery installs as one unit).
export function stampSchedulePlannedInstall(bags, selectedItems, project, todayIso) {
  var byId = {}, byAsm = {};
  (selectedItems || []).forEach(function(it) {
    if (!it || !it.id) return;
    byId[it.id] = it;
    if (it.assemblyId) (byAsm[it.assemblyId] = byAsm[it.assemblyId] || []).push(it);
  });
  // Event-planned rows carry the event's DESCRIPTION over the dates
  // (Cimon 2026-07-30) — a hard line break, not an inline separator, so
  // the date range never hyphenates across wrapped lines:
  //   Furniture install
  //   Aug 12 – 14, 2026
  // Manual plans have no label — date range alone.
  function labeled(it) {
    var display = plannedInstallDisplay(it, project);
    if (!display) return '';
    var plan = resolveInstallPlan(it, project);
    return (plan && plan.label) ? plan.label + '\n' + display : display;
  }
  (bags || []).forEach(function(bag) {
    var display = '';
    var srcItem = null;
    if (bag._assemblyId && byAsm[bag._assemblyId]) {
      var peers = byAsm[bag._assemblyId];
      for (var i = 0; i < peers.length && !display; i++) {
        display = labeled(peers[i]);
        if (display) srcItem = peers[i];
      }
    } else if (bag._itemId && byId[bag._itemId]) {
      display = labeled(byId[bag._itemId]);
      srcItem = byId[bag._itemId];
    }
    bag.install_planned = display;
    // Advisory overdue flag (grid-only tint; the PDF prints plain) —
    // stamped only when the caller passes todayIso.
    bag._install_overdue = !!(todayIso && srcItem && installPlanOverdue(srcItem, project, todayIso));
  });
  return bags;
}

// The client's FINAL price per row, tax included — itemLifecycleOwed
// (lineClient + marked-up fees + per-item tax, ccFeeMul applied), the
// same canonical math the FIN bills. Assembly rows sum their component
// peers (Component Independence: each peer is its own merchandise; the
// row represents all of them). $0 renders blank — an unpriced item
// (e.g. approved-but-unquoted) shows nothing rather than a fake $0;
// use a cell override to type an estimate.
export function stampScheduleClientPrices(bags, selectedItems, allItems, project) {
  var byId = {}, byAsm = {};
  (selectedItems || []).forEach(function(it) {
    if (!it || !it.id) return;
    byId[it.id] = it;
    if (it.assemblyId) (byAsm[it.assemblyId] = byAsm[it.assemblyId] || []).push(it);
  });
  (bags || []).forEach(function(bag) {
    var total = 0;
    if (bag._assemblyId && byAsm[bag._assemblyId]) {
      byAsm[bag._assemblyId].forEach(function(c) {
        total += itemLifecycleOwed(c, allItems, project) || 0;
      });
    } else if (bag._itemId && byId[bag._itemId]) {
      total = itemLifecycleOwed(byId[bag._itemId], allItems, project) || 0;
    }
    total = Math.round(total * 100) / 100;
    bag.client_price_final = total > 0 ? fmtMoney(total) : '';
  });
  return bags;
}

// Room grouping + ordering. Port of the legacy byRoom/mkRoomKey logic:
// key on roomNumber + roomName so identically-named rooms stay separate,
// sort rooms numerically then by name, sort rows by tag (numeric-aware),
// with component order as the wt_expanded secondary sort.
export function groupScheduleRowsByRoom(bags) {
  var byRoom = {};
  var roomNumMap = {};
  var SEP = '\x1F'; // legacy mkRoomKey separator — never appears in room names
  function mkKey(name, num) { return num ? (num + SEP + name) : name; }
  (bags || []).forEach(function(bag) {
    var name = bag._roomName || 'No Room';
    var num = bag._roomNumber || '';
    var key = mkKey(name, num);
    if (!byRoom[key]) byRoom[key] = [];
    byRoom[key].push(bag);
    if (!roomNumMap[key] && num) roomNumMap[key] = num;
  });
  Object.keys(byRoom).forEach(function(key) {
    byRoom[key].sort(function(a, b) {
      var tc = (a.tag || '').localeCompare(b.tag || '', undefined, { numeric: true, sensitivity: 'base' });
      if (tc !== 0) return tc;
      return (a._compOrder || 0) - (b._compOrder || 0);
    });
  });
  return Object.keys(byRoom).sort(function(a, b) {
    var na = parseFloat(roomNumMap[a]) || 9999;
    var nb = parseFloat(roomNumMap[b]) || 9999;
    return na !== nb ? na - nb : a.localeCompare(b);
  }).map(function(key) {
    var sep = key.indexOf(SEP);
    return {
      room: sep > -1 ? key.slice(sep + 1) : key,
      roomNumber: roomNumMap[key] || '',
      rows: byRoom[key],
    };
  });
}

// Install-event grouping (2026-08-24) — the event-scoped install run
// prints EVENT bands instead of room bands, riding the exact same
// {room, roomNumber, rows} group shape (roomNumber '' so the band prints
// the label alone): one band per event in date order, label
// 'Furniture Install · Aug 12 – 14, 2026', an 'Unscheduled' band last
// for rows with no (valid) event assignment. Rooms still sequence the
// rows WITHIN each band (groupScheduleRowsByRoom flattened). Assembly
// rows take the first peer carrying an event assignment — one peer on
// the event is assembly intent, same as stampSchedulePlannedInstall.
export function groupScheduleRowsByInstallEvent(bags, selectedItems, project) {
  var events = (project && project.installEvents) || [];
  var byId = {}, byAsm = {};
  (selectedItems || []).forEach(function(it) {
    if (!it || !it.id) return;
    byId[it.id] = it;
    if (it.assemblyId) (byAsm[it.assemblyId] = byAsm[it.assemblyId] || []).push(it);
  });
  function bagEventId(bag) {
    if (bag._assemblyId && byAsm[bag._assemblyId]) {
      var peers = byAsm[bag._assemblyId];
      for (var i = 0; i < peers.length; i++) {
        var evId = itemInstallEventId(peers[i], events);
        if (evId) return evId;
      }
      return '';
    }
    if (bag._itemId && byId[bag._itemId]) return itemInstallEventId(byId[bag._itemId], events);
    return '';
  }
  var byEvent = {};
  (bags || []).forEach(function(bag) {
    var key = bagEventId(bag) || INSTALL_EVENT_UNSCHEDULED;
    (byEvent[key] = byEvent[key] || []).push(bag);
  });
  function rowsInRoomOrder(groupBags) {
    var rows = [];
    groupScheduleRowsByRoom(groupBags).forEach(function(g) { rows = rows.concat(g.rows); });
    return rows;
  }
  var groups = [];
  sortInstallEvents(events).forEach(function(ev) {
    if (!ev) return;
    var groupBags = byEvent[String(ev.id)];
    if (!groupBags || groupBags.length === 0) return;
    var label = ev.label || 'Install event';
    var range = ev.startDate ? installRangeDisplay(ev.startDate, ev.days) : '';
    groups.push({
      room: range ? label + ' · ' + range : label,
      roomNumber: '',
      rows: rowsInRoomOrder(groupBags),
    });
  });
  if (byEvent[INSTALL_EVENT_UNSCHEDULED] && byEvent[INSTALL_EVENT_UNSCHEDULED].length > 0) {
    groups.push({
      room: 'Unscheduled',
      roomNumber: '',
      rows: rowsInRoomOrder(byEvent[INSTALL_EVENT_UNSCHEDULED]),
    });
  }
  return groups;
}

// The pure builder. Throws loudly on missing template, and on any column
// whose field is refused for the payload's audience (defense in depth
// behind the field picker — a client/gc payload must never carry pricing).
//
// args: {
//   schedule   — working doc (projectLabel, scheduleType, audience,
//                itemIds, cellOverrides, customValues, title)
//   template   — schedule_templates row (columns, rowModel, page)
//   items, assemblies, project
//   dateLabel  — preformatted display date (pure: caller supplies it)
// }
export function buildSchedulePayloadPure(args) {
  args = args || {};
  var schedule = args.schedule || {};
  var template = args.template;
  if (!template || !Array.isArray(template.columns) || template.columns.length === 0) {
    throw new Error('buildSchedulePayloadPure: template with columns is required');
  }
  var audience = schedule.audience || template.audience || 'client';
  var scheduleType = schedule.scheduleType || template.scheduleType || '';

  template.columns.forEach(function(col) {
    (col.blocks || []).forEach(function(b) {
      if (b && b.kind === 'field' && !scheduleFieldAllowedForAudience(b.path, audience)) {
        throw new Error('buildSchedulePayloadPure: field "' + b.path + '" is not allowed on a ' + audience + '-audience schedule');
      }
    });
  });

  var items = args.items || [];
  var wanted = {};
  (schedule.itemIds || []).forEach(function(id) { wanted[id] = true; });
  var selectedItems = items.filter(function(it) { return wanted[it.id]; });

  var bags = scheduleRowBagsForTemplate({
    template: template,
    selectedItems: selectedItems,
    assemblies: args.assemblies || [],
    scheduleType: scheduleType,
    audience: audience,
    allItems: items,
    project: args.project || null,
    vendors: args.vendors || null,
  });
  // Event-scoped install run (2026-08-24, per-run transient): the
  // selection upstream is already narrowed to the chosen events; here
  // the same choice flips the page from room bands to EVENT bands.
  var eventFilter = (audience === 'install' && Array.isArray(args.installEventFilter))
    ? args.installEventFilter.map(String) : [];
  var roomGroups = eventFilter.length > 0
    ? groupScheduleRowsByInstallEvent(bags, selectedItems, args.project || null)
    : groupScheduleRowsByRoom(bags);
  var eventScope = eventFilter.length > 0
    ? installEventScopeLabel(eventFilter, ((args.project || {}).installEvents) || [])
    : '';

  var rooms = roomGroups.map(function(g) {
    return {
      room: g.room,
      roomNumber: g.roomNumber,
      rows: g.rows.map(function(bag) {
        var imageUrl = '';
        var cells = template.columns.map(function(col) {
          var resolved = resolveScheduleCell(col, bag, schedule);
          if (resolved && resolved.kind === 'image') {
            imageUrl = resolved.url || '';
            return '';
          }
          // Schedules print ALL CAPS (Cimon 2026-07-29) — these fields
          // arrive mixed-case from live data, so the PDF payload
          // uppercases them. Render-path only: the grid keeps true
          // values (a grid cell edit must never write an uppercased
          // vendor name back over the canonical directory string).
          if (typeof resolved === 'string' && resolved &&
              (col.blocks || []).some(function(b) {
                return b && (b.path === 'vendor' || b.path === 'est_ship' || b.path === 'whereabouts');
              })) {
            return resolved.toUpperCase();
          }
          return resolved;
        });
        return {
          cells: cells,
          imageUrl: imageUrl,
          compType: bag.comp_type || '',
          assemblyName: bag._assemblyName || '',
          isFirstInAsm: !!bag._isFirstInAsm,
          tag: bag.tag || '',
        };
      }),
    };
  });

  // Schedule total (2026-07-13, owner's-rep request): when the template
  // carries the CLIENT PRICE column, total the PRINTED cells — including
  // typed cell overrides like "$12,000.00 (est.)" — so the band always
  // matches the column above it. First money token per cell; blank
  // (unpriced / by-others) cells contribute nothing.
  var priceColIdx = -1;
  template.columns.forEach(function(col, ci) {
    (col.blocks || []).forEach(function(b) {
      if (b && b.kind === 'field' && b.path === 'client_price_final') priceColIdx = ci;
    });
  });
  var totals = null;
  if (priceColIdx >= 0) {
    var totalSum = 0, pricedRows = 0;
    rooms.forEach(function(rm) {
      rm.rows.forEach(function(row) {
        var m = String(row.cells[priceColIdx] || '').match(/\$\s*([\d,]+(?:\.\d{1,2})?)/);
        if (m) {
          totalSum += parseFloat(m[1].replace(/,/g, '')) || 0;
          pricedRows++;
        }
      });
    });
    if (pricedRows > 0) {
      totals = {
        display: fmtMoney(Math.round(totalSum * 100) / 100),
        count: pricedRows,
      };
    }
  }

  var project = args.project || {};
  return {
    totals: totals,
    meta: {
      schema: SCHEDULE_PAYLOAD_SCHEMA,
      title: schedule.title || '',
      projectLabel: schedule.projectLabel || project.label || '',
      projectDisplay: project.displayName || project.label || schedule.projectLabel || '',
      projectCity: [project.city, project.state].filter(Boolean).join(', '),
      scheduleType: scheduleType,
      // Install is an AUDIENCE, not a schedule type (it filters WHICH
      // materials are delivering — a Lighting install run must never be
      // confused with THE Lighting schedule). Its document identity is
      // fixed; the type moves to the second line (Cimon 2026-07-30).
      scheduleLabel: audience === 'install'
        ? 'DELIVERY / INSTALL SCHEDULE'
        : (scheduleType ? scheduleType.toUpperCase() + ' ' : '') + 'SCHEDULE',
      audience: audience,
      // Event-scoped runs name their scope on this line (2026-08-24) —
      // "All Specifications — Furniture Install · Aug 12 – 14, 2026" —
      // so a partial run never reads as the full schedule.
      audienceLabel: audience === 'install'
        ? (!scheduleType || scheduleType === 'ALL' ? 'All Specifications' : scheduleType)
          + (eventScope ? ' — ' + eventScope : '')
        : scheduleAudienceLabel(audience),
      dateLabel: args.dateLabel || '',
      orientation: (template.page && template.page.orientation) || 'landscape',
      // 'letter' (default) | '17x11' | 'cad-22.75x21' — CAD attachment
      // formats for the Contractor audience (AutoCAD sheet placement).
      pageFormat: (template.page && template.page.format) || 'letter',
      // Per-room LOCATION band rows. Default true; page.roomBands === false
      // (the Contractor/GC templates, 2026-07-23) suppresses them — room
      // order still governs row sequence, the band headers just don't print.
      roomBands: !(template.page && template.page.roomBands === false),
      rowModel: template.rowModel || 'item',
    },
    columns: template.columns.map(function(col) {
      return {
        label: col.label || '',
        width: col.width || 10,
        kind: col.kind === 'image' ? 'image' : 'text',
      };
    }),
    rooms: rooms,
  };
}

// ── Seed templates ───────────────────────────────────────────────────────────
// One template per legacy layout so day-one output of the template engine
// matches today's schedules column-for-column (spec §5 Phase 1 commit 4).
// The 20260706_seed_schedule_templates.sql migration INSERTs exactly these
// definitions; scheduleSeedTemplates.test.js asserts the SQL and this
// export stay byte-identical, so neither can drift alone.
//
// Documented simplifications vs the legacy renderers (deliberate; revisit
// in Phase 2 when the new renderer diffs against legacy output):
//   - Finish/GC: the Tile&Stone-item-mixed-into-Finish column remap is a
//     per-row conditional the column model doesn't express; seeds carry
//     the standard Finish mapping.
//   - Lighting/GC: legacy runs photometric values through a placeholder
//     filter (lightingSpecVal). Seeds reference the raw fields.
//   - Install: legacy renders rich WR entry blocks (number + description
//     + date); the seed uses the flat wr_numbers roll-up.
//   - Hardware / Equipment / Art & Objects under the GC audience fell to
//     the legacy renderer's Lighting else-branch; they get no dedicated
//     seed — build one in the template editor when needed.
var seedField = function(id, label, width, path, extra) {
  return Object.assign({ id: id, label: label, width: width, blocks: [{ kind: 'field', path: path }] }, extra || {});
};
var seedImage = function(width) {
  return { id: 'image', label: '', width: width, kind: 'image' };
};
// Inline "4 EA" qty composite used by every GC/construction layout.
var seedQtyUnit = function(width) {
  return { id: 'qty', label: 'QTY', width: width, join: ' ', blocks: [{ kind: 'field', path: 'qty' }, { kind: 'field', path: 'unit' }] };
};
var GC_PAGE = { orientation: 'landscape' };
// CAD attachment formats (Contractor audience — AutoCAD sheet placement).
// roomBands:false (Cimon 2026-07-23): Contractor/GC schedules skip the
// per-room LOCATION band rows — the tag suffix carries the room number
// (L01-101 = room 101), so the bands only added height. Row ORDER is
// unchanged (room sequence, then tag); only the band headers go.
var GC_PAGE_17X11 = { orientation: 'landscape', format: '17x11', roomBands: false };
var GC_PAGE_CAD = { orientation: 'landscape', format: 'cad-22.75x21', roomBands: false };

export var SCHEDULE_SEED_TEMPLATES = [
  {
    name: 'Client — Standard',
    scheduleType: '', audience: 'client', rowModel: 'assembly', rowFilter: {}, page: GC_PAGE, isSeed: true,
    columns: [
      seedImage(16),
      seedField('product_name', 'ITEM', 33, 'product_name'),
      seedField('location', 'LOCATION', 22, 'location'),
      seedField('tag', 'TAG', 13, 'tag_short'),
      seedField('qty', 'QTY', 8, 'qty'),
      seedField('manufacturer', 'MANUFACTURER', 24, 'manufacturer'),
      seedField('model', 'MODEL', 21, 'model'),
      seedField('size', 'SIZE', 18, 'size'),
      seedField('finish', 'FINISH', 24, 'finish'),
      seedField('notes', 'NOTES', 30, 'notes'),
    ],
  },
  {
    name: 'Internal — Standard',
    scheduleType: '', audience: 'internal', rowModel: 'assembly', rowFilter: {}, page: GC_PAGE, isSeed: true,
    columns: [
      seedImage(16),
      seedField('product_name', 'ITEM', 33, 'product_name'),
      seedField('location', 'LOCATION', 22, 'location'),
      seedField('tag', 'TAG', 13, 'tag_short'),
      seedField('qty', 'QTY', 8, 'qty'),
      seedField('manufacturer', 'MANUFACTURER', 24, 'manufacturer'),
      seedField('model', 'MODEL', 21, 'model'),
      seedField('size', 'SIZE', 18, 'size'),
      seedField('finish', 'FINISH', 24, 'finish'),
      seedField('notes', 'NOTES', 26, 'notes'),
      seedField('price', 'PRICE', 20, 'price_summary'),
    ],
  },
  {
    name: 'Window Treatments — Client',
    scheduleType: 'Window Treatments', audience: 'client', rowModel: 'assembly', rowFilter: {}, page: GC_PAGE, isSeed: true,
    columns: [
      seedImage(16),
      seedField('product_name', 'ITEM', 28, 'product_name'),
      seedField('location', 'LOCATION', 20, 'location'),
      seedField('tag', 'TAG', 12, 'tag_short'),
      seedField('qty', 'QTY', 8, 'qty'),
      seedField('wt_desc', 'TYPE', 22, 'wt_desc'),
      seedField('wt_mount', 'MOUNT', 16, 'wt_mount'),
      seedField('wt_size', 'SIZE', 18, 'wt_size'),
      seedField('wt_panels_draw', 'PANELS/DRAW', 16, 'wt_panels_draw'),
      seedField('wt_lining', 'LINING', 16, 'wt_lining'),
      seedField('wt_hardware', 'HARDWARE', 28, 'wt_hardware'),
      seedField('wt_motor', 'MOTOR', 14, 'wt_motor'),
      seedField('vendor', 'VENDOR', 18, 'vendor'),
      seedField('notes', 'NOTES', 20, 'notes'),
    ],
  },
  {
    name: 'Window Treatments — Workroom',
    scheduleType: 'Window Treatments', audience: 'workroom', rowModel: 'assembly', rowFilter: {}, page: GC_PAGE, isSeed: true,
    columns: [
      seedImage(16),
      seedField('product_name', 'ITEM', 28, 'product_name'),
      seedField('location', 'LOCATION', 20, 'location'),
      seedField('tag', 'TAG', 12, 'tag_short'),
      seedField('qty', 'QTY', 8, 'qty'),
      seedField('wt_size', 'FINISHED SIZE', 18, 'wt_size'),
      seedField('construction', 'CONSTRUCTION', 28, 'wt_panels_block'),
      seedField('wt_lining', 'LINING', 14, 'wt_lining'),
      seedField('wt_hardware', 'HARDWARE', 26, 'wt_hardware'),
      seedField('source', 'SOURCE', 20, 'source'),
      seedField('notes', 'NOTES', 26, 'notes'),
      seedField('order_status', 'ORDER STATUS', 22, 'order_status'),
    ],
  },
  {
    name: 'Window Treatments — Internal (components)',
    scheduleType: 'Window Treatments', audience: 'internal', rowModel: 'wt_expanded', rowFilter: {}, page: GC_PAGE, isSeed: true,
    columns: [
      seedImage(16),
      seedField('comp_type', 'TYPE', 14, 'comp_type'),
      seedField('item', 'ITEM', 42, 'item'),
      seedField('size_dim', 'SIZE/DIM', 20, 'size_dim'),
      seedField('vendor', 'VENDOR', 26, 'vendor'),
      seedField('specs', 'SPECS', 50, 'specs'),
      seedField('qty', 'QTY', 10, 'qty'),
      seedField('unit', 'UNIT', 14, 'unit'),
      seedField('price', 'PRICE', 22, 'price_summary'),
    ],
  },
  {
    name: 'Tile & Stone — Client',
    scheduleType: 'Tile & Stone', audience: 'client', rowModel: 'assembly', rowFilter: {}, page: GC_PAGE, isSeed: true,
    columns: [
      seedImage(16),
      seedField('product_name', 'ITEM', 24, 'product_name'),
      seedField('location', 'LOCATION', 18, 'location'),
      seedField('tag', 'TAG', 12, 'tag_short'),
      seedField('qty', 'QTY', 8, 'qty'),
      seedField('manufacturer', 'MANUFACTURER', 20, 'manufacturer'),
      seedField('model', 'MODEL', 18, 'model'),
      seedField('size', 'SIZE', 14, 'size'),
      seedField('finish', 'FINISH', 18, 'finish'),
      seedField('ts_material', 'MATERIAL', 18, 'ts_material'),
      seedField('ts_tile_size', 'TILE SIZE', 14, 'ts_tile_size'),
      seedField('ts_thickness', 'THICKNESS', 14, 'ts_thickness'),
      seedField('ts_coverage', 'COVERAGE/QTY', 22, 'ts_coverage_notes'),
      seedField('ts_grout', 'GROUT SPEC', 18, 'ts_grout_spec'),
      seedField('notes', 'NOTES', 18, 'notes'),
    ],
  },
  {
    name: 'Window Treatments — Contractor/GC',
    scheduleType: 'Window Treatments', audience: 'gc', rowModel: 'assembly', rowFilter: {}, page: GC_PAGE_17X11, isSeed: true,
    columns: [
      seedField('tag', 'TAG', 14, 'tag_short'),
      seedField('item', 'ITEM', 38, 'item'),
      seedQtyUnit(10),
      seedField('description', 'DESCRIPTION', 38, 'wt_desc'),
      seedField('wt_mount', 'MOUNT', 20, 'wt_mount'),
      seedField('wt_width', 'WIDTH', 14, 'wt_width'),
      seedField('wt_length', 'LENGTH', 14, 'wt_length'),
      seedField('wt_panels', 'PANELS', 12, 'wt_panels'),
      seedField('wt_draw', 'DRAW', 16, 'wt_draw'),
      seedField('wt_style', 'STYLE', 22, 'wt_style'),
      seedField('wt_lining', 'LINING', 18, 'wt_lining'),
      seedField('wt_hardware', 'HARDWARE', 42, 'wt_hardware'),
      seedField('wt_motor', 'MOTOR', 22, 'wt_motor'),
      seedField('notes', 'NOTES', 42, 'notes'),
      seedField('wr', 'WR #', 20, 'wr_numbers'),
    ],
  },
  {
    name: 'Millwork — Contractor/GC',
    scheduleType: 'Millwork', audience: 'gc', rowModel: 'assembly', rowFilter: {}, page: GC_PAGE_17X11, isSeed: true,
    columns: [
      seedField('tag', 'TAG', 14, 'tag_short'),
      seedField('item', 'ITEM', 45, 'item'),
      seedQtyUnit(10),
      seedField('description', 'DESCRIPTION', 50, 'product_name'),
      seedField('mw_material', 'MATERIAL', 28, 'mw_material'),
      seedField('mw_species', 'SPECIES/GRADE', 28, 'mw_species'),
      seedField('mw_profile', 'PROFILE/STYLE', 24, 'mw_profile'),
      seedField('finish', 'FINISH', 24, 'finish'),
      seedField('mw_paint', 'PAINT/STAIN', 28, 'mw_paint'),
      seedField('size', 'SIZE', 28, 'size'),
      seedField('mw_linear', 'LIN. FT.', 16, 'mw_linear'),
      seedField('notes', 'NOTES', 55, 'mw_notes'),
      seedField('wr', 'WR #', 20, 'wr_numbers'),
    ],
  },
  {
    name: 'Appliances — Contractor/GC',
    scheduleType: 'Appliances', audience: 'gc', rowModel: 'assembly', rowFilter: {}, page: GC_PAGE_17X11, isSeed: true,
    columns: [
      seedField('tag', 'TAG', 16, 'tag_short'),
      seedField('item', 'ITEM', 40, 'item'),
      seedQtyUnit(12),
      seedField('description', 'DESCRIPTION', 40, 'product_name'),
      seedField('vendor', 'VENDOR', 28, 'vendor'),
      seedField('manufacturer', 'MANUFACTURER', 30, 'manufacturer'),
      seedField('model', 'MODEL', 30, 'model'),
      seedField('size', 'SIZE', 22, 'size'),
      seedField('finish', 'FINISH', 26, 'finish'),
      seedField('voltage_fuel', 'VOLTAGE/FUEL', 22, 'voltage_fuel'),
      seedField('btu', 'BTU/CAPACITY', 20, 'btu'),
      seedField('install_notes', 'INSTALL NOTES', 38, 'install_notes'),
      seedField('notes', 'NOTES', 30, 'notes'),
      seedField('wr', 'WR #', 18, 'wr_numbers'),
    ],
  },
  {
    name: 'Tile & Stone — Contractor/GC',
    scheduleType: 'Tile & Stone', audience: 'gc', rowModel: 'assembly', rowFilter: {}, page: GC_PAGE_17X11, isSeed: true,
    columns: [
      seedField('tag', 'TAG', 14, 'tag_short'),
      seedField('item', 'ITEM', 32, 'item'),
      seedQtyUnit(10),
      seedField('description', 'DESCRIPTION', 32, 'product_name'),
      seedField('vendor', 'VENDOR', 16, 'vendor'),
      seedField('manufacturer', 'MANUFACTURER', 20, 'manufacturer'),
      seedField('model', 'MODEL', 20, 'model'),
      seedField('size', 'SIZE', 16, 'size'),
      seedField('finish', 'FINISH', 20, 'finish'),
      seedField('ts_material', 'MATERIAL', 20, 'ts_material'),
      seedField('ts_tile_size', 'TILE SIZE', 16, 'ts_tile_size'),
      seedField('ts_thickness', 'THICKNESS', 14, 'ts_thickness'),
      seedField('ts_coverage', 'COVERAGE / QTY', 22, 'ts_coverage_notes'),
      seedField('ts_grout', 'GROUT SPEC', 18, 'ts_grout_spec'),
      seedField('notes', 'NOTES', 33, 'notes'),
      seedField('wr', 'WR #', 20, 'wr_numbers'),
    ],
  },
  {
    name: 'FF&E — Contractor/GC',
    scheduleType: 'FF&E', audience: 'gc', rowModel: 'assembly', rowFilter: {}, page: GC_PAGE_CAD, isSeed: true,
    columns: [
      seedField('tag', 'TAG', 20, 'tag_short'),
      seedField('item', 'ITEM', 30, 'item'),
      seedQtyUnit(12),
      seedField('description', 'DESCRIPTION', 50, 'product_name'),
      seedField('vendor', 'VENDOR', 25, 'vendor'),
      seedField('manufacturer', 'MANUFACTURER', 25, 'manufacturer'),
      seedField('model', 'MODEL', 25, 'model'),
      seedField('size', 'SIZE', 35, 'size'),
      seedField('finish', 'FINISH', 40, 'finish'),
      seedField('notes', 'NOTES', 50, 'notes'),
    ],
  },
  {
    name: 'Finish Schedule — Contractor/GC',
    scheduleType: 'Finish Schedule', audience: 'gc', rowModel: 'assembly', rowFilter: {}, page: GC_PAGE_CAD, isSeed: true,
    columns: [
      seedField('tag', 'TAG', 20, 'tag_short'),
      seedField('item', 'ITEM', 30, 'item'),
      seedQtyUnit(12),
      seedField('description', 'DESCRIPTION', 50, 'product_name'),
      seedField('vendor', 'VENDOR', 25, 'vendor'),
      seedField('manufacturer', 'MANUFACTURER', 25, 'manufacturer'),
      seedField('model', 'MODEL', 25, 'model'),
      seedField('size', 'SIZE', 35, 'size'),
      seedField('finish', 'FINISH', 40, 'finish'),
      seedField('notes', 'NOTES', 50, 'notes'),
    ],
  },
  {
    name: 'Plumbing — Contractor/GC',
    scheduleType: 'Plumbing / Fixtures', audience: 'gc', rowModel: 'assembly', rowFilter: {}, page: GC_PAGE_CAD, isSeed: true,
    columns: [
      seedField('tag', 'TAG', 20, 'tag_short'),
      seedField('item', 'ITEM', 30, 'item'),
      seedQtyUnit(12),
      seedField('description', 'DESCRIPTION', 50, 'product_name'),
      seedField('vendor', 'VENDOR', 25, 'vendor'),
      seedField('manufacturer', 'MANUFACTURER', 25, 'manufacturer'),
      seedField('model', 'MODEL', 25, 'model'),
      seedField('size', 'SIZE', 35, 'size'),
      seedField('finish', 'FINISH', 40, 'finish'),
      { id: 'gpm_spec', label: 'GPM SPEC NOTES', width: 30, join: ' · ', blocks: [{ kind: 'field', path: 'gpm' }, { kind: 'field', path: 'spec_notes' }] },
      seedField('notes', 'NOTES', 45, 'notes'),
    ],
  },
  {
    name: 'Lighting — Contractor/GC',
    scheduleType: 'Lighting', audience: 'gc', rowModel: 'assembly', rowFilter: {}, page: GC_PAGE_CAD, isSeed: true,
    columns: [
      seedField('tag', 'TAG', 20, 'tag_short'),
      seedField('item', 'ITEM', 30, 'item'),
      seedQtyUnit(12),
      seedField('description', 'DESCRIPTION', 50, 'product_name'),
      seedField('vendor', 'VENDOR', 25, 'vendor'),
      seedField('manufacturer', 'MANUFACTURER', 25, 'manufacturer'),
      seedField('model', 'MODEL', 25, 'model'),
      seedField('size', 'SIZE', 35, 'size'),
      seedField('finish', 'FINISH', 40, 'finish'),
      seedField('technology', 'TECHNOLOGY', 16, 'technology'),
      seedField('bulb', 'BULB', 13, 'bulb'),
      seedField('base', 'BASE', 13, 'base'),
      seedField('wattage', 'WATTAGE', 14, 'wattage'),
      seedField('lumens', 'LUMENS', 14, 'lumens'),
      seedField('kelvins', 'KELVINS', 14, 'kelvins'),
      seedField('cri', 'CRI', 11, 'cri'),
      seedField('voltage', 'VOLTAGE', 14, 'voltage'),
      seedField('circuitry', 'CIRCUITRY', 18, 'circuitry'),
      seedField('notes', 'NOTES', 45, 'notes'),
    ],
  },
  {
    name: 'Install / Delivery',
    scheduleType: 'ALL', audience: 'install', rowModel: 'assembly', isSeed: true, page: GC_PAGE,
    // 2026-07-29 rowFilter patch (20260729_install_rowfilter_statuses.sql):
    // the full canonical physical chain — pre-patch the DATA predated
    // Shipped/Delivered, so delivered items VANISHED from the install
    // schedule the moment they were marked delivered.
    rowFilter: {
      statuses: ['Ordered', 'Shipped', 'At Receiver', 'Delivered', 'Installed', 'Complete'],
      procurementClasses: ['owner_furnished', 'by_others'],
      receiverFilter: true,
    },
    columns: [
      seedImage(40),
      seedField('tag', 'TAG', 13, 'tag_short'),
      seedField('item', 'ITEM', 32, 'item'),
      seedField('product', 'PRODUCT', 30, 'product_name'),
      seedField('vendor', 'VENDOR', 26, 'vendor'),
      // 2026-07-28 (20260728_install_schedule_columns.sql patch): label
      // STATUS (the cell is trackingWhereabouts — estimates in parens,
      // delivered dates otherwise), WR narrowed 51→30, NOTES appended
      // (Document Notes — the external rail). The applied 20260706 seed
      // SQL stays as-run; scheduleSeedTemplates.test composes the patch.
      // 2026-09-17 (20260917_install_tracking_column_widths.sql patch):
      // SHIP STATUS 16→26 so a tracking number line fits, WR 30→22.
      seedField('est_ship', 'SHIP STATUS', 26, 'est_ship'),
      seedField('install_planned', 'PLANNED INSTALL', 18, 'install_planned'),
      seedField('dimensions', 'DIMENSIONS', 25, 'size'),
      seedQtyUnit(13),
      seedField('wr', 'WR ENTRIES', 22, 'wr_numbers'),
      seedField('sidemark', 'SIDEMARK', 25, 'sidemark'),
      seedField('notes', 'NOTES', 40, 'install_schedule_notes'),
    ],
  },
];

export function rowToScheduleTemplate(row) {
  if (!row) return null;
  return {
    id:           row.id,
    name:         row.name || '',
    scheduleType: row.schedule_type || '',
    audience:     row.audience || 'client',
    columns:      Array.isArray(row.columns) ? row.columns : [],
    rowModel:     row.row_model || 'item',
    rowFilter:    (row.row_filter && typeof row.row_filter === 'object') ? row.row_filter : {},
    page:         (row.page && typeof row.page === 'object') ? row.page : {},
    isSeed:       !!row.is_seed,
    createdBy:    row.created_by || '',
    lastEditedBy: row.last_edited_by || '',
    createdAt:    row.created_at || '',
    updatedAt:    row.updated_at || '',
  };
}

export function scheduleTemplateToRow(t) {
  t = t || {};
  return {
    id:             t.id || undefined,
    name:           t.name || '',
    schedule_type:  t.scheduleType || '',
    audience:       t.audience || 'client',
    columns:        Array.isArray(t.columns) ? t.columns : [],
    row_model:      t.rowModel || 'item',
    row_filter:     (t.rowFilter && typeof t.rowFilter === 'object') ? t.rowFilter : {},
    page:           (t.page && typeof t.page === 'object') ? t.page : {},
    is_seed:        !!t.isSeed,
    created_by:     t.createdBy || '',
    last_edited_by: t.lastEditedBy || '',
    updated_at:     new Date().toISOString(),
  };
}

export function rowToSchedule(row) {
  if (!row) return null;
  return {
    id:            row.id,
    projectLabel:  row.project_label || '',
    scheduleType:  row.schedule_type || '',
    audience:      row.audience || 'client',
    templateId:    row.template_id || null,
    title:         row.title || '',
    itemIds:       Array.isArray(row.item_ids) ? row.item_ids : [],
    extraIds:      Array.isArray(row.extra_ids) ? row.extra_ids : [],
    excludeIds:    Array.isArray(row.exclude_ids) ? row.exclude_ids : [],
    cellOverrides: (row.cell_overrides && typeof row.cell_overrides === 'object') ? row.cell_overrides : {},
    customValues:  (row.custom_values && typeof row.custom_values === 'object') ? row.custom_values : {},
    sortConfig:    (row.sort_config && typeof row.sort_config === 'object') ? row.sort_config : {},
    status:        row.status || 'draft',
    createdBy:     row.created_by || '',
    createdAt:     row.created_at || '',
    updatedAt:     row.updated_at || '',
  };
}

export function scheduleToRow(s) {
  s = s || {};
  return {
    id:             s.id || undefined,
    project_label:  s.projectLabel || '',
    schedule_type:  s.scheduleType || '',
    audience:       s.audience || 'client',
    template_id:    s.templateId || null,
    title:          s.title || '',
    item_ids:       Array.isArray(s.itemIds) ? s.itemIds : [],
    extra_ids:      Array.isArray(s.extraIds) ? s.extraIds : [],
    exclude_ids:    Array.isArray(s.excludeIds) ? s.excludeIds : [],
    cell_overrides: (s.cellOverrides && typeof s.cellOverrides === 'object') ? s.cellOverrides : {},
    custom_values:  (s.customValues && typeof s.customValues === 'object') ? s.customValues : {},
    sort_config:    (s.sortConfig && typeof s.sortConfig === 'object') ? s.sortConfig : {},
    status:         s.status || 'draft',
    created_by:     s.createdBy || '',
    updated_at:     new Date().toISOString(),
  };
}

// ── SALES TAX FILING → ITEMS + FILES (2026-10-15) ───────────────────────────
// A filing (sales_tax_filings) says what was paid to a state for a period.
// These helpers say WHICH ITEMS that payment covered and what paper proves
// it. Cimon, 2026-10-15: "all payments are stored in supabase — it cannot
// be changed in future through calculations. these are hard facts." So:
//   • sales_tax_filing_items freezes each item's tax dollars at save time;
//     item surfaces READ that row, never recompute.
//   • sales_tax_filing_files is the confirmation PDF / payment receipt.
//   • Nothing is written to `items`.
// Pure, Date-free (callers pass todayIso where it matters), tested in
// salesTaxFiling.test.js.

export function rowToSalesTaxFilingItem(row) {
  return {
    id:         row.id,
    filingId:   row.filing_id   || '',
    itemId:     row.item_id     || '',
    invoiceRef: row.invoice_ref || '',
    taxAmount:  Number(row.tax_amount || 0),
    createdAt:  row.created_at  || '',
  };
}
export function salesTaxFilingItemToRow(fi) {
  // The `id` KEY must be absent, not undefined, when there is no id: these
  // rows go up as an ARRAY upsert, and supabase-js builds the `columns=`
  // list from every key present — an undefined id is stringified away
  // from the body but still named in columns, so PostgREST fills it with
  // NULL and the NOT NULL constraint fires ("null value in column id",
  // Cimon's first Vail filing, 2026-10-16). Single-object upserts never
  // hit this, which is why every other mapper gets away with undefined.
  var row = {
    filing_id:   fi.filingId,
    item_id:     fi.itemId,
    invoice_ref: fi.invoiceRef || '',
    tax_amount:  Math.round((Number(fi.taxAmount) || 0) * 100) / 100,
  };
  var id = fi.id;
  if (id && !/^\d+$/.test(String(id))) row.id = id;
  return row;
}
export function rowToSalesTaxFilingFile(row) {
  return {
    id:          row.id,
    filingId:    row.filing_id    || '',
    storagePath: row.storage_path || '',
    filename:    row.filename     || '',
    sizeBytes:   row.size_bytes == null ? null : Number(row.size_bytes),
    uploadedBy:  row.uploaded_by  || '',
    uploadedAt:  row.uploaded_at  || '',
  };
}

// ── Sales tax payment → receipt for the bookkeeper (2026-10-16) ─────────
// The receipt row that mirrors a filing. Pure: the caller decides the id,
// the file and whether to write at all (only when amountPaid > 0). Every
// money and date field comes from the FROZEN filing — the receipt is a
// pointer (salesTaxFilingId), never a second source of truth. Status
// 'approved' from birth: a verified payment, nothing for Dan to review.
export function buildSalesTaxReceipt(filing, opts) {
  var o = opts || {};
  var f = filing || {};
  var returnLabel = String(o.returnLabel || '').trim();
  var stateName = String(o.stateName || US_STATE_NAMES[String(f.stateCode || '').toUpperCase()] || f.stateCode || '').trim();
  // Payee as the bookkeeper would write it: the state, or the home-rule
  // city that took the money. "Colorado state return" → "Colorado".
  var payee = returnLabel && !/state return$/i.test(returnLabel) ? returnLabel : stateName;
  var period = typeof salesTaxPeriodLabel === 'function' ? salesTaxPeriodLabel(f.periodKey) : String(f.periodKey || '');
  var now = o.now || new Date().toISOString();
  var notes = ['Sales tax', period, returnLabel, f.confirmationNo ? 'conf ' + f.confirmationNo : '', f.method || '']
    .filter(Boolean).join(' · ');
  return {
    id:               o.id || (o.existing && o.existing.id) || '',
    kind:             'sales_tax',
    status:           'approved',
    storagePath:      o.storagePath != null ? o.storagePath : ((o.existing && o.existing.storagePath) || ''),
    filename:         o.filename != null ? o.filename : ((o.existing && o.existing.filename) || ''),
    mimeType:         o.mimeType != null ? o.mimeType : ((o.existing && o.existing.mimeType) || ''),
    vendorName:       payee,
    receiptDate:      f.filedDate || f.paidDate || '',
    paidDate:         f.paidDate || '',
    totalAmount:      Math.round((Number(f.amountPaid) || 0) * 100) / 100,
    ccFee:            0,
    markupPct:        0,
    currency:         'USD',
    extractedJson:    { source: 'sales_tax_filing', filingId: f.id, stateCode: f.stateCode, returnKey: f.returnKey || '', periodKey: f.periodKey, taxDueSnapshot: f.taxDueSnapshot, confirmationNo: f.confirmationNo || '', method: f.method || '' },
    projectLabel:     '',
    createdBy:        (o.existing && o.existing.createdBy) || o.who || '',
    category:         'sales_tax',
    allocationMethod: 'manual',       // receipts CHECK allows net_price_weighted | manual only; nothing to allocate here
    allocationBreakdown: [],
    notes:            notes,
    approvedAt:       (o.existing && o.existing.approvedAt) || now,
    approvedBy:       (o.existing && o.existing.approvedBy) || o.who || '',
    salesTaxFilingId: f.id,
  };
}

// ── State tax documents (2026-10-16): the paper behind a registration —
// resale certificate, sales tax / business license — one row per file on
// the state_tax_records row (state or home-rule city). Owner + admin RLS.
export var STATE_TAX_DOCUMENT_KINDS = [
  ['resale_certificate', 'Resale certificate'],
  ['sales_tax_license',  'Sales tax license'],
  ['business_license',   'Business license'],
  ['other',              'Other'],
];
export function stateTaxDocumentKindLabel(kind) {
  var hit = STATE_TAX_DOCUMENT_KINDS.find(function(k) { return k[0] === kind; });
  return hit ? hit[1] : 'Document';
}
export function rowToStateTaxDocument(row) {
  if (!row) return null;
  return {
    id:               row.id,
    stateTaxRecordId: row.state_tax_record_id || '',
    kind:             row.kind || 'other',
    label:            row.label || '',
    expiresOn:        row.expires_on ? String(row.expires_on).slice(0, 10) : '',
    storagePath:      row.storage_path || '',
    filename:         row.filename || '',
    sizeBytes:        row.size_bytes == null ? null : Number(row.size_bytes),
    uploadedBy:       row.uploaded_by || '',
    uploadedAt:       row.uploaded_at || '',
  };
}
// Expiry state for the card: 'expired' | 'soon' (≤ 60 days) | '' .
export function stateTaxDocumentExpiryState(doc, today) {
  if (!doc || !doc.expiresOn) return '';
  var t = String(today || new Date().toISOString().slice(0, 10));
  if (doc.expiresOn < t) return 'expired';
  var ms = Date.parse(doc.expiresOn) - Date.parse(t);
  return ms <= 60 * 86400000 ? 'soon' : '';
}

// ── Due dates. state_tax_records.due_rule: 'last_day_next_month' |
// '<DD>_next_month' | ''. A quarter is due relative to its last month.
export function salesTaxPeriodEndDate(periodKey) {
  var k = String(periodKey || '');
  var y = parseInt(k.slice(0, 4), 10);
  if (!y) return '';
  var m;
  if (k.indexOf('-Q') > 0) m = parseInt(k.split('-Q')[1], 10) * 3;
  else m = parseInt(k.slice(5, 7), 10);
  if (!m) return '';
  var last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return y + '-' + String(m).padStart(2, '0') + '-' + String(last).padStart(2, '0');
}
export function salesTaxDueDate(periodKey, dueRule) {
  var end = salesTaxPeriodEndDate(periodKey);
  var rule = String(dueRule || '').trim();
  if (!end || !rule) return '';
  var y = parseInt(end.slice(0, 4), 10);
  var m = parseInt(end.slice(5, 7), 10);     // 1-12; next month is m (0-based m)
  var ny = m === 12 ? y + 1 : y;
  var nm = m === 12 ? 1 : m + 1;
  var day;
  if (rule === 'last_day_next_month') {
    day = new Date(Date.UTC(ny, nm, 0)).getUTCDate();
  } else {
    var mm = rule.match(/^(\d{1,2})_next_month$/);
    if (!mm) return '';
    day = Math.min(parseInt(mm[1], 10), new Date(Date.UTC(ny, nm, 0)).getUTCDate());
  }
  return ny + '-' + String(nm).padStart(2, '0') + '-' + String(day).padStart(2, '0');
}
export function salesTaxDueRuleLabel(dueRule) {
  var rule = String(dueRule || '').trim();
  if (rule === 'last_day_next_month') return 'due last day of the following month';
  var mm = rule.match(/^(\d{1,2})_next_month$/);
  if (!mm) return '';
  var d = parseInt(mm[1], 10);
  var suf = (d % 10 === 1 && d !== 11) ? 'st' : (d % 10 === 2 && d !== 12) ? 'nd' : (d % 10 === 3 && d !== 13) ? 'rd' : 'th';
  return 'due the ' + d + suf + ' of the following month';
}
// "Oct 31" / "Oct 31, 2026"
export function salesTaxShortDate(iso, withYear) {
  var s = String(iso || '').slice(0, 10);
  if (s.length < 10) return '';
  var d = new Date(s + 'T00:00:00');
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', withYear
    ? { month: 'short', day: 'numeric', year: 'numeric' }
    : { month: 'short', day: 'numeric' });
}
export function salesTaxDueRuleFor(stateTaxRecords, stateCode) {
  var rec = salesTaxStateRuleFor(stateTaxRecords, stateCode);
  return rec ? (rec.dueRule || '') : '';
}

// ── The items behind a state row, with each item's tax FROZEN from the
// FIN tax snapshot (final_invoice_items) when the FIN has one, else live
// — the same precedence buildSalesTaxReport uses, so the modal's items sum
// to the row's figure. Returns [{invoiceRef, invoiceId, paidDate, items:
// [{itemId, sku, name, tax, source}]}].
export function salesTaxFilingItemGroups(stateRow, refs) {
  var invoices          = (refs && refs.invoices)          || [];
  var items             = (refs && refs.items)             || [];
  var finalInvoiceItems = (refs && refs.finalInvoiceItems) || [];
  var projects          = (refs && refs.projects)          || [];
  var r2 = function(x) { return Math.round((x || 0) * 100) / 100; };
  var itemsById = {};
  items.forEach(function(it) { if (it && it.id) itemsById[it.id] = it; });
  var invById = {};
  invoices.forEach(function(inv) { if (inv && inv.id) invById[inv.id] = inv; });
  var projByLabel = {};
  projects.forEach(function(pr) { if (pr && pr.label) projByLabel[pr.label] = pr; });
  var snapByFin = {};
  finalInvoiceItems.forEach(function(row) {
    if (!row || !row.finalInvoiceId || !row.itemId) return;
    (snapByFin[row.finalInvoiceId] = snapByFin[row.finalInvoiceId] || {})[row.itemId] = row;
  });
  var groups = [];
  if (!stateRow) return groups;
  (stateRow.projects || []).forEach(function(pr) {
    var proj = projByLabel[pr.project] || null;
    (pr.fins || []).forEach(function(f) {
      var inv = invById[f.invoiceId];
      var finIds = inv ? (Array.isArray(inv.itemIds) ? inv.itemIds : (Array.isArray(inv.item_ids) ? inv.item_ids : [])) : [];
      // A delivery-basis entry names its own items — the FIN's other
      // items sit in another period's row (2026-10-16).
      var ids = Array.isArray(f.itemIds) ? f.itemIds : finIds;
      var snaps = snapByFin[f.invoiceId] || {};
      var complete = finIds.length > 0 && finIds.every(function(iid) { return !!snaps[iid]; });
      var rows = ids.map(function(iid) {
        var it = itemsById[iid];
        var tax, source;
        if (complete) { tax = snaps[iid].tax; source = 'snapshot'; }
        else { tax = it ? computeItemTax(it, proj, items) * ccFeeMul(it) : 0; source = 'live'; }
        return {
          itemId: iid,
          sku:    it ? (it.itemNumber || '') : '',
          name:   it ? (it.productName || it.item || '') : '(item not found)',
          tax:    r2(tax),
          source: source,
        };
      });
      groups.push({ invoiceRef: f.invoiceRef, invoiceId: f.invoiceId, paidDate: f.paidDate, paidBasis: f.paidBasis, bucketDate: f.bucketDate || '', deliveryBasis: !!f.deliveryBasis, partial: !!f.partial, items: rows });
    });
  });
  return groups;
}

// ── Row state for the Sales Tax page. 'due' (fuchsia: activity, nothing
// filed or short) | 'paid' (green: paid to the cent / over, or a zero
// return filed) | 'quiet' (grey: no activity, nothing filed).
export function salesTaxRowState(stateRow, filings) {
  // A state with several RETURNS (2026-10-16: Colorado + Town of Vail)
  // reads green only when EVERY return is paid; any open return keeps
  // the state fuchsia. Each return is judged against its own share.
  var returns = stateRow && Array.isArray(stateRow.returns) && stateRow.returns.length > 1 ? stateRow.returns : null;
  if (returns) {
    var states = returns.map(function(r) {
      var own = (filings || []).filter(function(f) { return String(f.returnKey || '').toUpperCase() === String(r.key || '').toUpperCase(); });
      return salesTaxRowState({ hasActivity: stateRow.hasActivity, tax: r.tax }, own);
    });
    if (states.some(function(x) { return x === 'due'; })) return 'due';
    if (states.every(function(x) { return x === 'paid'; })) return 'paid';
    return stateRow.hasActivity ? 'due' : 'quiet';
  }
  var paid = sumSalesTaxFilings(filings);
  var has = !!(stateRow && stateRow.hasActivity);
  if (!has) return (filings && filings.length) ? 'paid' : 'quiet';
  if (!filings || !filings.length) return 'due';
  return paid >= (stateRow.tax || 0) - 0.005 ? 'paid' : 'due';
}
// The return line's own state (same rules, one return).
// Every stamp an item carries — one per filing, so a Vail item filed on
// two returns lists both (2026-10-16). Each entry: { filingItem, filing,
// files }. Oldest filing first.
export function salesTaxRemittancesForItem(itemId, refs) {
  var filingItems = (refs && refs.salesTaxFilingItems) || [];
  var filings     = (refs && refs.salesTaxFilings)     || [];
  var files       = (refs && refs.salesTaxFilingFiles) || [];
  return filingItems.filter(function(x) { return x && x.itemId === itemId; }).map(function(fi) {
    var filing = filings.find(function(f) { return f && f.id === fi.filingId; }) || null;
    return { filingItem: fi, filing: filing, files: files.filter(function(f) { return f && f.filingId === fi.filingId; }) };
  }).filter(function(r) { return !!r.filing; }).sort(function(a, b) { return String(a.filing.paidDate || '').localeCompare(String(b.filing.paidDate || '')); });
}
// The label a filing's return prints under ("Colorado state return" /
// "Town of Vail"), from the state's codes. Blank key → the state return.
export function salesTaxReturnLabel(filing, codes) {
  if (!filing) return '';
  var list = salesTaxStateReturns(filing.stateCode, codes);
  var hit = list.find(function(r) { return r.key === String(filing.returnKey || '').toUpperCase(); });
  if (hit) return hit.label;
  return filing.jurisdiction ? filing.jurisdiction + ' return' : (list[0] ? list[0].label : 'State return');
}

// The name a FILING prints under on the overview: the state's name when
// it files a single return ("California"), else the return's own label
// ("Colorado state return" / "Town of Vail").
export function salesTaxFilingReturnName(filing, codes, stateTaxRecords) {
  if (!filing) return '';
  var list = salesTaxStateReturns(filing.stateCode, codes);
  if (list.length > 1 || String(filing.returnKey || '')) return salesTaxReturnLabel(filing, codes);
  var rec = salesTaxStateRuleFor(stateTaxRecords || [], filing.stateCode);
  return (rec && rec.stateName) || US_STATE_NAMES[filing.stateCode] || filing.stateCode;
}
// ── Item surfaces read the join: item → filing_items → filing (+ files).
// Returns null when the item's tax has never been stamped as remitted.
export function salesTaxRemittanceForItem(itemId, refs) {
  var filingItems = (refs && refs.salesTaxFilingItems) || [];
  var filings     = (refs && refs.salesTaxFilings)     || [];
  var files       = (refs && refs.salesTaxFilingFiles) || [];
  var fi = filingItems.find(function(x) { return x && x.itemId === itemId; });
  if (!fi) return null;
  var filing = filings.find(function(f) { return f && f.id === fi.filingId; }) || null;
  return {
    filingItem: fi,
    filing:     filing,
    files:      files.filter(function(f) { return f && f.filingId === fi.filingId; }),
  };
}
// 'unbilled' (no paid FIN yet) | 'collected' (FIN paid, not remitted) |
// 'remitted'. Carries the paid date + the return period for the copy
// "not yet · FIN paid Oct 3 → Oct 2026 return".
export function salesTaxItemStatus(item, refs) {
  var invoices = (refs && refs.invoices) || [];
  var rem = salesTaxRemittanceForItem(item && item.id, refs);
  if (rem && rem.filing) return { state: 'remitted', remittance: rem };
  var finId = item && item.finalInvoiceId;
  var fin = finId ? invoices.find(function(inv) { return inv && inv.id === finId; }) : null;
  if (!fin) return { state: 'unbilled', fin: null };
  var paid = finPaidDate(fin, {
    invoices: invoices,
    paymentAllocations: (refs && refs.paymentAllocations) || [],
    clientPayments: (refs && refs.clientPayments) || [],
    zeroFinBy: 'close',
  });
  if (!paid.date) return { state: 'unbilled', fin: fin };
  // The return period follows the state's basis (2026-10-16): on delivery
  // basis the item files in the month it was delivered into the state.
  var proj = ((refs && refs.projects) || []).find(function(pr) { return pr && pr.label === (item.project || ''); }) || null;
  var rule = proj ? salesTaxStateRuleFor((refs && refs.stateTaxRecords) || [], proj.state) : null;
  var bucket = salesTaxBucketDate(rule, item, { paidDate: paid.date }, proj);
  return {
    state: 'collected', fin: fin, paidDate: paid.date, paidBasis: paid.basis,
    bucketDate: bucket.date, bucketBasis: bucket.basis, deliveryBasis: !!(rule && rule.taxBasis === 'delivery'),
    periodKey: salesTaxPeriodKey(bucket.date, 'month'),
    periodLabel: salesTaxPeriodLabel(salesTaxPeriodKey(bucket.date, 'month')),
  };
}
// "by Dan" — the teammate's name from firm settings, else the owner's
// first name, else the mailbox part of the address.
export function salesTaxRecorderName(email, firmSettings) {
  var e = String(email || '').toLowerCase();
  if (!e) return '';
  var team = (firmSettings && firmSettings.studioTeam) || [];
  var m = team.find(function(t) { return t && String(t.email || '').toLowerCase() === e; });
  if (m && m.name) return String(m.name).split(' ')[0];
  if (e === 'c@cimonstarr.com') return 'Cimon';
  var local = e.split('@')[0];
  return local.charAt(0).toUpperCase() + local.slice(1);
}

// ── SALES TAX OVERVIEW — three views (handoff v3, 2026-10-16) ───────────
// The Sales Tax tab stopped being a period × state grid. Three views, one
// question each: Returns due (what do I owe, to whom, by when), Filed
// (what was paid, does it reconcile), Detail by month (the figures). The
// builders below are pure; the section only renders what they return.

// First day of a period key. '2026-10' → '2026-10-01'; '2026-Q4' → '2026-10-01'.
export function salesTaxPeriodStartDate(periodKey) {
  var k = String(periodKey || '');
  var y = parseInt(k.slice(0, 4), 10);
  if (!y) return '';
  var m;
  if (k.indexOf('-Q') > 0) m = (parseInt(k.split('-Q')[1], 10) - 1) * 3 + 1;
  else if (k.length === 4) m = 1;
  else m = parseInt(k.slice(5, 7), 10);
  if (!m) return '';
  return y + '-' + String(m).padStart(2, '0') + '-01';
}
// Where a period stands today: 'not_started' (period opens later) → 'open'
// (inside the period; figures read "so far") → 'closed' (period ended,
// return fileable) → 'overdue' (due date passed). No due rule → never
// overdue; it simply stays 'closed'.
export function salesTaxPeriodState(periodKey, dueDate, today) {
  var t = String(today || new Date().toISOString().slice(0, 10)).slice(0, 10);
  var start = salesTaxPeriodStartDate(periodKey);
  var end = salesTaxPeriodEndDate(periodKey);
  if (!start || !end) return 'closed';
  if (t < start) return 'not_started';
  if (t <= end) return 'open';
  if (dueDate && t > String(dueDate).slice(0, 10)) return 'overdue';
  return 'closed';
}
// Σ of the FROZEN per-item stamp amounts on one filing (audit finding #4).
export function salesTaxFilingStampSum(filingId, filingItems) {
  return Math.round((filingItems || []).reduce(function(s, fi) { return fi && fi.filingId === filingId ? s + (Number(fi.taxAmount) || 0) : s; }, 0) * 100) / 100;
}
// A filing row that exists but is missing part of its record: money paid
// with no item stamped, no PDF attached, or a zero return with no
// confirmation number. Returns { half, reasons:[], text } where text is
// the one line the Returns-due view prints under the return name.
export function salesTaxFilingIsHalfRecorded(filing, filingItems, filingFiles) {
  if (!filing) return { half: false, reasons: [], text: '' };
  var stamps = (filingItems || []).filter(function(fi) { return fi && fi.filingId === filing.id; }).length;
  var files = (filingFiles || []).filter(function(f) { return f && f.filingId === filing.id; }).length;
  var paid = Number(filing.amountPaid) || 0;
  var snap = Number(filing.taxDueSnapshot) || 0;
  var zero = paid < 0.005 && snap < 0.005;
  var reasons = [];
  if (!zero && stamps === 0) reasons.push('0 items stamped');
  if (files === 0) reasons.push('no PDF attached');
  // A zero return's confirmation may live only in its PDF (handoff §4.8
  // prints "Confirmation is in the PDF" for exactly that), so a blank
  // number alone is not half-recorded — only a blank number AND no PDF.
  if (zero && files === 0 && !String(filing.confirmationNo || '').trim()) reasons.push('no confirmation number');
  if (!reasons.length) return { half: false, reasons: [], text: '' };
  var text = 'Recorded, but ' + (reasons.length === 1 ? reasons[0] : reasons.slice(0, -1).join(', ') + ' and ' + reasons[reasons.length - 1]);
  return { half: true, reasons: reasons, text: text, stamps: stamps, files: files, zero: zero };
}
function _stShortMonthRange(a, b) {
  if (!a || !b) return salesTaxShortDate(a || b);
  var sa = salesTaxShortDate(a), sb = salesTaxShortDate(b);
  return sa === sb ? sa : sa + ' – ' + sb;
}
function _stAddDays(iso, n) {
  var d = new Date(String(iso).slice(0, 10) + 'T00:00:00Z');
  if (isNaN(d.getTime())) return '';
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
// Is this return owed for this period at all? False when the period ended
// before the return's own record was registered (Vail's record carries its
// own registered_from; the state record is the fallback). A period that
// CONTAINS the registration date is the first return and is owed.
export function salesTaxReturnOwedForPeriod(periodKey, rec, st) {
  var reg = String((rec && rec.registeredFrom) || (st && st.registeredFrom) || '').slice(0, 10);
  if (!reg) return true;
  var end = salesTaxPeriodEndDate(periodKey);
  return !end || end >= reg;
}
// The returns a state row files this period, each with its own tax,
// label, jurisdiction and rate. A one-return state yields its single
// synthetic return (key '') carrying the whole row.
export function salesTaxRowReturns(st) {
  var list = (st && Array.isArray(st.returns) && st.returns.length) ? st.returns : null;
  if (!list) return [{ key: '', label: (st && st.stateName) || '', jurisdiction: '', rate: st ? st.rate : 0, tax: st ? st.tax : 0 }];
  if (list.length === 1) return [Object.assign({}, list[0], { label: (st && st.stateName) || list[0].label, tax: st.tax })];
  return list;
}
// Per-reason item counts behind a delivery-basis state row, from its
// fins[] entries (each carries itemIds + bucketBasis). Used for the
// Returns-due second line: "4 items delivered to WY this month · 1 item
// on a planned-install date".
export function salesTaxRowBucketCounts(st) {
  var out = { delivered: [], planned: [], fallback: [], firstReturn: [], total: 0 };
  ((st && st.projects) || []).forEach(function(pr) {
    (pr.fins || []).forEach(function(f) {
      var ids = Array.isArray(f.itemIds) ? f.itemIds : [];
      out.total += ids.length;
      if (f.bucketBasis === 'planned') out.planned = out.planned.concat(ids);
      else if (f.bucketBasis === 'fallback') out.fallback = out.fallback.concat(ids);
      else if (f.bucketBasis === 'first_return') out.firstReturn = out.firstReturn.concat(ids);
      else out.delivered = out.delivered.concat(ids);
    });
  });
  return out;
}

// buildSalesTaxReturnsDue — View 1.
//   report: buildSalesTaxReport output (grouping 'month', year '' so a
//           December return due in January is not cut off; spineThrough =
//           next month so "period opens Nov 1" rows exist)
//   refs:   { salesTaxFilings, salesTaxFilingItems, salesTaxFilingFiles,
//             stateTaxRecords, salesTaxCodes }
//   opts:   { today, horizonDays (45) }
// Returns { attention:[…], due:[…], upcoming:{ count, amount, from, to,
//           lines:[…] }, sentence:{…} }. Every line carries period / st /
//           ret refs so the section can hand them to the modal unchanged.
export function buildSalesTaxReturnsDue(report, refs, opts) {
  var filings     = (refs && refs.salesTaxFilings)     || [];
  var filingItems = (refs && refs.salesTaxFilingItems) || [];
  var filingFiles = (refs && refs.salesTaxFilingFiles) || [];
  var records     = (refs && refs.stateTaxRecords)     || [];
  var codes       = (refs && refs.salesTaxCodes)       || [];
  var today = String((opts && opts.today) || new Date().toISOString().slice(0, 10)).slice(0, 10);
  var horizon = _stAddDays(today, (opts && opts.horizonDays) || 45);
  var r2 = function(x) { return Math.round((x || 0) * 100) / 100; };
  var pk = function(period, st) { return (st && st.periodKey) || period.key; };

  // ── Section A: half-recorded filings (any period, any year).
  var attention = [];
  filings.forEach(function(f) {
    var h = salesTaxFilingIsHalfRecorded(f, filingItems, filingFiles);
    if (!h.half) return;
    attention.push({
      kind: 'half', filing: f, text: h.text, reasons: h.reasons,
      filedDate: f.filedDate || f.paidDate || '', amountPaid: r2(f.amountPaid),
      returnLabel: salesTaxFilingReturnName(f, codes, records), periodLabel: salesTaxPeriodLabel(f.periodKey),
    });
  });
  attention.sort(function(a, b) { return String(b.filedDate).localeCompare(String(a.filedDate)); });

  // ── Sections B + C: every unfiled return, with its due date and state.
  var lines = [];
  ((report && report.periods) || []).forEach(function(period) {
    (period.states || []).forEach(function(st) {
      if (!st.registered) return;
      var key = pk(period, st);
      var periodLabel = st.periodKey ? st.periodLabel : period.label;
      var grouping = st.filingGrouping || 'month';
      salesTaxRowReturns(st).forEach(function(ret) {
        var own = salesTaxFilingsFor(filings, st.stateCode, key, ret.key);
        var rec = salesTaxStateRuleFor(records, st.stateCode, ret.jurisdiction);
        // A return is owed only from ITS record's registration on: a period
        // that ended before registered_from is not an obligation (the
        // pre-registration deliveries file on the first return instead).
        if (!salesTaxReturnOwedForPeriod(key, rec, st)) return;
        var due = salesTaxDueDate(key, rec ? rec.dueRule : '');
        var state = salesTaxPeriodState(key, due, today);
        var tax = r2(ret.tax);
        if (own.length) {
          // Filed. Short-paid by more than $1 against the when-filed
          // snapshot comes back onto the list with Edit (handoff §3).
          var paid = sumSalesTaxFilings(own);
          var snapTot = r2(own.reduce(function(s, f) { return s + (Number(f.taxDueSnapshot) || 0); }, 0));
          var shortBy = r2(snapTot - paid);
          if (shortBy >= 1) {
            lines.push({
              kind: 'short', period: period, st: st, ret: ret, filing: own[own.length - 1],
              stateCode: st.stateCode, stateName: st.stateName, periodKey: key, periodLabel: periodLabel, grouping: grouping,
              label: ret.label, tax: tax, shortBy: shortBy, dueDate: due, periodState: 'overdue', sortDate: due || today, action: 'edit',
            });
          }
          return;
        }
        var counts = salesTaxRowBucketCounts(st);
        lines.push({
          kind: 'due', period: period, st: st, ret: ret,
          stateCode: st.stateCode, stateName: st.stateName, periodKey: key, periodLabel: periodLabel, grouping: grouping,
          label: ret.label, extraLabels: [], tax: tax, dueDate: due, periodState: state, sortDate: due || salesTaxPeriodEndDate(key),
          zero: tax < 0.005, deliveryBasis: !!st.deliveryBasis, counts: counts, returnCount: 1,
          action: state === 'not_started' ? null : (tax < 0.005 ? 'zero' : 'file'),
        });
      });
    });
  });
  // Collapse zero returns from one state sharing a due date into one line.
  var merged = [];
  lines.forEach(function(ln) {
    if (ln.kind === 'due' && ln.zero) {
      var hit = merged.find(function(m) { return m.kind === 'due' && m.zero && m.stateCode === ln.stateCode && m.periodKey === ln.periodKey && m.dueDate === ln.dueDate; });
      if (hit) { hit.extraLabels.push(ln.label); hit.returnCount += 1; hit.rets = (hit.rets || [hit.ret]).concat([ln.ret]); if (hit.action) hit.action = 'zero'; return; }
    }
    merged.push(ln);
  });
  merged.sort(function(a, b) {
    var ao = a.periodState === 'overdue' ? 0 : 1, bo = b.periodState === 'overdue' ? 0 : 1;
    if (ao !== bo) return ao - bo;
    return String(a.sortDate).localeCompare(String(b.sortDate)) || String(a.stateCode).localeCompare(String(b.stateCode));
  });
  var due = [], later = [];
  merged.forEach(function(ln) {
    if (ln.kind === 'short' || ln.periodState === 'overdue' || String(ln.dueDate || ln.sortDate) <= horizon) due.push(ln);
    else later.push(ln);
  });
  var upcoming = {
    count: later.reduce(function(s, l) { return s + (l.returnCount || 1); }, 0),
    amount: r2(later.reduce(function(s, l) { return s + l.tax; }, 0)),
    from: later.length ? later[0].dueDate : '', to: later.length ? later[later.length - 1].dueDate : '',
    range: later.length ? _stShortMonthRange(later[0].dueDate, later[later.length - 1].dueDate) : '',
    lines: later,
  };

  // ── The status sentence.
  var overdue = due.filter(function(l) { return l.kind === 'due' && l.periodState === 'overdue'; });
  var ready = due.filter(function(l) { return l.kind === 'due' && (l.periodState === 'closed' || l.periodState === 'overdue'); });
  var sentence;
  if (overdue.length) {
    sentence = { kind: 'overdue', count: overdue.reduce(function(s, l) { return s + (l.returnCount || 1); }, 0), unpaid: r2(overdue.reduce(function(s, l) { return s + l.tax; }, 0)) };
  } else if (ready.length) {
    var dues = ready.map(function(l) { return l.dueDate; }).filter(Boolean).sort();
    sentence = { kind: 'ready', count: ready.reduce(function(s, l) { return s + (l.returnCount || 1); }, 0), nextDue: dues[0] || '', remit: r2(ready.reduce(function(s, l) { return s + l.tax; }, 0)) };
  } else {
    var all = due.filter(function(l) { return l.kind === 'due'; });
    var nd = all.map(function(l) { return l.dueDate; }).filter(Boolean).sort();
    sentence = { kind: 'quiet', nextDue: nd[0] || upcoming.from || '', count: all.reduce(function(s, l) { return s + (l.returnCount || 1); }, 0), amount: r2(all.reduce(function(s, l) { return s + l.tax; }, 0)), horizonDays: (opts && opts.horizonDays) || 45 };
  }
  return { attention: attention, due: due, upcoming: upcoming, sentence: sentence };
}

// buildSalesTaxFiledRows — View 2. One row per filing row in the database
// (the design's PNG collapses Colorado's two zero returns; the handoff
// text says one row per filing, and the text wins — every stored record
// is its own line). Sorted filed date desc, then return label.
//   refs: { salesTaxFilingItems, salesTaxFilingFiles, salesTaxCodes, stateTaxRecords }
//   opts: { year: '2026' | '', returnFilter: '' | 'WY' | 'CO' | 'CO:VAIL' }
// Returns { rows:[…], totals:{ paid, items, filings } }.
export function buildSalesTaxFiledRows(filings, refs, opts) {
  var filingItems = (refs && refs.salesTaxFilingItems) || [];
  var filingFiles = (refs && refs.salesTaxFilingFiles) || [];
  var codes       = (refs && refs.salesTaxCodes)       || [];
  var records     = (refs && refs.stateTaxRecords)     || [];
  var year = String((opts && opts.year) || '');
  var rf = String((opts && opts.returnFilter) || '');
  var r2 = function(x) { return Math.round((x || 0) * 100) / 100; };
  var rows = (filings || []).filter(function(f) {
    if (!f) return false;
    if (year && String(f.periodKey || '').slice(0, 4) !== year) return false;
    if (rf) {
      var parts = rf.split(':');
      if (f.stateCode !== parts[0]) return false;
      if (parts.length > 1 && String(f.returnKey || '').toUpperCase() !== parts[1]) return false;
      if (parts.length === 1 && String(f.returnKey || '')) return false;
    }
    return true;
  }).map(function(f) {
    var half = salesTaxFilingIsHalfRecorded(f, filingItems, filingFiles);
    var stamps = filingItems.filter(function(fi) { return fi.filingId === f.id; });
    var files = filingFiles.filter(function(x) { return x.filingId === f.id; });
    var paid = r2(f.amountPaid);
    var snap = r2(f.taxDueSnapshot);
    var stampSum = salesTaxFilingStampSum(f.id, filingItems);
    var zero = paid < 0.005 && snap < 0.005;
    var rule = salesTaxStateRuleFor(records, f.stateCode);
    var firstReturn = !!(rule && salesTaxFirstReturn(f.periodKey, rule.registeredFrom, f.grouping || 'month'));
    // Note column — first rule that applies (handoff §4.8).
    var figure = stamps.length ? stampSum : snap;
    var diff = r2(paid - figure);
    var note = null;
    var periodEnd = salesTaxPeriodEndDate(f.periodKey);
    var filedOn = String(f.filedDate || f.paidDate || '').slice(0, 10);
    if (half.half) note = { kind: 'half', text: 'Half recorded', action: 'complete it' };
    else if (!zero && Math.abs(diff) >= 0.005 && Math.abs(diff) < 1) note = { kind: 'rounding', text: Math.round(Math.abs(diff) * 100) + '¢ rounding' };
    else if (!zero && diff >= 1) note = { kind: 'over', text: 'Over by ' + fmtMoney(diff) + (String(f.notes || '').trim() ? ' · ' + String(f.notes).trim() : '') };
    else if (!zero && diff <= -1) note = { kind: 'short', text: 'Short by ' + fmtMoney(Math.abs(diff)) };
    else if (zero && !String(f.confirmationNo || '').trim()) note = { kind: 'conf', text: 'Confirmation is in the PDF' };
    else if (filedOn && periodEnd && filedOn < periodEnd) note = { kind: 'early', text: 'Filed before the period closed · check period' };
    return {
      filing: f, half: half.half, filedDate: filedOn,
      returnLabel: salesTaxFilingReturnName(f, codes, records), periodLabel: salesTaxPeriodLabel(f.periodKey),
      zero: zero, firstReturn: firstReturn,
      paid: paid, stampCount: stamps.length, stampSum: stampSum, confirmationNo: String(f.confirmationNo || '').trim(),
      files: files, note: note,
    };
  });
  rows.sort(function(a, b) { return String(b.filedDate).localeCompare(String(a.filedDate)) || String(a.returnLabel).localeCompare(String(b.returnLabel)); });
  return {
    rows: rows,
    totals: {
      paid: r2(rows.reduce(function(s, r) { return s + r.paid; }, 0)),
      items: rows.reduce(function(s, r) { return s + r.stampCount; }, 0),
      filings: rows.length,
    },
  };
}
// The Return pull-down on the Filed view: every return the registered
// states file, as { value, label }. value '' = all; 'CO' = the state
// return; 'CO:VAIL' = a named return.
export function salesTaxReturnFilterOptions(stateTaxRecords, codes) {
  var seen = {};
  var out = [];
  (stateTaxRecords || []).forEach(function(r) {
    if (!r) return;
    var code = String(r.stateCode || '').toUpperCase();
    if (!code || seen[code]) return;
    seen[code] = true;
    var status = String(r.status || 'active');
    if (status === 'closed' || status === 'not_required') return;
    var list = salesTaxStateReturns(code, codes);
    if (list.length <= 1) { out.push({ value: code, label: r.stateName || US_STATE_NAMES[code] || code }); return; }
    list.forEach(function(ret) { out.push({ value: ret.key ? code + ':' + ret.key : code, label: ret.label }); });
  });
  return out;
}
